import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { localizePath } from "../../components/LangRoute";

// Sprint 119.77 (2026-07-31) — Blog multi-idioma. Cada article té una entrada
// única amb id + slugs per idioma + translations map. 6 idiomes: ES/EN/FR/DE/IT/NL.
// Body components definits com a funcions React per idioma × article. HTML
// entities per accents (encoding Windows-safe).
//
// Sprint 155 (08/09/2026) — ELS ENLLAÇOS INTERNS JA NO SÓN RUTES ES NEUTRES.
//
// Aquí hi deia que mantenir-los en castellà «funciona a tots els idiomes».
// Donen 200, sí; el que no deia és que et canvien d'idioma pel camí. Un
// lector alemany a /de/blog/poolrechner-volumen-berechnen clicava la
// calculadora i queia a /tools/pool-volume-calculator, que és l'ANGLESA
// (la de volum té l'anglès com a idioma sense prefix). Ho va veure en
// Víctor el 08/09, i és la fuga just al país que estem intentant
// posicionar: Alemanya fa 340 impressions amb 0 clics.
//
// Són 248 enllaços repartits pels cossos dels articles. En comptes de
// tocar-los un a un, tots passen per aquest `Link`, que els tradueix amb
// `localizePath` — el mateix que ja fan la capçalera i les calculadores, i
// que des del Sprint 154 sap tant els prefixos com els slugs per idioma.
//
// Els enllaços entre articles del blog van a part: la seva traducció no és
// un prefix sinó el slug de l'altre article en aquell idioma.
function Link({ to, ...props }) {
  const { i18n } = useTranslation();
  const lang = i18n?.language?.split("-")[0] || "es";
  let desti = to;
  if (typeof to === "string") {
    const art = to.startsWith("/blog/")
      ? ARTICLES.find((a) => a.slugs && a.slugs.es === to.slice(6))
      : null;
    if (art) {
      // Si l'article destí no existeix en aquest idioma val més deixar-lo
      // en castellà que enviar el lector a una URL que no hi és.
      const slug = art.slugs[lang];
      desti = !slug ? to : lang === "es" ? `/blog/${slug}` : `/${lang}/blog/${slug}`;
    } else {
      desti = localizePath(to, lang);
    }
  }
  return <RouterLink to={desti} {...props} />;
}
//
// Adaptació de norma per idioma:
//   ES → UNE-EN 17645:2023
//   EN → EN 17645:2023
//   FR → NF EN 17645:2023
//   DE → DIN EN 17645:2023
//   IT → UNI EN 17645:2023

// ============================================================================
// ARTICLE 1 — Cost mensual piscina 2026
// ============================================================================

const Article1BodyES = () => (
  <>
    <h2>Resumen: cuánto gasta realmente una piscina residencial</h2>
    <p>
      Mantener una piscina residencial exterior enterrada en España cuesta, de
      media anualizada, <strong>entre 25 y 80 &euro; al mes</strong> en 2026.
      Ese abanico depende de cuatro factores principales:{" "}
      <strong>volumen y superficie</strong>, <strong>clima local</strong>,{" "}
      <strong>equipamiento instalado</strong> y <strong>h&aacute;bitos de uso</strong>.
    </p>
    <p>Para ordenar las magnitudes:</p>
    <ul>
      <li><strong>Piscina peque&ntilde;a (25-35 m&sup3;, no climatizada, temporada 4 meses)</strong>: 25-40 &euro;/mes anualizado.</li>
      <li><strong>Piscina media (40-60 m&sup3;, no climatizada, temporada 5 meses)</strong>: 40-60 &euro;/mes.</li>
      <li><strong>Piscina media climatizada con bomba de calor (temporada 8-10 meses)</strong>: 60-100 &euro;/mes.</li>
      <li><strong>Piscina grande (&gt;60 m&sup3;) climatizada, sin cubierta t&eacute;rmica</strong>: 80-140 &euro;/mes o m&aacute;s.</li>
    </ul>
    <p>
      La existencia o no de <strong>cubierta motorizada</strong>, la{" "}
      <strong>variable de la bomba de filtraci&oacute;n</strong> y el{" "}
      <strong>sistema de tratamiento de agua</strong> pueden cambiar la factura
      anual en m&aacute;s de 500 &euro;, sin tocar el uso.
    </p>
    <p>
      Antes de entrar al detalle, importa entender la l&oacute;gica: una piscina
      no es un electrodom&eacute;stico, es un{" "}
      <strong>sistema hidr&aacute;ulico y t&eacute;rmico</strong> que pierde
      agua por evaporaci&oacute;n y calor por convecci&oacute;n y radiaci&oacute;n.
      Cualquier ahorro real pasa por reducir esas dos p&eacute;rdidas.
    </p>

    <h2>Los 4 grandes gastos de una piscina</h2>
    <p>El coste total anual de una piscina residencial se distribuye t&iacute;picamente as&iacute;:</p>
    <ol>
      <li><strong>Electricidad</strong> &mdash; 55 a 70 % del total. Bomba de filtraci&oacute;n, iluminaci&oacute;n, y bomba de calor si existe.</li>
      <li><strong>Agua de reposici&oacute;n</strong> &mdash; 10 a 20 %. Compensa evaporaci&oacute;n, contralavados y salpicaduras.</li>
      <li><strong>Productos qu&iacute;micos</strong> &mdash; 10 a 20 %. Cloro, regulador de pH, algicida o sal si hay clorador salino.</li>
      <li><strong>Mantenimiento peri&oacute;dico</strong> &mdash; 5 a 15 %. Puesta a punto de temporada, reparaciones menores, recambios (juntas, bocinas, focos LED).</li>
    </ol>
    <p>
      La proporci&oacute;n cambia radicalmente si la piscina est&aacute;{" "}
      <strong>climatizada</strong>: en ese caso la electricidad puede superar
      el 75 % del gasto anual, especialmente si se emplea{" "}
      <strong>resistencia el&eacute;ctrica</strong> en lugar de{" "}
      <strong>bomba de calor</strong>.
    </p>

    <h2>Consumo el&eacute;ctrico detallado</h2>

    <h3>Bomba de filtraci&oacute;n</h3>
    <p>
      Es el equipo con m&aacute;s horas de funcionamiento al a&ntilde;o. Una
      bomba monovelocidad convencional de 0,75 a 1,5 CV en una piscina de 50 m&sup3;
      consume entre <strong>1.500 y 3.000 kWh anuales</strong> funcionando 6-8
      h/d&iacute;a en temporada de ba&ntilde;o, seg&uacute;n literatura t&eacute;cnica
      del sector.
    </p>
    <p>
      Con una tarifa media de <strong>0,18 &euro;/kWh</strong>, hablamos de{" "}
      <strong>270 a 540 &euro; al a&ntilde;o</strong> solo en filtraci&oacute;n.
      Una <strong>bomba de velocidad variable (VSD)</strong> bien programada
      reduce ese consumo entre un <strong>60 y un 80 %</strong>, dejando la
      factura de filtraci&oacute;n en 60-180 &euro;/a&ntilde;o.
    </p>

    <h3>Iluminaci&oacute;n</h3>
    <p>
      Los focos LED sumergibles consumen 15-30 W frente a 100-300 W de los
      antiguos hal&oacute;genos. En una piscina con 2-4 focos y 3 h de uso
      diario en temporada, la iluminaci&oacute;n LED apenas supone{" "}
      <strong>10-25 &euro;/a&ntilde;o</strong>. Un sistema hal&oacute;geno
      equivalente puede llegar a 100-250 &euro;/a&ntilde;o.
    </p>

    <h3>Bomba de calor (si existe)</h3>
    <p>
      Si tu piscina est&aacute; climatizada, la bomba de calor concentra la
      mayor parte del gasto el&eacute;ctrico. Su rendimiento se mide por el{" "}
      <strong>COP</strong> (Coefficient of Performance), que en r&eacute;gimen
      piscina se sit&uacute;a entre <strong>4 y 6</strong> en condiciones
      favorables (aire &gt;15 &deg;C y agua entre 24 y 28 &deg;C). Es decir:
      por cada kWh el&eacute;ctrico entregas 4-6 kWh t&eacute;rmicos al agua.
    </p>
    <p>
      Una piscina de 50 m&sup3; climatizada de mayo a septiembre con cubierta
      t&eacute;rmica consume aproximadamente{" "}
      <strong>1.500-3.500 kWh/a&ntilde;o</strong> de climatizaci&oacute;n,
      dependiendo del salto t&eacute;rmico objetivo y el clima. Sin cubierta
      t&eacute;rmica, la cifra puede duplicarse o triplicarse.
    </p>

    <h2>Consumo de agua</h2>
    <p>El agua se pierde por cuatro v&iacute;as:</p>
    <ul>
      <li><strong>Evaporaci&oacute;n</strong>: 5-8 mm/d&iacute;a en zonas c&aacute;lidas en verano, 2-4 mm/d&iacute;a en primavera y oto&ntilde;o. Una piscina de 32 m&sup2; pierde 160-256 litros/d&iacute;a por evaporaci&oacute;n pura en pleno verano.</li>
      <li><strong>Contralavados del filtro</strong>: 300-800 litros por operaci&oacute;n, t&iacute;picamente 1-2 veces al mes seg&uacute;n carga de suciedad.</li>
      <li><strong>Salpicaduras y arrastre</strong>: 5-15 litros por ba&ntilde;ista y sesi&oacute;n.</li>
      <li><strong>Vaciado o renovaci&oacute;n</strong>: la UNE 100312 no obliga a vaciar anualmente si la calidad del agua se mantiene, pero muchos usuarios renuevan parcialmente 10-30 % del volumen cada 2-3 a&ntilde;os.</li>
    </ul>
    <p>
      Con un precio medio del agua de <strong>1,5 a 3 &euro;/m&sup3;</strong>{" "}
      (var&iacute;a mucho entre municipios), la factura de agua anual de una
      piscina residencial en zona c&aacute;lida oscila entre{" "}
      <strong>30 y 90 &euro;/a&ntilde;o</strong>.
    </p>

    <h2>Consumo de productos qu&iacute;micos</h2>
    <p>El presupuesto de qu&iacute;micos depende del sistema de tratamiento:</p>
    <ul>
      <li><strong>Cloraci&oacute;n manual con hipoclorito</strong> o cloro s&oacute;lido: 50-120 &euro;/a&ntilde;o.</li>
      <li><strong>Clorador salino</strong>: menor gasto en cloro (la sal se repone puntualmente), 30-70 &euro;/a&ntilde;o. La sal cuesta unos 0,15-0,30 &euro;/kg y una recarga anual t&iacute;pica es de 20-50 kg.</li>
      <li><strong>Sistemas UV u ozono</strong>: reducen consumo de cloro entre 30 y 70 %, pero suman coste de recambio de l&aacute;mparas cada 8.000-12.000 h.</li>
    </ul>
    <p>
      A esto hay que sumar <strong>regulador de pH</strong> (10-25
      &euro;/a&ntilde;o), <strong>algicida</strong> puntual (10-20
      &euro;/a&ntilde;o) y <strong>floculante</strong> ocasional.
    </p>

    <h2>C&oacute;mo calcular el gasto real de TU piscina</h2>
    <p>La estimaci&oacute;n te&oacute;rica es &uacute;til, pero cada instalaci&oacute;n es distinta. Necesitas cuatro datos:</p>
    <ol>
      <li><strong>Volumen</strong> de la piscina en m&sup3; (largo &times; ancho &times; profundidad media).</li>
      <li><strong>Potencia real</strong> de la bomba de filtraci&oacute;n en kW (no el CV nominal &mdash; pide la ficha t&eacute;cnica).</li>
      <li><strong>Horas diarias de filtraci&oacute;n</strong> por temporada.</li>
      <li><strong>Tarifa el&eacute;ctrica</strong> media que pagas (&euro;/kWh, incluyendo peajes e impuestos).</li>
    </ol>
    <p>
      Con esos datos puedes usar las herramientas gratuitas de{" "}
      <Link to="/calculadoras">/calculadoras</Link> para estimar tu consumo
      anual y compararlo con la media. Y si quieres una{" "}
      <strong>clasificaci&oacute;n ambiental estimada seg&uacute;n UNE-EN 17645</strong>{" "}
      de tu piscina, obt&eacute;n tu{" "}
      <strong>Ficha T&eacute;cnica gratuita</strong> en{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> &mdash; recibir&aacute;s tu{" "}
      <strong>PoolLex ID</strong> &uacute;nico (formato PLX-XXXXXXX) y una
      estimaci&oacute;n de clase A a F.
    </p>

    <h2>C&oacute;mo reducir el consumo: 10 acciones concretas</h2>
    <ol>
      <li><strong>Instala una cubierta t&eacute;rmica motorizada o manual</strong>. Reduce evaporaci&oacute;n 60-70 % y p&eacute;rdidas de calor 40-60 %. Es la acci&oacute;n con mejor ROI en piscinas climatizadas.</li>
      <li><strong>Sustituye la bomba monovelocidad por una VSD</strong>. Ahorro t&iacute;pico: 60-80 % en consumo de filtraci&oacute;n.</li>
      <li><strong>Ajusta las horas de filtraci&oacute;n</strong> al volumen real y a la temperatura del agua. Regla orientativa: horas de filtraci&oacute;n diaria &asymp; temperatura del agua (&deg;C) / 2.</li>
      <li><strong>Cambia focos hal&oacute;genos por LED sumergibles</strong>. Consumo hasta 10 veces menor.</li>
      <li><strong>Si climatizas, usa bomba de calor con COP &gt;5</strong>, nunca resistencia el&eacute;ctrica.</li>
      <li><strong>Programa el filtrado en horas valle</strong> de tu tarifa el&eacute;ctrica (t&iacute;picamente 00:00-08:00 y fines de semana).</li>
      <li><strong>Controla el pH autom&aacute;ticamente</strong> con bomba dosificadora: evita sobreconsumo de cloro y precipitados.</li>
      <li><strong>Revisa fugas</strong> anualmente. Una fuga peque&ntilde;a de 5 mm/d&iacute;a es indistinguible de la evaporaci&oacute;n pero cuesta 60 m&sup3;/a&ntilde;o en una piscina de 32 m&sup2;.</li>
      <li><strong>Limpia el filtro correctamente</strong> &mdash; un filtro colmatado obliga a la bomba a trabajar m&aacute;s horas y con m&aacute;s consumo espec&iacute;fico.</li>
      <li><strong>Aisla t&eacute;rmicamente el vaso</strong> si construyes o rehabilitas. Reduce p&eacute;rdidas por conducci&oacute;n 15-25 %.</li>
    </ol>

    <h2>Certificado ambiental EN 17645: qu&eacute; significa para tu consumo</h2>
    <p>
      Desde 2023 existe la norma <strong>UNE-EN 17645:2023</strong>
      (adopci&oacute;n espa&ntilde;ola de la europea EN 17645), que establece
      una <strong>metodolog&iacute;a objetiva para clasificar la sostenibilidad ambiental
      de piscinas residenciales exteriores enterradas</strong>. Eval&uacute;a 8 componentes
      (estructura, circuito hidr&aacute;ulico, cubierta, bomba de filtraci&oacute;n,
      sistema de tratamiento de agua, sistema de calefacci&oacute;n, iluminaci&oacute;n) y otorga una{" "}
      <strong>clase A a F</strong>, an&aacute;loga a las etiquetas
      energ&eacute;ticas de electrodom&eacute;sticos.
    </p>
    <p>
      Una piscina <strong>clase A</strong> consume, en t&eacute;rminos
      comparables, entre <strong>3 y 5 veces menos</strong> recursos anuales
      que una <strong>clase F</strong> equivalente. La diferencia no est&aacute;
      en el uso, sino en el equipamiento y en c&oacute;mo se combinan los 8
      componentes.
    </p>
    <p>
      Conocer tu clasificaci&oacute;n es el primer paso para saber{" "}
      <strong>d&oacute;nde est&aacute;s</strong> y{" "}
      <strong>d&oacute;nde te compensa invertir primero</strong>. En{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> puedes obtener gratis
      una estimaci&oacute;n con tu <strong>PoolLex ID</strong>; para el sello
      firmado por ingeniero industrial colegiado, consulta el nivel 2 en{" "}
      <Link to="/pool-check">/pool-check</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> aplica la norma UNE-EN 17645:2023 con un
      motor de c&aacute;lculo que eval&uacute;a los 8 componentes y devuelve
      una estimaci&oacute;n de clase ambiental en menos de 2 minutos. No
      sustituye la certificaci&oacute;n firmada, pero te da un
      diagn&oacute;stico honesto de por d&oacute;nde empezar. Empieza gratis
      en <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

const Article1BodyEN = () => (
  <>
    <h2>Summary: what a residential pool really costs to run</h2>
    <p>
      Running a residential outdoor in-ground pool costs, on an annualised
      basis, <strong>between &euro;25 and &euro;80 per month</strong> in 2026.
      That range depends on four main drivers: <strong>volume and surface</strong>,{" "}
      <strong>local climate</strong>, <strong>installed equipment</strong> and{" "}
      <strong>usage habits</strong>.
    </p>
    <p>To put the numbers in order:</p>
    <ul>
      <li><strong>Small pool (25-35 m&sup3;, unheated, 4-month season)</strong>: &euro;25-40/month annualised.</li>
      <li><strong>Medium pool (40-60 m&sup3;, unheated, 5-month season)</strong>: &euro;40-60/month.</li>
      <li><strong>Medium pool heated with a heat pump (8-10 month season)</strong>: &euro;60-100/month.</li>
      <li><strong>Large pool (&gt;60 m&sup3;) heated without thermal cover</strong>: &euro;80-140/month or more.</li>
    </ul>
    <p>
      Whether or not you have a <strong>motorised cover</strong>, the
      <strong> filtration pump variant</strong> and the{" "}
      <strong>water treatment system</strong> can shift the annual bill by more
      than &euro;500 without touching how you use the pool.
    </p>
    <p>
      Before drilling into detail, understand the underlying logic: a pool is
      not an appliance, it is a{" "}
      <strong>hydraulic and thermal system</strong> that loses water by
      evaporation and heat by convection and radiation. Real savings come from
      cutting those two losses.
    </p>

    <h2>The 4 big cost buckets of a pool</h2>
    <p>The annual cost of a residential pool typically breaks down as:</p>
    <ol>
      <li><strong>Electricity</strong> &mdash; 55 to 70 % of the total. Filtration pump, lighting, and heat pump if present.</li>
      <li><strong>Top-up water</strong> &mdash; 10 to 20 %. Covers evaporation, backwash and splashing.</li>
      <li><strong>Chemicals</strong> &mdash; 10 to 20 %. Chlorine, pH regulator, algaecide or salt if you have a salt chlorinator.</li>
      <li><strong>Periodic maintenance</strong> &mdash; 5 to 15 %. Seasonal set-up, minor repairs, spare parts (gaskets, jets, LED lamps).</li>
    </ol>
    <p>
      The mix shifts sharply if the pool is <strong>heated</strong>: in that
      case electricity can top 75 % of the annual spend, especially if
      <strong> electric resistance</strong> is used instead of a{" "}
      <strong>heat pump</strong>.
    </p>

    <h2>Electricity in detail</h2>

    <h3>Filtration pump</h3>
    <p>
      It runs more hours per year than anything else in the pool. A
      conventional single-speed 0.75 to 1.5 HP pump on a 50 m&sup3; pool
      consumes between <strong>1,500 and 3,000 kWh per year</strong> running
      6-8 h/day in bathing season, according to sector technical literature.
    </p>
    <p>
      At an average tariff of <strong>&euro;0.18/kWh</strong> that is{" "}
      <strong>&euro;270 to &euro;540 per year</strong> just on filtration. A
      well-programmed <strong>variable-speed drive (VSD) pump</strong> reduces
      that use by <strong>60 to 80 %</strong>, leaving the filtration bill at
      &euro;60-180/year.
    </p>

    <h3>Lighting</h3>
    <p>
      Submersible LED lamps draw 15-30 W versus 100-300 W for legacy halogen.
      On a pool with 2-4 lamps and 3 h of daily use in season, LED lighting is
      barely <strong>&euro;10-25 per year</strong>. The equivalent halogen
      setup can hit &euro;100-250 per year.
    </p>

    <h3>Heat pump (if present)</h3>
    <p>
      If your pool is heated, the heat pump concentrates most of the
      electricity spend. Its performance is measured by the{" "}
      <strong>COP</strong> (Coefficient of Performance), which for pool duty
      sits between <strong>4 and 6</strong> under favourable conditions
      (air &gt;15 &deg;C and water between 24 and 28 &deg;C). Translation: for
      every 1 kWh of electricity you deliver 4-6 kWh of heat to the water.
    </p>
    <p>
      A 50 m&sup3; pool heated May to September with a thermal cover consumes
      roughly <strong>1,500-3,500 kWh/year</strong> of heating, depending on
      target temperature rise and climate. Without a thermal cover the number
      can double or triple.
    </p>

    <h2>Water use</h2>
    <p>Water leaves the pool via four routes:</p>
    <ul>
      <li><strong>Evaporation</strong>: 5-8 mm/day in warm zones in summer, 2-4 mm/day in spring and autumn. A 32 m&sup2; pool loses 160-256 litres/day to pure evaporation at the height of summer.</li>
      <li><strong>Filter backwash</strong>: 300-800 litres per cycle, typically 1-2 times/month depending on soiling.</li>
      <li><strong>Splashing and carry-over</strong>: 5-15 litres per bather per session.</li>
      <li><strong>Draining or renewal</strong>: the EN 15288-2 family does not require annual draining if water quality is maintained, but many owners partially renew 10-30 % of the volume every 2-3 years.</li>
    </ul>
    <p>
      At an average water price of <strong>&euro;1.5 to &euro;3 per m&sup3;</strong>{" "}
      (varies widely between municipalities), the annual water bill of a
      residential pool in a warm zone falls between{" "}
      <strong>&euro;30 and &euro;90 per year</strong>.
    </p>

    <h2>Chemical use</h2>
    <p>Chemical spend depends on the treatment system:</p>
    <ul>
      <li><strong>Manual chlorination with hypochlorite</strong> or solid chlorine: &euro;50-120/year.</li>
      <li><strong>Salt chlorinator</strong>: less chlorine spend (salt is topped up occasionally), &euro;30-70/year. Salt costs around &euro;0.15-0.30/kg and a typical annual top-up is 20-50 kg.</li>
      <li><strong>UV or ozone systems</strong>: cut chlorine use by 30-70 %, but add lamp replacement cost every 8,000-12,000 h.</li>
    </ul>
    <p>
      Add <strong>pH regulator</strong> (&euro;10-25/year), occasional{" "}
      <strong>algaecide</strong> (&euro;10-20/year) and occasional{" "}
      <strong>flocculant</strong>.
    </p>

    <h2>How to calculate YOUR pool's real cost</h2>
    <p>
      A theoretical estimate helps, but every installation is different. You
      need four data points:
    </p>
    <ol>
      <li><strong>Volume</strong> in m&sup3; (length &times; width &times; average depth).</li>
      <li><strong>Real power</strong> of the filtration pump in kW (not the nominal HP &mdash; ask for the datasheet).</li>
      <li><strong>Daily filtration hours</strong> per season.</li>
      <li><strong>Average electricity tariff</strong> you pay (&euro;/kWh, including taxes and grid fees).</li>
    </ol>
    <p>
      With that data you can use the free tools at{" "}
      <Link to="/calculadoras">/calculadoras</Link> to estimate your annual
      use and benchmark it. And if you want an{" "}
      <strong>estimated EN 17645 environmental class</strong> for your pool,
      get your free <strong>Technical Sheet</strong> at{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> &mdash; you will receive
      your unique <strong>PoolLex ID</strong> (format PLX-XXXXXXX) and an
      A-to-F class estimate.
    </p>

    <h2>How to cut consumption: 10 concrete actions</h2>
    <ol>
      <li><strong>Install a manual or motorised thermal cover</strong>. Cuts evaporation 60-70 % and heat loss 40-60 %. Best ROI on heated pools.</li>
      <li><strong>Replace the single-speed pump with a VSD</strong>. Typical saving: 60-80 % on filtration.</li>
      <li><strong>Tune filtration hours</strong> to real volume and water temperature. Rule of thumb: daily filtration hours &asymp; water temperature (&deg;C) / 2.</li>
      <li><strong>Swap halogen lamps for submersible LED</strong>. Up to 10&times; lower use.</li>
      <li><strong>If you heat, use a heat pump with COP &gt;5</strong>, never electric resistance.</li>
      <li><strong>Schedule filtering during off-peak hours</strong> on your tariff (typically 00:00-08:00 and weekends).</li>
      <li><strong>Control pH automatically</strong> with a dosing pump: avoids chlorine over-use and precipitates.</li>
      <li><strong>Check for leaks</strong> yearly. A tiny 5 mm/day leak looks like evaporation but costs 60 m&sup3;/year on a 32 m&sup2; pool.</li>
      <li><strong>Clean the filter properly</strong> &mdash; a clogged filter forces the pump to run longer at higher specific consumption.</li>
      <li><strong>Thermally insulate the shell</strong> when building or renovating. Cuts conduction losses 15-25 %.</li>
    </ol>

    <h2>EN 17645 environmental certificate: what it means for your consumption</h2>
    <p>
      Since 2023 the standard <strong>EN 17645:2023</strong> exists, setting an
      <strong> objective methodology to classify the environmental sustainability
      of residential outdoor in-ground pools</strong>. It evaluates 8
      components (shell, hydraulic circuit, cover, filtration pump, water
      treatment, heating, lighting, control) and awards an{" "}
      <strong>A-to-F class</strong>, analogous to the appliance energy label.
    </p>
    <p>
      A <strong>class A</strong> pool uses, on comparable terms,{" "}
      <strong>3 to 5 times fewer</strong> annual resources than an equivalent
      <strong> class F</strong>. The gap is not in use but in equipment and
      how the 8 components combine.
    </p>
    <p>
      Knowing your class is the first step to see <strong>where you are</strong>{" "}
      and <strong>where to invest first</strong>. At{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> you can get a free
      estimate with your <strong>PoolLex ID</strong>; for the engineer-signed
      seal, see level 2 at <Link to="/pool-check">/pool-check</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> applies EN 17645:2023 with a calculation
      engine that evaluates the 8 components and returns an estimated
      environmental class in under 2 minutes. It does not replace signed
      certification, but it gives an honest diagnostic of where to start.
      Start free at <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);
const Article1BodyFR = () => (
  <>
    <h2>R&eacute;sum&eacute; : combien co&ucirc;te r&eacute;ellement une piscine r&eacute;sidentielle</h2>
    <p>
      Entretenir une piscine r&eacute;sidentielle ext&eacute;rieure enterr&eacute;e
      co&ucirc;te, en moyenne annualis&eacute;e,{" "}
      <strong>entre 25 et 80 &euro; par mois</strong> en 2026. Cette fourchette
      d&eacute;pend de quatre facteurs principaux :{" "}
      <strong>volume et surface</strong>, <strong>climat local</strong>,{" "}
      <strong>&eacute;quipement install&eacute;</strong> et{" "}
      <strong>habitudes d'usage</strong>.
    </p>
    <p>Pour situer les ordres de grandeur :</p>
    <ul>
      <li><strong>Petite piscine (25-35 m&sup3;, non chauff&eacute;e, saison 4 mois)</strong> : 25-40 &euro;/mois annualis&eacute;.</li>
      <li><strong>Piscine moyenne (40-60 m&sup3;, non chauff&eacute;e, saison 5 mois)</strong> : 40-60 &euro;/mois.</li>
      <li><strong>Piscine moyenne chauff&eacute;e par pompe &agrave; chaleur (saison 8-10 mois)</strong> : 60-100 &euro;/mois.</li>
      <li><strong>Grande piscine (&gt;60 m&sup3;) chauff&eacute;e, sans couverture thermique</strong> : 80-140 &euro;/mois ou plus.</li>
    </ul>
    <p>
      La pr&eacute;sence ou non d'une <strong>couverture motoris&eacute;e</strong>,
      la <strong>variante de la pompe de filtration</strong> et le{" "}
      <strong>syst&egrave;me de traitement de l'eau</strong> peuvent changer la
      facture annuelle de plus de 500 &euro; sans toucher &agrave; l'usage.
    </p>
    <p>
      Avant d'entrer dans le d&eacute;tail, il faut comprendre la logique :
      une piscine n'est pas un &eacute;lectrom&eacute;nager, c'est un{" "}
      <strong>syst&egrave;me hydraulique et thermique</strong> qui perd de
      l'eau par &eacute;vaporation et de la chaleur par convection et
      rayonnement. Toute &eacute;conomie r&eacute;elle passe par la r&eacute;duction
      de ces deux pertes.
    </p>

    <h2>Les 4 grands postes de d&eacute;pense d'une piscine</h2>
    <p>Le co&ucirc;t total annuel d'une piscine r&eacute;sidentielle se r&eacute;partit typiquement ainsi :</p>
    <ol>
      <li><strong>&Eacute;lectricit&eacute;</strong> &mdash; 55 &agrave; 70 % du total. Pompe de filtration, &eacute;clairage, pompe &agrave; chaleur si pr&eacute;sente.</li>
      <li><strong>Eau d'appoint</strong> &mdash; 10 &agrave; 20 %. Compense &eacute;vaporation, contre-lavages et &eacute;claboussures.</li>
      <li><strong>Produits chimiques</strong> &mdash; 10 &agrave; 20 %. Chlore, r&eacute;gulateur de pH, algicide ou sel avec &eacute;lectrolyseur.</li>
      <li><strong>Maintenance p&eacute;riodique</strong> &mdash; 5 &agrave; 15 %. Mise en route de saison, r&eacute;parations mineures, pi&egrave;ces (joints, buses, LED).</li>
    </ol>
    <p>
      La proportion change radicalement si la piscine est{" "}
      <strong>chauff&eacute;e</strong> : l'&eacute;lectricit&eacute; peut alors
      d&eacute;passer 75 % de la d&eacute;pense annuelle, surtout si l'on
      utilise une <strong>r&eacute;sistance &eacute;lectrique</strong> plut&ocirc;t
      qu'une <strong>pompe &agrave; chaleur</strong>.
    </p>

    <h2>Consommation &eacute;lectrique d&eacute;taill&eacute;e</h2>

    <h3>Pompe de filtration</h3>
    <p>
      C'est l'&eacute;quipement le plus utilis&eacute; sur l'ann&eacute;e. Une
      pompe monovitesse conventionnelle de 0,75 &agrave; 1,5 CV sur une
      piscine de 50 m&sup3; consomme entre <strong>1 500 et 3 000 kWh par an</strong>{" "}
      en fonctionnant 6-8 h/jour en saison de baignade, selon la litt&eacute;rature
      technique du secteur.
    </p>
    <p>
      Avec un tarif moyen de <strong>0,18 &euro;/kWh</strong>, cela repr&eacute;sente{" "}
      <strong>270 &agrave; 540 &euro; par an</strong> uniquement pour la
      filtration. Une <strong>pompe &agrave; vitesse variable (VSD)</strong>{" "}
      bien programm&eacute;e r&eacute;duit cette consommation de{" "}
      <strong>60 &agrave; 80 %</strong>, ramenant la facture de filtration
      &agrave; 60-180 &euro;/an.
    </p>

    <h3>&Eacute;clairage</h3>
    <p>
      Les projecteurs LED immerg&eacute;s consomment 15-30 W contre 100-300 W
      pour les halog&egrave;nes anciens. Sur une piscine avec 2-4
      projecteurs et 3 h d'usage quotidien en saison, l'&eacute;clairage LED
      repr&eacute;sente &agrave; peine <strong>10-25 &euro;/an</strong>. Un
      syst&egrave;me halog&egrave;ne &eacute;quivalent peut atteindre 100-250 &euro;/an.
    </p>

    <h3>Pompe &agrave; chaleur (si pr&eacute;sente)</h3>
    <p>
      Si votre piscine est chauff&eacute;e, la pompe &agrave; chaleur concentre
      la majeure partie de la d&eacute;pense &eacute;lectrique. Sa performance
      se mesure par le <strong>COP</strong> (Coefficient de Performance), qui
      en r&eacute;gime piscine se situe entre <strong>4 et 6</strong> en
      conditions favorables (air &gt;15 &deg;C et eau entre 24 et 28 &deg;C).
      Autrement dit : par kWh &eacute;lectrique consomm&eacute; vous livrez 4-6
      kWh thermiques &agrave; l'eau.
    </p>
    <p>
      Une piscine de 50 m&sup3; chauff&eacute;e de mai &agrave; septembre avec
      couverture thermique consomme environ{" "}
      <strong>1 500-3 500 kWh/an</strong> de chauffage, selon l'&eacute;cart
      thermique cible et le climat. Sans couverture thermique, le chiffre
      peut doubler ou tripler.
    </p>

    <h2>Consommation d'eau</h2>
    <p>L'eau se perd par quatre voies :</p>
    <ul>
      <li><strong>&Eacute;vaporation</strong> : 5-8 mm/jour en zones chaudes en &eacute;t&eacute;, 2-4 mm/jour au printemps et &agrave; l'automne. Une piscine de 32 m&sup2; perd 160-256 litres/jour par &eacute;vaporation pure en plein &eacute;t&eacute;.</li>
      <li><strong>Contre-lavages du filtre</strong> : 300-800 litres par op&eacute;ration, typiquement 1-2 fois par mois selon la charge.</li>
      <li><strong>&Eacute;claboussures et entra&icirc;nement</strong> : 5-15 litres par baigneur et par s&eacute;ance.</li>
      <li><strong>Vidange ou renouvellement</strong> : la norme n'impose pas de vidange annuelle si la qualit&eacute; est maintenue, mais beaucoup d'utilisateurs renouvellent 10-30 % du volume tous les 2-3 ans.</li>
    </ul>
    <p>
      Avec un prix moyen de l'eau de <strong>1,5 &agrave; 3 &euro;/m&sup3;</strong>{" "}
      (tr&egrave;s variable selon commune), la facture annuelle d'eau d'une
      piscine r&eacute;sidentielle en zone chaude oscille entre{" "}
      <strong>30 et 90 &euro;/an</strong>.
    </p>

    <h2>Consommation de produits chimiques</h2>
    <p>Le budget produits d&eacute;pend du syst&egrave;me de traitement :</p>
    <ul>
      <li><strong>Chloration manuelle par hypochlorite</strong> ou chlore solide : 50-120 &euro;/an.</li>
      <li><strong>&Eacute;lectrolyseur au sel</strong> : moindre d&eacute;pense en chlore (le sel se compl&egrave;te ponctuellement), 30-70 &euro;/an. Le sel co&ucirc;te 0,15-0,30 &euro;/kg et une recharge annuelle typique est de 20-50 kg.</li>
      <li><strong>Syst&egrave;mes UV ou ozone</strong> : r&eacute;duisent le chlore de 30 &agrave; 70 %, mais ajoutent le co&ucirc;t de remplacement des lampes toutes les 8 000-12 000 h.</li>
    </ul>
    <p>
      Ajoutez <strong>r&eacute;gulateur de pH</strong> (10-25 &euro;/an),
      <strong> algicide</strong> ponctuel (10-20 &euro;/an) et{" "}
      <strong>floculant</strong> occasionnel.
    </p>

    <h2>Comment calculer le co&ucirc;t r&eacute;el de VOTRE piscine</h2>
    <p>L'estimation th&eacute;orique est utile, mais chaque installation est diff&eacute;rente. Il vous faut quatre donn&eacute;es :</p>
    <ol>
      <li><strong>Volume</strong> en m&sup3; (longueur &times; largeur &times; profondeur moyenne).</li>
      <li><strong>Puissance r&eacute;elle</strong> de la pompe de filtration en kW (pas le CV nominal &mdash; demandez la fiche technique).</li>
      <li><strong>Heures de filtration quotidiennes</strong> par saison.</li>
      <li><strong>Tarif &eacute;lectrique</strong> moyen que vous payez (&euro;/kWh, taxes et p&eacute;ages inclus).</li>
    </ol>
    <p>
      Avec ces donn&eacute;es vous pouvez utiliser les outils gratuits de{" "}
      <Link to="/calculadoras">/calculadoras</Link> pour estimer votre
      consommation annuelle et la comparer &agrave; la moyenne. Et si vous
      voulez une <strong>classification environnementale estim&eacute;e selon
      NF EN 17645</strong> de votre piscine, obtenez votre{" "}
      <strong>Fiche Technique gratuite</strong> sur{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> &mdash; vous recevrez
      votre <strong>PoolLex ID</strong> unique (format PLX-XXXXXXX) et une
      estimation de classe A &agrave; F.
    </p>

    <h2>Comment r&eacute;duire la consommation : 10 actions concr&egrave;tes</h2>
    <ol>
      <li><strong>Installez une couverture thermique manuelle ou motoris&eacute;e</strong>. R&eacute;duit l'&eacute;vaporation 60-70 % et les pertes de chaleur 40-60 %. Meilleur ROI sur piscines chauff&eacute;es.</li>
      <li><strong>Remplacez la pompe monovitesse par une VSD</strong>. &Eacute;conomie typique : 60-80 % sur la filtration.</li>
      <li><strong>Ajustez les heures de filtration</strong> au volume r&eacute;el et &agrave; la temp&eacute;rature de l'eau. R&egrave;gle indicative : heures de filtration quotidienne &asymp; temp&eacute;rature de l'eau (&deg;C) / 2.</li>
      <li><strong>Passez des halog&egrave;nes aux LED immerg&eacute;s</strong>. Consommation jusqu'&agrave; 10 fois moindre.</li>
      <li><strong>Si vous chauffez, utilisez une pompe &agrave; chaleur avec COP &gt;5</strong>, jamais une r&eacute;sistance &eacute;lectrique.</li>
      <li><strong>Programmez la filtration en heures creuses</strong> de votre tarif (typiquement 00:00-08:00 et week-ends).</li>
      <li><strong>Contr&ocirc;lez le pH automatiquement</strong> avec pompe doseuse : &eacute;vite la surconsommation de chlore et les pr&eacute;cipit&eacute;s.</li>
      <li><strong>V&eacute;rifiez les fuites</strong> chaque ann&eacute;e. Une fuite de 5 mm/jour se confond avec l'&eacute;vaporation mais co&ucirc;te 60 m&sup3;/an sur une piscine de 32 m&sup2;.</li>
      <li><strong>Nettoyez correctement le filtre</strong> &mdash; un filtre encrass&eacute; oblige la pompe &agrave; travailler plus longtemps avec une consommation sp&eacute;cifique plus &eacute;lev&eacute;e.</li>
      <li><strong>Isolez thermiquement le bassin</strong> si vous construisez ou r&eacute;habilitez. R&eacute;duit les pertes par conduction 15-25 %.</li>
    </ol>

    <h2>Certificat environnemental NF EN 17645 : ce que cela signifie pour votre consommation</h2>
    <p>
      Depuis 2023 existe la norme <strong>NF EN 17645:2023</strong>, qui
      &eacute;tablit une <strong>m&eacute;thodologie objective pour classer la
      durabilit&eacute; environnementale des piscines r&eacute;sidentielles
      ext&eacute;rieures enterr&eacute;es</strong>. Elle &eacute;value 8
      composants (structure, circuit hydraulique, couverture, pompe de
      filtration, syst&egrave;me de traitement, syst&egrave;me de chauffage,
      &eacute;clairage) et attribue une <strong>classe A &agrave; F</strong>,
      analogue aux &eacute;tiquettes &eacute;nerg&eacute;tiques d'&eacute;lectrom&eacute;nager.
    </p>
    <p>
      Une piscine <strong>classe A</strong> consomme, en termes comparables,
      entre <strong>3 et 5 fois moins</strong> de ressources annuelles qu'une
      &eacute;quivalente <strong>classe F</strong>. La diff&eacute;rence n'est
      pas dans l'usage mais dans l'&eacute;quipement et la combinaison des 8
      composants.
    </p>
    <p>
      Conna&icirc;tre votre classification est la premi&egrave;re &eacute;tape
      pour savoir <strong>o&ugrave; vous &ecirc;tes</strong> et{" "}
      <strong>o&ugrave; investir en priorit&eacute;</strong>. Sur{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> vous obtenez gratuitement
      une estimation avec votre <strong>PoolLex ID</strong> ; pour le sceau
      sign&eacute; par ing&eacute;nieur, voir le niveau 2 sur{" "}
      <Link to="/pool-check">/pool-check</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> applique la NF EN 17645:2023 avec un moteur
      de calcul qui &eacute;value les 8 composants et retourne une estimation
      de classe environnementale en moins de 2 minutes. Ne remplace pas la
      certification sign&eacute;e, mais donne un diagnostic honn&ecirc;te du
      point de d&eacute;part. Commencez gratuitement sur{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);
const Article1BodyDE = () => (
  <>
    <h2>Zusammenfassung: was ein privater Pool wirklich kostet</h2>
    <p>
      Der Betrieb eines privaten Freibad-Pools mit Einbaubecken kostet in
      Deutschland und &Ouml;sterreich im Jahresdurchschnitt{" "}
      <strong>zwischen 25 und 80 &euro; pro Monat</strong> im Jahr 2026. Die
      Spanne h&auml;ngt von vier Haupttreibern ab:{" "}
      <strong>Volumen und Fl&auml;che</strong>, <strong>lokales Klima</strong>,{" "}
      <strong>installierte Ausstattung</strong> und{" "}
      <strong>Nutzungsgewohnheiten</strong>.
    </p>
    <p>Zur Einordnung der Gr&ouml;&szlig;enordnungen:</p>
    <ul>
      <li><strong>Kleiner Pool (25-35 m&sup3;, unbeheizt, 4 Monate Saison)</strong>: 25-40 &euro;/Monat im Jahresdurchschnitt.</li>
      <li><strong>Mittlerer Pool (40-60 m&sup3;, unbeheizt, 5 Monate Saison)</strong>: 40-60 &euro;/Monat.</li>
      <li><strong>Mittlerer Pool mit W&auml;rmepumpe beheizt (8-10 Monate Saison)</strong>: 60-100 &euro;/Monat.</li>
      <li><strong>Gro&szlig;er Pool (&gt;60 m&sup3;) beheizt, ohne Thermoabdeckung</strong>: 80-140 &euro;/Monat oder mehr.</li>
    </ul>
    <p>
      Ob eine <strong>motorisierte Abdeckung</strong> vorhanden ist, die{" "}
      <strong>Variante der Filterpumpe</strong> und das{" "}
      <strong>Wasseraufbereitungssystem</strong> k&ouml;nnen die Jahresrechnung
      um mehr als 500 &euro; verschieben, ohne dass sich die Nutzung &auml;ndert.
    </p>
    <p>
      Bevor wir ins Detail gehen: ein Pool ist kein Haushaltsger&auml;t, sondern
      ein <strong>hydraulisches und thermisches System</strong>, das Wasser
      durch Verdunstung und W&auml;rme durch Konvektion und Strahlung verliert.
      Echte Einsparungen entstehen dadurch, dass man beide Verluste reduziert.
    </p>

    <h2>Die 4 gro&szlig;en Kostenbl&ouml;cke eines Pools</h2>
    <p>Die j&auml;hrlichen Kosten eines privaten Pools verteilen sich typischerweise so:</p>
    <ol>
      <li><strong>Strom</strong> &mdash; 55 bis 70 % der Gesamtkosten. Filterpumpe, Beleuchtung und W&auml;rmepumpe (falls vorhanden).</li>
      <li><strong>Nachf&uuml;llwasser</strong> &mdash; 10 bis 20 %. Ausgleich f&uuml;r Verdunstung, R&uuml;cksp&uuml;lung und Spritzverluste.</li>
      <li><strong>Chemikalien</strong> &mdash; 10 bis 20 %. Chlor, pH-Regler, Algizid oder Salz bei Salzelektrolyse.</li>
      <li><strong>Regelm&auml;&szlig;ige Wartung</strong> &mdash; 5 bis 15 %. Saisonstart, kleinere Reparaturen, Ersatzteile (Dichtungen, D&uuml;sen, LED-Leuchten).</li>
    </ol>
    <p>
      Bei <strong>beheizten</strong> Pools verschiebt sich das Verh&auml;ltnis
      deutlich: der Stromanteil kann 75 % der Jahreskosten &uuml;bersteigen,
      besonders wenn statt einer <strong>W&auml;rmepumpe</strong> ein{" "}
      <strong>elektrischer Heizstab</strong> zum Einsatz kommt.
    </p>

    <h2>Stromverbrauch im Detail</h2>

    <h3>Filterpumpe</h3>
    <p>
      Sie l&auml;uft mehr Stunden im Jahr als jede andere Komponente. Eine
      konventionelle einstufige Pumpe mit 0,75 bis 1,5 PS f&uuml;r einen 50 m&sup3;
      Pool verbraucht bei 6-8 h/Tag in der Badesaison{" "}
      <strong>zwischen 1.500 und 3.000 kWh pro Jahr</strong>, entsprechend der
      Fachliteratur.
    </p>
    <p>
      Bei einem durchschnittlichen Tarif von <strong>0,18 &euro;/kWh</strong>{" "}
      sind das <strong>270 bis 540 &euro; pro Jahr</strong> nur f&uuml;r die
      Filtration. Eine gut programmierte{" "}
      <strong>drehzahlgeregelte Pumpe (VSD)</strong> senkt diesen Verbrauch um{" "}
      <strong>60 bis 80 %</strong> und dr&uuml;ckt die Filterrechnung auf
      60-180 &euro;/Jahr.
    </p>

    <h3>Beleuchtung</h3>
    <p>
      Unterwasser-LED-Leuchten verbrauchen 15-30 W gegen&uuml;ber 100-300 W bei
      Halogen. Bei einem Pool mit 2-4 Leuchten und 3 h t&auml;glicher Nutzung
      in der Saison betr&auml;gt der LED-Verbrauch nur{" "}
      <strong>10-25 &euro; pro Jahr</strong>. Ein vergleichbares Halogensystem
      kann 100-250 &euro; pro Jahr erreichen.
    </p>

    <h3>W&auml;rmepumpe (falls vorhanden)</h3>
    <p>
      Bei beheizten Pools konzentriert die W&auml;rmepumpe den Gro&szlig;teil
      des Stromverbrauchs. Ihre Effizienz misst der <strong>COP</strong>{" "}
      (Coefficient of Performance), der im Poolbetrieb bei g&uuml;nstigen
      Bedingungen (Lufttemperatur &gt;15 &deg;C, Wasser 24-28 &deg;C) zwischen{" "}
      <strong>4 und 6</strong> liegt. Das hei&szlig;t: pro kWh Strom werden 4-6
      kWh thermische Energie ans Wasser abgegeben.
    </p>
    <p>
      Ein 50 m&sup3; Pool, beheizt von Mai bis September mit Thermoabdeckung,
      verbraucht rund <strong>1.500-3.500 kWh/Jahr</strong> zum Heizen, je nach
      Zieltemperatur und Klima. Ohne Abdeckung kann sich der Wert verdoppeln
      oder verdreifachen.
    </p>

    <h2>Wasserverbrauch</h2>
    <p>Wasser geht auf vier Wegen verloren:</p>
    <ul>
      <li><strong>Verdunstung</strong>: 5-8 mm/Tag in warmen Regionen im Sommer, 2-4 mm/Tag im Fr&uuml;hjahr und Herbst. Ein 32 m&sup2; Pool verliert im Hochsommer 160-256 Liter/Tag allein durch Verdunstung.</li>
      <li><strong>R&uuml;cksp&uuml;lung des Filters</strong>: 300-800 Liter pro Vorgang, typischerweise 1-2 Mal pro Monat je nach Schmutzanfall.</li>
      <li><strong>Spritzverluste und Verschleppung</strong>: 5-15 Liter pro Badegast und Nutzung.</li>
      <li><strong>Ablassen oder Erneuerung</strong>: Die Norm schreibt kein j&auml;hrliches Ablassen vor, wenn die Wasserqualit&auml;t stimmt, aber viele Betreiber erneuern alle 2-3 Jahre 10-30 % des Volumens.</li>
    </ul>
    <p>
      Bei einem durchschnittlichen Wasserpreis von <strong>1,5 bis 3 &euro;/m&sup3;</strong>{" "}
      (stark kommunal variabel) liegt die j&auml;hrliche Wasserrechnung eines
      privaten Pools in warmen Zonen zwischen{" "}
      <strong>30 und 90 &euro; pro Jahr</strong>.
    </p>

    <h2>Chemieverbrauch</h2>
    <p>Die Chemiekosten h&auml;ngen vom Aufbereitungssystem ab:</p>
    <ul>
      <li><strong>Manuelle Chlorung mit Hypochlorit</strong> oder festem Chlor: 50-120 &euro;/Jahr.</li>
      <li><strong>Salzelektrolyse</strong>: geringerer Chlorverbrauch (Salz wird punktuell nachgef&uuml;llt), 30-70 &euro;/Jahr. Salz kostet 0,15-0,30 &euro;/kg, typische Jahresnachf&uuml;llung 20-50 kg.</li>
      <li><strong>UV- oder Ozonsysteme</strong>: senken den Chlorverbrauch um 30-70 %, verursachen aber Lampenaustauschkosten alle 8.000-12.000 h.</li>
    </ul>
    <p>
      Dazu kommen <strong>pH-Regler</strong> (10-25 &euro;/Jahr),
      gelegentliches <strong>Algizid</strong> (10-20 &euro;/Jahr) und
      gelegentliches <strong>Flockungsmittel</strong>.
    </p>

    <h2>So berechnen Sie die tats&auml;chlichen Kosten IHRES Pools</h2>
    <p>Theoretische Sch&auml;tzungen helfen, aber jede Anlage ist anders. Sie brauchen vier Datenpunkte:</p>
    <ol>
      <li><strong>Volumen</strong> in m&sup3; (L&auml;nge &times; Breite &times; mittlere Tiefe).</li>
      <li><strong>Reale Leistung</strong> der Filterpumpe in kW (nicht die nominale PS &mdash; das Datenblatt anfordern).</li>
      <li><strong>T&auml;gliche Filterstunden</strong> pro Saison.</li>
      <li><strong>Durchschnittlicher Stromtarif</strong>, den Sie zahlen (&euro;/kWh, inkl. Steuern und Netzentgelte).</li>
    </ol>
    <p>
      Mit diesen Daten k&ouml;nnen Sie die kostenlosen Tools auf{" "}
      <Link to="/calculadoras">/calculadoras</Link> nutzen, um Ihren
      Jahresverbrauch zu sch&auml;tzen und zu vergleichen. Wenn Sie eine{" "}
      <strong>gesch&auml;tzte Umweltklasse nach DIN EN 17645</strong> f&uuml;r
      Ihren Pool wollen, holen Sie sich das kostenlose{" "}
      <strong>technische Datenblatt</strong> auf{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> &mdash; Sie erhalten Ihre
      eindeutige <strong>PoolLex ID</strong> (Format PLX-XXXXXXX) und eine
      Klassensch&auml;tzung von A bis F.
    </p>

    <h2>So senken Sie den Verbrauch: 10 konkrete Ma&szlig;nahmen</h2>
    <ol>
      <li><strong>Manuelle oder motorisierte Thermoabdeckung installieren</strong>. Reduziert Verdunstung 60-70 % und W&auml;rmeverluste 40-60 %. Beste Amortisation bei beheizten Pools.</li>
      <li><strong>Einstufige Pumpe durch VSD ersetzen</strong>. Typische Einsparung: 60-80 % beim Filterstrom.</li>
      <li><strong>Filterstunden</strong> an reales Volumen und Wassertemperatur anpassen. Faustregel: t&auml;gliche Filterstunden &asymp; Wassertemperatur (&deg;C) / 2.</li>
      <li><strong>Halogen durch Unterwasser-LED ersetzen</strong>. Bis zu 10-fach geringerer Verbrauch.</li>
      <li><strong>Beim Heizen W&auml;rmepumpe mit COP &gt;5</strong> verwenden, nie einen Heizstab.</li>
      <li><strong>Filterung in Nebentarifzeiten</strong> Ihres Stromvertrags planen (typischerweise 00:00-08:00 und Wochenenden).</li>
      <li><strong>pH automatisch regeln</strong> mit Dosierpumpe: verhindert Chlor&uuml;berdosierung und Ausf&auml;llungen.</li>
      <li><strong>J&auml;hrlich auf Undichtigkeiten pr&uuml;fen</strong>. Ein kleines Leck von 5 mm/Tag ist von Verdunstung nicht zu unterscheiden, kostet aber 60 m&sup3;/Jahr bei einem 32 m&sup2; Pool.</li>
      <li><strong>Filter richtig reinigen</strong> &mdash; ein zugesetzter Filter zwingt die Pumpe zu l&auml;ngerem Betrieb bei h&ouml;herem spezifischem Verbrauch.</li>
      <li><strong>Beckenh&uuml;lle thermisch d&auml;mmen</strong> beim Neubau oder bei einer Sanierung. Reduziert Leitungsverluste um 15-25 %.</li>
    </ol>

    <h2>Umweltzertifikat DIN EN 17645: was es f&uuml;r Ihren Verbrauch bedeutet</h2>
    <p>
      Seit 2023 existiert die Norm <strong>DIN EN 17645:2023</strong>, die eine{" "}
      <strong>objektive Methodik zur Bewertung der Umweltnachhaltigkeit
      privater Freibad-Pools</strong> festlegt. Sie bewertet 8 Komponenten
      (Beckenh&uuml;lle, Hydraulikkreislauf, Abdeckung, Filterpumpe,
      Wasseraufbereitung, Heizung, Beleuchtung, Steuerung) und vergibt eine{" "}
      <strong>Klasse von A bis F</strong>, analog zum Energielabel f&uuml;r
      Haushaltsger&auml;te.
    </p>
    <p>
      Ein Pool der <strong>Klasse A</strong> verbraucht auf vergleichbarer
      Basis <strong>3- bis 5-mal weniger</strong> j&auml;hrliche Ressourcen als
      ein &auml;quivalenter <strong>Klasse F</strong>. Der Unterschied liegt
      nicht in der Nutzung, sondern in der Ausstattung und deren Kombination.
    </p>
    <p>
      Ihre Klasse zu kennen ist der erste Schritt, um zu wissen,{" "}
      <strong>wo Sie stehen</strong> und{" "}
      <strong>wo Investitionen den gr&ouml;&szlig;ten Effekt haben</strong>.
      Unter <Link to="/ficha-tecnica">/ficha-tecnica</Link> erhalten Sie eine
      kostenlose Sch&auml;tzung mit Ihrer <strong>PoolLex ID</strong>; f&uuml;r
      das vom Ingenieur signierte Siegel siehe Stufe 2 unter{" "}
      <Link to="/pool-check">/pool-check</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> wendet die DIN EN 17645:2023 mit einer
      Berechnungsmaschine an, die die 8 Komponenten bewertet und in unter 2
      Minuten eine gesch&auml;tzte Umweltklasse liefert. Sie ersetzt keine
      signierte Zertifizierung, gibt aber eine ehrliche Diagnose als
      Ausgangspunkt. Kostenlos starten unter{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);
const Article1BodyIT = () => (
  <>
    <h2>Riepilogo: quanto costa davvero una piscina residenziale</h2>
    <p>
      Mantenere una piscina residenziale esterna interrata costa, in media
      annualizzata, <strong>tra 25 e 80 &euro; al mese</strong> nel 2026.
      Questa fascia dipende da quattro fattori principali:{" "}
      <strong>volume e superficie</strong>, <strong>clima locale</strong>,{" "}
      <strong>equipaggiamento installato</strong> e{" "}
      <strong>abitudini d'uso</strong>.
    </p>
    <p>Per ordinare gli ordini di grandezza:</p>
    <ul>
      <li><strong>Piscina piccola (25-35 m&sup3;, non riscaldata, stagione di 4 mesi)</strong>: 25-40 &euro;/mese annualizzato.</li>
      <li><strong>Piscina media (40-60 m&sup3;, non riscaldata, stagione di 5 mesi)</strong>: 40-60 &euro;/mese.</li>
      <li><strong>Piscina media riscaldata con pompa di calore (stagione 8-10 mesi)</strong>: 60-100 &euro;/mese.</li>
      <li><strong>Piscina grande (&gt;60 m&sup3;) riscaldata, senza copertura termica</strong>: 80-140 &euro;/mese o pi&ugrave;.</li>
    </ul>
    <p>
      La presenza o meno di una <strong>copertura motorizzata</strong>, la{" "}
      <strong>variante della pompa di filtrazione</strong> e il{" "}
      <strong>sistema di trattamento dell'acqua</strong> possono spostare la
      bolletta annuale di oltre 500 &euro; senza toccare l'uso.
    </p>
    <p>
      Prima di entrare nel dettaglio, va compresa la logica: una piscina non
      &egrave; un elettrodomestico, &egrave; un{" "}
      <strong>sistema idraulico e termico</strong> che perde acqua per
      evaporazione e calore per convezione e irraggiamento. Ogni risparmio
      reale passa dalla riduzione di queste due perdite.
    </p>

    <h2>Le 4 grandi voci di spesa di una piscina</h2>
    <p>Il costo totale annuo di una piscina residenziale si distribuisce tipicamente cos&igrave;:</p>
    <ol>
      <li><strong>Elettricit&agrave;</strong> &mdash; 55-70 % del totale. Pompa di filtrazione, illuminazione e pompa di calore se presente.</li>
      <li><strong>Acqua di reintegro</strong> &mdash; 10-20 %. Compensa evaporazione, controlavaggi e spruzzi.</li>
      <li><strong>Prodotti chimici</strong> &mdash; 10-20 %. Cloro, regolatore di pH, alghicida o sale se c'&egrave; elettrolisi salina.</li>
      <li><strong>Manutenzione periodica</strong> &mdash; 5-15 %. Messa in marcia stagionale, riparazioni minori, ricambi (guarnizioni, bocchette, faretti LED).</li>
    </ol>
    <p>
      Le proporzioni cambiano radicalmente se la piscina &egrave;{" "}
      <strong>riscaldata</strong>: in quel caso l'elettricit&agrave; pu&ograve;
      superare il 75 % della spesa annuale, specialmente se si utilizza una{" "}
      <strong>resistenza elettrica</strong> invece di una{" "}
      <strong>pompa di calore</strong>.
    </p>

    <h2>Consumo elettrico in dettaglio</h2>

    <h3>Pompa di filtrazione</h3>
    <p>
      &Egrave; l'apparecchio con pi&ugrave; ore di funzionamento all'anno. Una
      pompa monovelocit&agrave; convenzionale da 0,75-1,5 CV su una piscina
      di 50 m&sup3; consuma tra <strong>1.500 e 3.000 kWh all'anno</strong>{" "}
      funzionando 6-8 h/giorno in stagione balneare, secondo la letteratura
      tecnica del settore.
    </p>
    <p>
      Con una tariffa media di <strong>0,18 &euro;/kWh</strong>, si tratta di{" "}
      <strong>270-540 &euro; all'anno</strong> solo per la filtrazione. Una{" "}
      <strong>pompa a velocit&agrave; variabile (VSD)</strong> ben programmata
      riduce questo consumo del <strong>60-80 %</strong>, portando la bolletta
      di filtrazione a 60-180 &euro;/anno.
    </p>

    <h3>Illuminazione</h3>
    <p>
      I faretti LED sommersi consumano 15-30 W contro i 100-300 W degli alogeni
      tradizionali. Su una piscina con 2-4 faretti e 3 h di uso quotidiano in
      stagione, l'illuminazione LED costa appena{" "}
      <strong>10-25 &euro;/anno</strong>. Un sistema alogeno equivalente pu&ograve;
      arrivare a 100-250 &euro;/anno.
    </p>

    <h3>Pompa di calore (se presente)</h3>
    <p>
      Se la piscina &egrave; riscaldata, la pompa di calore concentra la
      maggior parte della spesa elettrica. La sua efficienza si misura con il{" "}
      <strong>COP</strong> (Coefficient of Performance), che in regime piscina
      si colloca tra <strong>4 e 6</strong> in condizioni favorevoli (aria
      &gt;15 &deg;C e acqua 24-28 &deg;C). In altre parole: per ogni kWh
      elettrico consegni 4-6 kWh termici all'acqua.
    </p>
    <p>
      Una piscina di 50 m&sup3; riscaldata da maggio a settembre con copertura
      termica consuma circa <strong>1.500-3.500 kWh/anno</strong> di
      riscaldamento, in base al salto termico obiettivo e al clima. Senza
      copertura il numero pu&ograve; raddoppiare o triplicare.
    </p>

    <h2>Consumo di acqua</h2>
    <p>L'acqua si perde per quattro vie:</p>
    <ul>
      <li><strong>Evaporazione</strong>: 5-8 mm/giorno in zone calde d'estate, 2-4 mm/giorno in primavera e autunno. Una piscina di 32 m&sup2; perde 160-256 litri/giorno per pura evaporazione in piena estate.</li>
      <li><strong>Controlavaggi del filtro</strong>: 300-800 litri per operazione, tipicamente 1-2 volte al mese secondo il carico di sporco.</li>
      <li><strong>Spruzzi e trascinamento</strong>: 5-15 litri per bagnante e sessione.</li>
      <li><strong>Svuotamento o rinnovo</strong>: la norma non impone lo svuotamento annuale se la qualit&agrave; dell'acqua &egrave; mantenuta, ma molti utenti rinnovano parzialmente il 10-30 % del volume ogni 2-3 anni.</li>
    </ul>
    <p>
      Con un prezzo medio dell'acqua di <strong>1,5-3 &euro;/m&sup3;</strong>{" "}
      (molto variabile tra comuni), la bolletta idrica annua di una piscina
      residenziale in zona calda oscilla tra{" "}
      <strong>30 e 90 &euro;/anno</strong>.
    </p>

    <h2>Consumo di prodotti chimici</h2>
    <p>Il budget chimico dipende dal sistema di trattamento:</p>
    <ul>
      <li><strong>Clorazione manuale con ipoclorito</strong> o cloro solido: 50-120 &euro;/anno.</li>
      <li><strong>Elettrolisi salina</strong>: minor spesa in cloro (il sale si reintegra puntualmente), 30-70 &euro;/anno. Il sale costa 0,15-0,30 &euro;/kg e una ricarica annuale tipica &egrave; 20-50 kg.</li>
      <li><strong>Sistemi UV o ozono</strong>: riducono il consumo di cloro del 30-70 %, ma aggiungono il costo di sostituzione delle lampade ogni 8.000-12.000 h.</li>
    </ul>
    <p>
      A questo si aggiungono <strong>regolatore di pH</strong> (10-25
      &euro;/anno), <strong>alghicida</strong> occasionale (10-20 &euro;/anno) e{" "}
      <strong>flocculante</strong> saltuario.
    </p>

    <h2>Come calcolare il costo reale della TUA piscina</h2>
    <p>La stima teorica &egrave; utile, ma ogni installazione &egrave; diversa. Servono quattro dati:</p>
    <ol>
      <li><strong>Volume</strong> della piscina in m&sup3; (lunghezza &times; larghezza &times; profondit&agrave; media).</li>
      <li><strong>Potenza reale</strong> della pompa di filtrazione in kW (non il CV nominale &mdash; chiedi la scheda tecnica).</li>
      <li><strong>Ore giornaliere di filtrazione</strong> per stagione.</li>
      <li><strong>Tariffa elettrica</strong> media che paghi (&euro;/kWh, comprensiva di oneri e imposte).</li>
    </ol>
    <p>
      Con questi dati puoi usare gli strumenti gratuiti di{" "}
      <Link to="/calculadoras">/calculadoras</Link> per stimare il consumo
      annuo e confrontarlo con la media. E se vuoi una{" "}
      <strong>classificazione ambientale stimata secondo UNI EN 17645</strong>{" "}
      della tua piscina, ottieni la tua{" "}
      <strong>Scheda Tecnica gratuita</strong> su{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> &mdash; riceverai il tuo{" "}
      <strong>PoolLex ID</strong> univoco (formato PLX-XXXXXXX) e una stima di
      classe da A a F.
    </p>

    <h2>Come ridurre il consumo: 10 azioni concrete</h2>
    <ol>
      <li><strong>Installa una copertura termica manuale o motorizzata</strong>. Riduce l'evaporazione 60-70 % e le perdite di calore 40-60 %. Miglior ROI su piscine riscaldate.</li>
      <li><strong>Sostituisci la pompa monovelocit&agrave; con una VSD</strong>. Risparmio tipico: 60-80 % sul consumo di filtrazione.</li>
      <li><strong>Regola le ore di filtrazione</strong> in base al volume reale e alla temperatura dell'acqua. Regola indicativa: ore di filtrazione giornaliera &asymp; temperatura dell'acqua (&deg;C) / 2.</li>
      <li><strong>Passa dagli alogeni ai LED sommersi</strong>. Consumo fino a 10 volte inferiore.</li>
      <li><strong>Se riscaldi, usa pompa di calore con COP &gt;5</strong>, mai resistenza elettrica.</li>
      <li><strong>Programma la filtrazione in fasce orarie a basso costo</strong> (tipicamente 00:00-08:00 e fine settimana).</li>
      <li><strong>Controlla il pH automaticamente</strong> con pompa dosatrice: evita il sovraconsumo di cloro e i precipitati.</li>
      <li><strong>Verifica le perdite</strong> ogni anno. Una piccola perdita di 5 mm/giorno si confonde con l'evaporazione ma costa 60 m&sup3;/anno su una piscina di 32 m&sup2;.</li>
      <li><strong>Pulisci correttamente il filtro</strong> &mdash; un filtro intasato obbliga la pompa a lavorare pi&ugrave; ore con consumo specifico maggiore.</li>
      <li><strong>Isola termicamente la vasca</strong> in costruzione o in ristrutturazione. Riduce le perdite per conduzione del 15-25 %.</li>
    </ol>

    <h2>Certificato ambientale UNI EN 17645: cosa significa per il tuo consumo</h2>
    <p>
      Dal 2023 esiste la norma <strong>UNI EN 17645:2023</strong>, che stabilisce
      una <strong>metodologia oggettiva per classificare la sostenibilit&agrave;
      ambientale delle piscine residenziali esterne interrate</strong>. Valuta
      8 componenti (struttura, circuito idraulico, copertura, pompa di
      filtrazione, sistema di trattamento dell'acqua, sistema di
      riscaldamento, illuminazione, controllo) e assegna una{" "}
      <strong>classe da A a F</strong>, analoga alle etichette energetiche
      degli elettrodomestici.
    </p>
    <p>
      Una piscina <strong>classe A</strong> consuma, in termini confrontabili,
      tra <strong>3 e 5 volte meno</strong> risorse annue di una equivalente{" "}
      <strong>classe F</strong>. La differenza non &egrave; nell'uso, ma
      nell'equipaggiamento e nella combinazione degli 8 componenti.
    </p>
    <p>
      Conoscere la propria classificazione &egrave; il primo passo per capire{" "}
      <strong>dove sei</strong> e <strong>dove conviene investire prima</strong>.
      Su <Link to="/ficha-tecnica">/ficha-tecnica</Link> ottieni gratis una
      stima con il tuo <strong>PoolLex ID</strong>; per il sigillo firmato da
      ingegnere, consulta il livello 2 su{" "}
      <Link to="/pool-check">/pool-check</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> applica la UNI EN 17645:2023 con un motore
      di calcolo che valuta gli 8 componenti e restituisce una stima di classe
      ambientale in meno di 2 minuti. Non sostituisce la certificazione
      firmata, ma fornisce una diagnosi onesta di dove partire. Inizia gratis
      su <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

// ============================================================================
// ARTICLE 2 — Certificat EN 17645
// ============================================================================

const Article2BodyES = () => (
  <>
    <h2>Qu&eacute; es UNE-EN 17645:2023</h2>
    <p>
      La <strong>UNE-EN 17645:2023</strong> es la adopci&oacute;n
      espa&ntilde;ola de la norma europea <strong>EN 17645:2023</strong>,
      publicada por el <strong>Comit&eacute; Europeo de Normalizaci&oacute;n (CEN)</strong>{" "}
      y titulada, en su versi&oacute;n oficial,{" "}
      <em>&laquo;Piscinas &mdash; Requisitos para la evaluaci&oacute;n de la sostenibilidad ambiental de piscinas residenciales exteriores enterradas&raquo;</em>.
    </p>
    <p>
      Se trata de la <strong>primera norma europea armonizada</strong> que
      establece una metodolog&iacute;a objetiva y reproducible para clasificar
      el desempe&ntilde;o ambiental de una piscina residencial. Antes de 2023
      exist&iacute;an certificaciones privadas, sellos de fabricantes y
      auditor&iacute;as puntuales, pero no hab&iacute;a un lenguaje com&uacute;n
      ni una escala comparable entre instalaciones.
    </p>
    <p>La norma se enmarca dentro del ecosistema regulatorio europeo, alineada con:</p>
    <ul>
      <li><strong>Reglamento (UE) 305/2011</strong> de productos de construcci&oacute;n (marcado CE de componentes).</li>
      <li><strong>Reglamento (UE) 813/2013</strong> sobre requisitos de dise&ntilde;o ecol&oacute;gico de aparatos de calefacci&oacute;n (aplicable a bombas de calor).</li>
      <li><strong>Reglamento (UE) 547/2012</strong> sobre requisitos de dise&ntilde;o ecol&oacute;gico de bombas hidr&aacute;ulicas.</li>
    </ul>
    <p>
      Es una norma <strong>voluntaria</strong>: ning&uacute;n reglamento
      estatal espa&ntilde;ol obliga hoy a certificar la piscina. Pero su
      adopci&oacute;n est&aacute; creciendo en varios frentes:{" "}
      <strong>subvenciones auton&oacute;micas</strong>,{" "}
      <strong>ordenanzas municipales de eficiencia h&iacute;drica</strong>,{" "}
      <strong>operaciones inmobiliarias</strong>,{" "}
      <strong>auditor&iacute;as corporativas</strong> y{" "}
      <strong>programas de instaladores homologados</strong>.
    </p>

    <h2>Los 8 componentes que eval&uacute;a la norma</h2>
    <p>
      UNE-EN 17645:2023 eval&uacute;a la piscina como{" "}
      <strong>sistema integrado</strong>, no como suma de piezas. La
      metodolog&iacute;a punt&uacute;a 8 componentes con pesos distintos
      seg&uacute;n su impacto ambiental agregado:
    </p>
    <ol>
      <li><strong>Estructura del vaso</strong>: material, aislamiento, durabilidad esperada, coeficientes de transmisi&oacute;n t&eacute;rmica.</li>
      <li><strong>Circuito hidr&aacute;ulico</strong>: dimensionado, p&eacute;rdidas de carga, aislamiento de tuber&iacute;as, disposici&oacute;n de skimmers y retornos.</li>
      <li><strong>Cubierta motorizada o manual</strong>: presencia o no, tipo (isot&eacute;rmica, deck, l&aacute;mina flotante), grado de estanqueidad al vapor.</li>
      <li><strong>Bomba de filtraci&oacute;n</strong>: tecnolog&iacute;a (monovelocidad, biveloz, VSD), rendimiento hidr&aacute;ulico, control asociado.</li>
      <li><strong>Sistema de tratamiento de agua</strong>: clorador salino, dosificaci&oacute;n manual, UV, ozono, o combinaciones h&iacute;bridas.</li>
      <li><strong>Sistema de calefacci&oacute;n</strong>: bomba de calor, intercambiador con caldera existente, resistencia el&eacute;ctrica, solar t&eacute;rmica.</li>
      <li><strong>Iluminaci&oacute;n</strong>: tipo de foco (LED, hal&oacute;geno, fluorescente), n&uacute;mero, potencia total instalada y control.</li>
      <li><strong>Sistema de control y automatismos</strong>: dosificaci&oacute;n autom&aacute;tica, medici&oacute;n pH/ORP, programaci&oacute;n horaria, monitorizaci&oacute;n remota.</li>
    </ol>
    <p>
      Cada componente aporta puntos a un <strong>&iacute;ndice global</strong>,
      y el &iacute;ndice se traduce en una <strong>clase A a F</strong> con
      umbrales publicados en la norma.
    </p>

    <h2>Escala A-F: qu&eacute; significa cada clase</h2>
    <p>La escala funciona de forma an&aacute;loga a las etiquetas energ&eacute;ticas de electrodom&eacute;sticos:</p>
    <ul>
      <li><strong>Clase A</strong>: piscina de referencia en eficiencia ambiental. Cubierta motorizada, bomba VSD, bomba de calor de alto COP, tratamiento salino con automatizaci&oacute;n, iluminaci&oacute;n LED, aislamiento del vaso. Representa el estado del arte disponible en el mercado.</li>
      <li><strong>Clase B</strong>: muy eficiente, con la mayor&iacute;a de componentes optimizados. Suele faltar 1 o 2 elementos para llegar a A.</li>
      <li><strong>Clase C</strong>: eficiencia notable. Piscina moderna con equipamiento actual pero sin todas las optimizaciones (por ejemplo, sin cubierta motorizada o sin VSD).</li>
      <li><strong>Clase D</strong>: eficiencia est&aacute;ndar. Piscina correcta pero con equipos convencionales. Es la clase m&aacute;s frecuente en obra nueva sin ambici&oacute;n ambiental espec&iacute;fica.</li>
      <li><strong>Clase E</strong>: eficiencia mejorable. Equipos antiguos o subdimensionados, ausencia de cubierta y control manual.</li>
      <li><strong>Clase F</strong>: piscina energ&eacute;ticamente ineficiente. Bomba monovelocidad, iluminaci&oacute;n hal&oacute;gena, sin cubierta, sin automatismo. T&iacute;picamente parque de m&aacute;s de 20 a&ntilde;os sin renovaci&oacute;n.</li>
    </ul>
    <p>
      Los <strong>datos preliminares</strong> del sector sugieren que el parque
      residencial espa&ntilde;ol actual se concentra en{" "}
      <strong>clases D y E</strong>, con menos del 5-10 % del parque alcanzando
      de forma natural la clase A. La buena noticia: <strong>una reforma parcial</strong>{" "}
      (cubierta + bomba VSD + LED) suele mover una piscina de E a C en una
      sola intervenci&oacute;n.
    </p>

    <h2>Estimaci&oacute;n gratuita vs. certificado firmado</h2>
    <p>Aqu&iacute; conviene distinguir dos productos con prop&oacute;sitos distintos:</p>

    <h3>Ficha T&eacute;cnica gratuita (nivel 1)</h3>
    <p>
      Es una <strong>estimaci&oacute;n</strong> basada en los datos que el
      usuario declara sobre su piscina: volumen, superficie, equipamiento
      instalado, h&aacute;bitos. El motor de PoolLex.io eval&uacute;a los 8
      componentes con los datos disponibles y devuelve una{" "}
      <strong>clase estimada A-F</strong>, junto con un{" "}
      <strong>PoolLex ID &uacute;nico</strong> (formato PLX-XXXXXXX) que
      identifica la piscina en la plataforma.
    </p>
    <p><strong>Sirve para</strong>: diagn&oacute;stico r&aacute;pido, comparaci&oacute;n con otras piscinas, planificar reformas, entender por d&oacute;nde empezar.</p>
    <p><strong>No sirve para</strong>: presentar ante administraciones, notarios o aseguradoras. Es orientativa, no certificada.</p>

    <h3>Sello firmado por ingeniero industrial colegiado (nivel 2)</h3>
    <p>
      Es un <strong>documento t&eacute;cnico</strong> revisado y firmado por
      un <strong>ingeniero industrial colegiado</strong> que ha verificado la
      documentaci&oacute;n aportada (fichas t&eacute;cnicas de equipos,
      certificados de instalaci&oacute;n, fotos, mediciones). Tiene validez
      legal y t&eacute;cnica.
    </p>
    <p><strong>Sirve para</strong>: auditor&iacute;a formal, operaciones de compraventa, subvenciones, licencias municipales que lo requieran, certificaciones inmobiliarias, informes periciales.</p>

    <h2>C&oacute;mo obtener la estimaci&oacute;n en 2 minutos</h2>
    <p>El proceso est&aacute; dise&ntilde;ado para ser r&aacute;pido y gratuito:</p>
    <ol>
      <li>Accede a <Link to="/ficha-tecnica">/ficha-tecnica</Link>.</li>
      <li>Introduce los datos b&aacute;sicos de tu piscina: <strong>volumen</strong> (m&sup3;), <strong>superficie</strong> (m&sup2;), <strong>a&ntilde;o de construcci&oacute;n</strong>, <strong>ubicaci&oacute;n</strong> (para c&aacute;lculo de clima).</li>
      <li>Indica el <strong>equipamiento</strong> instalado: tipo de bomba, si hay cubierta, sistema de tratamiento, calefacci&oacute;n, iluminaci&oacute;n.</li>
      <li>Env&iacute;a el formulario. El motor calcula tu <strong>clase estimada A-F</strong> y te asigna un <strong>PoolLex ID</strong> &uacute;nico.</li>
      <li>Recibes tu <strong>Ficha T&eacute;cnica en PDF</strong> con la clasificaci&oacute;n, un desglose por componentes y <strong>recomendaciones priorizadas</strong> de mejora.</li>
    </ol>
    <p>Todo el proceso lleva <strong>menos de 2 minutos</strong> si tienes los datos a mano.</p>

    <h2>Cu&aacute;ndo pedir el sello firmado</h2>
    <p>El sello firmado es &uacute;til, y a veces necesario, en varios escenarios:</p>
    <ul>
      <li><strong>Compraventa inmobiliaria</strong>: valorar la piscina como activo eficiente puede a&ntilde;adir entre un 2 y un 5 % al valor total del inmueble en operaciones donde el comprador prioriza sostenibilidad.</li>
      <li><strong>Auditor&iacute;as corporativas</strong>: empresas con reporting ESG que incluyen instalaciones deportivas o de ocio.</li>
      <li><strong>Subvenciones auton&oacute;micas</strong> de rehabilitaci&oacute;n energ&eacute;tica que empiecen a considerar la piscina.</li>
      <li><strong>Ordenanzas municipales</strong> que incorporen la clasificaci&oacute;n como criterio para licencia.</li>
      <li><strong>Litigios t&eacute;cnicos</strong> o siniestros donde haya que demostrar el estado de la instalaci&oacute;n en fecha determinada.</li>
      <li><strong>Programas de instaladores homologados</strong> para acreditar el resultado de una reforma.</li>
    </ul>
    <p>Si tu caso encaja en alguno de los anteriores, consulta el nivel 2 en <Link to="/pool-check">/pool-check</Link>.</p>

    <h2>Beneficios de certificar tu piscina</h2>
    <p>Los beneficios reales, m&aacute;s all&aacute; del marketing, se agrupan en tres bloques:</p>
    <p><strong>1. Ahorro operativo</strong>. Certificar obliga a auditar el sistema. En la mayor&iacute;a de los casos se detectan ineficiencias (horas de filtraci&oacute;n sobredimensionadas, fugas, mal control de pH, bomba sobredimensionada) que, corregidas, generan ahorros de <strong>150-400 &euro;/a&ntilde;o</strong> en una piscina residencial media.</p>
    <p><strong>2. Valor inmobiliario</strong>. Un activo con <strong>clase A o B certificada</strong> empieza a ser un argumento comercial en zonas donde el mercado inmobiliario valora sostenibilidad. La certificaci&oacute;n digital con PoolLex ID facilita mostrar el activo de forma verificable.</p>
    <p><strong>3. Acceso a programas de eficiencia</strong>. A medida que administraciones y aseguradoras adoptan la norma, disponer del certificado permite acceder a <strong>subvenciones</strong>, <strong>primas</strong> o <strong>descuentos</strong> que las piscinas no certificadas no reciben.</p>

    <h2>El papel de PoolLex.io: aplicadores de la norma, no autores</h2>
    <p>
      Un punto que nos importa dejar claro:{" "}
      <strong>PoolLex.io no es autor de la norma UNE-EN 17645</strong>. La
      norma la publica el{" "}
      <strong>Comit&eacute; Europeo de Normalizaci&oacute;n (CEN)</strong> y
      su comit&eacute; t&eacute;cnico correspondiente.{" "}
      <strong>PoolLex.io es un aplicador digital de la norma</strong>:
      construimos las herramientas para que propietarios, t&eacute;cnicos y
      administraciones puedan aplicarla de forma eficiente.
    </p>
    <p>Esta distinci&oacute;n es importante para tres razones:</p>
    <ul>
      <li><strong>Neutralidad</strong>: no defendemos versiones alternativas de la norma. Aplicamos la versi&oacute;n oficial vigente.</li>
      <li><strong>Actualizaci&oacute;n</strong>: si la norma cambia (revisi&oacute;n prevista cada 5-7 a&ntilde;os), nuestros c&aacute;lculos se adaptan.</li>
      <li><strong>Complementariedad</strong>: nuestro trabajo no compite con laboratorios, certificadores ni ingenier&iacute;as tradicionales. Complementa: nosotros hacemos el diagn&oacute;stico digital, ellos firman lo que requiere firma colegiada.</li>
    </ul>
    <p>
      <strong>PoolLex.io</strong> aplica el conocimiento normativo y de
      ingenier&iacute;a en una capa digital abierta, con la Ficha T&eacute;cnica
      gratuita como puerta de entrada. Empieza tu clasificaci&oacute;n en{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> y, si quieres profundizar
      en el proyecto, visita <Link to="/initiative">/initiative</Link> para
      conocer la filosof&iacute;a completa detr&aacute;s de la plataforma.
    </p>
  </>
);

const Article2BodyEN = () => (
  <>
    <h2>What is EN 17645:2023</h2>
    <p>
      <strong>EN 17645:2023</strong> is the European standard published by the{" "}
      <strong>European Committee for Standardization (CEN)</strong>, formally
      titled <em>&ldquo;Swimming pools &mdash; Requirements for the assessment
      of the environmental sustainability of residential outdoor in-ground
      pools&rdquo;</em>. In the UK it is adopted as{" "}
      <strong>BS EN 17645:2023</strong>.
    </p>
    <p>
      It is the <strong>first harmonised European standard</strong> that sets
      out an objective, reproducible methodology to classify the environmental
      performance of a residential pool. Before 2023 there were private
      certifications, manufacturer labels and one-off audits, but no shared
      language or comparable scale across installations.
    </p>
    <p>The standard sits within the European regulatory ecosystem, aligned with:</p>
    <ul>
      <li><strong>Regulation (EU) 305/2011</strong> on construction products (CE marking of components).</li>
      <li><strong>Regulation (EU) 813/2013</strong> on ecodesign requirements for space heaters (applicable to heat pumps).</li>
      <li><strong>Regulation (EU) 547/2012</strong> on ecodesign requirements for water pumps.</li>
    </ul>
    <p>
      The standard is <strong>voluntary</strong>: no national regulation
      currently mandates pool certification. But its uptake is growing across
      several fronts: <strong>regional grants</strong>,{" "}
      <strong>municipal water-efficiency ordinances</strong>,{" "}
      <strong>real-estate transactions</strong>,{" "}
      <strong>corporate audits</strong> and{" "}
      <strong>certified-installer programmes</strong>.
    </p>

    <h2>The 8 components the standard evaluates</h2>
    <p>
      EN 17645:2023 evaluates the pool as an{" "}
      <strong>integrated system</strong>, not as a sum of parts. The
      methodology scores 8 components with different weights according to
      their aggregate environmental impact:
    </p>
    <ol>
      <li><strong>Pool shell</strong>: material, insulation, expected life span, thermal transmission coefficients.</li>
      <li><strong>Hydraulic circuit</strong>: sizing, head loss, pipe insulation, skimmer and return layout.</li>
      <li><strong>Cover (motorised or manual)</strong>: presence, type (isothermal, deck, floating blanket), vapour tightness.</li>
      <li><strong>Filtration pump</strong>: technology (single-speed, two-speed, VSD), hydraulic performance, associated control.</li>
      <li><strong>Water treatment system</strong>: salt chlorinator, manual dosing, UV, ozone, or hybrid combinations.</li>
      <li><strong>Heating system</strong>: heat pump, exchanger with existing boiler, electric resistance, solar thermal.</li>
      <li><strong>Lighting</strong>: fixture type (LED, halogen, fluorescent), quantity, total installed power and control.</li>
      <li><strong>Control and automation system</strong>: automatic dosing, pH/ORP measurement, time scheduling, remote monitoring.</li>
    </ol>
    <p>
      Each component contributes points to a <strong>global index</strong>,
      and the index maps to an <strong>A-to-F class</strong> with thresholds
      published in the standard.
    </p>

    <h2>A-F scale: what each class means</h2>
    <p>The scale works like a household-appliance energy label:</p>
    <ul>
      <li><strong>Class A</strong>: reference pool in environmental efficiency. Motorised cover, VSD pump, high-COP heat pump, salt treatment with automation, LED lighting, shell insulation. Represents the state of the art available on the market.</li>
      <li><strong>Class B</strong>: highly efficient, with most components optimised. Usually missing 1 or 2 elements to reach A.</li>
      <li><strong>Class C</strong>: solid efficiency. Modern pool with current equipment but not all optimisations (for example, no motorised cover or no VSD).</li>
      <li><strong>Class D</strong>: standard efficiency. Correct pool but with conventional equipment. The most common class in new builds without a specific environmental ambition.</li>
      <li><strong>Class E</strong>: efficiency needs work. Old or undersized equipment, no cover, manual control.</li>
      <li><strong>Class F</strong>: energy-inefficient pool. Single-speed pump, halogen lighting, no cover, no automation. Typically over-20-year stock without upgrades.</li>
    </ul>
    <p>
      <strong>Preliminary sector data</strong> suggest the current residential
      pool stock across Europe is concentrated in <strong>classes D and E</strong>,
      with under 5-10 % of the fleet naturally hitting class A. The good news:{" "}
      <strong>a partial retrofit</strong> (cover + VSD pump + LED) usually moves
      a pool from E to C in a single intervention.
    </p>

    <h2>Free estimate vs. signed certificate</h2>
    <p>It helps to separate two products with different purposes:</p>

    <h3>Free Technical Sheet (level 1)</h3>
    <p>
      It is an <strong>estimate</strong> based on the data the owner declares:
      volume, surface, installed equipment, usage habits. The PoolLex.io
      engine evaluates the 8 components with the available data and returns an{" "}
      <strong>estimated A-F class</strong>, together with a{" "}
      <strong>unique PoolLex ID</strong> (format PLX-XXXXXXX) that identifies
      the pool on the platform.
    </p>
    <p><strong>Good for</strong>: fast diagnostic, comparison with other pools, planning retrofits, understanding where to start.</p>
    <p><strong>Not good for</strong>: submitting to authorities, notaries or insurers. It is indicative, not certified.</p>

    <h3>Engineer-signed seal (level 2)</h3>
    <p>
      A <strong>technical document</strong> reviewed and signed by a{" "}
      <strong>chartered industrial engineer</strong> who has verified the
      supporting documentation (equipment datasheets, installation
      certificates, photos, measurements). It has legal and technical weight.
    </p>
    <p><strong>Good for</strong>: formal audit, sale-and-purchase deals, grants, municipal permits that require it, real-estate certifications, expert reports.</p>

    <h2>How to get the estimate in 2 minutes</h2>
    <p>The process is designed to be fast and free:</p>
    <ol>
      <li>Go to <Link to="/ficha-tecnica">/ficha-tecnica</Link>.</li>
      <li>Enter basic pool data: <strong>volume</strong> (m&sup3;), <strong>surface</strong> (m&sup2;), <strong>year built</strong>, <strong>location</strong> (for climate calculation).</li>
      <li>Indicate the installed <strong>equipment</strong>: pump type, cover presence, treatment system, heating, lighting.</li>
      <li>Submit the form. The engine computes your <strong>estimated A-F class</strong> and assigns a unique <strong>PoolLex ID</strong>.</li>
      <li>You receive your <strong>Technical Sheet as PDF</strong> with the classification, a component breakdown and <strong>prioritised improvement recommendations</strong>.</li>
    </ol>
    <p>The whole process takes <strong>under 2 minutes</strong> if you have the data at hand.</p>

    <h2>When to request the signed seal</h2>
    <p>The signed seal is useful, and sometimes necessary, in several scenarios:</p>
    <ul>
      <li><strong>Real-estate transactions</strong>: valuing the pool as an efficient asset can add 2-5 % to the total property value in deals where the buyer prioritises sustainability.</li>
      <li><strong>Corporate audits</strong>: companies with ESG reporting that includes sports or leisure facilities.</li>
      <li><strong>Regional retrofit grants</strong> that start to include pools.</li>
      <li><strong>Municipal ordinances</strong> that incorporate the classification as a licensing criterion.</li>
      <li><strong>Technical disputes</strong> or insurance claims where the installation state on a given date needs proof.</li>
      <li><strong>Certified-installer programmes</strong> to prove the outcome of a retrofit.</li>
    </ul>
    <p>If your case fits any of the above, check level 2 at <Link to="/pool-check">/pool-check</Link>.</p>

    <h2>Benefits of certifying your pool</h2>
    <p>The real benefits, beyond marketing, come in three blocks:</p>
    <p><strong>1. Operating savings</strong>. Certifying forces an audit of the system. In most cases inefficiencies are found (oversized filtration hours, leaks, poor pH control, oversized pump) that, when fixed, yield <strong>&euro;150-400 per year</strong> on an average residential pool.</p>
    <p><strong>2. Real-estate value</strong>. An asset with a <strong>certified class A or B</strong> is starting to become a commercial argument in markets that value sustainability. Digital certification with a PoolLex ID makes the asset verifiable.</p>
    <p><strong>3. Access to efficiency programmes</strong>. As administrations and insurers adopt the standard, holding the certificate opens the door to <strong>grants</strong>, <strong>premiums</strong> or <strong>discounts</strong> that uncertified pools do not receive.</p>

    <h2>PoolLex.io's role: standard appliers, not authors</h2>
    <p>
      A point we want to make clearly:{" "}
      <strong>PoolLex.io is not the author of EN 17645</strong>. The standard
      is published by the <strong>European Committee for Standardization (CEN)</strong>{" "}
      and its relevant technical committee.{" "}
      <strong>PoolLex.io is a digital applier of the standard</strong>: we
      build the tools so owners, technicians and administrations can apply it
      efficiently.
    </p>
    <p>This distinction matters for three reasons:</p>
    <ul>
      <li><strong>Neutrality</strong>: we do not defend alternative versions of the standard. We apply the official version in force.</li>
      <li><strong>Updates</strong>: if the standard changes (revision expected every 5-7 years), our calculations adapt.</li>
      <li><strong>Complementarity</strong>: our work does not compete with labs, certifiers or traditional engineering firms. It complements: we deliver the digital diagnostic, they sign what requires chartered industrial engineering signature.</li>
    </ul>
    <p>
      <strong>PoolLex.io</strong> applies standards knowledge and engineering
      in an open digital layer, with the free Technical Sheet as the entry
      point. Start your classification at{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> and, if you want to dive
      deeper into the project, visit{" "}
      <Link to="/initiative">/initiative</Link> for the full philosophy behind
      the platform.
    </p>
  </>
);
const Article2BodyFR = () => (
  <>
    <h2>Qu'est-ce que la NF EN 17645:2023</h2>
    <p>
      La <strong>NF EN 17645:2023</strong> est l'adoption fran&ccedil;aise de
      la norme europ&eacute;enne <strong>EN 17645:2023</strong>, publi&eacute;e
      par le <strong>Comit&eacute; Europ&eacute;en de Normalisation (CEN)</strong>{" "}
      et intitul&eacute;e, dans sa version officielle,{" "}
      <em>&laquo;&nbsp;Piscines &mdash; Exigences pour l'&eacute;valuation de la
      durabilit&eacute; environnementale des piscines r&eacute;sidentielles
      ext&eacute;rieures enterr&eacute;es&nbsp;&raquo;</em>.
    </p>
    <p>
      Il s'agit de la <strong>premi&egrave;re norme europ&eacute;enne
      harmonis&eacute;e</strong> qui &eacute;tablit une m&eacute;thodologie
      objective et reproductible pour classer la performance environnementale
      d'une piscine r&eacute;sidentielle. Avant 2023 existaient des
      certifications priv&eacute;es, des labels de fabricants et des audits
      ponctuels, mais aucun langage commun ni &eacute;chelle comparable entre
      installations.
    </p>
    <p>La norme s'inscrit dans l'&eacute;cosyst&egrave;me r&eacute;glementaire europ&eacute;en, en coh&eacute;rence avec :</p>
    <ul>
      <li><strong>R&egrave;glement (UE) 305/2011</strong> sur les produits de construction (marquage CE des composants).</li>
      <li><strong>R&egrave;glement (UE) 813/2013</strong> sur les exigences d'&eacute;coconception des appareils de chauffage (applicable aux pompes &agrave; chaleur).</li>
      <li><strong>R&egrave;glement (UE) 547/2012</strong> sur les exigences d'&eacute;coconception des pompes hydrauliques.</li>
    </ul>
    <p>
      C'est une norme <strong>volontaire</strong> : aucun r&egrave;glement
      national fran&ccedil;ais n'oblige aujourd'hui &agrave; certifier la
      piscine. Mais son adoption cro&icirc;t sur plusieurs fronts :{" "}
      <strong>aides r&eacute;gionales</strong>,{" "}
      <strong>ordonnances municipales d'efficacit&eacute; hydrique</strong>,{" "}
      <strong>op&eacute;rations immobili&egrave;res</strong>,{" "}
      <strong>audits d'entreprise</strong> et{" "}
      <strong>programmes d'installateurs homologu&eacute;s</strong>.
    </p>

    <h2>Les 8 composants &eacute;valu&eacute;s par la norme</h2>
    <p>
      La NF EN 17645:2023 &eacute;value la piscine comme{" "}
      <strong>syst&egrave;me int&eacute;gr&eacute;</strong>, pas comme une
      somme de pi&egrave;ces. La m&eacute;thodologie note 8 composants avec
      des poids diff&eacute;rents selon leur impact environnemental agr&eacute;g&eacute; :
    </p>
    <ol>
      <li><strong>Structure du bassin</strong> : mat&eacute;riau, isolation, dur&eacute;e de vie attendue, coefficients de transmission thermique.</li>
      <li><strong>Circuit hydraulique</strong> : dimensionnement, pertes de charge, isolation des tuyauteries, disposition des skimmers et retours.</li>
      <li><strong>Couverture motoris&eacute;e ou manuelle</strong> : pr&eacute;sence, type (isothermique, deck, l&aacute;me flottante), &eacute;tanch&eacute;it&eacute; &agrave; la vapeur.</li>
      <li><strong>Pompe de filtration</strong> : technologie (monovitesse, bi-vitesse, VSD), performance hydraulique, contr&ocirc;le associ&eacute;.</li>
      <li><strong>Syst&egrave;me de traitement de l'eau</strong> : &eacute;lectrolyseur au sel, dosage manuel, UV, ozone ou combinaisons hybrides.</li>
      <li><strong>Syst&egrave;me de chauffage</strong> : pompe &agrave; chaleur, &eacute;changeur avec chaudi&egrave;re existante, r&eacute;sistance &eacute;lectrique, solaire thermique.</li>
      <li><strong>&Eacute;clairage</strong> : type de projecteur (LED, halog&egrave;ne, fluorescent), nombre, puissance totale install&eacute;e et contr&ocirc;le.</li>
      <li><strong>Syst&egrave;me de contr&ocirc;le et automatismes</strong> : dosage automatique, mesure pH/ORP, programmation horaire, supervision &agrave; distance.</li>
    </ol>
    <p>
      Chaque composant apporte des points &agrave; un{" "}
      <strong>indice global</strong>, et l'indice se traduit en une{" "}
      <strong>classe A &agrave; F</strong> avec des seuils publi&eacute;s dans
      la norme.
    </p>

    <h2>&Eacute;chelle A-F : ce que signifie chaque classe</h2>
    <p>L'&eacute;chelle fonctionne comme les &eacute;tiquettes &eacute;nerg&eacute;tiques d'&eacute;lectrom&eacute;nager :</p>
    <ul>
      <li><strong>Classe A</strong> : piscine de r&eacute;f&eacute;rence en efficacit&eacute; environnementale. Couverture motoris&eacute;e, pompe VSD, PAC haut COP, traitement au sel avec automatisation, &eacute;clairage LED, isolation du bassin. Repr&eacute;sente l'&eacute;tat de l'art disponible sur le march&eacute;.</li>
      <li><strong>Classe B</strong> : tr&egrave;s efficace, avec la plupart des composants optimis&eacute;s. Il manque g&eacute;n&eacute;ralement 1 ou 2 &eacute;l&eacute;ments pour atteindre A.</li>
      <li><strong>Classe C</strong> : efficacit&eacute; notable. Piscine moderne avec &eacute;quipement actuel mais sans toutes les optimisations (par exemple, sans couverture motoris&eacute;e ou sans VSD).</li>
      <li><strong>Classe D</strong> : efficacit&eacute; standard. Piscine correcte mais avec &eacute;quipements conventionnels. La classe la plus fr&eacute;quente en construction neuve sans ambition environnementale sp&eacute;cifique.</li>
      <li><strong>Classe E</strong> : efficacit&eacute; &agrave; am&eacute;liorer. &Eacute;quipements anciens ou sous-dimensionn&eacute;s, absence de couverture et contr&ocirc;le manuel.</li>
      <li><strong>Classe F</strong> : piscine &eacute;nerg&eacute;tiquement inefficace. Pompe monovitesse, &eacute;clairage halog&egrave;ne, sans couverture, sans automatisme. Typiquement parc de plus de 20 ans sans r&eacute;novation.</li>
    </ul>
    <p>
      Les <strong>donn&eacute;es pr&eacute;liminaires</strong> du secteur
      sugg&egrave;rent que le parc r&eacute;sidentiel actuel se concentre en{" "}
      <strong>classes D et E</strong>, avec moins de 5-10 % du parc atteignant
      naturellement la classe A. La bonne nouvelle :{" "}
      <strong>une r&eacute;novation partielle</strong> (couverture + pompe VSD
      + LED) fait souvent passer une piscine de E &agrave; C en une seule
      intervention.
    </p>

    <h2>Estimation gratuite vs. certificat sign&eacute;</h2>
    <p>Il convient de distinguer deux produits &agrave; finalit&eacute; distincte :</p>

    <h3>Fiche Technique gratuite (niveau 1)</h3>
    <p>
      C'est une <strong>estimation</strong> bas&eacute;e sur les donn&eacute;es
      d&eacute;clar&eacute;es par l'utilisateur : volume, surface,
      &eacute;quipement install&eacute;, habitudes. Le moteur de PoolLex.io
      &eacute;value les 8 composants avec les donn&eacute;es disponibles et
      retourne une <strong>classe estim&eacute;e A-F</strong>, avec un{" "}
      <strong>PoolLex ID unique</strong> (format PLX-XXXXXXX) qui identifie
      la piscine sur la plateforme.
    </p>
    <p><strong>Utile pour</strong> : diagnostic rapide, comparaison entre piscines, planification de r&eacute;novations, comprendre par o&ugrave; commencer.</p>
    <p><strong>Ne convient pas pour</strong> : pr&eacute;senter aux administrations, notaires ou assureurs. C'est indicatif, non certifi&eacute;.</p>

    <h3>Sceau sign&eacute; par ing&eacute;nieur (niveau 2)</h3>
    <p>
      C'est un <strong>document technique</strong> r&eacute;vis&eacute; et
      sign&eacute; par un <strong>ing&eacute;nieur</strong> qui a v&eacute;rifi&eacute;
      la documentation apport&eacute;e (fiches techniques d'&eacute;quipements,
      certificats d'installation, photos, mesures). Il a une validit&eacute;
      l&eacute;gale et technique.
    </p>
    <p><strong>Utile pour</strong> : audit formel, op&eacute;rations d'achat-vente, subventions, autorisations municipales qui l'exigent, certifications immobili&egrave;res, rapports d'expertise.</p>

    <h2>Comment obtenir l'estimation en 2 minutes</h2>
    <p>Le processus est con&ccedil;u pour &ecirc;tre rapide et gratuit :</p>
    <ol>
      <li>Acc&eacute;dez &agrave; <Link to="/ficha-tecnica">/ficha-tecnica</Link>.</li>
      <li>Saisissez les donn&eacute;es de base : <strong>volume</strong> (m&sup3;), <strong>surface</strong> (m&sup2;), <strong>ann&eacute;e de construction</strong>, <strong>emplacement</strong> (pour le calcul climatique).</li>
      <li>Indiquez l'<strong>&eacute;quipement</strong> install&eacute; : type de pompe, pr&eacute;sence de couverture, syst&egrave;me de traitement, chauffage, &eacute;clairage.</li>
      <li>Envoyez le formulaire. Le moteur calcule votre <strong>classe estim&eacute;e A-F</strong> et vous attribue un <strong>PoolLex ID</strong> unique.</li>
      <li>Vous recevez votre <strong>Fiche Technique en PDF</strong> avec la classification, une ventilation par composants et des <strong>recommandations priorit&eacute;s ROI</strong>.</li>
    </ol>
    <p>Tout le processus prend <strong>moins de 2 minutes</strong> si vous avez les donn&eacute;es sous la main.</p>

    <h2>Quand demander le sceau sign&eacute;</h2>
    <p>Le sceau sign&eacute; est utile, et parfois n&eacute;cessaire, dans plusieurs cas :</p>
    <ul>
      <li><strong>Op&eacute;rations immobili&egrave;res</strong> : valoriser la piscine comme actif efficient peut ajouter 2-5 % &agrave; la valeur totale du bien lorsque l'acqu&eacute;reur privil&eacute;gie la durabilit&eacute;.</li>
      <li><strong>Audits d'entreprise</strong> : soci&eacute;t&eacute;s avec reporting ESG qui incluent des installations sportives ou de loisirs.</li>
      <li><strong>Aides r&eacute;gionales</strong> &agrave; la r&eacute;habilitation &eacute;nerg&eacute;tique qui commencent &agrave; consid&eacute;rer la piscine.</li>
      <li><strong>Ordonnances municipales</strong> qui int&egrave;grent la classification comme crit&egrave;re de licence.</li>
      <li><strong>Litiges techniques</strong> ou sinistres o&ugrave; il faut d&eacute;montrer l'&eacute;tat de l'installation &agrave; une date donn&eacute;e.</li>
      <li><strong>Programmes d'installateurs homologu&eacute;s</strong> pour accr&eacute;diter le r&eacute;sultat d'une r&eacute;novation.</li>
    </ul>
    <p>Si votre cas correspond, consultez le niveau 2 sur <Link to="/pool-check">/pool-check</Link>.</p>

    <h2>B&eacute;n&eacute;fices de certifier votre piscine</h2>
    <p>Les b&eacute;n&eacute;fices r&eacute;els, au-del&agrave; du marketing, se regroupent en trois blocs :</p>
    <p><strong>1. &Eacute;conomies op&eacute;rationnelles</strong>. Certifier oblige &agrave; auditer le syst&egrave;me. Dans la plupart des cas on d&eacute;tecte des inefficacit&eacute;s (heures de filtration surdimensionn&eacute;es, fuites, mauvais contr&ocirc;le pH, pompe surdimensionn&eacute;e) qui, corrig&eacute;es, g&eacute;n&egrave;rent des &eacute;conomies de <strong>150-400 &euro;/an</strong> sur une piscine r&eacute;sidentielle moyenne.</p>
    <p><strong>2. Valeur immobili&egrave;re</strong>. Un actif avec <strong>classe A ou B certifi&eacute;e</strong> devient un argument commercial dans les zones o&ugrave; le march&eacute; valorise la durabilit&eacute;. La certification num&eacute;rique avec PoolLex ID rend l'actif v&eacute;rifiable.</p>
    <p><strong>3. Acc&egrave;s aux programmes d'efficacit&eacute;</strong>. &Agrave; mesure que les administrations et assureurs adoptent la norme, d&eacute;tenir le certificat ouvre l'acc&egrave;s aux <strong>subventions</strong>, <strong>primes</strong> ou <strong>rabais</strong> que les piscines non certifi&eacute;es ne re&ccedil;oivent pas.</p>

    <h2>Le r&ocirc;le de PoolLex.io : applicateurs de la norme, pas auteurs</h2>
    <p>
      Un point que nous voulons clarifier :{" "}
      <strong>PoolLex.io n'est pas l'auteur de la norme NF EN 17645</strong>.
      La norme est publi&eacute;e par le{" "}
      <strong>Comit&eacute; Europ&eacute;en de Normalisation (CEN)</strong> et
      son comit&eacute; technique.{" "}
      <strong>PoolLex.io est un applicateur num&eacute;rique de la norme</strong> :
      nous construisons les outils pour que propri&eacute;taires, techniciens
      et administrations puissent l'appliquer efficacement.
    </p>
    <p>Cette distinction est importante pour trois raisons :</p>
    <ul>
      <li><strong>Neutralit&eacute;</strong> : nous ne d&eacute;fendons pas de versions alternatives de la norme. Nous appliquons la version officielle en vigueur.</li>
      <li><strong>Mises &agrave; jour</strong> : si la norme change (r&eacute;vision pr&eacute;vue tous les 5-7 ans), nos calculs s'adaptent.</li>
      <li><strong>Compl&eacute;mentarit&eacute;</strong> : notre travail ne concurrence pas les laboratoires, certificateurs ni ing&eacute;nieries traditionnelles. Il compl&egrave;te : nous faisons le diagnostic num&eacute;rique, ils signent ce qui requiert la signature d'ing&eacute;nieur.</li>
    </ul>
    <p>
      <strong>PoolLex.io</strong> applique la connaissance normative et
      d'ing&eacute;nierie dans une couche num&eacute;rique ouverte, avec la
      Fiche Technique gratuite comme porte d'entr&eacute;e. Commencez votre
      classification sur <Link to="/ficha-tecnica">/ficha-tecnica</Link> et,
      si vous voulez approfondir, visitez{" "}
      <Link to="/initiative">/initiative</Link> pour la philosophie compl&egrave;te
      derri&egrave;re la plateforme.
    </p>
  </>
);
const Article2BodyDE = () => (
  <>
    <h2>Was ist DIN EN 17645:2023</h2>
    <p>
      Die <strong>DIN EN 17645:2023</strong> ist die deutsche &Uuml;bernahme
      der europ&auml;ischen Norm <strong>EN 17645:2023</strong>,
      ver&ouml;ffentlicht vom{" "}
      <strong>Europ&auml;ischen Komitee f&uuml;r Normung (CEN)</strong> und
      offiziell betitelt als{" "}
      <em>&bdquo;Schwimmb&auml;der &mdash; Anforderungen zur Bewertung der
      Umweltnachhaltigkeit privater Freibad-Schwimmb&auml;der mit
      Einbaubecken&ldquo;</em>.
    </p>
    <p>
      Es handelt sich um die{" "}
      <strong>erste harmonisierte europ&auml;ische Norm</strong>, die eine
      objektive und reproduzierbare Methodik zur Klassifizierung der
      Umweltleistung eines privaten Pools festlegt. Vor 2023 gab es private
      Zertifizierungen, Hersteller-Siegel und Einzelaudits, aber keine
      gemeinsame Sprache und keine vergleichbare Skala zwischen Anlagen.
    </p>
    <p>Die Norm ist Teil des europ&auml;ischen Regulierungsrahmens und aufeinander abgestimmt mit:</p>
    <ul>
      <li><strong>Verordnung (EU) 305/2011</strong> &uuml;ber Bauprodukte (CE-Kennzeichnung von Komponenten).</li>
      <li><strong>Verordnung (EU) 813/2013</strong> zu &Ouml;kodesign-Anforderungen an Raumheizger&auml;te (gilt f&uuml;r W&auml;rmepumpen).</li>
      <li><strong>Verordnung (EU) 547/2012</strong> zu &Ouml;kodesign-Anforderungen an Wasserpumpen.</li>
    </ul>
    <p>
      Die Norm ist <strong>freiwillig</strong>: keine deutsche oder
      &ouml;sterreichische Vorschrift verlangt heute die Poolzertifizierung.
      Ihre Verbreitung nimmt jedoch an mehreren Fronten zu:{" "}
      <strong>L&auml;nderf&ouml;rderungen</strong>,{" "}
      <strong>kommunale Wassereffizienzordnungen</strong>,{" "}
      <strong>Immobiliengesch&auml;fte</strong>,{" "}
      <strong>Unternehmensaudits</strong> und{" "}
      <strong>Programme f&uuml;r zertifizierte Installateure</strong>.
    </p>

    <h2>Die 8 Komponenten, die die Norm bewertet</h2>
    <p>
      DIN EN 17645:2023 bewertet den Pool als{" "}
      <strong>integriertes System</strong>, nicht als Summe von Einzelteilen.
      Die Methodik bewertet 8 Komponenten mit unterschiedlicher Gewichtung
      je nach ihrer aggregierten Umweltwirkung:
    </p>
    <ol>
      <li><strong>Beckenh&uuml;lle</strong>: Material, D&auml;mmung, erwartete Lebensdauer, W&auml;rmedurchgangskoeffizienten.</li>
      <li><strong>Hydraulikkreislauf</strong>: Dimensionierung, Druckverluste, Rohrisolierung, Anordnung von Skimmern und R&uuml;ckl&auml;ufen.</li>
      <li><strong>Abdeckung (motorisiert oder manuell)</strong>: Vorhandensein, Typ (isothermisch, Deck, Schwimmfolie), Dampfdichtigkeit.</li>
      <li><strong>Filterpumpe</strong>: Technologie (einstufig, zweistufig, VSD), hydraulische Leistung, zugeh&ouml;rige Steuerung.</li>
      <li><strong>Wasseraufbereitungssystem</strong>: Salzelektrolyse, manuelle Dosierung, UV, Ozon oder Hybridkombinationen.</li>
      <li><strong>Heizsystem</strong>: W&auml;rmepumpe, W&auml;rmetauscher mit bestehendem Kessel, elektrischer Heizstab, Solarthermie.</li>
      <li><strong>Beleuchtung</strong>: Leuchtentyp (LED, Halogen, Leuchtstoff), Anzahl, installierte Gesamtleistung und Steuerung.</li>
      <li><strong>Steuerungs- und Automatisierungssystem</strong>: automatische Dosierung, pH/ORP-Messung, Zeitprogrammierung, Fern&uuml;berwachung.</li>
    </ol>
    <p>
      Jede Komponente tr&auml;gt Punkte zu einem <strong>Gesamtindex</strong>{" "}
      bei, und der Index &uuml;bersetzt sich in eine <strong>Klasse A bis F</strong>{" "}
      mit in der Norm ver&ouml;ffentlichten Schwellenwerten.
    </p>

    <h2>Skala A-F: Bedeutung jeder Klasse</h2>
    <p>Die Skala funktioniert analog zu den Energielabels von Haushaltsger&auml;ten:</p>
    <ul>
      <li><strong>Klasse A</strong>: Referenzpool in Umwelteffizienz. Motorisierte Abdeckung, VSD-Pumpe, W&auml;rmepumpe mit hohem COP, Salzbehandlung mit Automatisierung, LED-Beleuchtung, Beckend&auml;mmung. Vertritt den Stand der Technik am Markt.</li>
      <li><strong>Klasse B</strong>: sehr effizient, die meisten Komponenten optimiert. Meist fehlen 1 oder 2 Elemente, um A zu erreichen.</li>
      <li><strong>Klasse C</strong>: solide Effizienz. Moderner Pool mit aktueller Ausstattung, aber ohne alle Optimierungen (z. B. ohne motorisierte Abdeckung oder ohne VSD).</li>
      <li><strong>Klasse D</strong>: Standardeffizienz. Korrekter Pool mit konventioneller Ausstattung. Die h&auml;ufigste Klasse im Neubau ohne spezifische Umweltambition.</li>
      <li><strong>Klasse E</strong>: verbesserungsbed&uuml;rftige Effizienz. Alte oder unterdimensionierte Ger&auml;te, keine Abdeckung, manuelle Steuerung.</li>
      <li><strong>Klasse F</strong>: energetisch ineffizienter Pool. Einstufige Pumpe, Halogenbeleuchtung, keine Abdeckung, keine Automatik. Typischerweise &uuml;ber 20 Jahre alter Bestand ohne Modernisierung.</li>
    </ul>
    <p>
      <strong>Vorl&auml;ufige Branchendaten</strong> deuten darauf hin, dass
      sich der aktuelle private Poolbestand in Europa auf{" "}
      <strong>Klassen D und E</strong> konzentriert, und dass weniger als 5-10 %
      des Bestands ohne Ma&szlig;nahmen die Klasse A erreichen. Die gute
      Nachricht: <strong>eine Teilsanierung</strong> (Abdeckung + VSD-Pumpe +
      LED) hebt einen Pool oft in einem einzigen Schritt von E auf C.
    </p>

    <h2>Kostenlose Sch&auml;tzung vs. signiertes Zertifikat</h2>
    <p>Hier lohnt es sich, zwei Produkte mit unterschiedlichen Zwecken zu unterscheiden:</p>

    <h3>Kostenloses technisches Datenblatt (Stufe 1)</h3>
    <p>
      Es ist eine <strong>Sch&auml;tzung</strong> basierend auf den vom Nutzer
      angegebenen Daten: Volumen, Fl&auml;che, installierte Ausstattung,
      Nutzungsverhalten. Die PoolLex.io-Engine bewertet die 8 Komponenten mit
      den verf&uuml;gbaren Daten und liefert eine{" "}
      <strong>gesch&auml;tzte Klasse A-F</strong>, zusammen mit einer{" "}
      <strong>eindeutigen PoolLex ID</strong> (Format PLX-XXXXXXX), die den
      Pool auf der Plattform identifiziert.
    </p>
    <p><strong>Geeignet f&uuml;r</strong>: schnelle Diagnose, Vergleich mit anderen Pools, Planung von Sanierungen, Verst&auml;ndnis, wo zu beginnen ist.</p>
    <p><strong>Nicht geeignet f&uuml;r</strong>: Vorlage bei Beh&ouml;rden, Notaren oder Versicherern. Es ist indikativ, nicht zertifiziert.</p>

    <h3>Vom Ingenieur signiertes Siegel (Stufe 2)</h3>
    <p>
      Ein <strong>technisches Dokument</strong>, gepr&uuml;ft und signiert von
      einem <strong>Ingenieur</strong>, der die vorgelegte Dokumentation
      &uuml;berpr&uuml;ft hat (Ger&auml;tedatenbl&auml;tter,
      Installationszertifikate, Fotos, Messungen). Es hat rechtliche und
      technische G&uuml;ltigkeit.
    </p>
    <p><strong>Geeignet f&uuml;r</strong>: formale Audits, Kauf-/Verkaufsgesch&auml;fte, F&ouml;rderungen, kommunale Genehmigungen, die es verlangen, Immobilienzertifizierungen, Sachverst&auml;ndigenberichte.</p>

    <h2>So erhalten Sie die Sch&auml;tzung in 2 Minuten</h2>
    <p>Der Prozess ist auf Schnelligkeit und Kostenfreiheit ausgelegt:</p>
    <ol>
      <li>Rufen Sie <Link to="/ficha-tecnica">/ficha-tecnica</Link> auf.</li>
      <li>Geben Sie die Basisdaten Ihres Pools ein: <strong>Volumen</strong> (m&sup3;), <strong>Fl&auml;che</strong> (m&sup2;), <strong>Baujahr</strong>, <strong>Standort</strong> (f&uuml;r die Klimaberechnung).</li>
      <li>Geben Sie die installierte <strong>Ausstattung</strong> an: Pumpentyp, ob eine Abdeckung vorhanden ist, Aufbereitungssystem, Heizung, Beleuchtung.</li>
      <li>Formular abschicken. Die Engine berechnet Ihre <strong>gesch&auml;tzte Klasse A-F</strong> und weist Ihnen eine eindeutige <strong>PoolLex ID</strong> zu.</li>
      <li>Sie erhalten Ihr <strong>Datenblatt als PDF</strong> mit Klassifizierung, Komponentenaufschl&uuml;sselung und <strong>priorisierten Verbesserungsempfehlungen</strong>.</li>
    </ol>
    <p>Der gesamte Prozess dauert <strong>unter 2 Minuten</strong>, wenn die Daten zur Hand sind.</p>

    <h2>Wann das signierte Siegel anfordern</h2>
    <p>Das signierte Siegel ist n&uuml;tzlich, und manchmal notwendig, in verschiedenen Szenarien:</p>
    <ul>
      <li><strong>Immobiliengesch&auml;fte</strong>: die Bewertung des Pools als effizientes Verm&ouml;gen kann 2-5 % zum Gesamtwert der Immobilie hinzuf&uuml;gen, wenn der K&auml;ufer Nachhaltigkeit priorisiert.</li>
      <li><strong>Unternehmensaudits</strong>: Firmen mit ESG-Reporting, das Sport- oder Freizeitanlagen einschlie&szlig;t.</li>
      <li><strong>L&auml;nderf&ouml;rderungen</strong> f&uuml;r energetische Sanierungen, die den Pool zu ber&uuml;cksichtigen beginnen.</li>
      <li><strong>Kommunale Ordnungen</strong>, die die Klassifizierung als Genehmigungskriterium aufnehmen.</li>
      <li><strong>Technische Streitigkeiten</strong> oder Schadensf&auml;lle, in denen der Zustand der Anlage zu einem bestimmten Zeitpunkt nachgewiesen werden muss.</li>
      <li><strong>Programme f&uuml;r zertifizierte Installateure</strong> zum Nachweis des Sanierungsergebnisses.</li>
    </ul>
    <p>Wenn Ihr Fall dazu passt, sehen Sie sich Stufe 2 unter <Link to="/pool-check">/pool-check</Link> an.</p>

    <h2>Nutzen der Zertifizierung Ihres Pools</h2>
    <p>Der echte Nutzen, jenseits von Marketing, gliedert sich in drei Bl&ouml;cke:</p>
    <p><strong>1. Betriebskostenersparnis</strong>. Die Zertifizierung erzwingt ein Audit des Systems. In den meisten F&auml;llen werden Ineffizienzen entdeckt (&uuml;berdimensionierte Filterstunden, Lecks, schlechte pH-Steuerung, &uuml;berdimensionierte Pumpe), deren Behebung Einsparungen von <strong>150-400 &euro;/Jahr</strong> bei einem durchschnittlichen privaten Pool bringt.</p>
    <p><strong>2. Immobilienwert</strong>. Ein Verm&ouml;gen mit <strong>zertifizierter Klasse A oder B</strong> wird zu einem Verkaufsargument in Regionen, in denen der Markt Nachhaltigkeit bewertet. Die digitale Zertifizierung mit PoolLex ID macht das Verm&ouml;gen verifizierbar.</p>
    <p><strong>3. Zugang zu Effizienzprogrammen</strong>. Mit zunehmender &Uuml;bernahme der Norm durch Verwaltungen und Versicherer erm&ouml;glicht das Zertifikat den Zugang zu <strong>F&ouml;rderungen</strong>, <strong>Boni</strong> oder <strong>Rabatten</strong>, die nicht zertifizierte Pools nicht erhalten.</p>

    <h2>Die Rolle von PoolLex.io: Anwender der Norm, nicht Autor</h2>
    <p>
      Ein Punkt, den wir klarstellen wollen:{" "}
      <strong>PoolLex.io ist nicht der Autor der Norm DIN EN 17645</strong>.
      Die Norm wird vom{" "}
      <strong>Europ&auml;ischen Komitee f&uuml;r Normung (CEN)</strong> und
      dessen zust&auml;ndigem technischen Komitee ver&ouml;ffentlicht.{" "}
      <strong>PoolLex.io ist ein digitaler Anwender der Norm</strong>: wir
      bauen die Werkzeuge, damit Eigent&uuml;mer, Techniker und Verwaltungen
      sie effizient anwenden k&ouml;nnen.
    </p>
    <p>Diese Unterscheidung ist aus drei Gr&uuml;nden wichtig:</p>
    <ul>
      <li><strong>Neutralit&auml;t</strong>: wir verteidigen keine alternativen Versionen der Norm. Wir wenden die offizielle geltende Version an.</li>
      <li><strong>Aktualisierung</strong>: wenn die Norm sich &auml;ndert (Revision alle 5-7 Jahre erwartet), passen sich unsere Berechnungen an.</li>
      <li><strong>Komplementarit&auml;t</strong>: unsere Arbeit konkurriert nicht mit Labors, Zertifizierern oder klassischen Ingenieurb&uuml;ros. Sie erg&auml;nzt: wir machen die digitale Diagnose, sie signieren, was eine Ingenieurunterschrift verlangt.</li>
    </ul>
    <p>
      <strong>PoolLex.io</strong> wendet Norm- und Ingenieurwissen in einer
      offenen digitalen Schicht an, mit dem kostenlosen technischen Datenblatt
      als Einstieg. Starten Sie Ihre Klassifizierung unter{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>, und wenn Sie tiefer
      einsteigen wollen, besuchen Sie{" "}
      <Link to="/initiative">/initiative</Link> f&uuml;r die vollst&auml;ndige
      Philosophie hinter der Plattform.
    </p>
  </>
);
const Article2BodyIT = () => (
  <>
    <h2>Cos'&egrave; la UNI EN 17645:2023</h2>
    <p>
      La <strong>UNI EN 17645:2023</strong> &egrave; l'adozione italiana della
      norma europea <strong>EN 17645:2023</strong>, pubblicata dal{" "}
      <strong>Comitato Europeo di Normazione (CEN)</strong> e intitolata,
      nella sua versione ufficiale,{" "}
      <em>&laquo;Piscine &mdash; Requisiti per la valutazione della
      sostenibilit&agrave; ambientale delle piscine residenziali esterne
      interrate&raquo;</em>.
    </p>
    <p>
      Si tratta della <strong>prima norma europea armonizzata</strong> che
      stabilisce una metodologia oggettiva e riproducibile per classificare
      la performance ambientale di una piscina residenziale. Prima del 2023
      esistevano certificazioni private, marchi di produttori e audit
      puntuali, ma nessun linguaggio comune n&eacute; scala confrontabile tra
      installazioni.
    </p>
    <p>La norma si inserisce nell'ecosistema regolamentare europeo, allineata con:</p>
    <ul>
      <li><strong>Regolamento (UE) 305/2011</strong> sui prodotti da costruzione (marcatura CE dei componenti).</li>
      <li><strong>Regolamento (UE) 813/2013</strong> sui requisiti di progettazione ecocompatibile degli apparecchi di riscaldamento (applicabile alle pompe di calore).</li>
      <li><strong>Regolamento (UE) 547/2012</strong> sui requisiti di progettazione ecocompatibile delle pompe idrauliche.</li>
    </ul>
    <p>
      &Egrave; una norma <strong>volontaria</strong>: nessun regolamento
      statale italiano impone oggi di certificare la piscina. Ma la sua
      adozione cresce su pi&ugrave; fronti: <strong>bandi regionali</strong>,{" "}
      <strong>regolamenti comunali di efficienza idrica</strong>,{" "}
      <strong>operazioni immobiliari</strong>,{" "}
      <strong>audit aziendali</strong> e{" "}
      <strong>programmi di installatori qualificati</strong>.
    </p>

    <h2>Gli 8 componenti che la norma valuta</h2>
    <p>
      La UNI EN 17645:2023 valuta la piscina come{" "}
      <strong>sistema integrato</strong>, non come somma di pezzi. La
      metodologia assegna punteggi a 8 componenti con pesi diversi secondo il
      loro impatto ambientale aggregato:
    </p>
    <ol>
      <li><strong>Struttura della vasca</strong>: materiale, isolamento, vita utile attesa, coefficienti di trasmissione termica.</li>
      <li><strong>Circuito idraulico</strong>: dimensionamento, perdite di carico, isolamento delle tubazioni, disposizione di skimmer e ritorni.</li>
      <li><strong>Copertura (motorizzata o manuale)</strong>: presenza, tipo (isotermica, deck, telo flottante), tenuta al vapore.</li>
      <li><strong>Pompa di filtrazione</strong>: tecnologia (monovelocit&agrave;, bivelocit&agrave;, VSD), performance idraulica, controllo associato.</li>
      <li><strong>Sistema di trattamento dell'acqua</strong>: elettrolisi salina, dosaggio manuale, UV, ozono o combinazioni ibride.</li>
      <li><strong>Sistema di riscaldamento</strong>: pompa di calore, scambiatore con caldaia esistente, resistenza elettrica, solare termico.</li>
      <li><strong>Illuminazione</strong>: tipo di faretto (LED, alogeno, fluorescente), numero, potenza totale installata e controllo.</li>
      <li><strong>Sistema di controllo e automatismi</strong>: dosaggio automatico, misurazione pH/ORP, programmazione oraria, monitoraggio remoto.</li>
    </ol>
    <p>
      Ogni componente apporta punti a un <strong>indice globale</strong>, e
      l'indice si traduce in una <strong>classe A-F</strong> con soglie
      pubblicate nella norma.
    </p>

    <h2>Scala A-F: cosa significa ogni classe</h2>
    <p>La scala funziona in modo analogo alle etichette energetiche degli elettrodomestici:</p>
    <ul>
      <li><strong>Classe A</strong>: piscina di riferimento in efficienza ambientale. Copertura motorizzata, pompa VSD, pompa di calore ad alto COP, trattamento salino con automazione, illuminazione LED, isolamento della vasca. Rappresenta lo stato dell'arte disponibile sul mercato.</li>
      <li><strong>Classe B</strong>: molto efficiente, con la maggior parte dei componenti ottimizzati. Solitamente mancano 1 o 2 elementi per raggiungere A.</li>
      <li><strong>Classe C</strong>: efficienza notevole. Piscina moderna con equipaggiamento attuale ma senza tutte le ottimizzazioni (ad esempio, senza copertura motorizzata o senza VSD).</li>
      <li><strong>Classe D</strong>: efficienza standard. Piscina corretta ma con equipaggiamento convenzionale. La classe pi&ugrave; frequente in nuove costruzioni senza ambizione ambientale specifica.</li>
      <li><strong>Classe E</strong>: efficienza migliorabile. Equipaggiamenti vecchi o sottodimensionati, assenza di copertura e controllo manuale.</li>
      <li><strong>Classe F</strong>: piscina energeticamente inefficiente. Pompa monovelocit&agrave;, illuminazione alogena, senza copertura, senza automatismo. Tipicamente parco di oltre 20 anni senza rinnovamento.</li>
    </ul>
    <p>
      I <strong>dati preliminari</strong> del settore suggeriscono che
      l'attuale parco residenziale italiano si concentra sulle{" "}
      <strong>classi D ed E</strong>, con meno del 5-10 % del parco che
      raggiunge naturalmente la classe A. La buona notizia:{" "}
      <strong>una riqualificazione parziale</strong> (copertura + pompa VSD +
      LED) sposta spesso una piscina da E a C con un unico intervento.
    </p>

    <h2>Stima gratuita vs. certificato firmato</h2>
    <p>Conviene distinguere due prodotti con finalit&agrave; diverse:</p>

    <h3>Scheda Tecnica gratuita (livello 1)</h3>
    <p>
      &Egrave; una <strong>stima</strong> basata sui dati dichiarati
      dall'utente: volume, superficie, equipaggiamento installato, abitudini.
      Il motore di PoolLex.io valuta gli 8 componenti con i dati disponibili
      e restituisce una <strong>classe stimata A-F</strong>, insieme a un{" "}
      <strong>PoolLex ID univoco</strong> (formato PLX-XXXXXXX) che identifica
      la piscina sulla piattaforma.
    </p>
    <p><strong>Serve per</strong>: diagnosi rapida, confronto con altre piscine, pianificare riqualificazioni, capire da dove iniziare.</p>
    <p><strong>Non serve per</strong>: presentare ad amministrazioni, notai o assicurazioni. &Egrave; indicativa, non certificata.</p>

    <h3>Sigillo firmato da ingegnere (livello 2)</h3>
    <p>
      &Egrave; un <strong>documento tecnico</strong> rivisto e firmato da un{" "}
      <strong>ingegnere</strong> che ha verificato la documentazione fornita
      (schede tecniche degli apparecchi, certificati di installazione, foto,
      misure). Ha validit&agrave; legale e tecnica.
    </p>
    <p><strong>Serve per</strong>: audit formale, operazioni di compravendita, incentivi, licenze comunali che lo richiedono, certificazioni immobiliari, perizie.</p>

    <h2>Come ottenere la stima in 2 minuti</h2>
    <p>Il processo &egrave; progettato per essere rapido e gratuito:</p>
    <ol>
      <li>Accedi a <Link to="/ficha-tecnica">/ficha-tecnica</Link>.</li>
      <li>Inserisci i dati di base della tua piscina: <strong>volume</strong> (m&sup3;), <strong>superficie</strong> (m&sup2;), <strong>anno di costruzione</strong>, <strong>ubicazione</strong> (per il calcolo climatico).</li>
      <li>Indica l'<strong>equipaggiamento</strong> installato: tipo di pompa, presenza di copertura, sistema di trattamento, riscaldamento, illuminazione.</li>
      <li>Invia il modulo. Il motore calcola la tua <strong>classe stimata A-F</strong> e ti assegna un <strong>PoolLex ID</strong> univoco.</li>
      <li>Ricevi la tua <strong>Scheda Tecnica in PDF</strong> con la classificazione, un dettaglio per componenti e <strong>raccomandazioni prioritizzate</strong> di miglioramento.</li>
    </ol>
    <p>L'intero processo richiede <strong>meno di 2 minuti</strong> se hai i dati a portata di mano.</p>

    <h2>Quando richiedere il sigillo firmato</h2>
    <p>Il sigillo firmato &egrave; utile, e a volte necessario, in vari scenari:</p>
    <ul>
      <li><strong>Operazioni immobiliari</strong>: valorizzare la piscina come asset efficiente pu&ograve; aggiungere il 2-5 % al valore totale dell'immobile quando l'acquirente privilegia la sostenibilit&agrave;.</li>
      <li><strong>Audit aziendali</strong>: aziende con reporting ESG che includono impianti sportivi o di svago.</li>
      <li><strong>Incentivi regionali</strong> di riqualificazione energetica che iniziano a considerare la piscina.</li>
      <li><strong>Regolamenti comunali</strong> che incorporano la classificazione come criterio di licenza.</li>
      <li><strong>Contenziosi tecnici</strong> o sinistri in cui va dimostrato lo stato dell'installazione in una data determinata.</li>
      <li><strong>Programmi di installatori qualificati</strong> per accreditare l'esito di una riqualificazione.</li>
    </ul>
    <p>Se il tuo caso rientra in uno di questi, consulta il livello 2 su <Link to="/pool-check">/pool-check</Link>.</p>

    <h2>Vantaggi di certificare la tua piscina</h2>
    <p>I vantaggi reali, oltre il marketing, si raggruppano in tre blocchi:</p>
    <p><strong>1. Risparmio operativo</strong>. Certificare obbliga a un audit del sistema. Nella maggior parte dei casi si rilevano inefficienze (ore di filtrazione sovradimensionate, perdite, cattivo controllo del pH, pompa sovradimensionata) che, corrette, generano risparmi di <strong>150-400 &euro;/anno</strong> su una piscina residenziale media.</p>
    <p><strong>2. Valore immobiliare</strong>. Un asset con <strong>classe A o B certificata</strong> inizia a essere un argomento commerciale in zone in cui il mercato immobiliare valorizza la sostenibilit&agrave;. La certificazione digitale con PoolLex ID rende l'asset verificabile.</p>
    <p><strong>3. Accesso a programmi di efficienza</strong>. Man mano che amministrazioni e assicurazioni adottano la norma, disporre del certificato consente di accedere a <strong>incentivi</strong>, <strong>premi</strong> o <strong>sconti</strong> che le piscine non certificate non ricevono.</p>

    <h2>Il ruolo di PoolLex.io: applicatori della norma, non autori</h2>
    <p>
      Un punto che ci preme chiarire:{" "}
      <strong>PoolLex.io non &egrave; autore della norma UNI EN 17645</strong>.
      La norma &egrave; pubblicata dal{" "}
      <strong>Comitato Europeo di Normazione (CEN)</strong> e dal suo comitato
      tecnico competente.{" "}
      <strong>PoolLex.io &egrave; un applicatore digitale della norma</strong>:
      costruiamo gli strumenti perch&eacute; proprietari, tecnici e
      amministrazioni possano applicarla in modo efficiente.
    </p>
    <p>Questa distinzione &egrave; importante per tre ragioni:</p>
    <ul>
      <li><strong>Neutralit&agrave;</strong>: non difendiamo versioni alternative della norma. Applichiamo la versione ufficiale in vigore.</li>
      <li><strong>Aggiornamento</strong>: se la norma cambia (revisione prevista ogni 5-7 anni), i nostri calcoli si adattano.</li>
      <li><strong>Complementarit&agrave;</strong>: il nostro lavoro non compete con laboratori, certificatori n&eacute; ingegnerie tradizionali. Complementa: noi facciamo la diagnosi digitale, loro firmano ci&ograve; che richiede firma di ingegnere.</li>
    </ul>
    <p>
      <strong>PoolLex.io</strong> applica la conoscenza normativa e
      ingegneristica in uno strato digitale aperto, con la Scheda Tecnica
      gratuita come porta d'ingresso. Inizia la tua classificazione su{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> e, se vuoi approfondire
      il progetto, visita <Link to="/initiative">/initiative</Link> per la
      filosofia completa dietro la piattaforma.
    </p>
  </>
);

// ============================================================================
// ARTICLE 3 — Estalvi energètic (15 accions)
// ============================================================================

const Article3BodyES = () => (
  <>
    <h2>Introducci&oacute;n: la piscina, gran consumidor invisible del hogar</h2>
    <p>
      Una piscina residencial exterior consume, de media, entre el{" "}
      <strong>15 y el 25 % del total del consumo el&eacute;ctrico anual</strong>{" "}
      de una vivienda unifamiliar con piscina. Si est&aacute;{" "}
      <strong>climatizada con bomba de calor</strong> durante temporada
      extendida, la cifra puede subir al <strong>35 %</strong> o m&aacute;s.
      En agua, la piscina representa habitualmente{" "}
      <strong>8-15 % del consumo anual dom&eacute;stico</strong>.
    </p>
    <p>
      Estas cifras son grandes, pero tambi&eacute;n son{" "}
      <strong>negociables</strong>: la mayor&iacute;a del parque residencial
      espa&ntilde;ol opera con equipos y configuraciones muy alejadas del
      &oacute;ptimo t&eacute;cnico disponible. La norma{" "}
      <strong>UNE-EN 17645:2023</strong> ha aportado por fin un lenguaje
      com&uacute;n para medir esta diferencia, y las 15 acciones que siguen
      ordenan lo que hoy da mejor retorno.
    </p>
    <p>
      Los rangos econ&oacute;micos son conservadores y orientativos. Todos los
      c&aacute;lculos asumen precio medio el&eacute;ctrico de{" "}
      <strong>0,18 &euro;/kWh</strong> y precio medio del agua de{" "}
      <strong>2 &euro;/m&sup3;</strong>, y se refieren a una piscina de
      referencia de <strong>50 m&sup3; y 32 m&sup2; de superficie</strong> en
      clima mediterr&aacute;neo.
    </p>

    <h2>1. Cubierta t&eacute;rmica (manual o motorizada)</h2>
    <p>La medida con mejor ROI en casi cualquier piscina.</p>
    <ul>
      <li><strong>Efecto</strong>: reduce evaporaci&oacute;n <strong>60-70 %</strong> y p&eacute;rdidas de calor nocturnas <strong>40-60 %</strong>. Reduce tambi&eacute;n horas de filtraci&oacute;n necesarias y consumo de qu&iacute;micos.</li>
      <li><strong>Inversi&oacute;n</strong>: 400-1.200 &euro; manual (l&aacute;mina flotante o cobertor de invierno), 4.000-9.000 &euro; motorizada con cofre.</li>
      <li><strong>Ahorro anual</strong>: 200-500 &euro; en piscina no climatizada, 500-1.500 &euro; en climatizada.</li>
      <li><strong>ROI</strong>: 1-3 temporadas en climatizada, 2-4 a&ntilde;os en no climatizada.</li>
    </ul>

    <h2>2. Bomba de filtraci&oacute;n con VSD (velocidad variable)</h2>
    <p>Aprovecha las <strong>leyes de afinidad hidr&aacute;ulica</strong>: al reducir la velocidad, el consumo cae aproximadamente al <strong>cubo</strong> de la velocidad. Filtrar m&aacute;s horas a menor velocidad consume mucho menos que filtrar pocas horas a alta velocidad.</p>
    <ul>
      <li><strong>Efecto</strong>: ahorro <strong>60-80 %</strong> en consumo de filtraci&oacute;n.</li>
      <li><strong>Inversi&oacute;n</strong>: 600-1.500 &euro; (bomba + instalaci&oacute;n).</li>
      <li><strong>Ahorro anual</strong>: 150-350 &euro;.</li>
      <li><strong>ROI</strong>: 2-6 a&ntilde;os.</li>
    </ul>

    <h2>3. Bomba de calor de alto COP frente a resistencia el&eacute;ctrica</h2>
    <p>La bomba de calor entrega <strong>4-6 kWh t&eacute;rmicos por cada kWh el&eacute;ctrico</strong> consumido (COP 4-6), frente al COP=1 de una resistencia el&eacute;ctrica.</p>
    <ul>
      <li><strong>Efecto</strong>: reduce consumo el&eacute;ctrico de climatizaci&oacute;n <strong>75-80 %</strong>.</li>
      <li><strong>Inversi&oacute;n</strong>: 2.500-6.000 &euro; una bomba de calor de piscina de 10-15 kW.</li>
      <li><strong>Ahorro anual (respecto a resistencia)</strong>: 800-2.000 &euro;.</li>
      <li><strong>ROI</strong>: 2-4 temporadas.</li>
    </ul>
    <p>Alineada con <strong>Reglamento (UE) 813/2013</strong> sobre requisitos de dise&ntilde;o ecol&oacute;gico de aparatos de calefacci&oacute;n.</p>

    <h2>4. Iluminaci&oacute;n LED sumergible</h2>
    <p>Un foco LED de 20-30 W sustituye a un hal&oacute;geno de 200-300 W con la misma luminosidad percibida.</p>
    <ul>
      <li><strong>Efecto</strong>: consumo hasta <strong>10&times; menor</strong> en iluminaci&oacute;n.</li>
      <li><strong>Inversi&oacute;n</strong>: 200-500 &euro; por foco (LED sumergible de calidad).</li>
      <li><strong>Ahorro anual</strong>: 30-100 &euro; seg&uacute;n horas de uso.</li>
      <li><strong>ROI</strong>: 3-6 a&ntilde;os. Adem&aacute;s duran 30.000-50.000 h vs 2.000-5.000 h del hal&oacute;geno.</li>
    </ul>

    <h2>5. Automatismos: control pH + ORP + programaci&oacute;n</h2>
    <p>Una bomba dosificadora de pH y un controlador ORP mantienen el agua siempre en rango sin sobreconsumo de qu&iacute;micos ni horas extra de filtraci&oacute;n.</p>
    <ul>
      <li><strong>Efecto</strong>: reduce qu&iacute;micos <strong>20-40 %</strong>, evita correcci&oacute;n manual, prolonga vida del vaso.</li>
      <li><strong>Inversi&oacute;n</strong>: 800-2.000 &euro; (bomba pH + sonda ORP + controlador).</li>
      <li><strong>Ahorro anual</strong>: 60-150 &euro; en qu&iacute;micos + tiempo del propietario.</li>
      <li><strong>ROI</strong>: 5-10 a&ntilde;os solo por qu&iacute;micos, mejor si valoras el tiempo.</li>
    </ul>

    <h2>6. Aislamiento del vaso y del circuito hidr&aacute;ulico</h2>
    <p>Especialmente relevante en piscinas climatizadas y en obra nueva.</p>
    <ul>
      <li><strong>Efecto</strong>: reduce p&eacute;rdidas por conducci&oacute;n <strong>15-25 %</strong>.</li>
      <li><strong>Inversi&oacute;n</strong>: 1.500-4.000 &euro; en obra nueva (poliestireno extruido perimetral), dif&iacute;cil de retrofit.</li>
      <li><strong>Ahorro anual</strong>: 100-300 &euro; en piscinas climatizadas.</li>
      <li><strong>ROI</strong>: 5-10 a&ntilde;os.</li>
    </ul>

    <h2>7. Reducci&oacute;n del tiempo de filtraci&oacute;n</h2>
    <p>Muchas piscinas est&aacute;n <strong>sobrefiltrando</strong>. La regla orientativa es:</p>
    <p><strong>Horas diarias de filtraci&oacute;n &asymp; temperatura del agua (&deg;C) / 2</strong></p>
    <ul>
      <li><strong>Efecto</strong>: ahorro directo proporcional a las horas eliminadas.</li>
      <li><strong>Inversi&oacute;n</strong>: 0 &euro; (solo reconfigurar el programador).</li>
      <li><strong>Ahorro anual</strong>: 30-150 &euro; seg&uacute;n el punto de partida.</li>
      <li><strong>ROI</strong>: inmediato.</li>
    </ul>
    <p>Puedes calcular el tiempo &oacute;ptimo para tu piscina en <Link to="/calculadoras">/calculadoras</Link>.</p>

    <h2>8. Tarifa el&eacute;ctrica: PVPC vs mercado libre con discriminaci&oacute;n horaria</h2>
    <p>Filtrar en <strong>horas valle</strong> (t&iacute;picamente 00:00-08:00 los laborables y fines de semana enteros) puede reducir el coste el&eacute;ctrico de la piscina <strong>30-50 %</strong> sin cambiar nada del equipo.</p>
    <ul>
      <li><strong>Efecto</strong>: menor precio por kWh consumido, sin reducir consumo.</li>
      <li><strong>Inversi&oacute;n</strong>: 0 &euro; (cambio de contrato).</li>
      <li><strong>Ahorro anual</strong>: 80-200 &euro;.</li>
      <li><strong>ROI</strong>: inmediato.</li>
    </ul>
    <p>Compara tu tarifa actual y valora si un contrato con discriminaci&oacute;n horaria (2 o 3 periodos) compensa seg&uacute;n tu perfil de uso.</p>

    <h2>9. Placas solares fotovoltaicas dedicadas</h2>
    <p>Solo tiene sentido si el consumo anual de piscina supera <strong>3.000 kWh</strong> (climatizaci&oacute;n o bomba VSD con uso prolongado).</p>
    <ul>
      <li><strong>Efecto</strong>: autoconsumo diurno que cubre 40-70 % del consumo el&eacute;ctrico de la piscina.</li>
      <li><strong>Inversi&oacute;n</strong>: 3.500-7.000 &euro; instalaci&oacute;n de 2-4 kWp dedicados.</li>
      <li><strong>Ahorro anual</strong>: 400-800 &euro;.</li>
      <li><strong>ROI</strong>: 6-10 a&ntilde;os seg&uacute;n radiaci&oacute;n local y precio el&eacute;ctrico.</li>
    </ul>
    <p>Aprovecha el hecho de que la piscina consume principalmente <strong>en horas diurnas</strong> (filtrado y climatizaci&oacute;n), lo que casa muy bien con la generaci&oacute;n fotovoltaica sin necesidad de bater&iacute;as.</p>

    <h2>10. Solar t&eacute;rmica para piscina</h2>
    <p>Sistema de bajo coste espec&iacute;fico para calentar agua de piscina con paneles de polipropileno o EPDM.</p>
    <ul>
      <li><strong>Efecto</strong>: cubre 50-80 % de la demanda t&eacute;rmica en temporada.</li>
      <li><strong>Inversi&oacute;n</strong>: 1.500-3.500 &euro; para superficie de captaci&oacute;n equivalente al 50-70 % de la superficie del vaso.</li>
      <li><strong>Ahorro anual</strong>: 300-700 &euro; en climatizaci&oacute;n.</li>
      <li><strong>ROI</strong>: 3-5 a&ntilde;os.</li>
    </ul>
    <p>Muy competitivo en climas con buena radiaci&oacute;n y demanda de climatizaci&oacute;n moderada.</p>

    <h2>11. Bombas de dosificaci&oacute;n autom&aacute;tica de qu&iacute;micos</h2>
    <p>Complementan el punto 5 con dosificaci&oacute;n proporcional al caudal filtrado.</p>
    <ul>
      <li><strong>Efecto</strong>: consumo de qu&iacute;micos <strong>10-20 % menor</strong>, calidad del agua m&aacute;s estable.</li>
      <li><strong>Inversi&oacute;n</strong>: 300-800 &euro;.</li>
      <li><strong>Ahorro anual</strong>: 40-100 &euro;.</li>
      <li><strong>ROI</strong>: 4-8 a&ntilde;os.</li>
    </ul>

    <h2>12. Detecci&oacute;n de fugas y monitorizaci&oacute;n remota</h2>
    <p>Una fuga de solo 5 mm/d&iacute;a es indistinguible visualmente de la evaporaci&oacute;n pero cuesta <strong>60 m&sup3;/a&ntilde;o</strong> en una piscina de 32 m&sup2;.</p>
    <ul>
      <li><strong>Efecto</strong>: detecci&oacute;n temprana de fugas, control de niveles.</li>
      <li><strong>Inversi&oacute;n</strong>: 400-1.200 &euro; un sistema b&aacute;sico de monitorizaci&oacute;n.</li>
      <li><strong>Ahorro anual</strong>: variable, pero una fuga no detectada puede costar 100-400 &euro;/a&ntilde;o en agua y qu&iacute;micos.</li>
      <li><strong>ROI</strong>: 3-8 a&ntilde;os en r&eacute;gimen normal, inmediato si ten&iacute;as una fuga latente.</li>
    </ul>

    <h2>13. Renovaci&oacute;n de circuito hidr&aacute;ulico sobredimensionado</h2>
    <p>Muchas piscinas antiguas tienen tuber&iacute;as de mayor di&aacute;metro del necesario y bombas sobredimensionadas heredadas.</p>
    <ul>
      <li><strong>Efecto</strong>: reducci&oacute;n de p&eacute;rdidas de carga, permite usar bomba de menor potencia.</li>
      <li><strong>Inversi&oacute;n</strong>: 1.000-3.500 &euro; seg&uacute;n alcance.</li>
      <li><strong>Ahorro anual</strong>: 80-200 &euro;.</li>
      <li><strong>ROI</strong>: 5-15 a&ntilde;os. Suele hacerse en el marco de una reforma m&aacute;s amplia.</li>
    </ul>

    <h2>14. Sistema de tratamiento de agua eficiente (salino, UV, ozono)</h2>
    <p>Sustituir la cloraci&oacute;n manual convencional por un <strong>clorador salino</strong> con automatismo, o combinar UV/ozono para reducir cloro libre, mejora la calidad del agua y reduce qu&iacute;micos.</p>
    <ul>
      <li><strong>Efecto</strong>: consumo de cloro <strong>50-70 % menor</strong>, agua percibida como m&aacute;s agradable.</li>
      <li><strong>Inversi&oacute;n</strong>: 800-2.500 &euro; clorador salino, 1.500-3.500 &euro; UV u ozono.</li>
      <li><strong>Ahorro anual</strong>: 50-150 &euro; en qu&iacute;micos.</li>
      <li><strong>ROI</strong>: 6-12 a&ntilde;os solo por qu&iacute;micos. Se justifica m&aacute;s por confort y calidad del agua.</li>
    </ul>

    <h2>15. Certificado EN 17645: qu&eacute; marca la diferencia entre clase A y clase F</h2>
    <p>Cerramos con la medida m&aacute;s estrat&eacute;gica: <strong>medir</strong> primero, invertir despu&eacute;s.</p>
    <ul>
      <li><strong>Efecto</strong>: identifica los puntos con mejor retorno antes de invertir a ciegas. Una piscina clase F consume <strong>3-5 veces m&aacute;s recursos</strong> que una equivalente clase A.</li>
      <li><strong>Inversi&oacute;n</strong>: <strong>0 &euro;</strong> para la Ficha T&eacute;cnica gratuita en <Link to="/ficha-tecnica">/ficha-tecnica</Link>; sello firmado bajo consulta en <Link to="/pool-check">/pool-check</Link>.</li>
      <li><strong>Ahorro anual</strong>: variable, pero permite priorizar las 14 acciones anteriores en el orden correcto para <strong>tu piscina concreta</strong>.</li>
      <li><strong>ROI</strong>: inmediato.</li>
    </ul>

    <h2>Tabla resumen: acci&oacute;n, inversi&oacute;n, ahorro y ROI</h2>
    <div className="overflow-x-auto not-prose my-8">
      <table className="w-full text-sm border-collapse border border-slate-200">
        <thead className="bg-slate-100">
          <tr>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">#</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Acci&oacute;n</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Inversi&oacute;n aprox.</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Ahorro anual</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">ROI</th>
          </tr>
        </thead>
        <tbody className="text-slate-600">
          <tr><td className="border border-slate-200 px-3 py-2">1</td><td className="border border-slate-200 px-3 py-2">Cubierta t&eacute;rmica motorizada</td><td className="border border-slate-200 px-3 py-2">4.000-9.000 &euro;</td><td className="border border-slate-200 px-3 py-2">500-1.500 &euro;</td><td className="border border-slate-200 px-3 py-2">3-6 a&ntilde;os</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">2</td><td className="border border-slate-200 px-3 py-2">Bomba VSD</td><td className="border border-slate-200 px-3 py-2">600-1.500 &euro;</td><td className="border border-slate-200 px-3 py-2">150-350 &euro;</td><td className="border border-slate-200 px-3 py-2">2-6 a&ntilde;os</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">3</td><td className="border border-slate-200 px-3 py-2">Bomba de calor COP&gt;5</td><td className="border border-slate-200 px-3 py-2">2.500-6.000 &euro;</td><td className="border border-slate-200 px-3 py-2">800-2.000 &euro;</td><td className="border border-slate-200 px-3 py-2">2-4 temporadas</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">4</td><td className="border border-slate-200 px-3 py-2">Iluminaci&oacute;n LED</td><td className="border border-slate-200 px-3 py-2">200-500 &euro;/foco</td><td className="border border-slate-200 px-3 py-2">30-100 &euro;</td><td className="border border-slate-200 px-3 py-2">3-6 a&ntilde;os</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">5</td><td className="border border-slate-200 px-3 py-2">Automatismo pH+ORP</td><td className="border border-slate-200 px-3 py-2">800-2.000 &euro;</td><td className="border border-slate-200 px-3 py-2">60-150 &euro;</td><td className="border border-slate-200 px-3 py-2">5-10 a&ntilde;os</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">6</td><td className="border border-slate-200 px-3 py-2">Aislamiento vaso</td><td className="border border-slate-200 px-3 py-2">1.500-4.000 &euro;</td><td className="border border-slate-200 px-3 py-2">100-300 &euro;</td><td className="border border-slate-200 px-3 py-2">5-10 a&ntilde;os</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">7</td><td className="border border-slate-200 px-3 py-2">Ajuste horas filtraci&oacute;n</td><td className="border border-slate-200 px-3 py-2">0 &euro;</td><td className="border border-slate-200 px-3 py-2">30-150 &euro;</td><td className="border border-slate-200 px-3 py-2">Inmediato</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">8</td><td className="border border-slate-200 px-3 py-2">Tarifa con DH</td><td className="border border-slate-200 px-3 py-2">0 &euro;</td><td className="border border-slate-200 px-3 py-2">80-200 &euro;</td><td className="border border-slate-200 px-3 py-2">Inmediato</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">9</td><td className="border border-slate-200 px-3 py-2">Solar fotovoltaica</td><td className="border border-slate-200 px-3 py-2">3.500-7.000 &euro;</td><td className="border border-slate-200 px-3 py-2">400-800 &euro;</td><td className="border border-slate-200 px-3 py-2">6-10 a&ntilde;os</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">10</td><td className="border border-slate-200 px-3 py-2">Solar t&eacute;rmica</td><td className="border border-slate-200 px-3 py-2">1.500-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">300-700 &euro;</td><td className="border border-slate-200 px-3 py-2">3-5 a&ntilde;os</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">11</td><td className="border border-slate-200 px-3 py-2">Dosificaci&oacute;n qu&iacute;mica</td><td className="border border-slate-200 px-3 py-2">300-800 &euro;</td><td className="border border-slate-200 px-3 py-2">40-100 &euro;</td><td className="border border-slate-200 px-3 py-2">4-8 a&ntilde;os</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">12</td><td className="border border-slate-200 px-3 py-2">Monitorizaci&oacute;n fugas</td><td className="border border-slate-200 px-3 py-2">400-1.200 &euro;</td><td className="border border-slate-200 px-3 py-2">Variable</td><td className="border border-slate-200 px-3 py-2">3-8 a&ntilde;os</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">13</td><td className="border border-slate-200 px-3 py-2">Reforma hidr&aacute;ulica</td><td className="border border-slate-200 px-3 py-2">1.000-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">80-200 &euro;</td><td className="border border-slate-200 px-3 py-2">5-15 a&ntilde;os</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">14</td><td className="border border-slate-200 px-3 py-2">Salino/UV/Ozono</td><td className="border border-slate-200 px-3 py-2">800-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">50-150 &euro;</td><td className="border border-slate-200 px-3 py-2">6-12 a&ntilde;os</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">15</td><td className="border border-slate-200 px-3 py-2">Certificado EN 17645</td><td className="border border-slate-200 px-3 py-2">0 &euro; Ficha</td><td className="border border-slate-200 px-3 py-2">Habilita las 14 anteriores</td><td className="border border-slate-200 px-3 py-2">Inmediato</td></tr>
        </tbody>
      </table>
    </div>

    <hr />

    <p>
      <strong>PoolLex.io</strong> te ayuda a ordenar esta lista seg&uacute;n{" "}
      <strong>tu piscina concreta</strong>. La Ficha T&eacute;cnica gratuita
      eval&uacute;a los 8 componentes de la norma UNE-EN 17645:2023 y prioriza
      las mejoras por impacto y ROI. Empieza en{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> y obt&eacute;n tu{" "}
      <strong>PoolLex ID</strong> &uacute;nico con clasificaci&oacute;n
      estimada A-F en menos de 2 minutos.
    </p>
  </>
);

const Article3BodyEN = () => (
  <>
    <h2>Introduction: the pool, the home's invisible big consumer</h2>
    <p>
      An outdoor residential pool consumes, on average, between{" "}
      <strong>15 and 25 % of the annual electricity total</strong> of a
      single-family home with a pool. If it is{" "}
      <strong>heated with a heat pump</strong> over an extended season, the
      figure can climb to <strong>35 %</strong> or more. In water terms the
      pool typically represents <strong>8-15 % of annual household use</strong>.
    </p>
    <p>
      These numbers are large, but also <strong>negotiable</strong>: most of
      the European residential stock operates with equipment and settings
      well below the current technical optimum. The standard{" "}
      <strong>EN 17645:2023</strong> has finally provided a common language to
      measure that gap, and the 15 actions below rank what gives the best
      return today.
    </p>
    <p>
      The economic ranges are conservative and indicative. All calculations
      assume an average electricity price of{" "}
      <strong>&euro;0.18/kWh</strong> and an average water price of{" "}
      <strong>&euro;2/m&sup3;</strong>, referred to a reference pool of{" "}
      <strong>50 m&sup3; and 32 m&sup2; of surface</strong> in a mild
      Mediterranean climate.
    </p>

    <h2>1. Thermal cover (manual or motorised)</h2>
    <p>The best-ROI action on almost any pool.</p>
    <ul>
      <li><strong>Effect</strong>: cuts evaporation <strong>60-70 %</strong> and night-time heat loss <strong>40-60 %</strong>. Also reduces filtration hours and chemical use.</li>
      <li><strong>Investment</strong>: &euro;400-1,200 manual (floating blanket or winter cover), &euro;4,000-9,000 motorised with housing.</li>
      <li><strong>Annual saving</strong>: &euro;200-500 on unheated pools, &euro;500-1,500 on heated ones.</li>
      <li><strong>Payback</strong>: 1-3 seasons on heated, 2-4 years on unheated.</li>
    </ul>

    <h2>2. Variable-speed (VSD) filtration pump</h2>
    <p>Exploits the <strong>hydraulic affinity laws</strong>: when speed drops, consumption falls roughly with the <strong>cube</strong> of speed. Filtering more hours at low speed uses far less than filtering few hours at high speed.</p>
    <ul>
      <li><strong>Effect</strong>: <strong>60-80 %</strong> saving on filtration use.</li>
      <li><strong>Investment</strong>: &euro;600-1,500 (pump + install).</li>
      <li><strong>Annual saving</strong>: &euro;150-350.</li>
      <li><strong>Payback</strong>: 2-6 years.</li>
    </ul>

    <h2>3. High-COP heat pump vs. electric resistance</h2>
    <p>A heat pump delivers <strong>4-6 thermal kWh per electric kWh</strong> consumed (COP 4-6), against the COP=1 of an electric resistance.</p>
    <ul>
      <li><strong>Effect</strong>: cuts heating electricity by <strong>75-80 %</strong>.</li>
      <li><strong>Investment</strong>: &euro;2,500-6,000 for a 10-15 kW pool heat pump.</li>
      <li><strong>Annual saving (vs. resistance)</strong>: &euro;800-2,000.</li>
      <li><strong>Payback</strong>: 2-4 seasons.</li>
    </ul>
    <p>Aligned with <strong>Regulation (EU) 813/2013</strong> on ecodesign requirements for space heaters.</p>

    <h2>4. Submersible LED lighting</h2>
    <p>A 20-30 W LED lamp replaces a 200-300 W halogen with the same perceived brightness.</p>
    <ul>
      <li><strong>Effect</strong>: up to <strong>10&times; lower</strong> lighting use.</li>
      <li><strong>Investment</strong>: &euro;200-500 per fixture (quality submersible LED).</li>
      <li><strong>Annual saving</strong>: &euro;30-100 depending on hours of use.</li>
      <li><strong>Payback</strong>: 3-6 years. Also 30,000-50,000 h life vs. 2,000-5,000 h halogen.</li>
    </ul>

    <h2>5. Automation: pH + ORP control + scheduling</h2>
    <p>A pH dosing pump plus an ORP controller keep water in range without over-dosing chemicals or extra filtration hours.</p>
    <ul>
      <li><strong>Effect</strong>: <strong>20-40 %</strong> less chemical use, no manual correction, longer shell life.</li>
      <li><strong>Investment</strong>: &euro;800-2,000 (pH pump + ORP probe + controller).</li>
      <li><strong>Annual saving</strong>: &euro;60-150 in chemicals + owner's time.</li>
      <li><strong>Payback</strong>: 5-10 years on chemicals alone, better if you value time.</li>
    </ul>

    <h2>6. Shell and hydraulic circuit insulation</h2>
    <p>Especially relevant on heated pools and new builds.</p>
    <ul>
      <li><strong>Effect</strong>: cuts conduction losses <strong>15-25 %</strong>.</li>
      <li><strong>Investment</strong>: &euro;1,500-4,000 on new builds (perimetral extruded polystyrene), hard to retrofit.</li>
      <li><strong>Annual saving</strong>: &euro;100-300 on heated pools.</li>
      <li><strong>Payback</strong>: 5-10 years.</li>
    </ul>

    <h2>7. Reducing filtration time</h2>
    <p>Many pools are <strong>over-filtering</strong>. Rule of thumb:</p>
    <p><strong>Daily filtration hours &asymp; water temperature (&deg;C) / 2</strong></p>
    <ul>
      <li><strong>Effect</strong>: direct saving proportional to hours removed.</li>
      <li><strong>Investment</strong>: &euro;0 (only reprogram the timer).</li>
      <li><strong>Annual saving</strong>: &euro;30-150 depending on starting point.</li>
      <li><strong>Payback</strong>: immediate.</li>
    </ul>
    <p>You can compute the optimum time for your pool at <Link to="/calculadoras">/calculadoras</Link>.</p>

    <h2>8. Electricity tariff: standard vs. time-of-use</h2>
    <p>Filtering in <strong>off-peak hours</strong> (typically 00:00-08:00 on weekdays and full weekends) can cut pool electricity cost by <strong>30-50 %</strong> without changing any equipment.</p>
    <ul>
      <li><strong>Effect</strong>: lower &euro;/kWh price without cutting kWh use.</li>
      <li><strong>Investment</strong>: &euro;0 (contract change).</li>
      <li><strong>Annual saving</strong>: &euro;80-200.</li>
      <li><strong>Payback</strong>: immediate.</li>
    </ul>
    <p>Compare your current tariff and check whether a time-of-use contract (2 or 3 periods) pays off for your usage profile.</p>

    <h2>9. Dedicated solar PV panels</h2>
    <p>Only makes sense if annual pool consumption exceeds <strong>3,000 kWh</strong> (heating or VSD pump with long use).</p>
    <ul>
      <li><strong>Effect</strong>: daytime self-consumption covering 40-70 % of pool electricity.</li>
      <li><strong>Investment</strong>: &euro;3,500-7,000 for a dedicated 2-4 kWp system.</li>
      <li><strong>Annual saving</strong>: &euro;400-800.</li>
      <li><strong>Payback</strong>: 6-10 years depending on local radiation and power price.</li>
    </ul>
    <p>Take advantage of the fact that a pool consumes mostly <strong>during daytime</strong> (filtering and heating), which pairs well with PV generation without needing batteries.</p>

    <h2>10. Solar thermal for pool heating</h2>
    <p>Low specific-cost system to heat pool water using polypropylene or EPDM panels.</p>
    <ul>
      <li><strong>Effect</strong>: covers 50-80 % of seasonal thermal demand.</li>
      <li><strong>Investment</strong>: &euro;1,500-3,500 for a collection area equivalent to 50-70 % of the pool surface.</li>
      <li><strong>Annual saving</strong>: &euro;300-700 on heating.</li>
      <li><strong>Payback</strong>: 3-5 years.</li>
    </ul>
    <p>Very competitive in climates with good radiation and moderate heating demand.</p>

    <h2>11. Automatic chemical dosing pumps</h2>
    <p>Complement point 5 with dosing proportional to filtered flow.</p>
    <ul>
      <li><strong>Effect</strong>: <strong>10-20 %</strong> less chemical use, more stable water quality.</li>
      <li><strong>Investment</strong>: &euro;300-800.</li>
      <li><strong>Annual saving</strong>: &euro;40-100.</li>
      <li><strong>Payback</strong>: 4-8 years.</li>
    </ul>

    <h2>12. Leak detection and remote monitoring</h2>
    <p>A leak of just 5 mm/day looks visually identical to evaporation but costs <strong>60 m&sup3;/year</strong> on a 32 m&sup2; pool.</p>
    <ul>
      <li><strong>Effect</strong>: early leak detection, level control.</li>
      <li><strong>Investment</strong>: &euro;400-1,200 for a basic monitoring system.</li>
      <li><strong>Annual saving</strong>: variable, but an undetected leak can cost &euro;100-400/year in water and chemicals.</li>
      <li><strong>Payback</strong>: 3-8 years under normal use, immediate if you had a hidden leak.</li>
    </ul>

    <h2>13. Overhaul of an oversized hydraulic circuit</h2>
    <p>Many older pools have oversized pipes and inherited oversized pumps.</p>
    <ul>
      <li><strong>Effect</strong>: reduced head loss, allows a lower-power pump.</li>
      <li><strong>Investment</strong>: &euro;1,000-3,500 depending on scope.</li>
      <li><strong>Annual saving</strong>: &euro;80-200.</li>
      <li><strong>Payback</strong>: 5-15 years. Usually done as part of a wider retrofit.</li>
    </ul>

    <h2>14. Efficient water treatment (salt, UV, ozone)</h2>
    <p>Replacing conventional manual chlorination with an automated <strong>salt chlorinator</strong>, or combining UV/ozone to cut free chlorine, improves water quality and reduces chemicals.</p>
    <ul>
      <li><strong>Effect</strong>: <strong>50-70 %</strong> less chlorine, water perceived as more pleasant.</li>
      <li><strong>Investment</strong>: &euro;800-2,500 salt chlorinator, &euro;1,500-3,500 UV or ozone.</li>
      <li><strong>Annual saving</strong>: &euro;50-150 in chemicals.</li>
      <li><strong>Payback</strong>: 6-12 years on chemicals alone. Justified more by comfort and water quality.</li>
    </ul>

    <h2>15. EN 17645 certificate: what tells class A from class F</h2>
    <p>We close with the most strategic action: <strong>measure</strong> first, invest later.</p>
    <ul>
      <li><strong>Effect</strong>: identifies best-return points before investing blindly. A class F pool uses <strong>3-5 times more resources</strong> than an equivalent class A.</li>
      <li><strong>Investment</strong>: <strong>&euro;0</strong> for the free Technical Sheet at <Link to="/ficha-tecnica">/ficha-tecnica</Link>; signed seal on request at <Link to="/pool-check">/pool-check</Link>.</li>
      <li><strong>Annual saving</strong>: variable, but allows prioritising the previous 14 actions in the right order for <strong>your specific pool</strong>.</li>
      <li><strong>Payback</strong>: immediate.</li>
    </ul>

    <h2>Summary table: action, investment, saving and ROI</h2>
    <div className="overflow-x-auto not-prose my-8">
      <table className="w-full text-sm border-collapse border border-slate-200">
        <thead className="bg-slate-100">
          <tr>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">#</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Action</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Approx. investment</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Annual saving</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Payback</th>
          </tr>
        </thead>
        <tbody className="text-slate-600">
          <tr><td className="border border-slate-200 px-3 py-2">1</td><td className="border border-slate-200 px-3 py-2">Motorised thermal cover</td><td className="border border-slate-200 px-3 py-2">&euro;4,000-9,000</td><td className="border border-slate-200 px-3 py-2">&euro;500-1,500</td><td className="border border-slate-200 px-3 py-2">3-6 years</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">2</td><td className="border border-slate-200 px-3 py-2">VSD pump</td><td className="border border-slate-200 px-3 py-2">&euro;600-1,500</td><td className="border border-slate-200 px-3 py-2">&euro;150-350</td><td className="border border-slate-200 px-3 py-2">2-6 years</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">3</td><td className="border border-slate-200 px-3 py-2">Heat pump COP&gt;5</td><td className="border border-slate-200 px-3 py-2">&euro;2,500-6,000</td><td className="border border-slate-200 px-3 py-2">&euro;800-2,000</td><td className="border border-slate-200 px-3 py-2">2-4 seasons</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">4</td><td className="border border-slate-200 px-3 py-2">LED lighting</td><td className="border border-slate-200 px-3 py-2">&euro;200-500/fixture</td><td className="border border-slate-200 px-3 py-2">&euro;30-100</td><td className="border border-slate-200 px-3 py-2">3-6 years</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">5</td><td className="border border-slate-200 px-3 py-2">pH+ORP automation</td><td className="border border-slate-200 px-3 py-2">&euro;800-2,000</td><td className="border border-slate-200 px-3 py-2">&euro;60-150</td><td className="border border-slate-200 px-3 py-2">5-10 years</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">6</td><td className="border border-slate-200 px-3 py-2">Shell insulation</td><td className="border border-slate-200 px-3 py-2">&euro;1,500-4,000</td><td className="border border-slate-200 px-3 py-2">&euro;100-300</td><td className="border border-slate-200 px-3 py-2">5-10 years</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">7</td><td className="border border-slate-200 px-3 py-2">Filtration hours tune</td><td className="border border-slate-200 px-3 py-2">&euro;0</td><td className="border border-slate-200 px-3 py-2">&euro;30-150</td><td className="border border-slate-200 px-3 py-2">Immediate</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">8</td><td className="border border-slate-200 px-3 py-2">Time-of-use tariff</td><td className="border border-slate-200 px-3 py-2">&euro;0</td><td className="border border-slate-200 px-3 py-2">&euro;80-200</td><td className="border border-slate-200 px-3 py-2">Immediate</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">9</td><td className="border border-slate-200 px-3 py-2">Solar PV</td><td className="border border-slate-200 px-3 py-2">&euro;3,500-7,000</td><td className="border border-slate-200 px-3 py-2">&euro;400-800</td><td className="border border-slate-200 px-3 py-2">6-10 years</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">10</td><td className="border border-slate-200 px-3 py-2">Solar thermal</td><td className="border border-slate-200 px-3 py-2">&euro;1,500-3,500</td><td className="border border-slate-200 px-3 py-2">&euro;300-700</td><td className="border border-slate-200 px-3 py-2">3-5 years</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">11</td><td className="border border-slate-200 px-3 py-2">Chemical dosing</td><td className="border border-slate-200 px-3 py-2">&euro;300-800</td><td className="border border-slate-200 px-3 py-2">&euro;40-100</td><td className="border border-slate-200 px-3 py-2">4-8 years</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">12</td><td className="border border-slate-200 px-3 py-2">Leak monitoring</td><td className="border border-slate-200 px-3 py-2">&euro;400-1,200</td><td className="border border-slate-200 px-3 py-2">Variable</td><td className="border border-slate-200 px-3 py-2">3-8 years</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">13</td><td className="border border-slate-200 px-3 py-2">Hydraulic overhaul</td><td className="border border-slate-200 px-3 py-2">&euro;1,000-3,500</td><td className="border border-slate-200 px-3 py-2">&euro;80-200</td><td className="border border-slate-200 px-3 py-2">5-15 years</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">14</td><td className="border border-slate-200 px-3 py-2">Salt/UV/Ozone</td><td className="border border-slate-200 px-3 py-2">&euro;800-3,500</td><td className="border border-slate-200 px-3 py-2">&euro;50-150</td><td className="border border-slate-200 px-3 py-2">6-12 years</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">15</td><td className="border border-slate-200 px-3 py-2">EN 17645 certificate</td><td className="border border-slate-200 px-3 py-2">&euro;0 free Sheet</td><td className="border border-slate-200 px-3 py-2">Enables the 14 above</td><td className="border border-slate-200 px-3 py-2">Immediate</td></tr>
        </tbody>
      </table>
    </div>

    <hr />

    <p>
      <strong>PoolLex.io</strong> helps you order this list for{" "}
      <strong>your specific pool</strong>. The free Technical Sheet evaluates
      the 8 components of EN 17645:2023 and ranks improvements by impact and
      ROI. Start at <Link to="/ficha-tecnica">/ficha-tecnica</Link> and get
      your unique <strong>PoolLex ID</strong> with an estimated A-F class in
      under 2 minutes.
    </p>
  </>
);
const Article3BodyFR = () => (
  <>
    <h2>Introduction : la piscine, grand consommateur invisible du foyer</h2>
    <p>
      Une piscine r&eacute;sidentielle ext&eacute;rieure consomme, en moyenne,
      entre <strong>15 et 25 % du total &eacute;lectrique annuel</strong>{" "}
      d'une maison unifamiliale avec piscine. Si elle est{" "}
      <strong>chauff&eacute;e par pompe &agrave; chaleur</strong> sur une
      saison &eacute;tendue, ce chiffre peut monter &agrave;{" "}
      <strong>35 %</strong> ou plus. En eau, la piscine repr&eacute;sente
      habituellement <strong>8-15 % de la consommation domestique annuelle</strong>.
    </p>
    <p>
      Ces chiffres sont &eacute;lev&eacute;s, mais aussi{" "}
      <strong>n&eacute;gociables</strong> : la majorit&eacute; du parc
      r&eacute;sidentiel europ&eacute;en fonctionne avec des &eacute;quipements
      et des r&eacute;glages tr&egrave;s &eacute;loign&eacute;s de l'optimum
      technique disponible. La norme{" "}
      <strong>NF EN 17645:2023</strong> a enfin fourni un langage commun pour
      mesurer cet &eacute;cart, et les 15 actions qui suivent classent ce qui
      donne aujourd'hui le meilleur retour.
    </p>
    <p>
      Les fourchettes &eacute;conomiques sont conservatrices et indicatives.
      Tous les calculs supposent un prix moyen de l'&eacute;lectricit&eacute;
      de <strong>0,18 &euro;/kWh</strong> et un prix moyen de l'eau de{" "}
      <strong>2 &euro;/m&sup3;</strong>, r&eacute;f&eacute;renc&eacute;s sur
      une piscine de <strong>50 m&sup3; et 32 m&sup2; de surface</strong> en
      climat m&eacute;diterran&eacute;en.
    </p>

    <h2>1. Couverture thermique (manuelle ou motoris&eacute;e)</h2>
    <p>La mesure &agrave; meilleur ROI sur presque toute piscine.</p>
    <ul>
      <li><strong>Effet</strong> : r&eacute;duit l'&eacute;vaporation <strong>60-70 %</strong> et les pertes de chaleur nocturnes <strong>40-60 %</strong>. R&eacute;duit aussi les heures de filtration n&eacute;cessaires et la consommation de produits chimiques.</li>
      <li><strong>Investissement</strong> : 400-1 200 &euro; manuelle (l&aacute;me flottante ou couverture d'hiver), 4 000-9 000 &euro; motoris&eacute;e avec coffre.</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 200-500 &euro; sur piscine non chauff&eacute;e, 500-1 500 &euro; sur chauff&eacute;e.</li>
      <li><strong>ROI</strong> : 1-3 saisons en chauff&eacute;e, 2-4 ans en non chauff&eacute;e.</li>
    </ul>

    <h2>2. Pompe de filtration VSD (vitesse variable)</h2>
    <p>Exploite les <strong>lois d'affinit&eacute; hydraulique</strong> : en r&eacute;duisant la vitesse, la consommation chute environ au <strong>cube</strong> de la vitesse. Filtrer plus d'heures &agrave; basse vitesse consomme bien moins que filtrer peu d'heures &agrave; haute vitesse.</p>
    <ul>
      <li><strong>Effet</strong> : &eacute;conomie <strong>60-80 %</strong> sur la filtration.</li>
      <li><strong>Investissement</strong> : 600-1 500 &euro; (pompe + installation).</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 150-350 &euro;.</li>
      <li><strong>ROI</strong> : 2-6 ans.</li>
    </ul>

    <h2>3. Pompe &agrave; chaleur haut COP vs. r&eacute;sistance &eacute;lectrique</h2>
    <p>La pompe &agrave; chaleur livre <strong>4-6 kWh thermiques par kWh &eacute;lectrique</strong> consomm&eacute; (COP 4-6), contre le COP=1 d'une r&eacute;sistance &eacute;lectrique.</p>
    <ul>
      <li><strong>Effet</strong> : r&eacute;duit la consommation &eacute;lectrique de chauffage <strong>75-80 %</strong>.</li>
      <li><strong>Investissement</strong> : 2 500-6 000 &euro; pour une pompe &agrave; chaleur de 10-15 kW.</li>
      <li><strong>&Eacute;conomie annuelle (vs. r&eacute;sistance)</strong> : 800-2 000 &euro;.</li>
      <li><strong>ROI</strong> : 2-4 saisons.</li>
    </ul>
    <p>Align&eacute;e avec le <strong>R&egrave;glement (UE) 813/2013</strong> sur les exigences d'&eacute;coconception des appareils de chauffage.</p>

    <h2>4. &Eacute;clairage LED immerg&eacute;</h2>
    <p>Un projecteur LED de 20-30 W remplace un halog&egrave;ne de 200-300 W avec la m&ecirc;me luminosit&eacute; per&ccedil;ue.</p>
    <ul>
      <li><strong>Effet</strong> : consommation jusqu'&agrave; <strong>10&times; moindre</strong> en &eacute;clairage.</li>
      <li><strong>Investissement</strong> : 200-500 &euro; par projecteur (LED immerg&eacute; de qualit&eacute;).</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 30-100 &euro; selon les heures d'usage.</li>
      <li><strong>ROI</strong> : 3-6 ans. Dur&eacute;e 30 000-50 000 h vs 2 000-5 000 h halog&egrave;ne.</li>
    </ul>

    <h2>5. Automatismes : contr&ocirc;le pH + ORP + programmation</h2>
    <p>Une pompe doseuse de pH et un contr&ocirc;leur ORP maintiennent l'eau en r&eacute;gime sans surconsommation de produits ni heures suppl&eacute;mentaires de filtration.</p>
    <ul>
      <li><strong>Effet</strong> : r&eacute;duit les produits chimiques <strong>20-40 %</strong>, &eacute;vite la correction manuelle, prolonge la vie du bassin.</li>
      <li><strong>Investissement</strong> : 800-2 000 &euro; (pompe pH + sonde ORP + contr&ocirc;leur).</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 60-150 &euro; en produits + temps propri&eacute;taire.</li>
      <li><strong>ROI</strong> : 5-10 ans par les seuls produits, meilleur si vous valorisez le temps.</li>
    </ul>

    <h2>6. Isolation du bassin et du circuit hydraulique</h2>
    <p>Particuli&egrave;rement pertinent sur piscines chauff&eacute;es et en construction neuve.</p>
    <ul>
      <li><strong>Effet</strong> : r&eacute;duit les pertes par conduction <strong>15-25 %</strong>.</li>
      <li><strong>Investissement</strong> : 1 500-4 000 &euro; en construction neuve (polystyr&egrave;ne extrud&eacute; p&eacute;rim&eacute;tral), difficile &agrave; retrofit.</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 100-300 &euro; sur piscines chauff&eacute;es.</li>
      <li><strong>ROI</strong> : 5-10 ans.</li>
    </ul>

    <h2>7. R&eacute;duction du temps de filtration</h2>
    <p>Beaucoup de piscines <strong>surfiltrent</strong>. R&egrave;gle indicative :</p>
    <p><strong>Heures quotidiennes de filtration &asymp; temp&eacute;rature de l'eau (&deg;C) / 2</strong></p>
    <ul>
      <li><strong>Effet</strong> : &eacute;conomie directe proportionnelle aux heures &eacute;limin&eacute;es.</li>
      <li><strong>Investissement</strong> : 0 &euro; (reconfigurer le programmateur).</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 30-150 &euro; selon le point de d&eacute;part.</li>
      <li><strong>ROI</strong> : imm&eacute;diat.</li>
    </ul>
    <p>Vous pouvez calculer le temps optimal pour votre piscine sur <Link to="/calculadoras">/calculadoras</Link>.</p>

    <h2>8. Tarif &eacute;lectrique : base vs. heures pleines/creuses</h2>
    <p>Filtrer en <strong>heures creuses</strong> (typiquement 00h-08h en semaine et week-ends entiers) peut r&eacute;duire le co&ucirc;t &eacute;lectrique de la piscine <strong>30-50 %</strong> sans changer d'&eacute;quipement.</p>
    <ul>
      <li><strong>Effet</strong> : prix par kWh moindre, sans r&eacute;duire la consommation.</li>
      <li><strong>Investissement</strong> : 0 &euro; (changement de contrat).</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 80-200 &euro;.</li>
      <li><strong>ROI</strong> : imm&eacute;diat.</li>
    </ul>
    <p>Comparez votre tarif actuel et &eacute;valuez si un contrat avec heures creuses (2 ou 3 p&eacute;riodes) compense selon votre profil d'usage.</p>

    <h2>9. Panneaux solaires photovolta&iuml;ques d&eacute;di&eacute;s</h2>
    <p>Cela n'a de sens que si la consommation annuelle de la piscine d&eacute;passe <strong>3 000 kWh</strong> (chauffage ou pompe VSD avec usage prolong&eacute;).</p>
    <ul>
      <li><strong>Effet</strong> : autoconsommation diurne couvrant 40-70 % de la consommation &eacute;lectrique de la piscine.</li>
      <li><strong>Investissement</strong> : 3 500-7 000 &euro; installation de 2-4 kWp d&eacute;di&eacute;s.</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 400-800 &euro;.</li>
      <li><strong>ROI</strong> : 6-10 ans selon radiation locale et prix &eacute;lectrique.</li>
    </ul>
    <p>Profitez du fait que la piscine consomme principalement <strong>en heures diurnes</strong> (filtration et chauffage), ce qui s'accorde bien avec la g&eacute;n&eacute;ration photovolta&iuml;que sans batteries.</p>

    <h2>10. Solaire thermique pour piscine</h2>
    <p>Syst&egrave;me &agrave; faible co&ucirc;t sp&eacute;cifique pour chauffer l'eau avec des panneaux en polypropyl&egrave;ne ou EPDM.</p>
    <ul>
      <li><strong>Effet</strong> : couvre 50-80 % de la demande thermique de saison.</li>
      <li><strong>Investissement</strong> : 1 500-3 500 &euro; pour une surface de captation &eacute;quivalente &agrave; 50-70 % de la surface du bassin.</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 300-700 &euro; en chauffage.</li>
      <li><strong>ROI</strong> : 3-5 ans.</li>
    </ul>
    <p>Tr&egrave;s comp&eacute;titif en climats &agrave; bonne radiation et demande de chauffage mod&eacute;r&eacute;e.</p>

    <h2>11. Pompes de dosage automatique de produits</h2>
    <p>Compl&egrave;tent le point 5 avec un dosage proportionnel au d&eacute;bit filtr&eacute;.</p>
    <ul>
      <li><strong>Effet</strong> : consommation de produits <strong>10-20 % moindre</strong>, qualit&eacute; d'eau plus stable.</li>
      <li><strong>Investissement</strong> : 300-800 &euro;.</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 40-100 &euro;.</li>
      <li><strong>ROI</strong> : 4-8 ans.</li>
    </ul>

    <h2>12. D&eacute;tection de fuites et supervision &agrave; distance</h2>
    <p>Une fuite de seulement 5 mm/jour est visuellement indissociable de l'&eacute;vaporation mais co&ucirc;te <strong>60 m&sup3;/an</strong> sur une piscine de 32 m&sup2;.</p>
    <ul>
      <li><strong>Effet</strong> : d&eacute;tection pr&eacute;coce des fuites, contr&ocirc;le des niveaux.</li>
      <li><strong>Investissement</strong> : 400-1 200 &euro; un syst&egrave;me basique de supervision.</li>
      <li><strong>&Eacute;conomie annuelle</strong> : variable, mais une fuite non d&eacute;tect&eacute;e peut co&ucirc;ter 100-400 &euro;/an en eau et produits.</li>
      <li><strong>ROI</strong> : 3-8 ans en r&eacute;gime normal, imm&eacute;diat si vous aviez une fuite latente.</li>
    </ul>

    <h2>13. R&eacute;novation d'un circuit hydraulique surdimensionn&eacute;</h2>
    <p>Beaucoup de piscines anciennes ont des tuyauteries plus grosses que n&eacute;cessaire et des pompes surdimensionn&eacute;es h&eacute;rit&eacute;es.</p>
    <ul>
      <li><strong>Effet</strong> : r&eacute;duction des pertes de charge, permet une pompe de moindre puissance.</li>
      <li><strong>Investissement</strong> : 1 000-3 500 &euro; selon l'ampleur.</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 80-200 &euro;.</li>
      <li><strong>ROI</strong> : 5-15 ans. Se fait en g&eacute;n&eacute;ral dans le cadre d'une r&eacute;novation plus large.</li>
    </ul>

    <h2>14. Traitement d'eau efficient (sel, UV, ozone)</h2>
    <p>Remplacer la chloration manuelle par un <strong>&eacute;lectrolyseur au sel</strong> automatis&eacute;, ou combiner UV/ozone pour r&eacute;duire le chlore libre, am&eacute;liore la qualit&eacute; de l'eau et r&eacute;duit les produits.</p>
    <ul>
      <li><strong>Effet</strong> : consommation de chlore <strong>50-70 % moindre</strong>, eau per&ccedil;ue comme plus agr&eacute;able.</li>
      <li><strong>Investissement</strong> : 800-2 500 &euro; &eacute;lectrolyseur au sel, 1 500-3 500 &euro; UV ou ozone.</li>
      <li><strong>&Eacute;conomie annuelle</strong> : 50-150 &euro; en produits.</li>
      <li><strong>ROI</strong> : 6-12 ans par les seuls produits. Se justifie davantage par le confort et la qualit&eacute; d'eau.</li>
    </ul>

    <h2>15. Certificat NF EN 17645 : ce qui distingue la classe A de la F</h2>
    <p>Nous terminons par la mesure la plus strat&eacute;gique : <strong>mesurer</strong> d'abord, investir ensuite.</p>
    <ul>
      <li><strong>Effet</strong> : identifie les points &agrave; meilleur retour avant d'investir &agrave; l'aveugle. Une piscine classe F consomme <strong>3-5 fois plus de ressources</strong> qu'une &eacute;quivalente classe A.</li>
      <li><strong>Investissement</strong> : <strong>0 &euro;</strong> pour la Fiche Technique gratuite sur <Link to="/ficha-tecnica">/ficha-tecnica</Link> ; sceau sign&eacute; sur devis via <Link to="/pool-check">/pool-check</Link>.</li>
      <li><strong>&Eacute;conomie annuelle</strong> : variable, mais permet de prioriser les 14 actions pr&eacute;c&eacute;dentes dans le bon ordre pour <strong>votre piscine concr&egrave;te</strong>.</li>
      <li><strong>ROI</strong> : imm&eacute;diat.</li>
    </ul>

    <h2>Tableau r&eacute;capitulatif : action, investissement, &eacute;conomie et ROI</h2>
    <div className="overflow-x-auto not-prose my-8">
      <table className="w-full text-sm border-collapse border border-slate-200">
        <thead className="bg-slate-100">
          <tr>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">#</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Action</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Investissement approx.</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">&Eacute;conomie annuelle</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">ROI</th>
          </tr>
        </thead>
        <tbody className="text-slate-600">
          <tr><td className="border border-slate-200 px-3 py-2">1</td><td className="border border-slate-200 px-3 py-2">Couverture thermique motoris&eacute;e</td><td className="border border-slate-200 px-3 py-2">4 000-9 000 &euro;</td><td className="border border-slate-200 px-3 py-2">500-1 500 &euro;</td><td className="border border-slate-200 px-3 py-2">3-6 ans</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">2</td><td className="border border-slate-200 px-3 py-2">Pompe VSD</td><td className="border border-slate-200 px-3 py-2">600-1 500 &euro;</td><td className="border border-slate-200 px-3 py-2">150-350 &euro;</td><td className="border border-slate-200 px-3 py-2">2-6 ans</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">3</td><td className="border border-slate-200 px-3 py-2">Pompe &agrave; chaleur COP&gt;5</td><td className="border border-slate-200 px-3 py-2">2 500-6 000 &euro;</td><td className="border border-slate-200 px-3 py-2">800-2 000 &euro;</td><td className="border border-slate-200 px-3 py-2">2-4 saisons</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">4</td><td className="border border-slate-200 px-3 py-2">&Eacute;clairage LED</td><td className="border border-slate-200 px-3 py-2">200-500 &euro;/projecteur</td><td className="border border-slate-200 px-3 py-2">30-100 &euro;</td><td className="border border-slate-200 px-3 py-2">3-6 ans</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">5</td><td className="border border-slate-200 px-3 py-2">Automatisme pH+ORP</td><td className="border border-slate-200 px-3 py-2">800-2 000 &euro;</td><td className="border border-slate-200 px-3 py-2">60-150 &euro;</td><td className="border border-slate-200 px-3 py-2">5-10 ans</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">6</td><td className="border border-slate-200 px-3 py-2">Isolation bassin</td><td className="border border-slate-200 px-3 py-2">1 500-4 000 &euro;</td><td className="border border-slate-200 px-3 py-2">100-300 &euro;</td><td className="border border-slate-200 px-3 py-2">5-10 ans</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">7</td><td className="border border-slate-200 px-3 py-2">Ajustement filtration</td><td className="border border-slate-200 px-3 py-2">0 &euro;</td><td className="border border-slate-200 px-3 py-2">30-150 &euro;</td><td className="border border-slate-200 px-3 py-2">Imm&eacute;diat</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">8</td><td className="border border-slate-200 px-3 py-2">Tarif heures creuses</td><td className="border border-slate-200 px-3 py-2">0 &euro;</td><td className="border border-slate-200 px-3 py-2">80-200 &euro;</td><td className="border border-slate-200 px-3 py-2">Imm&eacute;diat</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">9</td><td className="border border-slate-200 px-3 py-2">Solaire photovolta&iuml;que</td><td className="border border-slate-200 px-3 py-2">3 500-7 000 &euro;</td><td className="border border-slate-200 px-3 py-2">400-800 &euro;</td><td className="border border-slate-200 px-3 py-2">6-10 ans</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">10</td><td className="border border-slate-200 px-3 py-2">Solaire thermique</td><td className="border border-slate-200 px-3 py-2">1 500-3 500 &euro;</td><td className="border border-slate-200 px-3 py-2">300-700 &euro;</td><td className="border border-slate-200 px-3 py-2">3-5 ans</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">11</td><td className="border border-slate-200 px-3 py-2">Dosage chimique</td><td className="border border-slate-200 px-3 py-2">300-800 &euro;</td><td className="border border-slate-200 px-3 py-2">40-100 &euro;</td><td className="border border-slate-200 px-3 py-2">4-8 ans</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">12</td><td className="border border-slate-200 px-3 py-2">Supervision fuites</td><td className="border border-slate-200 px-3 py-2">400-1 200 &euro;</td><td className="border border-slate-200 px-3 py-2">Variable</td><td className="border border-slate-200 px-3 py-2">3-8 ans</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">13</td><td className="border border-slate-200 px-3 py-2">R&eacute;novation hydraulique</td><td className="border border-slate-200 px-3 py-2">1 000-3 500 &euro;</td><td className="border border-slate-200 px-3 py-2">80-200 &euro;</td><td className="border border-slate-200 px-3 py-2">5-15 ans</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">14</td><td className="border border-slate-200 px-3 py-2">Sel/UV/Ozone</td><td className="border border-slate-200 px-3 py-2">800-3 500 &euro;</td><td className="border border-slate-200 px-3 py-2">50-150 &euro;</td><td className="border border-slate-200 px-3 py-2">6-12 ans</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">15</td><td className="border border-slate-200 px-3 py-2">Certificat NF EN 17645</td><td className="border border-slate-200 px-3 py-2">0 &euro; Fiche</td><td className="border border-slate-200 px-3 py-2">Active les 14 pr&eacute;c&eacute;dentes</td><td className="border border-slate-200 px-3 py-2">Imm&eacute;diat</td></tr>
        </tbody>
      </table>
    </div>

    <hr />

    <p>
      <strong>PoolLex.io</strong> vous aide &agrave; ordonner cette liste pour{" "}
      <strong>votre piscine concr&egrave;te</strong>. La Fiche Technique
      gratuite &eacute;value les 8 composants de la NF EN 17645:2023 et
      priorise les am&eacute;liorations par impact et ROI. Commencez sur{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> et obtenez votre{" "}
      <strong>PoolLex ID</strong> unique avec classification estim&eacute;e
      A-F en moins de 2 minutes.
    </p>
  </>
);
const Article3BodyDE = () => (
  <>
    <h2>Einleitung: der Pool, gro&szlig;er unsichtbarer Verbraucher im Haushalt</h2>
    <p>
      Ein privater Freibad-Pool verbraucht im Schnitt zwischen{" "}
      <strong>15 und 25 % des j&auml;hrlichen Gesamtstromverbrauchs</strong>{" "}
      eines Einfamilienhauses mit Pool. Wird er per{" "}
      <strong>W&auml;rmepumpe</strong> &uuml;ber eine verl&auml;ngerte Saison
      beheizt, kann der Wert auf <strong>35 %</strong> oder mehr steigen. Beim
      Wasser macht der Pool typischerweise{" "}
      <strong>8-15 % des j&auml;hrlichen Haushaltsverbrauchs</strong> aus.
    </p>
    <p>
      Diese Zahlen sind gro&szlig;, aber auch <strong>verhandelbar</strong>:
      der Gro&szlig;teil des europ&auml;ischen Wohnpoolbestands arbeitet mit
      Ausstattung und Einstellungen, die weit vom technischen Optimum
      entfernt sind. Die Norm <strong>DIN EN 17645:2023</strong> hat endlich
      eine gemeinsame Sprache f&uuml;r diese L&uuml;cke geliefert, und die
      folgenden 15 Ma&szlig;nahmen ordnen, was heute den besten Ertrag bringt.
    </p>
    <p>
      Die wirtschaftlichen Spannen sind konservativ und indikativ. Alle
      Berechnungen unterstellen einen Strompreis von{" "}
      <strong>0,18 &euro;/kWh</strong> und einen Wasserpreis von{" "}
      <strong>2 &euro;/m&sup3;</strong>, bezogen auf einen Referenzpool von{" "}
      <strong>50 m&sup3; und 32 m&sup2; Oberfl&auml;che</strong> in gem&auml;&szlig;igtem
      mediterranem Klima.
    </p>

    <h2>1. Thermoabdeckung (manuell oder motorisiert)</h2>
    <p>Die Ma&szlig;nahme mit dem besten ROI f&uuml;r fast jeden Pool.</p>
    <ul>
      <li><strong>Wirkung</strong>: senkt Verdunstung <strong>60-70 %</strong> und n&auml;chtliche W&auml;rmeverluste <strong>40-60 %</strong>. Reduziert auch Filterstunden und Chemieverbrauch.</li>
      <li><strong>Investition</strong>: 400-1.200 &euro; manuell (Schwimmfolie oder Winterabdeckung), 4.000-9.000 &euro; motorisiert mit Kasten.</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 200-500 &euro; bei unbeheizten, 500-1.500 &euro; bei beheizten Pools.</li>
      <li><strong>Amortisation</strong>: 1-3 Saisons bei beheizten, 2-4 Jahre bei unbeheizten.</li>
    </ul>

    <h2>2. Drehzahlgeregelte Filterpumpe (VSD)</h2>
    <p>Nutzt die <strong>hydraulischen &Auml;hnlichkeitsgesetze</strong>: mit sinkender Drehzahl f&auml;llt der Verbrauch etwa mit der <strong>dritten Potenz</strong> der Drehzahl. L&auml;nger bei niedriger Drehzahl zu filtern verbraucht viel weniger als kurz bei hoher Drehzahl.</p>
    <ul>
      <li><strong>Wirkung</strong>: <strong>60-80 %</strong> Einsparung beim Filterstrom.</li>
      <li><strong>Investition</strong>: 600-1.500 &euro; (Pumpe + Installation).</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 150-350 &euro;.</li>
      <li><strong>Amortisation</strong>: 2-6 Jahre.</li>
    </ul>

    <h2>3. W&auml;rmepumpe mit hohem COP vs. Heizstab</h2>
    <p>Die W&auml;rmepumpe liefert <strong>4-6 kWh W&auml;rme pro kWh Strom</strong> (COP 4-6), gegen&uuml;ber COP=1 eines elektrischen Heizstabs.</p>
    <ul>
      <li><strong>Wirkung</strong>: senkt den Heizstromverbrauch um <strong>75-80 %</strong>.</li>
      <li><strong>Investition</strong>: 2.500-6.000 &euro; f&uuml;r eine Pool-W&auml;rmepumpe von 10-15 kW.</li>
      <li><strong>J&auml;hrliche Einsparung (vs. Heizstab)</strong>: 800-2.000 &euro;.</li>
      <li><strong>Amortisation</strong>: 2-4 Saisons.</li>
    </ul>
    <p>Konform mit der <strong>Verordnung (EU) 813/2013</strong> zu &Ouml;kodesign-Anforderungen an Raumheizger&auml;te.</p>

    <h2>4. Unterwasser-LED-Beleuchtung</h2>
    <p>Eine LED-Leuchte mit 20-30 W ersetzt einen Halogenstrahler mit 200-300 W bei gleicher wahrgenommener Helligkeit.</p>
    <ul>
      <li><strong>Wirkung</strong>: bis zu <strong>10-fach geringerer</strong> Beleuchtungsverbrauch.</li>
      <li><strong>Investition</strong>: 200-500 &euro; pro Leuchte (Qualit&auml;ts-LED).</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 30-100 &euro; je nach Nutzungsdauer.</li>
      <li><strong>Amortisation</strong>: 3-6 Jahre. Lebensdauer 30.000-50.000 h vs. 2.000-5.000 h Halogen.</li>
    </ul>

    <h2>5. Automation: pH-Regelung + ORP + Zeitprogrammierung</h2>
    <p>Eine pH-Dosierpumpe und ein ORP-Regler halten das Wasser im Sollbereich, ohne Chemie&uuml;berdosierung und Filter&uuml;berstunden.</p>
    <ul>
      <li><strong>Wirkung</strong>: senkt Chemieverbrauch um <strong>20-40 %</strong>, vermeidet manuelle Nachjustierung, verl&auml;ngert Beckenlebensdauer.</li>
      <li><strong>Investition</strong>: 800-2.000 &euro; (pH-Pumpe + ORP-Sonde + Regler).</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 60-150 &euro; an Chemie plus Eigenzeit.</li>
      <li><strong>Amortisation</strong>: 5-10 Jahre nur auf Chemie, besser wenn Sie Zeit bewerten.</li>
    </ul>

    <h2>6. D&auml;mmung von Becken und Hydraulikkreislauf</h2>
    <p>Besonders relevant bei beheizten Pools und im Neubau.</p>
    <ul>
      <li><strong>Wirkung</strong>: senkt Leitungsverluste um <strong>15-25 %</strong>.</li>
      <li><strong>Investition</strong>: 1.500-4.000 &euro; im Neubau (perimetraler extrudierter Polystyrol), schwer nachr&uuml;stbar.</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 100-300 &euro; bei beheizten Pools.</li>
      <li><strong>Amortisation</strong>: 5-10 Jahre.</li>
    </ul>

    <h2>7. Reduzierung der Filterzeit</h2>
    <p>Viele Pools werden <strong>&uuml;berfiltert</strong>. Faustregel:</p>
    <p><strong>T&auml;gliche Filterstunden &asymp; Wassertemperatur (&deg;C) / 2</strong></p>
    <ul>
      <li><strong>Wirkung</strong>: direkte Einsparung proportional zu den eingesparten Stunden.</li>
      <li><strong>Investition</strong>: 0 &euro; (nur Timer umprogrammieren).</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 30-150 &euro; je nach Ausgangspunkt.</li>
      <li><strong>Amortisation</strong>: sofort.</li>
    </ul>
    <p>Die optimale Zeit f&uuml;r Ihren Pool berechnen Sie unter <Link to="/calculadoras">/calculadoras</Link>.</p>

    <h2>8. Stromtarif: Standard vs. Zeittarif</h2>
    <p>Filtern in <strong>Nebenzeiten</strong> (typischerweise 00:00-08:00 werktags und ganze Wochenenden) kann die Stromkosten des Pools um <strong>30-50 %</strong> senken, ohne Ausstattung zu &auml;ndern.</p>
    <ul>
      <li><strong>Wirkung</strong>: geringerer Preis pro kWh, ohne Verbrauch zu reduzieren.</li>
      <li><strong>Investition</strong>: 0 &euro; (Vertragswechsel).</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 80-200 &euro;.</li>
      <li><strong>Amortisation</strong>: sofort.</li>
    </ul>
    <p>Vergleichen Sie Ihren aktuellen Tarif und pr&uuml;fen Sie, ob sich ein Zeittarif (2 oder 3 Perioden) f&uuml;r Ihr Nutzungsprofil lohnt.</p>

    <h2>9. Dedizierte Photovoltaik-Anlage</h2>
    <p>Sinnvoll nur, wenn der Jahresverbrauch des Pools <strong>3.000 kWh</strong> &uuml;bersteigt (Heizung oder VSD-Pumpe bei langer Nutzung).</p>
    <ul>
      <li><strong>Wirkung</strong>: tags&uuml;ber Eigenverbrauch, der 40-70 % des Poolstromverbrauchs deckt.</li>
      <li><strong>Investition</strong>: 3.500-7.000 &euro; f&uuml;r eine 2-4 kWp-Anlage.</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 400-800 &euro;.</li>
      <li><strong>Amortisation</strong>: 6-10 Jahre je nach Strahlung und Strompreis.</li>
    </ul>
    <p>Nutzen Sie, dass der Pool haupts&auml;chlich <strong>tags&uuml;ber</strong> verbraucht (Filtern und Heizen), was gut zur PV-Erzeugung ohne Batteriespeicher passt.</p>

    <h2>10. Solarthermie f&uuml;r Poolheizung</h2>
    <p>System mit niedrigen spezifischen Kosten zum Erw&auml;rmen von Poolwasser mit Polypropylen- oder EPDM-Kollektoren.</p>
    <ul>
      <li><strong>Wirkung</strong>: deckt 50-80 % des saisonalen W&auml;rmebedarfs.</li>
      <li><strong>Investition</strong>: 1.500-3.500 &euro; f&uuml;r eine Kollektorfl&auml;che, die 50-70 % der Beckenfl&auml;che entspricht.</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 300-700 &euro; bei der Heizung.</li>
      <li><strong>Amortisation</strong>: 3-5 Jahre.</li>
    </ul>
    <p>Sehr wettbewerbsf&auml;hig in Klimaregionen mit guter Strahlung und moderatem Heizbedarf.</p>

    <h2>11. Automatische Dosierpumpen f&uuml;r Chemie</h2>
    <p>Erg&auml;nzen Punkt 5 mit einer Dosierung proportional zum gefilterten Durchfluss.</p>
    <ul>
      <li><strong>Wirkung</strong>: Chemieverbrauch <strong>10-20 % geringer</strong>, stabilere Wasserqualit&auml;t.</li>
      <li><strong>Investition</strong>: 300-800 &euro;.</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 40-100 &euro;.</li>
      <li><strong>Amortisation</strong>: 4-8 Jahre.</li>
    </ul>

    <h2>12. Leckerkennung und Fern&uuml;berwachung</h2>
    <p>Ein Leck von nur 5 mm/Tag ist optisch nicht von Verdunstung zu unterscheiden, kostet aber <strong>60 m&sup3;/Jahr</strong> bei einem 32 m&sup2; Pool.</p>
    <ul>
      <li><strong>Wirkung</strong>: fr&uuml;he Leckerkennung, Pegel&uuml;berwachung.</li>
      <li><strong>Investition</strong>: 400-1.200 &euro; f&uuml;r ein einfaches &Uuml;berwachungssystem.</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: variabel, aber ein unentdecktes Leck kann 100-400 &euro;/Jahr an Wasser und Chemie kosten.</li>
      <li><strong>Amortisation</strong>: 3-8 Jahre bei Normalbetrieb, sofort bei bestehendem Leck.</li>
    </ul>

    <h2>13. Sanierung eines &uuml;berdimensionierten Hydraulikkreislaufs</h2>
    <p>Viele alte Pools haben zu gro&szlig;e Rohre und &uuml;berdimensionierte, geerbte Pumpen.</p>
    <ul>
      <li><strong>Wirkung</strong>: geringere Druckverluste, erm&ouml;glicht eine kleinere Pumpe.</li>
      <li><strong>Investition</strong>: 1.000-3.500 &euro; je nach Umfang.</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 80-200 &euro;.</li>
      <li><strong>Amortisation</strong>: 5-15 Jahre. Meist Teil einer gr&ouml;&szlig;eren Sanierung.</li>
    </ul>

    <h2>14. Effiziente Wasseraufbereitung (Salz, UV, Ozon)</h2>
    <p>Die manuelle Chlorung durch eine automatisierte <strong>Salzelektrolyse</strong> zu ersetzen oder UV/Ozon zu kombinieren, verbessert die Wasserqualit&auml;t und reduziert Chemikalien.</p>
    <ul>
      <li><strong>Wirkung</strong>: <strong>50-70 % weniger</strong> Chlorverbrauch, das Wasser wird als angenehmer empfunden.</li>
      <li><strong>Investition</strong>: 800-2.500 &euro; Salzelektrolyse, 1.500-3.500 &euro; UV oder Ozon.</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: 50-150 &euro; an Chemie.</li>
      <li><strong>Amortisation</strong>: 6-12 Jahre nur &uuml;ber Chemie. St&auml;rker durch Komfort und Wasserqualit&auml;t gerechtfertigt.</li>
    </ul>

    <h2>15. DIN EN 17645-Zertifikat: was Klasse A von Klasse F unterscheidet</h2>
    <p>Wir schlie&szlig;en mit der strategischsten Ma&szlig;nahme: erst <strong>messen</strong>, dann investieren.</p>
    <ul>
      <li><strong>Wirkung</strong>: identifiziert die Punkte mit dem besten Ertrag, bevor blind investiert wird. Ein Klasse-F-Pool verbraucht <strong>3-5 Mal mehr Ressourcen</strong> als ein &auml;quivalenter Klasse A.</li>
      <li><strong>Investition</strong>: <strong>0 &euro;</strong> f&uuml;r das kostenlose technische Datenblatt unter <Link to="/ficha-tecnica">/ficha-tecnica</Link>; signiertes Siegel auf Anfrage unter <Link to="/pool-check">/pool-check</Link>.</li>
      <li><strong>J&auml;hrliche Einsparung</strong>: variabel, aber erlaubt die Priorisierung der 14 vorherigen Ma&szlig;nahmen in der richtigen Reihenfolge f&uuml;r <strong>Ihren konkreten Pool</strong>.</li>
      <li><strong>Amortisation</strong>: sofort.</li>
    </ul>

    <h2>&Uuml;bersichtstabelle: Ma&szlig;nahme, Investition, Einsparung und ROI</h2>
    <div className="overflow-x-auto not-prose my-8">
      <table className="w-full text-sm border-collapse border border-slate-200">
        <thead className="bg-slate-100">
          <tr>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">#</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Ma&szlig;nahme</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Investition ca.</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">J&auml;hrliche Einsparung</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">ROI</th>
          </tr>
        </thead>
        <tbody className="text-slate-600">
          <tr><td className="border border-slate-200 px-3 py-2">1</td><td className="border border-slate-200 px-3 py-2">Motorisierte Thermoabdeckung</td><td className="border border-slate-200 px-3 py-2">4.000-9.000 &euro;</td><td className="border border-slate-200 px-3 py-2">500-1.500 &euro;</td><td className="border border-slate-200 px-3 py-2">3-6 Jahre</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">2</td><td className="border border-slate-200 px-3 py-2">VSD-Pumpe</td><td className="border border-slate-200 px-3 py-2">600-1.500 &euro;</td><td className="border border-slate-200 px-3 py-2">150-350 &euro;</td><td className="border border-slate-200 px-3 py-2">2-6 Jahre</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">3</td><td className="border border-slate-200 px-3 py-2">W&auml;rmepumpe COP&gt;5</td><td className="border border-slate-200 px-3 py-2">2.500-6.000 &euro;</td><td className="border border-slate-200 px-3 py-2">800-2.000 &euro;</td><td className="border border-slate-200 px-3 py-2">2-4 Saisons</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">4</td><td className="border border-slate-200 px-3 py-2">LED-Beleuchtung</td><td className="border border-slate-200 px-3 py-2">200-500 &euro;/Leuchte</td><td className="border border-slate-200 px-3 py-2">30-100 &euro;</td><td className="border border-slate-200 px-3 py-2">3-6 Jahre</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">5</td><td className="border border-slate-200 px-3 py-2">pH+ORP Automation</td><td className="border border-slate-200 px-3 py-2">800-2.000 &euro;</td><td className="border border-slate-200 px-3 py-2">60-150 &euro;</td><td className="border border-slate-200 px-3 py-2">5-10 Jahre</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">6</td><td className="border border-slate-200 px-3 py-2">Beckend&auml;mmung</td><td className="border border-slate-200 px-3 py-2">1.500-4.000 &euro;</td><td className="border border-slate-200 px-3 py-2">100-300 &euro;</td><td className="border border-slate-200 px-3 py-2">5-10 Jahre</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">7</td><td className="border border-slate-200 px-3 py-2">Filterstunden anpassen</td><td className="border border-slate-200 px-3 py-2">0 &euro;</td><td className="border border-slate-200 px-3 py-2">30-150 &euro;</td><td className="border border-slate-200 px-3 py-2">Sofort</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">8</td><td className="border border-slate-200 px-3 py-2">Zeittarif</td><td className="border border-slate-200 px-3 py-2">0 &euro;</td><td className="border border-slate-200 px-3 py-2">80-200 &euro;</td><td className="border border-slate-200 px-3 py-2">Sofort</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">9</td><td className="border border-slate-200 px-3 py-2">Photovoltaik</td><td className="border border-slate-200 px-3 py-2">3.500-7.000 &euro;</td><td className="border border-slate-200 px-3 py-2">400-800 &euro;</td><td className="border border-slate-200 px-3 py-2">6-10 Jahre</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">10</td><td className="border border-slate-200 px-3 py-2">Solarthermie</td><td className="border border-slate-200 px-3 py-2">1.500-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">300-700 &euro;</td><td className="border border-slate-200 px-3 py-2">3-5 Jahre</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">11</td><td className="border border-slate-200 px-3 py-2">Chemie-Dosierung</td><td className="border border-slate-200 px-3 py-2">300-800 &euro;</td><td className="border border-slate-200 px-3 py-2">40-100 &euro;</td><td className="border border-slate-200 px-3 py-2">4-8 Jahre</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">12</td><td className="border border-slate-200 px-3 py-2">Leck&uuml;berwachung</td><td className="border border-slate-200 px-3 py-2">400-1.200 &euro;</td><td className="border border-slate-200 px-3 py-2">Variabel</td><td className="border border-slate-200 px-3 py-2">3-8 Jahre</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">13</td><td className="border border-slate-200 px-3 py-2">Hydrauliksanierung</td><td className="border border-slate-200 px-3 py-2">1.000-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">80-200 &euro;</td><td className="border border-slate-200 px-3 py-2">5-15 Jahre</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">14</td><td className="border border-slate-200 px-3 py-2">Salz/UV/Ozon</td><td className="border border-slate-200 px-3 py-2">800-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">50-150 &euro;</td><td className="border border-slate-200 px-3 py-2">6-12 Jahre</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">15</td><td className="border border-slate-200 px-3 py-2">DIN EN 17645-Zertifikat</td><td className="border border-slate-200 px-3 py-2">0 &euro; Datenblatt</td><td className="border border-slate-200 px-3 py-2">Aktiviert die 14 vorherigen</td><td className="border border-slate-200 px-3 py-2">Sofort</td></tr>
        </tbody>
      </table>
    </div>

    <hr />

    <p>
      <strong>PoolLex.io</strong> hilft Ihnen, diese Liste f&uuml;r{" "}
      <strong>Ihren konkreten Pool</strong> zu ordnen. Das kostenlose
      technische Datenblatt bewertet die 8 Komponenten der DIN EN 17645:2023
      und priorisiert die Verbesserungen nach Wirkung und ROI. Starten Sie
      unter <Link to="/ficha-tecnica">/ficha-tecnica</Link> und erhalten Sie
      Ihre eindeutige <strong>PoolLex ID</strong> mit gesch&auml;tzter Klasse
      A-F in unter 2 Minuten.
    </p>
  </>
);
const Article3BodyIT = () => (
  <>
    <h2>Introduzione: la piscina, grande consumatore invisibile della casa</h2>
    <p>
      Una piscina residenziale esterna consuma, in media, tra il{" "}
      <strong>15 e il 25 % del totale elettrico annuo</strong> di una casa
      unifamiliare con piscina. Se &egrave;{" "}
      <strong>riscaldata con pompa di calore</strong> in stagione estesa, il
      valore pu&ograve; salire al <strong>35 %</strong> o pi&ugrave;. In
      acqua, la piscina rappresenta di solito{" "}
      <strong>l'8-15 % del consumo domestico annuo</strong>.
    </p>
    <p>
      Sono numeri grandi, ma anche <strong>negoziabili</strong>: la maggior
      parte del parco residenziale europeo funziona con equipaggiamento e
      configurazioni molto lontane dall'ottimo tecnico disponibile. La norma{" "}
      <strong>UNI EN 17645:2023</strong> ha finalmente fornito un linguaggio
      comune per misurare questa distanza, e le 15 azioni che seguono
      ordinano ci&ograve; che oggi rende di pi&ugrave;.
    </p>
    <p>
      Gli intervalli economici sono conservativi e indicativi. Tutti i
      calcoli assumono un prezzo medio dell'elettricit&agrave; di{" "}
      <strong>0,18 &euro;/kWh</strong> e un prezzo medio dell'acqua di{" "}
      <strong>2 &euro;/m&sup3;</strong>, riferiti a una piscina di
      riferimento di <strong>50 m&sup3; e 32 m&sup2; di superficie</strong> in
      clima mediterraneo.
    </p>

    <h2>1. Copertura termica (manuale o motorizzata)</h2>
    <p>La misura con miglior ROI su quasi qualsiasi piscina.</p>
    <ul>
      <li><strong>Effetto</strong>: riduce l'evaporazione <strong>60-70 %</strong> e le perdite di calore notturne <strong>40-60 %</strong>. Riduce anche le ore di filtrazione necessarie e il consumo di chimici.</li>
      <li><strong>Investimento</strong>: 400-1.200 &euro; manuale (telo flottante o copertura invernale), 4.000-9.000 &euro; motorizzata con cassone.</li>
      <li><strong>Risparmio annuo</strong>: 200-500 &euro; su piscina non riscaldata, 500-1.500 &euro; su riscaldata.</li>
      <li><strong>ROI</strong>: 1-3 stagioni su riscaldata, 2-4 anni su non riscaldata.</li>
    </ul>

    <h2>2. Pompa di filtrazione VSD (velocit&agrave; variabile)</h2>
    <p>Sfrutta le <strong>leggi di affinit&agrave; idraulica</strong>: riducendo la velocit&agrave;, il consumo cala approssimativamente al <strong>cubo</strong> della velocit&agrave;. Filtrare pi&ugrave; ore a bassa velocit&agrave; consuma molto meno di filtrare poche ore ad alta velocit&agrave;.</p>
    <ul>
      <li><strong>Effetto</strong>: risparmio <strong>60-80 %</strong> sul consumo di filtrazione.</li>
      <li><strong>Investimento</strong>: 600-1.500 &euro; (pompa + installazione).</li>
      <li><strong>Risparmio annuo</strong>: 150-350 &euro;.</li>
      <li><strong>ROI</strong>: 2-6 anni.</li>
    </ul>

    <h2>3. Pompa di calore ad alto COP vs. resistenza elettrica</h2>
    <p>La pompa di calore consegna <strong>4-6 kWh termici per ogni kWh elettrico</strong> consumato (COP 4-6), contro il COP=1 di una resistenza elettrica.</p>
    <ul>
      <li><strong>Effetto</strong>: riduce il consumo elettrico di riscaldamento del <strong>75-80 %</strong>.</li>
      <li><strong>Investimento</strong>: 2.500-6.000 &euro; per una pompa di calore da 10-15 kW.</li>
      <li><strong>Risparmio annuo (vs. resistenza)</strong>: 800-2.000 &euro;.</li>
      <li><strong>ROI</strong>: 2-4 stagioni.</li>
    </ul>
    <p>Allineata al <strong>Regolamento (UE) 813/2013</strong> sui requisiti di progettazione ecocompatibile degli apparecchi di riscaldamento.</p>

    <h2>4. Illuminazione LED sommersa</h2>
    <p>Un faretto LED da 20-30 W sostituisce un alogeno da 200-300 W con la stessa luminosit&agrave; percepita.</p>
    <ul>
      <li><strong>Effetto</strong>: consumo fino a <strong>10&times; inferiore</strong> in illuminazione.</li>
      <li><strong>Investimento</strong>: 200-500 &euro; per faretto (LED sommerso di qualit&agrave;).</li>
      <li><strong>Risparmio annuo</strong>: 30-100 &euro; secondo le ore d'uso.</li>
      <li><strong>ROI</strong>: 3-6 anni. Durata 30.000-50.000 h vs 2.000-5.000 h dell'alogeno.</li>
    </ul>

    <h2>5. Automatismi: controllo pH + ORP + programmazione</h2>
    <p>Una pompa dosatrice di pH e un controller ORP mantengono l'acqua sempre in range senza sovraconsumo di chimici n&eacute; ore extra di filtrazione.</p>
    <ul>
      <li><strong>Effetto</strong>: riduce i chimici del <strong>20-40 %</strong>, evita correzioni manuali, prolunga la vita della vasca.</li>
      <li><strong>Investimento</strong>: 800-2.000 &euro; (pompa pH + sonda ORP + controller).</li>
      <li><strong>Risparmio annuo</strong>: 60-150 &euro; in chimici + tempo del proprietario.</li>
      <li><strong>ROI</strong>: 5-10 anni solo per chimici, migliore se valorizzi il tempo.</li>
    </ul>

    <h2>6. Isolamento della vasca e del circuito idraulico</h2>
    <p>Particolarmente rilevante su piscine riscaldate e in nuova costruzione.</p>
    <ul>
      <li><strong>Effetto</strong>: riduce le perdite per conduzione del <strong>15-25 %</strong>.</li>
      <li><strong>Investimento</strong>: 1.500-4.000 &euro; in nuova costruzione (polistirene estruso perimetrale), difficile in retrofit.</li>
      <li><strong>Risparmio annuo</strong>: 100-300 &euro; su piscine riscaldate.</li>
      <li><strong>ROI</strong>: 5-10 anni.</li>
    </ul>

    <h2>7. Riduzione del tempo di filtrazione</h2>
    <p>Molte piscine <strong>sovrafiltrano</strong>. Regola indicativa:</p>
    <p><strong>Ore giornaliere di filtrazione &asymp; temperatura dell'acqua (&deg;C) / 2</strong></p>
    <ul>
      <li><strong>Effetto</strong>: risparmio diretto proporzionale alle ore eliminate.</li>
      <li><strong>Investimento</strong>: 0 &euro; (solo riconfigurare il programmatore).</li>
      <li><strong>Risparmio annuo</strong>: 30-150 &euro; secondo il punto di partenza.</li>
      <li><strong>ROI</strong>: immediato.</li>
    </ul>
    <p>Puoi calcolare il tempo ottimo per la tua piscina su <Link to="/calculadoras">/calculadoras</Link>.</p>

    <h2>8. Tariffa elettrica: base vs. fasce orarie</h2>
    <p>Filtrare in <strong>fasce a basso costo</strong> (tipicamente 00:00-08:00 nei feriali e weekend interi) pu&ograve; ridurre il costo elettrico della piscina del <strong>30-50 %</strong> senza cambiare nulla dell'equipaggiamento.</p>
    <ul>
      <li><strong>Effetto</strong>: minor prezzo per kWh, senza ridurre il consumo.</li>
      <li><strong>Investimento</strong>: 0 &euro; (cambio di contratto).</li>
      <li><strong>Risparmio annuo</strong>: 80-200 &euro;.</li>
      <li><strong>ROI</strong>: immediato.</li>
    </ul>
    <p>Confronta la tariffa attuale e valuta se un contratto a fasce (2 o 3 periodi) compensa in base al tuo profilo d'uso.</p>

    <h2>9. Pannelli solari fotovoltaici dedicati</h2>
    <p>Ha senso solo se il consumo annuo della piscina supera i <strong>3.000 kWh</strong> (riscaldamento o pompa VSD con uso prolungato).</p>
    <ul>
      <li><strong>Effetto</strong>: autoconsumo diurno che copre 40-70 % del consumo elettrico della piscina.</li>
      <li><strong>Investimento</strong>: 3.500-7.000 &euro; installazione di 2-4 kWp dedicati.</li>
      <li><strong>Risparmio annuo</strong>: 400-800 &euro;.</li>
      <li><strong>ROI</strong>: 6-10 anni secondo radiazione locale e prezzo elettrico.</li>
    </ul>
    <p>Sfrutta il fatto che la piscina consuma soprattutto <strong>in ore diurne</strong> (filtrazione e riscaldamento), il che si sposa bene con la generazione fotovoltaica senza batterie.</p>

    <h2>10. Solare termico per piscina</h2>
    <p>Sistema a basso costo specifico per riscaldare l'acqua con pannelli in polipropilene o EPDM.</p>
    <ul>
      <li><strong>Effetto</strong>: copre il 50-80 % della domanda termica stagionale.</li>
      <li><strong>Investimento</strong>: 1.500-3.500 &euro; per superficie di captazione pari al 50-70 % della superficie della vasca.</li>
      <li><strong>Risparmio annuo</strong>: 300-700 &euro; sul riscaldamento.</li>
      <li><strong>ROI</strong>: 3-5 anni.</li>
    </ul>
    <p>Molto competitivo in climi con buona radiazione e domanda di riscaldamento moderata.</p>

    <h2>11. Pompe di dosaggio automatico dei chimici</h2>
    <p>Completano il punto 5 con dosaggio proporzionale alla portata filtrata.</p>
    <ul>
      <li><strong>Effetto</strong>: consumo di chimici <strong>10-20 % minore</strong>, qualit&agrave; dell'acqua pi&ugrave; stabile.</li>
      <li><strong>Investimento</strong>: 300-800 &euro;.</li>
      <li><strong>Risparmio annuo</strong>: 40-100 &euro;.</li>
      <li><strong>ROI</strong>: 4-8 anni.</li>
    </ul>

    <h2>12. Rilevazione perdite e monitoraggio remoto</h2>
    <p>Una perdita di soli 5 mm/giorno &egrave; visivamente indistinguibile dall'evaporazione ma costa <strong>60 m&sup3;/anno</strong> su una piscina di 32 m&sup2;.</p>
    <ul>
      <li><strong>Effetto</strong>: rilevazione precoce delle perdite, controllo dei livelli.</li>
      <li><strong>Investimento</strong>: 400-1.200 &euro; per un sistema base di monitoraggio.</li>
      <li><strong>Risparmio annuo</strong>: variabile, ma una perdita non rilevata pu&ograve; costare 100-400 &euro;/anno in acqua e chimici.</li>
      <li><strong>ROI</strong>: 3-8 anni in regime normale, immediato se avevi una perdita latente.</li>
    </ul>

    <h2>13. Ristrutturazione di circuito idraulico sovradimensionato</h2>
    <p>Molte piscine vecchie hanno tubazioni di diametro maggiore del necessario e pompe sovradimensionate ereditate.</p>
    <ul>
      <li><strong>Effetto</strong>: riduzione delle perdite di carico, permette una pompa di minor potenza.</li>
      <li><strong>Investimento</strong>: 1.000-3.500 &euro; secondo l'ambito.</li>
      <li><strong>Risparmio annuo</strong>: 80-200 &euro;.</li>
      <li><strong>ROI</strong>: 5-15 anni. Si fa di solito nell'ambito di una ristrutturazione pi&ugrave; ampia.</li>
    </ul>

    <h2>14. Sistema di trattamento dell'acqua efficiente (salino, UV, ozono)</h2>
    <p>Sostituire la clorazione manuale con un <strong>elettrolizzatore salino</strong> automatizzato, o combinare UV/ozono per ridurre il cloro libero, migliora la qualit&agrave; dell'acqua e riduce i chimici.</p>
    <ul>
      <li><strong>Effetto</strong>: consumo di cloro <strong>50-70 % minore</strong>, acqua percepita come pi&ugrave; gradevole.</li>
      <li><strong>Investimento</strong>: 800-2.500 &euro; elettrolizzatore salino, 1.500-3.500 &euro; UV o ozono.</li>
      <li><strong>Risparmio annuo</strong>: 50-150 &euro; in chimici.</li>
      <li><strong>ROI</strong>: 6-12 anni solo per chimici. Si giustifica di pi&ugrave; per comfort e qualit&agrave; dell'acqua.</li>
    </ul>

    <h2>15. Certificato UNI EN 17645: cosa distingue la classe A dalla F</h2>
    <p>Chiudiamo con la misura pi&ugrave; strategica: <strong>misurare</strong> prima, investire dopo.</p>
    <ul>
      <li><strong>Effetto</strong>: identifica i punti con miglior ritorno prima di investire alla cieca. Una piscina classe F consuma <strong>3-5 volte pi&ugrave; risorse</strong> di una equivalente classe A.</li>
      <li><strong>Investimento</strong>: <strong>0 &euro;</strong> per la Scheda Tecnica gratuita su <Link to="/ficha-tecnica">/ficha-tecnica</Link>; sigillo firmato su preventivo su <Link to="/pool-check">/pool-check</Link>.</li>
      <li><strong>Risparmio annuo</strong>: variabile, ma permette di prioritizzare le 14 azioni precedenti nell'ordine corretto per <strong>la tua piscina concreta</strong>.</li>
      <li><strong>ROI</strong>: immediato.</li>
    </ul>

    <h2>Tabella riepilogativa: azione, investimento, risparmio e ROI</h2>
    <div className="overflow-x-auto not-prose my-8">
      <table className="w-full text-sm border-collapse border border-slate-200">
        <thead className="bg-slate-100">
          <tr>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">#</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Azione</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Investimento approx.</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Risparmio annuo</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">ROI</th>
          </tr>
        </thead>
        <tbody className="text-slate-600">
          <tr><td className="border border-slate-200 px-3 py-2">1</td><td className="border border-slate-200 px-3 py-2">Copertura termica motorizzata</td><td className="border border-slate-200 px-3 py-2">4.000-9.000 &euro;</td><td className="border border-slate-200 px-3 py-2">500-1.500 &euro;</td><td className="border border-slate-200 px-3 py-2">3-6 anni</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">2</td><td className="border border-slate-200 px-3 py-2">Pompa VSD</td><td className="border border-slate-200 px-3 py-2">600-1.500 &euro;</td><td className="border border-slate-200 px-3 py-2">150-350 &euro;</td><td className="border border-slate-200 px-3 py-2">2-6 anni</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">3</td><td className="border border-slate-200 px-3 py-2">Pompa di calore COP&gt;5</td><td className="border border-slate-200 px-3 py-2">2.500-6.000 &euro;</td><td className="border border-slate-200 px-3 py-2">800-2.000 &euro;</td><td className="border border-slate-200 px-3 py-2">2-4 stagioni</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">4</td><td className="border border-slate-200 px-3 py-2">Illuminazione LED</td><td className="border border-slate-200 px-3 py-2">200-500 &euro;/faretto</td><td className="border border-slate-200 px-3 py-2">30-100 &euro;</td><td className="border border-slate-200 px-3 py-2">3-6 anni</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">5</td><td className="border border-slate-200 px-3 py-2">Automatismo pH+ORP</td><td className="border border-slate-200 px-3 py-2">800-2.000 &euro;</td><td className="border border-slate-200 px-3 py-2">60-150 &euro;</td><td className="border border-slate-200 px-3 py-2">5-10 anni</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">6</td><td className="border border-slate-200 px-3 py-2">Isolamento vasca</td><td className="border border-slate-200 px-3 py-2">1.500-4.000 &euro;</td><td className="border border-slate-200 px-3 py-2">100-300 &euro;</td><td className="border border-slate-200 px-3 py-2">5-10 anni</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">7</td><td className="border border-slate-200 px-3 py-2">Ore di filtrazione</td><td className="border border-slate-200 px-3 py-2">0 &euro;</td><td className="border border-slate-200 px-3 py-2">30-150 &euro;</td><td className="border border-slate-200 px-3 py-2">Immediato</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">8</td><td className="border border-slate-200 px-3 py-2">Tariffa a fasce</td><td className="border border-slate-200 px-3 py-2">0 &euro;</td><td className="border border-slate-200 px-3 py-2">80-200 &euro;</td><td className="border border-slate-200 px-3 py-2">Immediato</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">9</td><td className="border border-slate-200 px-3 py-2">Solare fotovoltaico</td><td className="border border-slate-200 px-3 py-2">3.500-7.000 &euro;</td><td className="border border-slate-200 px-3 py-2">400-800 &euro;</td><td className="border border-slate-200 px-3 py-2">6-10 anni</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">10</td><td className="border border-slate-200 px-3 py-2">Solare termico</td><td className="border border-slate-200 px-3 py-2">1.500-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">300-700 &euro;</td><td className="border border-slate-200 px-3 py-2">3-5 anni</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">11</td><td className="border border-slate-200 px-3 py-2">Dosaggio chimico</td><td className="border border-slate-200 px-3 py-2">300-800 &euro;</td><td className="border border-slate-200 px-3 py-2">40-100 &euro;</td><td className="border border-slate-200 px-3 py-2">4-8 anni</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">12</td><td className="border border-slate-200 px-3 py-2">Monitoraggio perdite</td><td className="border border-slate-200 px-3 py-2">400-1.200 &euro;</td><td className="border border-slate-200 px-3 py-2">Variabile</td><td className="border border-slate-200 px-3 py-2">3-8 anni</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">13</td><td className="border border-slate-200 px-3 py-2">Ristrutturazione idraulica</td><td className="border border-slate-200 px-3 py-2">1.000-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">80-200 &euro;</td><td className="border border-slate-200 px-3 py-2">5-15 anni</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">14</td><td className="border border-slate-200 px-3 py-2">Salino/UV/Ozono</td><td className="border border-slate-200 px-3 py-2">800-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">50-150 &euro;</td><td className="border border-slate-200 px-3 py-2">6-12 anni</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">15</td><td className="border border-slate-200 px-3 py-2">Certificato UNI EN 17645</td><td className="border border-slate-200 px-3 py-2">0 &euro; Scheda</td><td className="border border-slate-200 px-3 py-2">Abilita le 14 precedenti</td><td className="border border-slate-200 px-3 py-2">Immediato</td></tr>
        </tbody>
      </table>
    </div>

    <hr />

    <p>
      <strong>PoolLex.io</strong> ti aiuta a ordinare questa lista per{" "}
      <strong>la tua piscina concreta</strong>. La Scheda Tecnica gratuita
      valuta gli 8 componenti della UNI EN 17645:2023 e ordina i miglioramenti
      per impatto e ROI. Inizia su{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> e ottieni il tuo{" "}
      <strong>PoolLex ID</strong> univoco con classificazione stimata A-F in
      meno di 2 minuti.
    </p>
  </>
);

// ============================================================================
// NL BODY COMPONENTS (Sprint 119.97 — 6è idioma)
// ============================================================================
// Adaptatie van norm voor NL: NEN-EN 17645:2023 (NEN = Nederlands Normalisatie-instituut).

const Article1BodyNL = () => (
  <>
    <h2>Samenvatting: wat kost een residentieel zwembad echt</h2>
    <p>
      Een residentieel buitenzwembad in de grond onderhouden kost, op
      jaarbasis gemiddeld, <strong>tussen 25 en 80 &euro; per maand</strong> in
      2026. Die bandbreedte hangt af van vier hoofdfactoren:{" "}
      <strong>volume en oppervlakte</strong>, <strong>lokaal klimaat</strong>,{" "}
      <strong>ge&iuml;nstalleerde uitrusting</strong> en{" "}
      <strong>gebruiksgewoonten</strong>.
    </p>
    <p>Om de orde van grootte in te schatten:</p>
    <ul>
      <li><strong>Klein zwembad (25-35 m&sup3;, niet verwarmd, seizoen van 4 maanden)</strong>: 25-40 &euro;/maand op jaarbasis.</li>
      <li><strong>Middelgroot zwembad (40-60 m&sup3;, niet verwarmd, seizoen van 5 maanden)</strong>: 40-60 &euro;/maand.</li>
      <li><strong>Middelgroot zwembad verwarmd met warmtepomp (seizoen van 8-10 maanden)</strong>: 60-100 &euro;/maand.</li>
      <li><strong>Groot zwembad (&gt;60 m&sup3;) verwarmd, zonder thermische afdekking</strong>: 80-140 &euro;/maand of meer.</li>
    </ul>
    <p>
      Het al dan niet aanwezig zijn van een{" "}
      <strong>gemotoriseerde afdekking</strong>, de{" "}
      <strong>variant van de filtratiepomp</strong> en het{" "}
      <strong>waterbehandelingssysteem</strong> kunnen de jaarrekening met meer
      dan 500 &euro; laten schommelen, zonder het gebruik aan te passen.
    </p>
    <p>
      Voordat we in detail treden, is het belangrijk de logica te begrijpen:
      een zwembad is geen huishoudelijk apparaat, het is een{" "}
      <strong>hydraulisch en thermisch systeem</strong> dat water verliest door
      verdamping en warmte door convectie en straling. Elke re&euml;le besparing
      komt neer op het beperken van die twee verliezen.
    </p>

    <h2>De 4 grote uitgavenposten van een zwembad</h2>
    <p>De totale jaarlijkse kosten van een residentieel zwembad zijn typisch als volgt verdeeld:</p>
    <ol>
      <li><strong>Elektriciteit</strong> &mdash; 55 tot 70 % van het totaal. Filtratiepomp, verlichting en warmtepomp indien aanwezig.</li>
      <li><strong>Aanvullingswater</strong> &mdash; 10 tot 20 %. Compenseert verdamping, terugspoelen en spatverlies.</li>
      <li><strong>Chemische producten</strong> &mdash; 10 tot 20 %. Chloor, pH-regelaar, algicide of zout als er een zoutelektrolyse aanwezig is.</li>
      <li><strong>Periodiek onderhoud</strong> &mdash; 5 tot 15 %. Seizoensopstart, kleine reparaties, wisselstukken (dichtingen, inlaten, LED-spots).</li>
    </ol>
    <p>
      De verhouding verschuift sterk als het zwembad{" "}
      <strong>verwarmd</strong> wordt: in dat geval kan elektriciteit meer dan
      75 % van de jaarlijkse uitgaven bedragen, zeker als er{" "}
      <strong>elektrische weerstand</strong> in plaats van een{" "}
      <strong>warmtepomp</strong> wordt gebruikt.
    </p>

    <h2>Gedetailleerd elektriciteitsverbruik</h2>

    <h3>Filtratiepomp</h3>
    <p>
      Dit is het apparaat met de meeste bedrijfsuren per jaar. Een conventionele
      &eacute;&eacute;ntoerenpomp van 0,75 tot 1,5 pk op een zwembad van 50 m&sup3;
      verbruikt tussen <strong>1.500 en 3.000 kWh per jaar</strong> bij 6-8
      uur/dag draaitijd tijdens het zwemseizoen, volgens de technische
      literatuur uit de sector.
    </p>
    <p>
      Aan een gemiddeld tarief van <strong>0,18 &euro;/kWh</strong> komt dat
      neer op <strong>270 tot 540 &euro; per jaar</strong> alleen aan filtratie.
      Een goed geprogrammeerde{" "}
      <strong>toerengeregelde pomp (VSD)</strong> vermindert dat verbruik met{" "}
      <strong>60 tot 80 %</strong>, waardoor de filtratierekening op
      60-180 &euro;/jaar uitkomt.
    </p>

    <h3>Verlichting</h3>
    <p>
      Onderwater LED-spots verbruiken 15-30 W tegenover 100-300 W voor de oude
      halogeenlampen. Bij een zwembad met 2-4 spots en 3 uur dagelijks gebruik
      in het seizoen kost LED-verlichting nauwelijks{" "}
      <strong>10-25 &euro;/jaar</strong>. Een gelijkwaardige halogeenopstelling
      kan oplopen tot 100-250 &euro;/jaar.
    </p>

    <h3>Warmtepomp (indien aanwezig)</h3>
    <p>
      Als uw zwembad verwarmd wordt, concentreert de warmtepomp het grootste
      deel van het elektriciteitsverbruik. Het rendement wordt gemeten met de{" "}
      <strong>COP</strong> (Coefficient of Performance), die voor zwembadgebruik
      tussen <strong>4 en 6</strong> ligt onder gunstige omstandigheden
      (lucht &gt;15 &deg;C en water tussen 24 en 28 &deg;C). Vertaald: voor
      elke kWh elektriciteit levert u 4-6 kWh warmte aan het water.
    </p>
    <p>
      Een zwembad van 50 m&sup3; verwarmd van mei tot september met een
      thermische afdekking verbruikt ongeveer{" "}
      <strong>1.500-3.500 kWh/jaar</strong> aan verwarming, afhankelijk van
      het beoogde temperatuurverschil en het klimaat. Zonder thermische
      afdekking kan dat getal verdubbelen of verdrievoudigen.
    </p>

    <h2>Waterverbruik</h2>
    <p>Water verlaat het zwembad langs vier wegen:</p>
    <ul>
      <li><strong>Verdamping</strong>: 5-8 mm/dag in warme zones in de zomer, 2-4 mm/dag in de lente en herfst. Een zwembad van 32 m&sup2; verliest 160-256 liter/dag door zuivere verdamping op het hoogtepunt van de zomer.</li>
      <li><strong>Terugspoelen van het filter</strong>: 300-800 liter per cyclus, doorgaans 1-2 keer per maand afhankelijk van de vervuiling.</li>
      <li><strong>Spatten en meesleuren</strong>: 5-15 liter per zwemmer per beurt.</li>
      <li><strong>Aftappen of vernieuwen</strong>: de EN 15288-2 reeks verplicht geen jaarlijkse lediging als de waterkwaliteit behouden blijft, maar veel eigenaars vernieuwen elk 2-3 jaar 10-30 % van het volume.</li>
    </ul>
    <p>
      Bij een gemiddelde waterprijs van <strong>1,5 tot 3 &euro;/m&sup3;</strong>{" "}
      (varieert sterk per gemeente) valt de jaarlijkse waterrekening van een
      residentieel zwembad in een warme zone tussen{" "}
      <strong>30 en 90 &euro; per jaar</strong>.
    </p>

    <h2>Verbruik van chemische producten</h2>
    <p>Het budget voor chemicali&euml;n hangt af van het behandelingssysteem:</p>
    <ul>
      <li><strong>Handmatige chlorering met hypochloriet</strong> of vaste chloor: 50-120 &euro;/jaar.</li>
      <li><strong>Zoutelektrolyse</strong>: minder uitgaven aan chloor (zout wordt occasioneel bijgevuld), 30-70 &euro;/jaar. Zout kost ongeveer 0,15-0,30 &euro;/kg en een typische jaarlijkse aanvulling bedraagt 20-50 kg.</li>
      <li><strong>UV- of ozonsystemen</strong>: verlagen het chloorverbruik met 30-70 %, maar voegen kosten toe voor het vervangen van lampen elke 8.000-12.000 uur.</li>
    </ul>
    <p>
      Daar komt <strong>pH-regelaar</strong> (10-25 &euro;/jaar), sporadisch{" "}
      <strong>algicide</strong> (10-20 &euro;/jaar) en occasioneel{" "}
      <strong>vlokmiddel</strong> bovenop.
    </p>

    <h2>Hoe bereken je de echte kosten van UW zwembad</h2>
    <p>
      Een theoretische schatting helpt, maar elke installatie is anders. U
      heeft vier gegevens nodig:
    </p>
    <ol>
      <li><strong>Volume</strong> van het zwembad in m&sup3; (lengte &times; breedte &times; gemiddelde diepte).</li>
      <li><strong>Re&euml;el vermogen</strong> van de filtratiepomp in kW (niet het nominale pk-vermogen &mdash; vraag de technische fiche).</li>
      <li><strong>Dagelijkse filtratie-uren</strong> per seizoen.</li>
      <li><strong>Gemiddeld elektriciteitstarief</strong> dat u betaalt (&euro;/kWh, inclusief nettarieven en belastingen).</li>
    </ol>
    <p>
      Met die gegevens kunt u de gratis tools op{" "}
      <Link to="/calculadoras">/calculadoras</Link> gebruiken om uw jaarverbruik
      te schatten en te vergelijken met het gemiddelde. En als u een{" "}
      <strong>geschatte milieuklasse volgens NEN-EN 17645</strong> voor uw
      zwembad wilt, vraag dan uw gratis{" "}
      <strong>Technische Fiche</strong> aan op{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> &mdash; u ontvangt uw
      unieke <strong>PoolLex ID</strong> (formaat PLX-XXXXXXX) en een schatting
      van klasse A tot F.
    </p>

    <h2>Hoe het verbruik verminderen: 10 concrete acties</h2>
    <ol>
      <li><strong>Installeer een thermische afdekking, manueel of gemotoriseerd</strong>. Vermindert verdamping 60-70 % en warmteverlies 40-60 %. De actie met de beste ROI op verwarmde zwembaden.</li>
      <li><strong>Vervang de &eacute;&eacute;ntoerenpomp door een VSD</strong>. Typische besparing: 60-80 % op filtratie.</li>
      <li><strong>Stem de filtratie-uren af</strong> op het re&euml;le volume en de watertemperatuur. Vuistregel: dagelijkse filtratie-uren &asymp; watertemperatuur (&deg;C) / 2.</li>
      <li><strong>Vervang halogeenspots door onderwater LED</strong>. Verbruik tot 10&times; lager.</li>
      <li><strong>Als u verwarmt, gebruik een warmtepomp met COP &gt;5</strong>, nooit elektrische weerstand.</li>
      <li><strong>Programmeer de filtratie tijdens daluren</strong> van uw tarief (typisch 00:00-08:00 en weekends).</li>
      <li><strong>Regel de pH automatisch</strong> met een doseerpomp: vermijdt overmatig chloorverbruik en neerslag.</li>
      <li><strong>Controleer jaarlijks op lekken</strong>. Een klein lek van 5 mm/dag is niet te onderscheiden van verdamping, maar kost 60 m&sup3;/jaar op een zwembad van 32 m&sup2;.</li>
      <li><strong>Reinig het filter correct</strong> &mdash; een verstopt filter dwingt de pomp om langer te draaien en met hoger specifiek verbruik.</li>
      <li><strong>Isoleer het bad thermisch</strong> bij nieuwbouw of renovatie. Vermindert geleidingsverliezen met 15-25 %.</li>
    </ol>

    <h2>Milieucertificaat EN 17645: wat het betekent voor uw verbruik</h2>
    <p>
      Sinds 2023 bestaat de norm <strong>NEN-EN 17645:2023</strong>
      (Nederlandse overname van de Europese EN 17645), die een{" "}
      <strong>objectieve methodologie vastlegt om de milieuduurzaamheid van
      residenti&euml;le buitenzwembaden in de grond te klasseren</strong>. De
      norm evalueert 8 componenten (bad, hydraulisch circuit, afdekking,
      filtratiepomp, waterbehandelingssysteem, verwarming, verlichting,
      besturing) en kent een <strong>klasse A tot F</strong> toe, analoog aan
      het energielabel van huishoudtoestellen.
    </p>
    <p>
      Een zwembad van <strong>klasse A</strong> verbruikt, in vergelijkbare
      termen, <strong>3 tot 5 keer minder</strong> jaarlijkse hulpbronnen dan
      een equivalent van <strong>klasse F</strong>. Het verschil zit niet in
      het gebruik, maar in de uitrusting en de manier waarop de 8 componenten
      worden gecombineerd.
    </p>
    <p>
      Uw klasse kennen is de eerste stap om te weten{" "}
      <strong>waar u staat</strong> en{" "}
      <strong>waar u eerst moet investeren</strong>. Op{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> kunt u gratis een
      schatting krijgen met uw <strong>PoolLex ID</strong>; voor het door een
      ingenieur ondertekende zegel raadpleegt u niveau 2 op{" "}
      <Link to="/pool-check">/pool-check</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> past NEN-EN 17645:2023 toe met een
      berekeningsmotor die de 8 componenten evalueert en een schatting van de
      milieuklasse geeft in minder dan 2 minuten. Het vervangt geen
      ondertekende certificering, maar geeft u een eerlijke diagnose van waar
      u kunt beginnen. Begin gratis op{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

const Article2BodyNL = () => (
  <>
    <h2>Wat is NEN-EN 17645:2023</h2>
    <p>
      De <strong>NEN-EN 17645:2023</strong> is de Nederlandse overname van de
      Europese norm <strong>EN 17645:2023</strong>, gepubliceerd door het{" "}
      <strong>Europees Comit&eacute; voor Normalisatie (CEN)</strong> en, in
      zijn offici&euml;le versie, getiteld{" "}
      <em>&laquo;Zwembaden &mdash; Eisen voor de beoordeling van de
      milieuduurzaamheid van residenti&euml;le buitenzwembaden in de
      grond&raquo;</em>.
    </p>
    <p>
      Het gaat om de <strong>eerste geharmoniseerde Europese norm</strong> die
      een objectieve en reproduceerbare methodologie vastlegt om de
      milieuprestaties van een residentieel zwembad te klasseren. V&oacute;&oacute;r
      2023 bestonden er particuliere certificeringen, fabrikantenlabels en
      ad-hoc audits, maar er was geen gemeenschappelijke taal noch een
      vergelijkbare schaal tussen installaties.
    </p>
    <p>De norm past binnen het Europese regelgevende ecosysteem, afgestemd op:</p>
    <ul>
      <li><strong>Verordening (EU) 305/2011</strong> voor bouwproducten (CE-markering van componenten).</li>
      <li><strong>Verordening (EU) 813/2013</strong> over ecologische ontwerpvereisten voor ruimteverwarmers (van toepassing op warmtepompen).</li>
      <li><strong>Verordening (EU) 547/2012</strong> over ecologische ontwerpvereisten voor waterpompen.</li>
    </ul>
    <p>
      Het is een <strong>vrijwillige</strong> norm: geen enkele nationale
      regeling verplicht vandaag de dag de certificering van uw zwembad. Maar
      de adoptie groeit op verschillende fronten:{" "}
      <strong>regionale subsidies</strong>,{" "}
      <strong>gemeentelijke verordeningen inzake waterefficiëntie</strong>,{" "}
      <strong>vastgoedtransacties</strong>,{" "}
      <strong>bedrijfsaudits</strong> en{" "}
      <strong>programma's voor erkende installateurs</strong>.
    </p>

    <h2>De 8 componenten die de norm evalueert</h2>
    <p>
      NEN-EN 17645:2023 evalueert het zwembad als{" "}
      <strong>ge&iuml;ntegreerd systeem</strong>, niet als som van onderdelen.
      De methodologie scoort 8 componenten met verschillende gewichten volgens
      hun geaggregeerde milieu-impact:
    </p>
    <ol>
      <li><strong>Structuur van het bad</strong>: materiaal, isolatie, verwachte levensduur, thermische transmissieco&euml;ffici&euml;nten.</li>
      <li><strong>Hydraulisch circuit</strong>: dimensionering, drukverliezen, isolatie van leidingen, opstelling van skimmers en retourinlaten.</li>
      <li><strong>Gemotoriseerde of manuele afdekking</strong>: aanwezigheid, type (isothermisch, deck, drijvende folie), dampdichtheid.</li>
      <li><strong>Filtratiepomp</strong>: technologie (&eacute;&eacute;ntoeren, tweetoeren, VSD), hydraulisch rendement, bijhorende sturing.</li>
      <li><strong>Waterbehandelingssysteem</strong>: zoutelektrolyse, handmatige dosering, UV, ozon of hybride combinaties.</li>
      <li><strong>Verwarmingssysteem</strong>: warmtepomp, warmtewisselaar met bestaande ketel, elektrische weerstand, thermische zonnepanelen.</li>
      <li><strong>Verlichting</strong>: type spot (LED, halogeen, fluorescent), aantal, totaal ge&iuml;nstalleerd vermogen en sturing.</li>
      <li><strong>Besturings- en automatiseringssysteem</strong>: automatische dosering, pH/ORP-meting, tijdsprogrammering, remote monitoring.</li>
    </ol>
    <p>
      Elke component draagt punten bij aan een{" "}
      <strong>globale index</strong>, en de index vertaalt zich in een{" "}
      <strong>klasse A tot F</strong> met drempels gepubliceerd in de norm.
    </p>

    <h2>Schaal A-F: wat elke klasse betekent</h2>
    <p>De schaal werkt analoog aan het energielabel voor huishoudapparaten:</p>
    <ul>
      <li><strong>Klasse A</strong>: referentiezwembad qua milieuprestaties. Gemotoriseerde afdekking, VSD-pomp, warmtepomp met hoge COP, zoutbehandeling met automatisering, LED-verlichting, isolatie van het bad. Vertegenwoordigt de state of the art op de markt.</li>
      <li><strong>Klasse B</strong>: zeer effici&euml;nt, met de meeste componenten geoptimaliseerd. Meestal ontbreken 1 of 2 elementen om A te bereiken.</li>
      <li><strong>Klasse C</strong>: aanzienlijke effici&euml;ntie. Modern zwembad met actuele uitrusting maar zonder alle optimalisaties (bijvoorbeeld zonder gemotoriseerde afdekking of zonder VSD).</li>
      <li><strong>Klasse D</strong>: standaard effici&euml;ntie. Correct zwembad met conventionele uitrusting. Meest voorkomende klasse bij nieuwbouw zonder specifieke milieuambitie.</li>
      <li><strong>Klasse E</strong>: verbetervatbare effici&euml;ntie. Oude of onderbemeten uitrusting, geen afdekking, manuele bediening.</li>
      <li><strong>Klasse F</strong>: energetisch inefficient zwembad. &Eacute;&eacute;ntoerenpomp, halogeenverlichting, geen afdekking, geen automatisering. Meestal park van meer dan 20 jaar zonder renovatie.</li>
    </ul>
    <p>
      De <strong>voorlopige gegevens</strong> uit de sector suggereren dat het
      huidige residenti&euml;le zwembadpark zich concentreert in{" "}
      <strong>klassen D en E</strong>, met minder dan 5-10 % van het park dat
      op natuurlijke wijze klasse A bereikt. Het goede nieuws:{" "}
      <strong>een gedeeltelijke renovatie</strong> (afdekking + VSD-pomp + LED)
      brengt een zwembad meestal van E naar C in &eacute;&eacute;n enkele
      interventie.
    </p>

    <h2>Gratis schatting vs. ondertekend certificaat</h2>
    <p>Het loont om twee producten met verschillende doelen te onderscheiden:</p>

    <h3>Gratis Technische Fiche (niveau 1)</h3>
    <p>
      Het gaat om een <strong>schatting</strong> op basis van de gegevens die de
      eigenaar over zijn zwembad opgeeft: volume, oppervlakte, ge&iuml;nstalleerde
      uitrusting, gewoonten. De motor van PoolLex.io evalueert de 8 componenten
      met de beschikbare gegevens en retourneert een{" "}
      <strong>geschatte klasse A-F</strong>, samen met een{" "}
      <strong>unieke PoolLex ID</strong> (formaat PLX-XXXXXXX) die het zwembad
      op het platform identificeert.
    </p>
    <p><strong>Dient voor</strong>: snelle diagnose, vergelijking met andere zwembaden, renovatie plannen, begrijpen waar u moet beginnen.</p>
    <p><strong>Dient niet voor</strong>: voorleggen aan overheden, notarissen of verzekeraars. Het is indicatief, niet gecertificeerd.</p>

    <h3>Zegel ondertekend door gediplomeerd ingenieur (niveau 2)</h3>
    <p>
      Een <strong>technisch document</strong> nagekeken en ondertekend door
      een <strong>gediplomeerd ingenieur (ir.)</strong> die de
      aangeleverde documentatie heeft geverifieerd (technische fiches van
      apparatuur, installatiecertificaten, foto's, metingen). Het heeft
      juridische en technische waarde.
    </p>
    <p><strong>Dient voor</strong>: formele audit, aan- en verkoopoperaties, subsidies, gemeentelijke vergunningen die het vereisen, vastgoedcertificeringen, deskundigenrapporten.</p>

    <h2>Hoe krijgt u de schatting in 2 minuten</h2>
    <p>Het proces is ontworpen om snel en gratis te zijn:</p>
    <ol>
      <li>Ga naar <Link to="/ficha-tecnica">/ficha-tecnica</Link>.</li>
      <li>Voer de basisgegevens van uw zwembad in: <strong>volume</strong> (m&sup3;), <strong>oppervlakte</strong> (m&sup2;), <strong>bouwjaar</strong>, <strong>locatie</strong> (voor de klimaatberekening).</li>
      <li>Geef de ge&iuml;nstalleerde <strong>uitrusting</strong> aan: type pomp, aanwezigheid van afdekking, waterbehandelingssysteem, verwarming, verlichting.</li>
      <li>Verstuur het formulier. De motor berekent uw <strong>geschatte klasse A-F</strong> en kent u een unieke <strong>PoolLex ID</strong> toe.</li>
      <li>U ontvangt uw <strong>Technische Fiche als PDF</strong> met de klassering, een uitsplitsing per component en <strong>geprioriteerde verbeteraanbevelingen</strong>.</li>
    </ol>
    <p>Het hele proces duurt <strong>minder dan 2 minuten</strong> als u de gegevens bij de hand heeft.</p>

    <h2>Wanneer het ondertekende zegel aanvragen</h2>
    <p>Het ondertekende zegel is nuttig, en soms noodzakelijk, in verschillende scenario's:</p>
    <ul>
      <li><strong>Vastgoedtransacties</strong>: het zwembad als effici&euml;nt actief waarderen kan 2 tot 5 % toevoegen aan de totale eigendomswaarde in transacties waar de koper duurzaamheid prioritair vindt.</li>
      <li><strong>Bedrijfsaudits</strong>: bedrijven met ESG-rapportering die sport- of vrijetijdsvoorzieningen omvatten.</li>
      <li><strong>Regionale subsidies</strong> voor energetische renovatie die zwembaden beginnen te overwegen.</li>
      <li><strong>Gemeentelijke verordeningen</strong> die de klassering opnemen als criterium voor vergunning.</li>
      <li><strong>Technische geschillen</strong> of schadegevallen waar de toestand van de installatie op een bepaalde datum moet worden aangetoond.</li>
      <li><strong>Programma's voor erkende installateurs</strong> om het resultaat van een renovatie te certificeren.</li>
    </ul>
    <p>Als uw geval binnen een van deze scenario's past, raadpleeg dan niveau 2 op <Link to="/pool-check">/pool-check</Link>.</p>

    <h2>Voordelen van het certificeren van uw zwembad</h2>
    <p>De re&euml;le voordelen, voorbij de marketing, groeperen zich in drie blokken:</p>
    <p><strong>1. Operationele besparing</strong>. Certificeren dwingt een audit van het systeem af. In de meeste gevallen worden inefficienties vastgesteld (overgedimensioneerde filtratie-uren, lekken, slechte pH-regeling, overgedimensioneerde pomp) die, na correctie, besparingen van <strong>150-400 &euro;/jaar</strong> opleveren bij een gemiddeld residentieel zwembad.</p>
    <p><strong>2. Vastgoedwaarde</strong>. Een activum met een <strong>gecertificeerde klasse A of B</strong> begint een commercieel argument te worden in markten waar het vastgoed duurzaamheid waardeert. Digitale certificering met PoolLex ID maakt het activum verifieerbaar aantoonbaar.</p>
    <p><strong>3. Toegang tot effici&euml;ntieprogramma's</strong>. Naarmate overheden en verzekeraars de norm overnemen, opent het bezit van het certificaat de deur naar <strong>subsidies</strong>, <strong>premies</strong> of <strong>kortingen</strong> die niet-gecertificeerde zwembaden niet ontvangen.</p>

    <h2>De rol van PoolLex.io: toepassers van de norm, geen auteurs</h2>
    <p>
      Een punt dat we duidelijk willen maken:{" "}
      <strong>PoolLex.io is geen auteur van de norm NEN-EN 17645</strong>. De
      norm wordt gepubliceerd door het{" "}
      <strong>Europees Comit&eacute; voor Normalisatie (CEN)</strong> en zijn
      relevant technisch comit&eacute;.{" "}
      <strong>PoolLex.io is een digitale toepasser van de norm</strong>: wij
      bouwen de tools zodat eigenaars, technici en overheden ze effici&euml;nt
      kunnen toepassen.
    </p>
    <p>Dit onderscheid is om drie redenen belangrijk:</p>
    <ul>
      <li><strong>Neutraliteit</strong>: wij verdedigen geen alternatieve versies van de norm. Wij passen de offici&euml;le geldende versie toe.</li>
      <li><strong>Actualisering</strong>: als de norm verandert (herziening voorzien elke 5-7 jaar), passen onze berekeningen zich aan.</li>
      <li><strong>Complementariteit</strong>: ons werk concurreert niet met laboratoria, certificeerders of traditionele ingenieursbureaus. Het vult aan: wij leveren de digitale diagnose, zij ondertekenen wat een ondertekening door een gediplomeerd ingenieur vereist.</li>
    </ul>
    <p>
      <strong>PoolLex.io</strong> past normkennis en ingenieurskennis toe in
      een open digitale laag, met de gratis Technische Fiche als toegangspoort.
      Start uw klassering op <Link to="/ficha-tecnica">/ficha-tecnica</Link> en,
      als u dieper in het project wilt duiken, bezoek dan{" "}
      <Link to="/initiative">/initiative</Link> voor de volledige filosofie
      achter het platform.
    </p>
  </>
);

const Article3BodyNL = () => (
  <>
    <h2>Inleiding: het zwembad, de onzichtbare grootverbruiker van het huis</h2>
    <p>
      Een residentieel buitenzwembad verbruikt gemiddeld tussen{" "}
      <strong>15 en 25 % van het totale jaarlijkse elektriciteitsverbruik</strong>{" "}
      van een eengezinswoning met zwembad. Als het{" "}
      <strong>verwarmd wordt met een warmtepomp</strong> tijdens een verlengd
      seizoen, kan dat getal oplopen tot <strong>35 %</strong> of meer. Qua
      water vertegenwoordigt het zwembad gewoonlijk{" "}
      <strong>8-15 % van het jaarlijkse huishoudelijk verbruik</strong>.
    </p>
    <p>
      Deze cijfers zijn hoog, maar ook{" "}
      <strong>onderhandelbaar</strong>: het grootste deel van het
      residenti&euml;le park in Europa werkt met apparatuur en configuraties
      die ver af zitten van het huidige technische optimum. De norm{" "}
      <strong>NEN-EN 17645:2023</strong> heeft eindelijk een gemeenschappelijke
      taal geleverd om dit verschil te meten, en de 15 acties hieronder rangschikken
      wat vandaag het beste rendement geeft.
    </p>
    <p>
      De economische bandbreedtes zijn conservatief en indicatief. Alle
      berekeningen gaan uit van een gemiddelde elektriciteitsprijs van{" "}
      <strong>0,18 &euro;/kWh</strong> en een gemiddelde waterprijs van{" "}
      <strong>2 &euro;/m&sup3;</strong>, gerelateerd aan een referentiezwembad
      van <strong>50 m&sup3; en 32 m&sup2; oppervlakte</strong> in een mild
      mediterraan klimaat.
    </p>

    <h2>1. Thermische afdekking (manueel of gemotoriseerd)</h2>
    <p>De maatregel met de beste ROI op vrijwel elk zwembad.</p>
    <ul>
      <li><strong>Effect</strong>: vermindert verdamping <strong>60-70 %</strong> en nachtelijke warmteverliezen <strong>40-60 %</strong>. Vermindert ook noodzakelijke filtratie-uren en chemicali&euml;nverbruik.</li>
      <li><strong>Investering</strong>: 400-1.200 &euro; manueel (drijvende folie of winterhoes), 4.000-9.000 &euro; gemotoriseerd met behuizing.</li>
      <li><strong>Jaarlijkse besparing</strong>: 200-500 &euro; op onverwarmde zwembaden, 500-1.500 &euro; op verwarmde.</li>
      <li><strong>ROI</strong>: 1-3 seizoenen op verwarmde, 2-4 jaar op onverwarmde.</li>
    </ul>

    <h2>2. Filtratiepomp met VSD (variabele snelheid)</h2>
    <p>Benut de <strong>hydraulische affiniteitswetten</strong>: bij het verlagen van de snelheid daalt het verbruik ongeveer met de <strong>derde macht</strong> van de snelheid. Meer uren filteren aan lagere snelheid verbruikt veel minder dan weinig uren filteren aan hoge snelheid.</p>
    <ul>
      <li><strong>Effect</strong>: besparing <strong>60-80 %</strong> op filtratieverbruik.</li>
      <li><strong>Investering</strong>: 600-1.500 &euro; (pomp + installatie).</li>
      <li><strong>Jaarlijkse besparing</strong>: 150-350 &euro;.</li>
      <li><strong>ROI</strong>: 2-6 jaar.</li>
    </ul>

    <h2>3. Warmtepomp met hoge COP versus elektrische weerstand</h2>
    <p>De warmtepomp levert <strong>4-6 kWh warmte per verbruikte kWh elektriciteit</strong> (COP 4-6), tegenover de COP=1 van een elektrische weerstand.</p>
    <ul>
      <li><strong>Effect</strong>: vermindert het elektriciteitsverbruik voor verwarming met <strong>75-80 %</strong>.</li>
      <li><strong>Investering</strong>: 2.500-6.000 &euro; voor een zwembadwarmtepomp van 10-15 kW.</li>
      <li><strong>Jaarlijkse besparing (t.o.v. weerstand)</strong>: 800-2.000 &euro;.</li>
      <li><strong>ROI</strong>: 2-4 seizoenen.</li>
    </ul>
    <p>Afgestemd op <strong>Verordening (EU) 813/2013</strong> over ecologische ontwerpvereisten voor ruimteverwarmers.</p>

    <h2>4. Onderwater LED-verlichting</h2>
    <p>Een LED-spot van 20-30 W vervangt een halogeen van 200-300 W met dezelfde waargenomen lichtsterkte.</p>
    <ul>
      <li><strong>Effect</strong>: verbruik tot <strong>10&times; lager</strong> in verlichting.</li>
      <li><strong>Investering</strong>: 200-500 &euro; per spot (kwalitatieve onderwater LED).</li>
      <li><strong>Jaarlijkse besparing</strong>: 30-100 &euro; naargelang de gebruiksduur.</li>
      <li><strong>ROI</strong>: 3-6 jaar. Bovendien gaan ze 30.000-50.000 uur mee vs. 2.000-5.000 uur bij halogeen.</li>
    </ul>

    <h2>5. Automatisering: pH + ORP-regeling + programmering</h2>
    <p>Een pH-doseerpomp en een ORP-regelaar houden het water altijd in het juiste bereik zonder overmatig chemisch verbruik of extra filtratie-uren.</p>
    <ul>
      <li><strong>Effect</strong>: vermindert chemicali&euml;n met <strong>20-40 %</strong>, vermijdt handmatige correctie, verlengt de levensduur van het bad.</li>
      <li><strong>Investering</strong>: 800-2.000 &euro; (pH-pomp + ORP-sonde + regelaar).</li>
      <li><strong>Jaarlijkse besparing</strong>: 60-150 &euro; in chemicali&euml;n + tijd van de eigenaar.</li>
      <li><strong>ROI</strong>: 5-10 jaar enkel voor chemicali&euml;n, beter als u tijd waardeert.</li>
    </ul>

    <h2>6. Isolatie van het bad en het hydraulisch circuit</h2>
    <p>Bijzonder relevant voor verwarmde zwembaden en bij nieuwbouw.</p>
    <ul>
      <li><strong>Effect</strong>: vermindert geleidingsverliezen <strong>15-25 %</strong>.</li>
      <li><strong>Investering</strong>: 1.500-4.000 &euro; bij nieuwbouw (perimetrische geextrudeerd polystyreen), moeilijk te retrofitten.</li>
      <li><strong>Jaarlijkse besparing</strong>: 100-300 &euro; in verwarmde zwembaden.</li>
      <li><strong>ROI</strong>: 5-10 jaar.</li>
    </ul>

    <h2>7. Vermindering van de filtratietijd</h2>
    <p>Veel zwembaden <strong>overfilteren</strong>. De vuistregel is:</p>
    <p><strong>Dagelijkse filtratie-uren &asymp; watertemperatuur (&deg;C) / 2</strong></p>
    <ul>
      <li><strong>Effect</strong>: directe besparing evenredig met de weggenomen uren.</li>
      <li><strong>Investering</strong>: 0 &euro; (enkel de tijdschakelaar herprogrammeren).</li>
      <li><strong>Jaarlijkse besparing</strong>: 30-150 &euro; naargelang het startpunt.</li>
      <li><strong>ROI</strong>: onmiddellijk.</li>
    </ul>
    <p>U kunt de optimale tijd voor uw zwembad berekenen op <Link to="/calculadoras">/calculadoras</Link>.</p>

    <h2>8. Elektriciteitstarief: standaard vs. tarief met daluren</h2>
    <p>Filteren tijdens <strong>daluren</strong> (typisch 00:00-08:00 op weekdagen en volledige weekends) kan de elektriciteitskost van het zwembad met <strong>30-50 %</strong> verminderen zonder iets aan de uitrusting te wijzigen.</p>
    <ul>
      <li><strong>Effect</strong>: lagere prijs per verbruikte kWh, zonder verbruik te verminderen.</li>
      <li><strong>Investering</strong>: 0 &euro; (contractwijziging).</li>
      <li><strong>Jaarlijkse besparing</strong>: 80-200 &euro;.</li>
      <li><strong>ROI</strong>: onmiddellijk.</li>
    </ul>
    <p>Vergelijk uw huidig tarief en beoordeel of een contract met tijdsdifferentiatie (2 of 3 periodes) rendabel is voor uw gebruiksprofiel.</p>

    <h2>9. Toegewijde fotovolta&iuml;sche zonnepanelen</h2>
    <p>Heeft alleen zin als het jaarverbruik van het zwembad meer dan <strong>3.000 kWh</strong> bedraagt (verwarming of VSD-pomp met langdurig gebruik).</p>
    <ul>
      <li><strong>Effect</strong>: zelfconsumptie overdag die 40-70 % van het elektriciteitsverbruik van het zwembad dekt.</li>
      <li><strong>Investering</strong>: 3.500-7.000 &euro; installatie van 2-4 kWp toegewijd.</li>
      <li><strong>Jaarlijkse besparing</strong>: 400-800 &euro;.</li>
      <li><strong>ROI</strong>: 6-10 jaar naargelang lokale straling en elektriciteitsprijs.</li>
    </ul>
    <p>Profiteer van het feit dat een zwembad hoofdzakelijk <strong>tijdens de daguren</strong> verbruikt (filtratie en verwarming), wat uitstekend samenvalt met fotovolta&iuml;sche opwekking zonder batterijen nodig te hebben.</p>

    <h2>10. Thermische zonne-energie voor zwembad</h2>
    <p>Systeem met lage specifieke kosten om zwembadwater te verwarmen met panelen van polypropyleen of EPDM.</p>
    <ul>
      <li><strong>Effect</strong>: dekt 50-80 % van de thermische vraag in het seizoen.</li>
      <li><strong>Investering</strong>: 1.500-3.500 &euro; voor een opvangoppervlak equivalent aan 50-70 % van de badoppervlakte.</li>
      <li><strong>Jaarlijkse besparing</strong>: 300-700 &euro; in verwarming.</li>
      <li><strong>ROI</strong>: 3-5 jaar.</li>
    </ul>
    <p>Zeer competitief in klimaten met goede straling en matige verwarmingsvraag.</p>

    <h2>11. Automatische doseerpompen voor chemicali&euml;n</h2>
    <p>Vullen punt 5 aan met dosering evenredig aan het gefilterde debiet.</p>
    <ul>
      <li><strong>Effect</strong>: chemicali&euml;nverbruik <strong>10-20 % lager</strong>, stabielere waterkwaliteit.</li>
      <li><strong>Investering</strong>: 300-800 &euro;.</li>
      <li><strong>Jaarlijkse besparing</strong>: 40-100 &euro;.</li>
      <li><strong>ROI</strong>: 4-8 jaar.</li>
    </ul>

    <h2>12. Lekdetectie en remote monitoring</h2>
    <p>Een lek van slechts 5 mm/dag is visueel niet te onderscheiden van verdamping, maar kost <strong>60 m&sup3;/jaar</strong> op een zwembad van 32 m&sup2;.</p>
    <ul>
      <li><strong>Effect</strong>: vroege detectie van lekken, niveaubeheersing.</li>
      <li><strong>Investering</strong>: 400-1.200 &euro; voor een basissysteem voor monitoring.</li>
      <li><strong>Jaarlijkse besparing</strong>: variabel, maar een onopgemerkt lek kan 100-400 &euro;/jaar kosten in water en chemicali&euml;n.</li>
      <li><strong>ROI</strong>: 3-8 jaar in normale omstandigheden, onmiddellijk als u een latent lek had.</li>
    </ul>

    <h2>13. Renovatie van overgedimensioneerd hydraulisch circuit</h2>
    <p>Veel oudere zwembaden hebben leidingen met een grotere diameter dan nodig en overgedimensioneerde geerfde pompen.</p>
    <ul>
      <li><strong>Effect</strong>: vermindering van drukverliezen, maakt het gebruik van een pomp met lager vermogen mogelijk.</li>
      <li><strong>Investering</strong>: 1.000-3.500 &euro; naargelang de omvang.</li>
      <li><strong>Jaarlijkse besparing</strong>: 80-200 &euro;.</li>
      <li><strong>ROI</strong>: 5-15 jaar. Wordt meestal uitgevoerd in het kader van een bredere renovatie.</li>
    </ul>

    <h2>14. Effici&euml;nt waterbehandelingssysteem (zout, UV, ozon)</h2>
    <p>Het vervangen van conventionele handmatige chlorering door een <strong>zoutelektrolyse</strong> met automatisering, of het combineren van UV/ozon om vrije chloor te verminderen, verbetert de waterkwaliteit en vermindert chemicali&euml;n.</p>
    <ul>
      <li><strong>Effect</strong>: chloorverbruik <strong>50-70 % lager</strong>, water wordt als aangenamer ervaren.</li>
      <li><strong>Investering</strong>: 800-2.500 &euro; zoutelektrolyse, 1.500-3.500 &euro; UV of ozon.</li>
      <li><strong>Jaarlijkse besparing</strong>: 50-150 &euro; in chemicali&euml;n.</li>
      <li><strong>ROI</strong>: 6-12 jaar enkel op chemicali&euml;n. Verantwoord meer door comfort en waterkwaliteit.</li>
    </ul>

    <h2>15. Certificaat EN 17645: wat het verschil maakt tussen klasse A en klasse F</h2>
    <p>We sluiten af met de meest strategische maatregel: eerst <strong>meten</strong>, daarna investeren.</p>
    <ul>
      <li><strong>Effect</strong>: identificeert de punten met het beste rendement voordat u blind investeert. Een zwembad van klasse F verbruikt <strong>3-5 keer meer hulpbronnen</strong> dan een equivalent van klasse A.</li>
      <li><strong>Investering</strong>: <strong>0 &euro;</strong> voor de gratis Technische Fiche op <Link to="/ficha-tecnica">/ficha-tecnica</Link>; ondertekend zegel op aanvraag op <Link to="/pool-check">/pool-check</Link>.</li>
      <li><strong>Jaarlijkse besparing</strong>: variabel, maar maakt het mogelijk de 14 voorgaande acties in de juiste volgorde te prioriteren voor <strong>uw specifieke zwembad</strong>.</li>
      <li><strong>ROI</strong>: onmiddellijk.</li>
    </ul>

    <h2>Overzichtstabel: actie, investering, besparing en ROI</h2>
    <div className="overflow-x-auto not-prose my-8">
      <table className="w-full text-sm border-collapse border border-slate-200">
        <thead className="bg-slate-100">
          <tr>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">#</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Actie</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Investering (ca.)</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Jaarlijkse besparing</th>
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">ROI</th>
          </tr>
        </thead>
        <tbody className="text-slate-600">
          <tr><td className="border border-slate-200 px-3 py-2">1</td><td className="border border-slate-200 px-3 py-2">Gemotoriseerde thermische afdekking</td><td className="border border-slate-200 px-3 py-2">4.000-9.000 &euro;</td><td className="border border-slate-200 px-3 py-2">500-1.500 &euro;</td><td className="border border-slate-200 px-3 py-2">3-6 jaar</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">2</td><td className="border border-slate-200 px-3 py-2">VSD-pomp</td><td className="border border-slate-200 px-3 py-2">600-1.500 &euro;</td><td className="border border-slate-200 px-3 py-2">150-350 &euro;</td><td className="border border-slate-200 px-3 py-2">2-6 jaar</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">3</td><td className="border border-slate-200 px-3 py-2">Warmtepomp COP&gt;5</td><td className="border border-slate-200 px-3 py-2">2.500-6.000 &euro;</td><td className="border border-slate-200 px-3 py-2">800-2.000 &euro;</td><td className="border border-slate-200 px-3 py-2">2-4 seizoenen</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">4</td><td className="border border-slate-200 px-3 py-2">LED-verlichting</td><td className="border border-slate-200 px-3 py-2">200-500 &euro;/spot</td><td className="border border-slate-200 px-3 py-2">30-100 &euro;</td><td className="border border-slate-200 px-3 py-2">3-6 jaar</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">5</td><td className="border border-slate-200 px-3 py-2">pH+ORP automatisering</td><td className="border border-slate-200 px-3 py-2">800-2.000 &euro;</td><td className="border border-slate-200 px-3 py-2">60-150 &euro;</td><td className="border border-slate-200 px-3 py-2">5-10 jaar</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">6</td><td className="border border-slate-200 px-3 py-2">Isolatie bad</td><td className="border border-slate-200 px-3 py-2">1.500-4.000 &euro;</td><td className="border border-slate-200 px-3 py-2">100-300 &euro;</td><td className="border border-slate-200 px-3 py-2">5-10 jaar</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">7</td><td className="border border-slate-200 px-3 py-2">Filtratie-uren afstemmen</td><td className="border border-slate-200 px-3 py-2">0 &euro;</td><td className="border border-slate-200 px-3 py-2">30-150 &euro;</td><td className="border border-slate-200 px-3 py-2">Onmiddellijk</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">8</td><td className="border border-slate-200 px-3 py-2">Tarief met daluren</td><td className="border border-slate-200 px-3 py-2">0 &euro;</td><td className="border border-slate-200 px-3 py-2">80-200 &euro;</td><td className="border border-slate-200 px-3 py-2">Onmiddellijk</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">9</td><td className="border border-slate-200 px-3 py-2">Fotovolta&iuml;sche zonne-energie</td><td className="border border-slate-200 px-3 py-2">3.500-7.000 &euro;</td><td className="border border-slate-200 px-3 py-2">400-800 &euro;</td><td className="border border-slate-200 px-3 py-2">6-10 jaar</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">10</td><td className="border border-slate-200 px-3 py-2">Thermische zonne-energie</td><td className="border border-slate-200 px-3 py-2">1.500-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">300-700 &euro;</td><td className="border border-slate-200 px-3 py-2">3-5 jaar</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">11</td><td className="border border-slate-200 px-3 py-2">Chemische dosering</td><td className="border border-slate-200 px-3 py-2">300-800 &euro;</td><td className="border border-slate-200 px-3 py-2">40-100 &euro;</td><td className="border border-slate-200 px-3 py-2">4-8 jaar</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">12</td><td className="border border-slate-200 px-3 py-2">Lekmonitoring</td><td className="border border-slate-200 px-3 py-2">400-1.200 &euro;</td><td className="border border-slate-200 px-3 py-2">Variabel</td><td className="border border-slate-200 px-3 py-2">3-8 jaar</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">13</td><td className="border border-slate-200 px-3 py-2">Hydraulische renovatie</td><td className="border border-slate-200 px-3 py-2">1.000-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">80-200 &euro;</td><td className="border border-slate-200 px-3 py-2">5-15 jaar</td></tr>
          <tr className="bg-slate-50"><td className="border border-slate-200 px-3 py-2">14</td><td className="border border-slate-200 px-3 py-2">Zout/UV/Ozon</td><td className="border border-slate-200 px-3 py-2">800-3.500 &euro;</td><td className="border border-slate-200 px-3 py-2">50-150 &euro;</td><td className="border border-slate-200 px-3 py-2">6-12 jaar</td></tr>
          <tr><td className="border border-slate-200 px-3 py-2">15</td><td className="border border-slate-200 px-3 py-2">Certificaat EN 17645</td><td className="border border-slate-200 px-3 py-2">0 &euro; Fiche</td><td className="border border-slate-200 px-3 py-2">Maakt de 14 voorgaande mogelijk</td><td className="border border-slate-200 px-3 py-2">Onmiddellijk</td></tr>
        </tbody>
      </table>
    </div>

    <hr />

    <p>
      <strong>PoolLex.io</strong> helpt u deze lijst te ordenen voor{" "}
      <strong>uw specifieke zwembad</strong>. De gratis Technische Fiche
      evalueert de 8 componenten van de norm NEN-EN 17645:2023 en prioriteert
      de verbeteringen op impact en ROI. Start op{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> en verkrijg uw unieke{" "}
      <strong>PoolLex ID</strong> met geschatte A-F klassering in minder dan
      2 minuten.
    </p>
  </>
);

// ============================================================================
// ARTICLE 4 — Costes operativos piscina 2026 (ES only)
// Sprint 119.100 (2026-08-12) — GSC query "costes operativos de una piscina"
// (12 imp/setmana, pos 63). Single-language ES.
// ============================================================================

const Article4BodyES = () => (
  <>
    <h2>Coste operativo vs coste de instalaci&oacute;n: dos conceptos distintos</h2>
    <p>
      Antes de entrar en cifras, conviene separar dos partidas que muchos
      propietarios mezclan y que la contabilidad t&eacute;cnica distingue con
      claridad. El <strong>coste de instalaci&oacute;n</strong> (CAPEX) es la
      inversi&oacute;n inicial: obra civil, vaso, coronaci&oacute;n, sala de
      m&aacute;quinas, primera puesta en marcha. El{" "}
      <strong>coste operativo</strong> (OPEX) es todo lo que la piscina consume
      a&ntilde;o tras a&ntilde;o para funcionar: electricidad, agua, productos
      qu&iacute;micos, mano de obra y las reparaciones que van apareciendo.
    </p>
    <p>
      Este art&iacute;culo se centra en el <strong>OPEX real</strong> de una
      piscina residencial exterior enterrada en Espa&ntilde;a en 2026. Los
      rangos que verás m&aacute;s abajo son estimaciones de sector: la
      <strong> factura anual</strong> t&iacute;pica se sit&uacute;a entre{" "}
      <strong>600 y 1.800 &euro;/a&ntilde;o</strong> seg&uacute;n tama&ntilde;o,
      equipamiento y clima.
    </p>

    <h2>Los 5 componentes del coste operativo anual</h2>
    <p>
      El OPEX de una piscina residencial se descompone en cinco partidas con
      pesos muy distintos:
    </p>
    <ol>
      <li><strong>Electricidad</strong> &mdash; 40 a 65 % del total.</li>
      <li><strong>Agua de reposici&oacute;n</strong> &mdash; 8 a 15 %.</li>
      <li><strong>Productos qu&iacute;micos</strong> &mdash; 10 a 18 %.</li>
      <li><strong>Mano de obra y mantenimiento programado</strong> &mdash; 10 a 25 % (depende de si lo hace el propietario o un servicio externo).</li>
      <li><strong>Reparaciones y recambios</strong> &mdash; 5 a 12 % en promedio anualizado (variable a&ntilde;o a a&ntilde;o).</li>
    </ol>
    <p>
      A esto habr&iacute;a que a&ntilde;adir la{" "}
      <strong>amortizaci&oacute;n contable de equipos</strong> con vida &uacute;til
      finita (bomba de calor, bomba de filtraci&oacute;n, l&aacute;mpara UV,
      cubierta motorizada), que t&eacute;cnicamente no es un gasto operativo
      sino una previsi&oacute;n de reposici&oacute;n futura, pero que conviene
      contemplar para no llevarse sorpresas.
    </p>

    <h2>Electricidad: la partida dominante</h2>
    <p>
      Con un precio medio en Espa&ntilde;a de{" "}
      <strong>0,15 a 0,30 &euro;/kWh</strong> (mercado libre, PVPC o tarifas 2.0
      TD con discriminaci&oacute;n horaria), el consumo el&eacute;ctrico anual
      de una piscina depende de tres equipos principales:
    </p>
    <h3>Bomba de filtraci&oacute;n</h3>
    <p>
      Es el equipo con m&aacute;s horas de servicio. Una bomba monovelocidad
      convencional de 0,75-1,5 CV en una piscina de 40-50 m&sup3; consume{" "}
      <strong>1.200 a 2.800 kWh/a&ntilde;o</strong> operando 6-8 h diarias en
      temporada. A 0,20 &euro;/kWh eso son{" "}
      <strong>240-560 &euro;/a&ntilde;o</strong> solo en filtraci&oacute;n.
    </p>
    <h3>Climatizaci&oacute;n (si existe)</h3>
    <p>
      Si tu piscina tiene bomba de calor y la usas de mayo a octubre con
      cubierta t&eacute;rmica, a&ntilde;ade <strong>300-700 &euro;/a&ntilde;o</strong>{" "}
      m&aacute;s. Sin cubierta, la cifra puede doblarse o triplicarse. Con
      resistencia el&eacute;ctrica pura (no recomendado) el gasto se multiplica
      hasta por cinco.
    </p>
    <h3>Iluminaci&oacute;n y accesorios</h3>
    <p>
      Focos LED sumergibles, bombas dosificadoras y cloradores salinos suman
      <strong> 30-80 &euro;/a&ntilde;o</strong> en conjunto. Es el bloque m&aacute;s
      peque&ntilde;o pero tambi&eacute;n el m&aacute;s f&aacute;cilmente
      optimizable.
    </p>

    <h2>Agua de reposici&oacute;n</h2>
    <p>
      Una piscina exterior pierde agua por{" "}
      <strong>evaporaci&oacute;n</strong> (principal), contralavados del filtro,
      salpicaduras y ocasionalmente vaciado parcial. El precio medio del agua
      en Espa&ntilde;a oscila entre <strong>1,20 y 3,50 &euro;/m&sup3;</strong>{" "}
      seg&uacute;n municipio (ordenanza fiscal + canon auton&oacute;mico +
      alcantarillado).
    </p>
    <p>
      Para una piscina de 32 m&sup2; en zona c&aacute;lida, la reposici&oacute;n
      anual t&iacute;pica es de <strong>15-40 m&sup3;/a&ntilde;o</strong>, es
      decir <strong>25-140 &euro;/a&ntilde;o</strong> en factura de agua. Los
      municipios con canon de saneamiento sobre volumen consumido pueden
      duplicar esa cifra si no est&aacute; declarada la piscina como uso
      espec&iacute;fico.
    </p>

    <h2>Productos qu&iacute;micos y tratamiento del agua</h2>
    <p>
      La partida de qu&iacute;micos depende directamente del sistema de
      desinfecci&oacute;n:
    </p>
    <ul>
      <li><strong>Cloro s&oacute;lido (hipoclorito o tricloro)</strong>: 80-150 &euro;/a&ntilde;o para piscinas de 40-60 m&sup3;.</li>
      <li><strong>Clorador salino</strong>: 40-90 &euro;/a&ntilde;o (menos cloro puntual, pero recarga anual de 20-40 kg de sal).</li>
      <li><strong>Regulador pH (l&iacute;quido o s&oacute;lido)</strong>: 15-35 &euro;/a&ntilde;o.</li>
      <li><strong>Algicida, floculante y kits de test</strong>: 20-50 &euro;/a&ntilde;o.</li>
    </ul>
    <p>
      El presupuesto total en qu&iacute;micos suele oscilar entre{" "}
      <strong>100 y 220 &euro;/a&ntilde;o</strong>. La cloraci&oacute;n salina
      reduce el gasto en cloro pero suma consumo el&eacute;ctrico del
      electrolizador (30-50 W activo el 30-40 % del tiempo de filtraci&oacute;n).
    </p>

    <h2>Mano de obra y mantenimiento programado</h2>
    <p>
      Si el propietario hace el mantenimiento ordinario (limpieza de skimmer,
      cepillado, medici&oacute;n de cloro y pH, contralavado semanal), el
      coste laboral es cero pero hay que sumar el tiempo dedicado: unas{" "}
      <strong>30-50 horas al a&ntilde;o</strong>. Si se contrata a un servicio
      externo con visitas quincenales, el presupuesto habitual es{" "}
      <strong>350-700 &euro;/temporada</strong>, que incluye desplazamientos,
      productos b&aacute;sicos y pequeños ajustes.
    </p>
    <p>
      A esto conviene a&ntilde;adir la <strong>puesta en marcha de temporada</strong>{" "}
      (marzo-abril) y el <strong>invernaje</strong> (octubre-noviembre) si se
      contrata externamente: 80-200 &euro; por cada operaci&oacute;n.
    </p>

    <h2>Reparaciones, recambios y amortizaci&oacute;n</h2>
    <p>
      Aunque los a&ntilde;os buenos apenas gastas nada aqu&iacute;, conviene
      provisionar una media anualizada para no descapitalizar la instalaci&oacute;n.
      Estos son los &oacute;rdenes de magnitud t&iacute;picos:
    </p>
    <table>
      <thead>
        <tr>
          <th>Elemento</th>
          <th>Vida &uacute;til</th>
          <th>Coste reposici&oacute;n</th>
          <th>Amortizaci&oacute;n anual</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>Bomba filtraci&oacute;n monovelocidad</td><td>8-12 a&ntilde;os</td><td>300-600 &euro;</td><td>30-70 &euro;/a&ntilde;o</td></tr>
        <tr><td>Bomba filtraci&oacute;n VSD</td><td>10-15 a&ntilde;os</td><td>700-1.400 &euro;</td><td>60-120 &euro;/a&ntilde;o</td></tr>
        <tr><td>Bomba de calor</td><td>10-15 a&ntilde;os</td><td>2.500-5.500 &euro;</td><td>200-450 &euro;/a&ntilde;o</td></tr>
        <tr><td>Cubierta motorizada</td><td>10-15 a&ntilde;os</td><td>4.000-9.000 &euro;</td><td>300-700 &euro;/a&ntilde;o</td></tr>
        <tr><td>Clorador salino (c&eacute;lula)</td><td>4-7 a&ntilde;os</td><td>250-500 &euro;</td><td>40-100 &euro;/a&ntilde;o</td></tr>
        <tr><td>Focos LED sumergibles (juego)</td><td>10-15 a&ntilde;os</td><td>300-600 &euro;</td><td>25-50 &euro;/a&ntilde;o</td></tr>
      </tbody>
    </table>

    <h2>Caso real 1: piscina de 30 m&sup3; no climatizada</h2>
    <p>
      Piscina rectangular 6&times;3&times;1,6 m en zona interior (Madrid),
      bomba monovelocidad 0,75 CV, cloraci&oacute;n manual, sin cubierta
      motorizada, temporada 4 meses (mayo-agosto):
    </p>
    <ul>
      <li>Electricidad filtraci&oacute;n: 350 &euro;/a&ntilde;o</li>
      <li>Agua reposici&oacute;n: 45 &euro;/a&ntilde;o</li>
      <li>Qu&iacute;micos: 130 &euro;/a&ntilde;o</li>
      <li>Mantenimiento propio (0 &euro; mano de obra)</li>
      <li>Amortizaci&oacute;n equipos: 75 &euro;/a&ntilde;o</li>
      <li><strong>Total OPEX: 600 &euro;/a&ntilde;o</strong> &asymp; 50 &euro;/mes anualizado</li>
    </ul>

    <h2>Caso real 2: piscina de 50 m&sup3; climatizada con cubierta</h2>
    <p>
      Piscina rectangular 8&times;4&times;1,55 m en zona levante (Valencia),
      bomba VSD, bomba de calor con COP 5, cubierta motorizada, clorador salino,
      temporada 8 meses (marzo-octubre):
    </p>
    <ul>
      <li>Electricidad filtraci&oacute;n: 180 &euro;/a&ntilde;o</li>
      <li>Electricidad climatizaci&oacute;n: 520 &euro;/a&ntilde;o</li>
      <li>Agua reposici&oacute;n: 70 &euro;/a&ntilde;o</li>
      <li>Qu&iacute;micos (salino): 85 &euro;/a&ntilde;o</li>
      <li>Servicio externo quincenal: 480 &euro;/a&ntilde;o</li>
      <li>Amortizaci&oacute;n equipos: 320 &euro;/a&ntilde;o</li>
      <li><strong>Total OPEX: 1.655 &euro;/a&ntilde;o</strong> &asymp; 138 &euro;/mes anualizado</li>
    </ul>

    <h2>Amortizaci&oacute;n de una bomba de calor: &iquest;compensa?</h2>
    <p>
      Una bomba de calor de 12-14 kW t&eacute;rmicos para piscinas de 40-60 m&sup3;
      cuesta 2.500-4.500 &euro; instalada. Frente a una resistencia el&eacute;ctrica
      equivalente, ahorra entre <strong>600 y 1.200 &euro;/a&ntilde;o</strong>{" "}
      en electricidad para el mismo salto t&eacute;rmico. Amortizaci&oacute;n:
      <strong> 3-5 temporadas</strong>. Frente a no climatizar, el retorno
      depende del uso: si extiendes la temporada 4-5 meses, el confort adicional
      justifica la inversi&oacute;n aunque el ROI puro sea largo.
    </p>

    <h2>Amortizaci&oacute;n de la cubierta t&eacute;rmica</h2>
    <p>
      Una cubierta motorizada cuesta 4.500-8.000 &euro; instalada. En piscinas
      climatizadas ahorra 40-60 % de energ&iacute;a de calefacci&oacute;n
      (250-450 &euro;/a&ntilde;o) y reduce evaporaci&oacute;n 60-70 % (40-80
      &euro;/a&ntilde;o en agua + qu&iacute;micos). Retorno: <strong>6-10 a&ntilde;os</strong>{" "}
      en climatizadas, m&aacute;s largo en no climatizadas. Adem&aacute;s, es
      el componente que m&aacute;s puntos otorga en la clasificaci&oacute;n{" "}
      <strong>UNE-EN 17645</strong>.
    </p>

    <h2>C&oacute;mo obtener el desglose exacto de tu piscina</h2>
    <p>
      Los rangos anteriores son medias del sector. Para saber cu&aacute;nto
      gasta <strong>exactamente tu piscina</strong> necesitas cinco datos:
    </p>
    <ol>
      <li>Volumen real (m&sup3;) &mdash; calc&uacute;lalo con{" "}
        <Link to="/tools/pool-volume-calculator">nuestra calculadora de volumen</Link>.</li>
      <li>Potencia real de la bomba (kW, no CV nominal).</li>
      <li>Horas diarias de filtraci&oacute;n por temporada.</li>
      <li>Tarifa el&eacute;ctrica media (&euro;/kWh incluyendo peajes).</li>
      <li>Precio del agua en tu municipio (&euro;/m&sup3;).</li>
    </ol>
    <p>
      Con estos datos, en <Link to="/calculadoras">/calculadoras</Link> tienes
      herramientas gratuitas para estimar cada partida. Y si quieres una{" "}
      <strong>clasificaci&oacute;n ambiental UNE-EN 17645</strong> completa que
      combine todos los factores (equipamiento, superficie, exposici&oacute;n
      solar, uso), obt&eacute;n gratis tu{" "}
      <Link to="/ficha-tecnica">Ficha T&eacute;cnica PoolLex</Link> con tu{" "}
      <strong>PoolLex ID</strong> &uacute;nico.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> convierte los datos declarados de tu piscina
      en un diagn&oacute;stico t&eacute;cnico honesto y una estimaci&oacute;n de
      OPEX anual coherente con la norma UNE-EN 17645:2023. Empieza gratis en{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

// ============================================================================
// ARTICLE 5 — Calculadora m3 piscina redonda (ES only)
// Sprint 119.100 (2026-08-12) — GSC queries "calcular m3 piscina redonda"
// (3 imp), "calculadora piscina redonda". Single-language ES.
// ============================================================================

const Article5BodyES = () => (
  <>
    <h2>La f&oacute;rmula esencial: V = &pi; &times; r&sup2; &times; h</h2>
    <p>
      Calcular los <strong>m&sup3;</strong> de una piscina redonda es un
      c&aacute;lculo de <strong>volumen de un cilindro</strong>: multiplicar la
      superficie del c&iacute;rculo por la altura del agua. La f&oacute;rmula
      exacta es:
    </p>
    <p>
      <strong>V (m&sup3;) = &pi; &times; r&sup2; &times; h</strong>
    </p>
    <p>Donde:</p>
    <ul>
      <li><strong>&pi; (pi)</strong> &asymp; 3,1416</li>
      <li><strong>r</strong> = radio de la piscina en metros (la mitad del di&aacute;metro)</li>
      <li><strong>h</strong> = altura del agua en metros (no la altura f&iacute;sica de la pared)</li>
    </ul>
    <p>
      Si prefieres trabajar directamente con el di&aacute;metro <strong>D</strong>,
      la f&oacute;rmula equivalente es:
    </p>
    <p>
      <strong>V (m&sup3;) = (&pi; / 4) &times; D&sup2; &times; h &asymp; 0,7854 &times; D&sup2; &times; h</strong>
    </p>

    <h2>El error m&aacute;s com&uacute;n: confundir radio con di&aacute;metro</h2>
    <p>
      Es la equivocaci&oacute;n que multiplica por 4 el resultado (o lo divide
      por 4, seg&uacute;n el sentido del error). El <strong>di&aacute;metro</strong>{" "}
      es la medida completa de lado a lado pasando por el centro. El{" "}
      <strong>radio</strong> es la mitad de esa medida.
    </p>
    <p>
      Ejemplo: una piscina Intex de <strong>4,57 m de di&aacute;metro</strong>{" "}
      tiene un radio de <strong>2,285 m</strong>. Si usas 4,57 como radio en la
      f&oacute;rmula, obtendr&aacute;s 4 veces el volumen real. Antes de
      calcular, ap&uacute;ntalo con claridad: r = D / 2.
    </p>

    <h2>Ejemplos resueltos por di&aacute;metro habitual</h2>
    <p>
      A continuaci&oacute;n calculamos el volumen para los di&aacute;metros
      m&aacute;s frecuentes en piscinas residenciales rodones, asumiendo la
      altura de agua est&aacute;ndar (agua hasta 90 % de la pared para evitar
      desbordamientos):
    </p>

    <h3>Piscina redonda de 3 m de di&aacute;metro</h3>
    <p>
      Radio = 1,5 m. Con altura de agua 0,76 m (piscina desmontable 76 cm):
    </p>
    <p>
      V = 3,1416 &times; 1,5&sup2; &times; 0,76 = 3,1416 &times; 2,25 &times; 0,76 &asymp;{" "}
      <strong>5,37 m&sup3;</strong> = 5.370 litros
    </p>

    <h3>Piscina redonda de 3,66 m de di&aacute;metro</h3>
    <p>
      Radio = 1,83 m. Con altura de agua 0,84 m (piscina desmontable 91 cm):
    </p>
    <p>
      V = 3,1416 &times; 1,83&sup2; &times; 0,84 = 3,1416 &times; 3,349 &times; 0,84 &asymp;{" "}
      <strong>8,84 m&sup3;</strong> = 8.840 litros
    </p>

    <h3>Piscina redonda de 4,57 m de di&aacute;metro</h3>
    <p>
      Radio = 2,285 m. Con altura de agua 1,07 m (piscina desmontable 122 cm):
    </p>
    <p>
      V = 3,1416 &times; 2,285&sup2; &times; 1,07 = 3,1416 &times; 5,221 &times; 1,07 &asymp;{" "}
      <strong>17,55 m&sup3;</strong> = 17.550 litros
    </p>

    <h3>Piscina redonda de 5,49 m de di&aacute;metro</h3>
    <p>
      Radio = 2,745 m. Con altura de agua 1,20 m (piscina desmontable 132 cm):
    </p>
    <p>
      V = 3,1416 &times; 2,745&sup2; &times; 1,20 = 3,1416 &times; 7,535 &times; 1,20 &asymp;{" "}
      <strong>28,41 m&sup3;</strong> = 28.410 litros
    </p>

    <h3>Piscina redonda de 6 m de di&aacute;metro</h3>
    <p>
      Radio = 3 m. Con altura de agua 1,32 m (piscina enterrada baja):
    </p>
    <p>
      V = 3,1416 &times; 3&sup2; &times; 1,32 = 3,1416 &times; 9 &times; 1,32 &asymp;{" "}
      <strong>37,32 m&sup3;</strong> = 37.320 litros
    </p>

    <h2>Tabla de referencia r&aacute;pida</h2>
    <p>
      Volumen aproximado seg&uacute;n di&aacute;metro y altura de agua. Redondeado
      al 0,1 m&sup3; m&aacute;s pr&oacute;ximo:
    </p>
    <table>
      <thead>
        <tr>
          <th>Di&aacute;metro (m)</th>
          <th>Altura agua 0,76 m</th>
          <th>Altura agua 0,90 m</th>
          <th>Altura agua 1,07 m</th>
          <th>Altura agua 1,32 m</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>3,00</td><td>5,4 m&sup3;</td><td>6,4 m&sup3;</td><td>7,6 m&sup3;</td><td>9,3 m&sup3;</td></tr>
        <tr><td>3,66</td><td>8,0 m&sup3;</td><td>9,5 m&sup3;</td><td>11,3 m&sup3;</td><td>13,9 m&sup3;</td></tr>
        <tr><td>4,57</td><td>12,5 m&sup3;</td><td>14,8 m&sup3;</td><td>17,6 m&sup3;</td><td>21,7 m&sup3;</td></tr>
        <tr><td>5,49</td><td>18,0 m&sup3;</td><td>21,3 m&sup3;</td><td>25,4 m&sup3;</td><td>31,3 m&sup3;</td></tr>
        <tr><td>6,00</td><td>21,5 m&sup3;</td><td>25,4 m&sup3;</td><td>30,3 m&sup3;</td><td>37,4 m&sup3;</td></tr>
      </tbody>
    </table>

    <h2>Piscinas desmontables Intex y Bestway: datos reales</h2>
    <p>
      Los fabricantes publican en la caja un volumen que asume{" "}
      <strong>llenado al 90 %</strong> de la altura de la pared (recomendaci&oacute;n
      de seguridad para evitar desbordamiento por peso de ba&ntilde;istas). Los
      modelos m&aacute;s vendidos:
    </p>
    <ul>
      <li><strong>Intex Easy Set 3,05 &times; 0,76 m</strong>: 3.853 litros declarados (llenado al 80 %). Real al 90 %: 4.180 l.</li>
      <li><strong>Intex Metal Frame 3,66 &times; 0,99 m</strong>: 6.503 litros declarados. Real al 90 %: 8.930 l.</li>
      <li><strong>Bestway Steel Pro 4,27 &times; 1,00 m</strong>: 10.250 litros declarados.</li>
      <li><strong>Intex Prism Frame 4,57 &times; 1,22 m</strong>: 16.805 litros declarados. Real al 90 %: 17.550 l.</li>
      <li><strong>Bestway Power Steel 5,49 &times; 1,32 m</strong>: 26.478 litros declarados. Real al 90 %: 28.410 l.</li>
    </ul>
    <p>
      Si el n&uacute;mero de tu caja difiere del c&aacute;lculo te&oacute;rico
      es porque el fabricante ha considerado el llenado real recomendado. Para
      dosificar cloro y sal usa siempre el <strong>volumen real de agua</strong>{" "}
      que hay en el vaso, no el te&oacute;rico m&aacute;ximo.
    </p>

    <h2>Convertir m&sup3; a litros: la regla que nunca falla</h2>
    <p>
      La conversi&oacute;n es fija y no depende de nada:
    </p>
    <p>
      <strong>1 m&sup3; = 1.000 litros</strong>
    </p>
    <p>
      Ejemplo: una piscina de 8,84 m&sup3; tiene 8.840 litros. Si necesitas
      conocer el equivalente en <strong>galones USA</strong>, multiplica por
      264,17; para <strong>galones UK</strong>, por 219,97.
    </p>

    <h2>Por qu&eacute; conocer el volumen exacto es cr&iacute;tico</h2>
    <p>
      No es un dato acad&eacute;mico: cinco decisiones habituales de tu piscina
      dependen directamente del volumen:
    </p>
    <ol>
      <li><strong>Dosificaci&oacute;n de cloro</strong>: 5-10 g de cloro activo por m&sup3; para desinfecci&oacute;n de choque. Un error del 20 % en el volumen te lleva a subdosificar (piscina turbia) o sobredosificar (irritaci&oacute;n, corrosi&oacute;n).</li>
      <li><strong>Sal para clorador salino</strong>: 4-5 kg/m&sup3; en carga inicial. Un error significa reponer sal inecesariamente o dejar el electrolizador fuera de rango.</li>
      <li><strong>Horas de filtraci&oacute;n</strong>: la regla del sector es filtrar el volumen 1 vez cada 4-6 h. Sin volumen exacto no puedes ajustar caudal &times; tiempo.</li>
      <li><strong>Bomba de calor</strong>: la potencia t&eacute;rmica se dimensiona por m&sup3;. Sobredimensionar cuesta caro; infradimensionar no calienta.</li>
      <li><strong>Antialgas y floculante</strong>: dosis en ml/m&sup3;. Sin volumen preciso, ineficaz.</li>
    </ol>

    <h2>Calculadora online: haz el c&aacute;lculo en 10 segundos</h2>
    <p>
      Si prefieres no operar a mano, en{" "}
      <Link to="/tools/pool-volume-calculator">/tools/pool-volume-calculator</Link>{" "}
      tienes una <strong>calculadora gratuita de volumen de piscina</strong>{" "}
      que acepta:
    </p>
    <ul>
      <li>Piscinas <strong>redondas</strong> (di&aacute;metro + altura).</li>
      <li>Piscinas <strong>rectangulares</strong> (largo &times; ancho &times; profundidad media).</li>
      <li>Piscinas <strong>ovales</strong> y de forma libre con profundidad variable.</li>
    </ul>
    <p>
      Devuelve el volumen en m&sup3; y en litros, y te da la referencia
      aproximada para dosificaci&oacute;n de qu&iacute;micos. Si adem&aacute;s
      quieres estimar el <strong>coste anual de mantenimiento</strong> de tu
      piscina y su <strong>clase ambiental UNE-EN 17645</strong>, obt&eacute;n
      gratis tu Ficha T&eacute;cnica en{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> convierte el volumen de tu piscina en un
      diagn&oacute;stico t&eacute;cnico completo: consumo estimado, dosificaci&oacute;n
      de qu&iacute;micos y clase ambiental A-F. Empieza gratis con tu{" "}
      <strong>PoolLex ID</strong> en{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>

    <p>
      <em>&iquest;Tu piscina no es redonda?</em> En{" "}
      <Link to="/blog/volumen-piscina-litros-guia">la gu&iacute;a general de
      volumen en litros</Link> tienes las f&oacute;rmulas para piscinas
      rectangulares, ovaladas y de forma libre, adem&aacute;s de la tabla de
      referencia por medidas y las aplicaciones pr&aacute;cticas
      (dosificaci&oacute;n, bomba y climatizaci&oacute;n).
    </p>
  </>
);

// ============================================================================
// ARTICLE 6 — Certificado finalización de obra piscina (ES only)
// Sprint 119.100 (2026-08-12) — GSC "certificado de finalización de obra
// para piscinas" (1 imp, pos 27). Single-language ES.
// ============================================================================

const Article6BodyES = () => (
  <>
    <h2>Qu&eacute; es el certificado de finalizaci&oacute;n de obra</h2>
    <p>
      El <strong>certificado final de obra</strong> (habitualmente abreviado
      como <strong>CFO</strong>) es el documento t&eacute;cnico firmado por la
      direcci&oacute;n facultativa que acredita que una obra terminada se ha
      ejecutado conforme al proyecto aprobado, a las normas t&eacute;cnicas
      aplicables y a las condiciones impuestas por la licencia municipal. Est&aacute;{" "}
      regulado por la <strong>Ley 38/1999 de Ordenaci&oacute;n de la Edificaci&oacute;n</strong>{" "}
      (LOE) y por los reglamentos auton&oacute;micos que la desarrollan.
    </p>
    <p>
      Aplicado a <strong>piscinas residenciales</strong>, el CFO es la pieza
      final que permite al ayuntamiento cerrar el expediente de obra y al
      propietario disponer del uso legal de la instalaci&oacute;n. Sin CFO, la
      piscina existe f&iacute;sicamente pero no est&aacute; regularizada.
    </p>

    <h2>&iquest;Cu&aacute;ndo es obligatorio para una piscina?</h2>
    <p>
      No todas las piscinas necesitan CFO. La regla depende del tipo de obra
      autorizada por la licencia inicial:
    </p>
    <ul>
      <li><strong>Piscina enterrada con obra mayor</strong> (excavaci&oacute;n, hormig&oacute;n, obra civil): <strong>CFO obligatorio</strong>.</li>
      <li><strong>Piscina prefabricada de poli&eacute;ster con obra menor</strong> (foso, base y conexiones): <strong>certificado de instalaci&oacute;n</strong> firmado por t&eacute;cnico competente. Algunos municipios lo equiparan a CFO simplificado.</li>
      <li><strong>Piscina desmontable</strong> (Intex, Bestway) sin obra: normalmente <strong>no</strong> requiere CFO, aunque puede exigir comunicaci&oacute;n previa o declaraci&oacute;n responsable.</li>
      <li><strong>Reforma de piscina existente</strong> con cambio de vaso, ampliaci&oacute;n o modificaci&oacute;n sustancial: <strong>CFO obligatorio</strong> si la licencia era de obra mayor.</li>
    </ul>
    <p>
      Consulta siempre la <strong>ordenanza municipal</strong> de tu ayuntamiento
      y el <strong>tipo de licencia</strong> concedida. La ordenanza puede
      exigir requisitos adicionales al m&iacute;nimo estatal (por ejemplo,
      memoria de seguridad EN 15288).
    </p>

    <h2>Qui&eacute;n puede firmar el certificado</h2>
    <p>
      El CFO debe estar firmado por t&eacute;cnicos competentes seg&uacute;n la
      LOE y sus normativas de desarrollo. Para piscinas residenciales
      enterradas, las combinaciones habituales son:
    </p>
    <ul>
      <li><strong>Arquitecto</strong> o <strong>arquitecto t&eacute;cnico</strong> como director de la obra civil (vaso, coronaci&oacute;n, playa).</li>
      <li><strong>Ingeniero industrial</strong>, <strong>ingeniero t&eacute;cnico industrial</strong> o graduado equivalente como director de las instalaciones (fontaner&iacute;a, sala de m&aacute;quinas, sistema el&eacute;ctrico, climatizaci&oacute;n si existe).</li>
    </ul>
    <p>
      Ambos t&eacute;cnicos deben estar <strong>colegiados</strong> y con el
      visado del colegio profesional correspondiente si la comunidad
      aut&oacute;noma o el ayuntamiento lo exigen. En algunos casos el CFO se
      firma conjuntamente; en otros, cada t&eacute;cnico emite su certificado
      parcial y el ayuntamiento los integra.
    </p>

    <h2>Diferencia con la legalizaci&oacute;n urban&iacute;stica</h2>
    <p>
      Son dos figuras distintas que suelen confundirse:
    </p>
    <ul>
      <li>La <strong>legalizaci&oacute;n urban&iacute;stica</strong> es el procedimiento para regularizar una piscina construida <strong>sin licencia previa</strong> o con desviaciones del proyecto aprobado. Requiere proyecto t&eacute;cnico, informe de compatibilidad urban&iacute;stica y pago de tasa. Puede acabar con multa si la infracci&oacute;n prescribi&oacute; o con orden de demolici&oacute;n si no.</li>
      <li>El <strong>certificado final de obra</strong> es el cierre normal de una obra <strong>que tuvo licencia</strong>. Es un tr&aacute;mite ordinario, no una regularizaci&oacute;n.</li>
    </ul>
    <p>
      Si tu piscina se construy&oacute; sin licencia, necesitas primero
      legalizar y luego, si procede, obtener CFO retrospectivo. Los dos
      procedimientos son complementarios pero no intercambiables.
    </p>

    <h2>Documentaci&oacute;n requerida para el CFO</h2>
    <p>
      El paquete t&iacute;pico que la direcci&oacute;n facultativa entrega al
      ayuntamiento incluye:
    </p>
    <ol>
      <li><strong>Certificado final de obra</strong> firmado y visado (modelo LOE anexo II).</li>
      <li><strong>Planos "as built"</strong> reflejando la ejecuci&oacute;n real (planta, secciones, sala de m&aacute;quinas, red hidr&aacute;ulica).</li>
      <li><strong>Memoria de instalaciones</strong> con potencias, caudales, hoja de datos de bombas y filtros, esquema el&eacute;ctrico.</li>
      <li><strong>Bolet&iacute;n de instalaci&oacute;n el&eacute;ctrica</strong> (industria) para la sala de m&aacute;quinas.</li>
      <li><strong>Certificados de materiales</strong>: hormig&oacute;n, revestimiento, sistemas de tratamiento.</li>
      <li><strong>Justificaci&oacute;n de cumplimiento</strong> del CTE (DB-SE, DB-SUA en zonas de p&uacute;blica concurrencia), UNE-EN 15288-1 y ordenanza municipal.</li>
    </ol>
    <p>
      En comunidades como Catalu&ntilde;a, Madrid o Andaluc&iacute;a algunos
      ayuntamientos exigen tambi&eacute;n el registro de la piscina en el
      censo municipal y la declaraci&oacute;n al padr&oacute;n de agua.
    </p>

    <h2>Procedimiento en el ayuntamiento</h2>
    <p>
      La secuencia est&aacute;ndar, sin sorpresas, es:
    </p>
    <ol>
      <li><strong>Finalizaci&oacute;n f&iacute;sica de la obra</strong> y comprobaciones finales de la direcci&oacute;n facultativa.</li>
      <li><strong>Elaboraci&oacute;n del CFO</strong> por los t&eacute;cnicos competentes (2-4 semanas seg&uacute;n complejidad).</li>
      <li><strong>Visado en los colegios profesionales</strong> correspondientes si aplica (1-2 semanas).</li>
      <li><strong>Presentaci&oacute;n en el ayuntamiento</strong> con instancia y pago de tasa de finalizaci&oacute;n.</li>
      <li><strong>Revisi&oacute;n t&eacute;cnica municipal</strong> (2-8 semanas seg&uacute;n cola de expedientes).</li>
      <li><strong>Resoluci&oacute;n favorable</strong> y comunicaci&oacute;n al propietario. Alta en el catastro si procede.</li>
    </ol>
    <p>
      El plazo total desde finalizaci&oacute;n de la obra hasta resoluci&oacute;n
      municipal oscila entre <strong>1 y 4 meses</strong>, dependiendo del
      ayuntamiento y de si hay requerimientos de subsanaci&oacute;n.
    </p>

    <h2>Coste aproximado del certificado</h2>
    <p>
      El precio de honorarios profesionales para el CFO de una piscina
      residencial se mueve en estas horquillas orientativas (2026):
    </p>
    <ul>
      <li><strong>Piscina prefabricada peque&ntilde;a</strong> (&lt;30 m&sup3;, sin instalaciones complejas): 150-300 &euro; + visados.</li>
      <li><strong>Piscina enterrada media</strong> (30-60 m&sup3;, con sala de m&aacute;quinas est&aacute;ndar): 300-500 &euro; + visados.</li>
      <li><strong>Piscina enterrada grande</strong> (&gt;60 m&sup3;, con climatizaci&oacute;n, dom&oacute;tica o revestimiento complejo): 500-900 &euro; + visados.</li>
    </ul>
    <p>
      A esto se a&ntilde;ade la <strong>tasa municipal</strong> de finalizaci&oacute;n
      de obra, que suele ser un porcentaje sobre el presupuesto de ejecuci&oacute;n
      material (t&iacute;picamente 0,5-1,5 %) o una cuota fija de{" "}
      <strong>60-200 &euro;</strong> seg&uacute;n ordenanza fiscal.
    </p>

    <h2>Errores frecuentes que retrasan la tramitaci&oacute;n</h2>
    <ul>
      <li><strong>Ejecutar cambios respecto al proyecto sin modificado aprobado</strong>: obliga a legalizar diferencias antes del CFO.</li>
      <li><strong>Instalaci&oacute;n el&eacute;ctrica sin bolet&iacute;n</strong>: sin este documento, el CFO queda incompleto.</li>
      <li><strong>Elementos accesorios no previstos</strong> (cubierta motorizada instalada despu&eacute;s, iluminaci&oacute;n adicional, cambio de sistema de tratamiento): pueden requerir addenda al proyecto original.</li>
      <li><strong>Documentaci&oacute;n no visada</strong> en colegios donde el visado es obligatorio.</li>
    </ul>

    <h2>Antes de solicitar el CFO: verifica cumplimiento</h2>
    <p>
      Si tienes dudas sobre si tu piscina cumple con los requisitos t&eacute;cnicos
      m&iacute;nimos (UNE-EN 15288-1/2, CTE, ordenanza municipal), antes de
      encargar el CFO puedes usar <Link to="/pool-check">/pool-check</Link>{" "}
      para un <strong>chequeo preliminar de cumplimiento</strong>. El servicio
      detecta:
    </p>
    <ul>
      <li>Distancias a lindes y edificaciones seg&uacute;n ordenanza municipal.</li>
      <li>Requisitos de seguridad (vallado, escalera, elementos anti-atrapamiento).</li>
      <li>Adecuaci&oacute;n de la sala de m&aacute;quinas (ventilaci&oacute;n, drenaje).</li>
      <li>Cumplimiento de normativa el&eacute;ctrica (REBI, distancias de seguridad).</li>
      <li>Documentaci&oacute;n necesaria seg&uacute;n tipo de licencia.</li>
    </ul>
    <p>
      Detectar carencias antes de presentar el CFO evita requerimientos de
      subsanaci&oacute;n que pueden retrasar la resoluci&oacute;n 2-3 meses.
    </p>

    <h2>El CFO y la certificaci&oacute;n ambiental UNE-EN 17645</h2>
    <p>
      El CFO acredita el cumplimiento reglamentario, no la eficiencia ambiental
      de la piscina. Son dos capas complementarias: la primera es{" "}
      <strong>obligatoria</strong> (licencia + CFO); la segunda,{" "}
      <strong>voluntaria</strong> pero cada vez m&aacute;s valorada por
      administraciones y compradores. La norma{" "}
      <strong>UNE-EN 17645:2023</strong> establece una clasificaci&oacute;n A-F
      de sostenibilidad ambiental que puedes obtener en paralelo al CFO,
      integr&aacute;ndola en tu documentaci&oacute;n de piscina.
    </p>
    <p>
      Obt&eacute;n gratis la clasificaci&oacute;n estimada de tu piscina en{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> con tu{" "}
      <strong>PoolLex ID</strong> &uacute;nico (formato PLX-XXXXXXX).
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> no sustituye ni emite el certificado de
      finalizaci&oacute;n de obra (eso lo hace la direcci&oacute;n facultativa
      con visado colegial), pero s&iacute; te ayuda a llegar mejor preparado
      al tr&aacute;mite: chequeo de cumplimiento preliminar en{" "}
      <Link to="/pool-check">/pool-check</Link> y Ficha T&eacute;cnica ambiental
      gratuita en <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

// ============================================================================
// ARTICLE 7 — Pool volume in liters: complete guide (EN only)
// Sprint 119.100 (2026-08-12) — GSC queries "pool volume calculator litres",
// "swimming pool volume calculator litres", "calculate pool volume in litres",
// "how many liters in a swimming pool". Single-language EN.
// ============================================================================

const Article7BodyEN = () => (
  <>
    <h2>Why pool volume in litres matters</h2>
    <p>
      Knowing your pool volume in litres is the single most useful number for
      day-to-day pool management. Every chemical dose on the label is written
      per <strong>1,000 or 10,000 litres</strong>. Every heat pump datasheet is
      sized for a water volume range. Every filtration cycle is calculated as
      a fraction of the pool volume moved per hour. Get this number wrong by
      20 % and you will either underdose (cloudy water, algae) or overdose
      (skin irritation, equipment corrosion, wasted product).
    </p>
    <p>
      This guide gives you the exact formulas, a quick-reference conversion
      table and the practical tips that keep you out of the two most common
      mistakes: <strong>using average depth on a sloped bottom</strong> and{" "}
      <strong>confusing radius with diameter</strong>.
    </p>

    {/* Sprint 158 — Bloc A · addendum SERP (14/09/2026). Les preguntes PAA de
        «pool volume calculator liters» demanen MIDES CONCRETES (16x32, 40x20,
        15x30), no fórmules. Xifres calculades amb 1 ft³ = 7,48052 gal US i
        1 gal US = 3,785412 L. Es mesura el 05/10: no tocar abans. */}
    <h2>Common Pool Sizes: Volume in Litres and Gallons</h2>
    <p><strong>Imperial sizes, 5 ft average depth</strong></p>
    <div className="overflow-x-auto">
      <table>
        <thead>
          <tr><th>Pool size</th><th>US gallons</th><th>Litres</th><th>m&sup3;</th></tr>
        </thead>
        <tbody>
          <tr><td>12 &times; 24 ft</td><td>10,772</td><td>40,776</td><td>40.8</td></tr>
          <tr><td>14 &times; 28 ft</td><td>14,662</td><td>55,501</td><td>55.5</td></tr>
          <tr><td>15 &times; 30 ft</td><td>16,831</td><td>63,713</td><td>63.7</td></tr>
          <tr><td>16 &times; 32 ft</td><td>19,150</td><td>72,491</td><td>72.5</td></tr>
          <tr><td>18 &times; 36 ft</td><td>24,237</td><td>91,747</td><td>91.7</td></tr>
          <tr><td>20 &times; 40 ft</td><td>29,922</td><td>113,267</td><td>113.3</td></tr>
        </tbody>
      </table>
    </div>
    <p><strong>Metric sizes, 1.5 m average depth</strong></p>
    <div className="overflow-x-auto">
      <table>
        <thead>
          <tr><th>Pool size</th><th>m&sup3;</th><th>Litres</th><th>US gallons</th></tr>
        </thead>
        <tbody>
          <tr><td>6 &times; 3 m</td><td>27.0</td><td>27,000</td><td>7,133</td></tr>
          <tr><td>7 &times; 3.5 m</td><td>36.8</td><td>36,750</td><td>9,708</td></tr>
          <tr><td>8 &times; 4 m</td><td>48.0</td><td>48,000</td><td>12,680</td></tr>
          <tr><td>9 &times; 4 m</td><td>54.0</td><td>54,000</td><td>14,265</td></tr>
          <tr><td>10 &times; 5 m</td><td>75.0</td><td>75,000</td><td>19,813</td></tr>
          <tr><td>12 &times; 6 m</td><td>108.0</td><td>108,000</td><td>28,531</td></tr>
        </tbody>
      </table>
    </div>
    <p>
      These figures assume a constant average depth. Many pools have a flat
      shallow section, a slope and a flat deep end, and when the shallow
      section is the longer one the real volume is lower than the simple
      average of the two depths suggests. An 8 &times; 4 m pool that is 1.0 m
      deep for half its length, slopes over the next quarter and is 2.0 m deep
      in the last quarter holds <strong>44,000 litres</strong>, not the 48,000
      litres the 1.5 m average gives: about 8 % less. Use our{" "}
      <Link to="/tools/pool-volume-calculator">pool volume calculator</Link>{" "}
      with your actual bottom profile for an accurate figure.
    </p>

    <h3>How many litres is a 16x32 pool?</h3>
    <p>
      A 16 &times; 32 ft pool with an average depth of 5 ft holds about{" "}
      <strong>72,500 litres (19,150 US gallons)</strong>. The exact figure
      depends on the bottom profile: if the shallow end is longer than the deep
      end, the real volume is lower than that average suggests.
    </p>

    <h3>How many gallons is a 40x20 pool?</h3>
    <p>
      A 40 &times; 20 ft pool holds about{" "}
      <strong>29,900 US gallons (113,300 litres)</strong> at an average depth
      of 5 ft. Each extra foot of average depth adds roughly 6,000 gallons.
    </p>

    <h3>How many litres is a 15x30 pool?</h3>
    <p>
      A 15 &times; 30 ft pool holds about{" "}
      <strong>63,700 litres (16,800 US gallons)</strong> at an average depth of
      5 ft. At a 4 ft average depth it is about 51,000 litres.
    </p>

    <h2>The conversion that never changes: m&sup3; to litres</h2>
    <p>
      One cubic metre of water always equals exactly one thousand litres:
    </p>
    <p>
      <strong>1 m&sup3; = 1,000 L</strong>
    </p>
    <p>
      Every formula below returns the volume in m&sup3;. To get litres,
      multiply by 1,000. To convert to US gallons multiply by 264.17; to UK
      gallons, multiply by 219.97.
    </p>

    <h2>Formula per pool shape</h2>

    <h3>Rectangular pool with flat bottom</h3>
    <p>
      <strong>V (m&sup3;) = length &times; width &times; depth</strong>
    </p>
    <p>Example: an 8 &times; 4 m pool with 1.5 m depth:</p>
    <p>V = 8 &times; 4 &times; 1.5 = 48 m&sup3; = 48,000 L</p>

    <h3>Rectangular pool with sloped bottom</h3>
    <p>
      Use the <strong>average depth</strong>, not the deep-end or shallow-end
      depth alone:
    </p>
    <p>
      <strong>V (m&sup3;) = length &times; width &times; (depth_shallow + depth_deep) / 2</strong>
    </p>
    <p>
      Example: an 8 &times; 4 m pool with 1.2 m in the shallow end and 1.8 m
      in the deep end:
    </p>
    <p>V = 8 &times; 4 &times; (1.2 + 1.8) / 2 = 8 &times; 4 &times; 1.5 = 48 m&sup3; = 48,000 L</p>
    <p>
      This works when the slope is linear (a single ramp). For pools with a
      hopper bottom (flat shallow, sudden drop, flat deep), split the pool
      into two rectangles and add the volumes.
    </p>

    <h3>Round pool</h3>
    <p>
      <strong>V (m&sup3;) = &pi; &times; r&sup2; &times; h</strong>
    </p>
    <p>
      Where r is the radius (half the diameter) and h is the water depth.
      Example: a 4.57 m diameter, 1.07 m water depth Intex frame pool:
    </p>
    <p>V = 3.1416 &times; 2.285&sup2; &times; 1.07 = 17.55 m&sup3; = 17,550 L</p>
    <p>
      If you prefer to work directly with diameter D:
    </p>
    <p>
      <strong>V (m&sup3;) &asymp; 0.7854 &times; D&sup2; &times; h</strong>
    </p>

    <h3>Oval pool</h3>
    <p>
      <strong>V (m&sup3;) = length &times; width &times; depth &times; 0.893</strong>
    </p>
    <p>
      Where length and width are the outer measurements of the oval and 0.893
      is a shape factor that accounts for the pool being an oval, not a
      rectangle. Example: a 6.10 &times; 3.66 m Bestway oval pool with 1.22 m
      water depth:
    </p>
    <p>V = 6.10 &times; 3.66 &times; 1.22 &times; 0.893 = 24.30 m&sup3; = 24,300 L</p>

    <h3>Kidney or free-form pool</h3>
    <p>
      Approximate as: <strong>V (m&sup3;) = length &times; width &times; avg_depth &times; 0.85</strong>
    </p>
    <p>
      The 0.85 factor absorbs the concavity of the shape. For precision, split
      the pool into two or three rectangles and add.
    </p>

    <h2>Quick reference table: common pool sizes</h2>
    <p>Volume in litres for the most frequent residential pool footprints, at 1.5 m average water depth:</p>
    <table>
      <thead>
        <tr>
          <th>Pool footprint (m)</th>
          <th>Shape</th>
          <th>Volume (m&sup3;)</th>
          <th>Volume (litres)</th>
          <th>Volume (US gal)</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>3 &times; 2</td><td>Rectangular</td><td>9.0</td><td>9,000</td><td>2,378</td></tr>
        <tr><td>4 &times; 3</td><td>Rectangular</td><td>18.0</td><td>18,000</td><td>4,755</td></tr>
        <tr><td>5 &times; 3</td><td>Rectangular</td><td>22.5</td><td>22,500</td><td>5,944</td></tr>
        <tr><td>6 &times; 3</td><td>Rectangular</td><td>27.0</td><td>27,000</td><td>7,133</td></tr>
        <tr><td>7 &times; 3.5</td><td>Rectangular</td><td>36.8</td><td>36,750</td><td>9,708</td></tr>
        <tr><td>8 &times; 4</td><td>Rectangular</td><td>48.0</td><td>48,000</td><td>12,680</td></tr>
        <tr><td>9 &times; 4</td><td>Rectangular</td><td>54.0</td><td>54,000</td><td>14,265</td></tr>
        <tr><td>10 &times; 5</td><td>Rectangular</td><td>75.0</td><td>75,000</td><td>19,813</td></tr>
        <tr><td>&Oslash; 3.66</td><td>Round (h=1.07)</td><td>11.3</td><td>11,260</td><td>2,975</td></tr>
        <tr><td>&Oslash; 4.57</td><td>Round (h=1.22)</td><td>20.0</td><td>20,010</td><td>5,286</td></tr>
        <tr><td>&Oslash; 5.49</td><td>Round (h=1.32)</td><td>31.3</td><td>31,260</td><td>8,258</td></tr>
      </tbody>
    </table>

    <h2>Common mistakes when calculating pool volume</h2>
    <ol>
      <li><strong>Using pool wall height instead of water depth</strong>. Pools are filled to 90 % of wall height for splash safety. Always measure the actual water level.</li>
      <li><strong>Confusing radius with diameter on round pools</strong>. The radius is half the diameter. Using the diameter as radius returns 4&times; the real volume.</li>
      <li><strong>Ignoring the sloped bottom</strong>. Using deep-end depth on a sloped pool overestimates volume by 20-30 %.</li>
      <li><strong>Rounding metric measurements to the wrong precision</strong>. A 5-cm error on a 5 m pool creates a 1 % volume error, which is acceptable; a 5-cm error on a 3 m pool creates a 1.7 % error.</li>
      <li><strong>Using the manufacturer's rated volume as gospel</strong>. Above-ground pool boxes state volume at 80 % or 90 % fill. Real usable water can differ by 10-15 %.</li>
    </ol>

    <h2>Application 1: chemical dosing</h2>
    <p>
      All pool chemicals are dosed per 1,000 or 10,000 litres of water. Quick
      reference for a typical 40,000 L pool (40 m&sup3;):
    </p>
    <ul>
      <li><strong>Shock chlorination</strong> (10 g/m&sup3; free chlorine): 400 g of granular tri-chlor or 1.2-1.5 L of 10 % liquid chlorine.</li>
      <li><strong>Salt initial charge</strong> for salt chlorinator (4 g/L target): 160 kg of pool salt.</li>
      <li><strong>pH decrease</strong> with muriatic acid (33 %): about 100 mL to drop pH by 0.1 units.</li>
      <li><strong>Algaecide</strong>: typical dose 100 mL per 40,000 L per week.</li>
    </ul>
    <p>
      A 20 % volume error on this pool means dosing 40 g of chlorine short or
      long &mdash; enough to leave water cloudy or cause skin irritation for
      swimmers.
    </p>

    <h2>Application 2: pump and filter sizing</h2>
    <p>
      Rule of thumb: your <strong>pump flow rate</strong> in m&sup3;/h should
      move the total pool volume once every <strong>4 to 6 hours</strong> of
      operation. For a 48 m&sup3; pool at 6 h/cycle:
    </p>
    <p>Required flow rate = 48 / 6 = 8 m&sup3;/h</p>
    <p>
      This determines the pump model and the filter size (a sand filter must
      handle the flow at &lt;50 m&sup3;/h per m&sup2; of filtering surface).
      Undersizing means longer daily filtration to compensate; oversizing
      wastes electricity and can turbulently move debris past the skimmer.
    </p>

    <h2>Application 3: heater and heat pump dimensioning</h2>
    <p>
      Raising 1 m&sup3; of pool water by 1 &deg;C takes about{" "}
      <strong>1.16 kWh</strong> of net heat. For a 40 m&sup3; pool with a 5 &deg;C
      temperature rise target (18 &rarr; 23 &deg;C):
    </p>
    <p>Net heat needed = 40 &times; 5 &times; 1.16 &asymp; 232 kWh</p>
    <p>
      A heat pump with 8 kW of thermal output would take 29 hours of continuous
      operation to reach the target, before accounting for losses through the
      water surface (typically 30-50 % additional in unheated exposure).
      Wrong volume, wrong heat pump.
    </p>

    <h2>Do the calculation online in 10 seconds</h2>
    <p>
      Save yourself the arithmetic. At{" "}
      <Link to="/tools/pool-volume-calculator">/tools/pool-volume-calculator</Link>{" "}
      you can enter your pool dimensions and shape and receive:
    </p>
    <ul>
      <li>Volume in m&sup3; and litres.</li>
      <li>Equivalent in US and UK gallons.</li>
      <li>Suggested chemical doses for shock chlorination and pH adjustment.</li>
      <li>Recommended pump flow range.</li>
    </ul>
    <p>
      If you want to go further and get an estimated <strong>EN 17645
      environmental class</strong> for your pool along with a unique{" "}
      <strong>PoolLex ID</strong>, generate your free Technical Sheet at{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> turns your pool volume and equipment data
      into a complete technical diagnostic: chemical dosing, running-cost
      estimate and A-F environmental class per EN 17645:2023. Start free at{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

const Article7BodyES = () => (
  <>
    <h2>Por qu&eacute; importa el volumen de la piscina en litros</h2>
    <p>
      El volumen en litros es el n&uacute;mero m&aacute;s &uacute;til del
      d&iacute;a a d&iacute;a de una piscina. Todas las dosis qu&iacute;micas
      de las etiquetas vienen expresadas por{" "}
      <strong>1.000 o 10.000 litros</strong>. Toda ficha t&eacute;cnica de
      bomba de calor se dimensiona para un rango de volumen. Todo ciclo de
      filtraci&oacute;n se calcula como una fracci&oacute;n del volumen movido
      por hora. Si te equivocas un 20 % en esta cifra, o infradosificas (agua
      turbia, algas) o sobredosificas (irritaci&oacute;n de piel,
      corrosi&oacute;n de equipos, producto desperdiciado).
    </p>
    <p>
      Esta gu&iacute;a te da las f&oacute;rmulas exactas, una tabla de
      conversi&oacute;n r&aacute;pida y los avisos que evitan los dos errores
      m&aacute;s frecuentes:{" "}
      <strong>usar la profundidad m&aacute;xima en un fondo inclinado</strong>{" "}
      y <strong>confundir radio con di&aacute;metro</strong>.
    </p>

    <h2>La conversi&oacute;n que nunca cambia: m&sup3; a litros</h2>
    <p>Un metro c&uacute;bico de agua equivale siempre a mil litros exactos:</p>
    <p><strong>1 m&sup3; = 1.000 L</strong></p>
    <p>
      Todas las f&oacute;rmulas de abajo devuelven el volumen en m&sup3;. Para
      obtener litros, multiplica por 1.000.
    </p>

    <h2>F&oacute;rmula seg&uacute;n la forma de la piscina</h2>

    <h3>Piscina rectangular con fondo plano</h3>
    <p><strong>V (m&sup3;) = largo &times; ancho &times; profundidad</strong></p>
    <p>Ejemplo: piscina de 8 &times; 4 m con 1,5 m de profundidad:</p>
    <p>V = 8 &times; 4 &times; 1,5 = 48 m&sup3; = 48.000 L</p>

    <h3>Piscina rectangular con fondo inclinado</h3>
    <p>
      Usa la <strong>profundidad media</strong>, nunca la de la zona profunda
      por s&iacute; sola:
    </p>
    <p>
      <strong>V (m&sup3;) = largo &times; ancho &times; (prof_m&iacute;nima + prof_m&aacute;xima) / 2</strong>
    </p>
    <p>Ejemplo: piscina de 8 &times; 4 m, de 1,2 m a 1,8 m:</p>
    <p>V = 8 &times; 4 &times; (1,2 + 1,8) / 2 = 8 &times; 4 &times; 1,5 = 48 m&sup3; = 48.000 L</p>
    <p>
      Esto vale cuando la pendiente es lineal (una sola rampa). Si la piscina
      tiene fondo en dos niveles con escal&oacute;n brusco, div&iacute;dela en
      dos rect&aacute;ngulos y suma los vol&uacute;menes.
    </p>

    <h3>Piscina redonda</h3>
    <p><strong>V (m&sup3;) = &pi; &times; r&sup2; &times; h</strong></p>
    <p>
      Donde r es el radio (la mitad del di&aacute;metro) y h la profundidad del
      agua. Ejemplo: piscina desmontable de 4,57 m de di&aacute;metro y 1,07 m
      de l&aacute;mina de agua:
    </p>
    <p>V = 3,1416 &times; 2,285&sup2; &times; 1,07 = 17,55 m&sup3; = 17.550 L</p>
    <p>Si prefieres trabajar directamente con el di&aacute;metro D:</p>
    <p><strong>V (m&sup3;) &asymp; 0,7854 &times; D&sup2; &times; h</strong></p>
    <p>
      Para un an&aacute;lisis m&aacute;s detallado de las piscinas circulares,
      con tabla por di&aacute;metros y casos de piscinas desmontables, consulta{" "}
      <Link to="/blog/calculadora-m3-piscina-redonda-formula">la gu&iacute;a
      espec&iacute;fica de piscinas redondas</Link>.
    </p>

    <h3>Piscina ovalada</h3>
    <p>
      <strong>V (m&sup3;) = largo &times; ancho &times; profundidad &times; 0,893</strong>
    </p>
    <p>
      Donde largo y ancho son las medidas exteriores del &oacute;valo y 0,893
      es el factor de forma que corrige el hecho de que no es un
      rect&aacute;ngulo. Ejemplo: piscina ovalada de 6,10 &times; 3,66 m con
      1,22 m de agua:
    </p>
    <p>V = 6,10 &times; 3,66 &times; 1,22 &times; 0,893 = 24,30 m&sup3; = 24.300 L</p>

    <h3>Piscina de forma libre o ri&ntilde;&oacute;n</h3>
    <p>
      Apr&oacute;ximala como:{" "}
      <strong>V (m&sup3;) = largo &times; ancho &times; prof_media &times; 0,85</strong>
    </p>
    <p>
      El factor 0,85 absorbe la concavidad de la forma. Para m&aacute;s
      precisi&oacute;n, divide la piscina en dos o tres rect&aacute;ngulos y
      suma.
    </p>

    <h2>Tabla de referencia r&aacute;pida: medidas habituales</h2>
    <p>
      Volumen en litros para las huellas residenciales m&aacute;s frecuentes,
      con 1,5 m de profundidad media:
    </p>
    <table>
      <thead>
        <tr>
          <th>Medidas (m)</th>
          <th>Forma</th>
          <th>Volumen (m&sup3;)</th>
          <th>Volumen (litros)</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>3 &times; 2</td><td>Rectangular</td><td>9,0</td><td>9.000</td></tr>
        <tr><td>4 &times; 3</td><td>Rectangular</td><td>18,0</td><td>18.000</td></tr>
        <tr><td>5 &times; 3</td><td>Rectangular</td><td>22,5</td><td>22.500</td></tr>
        <tr><td>6 &times; 3</td><td>Rectangular</td><td>27,0</td><td>27.000</td></tr>
        <tr><td>7 &times; 3,5</td><td>Rectangular</td><td>36,7</td><td>36.700</td></tr>
        <tr><td>8 &times; 4</td><td>Rectangular</td><td>48,0</td><td>48.000</td></tr>
        <tr><td>9 &times; 4</td><td>Rectangular</td><td>54,0</td><td>54.000</td></tr>
        <tr><td>10 &times; 5</td><td>Rectangular</td><td>75,0</td><td>75.000</td></tr>
        <tr><td>&Oslash; 3,66</td><td>Redonda (h=1,07)</td><td>11,3</td><td>11.260</td></tr>
        <tr><td>&Oslash; 4,57</td><td>Redonda (h=1,22)</td><td>20,0</td><td>20.010</td></tr>
        <tr><td>&Oslash; 5,49</td><td>Redonda (h=1,32)</td><td>31,3</td><td>31.260</td></tr>
      </tbody>
    </table>

    <h2>Errores frecuentes al calcular el volumen</h2>
    <ol>
      <li><strong>Usar la altura de la pared en vez de la l&aacute;mina de agua</strong>. Las piscinas se llenan al 90 % de la altura del vaso por seguridad de salpicaduras. Mide siempre el nivel real del agua.</li>
      <li><strong>Confundir radio con di&aacute;metro en piscinas redondas</strong>. El radio es la mitad del di&aacute;metro. Usar el di&aacute;metro como radio devuelve <strong>cuatro veces</strong> el volumen real.</li>
      <li><strong>Ignorar el fondo inclinado</strong>. Aplicar la profundidad m&aacute;xima en una piscina con pendiente sobreestima el volumen un 20-30 %.</li>
      <li><strong>Redondear las medidas con precisi&oacute;n insuficiente</strong>. Un error de 5 cm en una piscina de 5 m genera un 1 % de error de volumen (asumible); el mismo error en una de 3 m genera un 1,7 %.</li>
      <li><strong>Fiarse del volumen nominal del fabricante</strong>. Las cajas de piscinas desmontables indican el volumen al 80 % o 90 % de llenado. El agua real puede diferir un 10-15 %.</li>
    </ol>

    <h2>Aplicaci&oacute;n 1: dosificaci&oacute;n qu&iacute;mica</h2>
    <p>
      Todos los productos se dosifican por 1.000 o 10.000 litros. Referencia
      r&aacute;pida para una piscina t&iacute;pica de 40.000 L (40 m&sup3;):
    </p>
    <ul>
      <li><strong>Cloraci&oacute;n de choque</strong> (10 g/m&sup3; de cloro libre): 400 g de granulado o 1,2-1,5 L de hipoclorito al 10 %.</li>
      <li><strong>Carga inicial de sal</strong> para clorador salino (objetivo 4 g/L): 160 kg de sal.</li>
      <li><strong>Bajar el pH</strong> con salfum&aacute;n (33 %): unos 100 mL para bajar 0,1 unidades.</li>
      <li><strong>Alguicida</strong>: dosis habitual 100 mL por 40.000 L y semana.</li>
    </ul>
    <p>
      Un error del 20 % en el volumen de esta piscina significa dosificar 40 g
      de cloro de m&aacute;s o de menos: suficiente para dejar el agua turbia o
      para irritar la piel de los ba&ntilde;istas.
    </p>

    <h2>Aplicaci&oacute;n 2: dimensionar bomba y filtro</h2>
    <p>
      Regla pr&aacute;ctica: el <strong>caudal de la bomba</strong> en
      m&sup3;/h debe mover el volumen total de la piscina una vez cada{" "}
      <strong>4 a 6 horas</strong> de funcionamiento. Para 48 m&sup3; a 6 h por
      ciclo:
    </p>
    <p>Caudal necesario = 48 / 6 = 8 m&sup3;/h</p>
    <p>
      Esto determina el modelo de bomba y el tama&ntilde;o del filtro (un
      filtro de arena debe absorber el caudal a menos de 50 m&sup3;/h por
      m&sup2; de superficie filtrante). Quedarse corto obliga a filtrar
      m&aacute;s horas; pasarse malgasta electricidad y puede arrastrar
      suciedad m&aacute;s all&aacute; del skimmer.
    </p>

    <h2>Aplicaci&oacute;n 3: dimensionar la bomba de calor</h2>
    <p>
      Subir 1 m&sup3; de agua 1 &deg;C requiere alrededor de{" "}
      <strong>1,16 kWh</strong> de calor neto. Para una piscina de 40 m&sup3;
      con un salto objetivo de 5 &deg;C (18 &rarr; 23 &deg;C):
    </p>
    <p>Calor neto necesario = 40 &times; 5 &times; 1,16 &asymp; 232 kWh</p>
    <p>
      Una bomba de calor de 8 kW t&eacute;rmicos tardar&iacute;a 29 horas de
      funcionamiento continuo en alcanzar el objetivo, antes de contar las
      p&eacute;rdidas por la superficie del agua (t&iacute;picamente un 30-50 %
      adicional sin cubierta). Volumen equivocado, bomba de calor equivocada.
    </p>

    <h2>Haz el c&aacute;lculo online en 10 segundos</h2>
    <p>
      Ah&oacute;rrate la aritm&eacute;tica. En{" "}
      <Link to="/tools/pool-volume-calculator">la calculadora de volumen</Link>{" "}
      introduces dimensiones y forma y obtienes:
    </p>
    <ul>
      <li>Volumen en m&sup3; y litros.</li>
      <li>Equivalencia en galones.</li>
      <li>Dosis sugeridas de cloraci&oacute;n de choque y ajuste de pH.</li>
      <li>Rango de caudal de bomba recomendado.</li>
    </ul>
    <p>
      Si quieres ir m&aacute;s all&aacute; y obtener la{" "}
      <strong>clase ambiental EN 17645</strong> estimada de tu piscina junto a
      un <strong>PoolLex ID</strong> &uacute;nico, genera tu Ficha
      T&eacute;cnica gratuita en{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> convierte el volumen y los datos de
      equipamiento de tu piscina en un diagn&oacute;stico t&eacute;cnico
      completo: dosificaci&oacute;n qu&iacute;mica, estimaci&oacute;n de coste
      operativo y clase ambiental A-F seg&uacute;n UNE-EN 17645:2023. Empieza
      gratis en <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

const Article7BodyFR = () => (
  <>
    <h2>Pourquoi le volume de la piscine en litres est d&eacute;terminant</h2>
    <p>
      Le volume en litres est le chiffre le plus utile au quotidien. Toutes les
      doses chimiques figurant sur les &eacute;tiquettes sont exprim&eacute;es
      par <strong>1 000 ou 10 000 litres</strong>. Toute fiche technique de
      pompe &agrave; chaleur est dimensionn&eacute;e pour une plage de volume.
      Tout cycle de filtration se calcule comme une fraction du volume
      d&eacute;plac&eacute; par heure. Une erreur de 20 % sur ce chiffre et
      vous sous-dosez (eau trouble, algues) ou surdosez (irritation cutan&eacute;e,
      corrosion des &eacute;quipements, produit gaspill&eacute;).
    </p>
    <p>
      Ce guide vous donne les formules exactes, un tableau de conversion rapide
      et les mises en garde qui &eacute;vitent les deux erreurs les plus
      fr&eacute;quentes :{" "}
      <strong>utiliser la profondeur maximale sur un fond inclin&eacute;</strong>{" "}
      et <strong>confondre rayon et diam&egrave;tre</strong>.
    </p>

    <h2>La conversion qui ne change jamais : m&sup3; en litres</h2>
    <p>Un m&egrave;tre cube d&rsquo;eau vaut toujours mille litres exactement :</p>
    <p><strong>1 m&sup3; = 1 000 L</strong></p>
    <p>
      Toutes les formules ci-dessous renvoient le volume en m&sup3;. Pour
      obtenir des litres, multipliez par 1 000.
    </p>

    <h2>Formule selon la forme du bassin</h2>

    <h3>Piscine rectangulaire &agrave; fond plat</h3>
    <p><strong>V (m&sup3;) = longueur &times; largeur &times; profondeur</strong></p>
    <p>Exemple : piscine de 8 &times; 4 m avec 1,5 m de profondeur :</p>
    <p>V = 8 &times; 4 &times; 1,5 = 48 m&sup3; = 48 000 L</p>

    <h3>Piscine rectangulaire &agrave; fond inclin&eacute;</h3>
    <p>
      Utilisez la <strong>profondeur moyenne</strong>, jamais celle du grand
      bain seule :
    </p>
    <p>
      <strong>V (m&sup3;) = longueur &times; largeur &times; (prof_mini + prof_maxi) / 2</strong>
    </p>
    <p>Exemple : piscine de 8 &times; 4 m, de 1,2 m &agrave; 1,8 m :</p>
    <p>V = 8 &times; 4 &times; (1,2 + 1,8) / 2 = 8 &times; 4 &times; 1,5 = 48 m&sup3; = 48 000 L</p>
    <p>
      Cela vaut lorsque la pente est lin&eacute;aire (une seule rampe). Si le
      bassin comporte une fosse &agrave; plongeon avec rupture brusque,
      d&eacute;coupez-le en deux rectangles et additionnez.
    </p>

    <h3>Piscine ronde</h3>
    <p><strong>V (m&sup3;) = &pi; &times; r&sup2; &times; h</strong></p>
    <p>
      O&ugrave; r est le rayon (la moiti&eacute; du diam&egrave;tre) et h la
      hauteur d&rsquo;eau. Exemple : piscine hors-sol de 4,57 m de
      diam&egrave;tre et 1,07 m d&rsquo;eau :
    </p>
    <p>V = 3,1416 &times; 2,285&sup2; &times; 1,07 = 17,55 m&sup3; = 17 550 L</p>
    <p>Si vous pr&eacute;f&eacute;rez travailler avec le diam&egrave;tre D :</p>
    <p><strong>V (m&sup3;) &asymp; 0,7854 &times; D&sup2; &times; h</strong></p>

    <h3>Piscine ovale</h3>
    <p>
      <strong>V (m&sup3;) = longueur &times; largeur &times; profondeur &times; 0,893</strong>
    </p>
    <p>
      O&ugrave; longueur et largeur sont les mesures ext&eacute;rieures de
      l&rsquo;ovale et 0,893 le facteur de forme qui corrige le fait que ce
      n&rsquo;est pas un rectangle. Exemple : piscine ovale de 6,10 &times;
      3,66 m avec 1,22 m d&rsquo;eau :
    </p>
    <p>V = 6,10 &times; 3,66 &times; 1,22 &times; 0,893 = 24,30 m&sup3; = 24 300 L</p>

    <h3>Piscine forme libre ou haricot</h3>
    <p>
      Approchez par :{" "}
      <strong>V (m&sup3;) = longueur &times; largeur &times; prof_moyenne &times; 0,85</strong>
    </p>
    <p>
      Le facteur 0,85 absorbe la concavit&eacute; de la forme. Pour plus de
      pr&eacute;cision, d&eacute;coupez le bassin en deux ou trois rectangles.
    </p>

    <h2>Tableau de r&eacute;f&eacute;rence : dimensions courantes</h2>
    <p>
      Volume en litres pour les emprises r&eacute;sidentielles les plus
      fr&eacute;quentes, &agrave; 1,5 m de profondeur moyenne :
    </p>
    <table>
      <thead>
        <tr>
          <th>Dimensions (m)</th>
          <th>Forme</th>
          <th>Volume (m&sup3;)</th>
          <th>Volume (litres)</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>3 &times; 2</td><td>Rectangulaire</td><td>9,0</td><td>9 000</td></tr>
        <tr><td>4 &times; 3</td><td>Rectangulaire</td><td>18,0</td><td>18 000</td></tr>
        <tr><td>5 &times; 3</td><td>Rectangulaire</td><td>22,5</td><td>22 500</td></tr>
        <tr><td>6 &times; 3</td><td>Rectangulaire</td><td>27,0</td><td>27 000</td></tr>
        <tr><td>7 &times; 3,5</td><td>Rectangulaire</td><td>36,7</td><td>36 700</td></tr>
        <tr><td>8 &times; 4</td><td>Rectangulaire</td><td>48,0</td><td>48 000</td></tr>
        <tr><td>9 &times; 4</td><td>Rectangulaire</td><td>54,0</td><td>54 000</td></tr>
        <tr><td>10 &times; 5</td><td>Rectangulaire</td><td>75,0</td><td>75 000</td></tr>
        <tr><td>&Oslash; 3,66</td><td>Ronde (h=1,07)</td><td>11,3</td><td>11 260</td></tr>
        <tr><td>&Oslash; 4,57</td><td>Ronde (h=1,22)</td><td>20,0</td><td>20 010</td></tr>
        <tr><td>&Oslash; 5,49</td><td>Ronde (h=1,32)</td><td>31,3</td><td>31 260</td></tr>
      </tbody>
    </table>

    <h2>Erreurs fr&eacute;quentes de calcul</h2>
    <ol>
      <li><strong>Utiliser la hauteur de paroi au lieu de la hauteur d&rsquo;eau</strong>. Les bassins sont remplis &agrave; 90 % de la hauteur pour &eacute;viter les d&eacute;bordements. Mesurez toujours le niveau r&eacute;el.</li>
      <li><strong>Confondre rayon et diam&egrave;tre sur les bassins ronds</strong>. Le rayon vaut la moiti&eacute; du diam&egrave;tre. Utiliser le diam&egrave;tre comme rayon donne <strong>quatre fois</strong> le volume r&eacute;el.</li>
      <li><strong>Ignorer le fond inclin&eacute;</strong>. Appliquer la profondeur maximale surestime le volume de 20-30 %.</li>
      <li><strong>Arrondir les mesures &agrave; une pr&eacute;cision insuffisante</strong>. Une erreur de 5 cm sur un bassin de 5 m cr&eacute;e 1 % d&rsquo;&eacute;cart (acceptable) ; la m&ecirc;me erreur sur 3 m en cr&eacute;e 1,7 %.</li>
      <li><strong>Se fier au volume nominal du fabricant</strong>. Les emballages hors-sol indiquent le volume &agrave; 80 % ou 90 % de remplissage. L&rsquo;eau r&eacute;elle peut varier de 10-15 %.</li>
    </ol>

    <h2>Application 1 : dosage chimique</h2>
    <p>
      Tous les produits se dosent par 1 000 ou 10 000 litres.
      R&eacute;f&eacute;rence rapide pour une piscine de 40 000 L (40 m&sup3;) :
    </p>
    <ul>
      <li><strong>Chloration choc</strong> (10 g/m&sup3; de chlore libre) : 400 g de granul&eacute; ou 1,2-1,5 L de chlore liquide &agrave; 10 %.</li>
      <li><strong>Charge initiale de sel</strong> pour &eacute;lectrolyseur (cible 4 g/L) : 160 kg de sel.</li>
      <li><strong>Baisser le pH</strong> avec acide chlorhydrique (33 %) : environ 100 mL pour descendre de 0,1 unit&eacute;.</li>
      <li><strong>Anti-algues</strong> : dose habituelle 100 mL pour 40 000 L par semaine.</li>
    </ul>
    <p>
      Une erreur de 20 % sur ce bassin revient &agrave; doser 40 g de chlore en
      trop ou en moins : de quoi laisser l&rsquo;eau trouble ou irriter la peau
      des baigneurs.
    </p>

    <h2>Application 2 : dimensionner pompe et filtre</h2>
    <p>
      R&egrave;gle pratique : le <strong>d&eacute;bit de la pompe</strong> en
      m&sup3;/h doit brasser le volume total une fois toutes les{" "}
      <strong>4 &agrave; 6 heures</strong>. Pour 48 m&sup3; sur un cycle de 6 h :
    </p>
    <p>D&eacute;bit n&eacute;cessaire = 48 / 6 = 8 m&sup3;/h</p>
    <p>
      Cela d&eacute;termine le mod&egrave;le de pompe et la taille du filtre
      (un filtre &agrave; sable doit encaisser le d&eacute;bit &agrave; moins de
      50 m&sup3;/h par m&sup2; de surface filtrante). Sous-dimensionner oblige
      &agrave; filtrer plus longtemps ; surdimensionner gaspille de
      l&rsquo;&eacute;lectricit&eacute;.
    </p>

    <h2>Application 3 : dimensionner la pompe &agrave; chaleur</h2>
    <p>
      &Eacute;lever 1 m&sup3; d&rsquo;eau de 1 &deg;C demande environ{" "}
      <strong>1,16 kWh</strong> de chaleur nette. Pour 40 m&sup3; avec un
      &eacute;cart cible de 5 &deg;C (18 &rarr; 23 &deg;C) :
    </p>
    <p>Chaleur nette = 40 &times; 5 &times; 1,16 &asymp; 232 kWh</p>
    <p>
      Une pompe &agrave; chaleur de 8 kW thermiques mettrait 29 heures de
      fonctionnement continu pour atteindre la cible, avant de compter les
      pertes en surface (typiquement 30-50 % de plus sans couverture). Mauvais
      volume, mauvaise pompe.
    </p>

    <h2>Faites le calcul en ligne en 10 secondes</h2>
    <p>
      &Eacute;pargnez-vous l&rsquo;arithm&eacute;tique. Sur{" "}
      <Link to="/tools/pool-volume-calculator">le calculateur de volume</Link>{" "}
      vous saisissez dimensions et forme, et vous obtenez :
    </p>
    <ul>
      <li>Volume en m&sup3; et en litres.</li>
      <li>&Eacute;quivalence en gallons.</li>
      <li>Doses sugg&eacute;r&eacute;es de chloration choc et d&rsquo;ajustement du pH.</li>
      <li>Plage de d&eacute;bit de pompe recommand&eacute;e.</li>
    </ul>
    <p>
      Pour aller plus loin et obtenir la{" "}
      <strong>classe environnementale NF EN 17645</strong> estim&eacute;e de
      votre piscine ainsi qu&rsquo;un <strong>PoolLex ID</strong> unique,
      g&eacute;n&eacute;rez votre Fiche Technique gratuite sur{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> transforme le volume et les
      donn&eacute;es d&rsquo;&eacute;quipement de votre piscine en un
      diagnostic technique complet : dosage chimique, estimation du co&ucirc;t
      d&rsquo;exploitation et classe environnementale A-F selon NF EN
      17645:2023. Commencez gratuitement sur{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

const Article7BodyIT = () => (
  <>
    <h2>Perch&eacute; il volume della piscina in litri &egrave; decisivo</h2>
    <p>
      Il volume in litri &egrave; il numero pi&ugrave; utile nella gestione
      quotidiana. Tutte le dosi chimiche in etichetta sono espresse per{" "}
      <strong>1.000 o 10.000 litri</strong>. Ogni scheda tecnica di pompa di
      calore &egrave; dimensionata per un intervallo di volume. Ogni ciclo di
      filtrazione si calcola come frazione del volume movimentato per ora. Un
      errore del 20 % su questa cifra porta a sottodosare (acqua torbida,
      alghe) o sovradosare (irritazione cutanea, corrosione degli impianti,
      prodotto sprecato).
    </p>
    <p>
      Questa guida ti d&agrave; le formule esatte, una tabella di conversione
      rapida e gli avvisi che evitano i due errori pi&ugrave; comuni:{" "}
      <strong>usare la profondit&agrave; massima su un fondo inclinato</strong>{" "}
      e <strong>confondere raggio e diametro</strong>.
    </p>

    <h2>La conversione che non cambia mai: m&sup3; in litri</h2>
    <p>Un metro cubo d&rsquo;acqua equivale sempre a mille litri esatti:</p>
    <p><strong>1 m&sup3; = 1.000 L</strong></p>
    <p>
      Tutte le formule seguenti restituiscono il volume in m&sup3;. Per ottenere
      i litri, moltiplica per 1.000.
    </p>

    <h2>Formula per forma della vasca</h2>

    <h3>Piscina rettangolare a fondo piatto</h3>
    <p><strong>V (m&sup3;) = lunghezza &times; larghezza &times; profondit&agrave;</strong></p>
    <p>Esempio: piscina 8 &times; 4 m con 1,5 m di profondit&agrave;:</p>
    <p>V = 8 &times; 4 &times; 1,5 = 48 m&sup3; = 48.000 L</p>

    <h3>Piscina rettangolare a fondo inclinato</h3>
    <p>
      Usa la <strong>profondit&agrave; media</strong>, mai quella della zona
      profonda da sola:
    </p>
    <p>
      <strong>V (m&sup3;) = lunghezza &times; larghezza &times; (prof_min + prof_max) / 2</strong>
    </p>
    <p>Esempio: piscina 8 &times; 4 m, da 1,2 m a 1,8 m:</p>
    <p>V = 8 &times; 4 &times; (1,2 + 1,8) / 2 = 8 &times; 4 &times; 1,5 = 48 m&sup3; = 48.000 L</p>
    <p>
      Vale quando la pendenza &egrave; lineare (una sola rampa). Se la vasca ha
      un fondo a due livelli con gradino netto, dividila in due rettangoli e
      somma i volumi.
    </p>

    <h3>Piscina rotonda</h3>
    <p><strong>V (m&sup3;) = &pi; &times; r&sup2; &times; h</strong></p>
    <p>
      Dove r &egrave; il raggio (met&agrave; del diametro) e h
      l&rsquo;altezza dell&rsquo;acqua. Esempio: piscina fuori terra di 4,57 m
      di diametro e 1,07 m d&rsquo;acqua:
    </p>
    <p>V = 3,1416 &times; 2,285&sup2; &times; 1,07 = 17,55 m&sup3; = 17.550 L</p>
    <p>Se preferisci lavorare direttamente con il diametro D:</p>
    <p><strong>V (m&sup3;) &asymp; 0,7854 &times; D&sup2; &times; h</strong></p>

    <h3>Piscina ovale</h3>
    <p>
      <strong>V (m&sup3;) = lunghezza &times; larghezza &times; profondit&agrave; &times; 0,893</strong>
    </p>
    <p>
      Dove lunghezza e larghezza sono le misure esterne dell&rsquo;ovale e
      0,893 &egrave; il fattore di forma che corregge il fatto che non &egrave;
      un rettangolo. Esempio: piscina ovale di 6,10 &times; 3,66 m con 1,22 m
      d&rsquo;acqua:
    </p>
    <p>V = 6,10 &times; 3,66 &times; 1,22 &times; 0,893 = 24,30 m&sup3; = 24.300 L</p>

    <h3>Piscina a forma libera o a fagiolo</h3>
    <p>
      Approssima con:{" "}
      <strong>V (m&sup3;) = lunghezza &times; larghezza &times; prof_media &times; 0,85</strong>
    </p>
    <p>
      Il fattore 0,85 assorbe la concavit&agrave; della forma. Per maggiore
      precisione, dividi la vasca in due o tre rettangoli e somma.
    </p>

    <h2>Tabella di riferimento: misure abituali</h2>
    <p>
      Volume in litri per gli ingombri residenziali pi&ugrave; frequenti, con
      1,5 m di profondit&agrave; media:
    </p>
    <table>
      <thead>
        <tr>
          <th>Misure (m)</th>
          <th>Forma</th>
          <th>Volume (m&sup3;)</th>
          <th>Volume (litri)</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>3 &times; 2</td><td>Rettangolare</td><td>9,0</td><td>9.000</td></tr>
        <tr><td>4 &times; 3</td><td>Rettangolare</td><td>18,0</td><td>18.000</td></tr>
        <tr><td>5 &times; 3</td><td>Rettangolare</td><td>22,5</td><td>22.500</td></tr>
        <tr><td>6 &times; 3</td><td>Rettangolare</td><td>27,0</td><td>27.000</td></tr>
        <tr><td>7 &times; 3,5</td><td>Rettangolare</td><td>36,7</td><td>36.700</td></tr>
        <tr><td>8 &times; 4</td><td>Rettangolare</td><td>48,0</td><td>48.000</td></tr>
        <tr><td>9 &times; 4</td><td>Rettangolare</td><td>54,0</td><td>54.000</td></tr>
        <tr><td>10 &times; 5</td><td>Rettangolare</td><td>75,0</td><td>75.000</td></tr>
        <tr><td>&Oslash; 3,66</td><td>Rotonda (h=1,07)</td><td>11,3</td><td>11.260</td></tr>
        <tr><td>&Oslash; 4,57</td><td>Rotonda (h=1,22)</td><td>20,0</td><td>20.010</td></tr>
        <tr><td>&Oslash; 5,49</td><td>Rotonda (h=1,32)</td><td>31,3</td><td>31.260</td></tr>
      </tbody>
    </table>

    <h2>Errori frequenti nel calcolo</h2>
    <ol>
      <li><strong>Usare l&rsquo;altezza della parete invece del livello d&rsquo;acqua</strong>. Le vasche si riempiono al 90 % dell&rsquo;altezza per sicurezza. Misura sempre il livello reale.</li>
      <li><strong>Confondere raggio e diametro nelle piscine rotonde</strong>. Il raggio &egrave; met&agrave; del diametro. Usare il diametro come raggio restituisce <strong>quattro volte</strong> il volume reale.</li>
      <li><strong>Ignorare il fondo inclinato</strong>. Applicare la profondit&agrave; massima sovrastima il volume del 20-30 %.</li>
      <li><strong>Arrotondare le misure con precisione insufficiente</strong>. Un errore di 5 cm su una vasca di 5 m genera l&rsquo;1 % di scarto (accettabile); lo stesso errore su 3 m genera l&rsquo;1,7 %.</li>
      <li><strong>Fidarsi del volume nominale del produttore</strong>. Le confezioni fuori terra indicano il volume all&rsquo;80 % o 90 % di riempimento. L&rsquo;acqua reale pu&ograve; variare del 10-15 %.</li>
    </ol>

    <h2>Applicazione 1: dosaggio chimico</h2>
    <p>
      Tutti i prodotti si dosano per 1.000 o 10.000 litri. Riferimento rapido
      per una piscina da 40.000 L (40 m&sup3;):
    </p>
    <ul>
      <li><strong>Clorazione shock</strong> (10 g/m&sup3; di cloro libero): 400 g di granulare o 1,2-1,5 L di cloro liquido al 10 %.</li>
      <li><strong>Carica iniziale di sale</strong> per elettrolizzatore (target 4 g/L): 160 kg di sale.</li>
      <li><strong>Abbassare il pH</strong> con acido cloridrico (33 %): circa 100 mL per scendere di 0,1 unit&agrave;.</li>
      <li><strong>Alghicida</strong>: dose abituale 100 mL per 40.000 L a settimana.</li>
    </ul>
    <p>
      Un errore del 20 % su questa vasca significa dosare 40 g di cloro in
      pi&ugrave; o in meno: abbastanza per lasciare l&rsquo;acqua torbida o
      irritare la pelle dei bagnanti.
    </p>

    <h2>Applicazione 2: dimensionare pompa e filtro</h2>
    <p>
      Regola pratica: la <strong>portata della pompa</strong> in m&sup3;/h deve
      movimentare il volume totale una volta ogni{" "}
      <strong>4-6 ore</strong> di funzionamento. Per 48 m&sup3; su ciclo di 6 h:
    </p>
    <p>Portata necessaria = 48 / 6 = 8 m&sup3;/h</p>
    <p>
      Questo determina il modello di pompa e la dimensione del filtro (un filtro
      a sabbia deve reggere la portata a meno di 50 m&sup3;/h per m&sup2; di
      superficie filtrante). Sottodimensionare obbliga a filtrare pi&ugrave;
      ore; sovradimensionare spreca elettricit&agrave;.
    </p>

    <h2>Applicazione 3: dimensionare la pompa di calore</h2>
    <p>
      Alzare 1 m&sup3; d&rsquo;acqua di 1 &deg;C richiede circa{" "}
      <strong>1,16 kWh</strong> di calore netto. Per 40 m&sup3; con un salto
      obiettivo di 5 &deg;C (18 &rarr; 23 &deg;C):
    </p>
    <p>Calore netto = 40 &times; 5 &times; 1,16 &asymp; 232 kWh</p>
    <p>
      Una pompa di calore da 8 kW termici impiegherebbe 29 ore di funzionamento
      continuo per raggiungere l&rsquo;obiettivo, prima di considerare le
      perdite dalla superficie (tipicamente 30-50 % in pi&ugrave; senza
      copertura). Volume sbagliato, pompa sbagliata.
    </p>

    <h2>Fai il calcolo online in 10 secondi</h2>
    <p>
      Risparmiati l&rsquo;aritmetica. Su{" "}
      <Link to="/tools/pool-volume-calculator">il calcolatore di volume</Link>{" "}
      inserisci dimensioni e forma e ottieni:
    </p>
    <ul>
      <li>Volume in m&sup3; e litri.</li>
      <li>Equivalenza in galloni.</li>
      <li>Dosi suggerite di clorazione shock e regolazione del pH.</li>
      <li>Intervallo di portata pompa consigliato.</li>
    </ul>
    <p>
      Per andare oltre e ottenere la{" "}
      <strong>classe ambientale UNI EN 17645</strong> stimata della tua piscina
      insieme a un <strong>PoolLex ID</strong> univoco, genera la tua Scheda
      Tecnica gratuita su <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> trasforma il volume e i dati di
      equipaggiamento della tua piscina in una diagnosi tecnica completa:
      dosaggio chimico, stima del costo di esercizio e classe ambientale A-F
      secondo UNI EN 17645:2023. Inizia gratis su{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

const Article7BodyNL = () => (
  <>
    <h2>Waarom het zwembadvolume in liters bepalend is</h2>
    <p>
      Het volume in liters is het nuttigste getal in het dagelijks beheer. Alle
      chemische doseringen op etiketten staan per{" "}
      <strong>1.000 of 10.000 liter</strong>. Elk warmtepompdatablad is
      gedimensioneerd voor een volumebereik. Elke filtratiecyclus wordt berekend
      als een fractie van het per uur verplaatste volume. Zit je er 20 % naast,
      dan doseer je te weinig (troebel water, algen) of te veel
      (huidirritatie, corrosie van apparatuur, verspild product).
    </p>
    <p>
      Deze gids geeft je de exacte formules, een snelle omrekentabel en de
      waarschuwingen die de twee meest voorkomende fouten voorkomen:{" "}
      <strong>de maximale diepte gebruiken bij een hellende bodem</strong> en{" "}
      <strong>straal en diameter verwarren</strong>.
    </p>

    <h2>De omrekening die nooit verandert: m&sup3; naar liters</h2>
    <p>Een kubieke meter water is altijd precies duizend liter:</p>
    <p><strong>1 m&sup3; = 1.000 L</strong></p>
    <p>
      Alle onderstaande formules geven het volume in m&sup3;. Vermenigvuldig met
      1.000 voor liters.
    </p>

    <h2>Formule per zwembadvorm</h2>

    <h3>Rechthoekig bad met vlakke bodem</h3>
    <p><strong>V (m&sup3;) = lengte &times; breedte &times; diepte</strong></p>
    <p>Voorbeeld: bad van 8 &times; 4 m met 1,5 m diepte:</p>
    <p>V = 8 &times; 4 &times; 1,5 = 48 m&sup3; = 48.000 L</p>

    <h3>Rechthoekig bad met hellende bodem</h3>
    <p>
      Gebruik de <strong>gemiddelde diepte</strong>, nooit alleen die van het
      diepe deel:
    </p>
    <p>
      <strong>V (m&sup3;) = lengte &times; breedte &times; (diepte_min + diepte_max) / 2</strong>
    </p>
    <p>Voorbeeld: bad van 8 &times; 4 m, van 1,2 m tot 1,8 m:</p>
    <p>V = 8 &times; 4 &times; (1,2 + 1,8) / 2 = 8 &times; 4 &times; 1,5 = 48 m&sup3; = 48.000 L</p>
    <p>
      Dit geldt bij een lineaire helling (&eacute;&eacute;n schuine vlak). Heeft
      het bad een bodem in twee niveaus met een scherpe trap, deel het dan op in
      twee rechthoeken en tel de volumes op.
    </p>

    <h3>Rond bad</h3>
    <p><strong>V (m&sup3;) = &pi; &times; r&sup2; &times; h</strong></p>
    <p>
      Waarbij r de straal is (de helft van de diameter) en h de waterhoogte.
      Voorbeeld: opzetzwembad van 4,57 m diameter en 1,07 m water:
    </p>
    <p>V = 3,1416 &times; 2,285&sup2; &times; 1,07 = 17,55 m&sup3; = 17.550 L</p>
    <p>Werk je liever direct met de diameter D:</p>
    <p><strong>V (m&sup3;) &asymp; 0,7854 &times; D&sup2; &times; h</strong></p>

    <h3>Ovaal bad</h3>
    <p>
      <strong>V (m&sup3;) = lengte &times; breedte &times; diepte &times; 0,893</strong>
    </p>
    <p>
      Waarbij lengte en breedte de buitenmaten van de ovaal zijn en 0,893 de
      vormfactor die corrigeert dat het geen rechthoek is. Voorbeeld: ovaal bad
      van 6,10 &times; 3,66 m met 1,22 m water:
    </p>
    <p>V = 6,10 &times; 3,66 &times; 1,22 &times; 0,893 = 24,30 m&sup3; = 24.300 L</p>

    <h3>Vrije vorm of niervorm</h3>
    <p>
      Benader met:{" "}
      <strong>V (m&sup3;) = lengte &times; breedte &times; gem_diepte &times; 0,85</strong>
    </p>
    <p>
      De factor 0,85 vangt de holling van de vorm op. Voor meer precisie deel je
      het bad op in twee of drie rechthoeken en telt de volumes op.
    </p>

    <h2>Naslagtabel: gangbare maten</h2>
    <p>
      Volume in liters voor de meest voorkomende particuliere afmetingen, bij
      1,5 m gemiddelde diepte:
    </p>
    <table>
      <thead>
        <tr>
          <th>Maten (m)</th>
          <th>Vorm</th>
          <th>Volume (m&sup3;)</th>
          <th>Volume (liters)</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>3 &times; 2</td><td>Rechthoekig</td><td>9,0</td><td>9.000</td></tr>
        <tr><td>4 &times; 3</td><td>Rechthoekig</td><td>18,0</td><td>18.000</td></tr>
        <tr><td>5 &times; 3</td><td>Rechthoekig</td><td>22,5</td><td>22.500</td></tr>
        <tr><td>6 &times; 3</td><td>Rechthoekig</td><td>27,0</td><td>27.000</td></tr>
        <tr><td>7 &times; 3,5</td><td>Rechthoekig</td><td>36,7</td><td>36.700</td></tr>
        <tr><td>8 &times; 4</td><td>Rechthoekig</td><td>48,0</td><td>48.000</td></tr>
        <tr><td>9 &times; 4</td><td>Rechthoekig</td><td>54,0</td><td>54.000</td></tr>
        <tr><td>10 &times; 5</td><td>Rechthoekig</td><td>75,0</td><td>75.000</td></tr>
        <tr><td>&Oslash; 3,66</td><td>Rond (h=1,07)</td><td>11,3</td><td>11.260</td></tr>
        <tr><td>&Oslash; 4,57</td><td>Rond (h=1,22)</td><td>20,0</td><td>20.010</td></tr>
        <tr><td>&Oslash; 5,49</td><td>Rond (h=1,32)</td><td>31,3</td><td>31.260</td></tr>
      </tbody>
    </table>

    <h2>Veelgemaakte rekenfouten</h2>
    <ol>
      <li><strong>De wandhoogte gebruiken in plaats van de waterhoogte</strong>. Baden worden tot 90 % van de wandhoogte gevuld tegen overslaan. Meet altijd het werkelijke waterniveau.</li>
      <li><strong>Straal en diameter verwarren bij ronde baden</strong>. De straal is de helft van de diameter. De diameter als straal gebruiken geeft <strong>vier keer</strong> het echte volume.</li>
      <li><strong>De hellende bodem negeren</strong>. De maximale diepte toepassen overschat het volume met 20-30 %.</li>
      <li><strong>Maten te grof afronden</strong>. Een fout van 5 cm op een bad van 5 m geeft 1 % afwijking (acceptabel); dezelfde fout op 3 m geeft 1,7 %.</li>
      <li><strong>Blind vertrouwen op het nominale volume van de fabrikant</strong>. Verpakkingen van opzetbaden vermelden het volume bij 80 % of 90 % vulling. Het echte water kan 10-15 % afwijken.</li>
    </ol>

    <h2>Toepassing 1: chemische dosering</h2>
    <p>
      Alle producten worden gedoseerd per 1.000 of 10.000 liter. Snelle
      referentie voor een bad van 40.000 L (40 m&sup3;):
    </p>
    <ul>
      <li><strong>Shockchlorering</strong> (10 g/m&sup3; vrij chloor): 400 g granulaat of 1,2-1,5 L vloeibaar chloor 10 %.</li>
      <li><strong>Startdosering zout</strong> voor zoutelektrolyse (doel 4 g/L): 160 kg zwembadzout.</li>
      <li><strong>pH verlagen</strong> met zoutzuur (33 %): ongeveer 100 mL om 0,1 eenheid te zakken.</li>
      <li><strong>Algenbestrijder</strong>: gangbare dosis 100 mL per 40.000 L per week.</li>
    </ul>
    <p>
      Een fout van 20 % op dit bad betekent 40 g chloor te veel of te weinig
      doseren: genoeg om het water troebel te laten of de huid van zwemmers te
      irriteren.
    </p>

    <h2>Toepassing 2: pomp en filter dimensioneren</h2>
    <p>
      Vuistregel: het <strong>pompdebiet</strong> in m&sup3;/h moet het totale
      volume eens per <strong>4 tot 6 uur</strong> rondpompen. Voor 48 m&sup3;
      bij een cyclus van 6 h:
    </p>
    <p>Benodigd debiet = 48 / 6 = 8 m&sup3;/h</p>
    <p>
      Dit bepaalt het pompmodel en de filtergrootte (een zandfilter moet het
      debiet aankunnen bij minder dan 50 m&sup3;/h per m&sup2;
      filteroppervlak). Te klein dimensioneren dwingt tot langer filteren; te
      groot verspilt stroom.
    </p>

    <h2>Toepassing 3: warmtepomp dimensioneren</h2>
    <p>
      1 m&sup3; water 1 &deg;C opwarmen vraagt ongeveer{" "}
      <strong>1,16 kWh</strong> netto warmte. Voor 40 m&sup3; met een
      doelsprong van 5 &deg;C (18 &rarr; 23 &deg;C):
    </p>
    <p>Netto warmte = 40 &times; 5 &times; 1,16 &asymp; 232 kWh</p>
    <p>
      Een warmtepomp van 8 kW thermisch zou 29 uur continu draaien om het doel
      te halen, nog v&oacute;&oacute;r verliezen aan het wateroppervlak
      (doorgaans 30-50 % extra zonder afdekking). Verkeerd volume, verkeerde
      warmtepomp.
    </p>

    <h2>Reken het online uit in 10 seconden</h2>
    <p>
      Bespaar je het rekenwerk. Op{" "}
      <Link to="/tools/pool-volume-calculator">de volumecalculator</Link> vul je
      afmetingen en vorm in en krijg je:
    </p>
    <ul>
      <li>Volume in m&sup3; en liters.</li>
      <li>Equivalent in gallons.</li>
      <li>Voorgestelde doseringen voor shockchlorering en pH-correctie.</li>
      <li>Aanbevolen bereik voor het pompdebiet.</li>
    </ul>
    <p>
      Wil je verder gaan en de geschatte{" "}
      <strong>NEN-EN 17645 milieuklasse</strong> van je zwembad krijgen samen
      met een uniek <strong>PoolLex ID</strong>, genereer dan je gratis
      Technische Fiche op <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> zet het volume en de apparatuurgegevens van je
      zwembad om in een complete technische diagnose: chemische dosering,
      schatting van de exploitatiekosten en milieuklasse A-F volgens NEN-EN
      17645:2023. Begin gratis op{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

// ============================================================================
// ARTICLE 8 — Poolrechner: Poolvolumen richtig berechnen (DE only)
// Sprint 119.100 (2026-08-12) — GSC queries "poolrechner", "pool kubikmeter
// rechner", "wassermenge pool berechnen", "volumenrechner pool". Single-lang DE.
// ============================================================================

const Article8BodyDE = () => (
  <>
    <h2>Warum das Poolvolumen so wichtig ist</h2>
    <p>
      Das Volumen Ihres Pools in Kubikmetern und Litern ist der wichtigste
      Wert f&uuml;r den t&auml;glichen Poolbetrieb. Jede{" "}
      <strong>Chemikaliendosierung</strong> wird pro 1.000 oder 10.000 Liter
      angegeben. Jede <strong>W&auml;rmepumpe</strong> ist f&uuml;r einen
      bestimmten Volumenbereich ausgelegt. Jeder{" "}
      <strong>Filtrationszyklus</strong> wird als Teil des Poolvolumens pro
      Stunde berechnet. Ein Volumenfehler von 20 % f&uuml;hrt zu Unterdosierung
      (tr&uuml;bes Wasser, Algen) oder &Uuml;berdosierung (Hautreizungen,
      Korrosion, verschwendetes Produkt).
    </p>
    <p>
      Dieser Leitfaden liefert Ihnen die exakten Formeln f&uuml;r jede Poolform,
      eine schnelle Umrechnungstabelle und die praktischen Tipps, die die
      beiden h&auml;ufigsten Fehler vermeiden: <strong>Radius mit Durchmesser
      verwechseln</strong> und <strong>Wandh&ouml;he statt Wassertiefe verwenden</strong>.
    </p>

    <h2>Grundumrechnung: m&sup3; zu Liter</h2>
    <p>Ein Kubikmeter Wasser entspricht immer exakt tausend Litern:</p>
    <p><strong>1 m&sup3; = 1.000 L</strong></p>
    <p>
      Alle Formeln unten liefern das Volumen in m&sup3;. F&uuml;r Liter mit
      1.000 multiplizieren. F&uuml;r US-Gallonen mit 264,17; f&uuml;r britische
      Gallonen mit 219,97.
    </p>

    <h2>Formeln nach Poolform</h2>

    <h3>Rechteckiger Pool mit flachem Boden</h3>
    <p><strong>V (m&sup3;) = L&auml;nge &times; Breite &times; Tiefe</strong></p>
    <p>Beispiel: ein 8 &times; 4 m Pool mit 1,5 m Wassertiefe:</p>
    <p>V = 8 &times; 4 &times; 1,5 = 48 m&sup3; = 48.000 Liter</p>

    <h3>Rechteckiger Pool mit geneigtem Boden</h3>
    <p>
      Verwenden Sie die <strong>mittlere Wassertiefe</strong>, nicht die tiefste
      oder flachste Stelle allein:
    </p>
    <p><strong>V (m&sup3;) = L&auml;nge &times; Breite &times; (Tiefe_flach + Tiefe_tief) / 2</strong></p>
    <p>Beispiel: ein 8 &times; 4 m Pool mit 1,2 m im flachen und 1,8 m im tiefen Bereich:</p>
    <p>V = 8 &times; 4 &times; (1,2 + 1,8) / 2 = 48 m&sup3; = 48.000 Liter</p>
    <p>
      Dies gilt f&uuml;r linear geneigten Boden. F&uuml;r Pools mit Trichterboden
      (flach + pl&ouml;tzlicher Sprung + tief) den Pool in zwei Rechtecke
      unterteilen und Volumina addieren.
    </p>

    <h3>Runder Pool</h3>
    <p><strong>V (m&sup3;) = &pi; &times; r&sup2; &times; h</strong></p>
    <p>
      Wobei r der Radius ist (halber Durchmesser) und h die Wassertiefe.
      Beispiel: ein Intex Frame Pool mit 4,57 m Durchmesser und 1,07 m
      Wassertiefe:
    </p>
    <p>V = 3,1416 &times; 2,285&sup2; &times; 1,07 = 17,55 m&sup3; = 17.550 Liter</p>
    <p>Oder mit dem Durchmesser D direkt:</p>
    <p><strong>V (m&sup3;) &asymp; 0,7854 &times; D&sup2; &times; h</strong></p>

    <h3>Ovaler Pool</h3>
    <p><strong>V (m&sup3;) = L&auml;nge &times; Breite &times; Tiefe &times; 0,893</strong></p>
    <p>
      Wobei L&auml;nge und Breite die &auml;u&szlig;eren Ma&szlig;e des Ovals
      sind und 0,893 ein Formfaktor. Beispiel: ein Bestway ovaler Pool 6,10
      &times; 3,66 m mit 1,22 m Wassertiefe:
    </p>
    <p>V = 6,10 &times; 3,66 &times; 1,22 &times; 0,893 = 24,30 m&sup3; = 24.300 Liter</p>

    <h3>Nierenform oder freie Form</h3>
    <p>N&auml;herung: <strong>V (m&sup3;) = L&auml;nge &times; Breite &times; mittlere_Tiefe &times; 0,85</strong></p>
    <p>
      Der Faktor 0,85 kompensiert die konkave Form. F&uuml;r pr&auml;zise
      Berechnung Pool in zwei oder drei Rechtecke unterteilen und addieren.
    </p>

    <h2>Referenztabelle: g&auml;ngige Poolgr&ouml;&szlig;en</h2>
    <p>Volumen in Litern f&uuml;r die h&auml;ufigsten Wohnpool-Grundrisse, bei 1,5 m mittlerer Wassertiefe:</p>
    <table>
      <thead>
        <tr>
          <th>Poolgrundriss (m)</th>
          <th>Form</th>
          <th>Volumen (m&sup3;)</th>
          <th>Volumen (Liter)</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>3 &times; 2</td><td>Rechteckig</td><td>9,0</td><td>9.000</td></tr>
        <tr><td>4 &times; 3</td><td>Rechteckig</td><td>18,0</td><td>18.000</td></tr>
        <tr><td>5 &times; 3</td><td>Rechteckig</td><td>22,5</td><td>22.500</td></tr>
        <tr><td>6 &times; 3</td><td>Rechteckig</td><td>27,0</td><td>27.000</td></tr>
        <tr><td>7 &times; 3,5</td><td>Rechteckig</td><td>36,7</td><td>36.700</td></tr>
        <tr><td>8 &times; 4</td><td>Rechteckig</td><td>48,0</td><td>48.000</td></tr>
        <tr><td>10 &times; 5</td><td>Rechteckig</td><td>75,0</td><td>75.000</td></tr>
        <tr><td>&Oslash; 3,66</td><td>Rund (h=1,07)</td><td>11,3</td><td>11.260</td></tr>
        <tr><td>&Oslash; 4,57</td><td>Rund (h=1,22)</td><td>20,0</td><td>20.010</td></tr>
        <tr><td>&Oslash; 5,49</td><td>Rund (h=1,32)</td><td>31,3</td><td>31.260</td></tr>
      </tbody>
    </table>

    <h2>Aufstellpools (Intex, Bestway): reale Daten</h2>
    <p>
      Die Hersteller drucken das Volumen bei <strong>80-90 % F&uuml;llstand</strong>{" "}
      der Wandh&ouml;he auf die Verpackung (Sicherheitsempfehlung). Die
      meistverkauften Modelle:
    </p>
    <ul>
      <li><strong>Intex Easy Set 3,05 &times; 0,76 m</strong>: 3.853 L angegeben (80 %). Real bei 90 %: 4.180 L.</li>
      <li><strong>Intex Metal Frame 3,66 &times; 0,99 m</strong>: 6.503 L angegeben. Real bei 90 %: 8.930 L.</li>
      <li><strong>Bestway Steel Pro 4,27 &times; 1,00 m</strong>: 10.250 L angegeben.</li>
      <li><strong>Intex Prism Frame 4,57 &times; 1,22 m</strong>: 16.805 L angegeben. Real bei 90 %: 17.550 L.</li>
      <li><strong>Bestway Power Steel 5,49 &times; 1,32 m</strong>: 26.478 L angegeben. Real bei 90 %: 28.410 L.</li>
    </ul>

    <h2>H&auml;ufige Fehler bei der Volumenberechnung</h2>
    <ol>
      <li><strong>Wandh&ouml;he statt Wassertiefe verwenden</strong>. Pools werden zu 80-90 % der Wandh&ouml;he gef&uuml;llt. Immer den tats&auml;chlichen Wasserstand messen.</li>
      <li><strong>Radius mit Durchmesser verwechseln bei runden Pools</strong>. Der Radius ist die H&auml;lfte des Durchmessers. Wird der Durchmesser als Radius eingesetzt, ergibt sich das 4-fache Volumen.</li>
      <li><strong>Geneigten Boden ignorieren</strong>. Die maximale Tiefe eines geneigten Pools verwenden f&uuml;hrt zu 20-30 % &Uuml;bersch&auml;tzung.</li>
      <li><strong>Verpackungsangabe als Wahrheit nehmen</strong>. Verpackte Volumen basieren auf 80-90 % F&uuml;llstand. Reales nutzbares Wasser kann 10-15 % abweichen.</li>
      <li><strong>Zu grobe Ma&szlig;abrundung</strong>. 5 cm Fehler auf einem 3 m Pool erzeugen 1,7 % Volumenfehler.</li>
    </ol>

    <h2>Anwendung 1: Chemiedosierung</h2>
    <p>
      Alle Poolchemikalien werden pro 1.000 oder 10.000 Liter Wasser dosiert.
      Schnellreferenz f&uuml;r einen typischen 40.000 L Pool (40 m&sup3;):
    </p>
    <ul>
      <li><strong>Schockchlorierung</strong> (10 g/m&sup3; freies Chlor): 400 g Trichlor-Granulat oder 1,2-1,5 L Chlorbleichlauge 10 %.</li>
      <li><strong>Salz-Erstf&uuml;llung</strong> f&uuml;r Salzelektrolyse (4 g/L Ziel): 160 kg Poolsalz.</li>
      <li><strong>pH-Senkung</strong> mit Salzs&auml;ure (33 %): etwa 100 mL f&uuml;r 0,1 Punkte Senkung.</li>
      <li><strong>Algizid</strong>: typisch 100 mL pro 40.000 L pro Woche.</li>
    </ul>
    <p>
      Ein 20 % Volumenfehler bei diesem Pool bedeutet 40 g Chlor zu wenig oder
      zu viel &mdash; genug f&uuml;r tr&uuml;bes Wasser oder Hautreizung.
    </p>

    <h2>Anwendung 2: Filterpumpe dimensionieren</h2>
    <p>
      Faustregel: Der <strong>Pumpen-Volumenstrom</strong> in m&sup3;/h sollte
      das Gesamtvolumen alle <strong>4 bis 6 Stunden</strong> einmal
      umw&auml;lzen. F&uuml;r einen 48 m&sup3; Pool bei 6 h/Zyklus:
    </p>
    <p>Erforderlicher Volumenstrom = 48 / 6 = 8 m&sup3;/h</p>
    <p>
      Dies bestimmt das Pumpenmodell und die Filtergr&ouml;&szlig;e. Zu klein
      dimensioniert bedeutet l&auml;ngere t&auml;gliche Filtration; zu gro&szlig;
      dimensioniert verschwendet Strom.
    </p>

    <h2>Anwendung 3: W&auml;rmepumpe auslegen</h2>
    <p>
      Um 1 m&sup3; Poolwasser um 1 &deg;C zu erw&auml;rmen, werden etwa{" "}
      <strong>1,16 kWh</strong> Nettow&auml;rme ben&ouml;tigt. F&uuml;r einen
      40 m&sup3; Pool mit 5 &deg;C Temperaturerh&ouml;hung (18 &rarr; 23 &deg;C):
    </p>
    <p>Nettow&auml;rmebedarf = 40 &times; 5 &times; 1,16 &asymp; 232 kWh</p>
    <p>
      Eine W&auml;rmepumpe mit 8 kW thermischer Leistung braucht 29 Stunden
      kontinuierlichen Betrieb, bevor Verluste &uuml;ber die Wasseroberfl&auml;che
      ber&uuml;cksichtigt werden (typisch 30-50 % zus&auml;tzlich ohne
      Abdeckung). Falsches Volumen, falsche W&auml;rmepumpe.
    </p>

    <h2>Berechnung online in 10 Sekunden</h2>
    <p>
      Ersparen Sie sich die Rechnerei. Unter{" "}
      <Link to="/tools/pool-volume-calculator">/tools/pool-volume-calculator</Link>{" "}
      geben Sie Poolform und Ma&szlig;e ein und erhalten:
    </p>
    <ul>
      <li>Volumen in m&sup3; und Litern.</li>
      <li>&Auml;quivalent in US- und UK-Gallonen.</li>
      <li>Empfohlene Chemiedosierung f&uuml;r Schockchlorierung und pH-Anpassung.</li>
      <li>Empfohlenen Pumpen-Volumenstrom.</li>
    </ul>
    <p>
      Wollen Sie mehr und eine gesch&auml;tzte <strong>DIN EN 17645
      Umweltklasse</strong> f&uuml;r Ihren Pool mit einer eindeutigen{" "}
      <strong>PoolLex ID</strong> erhalten, erstellen Sie kostenlos Ihr
      technisches Datenblatt unter{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>

    <hr />

    <p>
      <strong>PoolLex.io</strong> wandelt Poolvolumen und Ausstattungsdaten in
      eine komplette technische Diagnose um: Chemiedosierung, Betriebskostensch&auml;tzung
      und A-F Umweltklasse nach DIN EN 17645:2023. Starten Sie kostenlos unter{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

// ============================================================================
// ARTICLE 9 — Que es PoolLex (defensa brand vs Poolex.fr, Sprint 119.114)
// ============================================================================

const Article9BodyES = () => (
  <>
    <h2>PoolLex.io en 30 segundos</h2>
    <p>
      <strong>PoolLex.io</strong> es la iniciativa europea de{" "}
      <strong>certificación digital de sostenibilidad ambiental de piscinas</strong>{" "}
      privadas exteriores enterradas, basada en la norma <strong>UNE-EN 17645:2023</strong>.
      Su misión es hacer visible, medible y comparable el impacto ambiental
      de cada piscina a través de un identificador único europeo:{" "}
      <strong>PoolLex ID</strong> (formato PLX-XXXXXXX).
    </p>
    <p>
      No vendemos equipos, ni instalamos piscinas, ni somos un fabricante. Somos{" "}
      <strong>aplicadores de la norma</strong>, no autores: recogemos los datos
      técnicos que tú aportas y los traducimos en documentación oficial
      descargable (ficha técnica PDF, clasificación ambiental A-F) para
      propietarios, instaladores y administraciones.
    </p>

    <h2>Los 3 servicios de PoolLex.io</h2>
    <ul>
      <li>
        <strong><Link to="/ficha-tecnica">Ficha Técnica gratuita</Link></strong> —
        genera el PoolLex ID único europeo de tu piscina en 30 segundos, sin
        registro, con PDF oficial descargable.
      </li>
      <li>
        <strong><Link to="/pool-check">Pool Check</Link></strong> — comprueba
        el cumplimiento normativo básico de tu piscina en España (urbanístico,
        estructural y de seguridad).
      </li>
      <li>
        <strong><Link to="/solicitar-sello">Sello EN 17645 firmado</Link></strong> —
        certificación oficial firmada por un ingeniero industrial colegiado con
        clasificación ambiental A-F auditada.
      </li>
    </ul>

    <h2>Certificación, no fabricación</h2>
    <p>
      PoolLex.io es una <strong>plataforma digital de certificación</strong>{" "}
      basada en la norma UNE-EN 17645:2023. No fabricamos equipos, no
      instalamos piscinas y no vendemos productos técnicos.
    </p>
    <p>
      Nuestro rol es <strong>aplicar la norma técnica europea</strong> y
      traducirla en documentación digital accesible: fichas técnicas
      gratuitas, clasificación ambiental A-F, informes de mejora y sellos
      firmados. Todo desde una única plataforma, sin instalar software y sin
      registro para las funciones básicas.
    </p>
    <p>
      Nos posicionamos como <strong>aplicadores de la norma, no como
      autores</strong>: la norma la escribe CEN, nosotros la ponemos al
      alcance de propietarios, instaladores y administraciones europeos.
    </p>

    <h2>Para quién es PoolLex.io</h2>
    <ul>
      <li>
        <strong>Propietarios de piscina</strong> — quieren saber el impacto
        ambiental real de su piscina y disponer de ficha técnica oficial para
        trámites o venta.
      </li>
      <li>
        <strong>Instaladores y mantenedores</strong> — necesitan diferenciarse
        con documentación EN 17645 verificable para clientes exigentes.
      </li>
      <li>
        <strong>Administraciones y municipios</strong> — quieren dato
        estandarizado sobre el parque de piscinas del término municipal.
      </li>
    </ul>

    <h2>PoolLex ID — el identificador único europeo</h2>
    <p>
      Cada ficha técnica generada recibe un <strong>PoolLex ID</strong> con
      formato <strong>PLX-XXXXXXX</strong> (7 dígitos). Es un identificador
      universal único emitido por PoolLex.io que sirve como referencia estable
      para la piscina a lo largo de todo su ciclo de vida (instalación,
      mantenimiento, reforma, transmisión). Análogo al VIN en automoción o
      al referencial catastral en bienes inmuebles.
    </p>
    <p>
      El PoolLex ID es <strong>gratuito, sin registro y descargable
      inmediatamente</strong> en{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

const Article9BodyEN = () => (
  <>
    <h2>PoolLex.io in 30 seconds</h2>
    <p>
      <strong>PoolLex.io</strong> is the European initiative for{" "}
      <strong>digital certification of pool environmental sustainability</strong>,
      focused on private outdoor in-ground pools, based on the{" "}
      <strong>EN 17645:2023</strong> standard. Its mission is to make each
      pool's environmental impact visible, measurable and comparable through
      a unique European identifier: <strong>PoolLex ID</strong> (format
      PLX-XXXXXXX).
    </p>
    <p>
      We don't sell equipment, don't install pools, and are not a manufacturer.
      We are <strong>appliers of the standard</strong>, not authors: we take
      the technical data you provide and turn it into official downloadable
      documentation (technical sheet PDF, A-F environmental classification)
      for owners, installers and administrations.
    </p>

    <h2>The 3 PoolLex.io services</h2>
    <ul>
      <li>
        <strong><Link to="/ficha-tecnica">Free Technical Sheet</Link></strong> —
        generate the unique European PoolLex ID for your pool in 30 seconds,
        no registration required, official downloadable PDF.
      </li>
      <li>
        <strong><Link to="/pool-check">Pool Check</Link></strong> — verify
        basic regulatory compliance of your pool in Spain (urban, structural
        and safety).
      </li>
      <li>
        <strong><Link to="/solicitar-sello">Signed EN 17645 Seal</Link></strong> —
        official certification signed by a chartered industrial engineer with audited
        A-F environmental classification.
      </li>
    </ul>

    <h2>Certification, not manufacturing</h2>
    <p>
      PoolLex.io is a <strong>digital certification platform</strong> based
      on the EN 17645:2023 standard. We don't manufacture equipment, don't
      install pools and don't sell technical products.
    </p>
    <p>
      Our role is to <strong>apply the European technical standard</strong>{" "}
      and translate it into accessible digital documentation: free technical
      sheets, A-F environmental classification, improvement reports and
      signed seals. All from a single platform, without installing software
      and without registration for basic functions.
    </p>
    <p>
      We position ourselves as <strong>appliers of the standard, not
      authors</strong>: the standard is written by CEN, we make it accessible
      to European owners, installers and administrations.
    </p>

    <h2>Who is PoolLex.io for</h2>
    <ul>
      <li>
        <strong>Pool owners</strong> — want to know the real environmental
        impact of their pool and have an official technical sheet for
        procedures or sale.
      </li>
      <li>
        <strong>Installers and maintenance companies</strong> — need to
        differentiate with verifiable EN 17645 documentation for demanding
        clients.
      </li>
      <li>
        <strong>Administrations and municipalities</strong> — want standardized
        data on the pool inventory in their jurisdiction.
      </li>
    </ul>

    <h2>PoolLex ID — the unique European identifier</h2>
    <p>
      Each generated technical sheet receives a <strong>PoolLex ID</strong>{" "}
      with format <strong>PLX-XXXXXXX</strong> (7 digits). It's a unique
      universal identifier issued by PoolLex.io that serves as a stable
      reference for the pool throughout its entire life cycle (installation,
      maintenance, renovation, transfer). Analogous to the VIN in automotive
      or the cadastral reference in real estate.
    </p>
    <p>
      The PoolLex ID is <strong>free, no registration required and
      immediately downloadable</strong> at{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

const Article9BodyFR = () => (
  <>
    <h2>PoolLex.io en 30 secondes</h2>
    <p>
      <strong>PoolLex.io</strong> est l'initiative europ&eacute;enne de{" "}
      <strong>certification num&eacute;rique de la durabilit&eacute;
      environnementale des piscines</strong> priv&eacute;es ext&eacute;rieures
      enterr&eacute;es, bas&eacute;e sur la norme{" "}
      <strong>NF EN 17645:2023</strong>. Sa mission est de rendre visible,
      mesurable et comparable l'impact environnemental de chaque piscine
      gr&acirc;ce &agrave; un identifiant unique europ&eacute;en :{" "}
      <strong>PoolLex ID</strong> (format PLX-XXXXXXX).
    </p>
    <p>
      Nous ne vendons pas d'&eacute;quipements, n'installons pas de piscines
      et ne sommes pas un fabricant. Nous sommes <strong>applicateurs de la
      norme</strong>, pas auteurs : nous prenons les donn&eacute;es techniques
      que vous fournissez et les transformons en documentation officielle
      t&eacute;l&eacute;chargeable (fiche technique PDF, classification
      environnementale A-F) pour propri&eacute;taires, installateurs et
      administrations.
    </p>

    <h2>Les 3 services de PoolLex.io</h2>
    <ul>
      <li>
        <strong><Link to="/ficha-tecnica">Fiche Technique gratuite</Link></strong> —
        g&eacute;n&eacute;rez le PoolLex ID unique europ&eacute;en de votre
        piscine en 30 secondes, sans inscription, PDF officiel
        t&eacute;l&eacute;chargeable.
      </li>
      <li>
        <strong><Link to="/pool-check">Pool Check</Link></strong> —
        v&eacute;rifiez la conformit&eacute; r&eacute;glementaire de base de
        votre piscine en Espagne (urbanisme, structure et s&eacute;curit&eacute;).
      </li>
      <li>
        <strong><Link to="/solicitar-sello">Sceau NF EN 17645 sign&eacute;</Link></strong>{" "}
        — certification officielle sign&eacute;e par un ing&eacute;nieur dipl&ocirc;m&eacute; avec classification environnementale A-F
        audit&eacute;e.
      </li>
    </ul>

    <h2>Certification, pas fabrication</h2>
    <p>
      PoolLex.io est une <strong>plateforme num&eacute;rique de
      certification</strong> bas&eacute;e sur la norme NF EN 17645:2023. Nous
      ne fabriquons pas d'&eacute;quipements, n'installons pas de piscines et
      ne vendons pas de produits techniques.
    </p>
    <p>
      Notre r&ocirc;le est d'<strong>appliquer la norme technique
      europ&eacute;enne</strong> et de la traduire en documentation
      num&eacute;rique accessible : fiches techniques gratuites, classification
      environnementale A-F, rapports d'am&eacute;lioration et sceaux
      sign&eacute;s. Le tout depuis une plateforme unique, sans installer de
      logiciel et sans inscription pour les fonctions de base.
    </p>
    <p>
      Nous nous positionnons comme <strong>applicateurs de la norme, pas
      auteurs</strong> : la norme est &eacute;crite par le CEN, nous la
      rendons accessible aux propri&eacute;taires, installateurs et
      administrations europ&eacute;ens.
    </p>

    <h2>&Agrave; qui s'adresse PoolLex.io</h2>
    <ul>
      <li>
        <strong>Propri&eacute;taires de piscine</strong> — veulent conna&icirc;tre
        l'impact environnemental r&eacute;el de leur piscine et disposer d'une
        fiche technique officielle pour d&eacute;marches ou vente.
      </li>
      <li>
        <strong>Installateurs et soci&eacute;t&eacute;s de maintenance</strong> —
        ont besoin de se diff&eacute;rencier avec documentation NF EN 17645
        v&eacute;rifiable pour clients exigeants.
      </li>
      <li>
        <strong>Administrations et municipalit&eacute;s</strong> — veulent des
        donn&eacute;es standardis&eacute;es sur le parc de piscines de leur
        commune.
      </li>
    </ul>

    <h2>PoolLex ID — l'identifiant unique europ&eacute;en</h2>
    <p>
      Chaque fiche technique g&eacute;n&eacute;r&eacute;e re&ccedil;oit un{" "}
      <strong>PoolLex ID</strong> au format <strong>PLX-XXXXXXX</strong> (7
      chiffres). C'est un identifiant universel unique &eacute;mis par
      PoolLex.io qui sert de r&eacute;f&eacute;rence stable pour la piscine
      tout au long de son cycle de vie (installation, maintenance, r&eacute;novation,
      transmission). Analogue au VIN en automobile ou &agrave; la
      r&eacute;f&eacute;rence cadastrale en immobilier.
    </p>
    <p>
      Le PoolLex ID est <strong>gratuit, sans inscription et
      imm&eacute;diatement t&eacute;l&eacute;chargeable</strong> sur{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

const Article9BodyDE = () => (
  <>
    <h2>PoolLex.io in 30 Sekunden</h2>
    <p>
      <strong>PoolLex.io</strong> ist die europ&auml;ische Initiative f&uuml;r{" "}
      <strong>digitale Zertifizierung der Umweltnachhaltigkeit von
      Pools</strong>, fokussiert auf private Freibad-Pools mit
      Erdeinbau, basierend auf der Norm <strong>DIN EN 17645:2023</strong>.
      Die Mission besteht darin, den Umwelteinfluss jedes Pools sichtbar,
      messbar und vergleichbar zu machen — durch eine eindeutige
      europ&auml;ische Kennung: <strong>PoolLex ID</strong> (Format
      PLX-XXXXXXX).
    </p>
    <p>
      Wir verkaufen keine Ger&auml;te, installieren keine Pools und sind kein
      Hersteller. Wir sind <strong>Anwender der Norm</strong>, nicht Autoren:
      Wir nehmen die technischen Daten, die Sie liefern, und wandeln sie in
      offizielle herunterladbare Dokumentation um (technisches Datenblatt PDF,
      Umweltklassifizierung A-F) f&uuml;r Eigent&uuml;mer, Installateure und
      Verwaltungen.
    </p>

    <h2>Die 3 PoolLex.io-Dienste</h2>
    <ul>
      <li>
        <strong><Link to="/ficha-tecnica">Kostenloses technisches Datenblatt</Link></strong>{" "}
        — generieren Sie die eindeutige europ&auml;ische PoolLex ID f&uuml;r
        Ihren Pool in 30 Sekunden, ohne Registrierung, offizielles PDF zum
        Download.
      </li>
      <li>
        <strong><Link to="/pool-check">Pool Check</Link></strong> —
        &uuml;berpr&uuml;fen Sie die grundlegende regulatorische Konformit&auml;t
        Ihres Pools in Spanien (st&auml;dtebaulich, strukturell und
        sicherheitstechnisch).
      </li>
      <li>
        <strong><Link to="/solicitar-sello">Signiertes DIN EN 17645-Siegel</Link></strong>{" "}
        — offizielle Zertifizierung unterschrieben von einem eingetragenen
        Ingenieur mit gepr&uuml;fter A-F Umweltklassifizierung.
      </li>
    </ul>

    <h2>Zertifizierung, nicht Herstellung</h2>
    <p>
      PoolLex.io ist eine <strong>digitale Zertifizierungsplattform</strong>{" "}
      basierend auf der Norm DIN EN 17645:2023. Wir stellen keine Ausr&uuml;stung
      her, installieren keine Pools und verkaufen keine technischen Produkte.
    </p>
    <p>
      Unsere Rolle besteht darin, die <strong>europ&auml;ische technische
      Norm anzuwenden</strong> und sie in zug&auml;ngliche digitale
      Dokumentation umzuwandeln: kostenlose technische Datenbl&auml;tter,
      A-F Umweltklassifizierung, Verbesserungsberichte und signierte Siegel.
      Alles von einer einzigen Plattform, ohne Software zu installieren und
      ohne Registrierung f&uuml;r die Grundfunktionen.
    </p>
    <p>
      Wir positionieren uns als <strong>Anwender der Norm, nicht als
      Autoren</strong>: Die Norm wird vom CEN geschrieben, wir machen sie
      europ&auml;ischen Eigent&uuml;mern, Installateuren und Verwaltungen
      zug&auml;nglich.
    </p>

    <h2>F&uuml;r wen ist PoolLex.io</h2>
    <ul>
      <li>
        <strong>Pool-Eigent&uuml;mer</strong> — m&ouml;chten den realen
        Umwelteinfluss ihres Pools kennen und &uuml;ber ein offizielles
        technisches Datenblatt f&uuml;r Verfahren oder Verkauf verf&uuml;gen.
      </li>
      <li>
        <strong>Installateure und Wartungsunternehmen</strong> — m&uuml;ssen
        sich mit &uuml;berpr&uuml;fbarer DIN EN 17645-Dokumentation f&uuml;r
        anspruchsvolle Kunden differenzieren.
      </li>
      <li>
        <strong>Verwaltungen und Gemeinden</strong> — wollen standardisierte
        Daten &uuml;ber den Pool-Bestand ihres Zust&auml;ndigkeitsbereichs.
      </li>
    </ul>

    <h2>PoolLex ID — die eindeutige europ&auml;ische Kennung</h2>
    <p>
      Jedes generierte technische Datenblatt erh&auml;lt eine <strong>PoolLex
      ID</strong> im Format <strong>PLX-XXXXXXX</strong> (7 Ziffern). Es ist
      eine eindeutige universelle Kennung, ausgegeben von PoolLex.io, die als
      stabile Referenz f&uuml;r den Pool w&auml;hrend seines gesamten
      Lebenszyklus dient (Installation, Wartung, Renovierung, &Uuml;bertragung).
      Analog zur VIN in der Automobilindustrie oder zur Katasterreferenz bei
      Immobilien.
    </p>
    <p>
      Die PoolLex ID ist <strong>kostenlos, ohne Registrierung und sofort
      herunterladbar</strong> unter <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

const Article9BodyIT = () => (
  <>
    <h2>PoolLex.io in 30 secondi</h2>
    <p>
      <strong>PoolLex.io</strong> &egrave; l'iniziativa europea di{" "}
      <strong>certificazione digitale della sostenibilit&agrave; ambientale
      delle piscine</strong> private esterne interrate, basata sulla norma{" "}
      <strong>UNI EN 17645:2023</strong>. La sua missione &egrave; rendere
      visibile, misurabile e comparabile l'impatto ambientale di ogni piscina
      attraverso un identificatore unico europeo: <strong>PoolLex ID</strong>{" "}
      (formato PLX-XXXXXXX).
    </p>
    <p>
      Non vendiamo attrezzature, non installiamo piscine e non siamo un
      produttore. Siamo <strong>applicatori della norma</strong>, non autori:
      prendiamo i dati tecnici che fornisci e li trasformiamo in documentazione
      ufficiale scaricabile (scheda tecnica PDF, classificazione ambientale
      A-F) per propietari, installatori e amministrazioni.
    </p>

    <h2>I 3 servizi di PoolLex.io</h2>
    <ul>
      <li>
        <strong><Link to="/ficha-tecnica">Scheda Tecnica gratuita</Link></strong> —
        genera il PoolLex ID unico europeo della tua piscina in 30 secondi,
        senza registrazione, PDF ufficiale scaricabile.
      </li>
      <li>
        <strong><Link to="/pool-check">Pool Check</Link></strong> — verifica
        la conformit&agrave; normativa di base della tua piscina in Spagna
        (urbanistica, strutturale e di sicurezza).
      </li>
      <li>
        <strong><Link to="/solicitar-sello">Sigillo UNI EN 17645 firmato</Link></strong>{" "}
        — certificazione ufficiale firmata da un ingegnere iscritto all'Albo
        con classificazione ambientale A-F verificata.
      </li>
    </ul>

    <h2>Certificazione, non fabbricazione</h2>
    <p>
      PoolLex.io &egrave; una <strong>piattaforma digitale di
      certificazione</strong> basata sulla norma UNI EN 17645:2023. Non
      produciamo attrezzature, non installiamo piscine e non vendiamo prodotti
      tecnici.
    </p>
    <p>
      Il nostro ruolo &egrave; <strong>applicare la norma tecnica
      europea</strong> e tradurla in documentazione digitale accessibile:
      schede tecniche gratuite, classificazione ambientale A-F, relazioni di
      miglioramento e sigilli firmati. Tutto da un'unica piattaforma, senza
      installare software e senza registrazione per le funzioni di base.
    </p>
    <p>
      Ci posizioniamo come <strong>applicatori della norma, non autori</strong>:
      la norma &egrave; scritta dal CEN, noi la rendiamo accessibile a
      propietari, installatori e amministrazioni europei.
    </p>

    <h2>Per chi &egrave; PoolLex.io</h2>
    <ul>
      <li>
        <strong>Propietari di piscina</strong> — vogliono conoscere l'impatto
        ambientale reale della loro piscina e disporre di una scheda tecnica
        ufficiale per pratiche o vendita.
      </li>
      <li>
        <strong>Installatori e imprese di manutenzione</strong> — hanno
        bisogno di differenziarsi con documentazione UNI EN 17645 verificabile
        per clienti esigenti.
      </li>
      <li>
        <strong>Amministrazioni e comuni</strong> — vogliono dati
        standardizzati sul parco di piscine del loro territorio.
      </li>
    </ul>

    <h2>PoolLex ID — l'identificatore unico europeo</h2>
    <p>
      Ogni scheda tecnica generata riceve un <strong>PoolLex ID</strong> con
      formato <strong>PLX-XXXXXXX</strong> (7 cifre). &Egrave; un
      identificatore universale unico emesso da PoolLex.io che funge da
      riferimento stabile per la piscina per l'intero ciclo di vita
      (installazione, manutenzione, ristrutturazione, trasferimento). Analogo
      al VIN nell'automotive o al riferimento catastale nel settore
      immobiliare.
    </p>
    <p>
      Il PoolLex ID &egrave; <strong>gratuito, senza registrazione e
      immediatamente scaricabile</strong> su{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

const Article9BodyNL = () => (
  <>
    <h2>PoolLex.io in 30 seconden</h2>
    <p>
      <strong>PoolLex.io</strong> is het Europese initiatief voor{" "}
      <strong>digitale certificering van milieuduurzaamheid van
      zwembaden</strong>, gericht op priv&eacute; buitenzwembaden ingegraven,
      gebaseerd op de norm <strong>NEN-EN 17645:2023</strong>. De missie is
      de milieu-impact van elk zwembad zichtbaar, meetbaar en vergelijkbaar
      te maken via een unieke Europese identificatie: <strong>PoolLex ID</strong>{" "}
      (formaat PLX-XXXXXXX).
    </p>
    <p>
      Wij verkopen geen apparatuur, installeren geen zwembaden en zijn geen
      fabrikant. Wij zijn <strong>toepassers van de norm</strong>, geen
      auteurs: wij nemen de technische gegevens die u aanlevert en zetten ze
      om in officiële downloadbare documentatie (technische fiche PDF, A-F
      milieuclassificatie) voor eigenaars, installateurs en administraties.
    </p>

    <h2>De 3 PoolLex.io-diensten</h2>
    <ul>
      <li>
        <strong><Link to="/ficha-tecnica">Gratis Technische Fiche</Link></strong> —
        genereer de unieke Europese PoolLex ID voor uw zwembad in 30 seconden,
        zonder registratie, officiële downloadbare PDF.
      </li>
      <li>
        <strong><Link to="/pool-check">Pool Check</Link></strong> — verifieer
        de basisnaleving van regelgeving voor uw zwembad in Spanje (stedelijk,
        structureel en veiligheid).
      </li>
      <li>
        <strong><Link to="/solicitar-sello">Ondertekend NEN-EN 17645-zegel</Link></strong>{" "}
        — officiële certificering ondertekend door een gediplomeerd ingenieur (ir.) met geaudite A-F milieuclassificatie.
      </li>
    </ul>

    <h2>Certificering, geen fabricage</h2>
    <p>
      PoolLex.io is een <strong>digitaal certificatieplatform</strong>{" "}
      gebaseerd op de norm NEN-EN 17645:2023. Wij fabriceren geen apparatuur,
      installeren geen zwembaden en verkopen geen technische producten.
    </p>
    <p>
      Onze rol is het <strong>toepassen van de Europese technische norm</strong>{" "}
      en deze vertalen naar toegankelijke digitale documentatie: gratis
      technische fiches, A-F milieuclassificatie, verbeteringsrapporten en
      ondertekende zegels. Alles vanaf één platform, zonder software te
      installeren en zonder registratie voor de basisfuncties.
    </p>
    <p>
      Wij positioneren onszelf als <strong>toepassers van de norm, geen
      auteurs</strong>: de norm is geschreven door het CEN, wij maken deze
      toegankelijk voor Europese eigenaars, installateurs en administraties.
    </p>

    <h2>Voor wie is PoolLex.io</h2>
    <ul>
      <li>
        <strong>Zwembadeigenaars</strong> — willen de werkelijke milieu-impact
        van hun zwembad kennen en beschikken over een officiële technische
        fiche voor procedures of verkoop.
      </li>
      <li>
        <strong>Installateurs en onderhoudsbedrijven</strong> — moeten zich
        onderscheiden met verifieerbare NEN-EN 17645-documentatie voor
        veeleisende klanten.
      </li>
      <li>
        <strong>Administraties en gemeenten</strong> — willen gestandaardiseerde
        gegevens over het zwembadenbestand van hun rechtsgebied.
      </li>
    </ul>

    <h2>PoolLex ID — de unieke Europese identificatie</h2>
    <p>
      Elke gegenereerde technische fiche krijgt een <strong>PoolLex ID</strong>{" "}
      met formaat <strong>PLX-XXXXXXX</strong> (7 cijfers). Het is een uniek
      universeel identificatiemiddel uitgegeven door PoolLex.io dat dient als
      stabiele referentie voor het zwembad gedurende de gehele levenscyclus
      (installatie, onderhoud, renovatie, overdracht). Analoog aan het VIN
      in de auto-industrie of de kadastrale referentie in vastgoed.
    </p>
    <p>
      De PoolLex ID is <strong>gratis, zonder registratie en direct
      downloadbaar</strong> op <Link to="/ficha-tecnica">/ficha-tecnica</Link>.
    </p>
  </>
);

// ============================================================================
// ARTICLES EXPORT
// ============================================================================

// ============================================================
// ARTICLE 14 — Coste mensual caso real 40m2 (Sprint 119.111c LONG_TAIL)
// ============================================================

const Article14BodyES = () => (
  <>
    <h2>Datos del caso</h2>
    <p>
      Analizamos el coste mensual real de una piscina residencial estandar en
      un chalet unifamiliar de zona climatica templada del sur de Europa. Los
      datos base:
    </p>
    <ul>
      <li><strong>Superficie</strong>: 40 m&sup2; (8 &times; 5 m)</li>
      <li><strong>Volumen</strong>: 60 m&sup3; (profundidad media 1,5 m)</li>
      <li><strong>Bomba filtraci&oacute;n</strong>: 1 CV monovelocidad, 8 h/d&iacute;a en temporada</li>
      <li><strong>Bomba de calor</strong>: 12 kW, COP 4,5 &mdash; temporada 6 meses</li>
      <li><strong>Cubierta t&eacute;rmica</strong>: manual (l&aacute;mina flotante)</li>
      <li><strong>Sistema tratamiento</strong>: clorador salino de 150 W</li>
      <li><strong>Iluminaci&oacute;n</strong>: 3 focos LED de 25 W cada uno</li>
    </ul>
    <p>
      Todas las cifras siguientes se anualizan y se dividen por 12 para
      obtener el coste mensual promedio, incluyendo meses fuera de temporada
      de menor gasto.
    </p>

    <h2>Electricidad</h2>
    <p>Desglose anual de consumos el&eacute;ctricos:</p>
    <ul>
      <li>Bomba filtraci&oacute;n (0,75 kW &times; 8 h/d &times; 180 d): <strong>1.080 kWh</strong></li>
      <li>Bomba de calor (12 kW &times; 500 h/temporada &times; 1/4,5 COP): <strong>1.333 kWh</strong></li>
      <li>Clorador salino (0,15 kW &times; 8 h/d &times; 180 d): <strong>216 kWh</strong></li>
      <li>Iluminaci&oacute;n (0,075 kW &times; 2 h/d &times; 120 d): <strong>18 kWh</strong></li>
    </ul>
    <p>
      Total: <strong>2.647 kWh/a&ntilde;o</strong>. Con tarifa media
      ponderada de 0,18 &euro;/kWh: <strong>476 &euro;/a&ntilde;o</strong> =
      <strong>39,7 &euro;/mes promedio anual</strong>.
    </p>

    <h2>Agua de reposici&oacute;n</h2>
    <p>
      La evaporaci&oacute;n en zona templada del sur oscila entre 5-7 mm/d&iacute;a
      en verano y 1-2 mm/d&iacute;a en invierno. Para 40 m&sup2; de superficie:
    </p>
    <ul>
      <li>Verano (junio-agosto, 90 d, 6 mm/d): 90 &times; 40 &times; 6 = <strong>21,6 m&sup3;</strong></li>
      <li>Resto del a&ntilde;o (275 d, 2 mm/d promedio): 275 &times; 40 &times; 2 = <strong>22 m&sup3;</strong></li>
      <li>Contralavados y salpicaduras: <strong>~10 m&sup3;</strong></li>
    </ul>
    <p>
      Total: <strong>~54 m&sup3;/a&ntilde;o</strong>. A 2,20 &euro;/m&sup3;
      (media saneamiento incluido): <strong>119 &euro;/a&ntilde;o</strong> =
      <strong>9,9 &euro;/mes</strong>.
    </p>

    <h2>Productos qu&iacute;micos</h2>
    <p>Con clorador salino y buen mantenimiento del pH, el coste qu&iacute;mico se limita a:</p>
    <ul>
      <li>Reposici&oacute;n de sal (2 sacos 25 kg/a&ntilde;o): <strong>30 &euro;</strong></li>
      <li>Reductor de pH: <strong>45 &euro;</strong></li>
      <li>Algicida y floculante: <strong>25 &euro;</strong></li>
      <li>Cloro shock (2-3 tratamientos/a&ntilde;o): <strong>20 &euro;</strong></li>
    </ul>
    <p>
      Total: <strong>120 &euro;/a&ntilde;o</strong> = <strong>10 &euro;/mes</strong>.
      Una piscina sin clorador salino consumir&iacute;a el doble en
      pastillas de cloro.
    </p>

    <h2>Mantenimiento y recambios</h2>
    <p>
      Contempla la puesta a punto de temporada y los recambios peri&oacute;dicos
      previsibles:
    </p>
    <ul>
      <li>Puesta a punto primavera (limpieza, revisi&oacute;n, ajuste): <strong>150 &euro;</strong></li>
      <li>Amortizaci&oacute;n c&eacute;lula clorador (vida 5-7 a&ntilde;os): <strong>60 &euro;/a&ntilde;o</strong></li>
      <li>Recambios menores (juntas, tornillos, focos LED): <strong>40 &euro;/a&ntilde;o</strong></li>
    </ul>
    <p>
      Total: <strong>250 &euro;/a&ntilde;o</strong> = <strong>20,8 &euro;/mes</strong>.
    </p>

    <h2>Total mensual</h2>
    <p>Suma anualizada de las cuatro partidas:</p>
    <ul>
      <li>Electricidad: 476 &euro; (48 %)</li>
      <li>Agua: 119 &euro; (12 %)</li>
      <li>Qu&iacute;micos: 120 &euro; (12 %)</li>
      <li>Mantenimiento: 250 &euro; (25 %)</li>
      <li>Amortizaci&oacute;n bomba de calor (10 a&ntilde;os, 3.500 &euro;): 350 &euro; (35 %)</li>
    </ul>
    <p>
      <strong>Total con amortizaciones incluidas: ~1.315 &euro;/a&ntilde;o =
      110 &euro;/mes promedio anual.</strong> Sin contar amortizaci&oacute;n
      de bomba de calor: ~965 &euro;/a&ntilde;o = <strong>80 &euro;/mes</strong>.
    </p>
    <p>
      Para adaptar el c&aacute;lculo a tu piscina concreta usa la{" "}
      <Link to="/tools/pool-water-calculator">calculadora de agua</Link> y la{" "}
      <Link to="/tools/pool-energy-calculator">calculadora energ&eacute;tica</Link>,
      y complementa con la <Link to="/ficha-tecnica">ficha t&eacute;cnica</Link>{" "}
      para conocer tu clase EN 17645 estimada.
    </p>
  </>
);

const Article14BodyEN = () => (
  <>
    <h2>Case data</h2>
    <p>
      We analyse the real monthly cost of a standard residential pool in a
      detached house in temperate southern Europe. Baseline data:
    </p>
    <ul>
      <li><strong>Surface</strong>: 40 m&sup2; (8 &times; 5 m)</li>
      <li><strong>Volume</strong>: 60 m&sup3; (average depth 1.5 m)</li>
      <li><strong>Filtration pump</strong>: 1 HP single-speed, 8 h/day in season</li>
      <li><strong>Heat pump</strong>: 12 kW, COP 4.5 &mdash; 6-month season</li>
      <li><strong>Thermal cover</strong>: manual (floating sheet)</li>
      <li><strong>Treatment system</strong>: 150 W salt chlorinator</li>
      <li><strong>Lighting</strong>: 3 &times; 25 W LED spots</li>
    </ul>
    <p>
      All figures below are annualised and divided by 12 for average monthly
      cost, including lower-cost off-season months.
    </p>

    <h2>Electricity</h2>
    <p>Annual electricity breakdown:</p>
    <ul>
      <li>Filtration pump (0.75 kW &times; 8 h/d &times; 180 d): <strong>1,080 kWh</strong></li>
      <li>Heat pump (12 kW &times; 500 h/season &times; 1/4.5 COP): <strong>1,333 kWh</strong></li>
      <li>Salt chlorinator (0.15 kW &times; 8 h/d &times; 180 d): <strong>216 kWh</strong></li>
      <li>Lighting (0.075 kW &times; 2 h/d &times; 120 d): <strong>18 kWh</strong></li>
    </ul>
    <p>
      Total: <strong>2,647 kWh/year</strong>. With a weighted average tariff
      of &euro;0.18/kWh: <strong>&euro;476/year</strong> =
      <strong>&euro;39.7/month annual average</strong>.
    </p>

    <h2>Top-up water</h2>
    <p>
      Evaporation in a temperate southern zone ranges from 5-7 mm/day in
      summer to 1-2 mm/day in winter. For a 40 m&sup2; surface:
    </p>
    <ul>
      <li>Summer (Jun-Aug, 90 d, 6 mm/d): 90 &times; 40 &times; 6 = <strong>21.6 m&sup3;</strong></li>
      <li>Rest of year (275 d, 2 mm/d avg): 275 &times; 40 &times; 2 = <strong>22 m&sup3;</strong></li>
      <li>Backwash and splashing: <strong>~10 m&sup3;</strong></li>
    </ul>
    <p>
      Total: <strong>~54 m&sup3;/year</strong>. At &euro;2.20/m&sup3; (average
      including sewerage): <strong>&euro;119/year</strong> =
      <strong>&euro;9.9/month</strong>.
    </p>

    <h2>Chemicals</h2>
    <p>With salt chlorination and good pH management, chemical cost is limited to:</p>
    <ul>
      <li>Salt top-up (2 x 25 kg sacks/year): <strong>&euro;30</strong></li>
      <li>pH reducer: <strong>&euro;45</strong></li>
      <li>Algaecide and flocculant: <strong>&euro;25</strong></li>
      <li>Shock chlorine (2-3 treatments/year): <strong>&euro;20</strong></li>
    </ul>
    <p>
      Total: <strong>&euro;120/year</strong> = <strong>&euro;10/month</strong>.
      A pool without salt chlorination would spend twice as much on chlorine
      tablets.
    </p>

    <h2>Maintenance and parts</h2>
    <p>Covers seasonal start-up and predictable periodic replacements:</p>
    <ul>
      <li>Spring start-up (cleaning, inspection, adjustment): <strong>&euro;150</strong></li>
      <li>Chlorinator cell depreciation (5-7 year life): <strong>&euro;60/year</strong></li>
      <li>Minor parts (seals, screws, LED spots): <strong>&euro;40/year</strong></li>
    </ul>
    <p>
      Total: <strong>&euro;250/year</strong> = <strong>&euro;20.8/month</strong>.
    </p>

    <h2>Monthly total</h2>
    <p>Annualised sum of the four items:</p>
    <ul>
      <li>Electricity: &euro;476 (48 %)</li>
      <li>Water: &euro;119 (12 %)</li>
      <li>Chemicals: &euro;120 (12 %)</li>
      <li>Maintenance: &euro;250 (25 %)</li>
      <li>Heat pump depreciation (10 years, &euro;3,500): &euro;350 (35 %)</li>
    </ul>
    <p>
      <strong>Total with depreciation included: ~&euro;1,315/year =
      &euro;110/month annual average.</strong> Excluding heat pump
      depreciation: ~&euro;965/year = <strong>&euro;80/month</strong>.
    </p>
    <p>
      To adapt the calculation to your specific pool use the{" "}
      <Link to="/tools/pool-water-calculator">water calculator</Link> and the{" "}
      <Link to="/tools/pool-energy-calculator">energy calculator</Link>, and
      complement with the <Link to="/ficha-tecnica">technical sheet</Link> to
      learn your estimated EN 17645 class.
    </p>
  </>
);

const Article14BodyFR = () => (
  <>
    <h2>Donn&eacute;es du cas</h2>
    <p>
      Nous analysons le co&ucirc;t mensuel r&eacute;el d'une piscine
      r&eacute;sidentielle standard dans une maison individuelle en zone
      temp&eacute;r&eacute;e du sud de l'Europe. Donn&eacute;es de base&nbsp;:
    </p>
    <ul>
      <li><strong>Surface</strong>&nbsp;: 40 m&sup2; (8 &times; 5 m)</li>
      <li><strong>Volume</strong>&nbsp;: 60 m&sup3; (profondeur moyenne 1,5 m)</li>
      <li><strong>Pompe filtration</strong>&nbsp;: 1 CV monovitesse, 8 h/jour en saison</li>
      <li><strong>Pompe &agrave; chaleur</strong>&nbsp;: 12 kW, COP 4,5 &mdash; saison 6 mois</li>
      <li><strong>Couverture thermique</strong>&nbsp;: manuelle (b&acirc;che flottante)</li>
      <li><strong>Traitement</strong>&nbsp;: &eacute;lectrolyseur au sel de 150 W</li>
      <li><strong>&Eacute;clairage</strong>&nbsp;: 3 projecteurs LED de 25 W</li>
    </ul>
    <p>
      Tous les chiffres sont annualis&eacute;s puis divis&eacute;s par 12
      pour obtenir le co&ucirc;t mensuel moyen, y compris les mois hors saison
      &agrave; d&eacute;pense r&eacute;duite.
    </p>

    <h2>&Eacute;lectricit&eacute;</h2>
    <p>D&eacute;tail de consommation &eacute;lectrique annuel&nbsp;:</p>
    <ul>
      <li>Pompe filtration (0,75 kW &times; 8 h/j &times; 180 j)&nbsp;: <strong>1&nbsp;080 kWh</strong></li>
      <li>Pompe &agrave; chaleur (12 kW &times; 500 h/saison &times; 1/4,5 COP)&nbsp;: <strong>1&nbsp;333 kWh</strong></li>
      <li>&Eacute;lectrolyseur (0,15 kW &times; 8 h/j &times; 180 j)&nbsp;: <strong>216 kWh</strong></li>
      <li>&Eacute;clairage (0,075 kW &times; 2 h/j &times; 120 j)&nbsp;: <strong>18 kWh</strong></li>
    </ul>
    <p>
      Total&nbsp;: <strong>2&nbsp;647 kWh/an</strong>. Tarif moyen
      pond&eacute;r&eacute; 0,18 &euro;/kWh&nbsp;: <strong>476 &euro;/an</strong>{" "}
      = <strong>39,7 &euro;/mois moyenne annuelle</strong>.
    </p>

    <h2>Eau d'appoint</h2>
    <p>
      L'&eacute;vaporation en zone temp&eacute;r&eacute;e du sud varie de
      5-7 mm/jour en &eacute;t&eacute; &agrave; 1-2 mm/jour en hiver. Pour
      40 m&sup2; de surface&nbsp;:
    </p>
    <ul>
      <li>&Eacute;t&eacute; (juin-ao&ucirc;t, 90 j, 6 mm/j)&nbsp;: 90 &times; 40 &times; 6 = <strong>21,6 m&sup3;</strong></li>
      <li>Reste de l'ann&eacute;e (275 j, 2 mm/j moyenne)&nbsp;: 275 &times; 40 &times; 2 = <strong>22 m&sup3;</strong></li>
      <li>Contre-lavages et &eacute;claboussures&nbsp;: <strong>~10 m&sup3;</strong></li>
    </ul>
    <p>
      Total&nbsp;: <strong>~54 m&sup3;/an</strong>. &Agrave; 2,20 &euro;/m&sup3;
      (moyenne assainissement inclus)&nbsp;: <strong>119 &euro;/an</strong> =
      <strong>9,9 &euro;/mois</strong>.
    </p>

    <h2>Produits chimiques</h2>
    <p>Avec &eacute;lectrolyse au sel et bonne gestion du pH, le co&ucirc;t chimique se limite &agrave;&nbsp;:</p>
    <ul>
      <li>Compl&eacute;ment sel (2 sacs 25 kg/an)&nbsp;: <strong>30 &euro;</strong></li>
      <li>R&eacute;ducteur de pH&nbsp;: <strong>45 &euro;</strong></li>
      <li>Algicide et floculant&nbsp;: <strong>25 &euro;</strong></li>
      <li>Chlore choc (2-3 traitements/an)&nbsp;: <strong>20 &euro;</strong></li>
    </ul>
    <p>
      Total&nbsp;: <strong>120 &euro;/an</strong> = <strong>10 &euro;/mois</strong>.
      Une piscine sans &eacute;lectrolyse consommerait le double en pastilles
      de chlore.
    </p>

    <h2>Entretien et pi&egrave;ces</h2>
    <p>Couvre la mise en route saisonni&egrave;re et les remplacements p&eacute;riodiques pr&eacute;visibles&nbsp;:</p>
    <ul>
      <li>Mise en route printemps (nettoyage, r&eacute;vision)&nbsp;: <strong>150 &euro;</strong></li>
      <li>Amortissement cellule &eacute;lectrolyseur (dur&eacute;e 5-7 ans)&nbsp;: <strong>60 &euro;/an</strong></li>
      <li>Petites pi&egrave;ces (joints, vis, projecteurs LED)&nbsp;: <strong>40 &euro;/an</strong></li>
    </ul>
    <p>
      Total&nbsp;: <strong>250 &euro;/an</strong> = <strong>20,8 &euro;/mois</strong>.
    </p>

    <h2>Total mensuel</h2>
    <p>Somme annualis&eacute;e des quatre postes&nbsp;:</p>
    <ul>
      <li>&Eacute;lectricit&eacute;&nbsp;: 476 &euro; (48 %)</li>
      <li>Eau&nbsp;: 119 &euro; (12 %)</li>
      <li>Chimie&nbsp;: 120 &euro; (12 %)</li>
      <li>Entretien&nbsp;: 250 &euro; (25 %)</li>
      <li>Amortissement pompe &agrave; chaleur (10 ans, 3&nbsp;500 &euro;)&nbsp;: 350 &euro; (35 %)</li>
    </ul>
    <p>
      <strong>Total amortissements inclus&nbsp;: ~1&nbsp;315 &euro;/an =
      110 &euro;/mois moyenne annuelle.</strong> Hors amortissement pompe
      &agrave; chaleur&nbsp;: ~965 &euro;/an = <strong>80 &euro;/mois</strong>.
    </p>
    <p>
      Pour adapter le calcul &agrave; votre piscine utilisez le{" "}
      <Link to="/tools/pool-water-calculator">calculateur d'eau</Link> et le{" "}
      <Link to="/tools/pool-energy-calculator">calculateur &eacute;nerg&eacute;tique</Link>,
      et compl&eacute;tez avec la <Link to="/ficha-tecnica">fiche
      technique</Link> pour conna&icirc;tre votre classe NF EN 17645
      estim&eacute;e.
    </p>
  </>
);

const Article14BodyDE = () => (
  <>
    <h2>Falldaten</h2>
    <p>
      Wir analysieren die realen monatlichen Kosten eines Standard-Privatpools
      in einem Einfamilienhaus im gem&auml;&szlig;igten S&uuml;deuropa.
      Basisdaten:
    </p>
    <ul>
      <li><strong>Fl&auml;che</strong>: 40 m&sup2; (8 &times; 5 m)</li>
      <li><strong>Volumen</strong>: 60 m&sup3; (Durchschnittstiefe 1,5 m)</li>
      <li><strong>Filterpumpe</strong>: 1 PS einstufig, 8 h/Tag in Saison</li>
      <li><strong>W&auml;rmepumpe</strong>: 12 kW, COP 4,5 &mdash; 6-Monats-Saison</li>
      <li><strong>Thermoabdeckung</strong>: manuell (Solarfolie)</li>
      <li><strong>Aufbereitung</strong>: 150 W Salzelektrolyse</li>
      <li><strong>Beleuchtung</strong>: 3 &times; 25 W LED-Scheinwerfer</li>
    </ul>
    <p>
      Alle Zahlen unten sind annualisiert und durch 12 geteilt, um die
      monatlichen Durchschnittskosten zu erhalten, einschlie&szlig;lich der
      g&uuml;nstigeren Nebensaisonmonate.
    </p>

    <h2>Strom</h2>
    <p>J&auml;hrliche Stromaufschl&uuml;sselung:</p>
    <ul>
      <li>Filterpumpe (0,75 kW &times; 8 h/d &times; 180 d): <strong>1.080 kWh</strong></li>
      <li>W&auml;rmepumpe (12 kW &times; 500 h/Saison &times; 1/4,5 COP): <strong>1.333 kWh</strong></li>
      <li>Salzelektrolyse (0,15 kW &times; 8 h/d &times; 180 d): <strong>216 kWh</strong></li>
      <li>Beleuchtung (0,075 kW &times; 2 h/d &times; 120 d): <strong>18 kWh</strong></li>
    </ul>
    <p>
      Gesamt: <strong>2.647 kWh/Jahr</strong>. Bei gewichtetem Durchschnittstarif
      0,18 &euro;/kWh: <strong>476 &euro;/Jahr</strong> =
      <strong>39,7 &euro;/Monat Jahresdurchschnitt</strong>.
    </p>

    <h2>Nachf&uuml;llwasser</h2>
    <p>
      Verdunstung im gem&auml;&szlig;igten S&uuml;den betr&auml;gt 5-7 mm/Tag
      im Sommer bis 1-2 mm/Tag im Winter. F&uuml;r 40 m&sup2; Fl&auml;che:
    </p>
    <ul>
      <li>Sommer (Jun-Aug, 90 d, 6 mm/d): 90 &times; 40 &times; 6 = <strong>21,6 m&sup3;</strong></li>
      <li>Restjahr (275 d, 2 mm/d Durchschnitt): 275 &times; 40 &times; 2 = <strong>22 m&sup3;</strong></li>
      <li>R&uuml;cksp&uuml;lungen und Spritzwasser: <strong>~10 m&sup3;</strong></li>
    </ul>
    <p>
      Gesamt: <strong>~54 m&sup3;/Jahr</strong>. Bei 2,20 &euro;/m&sup3;
      (Durchschnitt mit Abwasser): <strong>119 &euro;/Jahr</strong> =
      <strong>9,9 &euro;/Monat</strong>.
    </p>

    <h2>Chemie</h2>
    <p>Mit Salzelektrolyse und gutem pH-Management sind die Chemiekosten begrenzt auf:</p>
    <ul>
      <li>Salznachf&uuml;llung (2 &times; 25 kg S&auml;cke/Jahr): <strong>30 &euro;</strong></li>
      <li>pH-Senker: <strong>45 &euro;</strong></li>
      <li>Algizid und Flockmittel: <strong>25 &euro;</strong></li>
      <li>Schockchlor (2-3 Behandlungen/Jahr): <strong>20 &euro;</strong></li>
    </ul>
    <p>
      Gesamt: <strong>120 &euro;/Jahr</strong> = <strong>10 &euro;/Monat</strong>.
      Ein Pool ohne Salzelektrolyse w&uuml;rde doppelt so viel f&uuml;r
      Chlortabletten ausgeben.
    </p>

    <h2>Wartung und Ersatzteile</h2>
    <p>Umfasst Saisoninbetriebnahme und vorhersehbaren periodischen Austausch:</p>
    <ul>
      <li>Fr&uuml;hjahrsinbetriebnahme (Reinigung, Pr&uuml;fung): <strong>150 &euro;</strong></li>
      <li>Elektrolysezellen-Abschreibung (Lebensdauer 5-7 Jahre): <strong>60 &euro;/Jahr</strong></li>
      <li>Kleinteile (Dichtungen, Schrauben, LED-Scheinwerfer): <strong>40 &euro;/Jahr</strong></li>
    </ul>
    <p>
      Gesamt: <strong>250 &euro;/Jahr</strong> = <strong>20,8 &euro;/Monat</strong>.
    </p>

    <h2>Monatliche Gesamtkosten</h2>
    <p>Annualisierte Summe der vier Posten:</p>
    <ul>
      <li>Strom: 476 &euro; (48 %)</li>
      <li>Wasser: 119 &euro; (12 %)</li>
      <li>Chemie: 120 &euro; (12 %)</li>
      <li>Wartung: 250 &euro; (25 %)</li>
      <li>W&auml;rmepumpen-Abschreibung (10 Jahre, 3.500 &euro;): 350 &euro; (35 %)</li>
    </ul>
    <p>
      <strong>Gesamt mit Abschreibungen: ~1.315 &euro;/Jahr =
      110 &euro;/Monat Jahresdurchschnitt.</strong> Ohne
      W&auml;rmepumpen-Abschreibung: ~965 &euro;/Jahr =
      <strong>80 &euro;/Monat</strong>.
    </p>
    <p>
      Um die Berechnung auf Ihren Pool anzupassen, nutzen Sie den{" "}
      <Link to="/tools/pool-water-calculator">Wasserrechner</Link> und den{" "}
      <Link to="/tools/pool-energy-calculator">Energie-Rechner</Link> und
      erg&auml;nzen Sie mit dem <Link to="/ficha-tecnica">technischen
      Datenblatt</Link>, um Ihre gesch&auml;tzte DIN EN 17645 Klasse zu
      erfahren.
    </p>
  </>
);

const Article14BodyIT = () => (
  <>
    <h2>Dati del caso</h2>
    <p>
      Analizziamo il costo mensile reale di una piscina residenziale standard
      in una villa unifamiliare in zona temperata del sud Europa. Dati base:
    </p>
    <ul>
      <li><strong>Superficie</strong>: 40 m&sup2; (8 &times; 5 m)</li>
      <li><strong>Volume</strong>: 60 m&sup3; (profondit&agrave; media 1,5 m)</li>
      <li><strong>Pompa filtrazione</strong>: 1 CV monovelocit&agrave;, 8 h/giorno in stagione</li>
      <li><strong>Pompa di calore</strong>: 12 kW, COP 4,5 &mdash; stagione 6 mesi</li>
      <li><strong>Copertura termica</strong>: manuale (telo flottante)</li>
      <li><strong>Trattamento</strong>: elettrolisi al sale da 150 W</li>
      <li><strong>Illuminazione</strong>: 3 proiettori LED da 25 W</li>
    </ul>
    <p>
      Tutte le cifre sono annualizzate e divise per 12 per ottenere il costo
      medio mensile, compresi i mesi fuori stagione a minor spesa.
    </p>

    <h2>Elettricit&agrave;</h2>
    <p>Dettaglio consumi elettrici annuali:</p>
    <ul>
      <li>Pompa filtrazione (0,75 kW &times; 8 h/g &times; 180 g): <strong>1.080 kWh</strong></li>
      <li>Pompa di calore (12 kW &times; 500 h/stagione &times; 1/4,5 COP): <strong>1.333 kWh</strong></li>
      <li>Elettrolisi al sale (0,15 kW &times; 8 h/g &times; 180 g): <strong>216 kWh</strong></li>
      <li>Illuminazione (0,075 kW &times; 2 h/g &times; 120 g): <strong>18 kWh</strong></li>
    </ul>
    <p>
      Totale: <strong>2.647 kWh/anno</strong>. Tariffa media ponderata
      0,18 &euro;/kWh: <strong>476 &euro;/anno</strong> =
      <strong>39,7 &euro;/mese media annuale</strong>.
    </p>

    <h2>Acqua di reintegro</h2>
    <p>
      L'evaporazione in zona temperata meridionale varia da 5-7 mm/giorno in
      estate a 1-2 mm/giorno in inverno. Per 40 m&sup2; di superficie:
    </p>
    <ul>
      <li>Estate (giu-ago, 90 g, 6 mm/g): 90 &times; 40 &times; 6 = <strong>21,6 m&sup3;</strong></li>
      <li>Resto dell'anno (275 g, 2 mm/g media): 275 &times; 40 &times; 2 = <strong>22 m&sup3;</strong></li>
      <li>Controlavaggi e spruzzi: <strong>~10 m&sup3;</strong></li>
    </ul>
    <p>
      Totale: <strong>~54 m&sup3;/anno</strong>. A 2,20 &euro;/m&sup3;
      (media fognatura inclusa): <strong>119 &euro;/anno</strong> =
      <strong>9,9 &euro;/mese</strong>.
    </p>

    <h2>Prodotti chimici</h2>
    <p>Con elettrolisi al sale e buon controllo del pH il costo chimico si limita a:</p>
    <ul>
      <li>Reintegro sale (2 sacchi 25 kg/anno): <strong>30 &euro;</strong></li>
      <li>Riduttore di pH: <strong>45 &euro;</strong></li>
      <li>Alghicida e flocculante: <strong>25 &euro;</strong></li>
      <li>Cloro shock (2-3 trattamenti/anno): <strong>20 &euro;</strong></li>
    </ul>
    <p>
      Totale: <strong>120 &euro;/anno</strong> = <strong>10 &euro;/mese</strong>.
      Una piscina senza elettrolisi consumerebbe il doppio in pastiglie di
      cloro.
    </p>

    <h2>Manutenzione e ricambi</h2>
    <p>Comprende messa a punto stagionale e ricambi periodici prevedibili:</p>
    <ul>
      <li>Messa a punto primaverile (pulizia, revisione): <strong>150 &euro;</strong></li>
      <li>Ammortamento cellula elettrolizzatore (vita 5-7 anni): <strong>60 &euro;/anno</strong></li>
      <li>Ricambi minori (guarnizioni, viti, proiettori LED): <strong>40 &euro;/anno</strong></li>
    </ul>
    <p>
      Totale: <strong>250 &euro;/anno</strong> = <strong>20,8 &euro;/mese</strong>.
    </p>

    <h2>Totale mensile</h2>
    <p>Somma annualizzata delle quattro voci:</p>
    <ul>
      <li>Elettricit&agrave;: 476 &euro; (48 %)</li>
      <li>Acqua: 119 &euro; (12 %)</li>
      <li>Chimica: 120 &euro; (12 %)</li>
      <li>Manutenzione: 250 &euro; (25 %)</li>
      <li>Ammortamento pompa di calore (10 anni, 3.500 &euro;): 350 &euro; (35 %)</li>
    </ul>
    <p>
      <strong>Totale ammortamenti inclusi: ~1.315 &euro;/anno =
      110 &euro;/mese media annuale.</strong> Senza ammortamento pompa di
      calore: ~965 &euro;/anno = <strong>80 &euro;/mese</strong>.
    </p>
    <p>
      Per adattare il calcolo alla tua piscina usa il{" "}
      <Link to="/tools/pool-water-calculator">calcolatore d'acqua</Link> e il{" "}
      <Link to="/tools/pool-energy-calculator">calcolatore energetico</Link>,
      e integra con la <Link to="/ficha-tecnica">scheda tecnica</Link> per
      conoscere la classe UNI EN 17645 stimata.
    </p>
  </>
);

const Article14BodyNL = () => (
  <>
    <h2>Gegevens van het geval</h2>
    <p>
      We analyseren de re&euml;le maandelijkse kost van een standaard
      residentieel zwembad in een vrijstaande woning in gematigd Zuid-Europa.
      Basisgegevens:
    </p>
    <ul>
      <li><strong>Oppervlakte</strong>: 40 m&sup2; (8 &times; 5 m)</li>
      <li><strong>Volume</strong>: 60 m&sup3; (gemiddelde diepte 1,5 m)</li>
      <li><strong>Filtratiepomp</strong>: 1 pk &eacute;&eacute;nsnelheid, 8 u/dag in seizoen</li>
      <li><strong>Warmtepomp</strong>: 12 kW, COP 4,5 &mdash; seizoen 6 maanden</li>
      <li><strong>Thermische afdekking</strong>: handmatig (drijffolie)</li>
      <li><strong>Behandeling</strong>: zoutelektrolyse 150 W</li>
      <li><strong>Verlichting</strong>: 3 LED-spots van 25 W</li>
    </ul>
    <p>
      Alle cijfers hieronder zijn geannualiseerd en gedeeld door 12 voor
      gemiddelde maandkost, inclusief de goedkopere maanden buiten seizoen.
    </p>

    <h2>Elektriciteit</h2>
    <p>Jaarlijkse elektriciteitsverdeling:</p>
    <ul>
      <li>Filtratiepomp (0,75 kW &times; 8 u/d &times; 180 d): <strong>1.080 kWh</strong></li>
      <li>Warmtepomp (12 kW &times; 500 u/seizoen &times; 1/4,5 COP): <strong>1.333 kWh</strong></li>
      <li>Zoutelektrolyse (0,15 kW &times; 8 u/d &times; 180 d): <strong>216 kWh</strong></li>
      <li>Verlichting (0,075 kW &times; 2 u/d &times; 120 d): <strong>18 kWh</strong></li>
    </ul>
    <p>
      Totaal: <strong>2.647 kWh/jaar</strong>. Gewogen gemiddeld tarief
      &euro;0,18/kWh: <strong>&euro;476/jaar</strong> =
      <strong>&euro;39,7/maand jaargemiddelde</strong>.
    </p>

    <h2>Aanvullingswater</h2>
    <p>
      Verdamping in gematigd zuidelijk klimaat varieert van 5-7 mm/dag in
      zomer tot 1-2 mm/dag in winter. Voor 40 m&sup2; oppervlak:
    </p>
    <ul>
      <li>Zomer (jun-aug, 90 d, 6 mm/d): 90 &times; 40 &times; 6 = <strong>21,6 m&sup3;</strong></li>
      <li>Rest van het jaar (275 d, 2 mm/d gemiddeld): 275 &times; 40 &times; 2 = <strong>22 m&sup3;</strong></li>
      <li>Terugspoelen en spatten: <strong>~10 m&sup3;</strong></li>
    </ul>
    <p>
      Totaal: <strong>~54 m&sup3;/jaar</strong>. Aan &euro;2,20/m&sup3;
      (gemiddeld incl. riolering): <strong>&euro;119/jaar</strong> =
      <strong>&euro;9,9/maand</strong>.
    </p>

    <h2>Chemicali&euml;n</h2>
    <p>Met zoutelektrolyse en goed pH-beheer is de chemiekost beperkt tot:</p>
    <ul>
      <li>Zoutaanvulling (2 zakken 25 kg/jaar): <strong>&euro;30</strong></li>
      <li>pH-verlager: <strong>&euro;45</strong></li>
      <li>Algicide en flocculant: <strong>&euro;25</strong></li>
      <li>Schokchloor (2-3 behandelingen/jaar): <strong>&euro;20</strong></li>
    </ul>
    <p>
      Totaal: <strong>&euro;120/jaar</strong> = <strong>&euro;10/maand</strong>.
      Een zwembad zonder zoutelektrolyse zou het dubbele uitgeven aan
      chloortabletten.
    </p>

    <h2>Onderhoud en reserveonderdelen</h2>
    <p>Dekt seizoenopstart en voorspelbare periodieke vervangingen:</p>
    <ul>
      <li>Voorjaarsopstart (reiniging, inspectie): <strong>&euro;150</strong></li>
      <li>Afschrijving elektrolysecel (levensduur 5-7 jaar): <strong>&euro;60/jaar</strong></li>
      <li>Kleine onderdelen (dichtingen, schroeven, LED-spots): <strong>&euro;40/jaar</strong></li>
    </ul>
    <p>
      Totaal: <strong>&euro;250/jaar</strong> = <strong>&euro;20,8/maand</strong>.
    </p>

    <h2>Maandelijks totaal</h2>
    <p>Geannualiseerde som van de vier posten:</p>
    <ul>
      <li>Elektriciteit: &euro;476 (48 %)</li>
      <li>Water: &euro;119 (12 %)</li>
      <li>Chemie: &euro;120 (12 %)</li>
      <li>Onderhoud: &euro;250 (25 %)</li>
      <li>Afschrijving warmtepomp (10 jaar, &euro;3.500): &euro;350 (35 %)</li>
    </ul>
    <p>
      <strong>Totaal incl. afschrijvingen: ~&euro;1.315/jaar =
      &euro;110/maand jaargemiddelde.</strong> Zonder afschrijving warmtepomp:
      ~&euro;965/jaar = <strong>&euro;80/maand</strong>.
    </p>
    <p>
      Om de berekening aan uw zwembad aan te passen gebruikt u de{" "}
      <Link to="/tools/pool-water-calculator">watercalculator</Link> en de{" "}
      <Link to="/tools/pool-energy-calculator">energiecalculator</Link>, en
      vult u aan met de <Link to="/ficha-tecnica">technische fiche</Link> om
      uw geschatte NEN-EN 17645 klasse te kennen.
    </p>
  </>
);

// ============================================================
// ARTICLE 13 — Consumo el&eacute;ctrico desglose (Sprint 119.111c LONG_TAIL)
// ============================================================

const Article13BodyES = () => (
  <>
    <h2>Bomba de filtraci&oacute;n</h2>
    <p>
      La bomba de filtraci&oacute;n es el equipo con m&aacute;s horas de
      funcionamiento al a&ntilde;o y el <strong>mayor consumo el&eacute;ctrico
      no discrecional</strong> de la piscina. Una bomba monovelocidad de{" "}
      <strong>0,75-1,5 CV</strong> funcionando 6-8 h/d&iacute;a en temporada
      consume entre <strong>1.500 y 3.000 kWh anuales</strong>.
    </p>
    <p>
      Una <strong>bomba de velocidad variable</strong> (VSD) bien programada
      reduce ese consumo entre un <strong>60 y un 80 %</strong>, principalmente
      trabajando a caudal reducido durante la mayor parte del tiempo de
      filtraci&oacute;n. Es la actuaci&oacute;n con mayor retorno econ&oacute;mico
      en la mayor&iacute;a de reformas.
    </p>

    <h2>Bomba de calor</h2>
    <p>
      La bomba de calor concentra la mayor parte del gasto el&eacute;ctrico en
      piscinas climatizadas. Su rendimiento se mide por el <strong>COP</strong>{" "}
      (Coefficient of Performance): un COP 5 significa que por cada kWh
      el&eacute;ctrico consumido entrega 5 kWh t&eacute;rmicos al agua.
    </p>
    <p>
      Consumo t&iacute;pico en una piscina de 50 m&sup3; en zona templada,
      temporada extendida (mayo-octubre): <strong>2.500-5.000 kWh anuales</strong>.
      Sin cubierta t&eacute;rmica el consumo puede duplicarse por las
      p&eacute;rdidas nocturnas por radiaci&oacute;n y convecci&oacute;n.
    </p>

    <h2>Iluminaci&oacute;n</h2>
    <p>
      La iluminaci&oacute;n sumergida representa una fracci&oacute;n peque&ntilde;a
      del consumo total. Focos LED modernos consumen{" "}
      <strong>15-30 W por punto</strong> frente a 100-300 W de los antiguos
      hal&oacute;genos. En una piscina con 2-4 focos y 3 h de uso diario en
      temporada, el consumo est&aacute; en <strong>30-100 kWh anuales</strong>.
    </p>
    <p>
      El cambio a LED es rentable pero de bajo impacto absoluto: reduce la
      factura anual en pocas decenas de euros, no en cientos.
    </p>

    <h2>Sistemas de salinizaci&oacute;n</h2>
    <p>
      El clorador salino consume <strong>150-400 W</strong> durante su
      funcionamiento. En una piscina de 50 m&sup3; con 6-8 h diarias de
      cloraci&oacute;n en temporada, el consumo est&aacute; entre{" "}
      <strong>200 y 500 kWh anuales</strong>.
    </p>
    <p>
      Es un consumo relevante pero contenido &mdash; equivalente al de una
      bomba de filtraci&oacute;n eficiente. El coste operativo se compensa
      con el ahorro de compra de pastillas de cloro.
    </p>

    <h2>Tarifa: valle vs punta</h2>
    <p>
      La factura el&eacute;ctrica de una piscina depende tanto del{" "}
      <strong>consumo (kWh)</strong> como de la <strong>hora</strong> en que
      se consume. Con tarifas indexadas o con discriminaci&oacute;n horaria,
      el precio del kWh puede variar de <strong>0,08 &euro;/kWh en valle</strong>{" "}
      a <strong>0,25 &euro;/kWh en punta</strong>.
    </p>
    <p>
      Programar la filtraci&oacute;n y la bomba de calor mayoritariamente en
      horas valle (00:00-08:00 y fines de semana) puede reducir la factura
      el&eacute;ctrica de la piscina entre un <strong>25 y un 40 %</strong>{" "}
      sin cambiar equipos. Calcula tu consumo estimado con nuestra{" "}
      <Link to="/tools/pool-energy-calculator">calculadora energ&eacute;tica</Link>{" "}
      y contrasta con la <Link to="/ficha-tecnica">ficha t&eacute;cnica</Link>{" "}
      de tu piscina para localizar la clase EN 17645 estimada.
    </p>
  </>
);

const Article13BodyEN = () => (
  <>
    <h2>Filtration pump</h2>
    <p>
      The filtration pump has the highest annual runtime and is the pool's{" "}
      <strong>largest non-discretionary electricity consumer</strong>. A
      single-speed pump of <strong>0.75-1.5 HP</strong> running 6-8 h/day in
      season consumes between <strong>1,500 and 3,000 kWh per year</strong>.
    </p>
    <p>
      A well-programmed <strong>variable-speed pump</strong> (VSD) cuts that
      consumption by <strong>60-80 %</strong>, mostly by running at reduced
      flow for most of the filtration period. It is the retrofit with the
      highest financial return in most refurbishments.
    </p>

    <h2>Heat pump</h2>
    <p>
      The heat pump concentrates most of the electricity cost in heated pools.
      Its performance is measured by the <strong>COP</strong> (Coefficient of
      Performance): a COP of 5 means that for every kWh of electricity
      consumed it delivers 5 kWh of heat to the water.
    </p>
    <p>
      Typical consumption in a 50 m&sup3; pool in temperate zone, extended
      season (May-October): <strong>2,500-5,000 kWh per year</strong>. Without
      a thermal cover consumption can double due to overnight radiation and
      convection losses.
    </p>

    <h2>Lighting</h2>
    <p>
      Underwater lighting represents a small fraction of total consumption.
      Modern LED fixtures use <strong>15-30 W per point</strong> versus
      100-300 W for old halogens. In a pool with 2-4 fixtures and 3 h daily
      use in season, consumption is around <strong>30-100 kWh per year</strong>.
    </p>
    <p>
      Switching to LED is profitable but small in absolute impact: it cuts
      the annual bill by tens of euros, not hundreds.
    </p>

    <h2>Salt chlorination systems</h2>
    <p>
      A salt chlorinator draws <strong>150-400 W</strong> while running. In a
      50 m&sup3; pool with 6-8 h daily chlorination in season, consumption
      is between <strong>200 and 500 kWh per year</strong>.
    </p>
    <p>
      It is a relevant but contained load &mdash; equivalent to an efficient
      filtration pump. The operating cost is offset by the savings from not
      buying chlorine tablets.
    </p>

    <h2>Tariff: off-peak vs peak</h2>
    <p>
      A pool's electricity bill depends both on <strong>consumption (kWh)</strong>{" "}
      and on the <strong>time</strong> when it is consumed. With time-of-use
      or indexed tariffs, the kWh price can range from{" "}
      <strong>&euro;0.08/kWh off-peak</strong> to{" "}
      <strong>&euro;0.25/kWh at peak</strong>.
    </p>
    <p>
      Scheduling filtration and the heat pump mostly during off-peak hours
      (00:00-08:00 and weekends) can cut the pool's electricity bill by{" "}
      <strong>25-40 %</strong> without changing any equipment. Compute your
      estimated consumption in our{" "}
      <Link to="/tools/pool-energy-calculator">energy calculator</Link> and
      cross-check with your pool's{" "}
      <Link to="/ficha-tecnica">technical sheet</Link> to find its estimated
      EN 17645 class.
    </p>
  </>
);

const Article13BodyFR = () => (
  <>
    <h2>Pompe de filtration</h2>
    <p>
      La pompe de filtration est l'&eacute;quipement au plus grand nombre
      d'heures annuelles et le <strong>plus gros consommateur &eacute;lectrique
      non discr&eacute;tionnaire</strong> de la piscine. Une pompe monovitesse
      de <strong>0,75-1,5 CV</strong> fonctionnant 6-8 h/jour en saison
      consomme entre <strong>1&nbsp;500 et 3&nbsp;000 kWh par an</strong>.
    </p>
    <p>
      Une <strong>pompe &agrave; vitesse variable</strong> (VSD) bien
      programm&eacute;e r&eacute;duit cette consommation de{" "}
      <strong>60 &agrave; 80 %</strong>, principalement en travaillant &agrave;
      d&eacute;bit r&eacute;duit la majeure partie du temps. C'est l'action
      au meilleur retour &eacute;conomique dans la plupart des r&eacute;novations.
    </p>

    <h2>Pompe &agrave; chaleur</h2>
    <p>
      La pompe &agrave; chaleur concentre l'essentiel du co&ucirc;t
      &eacute;lectrique en piscine chauff&eacute;e. Son rendement se mesure
      par le <strong>COP</strong> (Coefficient of Performance)&nbsp;: un COP
      de 5 signifie que pour 1 kWh &eacute;lectrique consomm&eacute; elle
      d&eacute;livre 5 kWh thermiques &agrave; l'eau.
    </p>
    <p>
      Consommation typique pour une piscine de 50 m&sup3; en zone
      temp&eacute;r&eacute;e, saison &eacute;tendue (mai-octobre)&nbsp;:{" "}
      <strong>2&nbsp;500-5&nbsp;000 kWh par an</strong>. Sans couverture
      thermique la consommation peut doubler par pertes nocturnes de
      rayonnement et convection.
    </p>

    <h2>&Eacute;clairage</h2>
    <p>
      L'&eacute;clairage subaquatique repr&eacute;sente une petite fraction
      de la consommation totale. Les projecteurs LED modernes consomment{" "}
      <strong>15-30 W par point</strong> contre 100-300 W pour les anciens
      halog&egrave;nes. Pour 2-4 projecteurs et 3 h d'usage quotidien en
      saison, la consommation est autour de{" "}
      <strong>30-100 kWh par an</strong>.
    </p>
    <p>
      Le passage au LED est rentable mais d'impact absolu faible&nbsp;: il
      r&eacute;duit la facture annuelle de quelques dizaines d'euros, pas de
      centaines.
    </p>

    <h2>Syst&egrave;mes d'&eacute;lectrolyse au sel</h2>
    <p>
      L'&eacute;lectrolyseur consomme <strong>150-400 W</strong> pendant son
      fonctionnement. Sur une piscine de 50 m&sup3; avec 6-8 h de
      chloration quotidiennes en saison, la consommation est de{" "}
      <strong>200-500 kWh par an</strong>.
    </p>
    <p>
      Charge notable mais contenue &mdash; &eacute;quivalente &agrave; une
      pompe de filtration efficace. Le co&ucirc;t d'exploitation se compense
      par l'&eacute;conomie sur l'achat de pastilles de chlore.
    </p>

    <h2>Tarif&nbsp;: heures creuses vs pleines</h2>
    <p>
      La facture &eacute;lectrique d&eacute;pend autant de la{" "}
      <strong>consommation (kWh)</strong> que de l'<strong>heure</strong> de
      consommation. Avec tarifs index&eacute;s ou heures creuses, le prix du
      kWh peut varier de <strong>0,08 &euro;/kWh en heures creuses</strong>{" "}
      &agrave; <strong>0,25 &euro;/kWh en pointe</strong>.
    </p>
    <p>
      Programmer la filtration et la pompe &agrave; chaleur essentiellement
      en heures creuses (00h00-08h00 et week-ends) peut r&eacute;duire la
      facture &eacute;lectrique de la piscine de <strong>25 &agrave; 40 %</strong>{" "}
      sans changer d'&eacute;quipement. Calcule ta consommation estim&eacute;e
      sur notre{" "}
      <Link to="/tools/pool-energy-calculator">calculateur &eacute;nerg&eacute;tique</Link>{" "}
      et croise avec la{" "}
      <Link to="/ficha-tecnica">fiche technique</Link> de ta piscine pour
      localiser sa classe NF EN 17645 estim&eacute;e.
    </p>
  </>
);

const Article13BodyDE = () => (
  <>
    <h2>Filterpumpe</h2>
    <p>
      Die Filterpumpe hat die meisten Betriebsstunden im Jahr und ist der{" "}
      <strong>gr&ouml;&szlig;te nicht-discretionary Stromverbraucher</strong>{" "}
      des Pools. Eine einstufige Pumpe mit <strong>0,75-1,5 PS</strong> bei
      6-8 h/Tag in der Saison verbraucht zwischen{" "}
      <strong>1.500 und 3.000 kWh pro Jahr</strong>.
    </p>
    <p>
      Eine gut programmierte <strong>drehzahlgeregelte Pumpe</strong> (VSD)
      senkt diesen Verbrauch um <strong>60-80 %</strong>, haupts&auml;chlich
      indem sie den Gro&szlig;teil der Filtrationszeit mit reduziertem
      Durchsatz l&auml;uft. Sie ist die Ma&szlig;nahme mit dem h&ouml;chsten
      wirtschaftlichen R&uuml;ckflu&szlig; in den meisten Sanierungen.
    </p>

    <h2>W&auml;rmepumpe</h2>
    <p>
      Die W&auml;rmepumpe konzentriert den Gro&szlig;teil der Stromkosten in
      beheizten Pools. Ihre Leistung wird &uuml;ber den <strong>COP</strong>{" "}
      (Coefficient of Performance) gemessen: COP 5 bedeutet, pro verbrauchter
      kWh Strom liefert sie 5 kWh Wasserw&auml;rme.
    </p>
    <p>
      Typischer Verbrauch in einem 50 m&sup3; Pool in gem&auml;&szlig;igter
      Zone, verl&auml;ngerte Saison (Mai-Oktober): <strong>2.500-5.000 kWh
      pro Jahr</strong>. Ohne Thermoabdeckung kann sich der Verbrauch durch
      n&auml;chtliche Strahlungs- und Konvektionsverluste verdoppeln.
    </p>

    <h2>Beleuchtung</h2>
    <p>
      Unterwasserbeleuchtung ist ein kleiner Anteil am Gesamtverbrauch.
      Moderne LED-Scheinwerfer nutzen <strong>15-30 W pro Punkt</strong>{" "}
      gegen&uuml;ber 100-300 W bei alten Halogenmodellen. Bei 2-4
      Scheinwerfern und 3 h t&auml;glicher Nutzung in der Saison betr&auml;gt
      der Verbrauch etwa <strong>30-100 kWh pro Jahr</strong>.
    </p>
    <p>
      LED-Umstellung ist rentabel, aber der absolute Effekt ist gering:
      Ersparnis von zehn Euro pro Jahr, nicht Hunderten.
    </p>

    <h2>Salzelektrolyse-Systeme</h2>
    <p>
      Die Salzelektrolyse zieht <strong>150-400 W</strong> im Betrieb. Bei
      einem 50 m&sup3; Pool mit 6-8 h Chlorierung t&auml;glich in der Saison
      liegt der Verbrauch bei <strong>200-500 kWh pro Jahr</strong>.
    </p>
    <p>
      Relevanter, aber begrenzter Verbrauch &mdash; vergleichbar mit einer
      effizienten Filterpumpe. Die Betriebskosten werden durch die Einsparung
      an Chlortabletten kompensiert.
    </p>

    <h2>Tarif: Schwachlast vs. Hochlast</h2>
    <p>
      Die Poolstromrechnung h&auml;ngt sowohl vom <strong>Verbrauch (kWh)</strong>{" "}
      als auch von der <strong>Uhrzeit</strong> ab. Bei Zeittarifen kann der
      kWh-Preis zwischen <strong>0,08 &euro;/kWh Schwachlast</strong> und{" "}
      <strong>0,25 &euro;/kWh Spitzenlast</strong> schwanken.
    </p>
    <p>
      Filtration und W&auml;rmepumpe &uuml;berwiegend in Schwachlaststunden
      (00:00-08:00 und Wochenenden) zu betreiben, senkt die Poolstromrechnung
      um <strong>25-40 %</strong> ohne Ger&auml;tewechsel. Berechnen Sie den
      Verbrauch in unserem{" "}
      <Link to="/tools/pool-energy-calculator">Energie-Rechner</Link> und
      gleichen Sie ihn mit dem <Link to="/ficha-tecnica">technischen
      Datenblatt</Link> Ihres Pools ab, um die gesch&auml;tzte DIN EN 17645
      Klasse zu ermitteln.
    </p>
  </>
);

const Article13BodyIT = () => (
  <>
    <h2>Pompa di filtrazione</h2>
    <p>
      La pompa di filtrazione &egrave; l'apparecchio con pi&ugrave; ore annue
      di funzionamento e il <strong>maggior consumatore elettrico non
      discrezionale</strong> della piscina. Una pompa monovelocit&agrave; da{" "}
      <strong>0,75-1,5 CV</strong> in funzione 6-8 h/giorno in stagione
      consuma tra <strong>1.500 e 3.000 kWh all'anno</strong>.
    </p>
    <p>
      Una <strong>pompa a velocit&agrave; variabile</strong> (VSD) ben
      programmata riduce quel consumo del <strong>60-80 %</strong>, lavorando
      soprattutto a portata ridotta per la maggior parte del tempo di
      filtrazione. &Egrave; l'intervento con il maggior ritorno economico in
      quasi tutte le ristrutturazioni.
    </p>

    <h2>Pompa di calore</h2>
    <p>
      La pompa di calore concentra la maggior parte della spesa elettrica in
      piscine riscaldate. Il suo rendimento si misura tramite il{" "}
      <strong>COP</strong> (Coefficient of Performance): un COP 5 significa
      che per ogni kWh elettrico consumato eroga 5 kWh termici all'acqua.
    </p>
    <p>
      Consumo tipico su piscina da 50 m&sup3; in zona temperata, stagione
      estesa (maggio-ottobre): <strong>2.500-5.000 kWh all'anno</strong>.
      Senza copertura termica il consumo pu&ograve; raddoppiare per perdite
      notturne di radiazione e convezione.
    </p>

    <h2>Illuminazione</h2>
    <p>
      L'illuminazione subacquea rappresenta una piccola frazione del consumo
      totale. I proiettori LED moderni consumano <strong>15-30 W per punto</strong>{" "}
      contro 100-300 W degli alogeni vecchi. Con 2-4 proiettori e 3 h di uso
      quotidiano in stagione, il consumo &egrave; di{" "}
      <strong>30-100 kWh all'anno</strong>.
    </p>
    <p>
      Il passaggio a LED &egrave; conveniente ma di basso impatto assoluto:
      riduce la bolletta annuale di poche decine di euro, non centinaia.
    </p>

    <h2>Sistemi di elettrolisi al sale</h2>
    <p>
      L'elettrolizzatore assorbe <strong>150-400 W</strong> in funzione. Su
      una piscina da 50 m&sup3; con 6-8 h di clorazione al giorno in
      stagione, il consumo &egrave; di <strong>200-500 kWh all'anno</strong>.
    </p>
    <p>
      Carico rilevante ma contenuto &mdash; equivalente a una pompa di
      filtrazione efficiente. Il costo operativo &egrave; compensato dal
      risparmio sull'acquisto di pastiglie di cloro.
    </p>

    <h2>Tariffa: fascia bassa vs picco</h2>
    <p>
      La bolletta elettrica dipende sia dal <strong>consumo (kWh)</strong> sia
      dall'<strong>ora</strong> in cui si consuma. Con tariffe a fasce o
      indicizzate, il prezzo del kWh pu&ograve; variare da{" "}
      <strong>0,08 &euro;/kWh in fascia bassa</strong> a{" "}
      <strong>0,25 &euro;/kWh in picco</strong>.
    </p>
    <p>
      Programmare filtrazione e pompa di calore prevalentemente in fasce
      basse (00:00-08:00 e weekend) pu&ograve; ridurre la bolletta elettrica
      della piscina del <strong>25-40 %</strong> senza cambiare
      apparecchiature. Calcola il consumo stimato nel nostro{" "}
      <Link to="/tools/pool-energy-calculator">calcolatore energetico</Link>{" "}
      e incrocia con la <Link to="/ficha-tecnica">scheda tecnica</Link> della
      tua piscina per individuare la classe UNI EN 17645 stimata.
    </p>
  </>
);

const Article13BodyNL = () => (
  <>
    <h2>Filtratiepomp</h2>
    <p>
      De filtratiepomp heeft de meeste bedrijfsuren per jaar en is de{" "}
      <strong>grootste niet-discretionaire elektriciteitsverbruiker</strong>{" "}
      van het zwembad. Een &eacute;&eacute;nsnelheidspomp van{" "}
      <strong>0,75-1,5 pk</strong> die 6-8 u/dag in seizoen draait, verbruikt
      tussen <strong>1.500 en 3.000 kWh per jaar</strong>.
    </p>
    <p>
      Een goed geprogrammeerde <strong>pomp met variabele snelheid</strong>{" "}
      (VSD) verlaagt dat verbruik met <strong>60-80 %</strong>, vooral door
      op een gereduceerd debiet te draaien gedurende het grootste deel van
      de filtratietijd. Het is de renovatie met de hoogste terugverdientijd
      in de meeste gevallen.
    </p>

    <h2>Warmtepomp</h2>
    <p>
      De warmtepomp concentreert het grootste deel van de elektriciteitskost
      in verwarmde zwembaden. De prestatie wordt gemeten via de{" "}
      <strong>COP</strong> (Coefficient of Performance): een COP van 5
      betekent dat per verbruikte kWh elektriciteit 5 kWh warmte aan het
      water wordt geleverd.
    </p>
    <p>
      Typisch verbruik voor een 50 m&sup3; zwembad in gematigde zone,
      verlengd seizoen (mei-oktober): <strong>2.500-5.000 kWh per jaar</strong>.
      Zonder thermische afdekking kan het verbruik verdubbelen door
      nachtelijke stralings- en convectieverliezen.
    </p>

    <h2>Verlichting</h2>
    <p>
      Onderwaterverlichting is een klein aandeel van het totale verbruik.
      Moderne LED-spots gebruiken <strong>15-30 W per punt</strong> tegenover
      100-300 W voor oude halogenen. Met 2-4 spots en 3 u dagelijks gebruik
      in seizoen ligt het verbruik rond <strong>30-100 kWh per jaar</strong>.
    </p>
    <p>
      Overschakelen op LED is rendabel maar heeft een kleine absolute impact:
      het verlaagt de jaarrekening met tientallen euro's, niet honderden.
    </p>

    <h2>Zoutelektrolyse-systemen</h2>
    <p>
      De zoutelektrolyse verbruikt <strong>150-400 W</strong> tijdens werking.
      In een zwembad van 50 m&sup3; met 6-8 u dagelijkse chlorering in
      seizoen ligt het verbruik tussen <strong>200 en 500 kWh per jaar</strong>.
    </p>
    <p>
      Relevante maar beperkte belasting &mdash; vergelijkbaar met een
      effici&euml;nte filtratiepomp. De bedrijfskost wordt gecompenseerd door
      besparingen op de aankoop van chloortabletten.
    </p>

    <h2>Tarief: dal- vs piekuren</h2>
    <p>
      De elektriciteitsrekening hangt af van zowel het <strong>verbruik (kWh)</strong>{" "}
      als het <strong>tijdstip</strong> van verbruik. Met dagtarieven of
      ge&iuml;ndexeerde tarieven kan de kWh-prijs vari&euml;ren van{" "}
      <strong>&euro;0,08/kWh in daluren</strong> tot{" "}
      <strong>&euro;0,25/kWh in piekuren</strong>.
    </p>
    <p>
      Filtratie en warmtepomp voornamelijk in daluren (00:00-08:00 en
      weekends) plannen kan de elektriciteitsrekening met{" "}
      <strong>25-40 %</strong> verlagen zonder apparatuur te vervangen.
      Bereken uw geschatte verbruik in onze{" "}
      <Link to="/tools/pool-energy-calculator">energiecalculator</Link> en
      vergelijk met de <Link to="/ficha-tecnica">technische fiche</Link> van
      uw zwembad om de geschatte NEN-EN 17645 klasse te vinden.
    </p>
  </>
);

// ============================================================
// ARTICLE 12 — Sal piscina (Sprint 119.111c LONG_TAIL)
// ============================================================

const Article12BodyES = () => (
  <>
    <h2>F&oacute;rmula b&aacute;sica de c&aacute;lculo</h2>
    <p>
      La cantidad de sal necesaria para una piscina con clorador salino se
      calcula sobre el <strong>volumen de agua</strong> y la{" "}
      <strong>concentraci&oacute;n objetivo</strong>. La f&oacute;rmula es:
    </p>
    <p>
      <strong>Sal (kg) = Volumen (m&sup3;) &times; Concentraci&oacute;n objetivo (g/L) / 1000</strong>
    </p>
    <p>
      La mayor&iacute;a de cloradores salinos residenciales trabajan entre{" "}
      <strong>3 y 5 g/L</strong> (3.000 a 5.000 ppm). Para una piscina de{" "}
      <strong>50 m&sup3;</strong> con objetivo 4 g/L el c&aacute;lculo es:
      50 &times; 4 = <strong>200 kg de sal</strong>. Puedes hacer el c&aacute;lculo
      con tus valores exactos en{" "}
      <Link to="/calculadora-sal-piscina">nuestra calculadora de sal</Link>.
    </p>

    <h2>Cu&aacute;ndo a&ntilde;adir sal</h2>
    <p>La sal se a&ntilde;ade en dos situaciones t&iacute;picas:</p>
    <ul>
      <li><strong>Puesta en marcha inicial</strong> &mdash; primera carga tras llenar o vaciar la piscina, se dosifica todo el c&aacute;lculo de una vez.</li>
      <li><strong>Reposici&oacute;n peri&oacute;dica</strong> &mdash; para compensar la sal perdida por salpicaduras, contralavados y evacuaciones parciales.</li>
    </ul>
    <p>
      La sal <strong>no se consume</strong> por el proceso de electr&oacute;lisis
      &mdash; se recicla continuamente entre cloro activo y cloruro s&oacute;dico.
      Solo baja por p&eacute;rdidas f&iacute;sicas de agua.
    </p>

    <h2>Errores comunes</h2>
    <ul>
      <li><strong>Confundir sal de mesa con sal para clorador</strong> &mdash; la sal para piscinas es NaCl puro &gt;99,5 %, sin antiaglomerantes ni yodo, que da&ntilde;an el electrodo.</li>
      <li><strong>A&ntilde;adir sal con el clorador encendido</strong> &mdash; puede provocar picos de cloro y da&ntilde;os al electrodo. Apagar 24 h y disolver completamente antes de reactivar.</li>
      <li><strong>Dosificar por volumen te&oacute;rico en lugar de real</strong> &mdash; una piscina que ha perdido agua por evaporaci&oacute;n tiene menos volumen del que aparece en la placa.</li>
      <li><strong>Distribuir la sal en un solo punto</strong> &mdash; conviene esparcirla por toda la superficie para acelerar la disoluci&oacute;n y evitar acumulaci&oacute;n en el fondo.</li>
    </ul>

    <h2>TDS y pH: c&oacute;mo afecta la sal</h2>
    <p>
      El <strong>TDS</strong> (Total Dissolved Solids) mide todos los s&oacute;lidos
      disueltos en el agua, incluida la sal. Con clorador salino el TDS
      objetivo est&aacute; entre <strong>3.500 y 5.500 ppm</strong>. Por encima
      de 6.000 ppm empieza a haber riesgo de corrosi&oacute;n en elementos
      met&aacute;licos (escaleras, focos, tornillos).
    </p>
    <p>
      El <strong>pH</strong> tiende a subir en piscinas con clorador salino
      por el desprendimiento de hidr&oacute;geno del proceso electrol&iacute;tico.
      Es habitual dosificar peri&oacute;dicamente reductor de pH (bisulfato
      s&oacute;dico o &aacute;cido clorh&iacute;drico dilu&iacute;do) para
      mantenerlo entre <strong>7,2 y 7,6</strong>.
    </p>

    <h2>Mantenimiento mensual</h2>
    <p>
      Revisa la concentraci&oacute;n de sal <strong>mensualmente</strong> con
      tiras reactivas o fotom&eacute;tricamente, y ajusta si baja m&aacute;s
      de un 10 % del valor recomendado por el fabricante del clorador.
      Documenta la lectura para tener un historial de deriva de TDS y detectar
      p&eacute;rdidas anormales de agua.
    </p>
    <p>
      Para dimensionar reposiciones de manera exacta usa{" "}
      <Link to="/calculadora-sal-piscina">la calculadora de sal</Link> y
      combina con la <Link to="/tools/pool-water-calculator">calculadora de
      agua</Link> si necesitas estimar el volumen actual tras evaporaci&oacute;n
      o contralavado.
    </p>
  </>
);

const Article12BodyEN = () => (
  <>
    <h2>Basic calculation formula</h2>
    <p>
      The salt quantity needed for a pool with a salt chlorinator is based on
      the <strong>water volume</strong> and the <strong>target concentration</strong>.
      The formula is:
    </p>
    <p>
      <strong>Salt (kg) = Volume (m&sup3;) &times; Target concentration (g/L) / 1000</strong>
    </p>
    <p>
      Most residential salt chlorinators work between <strong>3 and 5 g/L</strong>{" "}
      (3,000 to 5,000 ppm). For a <strong>50 m&sup3;</strong> pool with a 4 g/L
      target the calculation is: 50 &times; 4 = <strong>200 kg of salt</strong>.
      Run the exact numbers for your pool at{" "}
      <Link to="/calculadora-sal-piscina">our salt calculator</Link>.
    </p>

    <h2>When to add salt</h2>
    <p>Salt is added in two typical situations:</p>
    <ul>
      <li><strong>Initial start-up</strong> &mdash; first charge after filling or draining, the entire calculated amount goes in at once.</li>
      <li><strong>Periodic top-up</strong> &mdash; to compensate for salt lost through splashing, backwashing and partial drain-downs.</li>
    </ul>
    <p>
      Salt is <strong>not consumed</strong> by the electrolysis process &mdash;
      it cycles continuously between active chlorine and sodium chloride. It
      only drops through physical water losses.
    </p>

    <h2>Common mistakes</h2>
    <ul>
      <li><strong>Confusing table salt with pool salt</strong> &mdash; pool salt is pure NaCl &gt;99.5 %, without anti-caking agents or iodine, which damage the cell.</li>
      <li><strong>Adding salt with the chlorinator on</strong> &mdash; can cause chlorine spikes and cell damage. Turn off for 24 h and dissolve fully before restarting.</li>
      <li><strong>Dosing on theoretical volume instead of real</strong> &mdash; a pool that has lost water to evaporation has less volume than the label suggests.</li>
      <li><strong>Dropping salt at a single point</strong> &mdash; spread it over the whole surface to speed dissolution and prevent bottom accumulation.</li>
    </ul>

    <h2>TDS and pH: how salt affects them</h2>
    <p>
      <strong>TDS</strong> (Total Dissolved Solids) measures every dissolved
      solid in the water, including salt. With a salt chlorinator the target
      TDS sits between <strong>3,500 and 5,500 ppm</strong>. Above 6,000 ppm
      corrosion risk on metallic parts (ladders, lights, screws) starts to
      appear.
    </p>
    <p>
      <strong>pH</strong> tends to rise in salt-chlorinated pools because of
      hydrogen release from the electrolysis process. Periodic dosing of pH
      reducer (sodium bisulphate or diluted hydrochloric acid) is standard to
      keep it between <strong>7.2 and 7.6</strong>.
    </p>

    <h2>Monthly maintenance</h2>
    <p>
      Check the salt concentration <strong>monthly</strong> with test strips
      or photometrically, and top up when it drops more than 10 % below the
      chlorinator manufacturer's recommended level. Log every reading to keep
      a TDS drift history and detect abnormal water losses.
    </p>
    <p>
      To size top-ups accurately use the{" "}
      <Link to="/calculadora-sal-piscina">salt calculator</Link> and
      combine it with the{" "}
      <Link to="/tools/pool-water-calculator">water calculator</Link> if you
      need to estimate the current volume after evaporation or backwash.
    </p>
  </>
);

const Article12BodyFR = () => (
  <>
    <h2>Formule de calcul de base</h2>
    <p>
      La quantit&eacute; de sel n&eacute;cessaire pour une piscine avec
      &eacute;lectrolyseur au sel se calcule sur le <strong>volume d'eau</strong>{" "}
      et la <strong>concentration cible</strong>. La formule est&nbsp;:
    </p>
    <p>
      <strong>Sel (kg) = Volume (m&sup3;) &times; Concentration cible (g/L) / 1000</strong>
    </p>
    <p>
      La plupart des &eacute;lectrolyseurs r&eacute;sidentiels fonctionnent
      entre <strong>3 et 5 g/L</strong> (3&nbsp;000 &agrave; 5&nbsp;000 ppm).
      Pour une piscine de <strong>50 m&sup3;</strong> avec objectif 4 g/L le
      calcul est&nbsp;: 50 &times; 4 = <strong>200 kg de sel</strong>. Faites
      le calcul avec vos valeurs exactes sur{" "}
      <Link to="/calculadora-sal-piscina">notre calculateur de sel</Link>.
    </p>

    <h2>Quand ajouter du sel</h2>
    <p>Le sel s'ajoute dans deux situations typiques&nbsp;:</p>
    <ul>
      <li><strong>Mise en route initiale</strong> &mdash; premi&egrave;re charge apr&egrave;s remplissage ou vidange, on dose l'int&eacute;gralit&eacute; en une fois.</li>
      <li><strong>Compl&eacute;ment p&eacute;riodique</strong> &mdash; pour compenser le sel perdu par &eacute;claboussures, contre-lavages et vidanges partielles.</li>
    </ul>
    <p>
      Le sel n'est <strong>pas consomm&eacute;</strong> par le processus
      d'&eacute;lectrolyse &mdash; il se recycle en permanence entre chlore
      actif et chlorure de sodium. Il ne baisse que par pertes physiques
      d'eau.
    </p>

    <h2>Erreurs fr&eacute;quentes</h2>
    <ul>
      <li><strong>Confondre sel de table et sel pour &eacute;lectrolyseur</strong> &mdash; le sel pour piscines est du NaCl pur &gt;99,5 %, sans anti-agglom&eacute;rant ni iode, qui endommagent la cellule.</li>
      <li><strong>Ajouter du sel avec l'&eacute;lectrolyseur allum&eacute;</strong> &mdash; risque de pics de chlore et de d&eacute;g&acirc;ts &agrave; la cellule. Arr&ecirc;ter 24 h et laisser dissoudre compl&egrave;tement.</li>
      <li><strong>Doser sur le volume th&eacute;orique au lieu du r&eacute;el</strong> &mdash; une piscine ayant perdu de l'eau par &eacute;vaporation a un volume inf&eacute;rieur &agrave; celui indiqu&eacute; sur la plaque.</li>
      <li><strong>D&eacute;verser le sel en un seul point</strong> &mdash; il faut le r&eacute;partir sur toute la surface pour acc&eacute;l&eacute;rer la dissolution et &eacute;viter l'accumulation au fond.</li>
    </ul>

    <h2>TDS et pH&nbsp;: l'effet du sel</h2>
    <p>
      Le <strong>TDS</strong> (Total Dissolved Solids) mesure tous les solides
      dissous dans l'eau, y compris le sel. Avec &eacute;lectrolyseur au sel
      le TDS cible se situe entre <strong>3&nbsp;500 et 5&nbsp;500 ppm</strong>.
      Au-dessus de 6&nbsp;000 ppm appara&icirc;t un risque de corrosion des
      &eacute;l&eacute;ments m&eacute;talliques (&eacute;chelles, projecteurs,
      vis).
    </p>
    <p>
      Le <strong>pH</strong> a tendance &agrave; monter dans les piscines au
      sel &agrave; cause du d&eacute;gagement d'hydrog&egrave;ne li&eacute;
      &agrave; l'&eacute;lectrolyse. Il est habituel de doser p&eacute;riodiquement
      un r&eacute;ducteur de pH (bisulfate de sodium ou acide chlorhydrique
      dilu&eacute;) pour le maintenir entre <strong>7,2 et 7,6</strong>.
    </p>

    <h2>Entretien mensuel</h2>
    <p>
      V&eacute;rifie la concentration de sel <strong>mensuellement</strong>{" "}
      avec des bandelettes ou photom&eacute;triquement, et ajuste si elle
      baisse de plus de 10 % sous la valeur recommand&eacute;e par le fabricant
      de l'&eacute;lectrolyseur. Consigne chaque lecture pour disposer d'un
      historique de d&eacute;rive du TDS et d&eacute;tecter des pertes d'eau
      anormales.
    </p>
    <p>
      Pour dimensionner pr&eacute;cis&eacute;ment les compl&eacute;ments,
      utilise le{" "}
      <Link to="/calculadora-sal-piscina">calculateur de sel</Link> et
      combine-le avec le{" "}
      <Link to="/tools/pool-water-calculator">calculateur d'eau</Link> si tu
      dois estimer le volume actuel apr&egrave;s &eacute;vaporation ou
      contre-lavage.
    </p>
  </>
);

const Article12BodyDE = () => (
  <>
    <h2>Grundformel</h2>
    <p>
      Die Salzmenge f&uuml;r einen Pool mit Salzelektrolyse berechnet sich
      aus <strong>Wasservolumen</strong> und <strong>Zielkonzentration</strong>.
      Die Formel lautet:
    </p>
    <p>
      <strong>Salz (kg) = Volumen (m&sup3;) &times; Zielkonzentration (g/L) / 1000</strong>
    </p>
    <p>
      Die meisten privaten Salzelektrolysen arbeiten zwischen <strong>3 und 5
      g/L</strong> (3.000 bis 5.000 ppm). F&uuml;r einen <strong>50 m&sup3;</strong>
      Pool mit Ziel 4 g/L: 50 &times; 4 = <strong>200 kg Salz</strong>. Berechnen
      Sie Ihre exakten Werte in unserem{" "}
      <Link to="/calculadora-sal-piscina">Salzrechner</Link>.
    </p>

    <h2>Wann Salz nachf&uuml;llen</h2>
    <p>Salz wird in zwei typischen Situationen zugef&uuml;hrt:</p>
    <ul>
      <li><strong>Erstinbetriebnahme</strong> &mdash; erste Ladung nach F&uuml;llen oder Entleeren, gesamte berechnete Menge auf einmal.</li>
      <li><strong>Regelm&auml;&szlig;iges Nachf&uuml;llen</strong> &mdash; um Salzverluste durch Spritzwasser, R&uuml;cksp&uuml;lungen und Teilentleerungen auszugleichen.</li>
    </ul>
    <p>
      Salz wird durch die Elektrolyse <strong>nicht verbraucht</strong>{" "}
      &mdash; es zirkuliert st&auml;ndig zwischen aktivem Chlor und Natriumchlorid.
      Es sinkt nur durch physische Wasserverluste.
    </p>

    <h2>H&auml;ufige Fehler</h2>
    <ul>
      <li><strong>Speisesalz mit Poolsalz verwechseln</strong> &mdash; Poolsalz ist reines NaCl &gt;99,5 %, ohne Rieselhilfen oder Jod, die die Zelle sch&auml;digen.</li>
      <li><strong>Salz bei laufender Elektrolyse zugeben</strong> &mdash; kann Chlorspitzen und Zellensch&auml;den verursachen. 24 h abschalten und vollst&auml;ndig aufl&ouml;sen lassen.</li>
      <li><strong>Dosierung nach theoretischem statt realem Volumen</strong> &mdash; ein Pool mit Verdunstungsverlust hat weniger Volumen als das Typenschild angibt.</li>
      <li><strong>Salz an einer Stelle sch&uuml;tten</strong> &mdash; auf der gesamten Oberfl&auml;che verteilen, um Aufl&ouml;sung zu beschleunigen und Ablagerung am Boden zu vermeiden.</li>
    </ul>

    <h2>TDS und pH: Salzwirkung</h2>
    <p>
      Der <strong>TDS-Wert</strong> (Total Dissolved Solids) misst alle im
      Wasser gel&ouml;sten Feststoffe, einschlie&szlig;lich Salz. Bei
      Salzelektrolyse liegt der Ziel-TDS zwischen{" "}
      <strong>3.500 und 5.500 ppm</strong>. Ab 6.000 ppm besteht Korrosionsrisiko
      f&uuml;r metallische Bauteile (Leitern, Scheinwerfer, Schrauben).
    </p>
    <p>
      Der <strong>pH-Wert</strong> steigt in Salzpools durch Wasserstofffreisetzung
      w&auml;hrend der Elektrolyse. &Uuml;blich ist die regelm&auml;&szlig;ige
      Dosierung von pH-Senker (Natriumbisulfat oder verd&uuml;nnte Salzs&auml;ure)
      zur Aufrechterhaltung zwischen <strong>7,2 und 7,6</strong>.
    </p>

    <h2>Monatliche Wartung</h2>
    <p>
      Pr&uuml;fen Sie die Salzkonzentration <strong>monatlich</strong> mit
      Teststreifen oder photometrisch und f&uuml;llen Sie nach, wenn sie mehr
      als 10 % unter den vom Elektrolyse-Hersteller empfohlenen Wert f&auml;llt.
      Dokumentieren Sie jede Messung, um einen TDS-Verlauf zu f&uuml;hren und
      abnorme Wasserverluste zu erkennen.
    </p>
    <p>
      Zur genauen Dimensionierung der Nachf&uuml;llung nutzen Sie den{" "}
      <Link to="/calculadora-sal-piscina">Salzrechner</Link> und
      kombinieren Sie mit dem{" "}
      <Link to="/tools/pool-water-calculator">Wasserrechner</Link>, wenn Sie
      das aktuelle Volumen nach Verdunstung oder R&uuml;cksp&uuml;lung
      absch&auml;tzen m&uuml;ssen.
    </p>
  </>
);

const Article12BodyIT = () => (
  <>
    <h2>Formula base di calcolo</h2>
    <p>
      La quantit&agrave; di sale necessaria per una piscina con elettrolisi
      al sale si calcola sul <strong>volume d'acqua</strong> e sulla{" "}
      <strong>concentrazione obiettivo</strong>. La formula &egrave;:
    </p>
    <p>
      <strong>Sale (kg) = Volume (m&sup3;) &times; Concentrazione obiettivo (g/L) / 1000</strong>
    </p>
    <p>
      La maggior parte degli elettrolizzatori residenziali lavora tra{" "}
      <strong>3 e 5 g/L</strong> (3.000-5.000 ppm). Per una piscina da{" "}
      <strong>50 m&sup3;</strong> con obiettivo 4 g/L il calcolo &egrave;:
      50 &times; 4 = <strong>200 kg di sale</strong>. Puoi effettuare il
      calcolo con i tuoi valori esatti nel nostro{" "}
      <Link to="/calculadora-sal-piscina">calcolatore di sale</Link>.
    </p>

    <h2>Quando aggiungere sale</h2>
    <p>Il sale si aggiunge in due situazioni tipiche:</p>
    <ul>
      <li><strong>Messa in servizio iniziale</strong> &mdash; prima carica dopo riempimento o svuotamento, intera quantit&agrave; calcolata in una volta.</li>
      <li><strong>Reintegro periodico</strong> &mdash; per compensare il sale perso per schizzi, controlavaggi e svuotamenti parziali.</li>
    </ul>
    <p>
      Il sale <strong>non viene consumato</strong> dal processo di elettrolisi
      &mdash; circola continuamente tra cloro attivo e cloruro di sodio.
      Diminuisce solo per perdite fisiche d'acqua.
    </p>

    <h2>Errori comuni</h2>
    <ul>
      <li><strong>Confondere sale da tavola con sale per elettrolisi</strong> &mdash; il sale per piscine &egrave; NaCl puro &gt;99,5 %, senza antiagglomeranti o iodio, che danneggiano la cellula.</li>
      <li><strong>Aggiungere sale con l'elettrolizzatore acceso</strong> &mdash; pu&ograve; provocare picchi di cloro e danni alla cellula. Spegnere 24 h e sciogliere completamente prima di riavviare.</li>
      <li><strong>Dosare sul volume teorico invece che reale</strong> &mdash; una piscina che ha perso acqua per evaporazione ha meno volume di quello dichiarato in targa.</li>
      <li><strong>Versare il sale in un unico punto</strong> &mdash; distribuirlo su tutta la superficie per accelerare la dissoluzione ed evitare accumulo sul fondo.</li>
    </ul>

    <h2>TDS e pH: come agisce il sale</h2>
    <p>
      Il <strong>TDS</strong> (Total Dissolved Solids) misura tutti i solidi
      disciolti in acqua, sale incluso. Con elettrolisi al sale il TDS
      obiettivo &egrave; tra <strong>3.500 e 5.500 ppm</strong>. Oltre i 6.000
      ppm si presenta rischio di corrosione su elementi metallici (scale,
      proiettori, viti).
    </p>
    <p>
      Il <strong>pH</strong> tende a salire nelle piscine con elettrolisi
      per il rilascio di idrogeno del processo. &Egrave; abituale dosare
      periodicamente riduttore di pH (bisolfato di sodio o acido cloridrico
      diluito) per mantenerlo tra <strong>7,2 e 7,6</strong>.
    </p>

    <h2>Manutenzione mensile</h2>
    <p>
      Controlla la concentrazione di sale <strong>mensilmente</strong> con
      strisce reattive o fotometricamente, e reintegra se scende oltre il 10 %
      sotto il valore raccomandato dal produttore dell'elettrolizzatore.
      Registra ogni lettura per avere uno storico di deriva del TDS e
      rilevare perdite d'acqua anomale.
    </p>
    <p>
      Per dimensionare con precisione i reintegri usa il{" "}
      <Link to="/calculadora-sal-piscina">calcolatore di sale</Link> e
      combinalo con il{" "}
      <Link to="/tools/pool-water-calculator">calcolatore d'acqua</Link> se
      devi stimare il volume attuale dopo evaporazione o controlavaggio.
    </p>
  </>
);

const Article12BodyNL = () => (
  <>
    <h2>Basisformule</h2>
    <p>
      De hoeveelheid zout nodig voor een zwembad met zoutelektrolyse wordt
      berekend op basis van het <strong>watervolume</strong> en de{" "}
      <strong>doelconcentratie</strong>. De formule is:
    </p>
    <p>
      <strong>Zout (kg) = Volume (m&sup3;) &times; Doelconcentratie (g/L) / 1000</strong>
    </p>
    <p>
      De meeste residenti&euml;le zoutelektrolysecellen werken tussen{" "}
      <strong>3 en 5 g/L</strong> (3.000-5.000 ppm). Voor een zwembad van{" "}
      <strong>50 m&sup3;</strong> met doel 4 g/L: 50 &times; 4 =
      <strong>200 kg zout</strong>. Bereken uw exacte waarden in onze{" "}
      <Link to="/calculadora-sal-piscina">zoutcalculator</Link>.
    </p>

    <h2>Wanneer zout toevoegen</h2>
    <p>Zout wordt in twee typische situaties toegevoegd:</p>
    <ul>
      <li><strong>Eerste ingebruikname</strong> &mdash; eerste lading na vullen of legen, volledige berekende hoeveelheid ineens.</li>
      <li><strong>Periodieke aanvulling</strong> &mdash; om verloren zout door spatten, terugspoelen en gedeeltelijke afvoer te compenseren.</li>
    </ul>
    <p>
      Zout wordt <strong>niet verbruikt</strong> door het elektrolyseproces
      &mdash; het circuleert continu tussen actief chloor en natriumchloride.
      Het daalt alleen door fysieke waterverliezen.
    </p>

    <h2>Veelgemaakte fouten</h2>
    <ul>
      <li><strong>Tafelzout verwarren met zwembadzout</strong> &mdash; zwembadzout is zuiver NaCl &gt;99,5 %, zonder antiklontermiddelen of jodium, die de cel beschadigen.</li>
      <li><strong>Zout toevoegen met de elektrolyse aan</strong> &mdash; kan chloorpieken en celschade veroorzaken. 24 u uitschakelen en volledig laten oplossen v&oacute;&oacute;r herstart.</li>
      <li><strong>Doseren op theoretisch in plaats van re&euml;el volume</strong> &mdash; een zwembad met verdampingsverlies heeft minder volume dan het typeplaatje aangeeft.</li>
      <li><strong>Zout op &eacute;&eacute;n plek gieten</strong> &mdash; verspreid over het volledige oppervlak om oplossing te versnellen en bodemaccumulatie te vermijden.</li>
    </ul>

    <h2>TDS en pH: effect van zout</h2>
    <p>
      De <strong>TDS-waarde</strong> (Total Dissolved Solids) meet alle opgeloste
      vaste stoffen in het water, inclusief zout. Met zoutelektrolyse ligt de
      doel-TDS tussen <strong>3.500 en 5.500 ppm</strong>. Boven 6.000 ppm
      ontstaat corrosierisico voor metalen onderdelen (ladders, spots, schroeven).
    </p>
    <p>
      De <strong>pH</strong> stijgt vaak in zoutzwembaden door de waterstofafgifte
      tijdens elektrolyse. Het is gebruikelijk om periodiek een pH-verlager
      (natriumbisulfaat of verdund zoutzuur) te doseren om deze tussen{" "}
      <strong>7,2 en 7,6</strong> te houden.
    </p>

    <h2>Maandelijks onderhoud</h2>
    <p>
      Controleer de zoutconcentratie <strong>maandelijks</strong> met
      teststrips of fotometrisch, en vul aan wanneer deze meer dan 10 %
      onder de door de elektrolysefabrikant aanbevolen waarde daalt. Noteer
      elke meting om een TDS-verloop bij te houden en abnormale
      waterverliezen te detecteren.
    </p>
    <p>
      Voor nauwkeurige dimensionering van aanvullingen gebruikt u de{" "}
      <Link to="/calculadora-sal-piscina">zoutcalculator</Link> en
      combineert u met de{" "}
      <Link to="/tools/pool-water-calculator">watercalculator</Link> als u
      het huidige volume na verdamping of terugspoelen moet inschatten.
    </p>
  </>
);

// ============================================================
// ARTICLE 11 — PoolLex ID explicado (Sprint 119.111c LONG_TAIL)
// ============================================================

const Article11BodyES = () => (
  <>
    <h2>Qu&eacute; es el PoolLex ID</h2>
    <p>
      El <strong>PoolLex ID</strong> es el <strong>identificador &uacute;nico
      europeo</strong> que PoolLex.io asigna a cada piscina residencial cuando
      se genera su ficha t&eacute;cnica. Es un c&oacute;digo estable, no
      reutilizable, que acompa&ntilde;a a la piscina a lo largo de todo su
      ciclo de vida: instalaci&oacute;n, mantenimiento, reforma, compraventa
      y baja.
    </p>
    <p>
      Su funci&oacute;n es dar un <strong>punto de referencia com&uacute;n</strong>{" "}
      a todos los actores que necesitan hablar de la misma piscina:
      propietario, instalador, mantenedor, notario, administraci&oacute;n
      municipal, asegurador y comprador potencial.
    </p>

    <h2>Formato PLX-XXXXXXX</h2>
    <p>
      El identificador tiene el formato <strong>PLX-XXXXXXX</strong>:
    </p>
    <ul>
      <li><strong>PLX</strong> &mdash; prefijo fijo de PoolLex.io, marca la emisi&oacute;n europea.</li>
      <li><strong>XXXXXXX</strong> &mdash; 7 d&iacute;gitos num&eacute;ricos correlativos, capacidad de 10 millones de piscinas &uacute;nicas.</li>
    </ul>
    <p>
      Ejemplo real: <strong>PLX-0000001</strong> es la piscina demo oficial de
      PoolLex.io. El formato es intencionadamente corto y legible para poder
      transcribirlo en un contrato en papel, un correo o incluso a mano.
    </p>

    <h2>C&oacute;mo se obtiene</h2>
    <p>
      El PoolLex ID se emite <strong>gratis, sin registro y en 30 segundos</strong>{" "}
      cuando generas la ficha t&eacute;cnica de tu piscina. El proceso:
    </p>
    <ul>
      <li>Vas a <Link to="/ficha-tecnica">/ficha-tecnica</Link>.</li>
      <li>Declaras 6-8 datos b&aacute;sicos (dimensiones, ubicaci&oacute;n, equipamiento).</li>
      <li>El sistema genera el PDF con el PoolLex ID impreso.</li>
    </ul>
    <p>
      No hay coste, no hay registro obligatorio y el PDF es descargable
      inmediatamente. La emisi&oacute;n del ID es un acto declarativo del
      propietario &mdash; no verifica f&iacute;sicamente la piscina, sino que
      cataloga los datos aportados.
    </p>

    <h2>Usos pr&aacute;cticos</h2>
    <p>El PoolLex ID sirve como <strong>referencia t&eacute;cnica estable</strong> en:</p>
    <ul>
      <li><strong>Compraventa inmobiliaria</strong> &mdash; aporta al comprador informaci&oacute;n verificable sobre la piscina incluida en la vivienda.</li>
      <li><strong>Solicitud de ayudas de eficiencia</strong> &mdash; permite documentar el estado previo antes de una reforma subvencionada.</li>
      <li><strong>Contratos de mantenimiento</strong> &mdash; identifica la piscina en el contrato, evitando ambig&uuml;edades entre varias piscinas gestionadas por el mismo mantenedor.</li>
      <li><strong>Tr&aacute;mites urban&iacute;sticos</strong> &mdash; aporta un identificador com&uacute;n a las administraciones locales.</li>
      <li><strong>Registro asegurador</strong> &mdash; simplifica la vinculaci&oacute;n entre p&oacute;liza y activo asegurado.</li>
    </ul>

    <h2>Analog&iacute;a: VIN del coche y referencia catastral</h2>
    <p>
      El PoolLex ID cumple para una piscina la misma funci&oacute;n que el{" "}
      <strong>VIN</strong> (Vehicle Identification Number) para un coche o la{" "}
      <strong>referencia catastral</strong> para una parcela: es un c&oacute;digo
      &uacute;nico e inmutable que identifica un activo concreto y permite
      seguir su historial.
    </p>
    <p>
      A diferencia del VIN &mdash; que asigna el fabricante &mdash; el PoolLex
      ID lo emite PoolLex.io como <strong>tercera parte neutral</strong>, no
      vinculada a ning&uacute;n fabricante ni instalador. Esta neutralidad es
      clave para que el identificador sea aceptado por todos los actores del
      ecosistema.
    </p>
    <p>
      Para obtener el tuyo, entra en <Link to="/ficha-tecnica">/ficha-tecnica</Link>{" "}
      y genera la ficha t&eacute;cnica en menos de un minuto.
    </p>
  </>
);

const Article11BodyEN = () => (
  <>
    <h2>What the PoolLex ID is</h2>
    <p>
      The <strong>PoolLex ID</strong> is the <strong>unique European
      identifier</strong> PoolLex.io assigns to every residential pool when its
      technical sheet is generated. It is a stable, non-reusable code that
      accompanies the pool through its entire life cycle: installation,
      maintenance, refurbishment, sale and decommissioning.
    </p>
    <p>
      Its role is to give a <strong>common reference point</strong> to every
      actor who needs to talk about the same pool: owner, installer, service
      provider, notary, municipal authority, insurer and prospective buyer.
    </p>

    <h2>PLX-XXXXXXX format</h2>
    <p>
      The identifier follows the format <strong>PLX-XXXXXXX</strong>:
    </p>
    <ul>
      <li><strong>PLX</strong> &mdash; fixed PoolLex.io prefix, marks European issuance.</li>
      <li><strong>XXXXXXX</strong> &mdash; 7 correlative numeric digits, room for 10 million unique pools.</li>
    </ul>
    <p>
      Real example: <strong>PLX-0000001</strong> is PoolLex.io's official demo
      pool. The format is deliberately short and legible so it can be written
      on a paper contract, an email or even by hand.
    </p>

    <h2>How to obtain it</h2>
    <p>
      The PoolLex ID is issued <strong>free, without registration, in 30 seconds</strong>{" "}
      when you generate your pool's technical sheet. The process:
    </p>
    <ul>
      <li>Visit <Link to="/ficha-tecnica">/ficha-tecnica</Link>.</li>
      <li>Declare 6-8 basic data points (dimensions, location, equipment).</li>
      <li>The system generates the PDF with the PoolLex ID printed on it.</li>
    </ul>
    <p>
      There is no cost, no mandatory registration and the PDF is immediately
      downloadable. Issuing the ID is a declarative act by the owner &mdash;
      it does not physically inspect the pool, it catalogues the data
      provided.
    </p>

    <h2>Practical uses</h2>
    <p>The PoolLex ID serves as a <strong>stable technical reference</strong> in:</p>
    <ul>
      <li><strong>Property transactions</strong> &mdash; gives the buyer verifiable information about the pool included with the house.</li>
      <li><strong>Efficiency grant applications</strong> &mdash; lets you document the baseline state before a subsidised refurbishment.</li>
      <li><strong>Maintenance contracts</strong> &mdash; identifies the pool in the contract, avoiding ambiguity between several pools handled by the same service company.</li>
      <li><strong>Urban permits</strong> &mdash; provides a shared identifier for local authorities.</li>
      <li><strong>Insurance registration</strong> &mdash; simplifies linking policy to insured asset.</li>
    </ul>

    <h2>Analogy: car VIN and cadastral reference</h2>
    <p>
      The PoolLex ID plays for a pool the same role the <strong>VIN</strong>{" "}
      (Vehicle Identification Number) plays for a car or the{" "}
      <strong>cadastral reference</strong> for a plot: a unique, immutable
      code that identifies a specific asset and lets you follow its history.
    </p>
    <p>
      Unlike the VIN &mdash; which is assigned by the manufacturer &mdash; the
      PoolLex ID is issued by PoolLex.io as a <strong>neutral third party</strong>,
      not tied to any manufacturer or installer. That neutrality is key for
      the identifier to be accepted by every actor in the ecosystem.
    </p>
    <p>
      To get yours, go to <Link to="/ficha-tecnica">/ficha-tecnica</Link> and
      generate the technical sheet in under a minute.
    </p>
  </>
);

const Article11BodyFR = () => (
  <>
    <h2>Qu'est-ce que le PoolLex ID</h2>
    <p>
      Le <strong>PoolLex ID</strong> est l'<strong>identifiant unique
      europ&eacute;en</strong> attribu&eacute; par PoolLex.io &agrave; chaque
      piscine r&eacute;sidentielle lors de la g&eacute;n&eacute;ration de sa
      fiche technique. C'est un code stable, non r&eacute;utilisable, qui
      accompagne la piscine tout au long de son cycle de vie: installation,
      maintenance, r&eacute;novation, vente et mise hors service.
    </p>
    <p>
      Son r&ocirc;le est de fournir un <strong>point de r&eacute;f&eacute;rence
      commun</strong> &agrave; tous les acteurs qui doivent parler de la
      m&ecirc;me piscine: propri&eacute;taire, installateur, mainteneur,
      notaire, administration municipale, assureur et acheteur potentiel.
    </p>

    <h2>Format PLX-XXXXXXX</h2>
    <p>
      L'identifiant suit le format <strong>PLX-XXXXXXX</strong>:
    </p>
    <ul>
      <li><strong>PLX</strong> &mdash; pr&eacute;fixe fixe de PoolLex.io, marque l'&eacute;mission europ&eacute;enne.</li>
      <li><strong>XXXXXXX</strong> &mdash; 7 chiffres num&eacute;riques cons&eacute;cutifs, capacit&eacute; de 10 millions de piscines uniques.</li>
    </ul>
    <p>
      Exemple r&eacute;el&nbsp;: <strong>PLX-0000001</strong> est la piscine
      d&eacute;monstration officielle de PoolLex.io. Le format est
      d&eacute;lib&eacute;r&eacute;ment court et lisible pour pouvoir &ecirc;tre
      transcrit sur un contrat papier, un e-mail ou m&ecirc;me &agrave; la main.
    </p>

    <h2>Comment l'obtenir</h2>
    <p>
      Le PoolLex ID est &eacute;mis <strong>gratuitement, sans inscription et
      en 30 secondes</strong> lors de la g&eacute;n&eacute;ration de la fiche
      technique de votre piscine. Le processus&nbsp;:
    </p>
    <ul>
      <li>Rendez-vous sur <Link to="/ficha-tecnica">/ficha-tecnica</Link>.</li>
      <li>D&eacute;clarez 6-8 donn&eacute;es de base (dimensions, localisation, &eacute;quipement).</li>
      <li>Le syst&egrave;me g&eacute;n&egrave;re le PDF avec le PoolLex ID imprim&eacute;.</li>
    </ul>
    <p>
      Aucun co&ucirc;t, aucune inscription obligatoire, PDF t&eacute;l&eacute;chargeable
      imm&eacute;diatement. L'&eacute;mission de l'ID est un acte d&eacute;claratif
      du propri&eacute;taire &mdash; elle ne v&eacute;rifie pas la piscine
      physiquement, elle catalogue les donn&eacute;es fournies.
    </p>

    <h2>Usages pratiques</h2>
    <p>Le PoolLex ID sert de <strong>r&eacute;f&eacute;rence technique stable</strong> pour&nbsp;:</p>
    <ul>
      <li><strong>Transactions immobili&egrave;res</strong> &mdash; donne &agrave; l'acheteur une information v&eacute;rifiable sur la piscine incluse dans le bien.</li>
      <li><strong>Demandes d'aides &agrave; l'efficacit&eacute;</strong> &mdash; permet de documenter l'&eacute;tat initial avant une r&eacute;novation subventionn&eacute;e.</li>
      <li><strong>Contrats de maintenance</strong> &mdash; identifie la piscine dans le contrat, &eacute;vitant les ambigu&iuml;t&eacute;s entre plusieurs bassins g&eacute;r&eacute;s par la m&ecirc;me entreprise.</li>
      <li><strong>D&eacute;marches d'urbanisme</strong> &mdash; fournit un identifiant partag&eacute; aux collectivit&eacute;s locales.</li>
      <li><strong>Enregistrement assureur</strong> &mdash; simplifie le lien entre police et actif assur&eacute;.</li>
    </ul>

    <h2>Analogie&nbsp;: VIN automobile et r&eacute;f&eacute;rence cadastrale</h2>
    <p>
      Le PoolLex ID joue pour une piscine le m&ecirc;me r&ocirc;le que le{" "}
      <strong>VIN</strong> (Vehicle Identification Number) pour une voiture ou
      la <strong>r&eacute;f&eacute;rence cadastrale</strong> pour une parcelle&nbsp;:
      un code unique et immuable qui identifie un actif concret et permet de
      suivre son historique.
    </p>
    <p>
      Contrairement au VIN &mdash; attribu&eacute; par le fabricant &mdash; le
      PoolLex ID est &eacute;mis par PoolLex.io en tant que{" "}
      <strong>tierce partie neutre</strong>, non li&eacute;e &agrave; un
      fabricant ni &agrave; un installateur. Cette neutralit&eacute; est
      d&eacute;terminante pour que l'identifiant soit accept&eacute; par tous
      les acteurs de l'&eacute;cosyst&egrave;me.
    </p>
    <p>
      Pour obtenir le v&ocirc;tre, rendez-vous sur{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> et g&eacute;n&eacute;rez
      la fiche technique en moins d'une minute.
    </p>
  </>
);

const Article11BodyDE = () => (
  <>
    <h2>Was ist die PoolLex ID</h2>
    <p>
      Die <strong>PoolLex ID</strong> ist die <strong>eindeutige europ&auml;ische
      Kennung</strong>, die PoolLex.io jedem Privatpool bei Erstellung seines
      technischen Datenblatts zuweist. Es ist ein stabiler, nicht wiederverwendbarer
      Code, der den Pool durch seinen gesamten Lebenszyklus begleitet:
      Installation, Wartung, Sanierung, Verkauf und Au&szlig;erbetriebnahme.
    </p>
    <p>
      Ihre Aufgabe: allen Beteiligten einen <strong>gemeinsamen Referenzpunkt</strong>{" "}
      zu geben &mdash; Eigent&uuml;mer, Installateur, Wartungsfirma, Notar,
      Kommune, Versicherer und potenzieller K&auml;ufer.
    </p>

    <h2>Format PLX-XXXXXXX</h2>
    <p>
      Die Kennung folgt dem Format <strong>PLX-XXXXXXX</strong>:
    </p>
    <ul>
      <li><strong>PLX</strong> &mdash; fester PoolLex.io-Pr&auml;fix, markiert die europ&auml;ische Ausgabe.</li>
      <li><strong>XXXXXXX</strong> &mdash; 7 fortlaufende numerische Ziffern, Kapazit&auml;t f&uuml;r 10 Millionen einzigartige Pools.</li>
    </ul>
    <p>
      Reales Beispiel: <strong>PLX-0000001</strong> ist der offizielle Demo-Pool
      von PoolLex.io. Das Format ist bewusst kurz und lesbar, damit es auf
      Papiervertr&auml;gen, in E-Mails oder sogar handschriftlich
      &uuml;bertragen werden kann.
    </p>

    <h2>Wie erh&auml;lt man sie</h2>
    <p>
      Die PoolLex ID wird <strong>kostenlos, ohne Registrierung und in 30
      Sekunden</strong> ausgegeben, wenn Sie das technische Datenblatt Ihres
      Pools erstellen. Der Ablauf:
    </p>
    <ul>
      <li>Besuchen Sie <Link to="/ficha-tecnica">/ficha-tecnica</Link>.</li>
      <li>Deklarieren Sie 6-8 Basisdaten (Abmessungen, Standort, Ausstattung).</li>
      <li>Das System erzeugt das PDF mit der aufgedruckten PoolLex ID.</li>
    </ul>
    <p>
      Keine Kosten, keine verpflichtende Registrierung, PDF sofort verf&uuml;gbar.
      Die Ausgabe der ID ist ein deklarativer Akt des Eigent&uuml;mers &mdash;
      sie pr&uuml;ft den Pool nicht physisch, sondern katalogisiert die
      angegebenen Daten.
    </p>

    <h2>Praktische Anwendungen</h2>
    <p>Die PoolLex ID dient als <strong>stabile technische Referenz</strong> f&uuml;r:</p>
    <ul>
      <li><strong>Immobilientransaktionen</strong> &mdash; liefert dem K&auml;ufer &uuml;berpr&uuml;fbare Informationen zum Pool im Kaufobjekt.</li>
      <li><strong>Effizienzf&ouml;rderung</strong> &mdash; erlaubt die Dokumentation des Ausgangszustands vor einer gef&ouml;rderten Sanierung.</li>
      <li><strong>Wartungsvertr&auml;ge</strong> &mdash; identifiziert den Pool im Vertrag und vermeidet Verwechslungen zwischen mehreren von derselben Firma betreuten Pools.</li>
      <li><strong>Baubeh&ouml;rdliche Verfahren</strong> &mdash; bietet Kommunen eine gemeinsame Kennung.</li>
      <li><strong>Versicherungserfassung</strong> &mdash; vereinfacht die Verkn&uuml;pfung von Police und versichertem Objekt.</li>
    </ul>

    <h2>Analogie: Fahrzeug-VIN und Grundbuch-Referenz</h2>
    <p>
      Die PoolLex ID erf&uuml;llt f&uuml;r einen Pool dieselbe Rolle wie die{" "}
      <strong>VIN</strong> (Vehicle Identification Number) f&uuml;r ein Auto
      oder die <strong>Grundbuch-Referenz</strong> f&uuml;r ein Grundst&uuml;ck:
      ein eindeutiger, unver&auml;nderlicher Code, der ein konkretes Objekt
      identifiziert und seine Historie nachvollziehbar macht.
    </p>
    <p>
      Anders als die VIN &mdash; die vom Hersteller vergeben wird &mdash;
      erteilt PoolLex.io die PoolLex ID als <strong>neutrale Drittpartei</strong>,
      nicht gebunden an einen Hersteller oder Installateur. Diese Neutralit&auml;t
      ist entscheidend f&uuml;r die Akzeptanz durch alle Akteure des &Ouml;kosystems.
    </p>
    <p>
      F&uuml;r Ihre eigene ID besuchen Sie{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> und erstellen Sie das
      Datenblatt in unter einer Minute.
    </p>
  </>
);

const Article11BodyIT = () => (
  <>
    <h2>Cos'&egrave; il PoolLex ID</h2>
    <p>
      Il <strong>PoolLex ID</strong> &egrave; l'<strong>identificatore unico
      europeo</strong> che PoolLex.io assegna a ogni piscina residenziale al
      momento della generazione della scheda tecnica. &Egrave; un codice
      stabile, non riutilizzabile, che accompagna la piscina lungo tutto il
      suo ciclo di vita: installazione, manutenzione, ristrutturazione,
      compravendita e dismissione.
    </p>
    <p>
      La sua funzione &egrave; fornire un <strong>punto di riferimento comune</strong>{" "}
      a tutti gli attori che devono parlare della stessa piscina:
      proprietario, installatore, manutentore, notaio, amministrazione
      comunale, assicuratore e potenziale acquirente.
    </p>

    <h2>Formato PLX-XXXXXXX</h2>
    <p>
      L'identificatore segue il formato <strong>PLX-XXXXXXX</strong>:
    </p>
    <ul>
      <li><strong>PLX</strong> &mdash; prefisso fisso di PoolLex.io, indica l'emissione europea.</li>
      <li><strong>XXXXXXX</strong> &mdash; 7 cifre numeriche consecutive, capacit&agrave; per 10 milioni di piscine uniche.</li>
    </ul>
    <p>
      Esempio reale: <strong>PLX-0000001</strong> &egrave; la piscina demo
      ufficiale di PoolLex.io. Il formato &egrave; intenzionalmente corto e
      leggibile per poter essere trascritto su un contratto cartaceo, una
      e-mail o anche a mano.
    </p>

    <h2>Come si ottiene</h2>
    <p>
      Il PoolLex ID viene emesso <strong>gratis, senza registrazione, in 30
      secondi</strong> quando si genera la scheda tecnica della piscina. Il
      procedimento:
    </p>
    <ul>
      <li>Vai su <Link to="/ficha-tecnica">/ficha-tecnica</Link>.</li>
      <li>Dichiari 6-8 dati di base (dimensioni, ubicazione, attrezzatura).</li>
      <li>Il sistema genera il PDF con il PoolLex ID stampato.</li>
    </ul>
    <p>
      Nessun costo, nessuna registrazione obbligatoria, PDF scaricabile
      subito. L'emissione dell'ID &egrave; un atto dichiarativo del
      proprietario &mdash; non verifica fisicamente la piscina, ma cataloga
      i dati forniti.
    </p>

    <h2>Usi pratici</h2>
    <p>Il PoolLex ID funziona come <strong>riferimento tecnico stabile</strong> in:</p>
    <ul>
      <li><strong>Transazioni immobiliari</strong> &mdash; fornisce all'acquirente informazioni verificabili sulla piscina inclusa nell'immobile.</li>
      <li><strong>Richieste di incentivi di efficienza</strong> &mdash; permette di documentare lo stato iniziale prima di una ristrutturazione incentivata.</li>
      <li><strong>Contratti di manutenzione</strong> &mdash; identifica la piscina nel contratto, evitando ambiguit&agrave; tra pi&ugrave; piscine gestite dalla stessa impresa.</li>
      <li><strong>Pratiche urbanistiche</strong> &mdash; offre alle amministrazioni locali un identificatore condiviso.</li>
      <li><strong>Registrazione assicurativa</strong> &mdash; semplifica il collegamento tra polizza e bene assicurato.</li>
    </ul>

    <h2>Analogia: VIN dell'auto e riferimento catastale</h2>
    <p>
      Il PoolLex ID svolge per una piscina lo stesso ruolo del{" "}
      <strong>VIN</strong> (Vehicle Identification Number) per un'auto o del{" "}
      <strong>riferimento catastale</strong> per una particella: un codice
      unico e immutabile che identifica un bene concreto e ne consente il
      tracciamento storico.
    </p>
    <p>
      A differenza del VIN &mdash; attribuito dal produttore &mdash; il
      PoolLex ID viene emesso da PoolLex.io come <strong>terza parte
      neutrale</strong>, non legata ad alcun produttore o installatore. Questa
      neutralit&agrave; &egrave; determinante affinch&eacute; l'identificatore
      sia accettato da tutti gli attori dell'ecosistema.
    </p>
    <p>
      Per ottenere il tuo, vai su{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> e genera la scheda
      tecnica in meno di un minuto.
    </p>
  </>
);

const Article11BodyNL = () => (
  <>
    <h2>Wat is de PoolLex ID</h2>
    <p>
      De <strong>PoolLex ID</strong> is de <strong>unieke Europese
      identificatie</strong> die PoolLex.io aan elk residentieel zwembad
      toekent bij het genereren van de technische fiche. Het is een stabiele,
      niet-hergebruikbare code die het zwembad tijdens zijn hele levenscyclus
      vergezelt: installatie, onderhoud, renovatie, verkoop en
      buitengebruikstelling.
    </p>
    <p>
      De functie ervan is om een <strong>gemeenschappelijk referentiepunt</strong>{" "}
      te bieden aan alle actoren die over hetzelfde zwembad moeten spreken:
      eigenaar, installateur, onderhoudsfirma, notaris, gemeente, verzekeraar
      en potenti&euml;le koper.
    </p>

    <h2>Formaat PLX-XXXXXXX</h2>
    <p>
      De identificatie volgt het formaat <strong>PLX-XXXXXXX</strong>:
    </p>
    <ul>
      <li><strong>PLX</strong> &mdash; vaste PoolLex.io-prefix, duidt de Europese uitgifte aan.</li>
      <li><strong>XXXXXXX</strong> &mdash; 7 opeenvolgende numerieke cijfers, capaciteit voor 10 miljoen unieke zwembaden.</li>
    </ul>
    <p>
      Concreet voorbeeld: <strong>PLX-0000001</strong> is het offici&euml;le
      demo-zwembad van PoolLex.io. Het formaat is opzettelijk kort en leesbaar
      zodat het op een papieren contract, een e-mail of zelfs handmatig kan
      worden overgeschreven.
    </p>

    <h2>Hoe verkrijgen</h2>
    <p>
      De PoolLex ID wordt <strong>gratis, zonder registratie en in 30
      seconden</strong> uitgegeven bij het genereren van de technische fiche
      van uw zwembad. Het proces:
    </p>
    <ul>
      <li>Ga naar <Link to="/ficha-tecnica">/ficha-tecnica</Link>.</li>
      <li>Verklaar 6-8 basisgegevens (afmetingen, locatie, uitrusting).</li>
      <li>Het systeem genereert de PDF met de PoolLex ID erop afgedrukt.</li>
    </ul>
    <p>
      Geen kosten, geen verplichte registratie, PDF onmiddellijk beschikbaar.
      Het uitgeven van de ID is een verklarende handeling van de eigenaar
      &mdash; het zwembad wordt niet fysiek gecontroleerd, wel worden de
      opgegeven gegevens gecatalogiseerd.
    </p>

    <h2>Praktische toepassingen</h2>
    <p>De PoolLex ID dient als <strong>stabiele technische referentie</strong> voor:</p>
    <ul>
      <li><strong>Vastgoedtransacties</strong> &mdash; geeft de koper verifieerbare informatie over het zwembad bij de woning.</li>
      <li><strong>Aanvragen van efficiency-subsidies</strong> &mdash; laat toe de begintoestand te documenteren voor een gesubsidieerde renovatie.</li>
      <li><strong>Onderhoudscontracten</strong> &mdash; identificeert het zwembad in het contract, wat verwarring vermijdt tussen meerdere zwembaden bij dezelfde onderhoudsfirma.</li>
      <li><strong>Stedenbouwkundige procedures</strong> &mdash; biedt lokale overheden een gedeelde identificatie.</li>
      <li><strong>Verzekeringsregistratie</strong> &mdash; vereenvoudigt de link tussen polis en verzekerd goed.</li>
    </ul>

    <h2>Analogie: auto-VIN en kadastrale referentie</h2>
    <p>
      De PoolLex ID vervult voor een zwembad dezelfde rol als de{" "}
      <strong>VIN</strong> (Vehicle Identification Number) voor een auto of de{" "}
      <strong>kadastrale referentie</strong> voor een perceel: een unieke,
      onveranderlijke code die een concreet goed identificeert en de
      geschiedenis ervan traceerbaar maakt.
    </p>
    <p>
      In tegenstelling tot de VIN &mdash; toegekend door de fabrikant &mdash;
      wordt de PoolLex ID uitgegeven door PoolLex.io als <strong>neutrale
      derde partij</strong>, niet verbonden aan een fabrikant of installateur.
      Deze neutraliteit is bepalend om aanvaarding door alle actoren van het
      ecosysteem te verzekeren.
    </p>
    <p>
      Om de uwe te bekomen, ga naar{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> en genereer de
      technische fiche in minder dan een minuut.
    </p>
  </>
);

// ============================================================
// ARTICLE 10 — UNE-EN 17645 explicada (Sprint 119.111c LONG_TAIL)
// ============================================================

const Article10BodyES = () => (
  <>
    <h2>Qu&eacute; es UNE-EN 17645 y qui&eacute;n la publica</h2>
    <p>
      <strong>UNE-EN 17645:2023</strong> es la norma t&eacute;cnica europea que
      establece el m&eacute;todo para <strong>evaluar la sostenibilidad ambiental
      de las piscinas residenciales</strong>. Est&aacute; publicada por{" "}
      <strong>CEN</strong> (Comit&eacute; Europ&eacute;en de Normalisation) y
      adoptada por los organismos nacionales de normalizaci&oacute;n de cada
      pa&iacute;s de la UE. En Espa&ntilde;a la publica AENOR bajo el c&oacute;digo
      UNE-EN 17645:2023.
    </p>
    <p>
      Es una norma <strong>voluntaria</strong> &mdash; ninguna ley espa&ntilde;ola
      la impone hoy &mdash;, pero act&uacute;a como referencia t&eacute;cnica
      com&uacute;n para propietarios, instaladores, administraciones y
      operadores inmobiliarios que quieran hablar el mismo idioma ambiental.
    </p>

    <h2>Prop&oacute;sito y alcance</h2>
    <p>
      La norma cubre <strong>piscinas privadas residenciales exteriores enterradas</strong>{" "}
      &mdash; quedan fuera las piscinas p&uacute;blicas, elevadas, desmontables,
      cubiertas de uso colectivo, terap&eacute;uticas o deportivas. Se aplica
      tanto a piscinas nuevas como existentes y considera el <strong>ciclo de
      vida completo</strong>: dise&ntilde;o, construcci&oacute;n, operaci&oacute;n
      y mantenimiento.
    </p>
    <p>
      Su objetivo es dar una <strong>metodolog&iacute;a homog&eacute;nea</strong>{" "}
      para cuantificar consumos de agua, energ&iacute;a y productos qu&iacute;micos,
      y traducirlos en una clase ambiental comparable entre piscinas.
    </p>

    <h2>Los 9 grupos evaluados</h2>
    <p>La norma organiza la evaluaci&oacute;n en <strong>9 &aacute;reas t&eacute;cnicas</strong>:</p>
    <ul>
      <li>Volumen, superficie y geometr&iacute;a del vaso</li>
      <li>Sistema de filtraci&oacute;n (bomba y filtro)</li>
      <li>Sistema de calentamiento (bomba de calor, resistencia, solar)</li>
      <li>Iluminaci&oacute;n sumergida</li>
      <li>Cubierta t&eacute;rmica o motorizada</li>
      <li>Sistema de tratamiento qu&iacute;mico (cloro, salino, UV, oz&oacute;n)</li>
      <li>Zona clim&aacute;tica y condiciones locales</li>
      <li>Uso previsto y temporada de ba&ntilde;o</li>
      <li>Sistemas de recuperaci&oacute;n de agua y contralavado</li>
    </ul>

    <h2>Sistema de clasificaci&oacute;n A-F</h2>
    <p>
      Cada piscina evaluada recibe una <strong>clase ambiental de A a F</strong>,
      donde <strong>A</strong> representa m&aacute;xima eficiencia (bomba VSD,
      cubierta motorizada, bomba de calor de alto COP, ubicaci&oacute;n
      favorable) y <strong>F</strong> el m&iacute;nimo (bomba monovelocidad,
      sin cubierta, calentamiento por resistencia el&eacute;ctrica).
    </p>
    <p>
      La clase se obtiene aplicando la metodolog&iacute;a de la norma al conjunto
      de datos t&eacute;cnicos declarados. En{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> puedes obtener una
      estimaci&oacute;n gratuita de la clase de tu piscina en 30 segundos.
    </p>

    <h2>Qui&eacute;n deber&iacute;a usarla</h2>
    <ul>
      <li><strong>Propietarios</strong> &mdash; para documentar la eficiencia real de su piscina antes de una reforma, venta o solicitud de ayuda.</li>
      <li><strong>Instaladores y proyectistas</strong> &mdash; para diferenciarse ofreciendo dise&ntilde;os con clase A-B verificable.</li>
      <li><strong>Administraciones locales</strong> &mdash; para incorporar la clasificaci&oacute;n en ordenanzas de eficiencia h&iacute;drica o energ&eacute;tica.</li>
      <li><strong>Aseguradoras y peritos</strong> &mdash; para valorar objetivamente el estado t&eacute;cnico de la instalaci&oacute;n.</li>
    </ul>

    <h2>Variantes nacionales</h2>
    <p>
      La misma norma europea se publica con c&oacute;digo local en cada pa&iacute;s
      miembro:{" "}
      <strong>UNE-EN 17645</strong> (Espa&ntilde;a),{" "}
      <strong>NF EN 17645</strong> (Francia),{" "}
      <strong>DIN EN 17645</strong> (Alemania),{" "}
      <strong>UNI EN 17645</strong> (Italia),{" "}
      <strong>NEN-EN 17645</strong> (Pa&iacute;ses Bajos y B&eacute;lgica). El
      contenido t&eacute;cnico es id&eacute;ntico &mdash; s&oacute;lo cambia el
      prefijo del c&oacute;digo del organismo emisor.
    </p>
    <p>
      Para conocer m&aacute;s sobre c&oacute;mo aplicamos esta norma, visita{" "}
      <Link to="/initiative">nuestra iniciativa</Link> o genera directamente la{" "}
      <Link to="/ficha-tecnica">ficha t&eacute;cnica</Link> de tu piscina.
    </p>
  </>
);

const Article10BodyEN = () => (
  <>
    <h2>What EN 17645 is and who publishes it</h2>
    <p>
      <strong>EN 17645:2023</strong> is the European technical standard that
      sets the method for <strong>assessing the environmental sustainability
      of residential swimming pools</strong>. It is published by{" "}
      <strong>CEN</strong> (European Committee for Standardization) and adopted
      by the national standards body of every EU member state. In the UK the
      equivalent adoption is BS EN 17645:2023.
    </p>
    <p>
      The standard is <strong>voluntary</strong> &mdash; no European law
      currently mandates it &mdash; but it acts as a common technical language
      for owners, installers, administrations and real-estate operators who
      want to compare pools on the same footing.
    </p>

    <h2>Purpose and scope</h2>
    <p>
      The standard covers <strong>private outdoor in-ground residential pools</strong>{" "}
      &mdash; public pools, above-ground, demountable, indoor collective,
      therapeutic and sport pools fall outside its scope. It applies to new
      and existing pools and considers the <strong>full life cycle</strong>:
      design, construction, operation and maintenance.
    </p>
    <p>
      Its goal is to provide a <strong>homogeneous methodology</strong> to
      quantify water, energy and chemical consumption and translate them into
      an environmental class that is comparable between pools.
    </p>

    <h2>The 9 evaluated groups</h2>
    <p>The standard organises the assessment into <strong>9 technical areas</strong>:</p>
    <ul>
      <li>Volume, surface and basin geometry</li>
      <li>Filtration system (pump and filter)</li>
      <li>Heating system (heat pump, resistance, solar)</li>
      <li>Underwater lighting</li>
      <li>Thermal or motorised cover</li>
      <li>Chemical treatment system (chlorine, salt, UV, ozone)</li>
      <li>Climate zone and local conditions</li>
      <li>Intended use and swimming season</li>
      <li>Water recovery and backwash systems</li>
    </ul>

    <h2>A-F classification system</h2>
    <p>
      Each evaluated pool receives an <strong>environmental class from A to F</strong>,
      where <strong>A</strong> represents maximum efficiency (VSD pump,
      motorised cover, high-COP heat pump, favourable location) and{" "}
      <strong>F</strong> the minimum (single-speed pump, no cover, electric
      resistance heating).
    </p>
    <p>
      The class is derived by applying the standard's methodology to declared
      technical data. At <Link to="/ficha-tecnica">/ficha-tecnica</Link> you
      can obtain a free estimate of your pool's class in 30 seconds.
    </p>

    <h2>Who should use it</h2>
    <ul>
      <li><strong>Owners</strong> &mdash; to document the real efficiency of their pool before a refurbishment, sale or grant application.</li>
      <li><strong>Installers and designers</strong> &mdash; to differentiate by offering verifiable class A-B designs.</li>
      <li><strong>Local authorities</strong> &mdash; to integrate the class into water or energy efficiency ordinances.</li>
      <li><strong>Insurers and surveyors</strong> &mdash; to objectively assess the technical condition of the installation.</li>
    </ul>

    <h2>National variants</h2>
    <p>
      The same European standard is published with a local code in each member
      state:{" "}
      <strong>UNE-EN 17645</strong> (Spain),{" "}
      <strong>NF EN 17645</strong> (France),{" "}
      <strong>DIN EN 17645</strong> (Germany),{" "}
      <strong>UNI EN 17645</strong> (Italy),{" "}
      <strong>NEN-EN 17645</strong> (Netherlands and Belgium),{" "}
      <strong>BS EN 17645</strong> (United Kingdom). Technical content is
      identical &mdash; only the issuing body prefix changes.
    </p>
    <p>
      To learn more about how we apply this standard visit{" "}
      <Link to="/initiative">our initiative</Link> or generate your pool's{" "}
      <Link to="/ficha-tecnica">technical sheet</Link> directly.
    </p>
  </>
);

const Article10BodyFR = () => (
  <>
    <h2>Qu'est-ce que la NF EN 17645 et qui la publie</h2>
    <p>
      La <strong>NF EN 17645:2023</strong> est la norme technique europ&eacute;enne
      qui &eacute;tablit la m&eacute;thode pour <strong>&eacute;valuer la
      durabilit&eacute; environnementale des piscines r&eacute;sidentielles</strong>.
      Elle est publi&eacute;e par le <strong>CEN</strong> (Comit&eacute; Europ&eacute;en
      de Normalisation) et adopt&eacute;e par les organismes nationaux de
      normalisation de chaque &Eacute;tat membre. En France elle est publi&eacute;e
      par l'AFNOR sous le code NF EN 17645:2023.
    </p>
    <p>
      C'est une norme <strong>volontaire</strong> &mdash; aucune loi ne l'impose
      aujourd'hui &mdash; mais elle sert de langage technique commun pour
      propri&eacute;taires, installateurs, administrations et op&eacute;rateurs
      immobiliers qui veulent comparer les piscines sur un m&ecirc;me r&eacute;f&eacute;rentiel.
    </p>

    <h2>Objectif et champ d'application</h2>
    <p>
      La norme couvre les <strong>piscines priv&eacute;es r&eacute;sidentielles
      ext&eacute;rieures enterr&eacute;es</strong> &mdash; sont exclues les
      piscines publiques, hors-sol, d&eacute;montables, couvertes collectives,
      th&eacute;rapeutiques ou sportives. Elle s'applique aux piscines neuves
      comme existantes et consid&egrave;re le <strong>cycle de vie complet</strong>:
      conception, construction, exploitation et maintenance.
    </p>
    <p>
      Son objectif est de fournir une <strong>m&eacute;thodologie homog&egrave;ne</strong>{" "}
      pour quantifier les consommations d'eau, d'&eacute;nergie et de produits
      chimiques et les traduire en une classe environnementale comparable
      entre piscines.
    </p>

    <h2>Les 9 groupes &eacute;valu&eacute;s</h2>
    <p>La norme organise l'&eacute;valuation en <strong>9 domaines techniques</strong>&nbsp;:</p>
    <ul>
      <li>Volume, surface et g&eacute;om&eacute;trie du bassin</li>
      <li>Syst&egrave;me de filtration (pompe et filtre)</li>
      <li>Syst&egrave;me de chauffage (pompe &agrave; chaleur, r&eacute;sistance, solaire)</li>
      <li>&Eacute;clairage subaquatique</li>
      <li>Couverture thermique ou motoris&eacute;e</li>
      <li>Syst&egrave;me de traitement chimique (chlore, sel, UV, ozone)</li>
      <li>Zone climatique et conditions locales</li>
      <li>Usage pr&eacute;vu et saison de baignade</li>
      <li>Syst&egrave;mes de r&eacute;cup&eacute;ration d'eau et contre-lavage</li>
    </ul>

    <h2>Syst&egrave;me de classification A-F</h2>
    <p>
      Chaque piscine &eacute;valu&eacute;e re&ccedil;oit une <strong>classe
      environnementale de A &agrave; F</strong>, o&ugrave; <strong>A</strong>{" "}
      repr&eacute;sente l'efficacit&eacute; maximale (pompe VSD, couverture
      motoris&eacute;e, pompe &agrave; chaleur &agrave; COP &eacute;lev&eacute;)
      et <strong>F</strong> le minimum (pompe monovitesse, sans couverture,
      chauffage par r&eacute;sistance &eacute;lectrique).
    </p>
    <p>
      La classe est obtenue en appliquant la m&eacute;thodologie de la norme
      aux donn&eacute;es techniques d&eacute;clar&eacute;es. Sur{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> vous pouvez obtenir une
      estimation gratuite de la classe de votre piscine en 30 secondes.
    </p>

    <h2>Qui devrait l'utiliser</h2>
    <ul>
      <li><strong>Propri&eacute;taires</strong> &mdash; pour documenter l'efficacit&eacute; r&eacute;elle de leur piscine avant r&eacute;novation, vente ou demande d'aide.</li>
      <li><strong>Installateurs et concepteurs</strong> &mdash; pour se diff&eacute;rencier en proposant des conceptions classe A-B v&eacute;rifiables.</li>
      <li><strong>Collectivit&eacute;s locales</strong> &mdash; pour int&eacute;grer la classification dans les r&egrave;glements d'efficacit&eacute; hydrique ou &eacute;nerg&eacute;tique.</li>
      <li><strong>Assureurs et experts</strong> &mdash; pour &eacute;valuer objectivement l'&eacute;tat technique de l'installation.</li>
    </ul>

    <h2>Variantes nationales</h2>
    <p>
      La m&ecirc;me norme europ&eacute;enne est publi&eacute;e avec un code
      local dans chaque &Eacute;tat membre&nbsp;:{" "}
      <strong>NF EN 17645</strong> (France),{" "}
      <strong>UNE-EN 17645</strong> (Espagne),{" "}
      <strong>DIN EN 17645</strong> (Allemagne),{" "}
      <strong>UNI EN 17645</strong> (Italie),{" "}
      <strong>NEN-EN 17645</strong> (Pays-Bas et Belgique). Le contenu technique
      est identique &mdash; seul le pr&eacute;fixe change.
    </p>
    <p>
      Pour d&eacute;couvrir comment nous appliquons cette norme, visitez{" "}
      <Link to="/initiative">notre initiative</Link> ou g&eacute;n&eacute;rez
      directement la <Link to="/ficha-tecnica">fiche technique</Link> de votre
      piscine.
    </p>
  </>
);

const Article10BodyDE = () => (
  <>
    <h2>Was ist DIN EN 17645 und wer ver&ouml;ffentlicht sie</h2>
    <p>
      <strong>DIN EN 17645:2023</strong> ist die europ&auml;ische technische
      Norm, die die Methode zur <strong>Bewertung der Umweltnachhaltigkeit
      privater Schwimmb&auml;der</strong> festlegt. Sie wird vom <strong>CEN</strong>{" "}
      (Europ&auml;isches Komitee f&uuml;r Normung) ver&ouml;ffentlicht und von
      den nationalen Normungsorganisationen jedes EU-Mitgliedstaats
      &uuml;bernommen. In Deutschland wird sie vom DIN als DIN EN 17645:2023
      ver&ouml;ffentlicht.
    </p>
    <p>
      Es ist eine <strong>freiwillige Norm</strong> &mdash; kein Gesetz schreibt
      sie derzeit vor &mdash; sie dient jedoch als gemeinsame technische
      Sprache f&uuml;r Eigent&uuml;mer, Installateure, Verwaltungen und
      Immobilienakteure, die Pools auf gleicher Basis vergleichen wollen.
    </p>

    <h2>Zweck und Anwendungsbereich</h2>
    <p>
      Die Norm gilt f&uuml;r <strong>private Freibad-Pools mit Erdeinbau</strong>{" "}
      &mdash; &ouml;ffentliche, Aufstell-, Zerlegbare, kollektive
      Hallenb&auml;der sowie Therapie- oder Sportbecken fallen nicht darunter.
      Sie gilt f&uuml;r neue und bestehende Pools und ber&uuml;cksichtigt den{" "}
      <strong>vollst&auml;ndigen Lebenszyklus</strong>: Planung, Bau, Betrieb
      und Wartung.
    </p>
    <p>
      Ihr Ziel ist eine <strong>einheitliche Methodik</strong> zur Quantifizierung
      des Wasser-, Energie- und Chemieverbrauchs und deren &Uuml;bersetzung
      in eine zwischen Pools vergleichbare Umweltklasse.
    </p>

    <h2>Die 9 bewerteten Gruppen</h2>
    <p>Die Norm gliedert die Bewertung in <strong>9 technische Bereiche</strong>:</p>
    <ul>
      <li>Volumen, Fl&auml;che und Beckengeometrie</li>
      <li>Filtrationssystem (Pumpe und Filter)</li>
      <li>Heizsystem (W&auml;rmepumpe, Widerstand, Solar)</li>
      <li>Unterwasserbeleuchtung</li>
      <li>Thermische oder motorisierte Abdeckung</li>
      <li>Chemisches Aufbereitungssystem (Chlor, Salz, UV, Ozon)</li>
      <li>Klimazone und lokale Bedingungen</li>
      <li>Nutzungsart und Badesaison</li>
      <li>Wasserr&uuml;ckgewinnungs- und R&uuml;cksp&uuml;lsysteme</li>
    </ul>

    <h2>A-F Klassifizierungssystem</h2>
    <p>
      Jeder bewertete Pool erh&auml;lt eine <strong>Umweltklasse von A bis F</strong>,
      wobei <strong>A</strong> maximale Effizienz repr&auml;sentiert (VSD-Pumpe,
      motorisierte Abdeckung, W&auml;rmepumpe mit hohem COP, g&uuml;nstige
      Lage) und <strong>F</strong> das Minimum (einstufige Pumpe, ohne
      Abdeckung, Heizung mit elektrischem Widerstand).
    </p>
    <p>
      Die Klasse wird durch Anwendung der Normmethodik auf die deklarierten
      technischen Daten ermittelt. Unter{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> k&ouml;nnen Sie in 30
      Sekunden eine kostenlose Sch&auml;tzung der Klasse Ihres Pools erhalten.
    </p>

    <h2>Wer sollte sie verwenden</h2>
    <ul>
      <li><strong>Eigent&uuml;mer</strong> &mdash; um die reale Effizienz des Pools vor Sanierung, Verkauf oder F&ouml;rderantrag zu dokumentieren.</li>
      <li><strong>Installateure und Planer</strong> &mdash; um sich mit nachweislichen Klasse-A-B-Entw&uuml;rfen zu differenzieren.</li>
      <li><strong>Kommunen</strong> &mdash; um die Klassifizierung in Wasser- oder Energieeffizienzverordnungen zu integrieren.</li>
      <li><strong>Versicherer und Gutachter</strong> &mdash; um den technischen Zustand der Anlage objektiv zu beurteilen.</li>
    </ul>

    <h2>Nationale Varianten</h2>
    <p>
      Dieselbe europ&auml;ische Norm wird in jedem Mitgliedstaat mit einem
      lokalen Code ver&ouml;ffentlicht:{" "}
      <strong>DIN EN 17645</strong> (Deutschland),{" "}
      <strong>UNE-EN 17645</strong> (Spanien),{" "}
      <strong>NF EN 17645</strong> (Frankreich),{" "}
      <strong>UNI EN 17645</strong> (Italien),{" "}
      <strong>NEN-EN 17645</strong> (Niederlande und Belgien),{" "}
      <strong>&Ouml;NORM EN 17645</strong> (&Ouml;sterreich),{" "}
      <strong>SN EN 17645</strong> (Schweiz). Der technische Inhalt ist
      identisch &mdash; nur das Pr&auml;fix der herausgebenden Stelle &auml;ndert
      sich.
    </p>
    <p>
      Erfahren Sie mehr &uuml;ber unsere Anwendung der Norm auf{" "}
      <Link to="/initiative">unserer Initiative-Seite</Link> oder erstellen Sie
      direkt das <Link to="/ficha-tecnica">technische Datenblatt</Link> Ihres
      Pools.
    </p>
  </>
);

const Article10BodyIT = () => (
  <>
    <h2>Cos'&egrave; la UNI EN 17645 e chi la pubblica</h2>
    <p>
      La <strong>UNI EN 17645:2023</strong> &egrave; la norma tecnica europea
      che stabilisce il metodo per <strong>valutare la sostenibilit&agrave;
      ambientale delle piscine residenziali</strong>. &Egrave; pubblicata dal{" "}
      <strong>CEN</strong> (Comitato Europeo di Normazione) e adottata dagli
      organismi nazionali di normazione di ciascuno Stato membro dell'UE. In
      Italia &egrave; pubblicata dall'UNI con il codice UNI EN 17645:2023.
    </p>
    <p>
      &Egrave; una norma <strong>volontaria</strong> &mdash; nessuna legge la
      impone oggi &mdash; ma funge da linguaggio tecnico comune per
      proprietari, installatori, amministrazioni e operatori immobiliari che
      vogliono confrontare le piscine sulla stessa base.
    </p>

    <h2>Scopo e campo di applicazione</h2>
    <p>
      La norma copre <strong>piscine private residenziali esterne interrate</strong>{" "}
      &mdash; sono escluse le piscine pubbliche, fuori terra, smontabili,
      coperte collettive, terapeutiche o sportive. Si applica a piscine nuove
      ed esistenti e considera l'intero <strong>ciclo di vita</strong>:
      progettazione, costruzione, esercizio e manutenzione.
    </p>
    <p>
      L'obiettivo &egrave; fornire una <strong>metodologia omogenea</strong>{" "}
      per quantificare i consumi di acqua, energia e prodotti chimici e
      tradurli in una classe ambientale confrontabile tra piscine.
    </p>

    <h2>I 9 gruppi valutati</h2>
    <p>La norma organizza la valutazione in <strong>9 aree tecniche</strong>:</p>
    <ul>
      <li>Volume, superficie e geometria della vasca</li>
      <li>Sistema di filtrazione (pompa e filtro)</li>
      <li>Sistema di riscaldamento (pompa di calore, resistenza, solare)</li>
      <li>Illuminazione subacquea</li>
      <li>Copertura termica o motorizzata</li>
      <li>Sistema di trattamento chimico (cloro, sale, UV, ozono)</li>
      <li>Zona climatica e condizioni locali</li>
      <li>Uso previsto e stagione balneare</li>
      <li>Sistemi di recupero acqua e controlavaggio</li>
    </ul>

    <h2>Sistema di classificazione A-F</h2>
    <p>
      Ogni piscina valutata riceve una <strong>classe ambientale da A a F</strong>,
      dove <strong>A</strong> rappresenta la massima efficienza (pompa VSD,
      copertura motorizzata, pompa di calore ad alto COP, ubicazione
      favorevole) e <strong>F</strong> il minimo (pompa monovelocit&agrave;,
      senza copertura, riscaldamento a resistenza elettrica).
    </p>
    <p>
      La classe si ottiene applicando la metodologia della norma ai dati
      tecnici dichiarati. Su <Link to="/ficha-tecnica">/ficha-tecnica</Link>{" "}
      puoi ottenere una stima gratuita della classe della tua piscina in 30
      secondi.
    </p>

    <h2>Chi dovrebbe usarla</h2>
    <ul>
      <li><strong>Proprietari</strong> &mdash; per documentare l'efficienza reale della piscina prima di ristrutturazione, vendita o richiesta di incentivi.</li>
      <li><strong>Installatori e progettisti</strong> &mdash; per distinguersi offrendo progetti classe A-B verificabili.</li>
      <li><strong>Amministrazioni locali</strong> &mdash; per integrare la classificazione in ordinanze di efficienza idrica o energetica.</li>
      <li><strong>Assicuratori e periti</strong> &mdash; per valutare oggettivamente lo stato tecnico dell'impianto.</li>
    </ul>

    <h2>Varianti nazionali</h2>
    <p>
      La stessa norma europea &egrave; pubblicata con un codice locale in
      ogni Stato membro:{" "}
      <strong>UNI EN 17645</strong> (Italia),{" "}
      <strong>UNE-EN 17645</strong> (Spagna),{" "}
      <strong>NF EN 17645</strong> (Francia),{" "}
      <strong>DIN EN 17645</strong> (Germania),{" "}
      <strong>NEN-EN 17645</strong> (Paesi Bassi e Belgio). Il contenuto
      tecnico &egrave; identico &mdash; cambia solo il prefisso dell'organismo
      emittente.
    </p>
    <p>
      Per saperne di pi&ugrave; su come applichiamo questa norma visita{" "}
      <Link to="/initiative">la nostra iniziativa</Link> o genera direttamente
      la <Link to="/ficha-tecnica">scheda tecnica</Link> della tua piscina.
    </p>
  </>
);

const Article10BodyNL = () => (
  <>
    <h2>Wat is NEN-EN 17645 en wie publiceert het</h2>
    <p>
      <strong>NEN-EN 17645:2023</strong> is de Europese technische norm die de
      methode vaststelt voor het <strong>beoordelen van de milieuduurzaamheid
      van residenti&euml;le zwembaden</strong>. Ze wordt gepubliceerd door de{" "}
      <strong>CEN</strong> (Europees Comit&eacute; voor Normalisatie) en
      overgenomen door de nationale normalisatie-instituten van elke EU-lidstaat.
      In Nederland en Belgi&euml; wordt ze gepubliceerd als NEN-EN 17645:2023
      (in Belgi&euml; ook NBN EN 17645).
    </p>
    <p>
      Het is een <strong>vrijwillige norm</strong> &mdash; geen enkele wet
      verplicht ze vandaag &mdash; maar ze fungeert als gemeenschappelijke
      technische taal voor eigenaars, installateurs, overheden en
      vastgoedactoren die zwembaden op dezelfde basis willen vergelijken.
    </p>

    <h2>Doel en toepassingsgebied</h2>
    <p>
      De norm dekt <strong>priv&eacute; residenti&euml;le buitenzwembaden
      in de grond</strong> &mdash; publieke, bovengrondse, demonteerbare,
      collectieve overdekte, therapeutische of sportzwembaden vallen buiten
      het toepassingsgebied. Ze geldt voor nieuwe en bestaande zwembaden en
      houdt rekening met de <strong>volledige levenscyclus</strong>: ontwerp,
      bouw, exploitatie en onderhoud.
    </p>
    <p>
      Het doel is een <strong>uniforme methodologie</strong> te bieden om
      water-, energie- en chemieverbruik te kwantificeren en om te zetten in
      een tussen zwembaden vergelijkbare milieuklasse.
    </p>

    <h2>De 9 ge&euml;valueerde groepen</h2>
    <p>De norm structureert de beoordeling in <strong>9 technische domeinen</strong>:</p>
    <ul>
      <li>Volume, oppervlakte en geometrie van het bassin</li>
      <li>Filtratiesysteem (pomp en filter)</li>
      <li>Verwarmingssysteem (warmtepomp, weerstand, zon)</li>
      <li>Onderwaterverlichting</li>
      <li>Thermische of gemotoriseerde afdekking</li>
      <li>Chemisch behandelingssysteem (chloor, zout, UV, ozon)</li>
      <li>Klimaatzone en lokale omstandigheden</li>
      <li>Bedoeld gebruik en zwemseizoen</li>
      <li>Waterrecuperatie- en terugspoelsystemen</li>
    </ul>

    <h2>A-F classificatiesysteem</h2>
    <p>
      Elk beoordeeld zwembad krijgt een <strong>milieuklasse van A tot F</strong>,
      waarbij <strong>A</strong> staat voor maximale effici&euml;ntie (VSD-pomp,
      gemotoriseerde afdekking, warmtepomp met hoge COP, gunstige ligging) en{" "}
      <strong>F</strong> voor het minimum (&eacute;&eacute;nsnelheidspomp,
      zonder afdekking, elektrische weerstandsverwarming).
    </p>
    <p>
      De klasse wordt bepaald door de methodologie van de norm toe te passen
      op de opgegeven technische gegevens. Op{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> kunt u in 30 seconden
      een gratis schatting van de klasse van uw zwembad krijgen.
    </p>

    <h2>Wie zou het moeten gebruiken</h2>
    <ul>
      <li><strong>Eigenaars</strong> &mdash; om de re&euml;le effici&euml;ntie van hun zwembad te documenteren voor renovatie, verkoop of subsidieaanvraag.</li>
      <li><strong>Installateurs en ontwerpers</strong> &mdash; om zich te onderscheiden met verifieerbare klasse A-B-ontwerpen.</li>
      <li><strong>Lokale overheden</strong> &mdash; om de classificatie op te nemen in verordeningen inzake water- of energie-effici&euml;ntie.</li>
      <li><strong>Verzekeraars en experts</strong> &mdash; om de technische staat van de installatie objectief te beoordelen.</li>
    </ul>

    <h2>Nationale varianten</h2>
    <p>
      Dezelfde Europese norm wordt in elke lidstaat met een lokale code
      gepubliceerd:{" "}
      <strong>NEN-EN 17645</strong> (Nederland en Belgi&euml;),{" "}
      <strong>UNE-EN 17645</strong> (Spanje),{" "}
      <strong>NF EN 17645</strong> (Frankrijk),{" "}
      <strong>DIN EN 17645</strong> (Duitsland),{" "}
      <strong>UNI EN 17645</strong> (Itali&euml;). De technische inhoud is
      identiek &mdash; alleen het prefix van de uitgevende instantie verandert.
    </p>
    <p>
      Voor meer info over hoe wij deze norm toepassen bezoek{" "}
      <Link to="/initiative">ons initiatief</Link> of genereer direct de{" "}
      <Link to="/ficha-tecnica">technische fiche</Link> van uw zwembad.
    </p>
  </>
);

// ============================================================================
// ARTICLE 15 — Piscina sostenible: gu&iacute;a completa 2026 (sostenibilidad)
// ============================================================================

const Article15BodyES = () => (
  <>
    <h2>Qu&eacute; es una piscina sostenible</h2>
    <p>
      Una piscina sostenible no es solo una piscina que consume poca
      electricidad. Es una instalaci&oacute;n dise&ntilde;ada, equipada y
      operada para minimizar su impacto ambiental durante todo el ciclo de vida:{" "}
      <strong>agua, energ&iacute;a, productos qu&iacute;micos, materiales y
      durabilidad</strong>. Reducir un vector (por ejemplo el consumo
      el&eacute;ctrico con placas solares) sin actuar sobre los dem&aacute;s no
      basta. La sostenibilidad real es la que el marco europeo{" "}
      <strong>UNE-EN 17645:2023</strong> denomina <em>impacto ambiental
      global</em> de la piscina residencial exterior enterrada.
    </p>

    <h2>Los 6 pilares de la sostenibilidad de una piscina</h2>
    <ul>
      <li><strong>Filtraci&oacute;n eficiente (VSD)</strong> &mdash; una bomba de velocidad variable reduce el consumo entre un <strong>60 y un 80 %</strong> frente a una monovelocidad equivalente.</li>
      <li><strong>Cubierta t&eacute;rmica motorizada</strong> &mdash; reduce la evaporaci&oacute;n un 60-70 % y las p&eacute;rdidas nocturnas de calor un 40-60 %.</li>
      <li><strong>Climatizaci&oacute;n con COP &ge; 6</strong> &mdash; una bomba de calor con COP 6 consume la mitad que una de COP 3 para el mismo aporte t&eacute;rmico.</li>
      <li><strong>Iluminaci&oacute;n LED</strong> &mdash; focos de 15-30 W frente a hal&oacute;genos de 100-300 W: hasta un <strong>85 % menos</strong> de consumo.</li>
      <li><strong>Tratamiento qu&iacute;mico optimizado</strong> &mdash; salino, UV o hidrolizador reducen el uso de cloro y regulan mejor el pH.</li>
      <li><strong>Estructura bien aislada</strong> &mdash; vaso con aislamiento perimetral y equipos t&eacute;cnicos con envolvente estanca reducen p&eacute;rdidas por conducci&oacute;n.</li>
    </ul>

    <h2>C&oacute;mo se mide la sostenibilidad: la norma UNE-EN 17645:2023</h2>
    <p>
      La norma <strong>UNE-EN 17645:2023</strong>, publicada por CEN y adoptada
      en Espa&ntilde;a por UNE, es el primer marco europeo armonizado para
      evaluar el impacto ambiental de una piscina residencial exterior
      enterrada. Puntu&uacute;a hasta <strong>8 componentes</strong>{" "}
      (filtraci&oacute;n, climatizaci&oacute;n, cubierta, iluminaci&oacute;n,
      tratamiento, vaso, hidr&aacute;ulica y control) y asigna una{" "}
      <strong>clase de la A a la F</strong>, an&aacute;loga al etiquetado
      energ&eacute;tico. La clase A representa la m&aacute;xima eficiencia y la
      F la m&iacute;nima.
    </p>

    <h2>5 mejoras que multiplican tu clasificaci&oacute;n en 6 meses</h2>
    <ul>
      <li><strong>Sustituir la bomba monovelocidad por una VSD</strong> &mdash; salto de 1-2 clases (E&rarr;C).</li>
      <li><strong>Instalar cubierta motorizada</strong> &mdash; hasta 1 clase adicional; ROI 1-3 temporadas en climatizadas.</li>
      <li><strong>Cambiar hal&oacute;genos por LED</strong> &mdash; mejora parcial pero rentable (payback &lt; 12 meses).</li>
      <li><strong>Programar filtraci&oacute;n y bomba de calor en horas valle</strong> &mdash; ahorra 25-40 % sin inversi&oacute;n.</li>
      <li><strong>Migrar a clorador salino o hidr&oacute;lisis</strong> &mdash; reduce cloro qu&iacute;mico y salvaguarda el vaso.</li>
    </ul>

    <h2>Certifica tu piscina como sostenible con PoolLex.io</h2>
    <p>
      PoolLex.io emite la <strong>Ficha T&eacute;cnica gratuita</strong> en 2
      minutos con la clase EN 17645 estimada de tu piscina, y el{" "}
      <strong>Sello firmado por ingeniero industrial colegiado</strong> para
      operaciones inmobiliarias o licencias municipales. Consulta{" "}
      <Link to="/initiative">la iniciativa PoolLex</Link>, obt&eacute;n tu
      diagn&oacute;stico en <Link to="/ficha-tecnica">/ficha-tecnica</Link> y
      solicita el sello en <Link to="/solicitar-sello">/solicitar-sello</Link>.
    </p>
  </>
);

const Article15BodyEN = () => (
  <>
    <h2>What a sustainable pool actually is</h2>
    <p>
      A sustainable pool is not simply a low-electricity pool. It is a facility
      designed, equipped and operated to minimise its environmental footprint
      across the whole life cycle:{" "}
      <strong>water, energy, chemicals, materials and durability</strong>.
      Reducing one vector (for example electricity via solar PV) without acting
      on the others is not enough. Real sustainability is what the European
      framework <strong>EN 17645:2023</strong> calls the <em>overall
      environmental impact</em> of an outdoor residential in-ground pool.
    </p>

    <h2>The 6 pillars of pool sustainability</h2>
    <ul>
      <li><strong>Efficient filtration (VSD)</strong> &mdash; a variable-speed pump cuts filtration consumption by <strong>60 to 80 %</strong>.</li>
      <li><strong>Motorised thermal cover</strong> &mdash; cuts evaporation by 60-70 % and night-time heat loss by 40-60 %.</li>
      <li><strong>Heating with COP &ge; 6</strong> &mdash; a heat pump with a nominal COP of 6 draws half the electricity of a COP-3 unit.</li>
      <li><strong>LED lighting</strong> &mdash; 15-30 W fixtures replace 100-300 W halogen: up to <strong>85 % less</strong> consumption.</li>
      <li><strong>Optimised water treatment</strong> &mdash; salt chlorination, UV or advanced hydrolysis cut chlorine use and stabilise pH.</li>
      <li><strong>Well-insulated structure</strong> &mdash; perimeter-insulated shell and weatherproof plant room reduce ground conduction losses.</li>
    </ul>

    <h2>How sustainability is measured: the EN 17645:2023 standard</h2>
    <p>
      <strong>EN 17645:2023</strong>, published by CEN and adopted across
      Europe, is the first harmonised framework to assess the environmental
      impact of an outdoor residential in-ground pool. It scores up to{" "}
      <strong>8 components</strong> (filtration, heating, cover, lighting,
      treatment, shell, hydraulics and control) and assigns a{" "}
      <strong>class from A to F</strong>, analogous to the appliance energy
      label. Class A is the most efficient, class F the least.
    </p>

    <h2>5 upgrades that boost your rating in 6 months</h2>
    <ul>
      <li><strong>Replace the single-speed pump with a VSD</strong> &mdash; typical jump of 1-2 classes (E&rarr;C).</li>
      <li><strong>Fit a motorised thermal cover</strong> &mdash; up to 1 extra class; payback 1-3 seasons on heated pools.</li>
      <li><strong>Swap halogen for LED</strong> &mdash; partial but profitable gain (payback under 12 months).</li>
      <li><strong>Schedule filtration and heat pump on off-peak</strong> &mdash; saves 25-40 % on the bill with no capex.</li>
      <li><strong>Migrate to salt chlorination or hydrolysis</strong> &mdash; cuts chemical chlorine and protects the shell.</li>
    </ul>

    <h2>Certify your pool as sustainable with PoolLex.io</h2>
    <p>
      PoolLex.io issues the <strong>free Technical Sheet</strong> in 2 minutes
      with your estimated EN 17645 class, and the{" "}
      <strong>Seal signed by a chartered industrial engineer</strong> for
      property transactions or municipal permits. Read more at{" "}
      <Link to="/initiative">the PoolLex initiative</Link>, get your diagnostic
      at <Link to="/ficha-tecnica">/ficha-tecnica</Link> and request the
      certified seal at <Link to="/solicitar-sello">/solicitar-sello</Link>.
    </p>
  </>
);

const Article15BodyFR = () => (
  <>
    <h2>Ce qu'est vraiment une piscine durable</h2>
    <p>
      Une piscine durable n'est pas simplement une piscine peu consommatrice
      d'&eacute;lectricit&eacute;. C'est une installation con&ccedil;ue,
      &eacute;quip&eacute;e et exploit&eacute;e pour minimiser son impact
      environnemental sur tout le cycle de vie :{" "}
      <strong>eau, &eacute;nergie, produits chimiques, mat&eacute;riaux et
      durabilit&eacute;</strong>. La vraie durabilit&eacute; est celle que le
      cadre europ&eacute;en <strong>NF EN 17645:2023</strong> appelle{" "}
      <em>impact environnemental global</em> de la piscine r&eacute;sidentielle
      ext&eacute;rieure enterr&eacute;e.
    </p>

    <h2>Les 6 piliers de la durabilit&eacute; d'une piscine</h2>
    <ul>
      <li><strong>Filtration efficiente (VSD)</strong> &mdash; pompe &agrave; vitesse variable r&eacute;duit la filtration de <strong>60 &agrave; 80 %</strong>.</li>
      <li><strong>Couverture thermique motoris&eacute;e</strong> &mdash; r&eacute;duit l'&eacute;vaporation 60-70 % et pertes nocturnes 40-60 %.</li>
      <li><strong>Climatisation COP &ge; 6</strong> &mdash; pompe COP 6 consomme la moiti&eacute; d'une COP 3 pour le m&ecirc;me apport.</li>
      <li><strong>&Eacute;clairage LED</strong> &mdash; 15-30 W vs halog&egrave;nes 100-300 W : jusqu'&agrave; <strong>85 % de moins</strong>.</li>
      <li><strong>Traitement chimique optimis&eacute;</strong> &mdash; chloration au sel, UV ou hydrolyse r&eacute;duisent le chlore.</li>
      <li><strong>Structure bien isol&eacute;e</strong> &mdash; bassin avec isolation p&eacute;rim&eacute;trique r&eacute;duit pertes vers le sol.</li>
    </ul>

    <h2>Comment se mesure la durabilit&eacute; : la NF EN 17645:2023</h2>
    <p>
      <strong>NF EN 17645:2023</strong>, publi&eacute;e par le CEN et
      adopt&eacute;e en France par AFNOR, est le premier cadre europ&eacute;en
      harmonis&eacute; pour &eacute;valuer l'impact environnemental d'une
      piscine r&eacute;sidentielle ext&eacute;rieure enterr&eacute;e. Elle note
      jusqu'&agrave; <strong>8 composants</strong> et attribue une{" "}
      <strong>classe de A &agrave; F</strong>. La classe A est la plus
      efficace, la F la moins.
    </p>

    <h2>5 am&eacute;liorations qui multiplient votre classe en 6 mois</h2>
    <ul>
      <li><strong>Remplacer la pompe monovitesse par une VSD</strong> &mdash; saut de 1-2 classes (E&rarr;C).</li>
      <li><strong>Installer une couverture motoris&eacute;e</strong> &mdash; jusqu'&agrave; 1 classe suppl&eacute;mentaire.</li>
      <li><strong>Passer des halog&egrave;nes au LED</strong> &mdash; gain partiel mais rentable (payback &lt; 12 mois).</li>
      <li><strong>Programmer filtration et pompe &agrave; chaleur en heures creuses</strong> &mdash; &eacute;conomie 25-40 %.</li>
      <li><strong>Migrer vers chloration au sel ou hydrolyse</strong> &mdash; r&eacute;duit le chlore chimique.</li>
    </ul>

    <h2>Certifiez votre piscine comme durable avec PoolLex.io</h2>
    <p>
      PoolLex.io d&eacute;livre la <strong>Fiche Technique gratuite</strong>{" "}
      en 2 minutes avec la classe NF EN 17645 estim&eacute;e, et le{" "}
      <strong>Sceau sign&eacute; par un ing&eacute;nieur dipl&ocirc;m&eacute;</strong>{" "}
      pour transactions immobili&egrave;res ou permis de construire. Voir{" "}
      <Link to="/initiative">l'initiative PoolLex</Link>, diagnostic sur{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> et sceau certifi&eacute;
      sur <Link to="/solicitar-sello">/solicitar-sello</Link>.
    </p>
  </>
);

const Article15BodyDE = () => (
  <>
    <h2>Was ein nachhaltiger Pool wirklich ist</h2>
    <p>
      Ein nachhaltiger Pool ist nicht einfach ein Pool mit geringem
      Stromverbrauch. Er ist eine Anlage, die so ausgelegt, ausger&uuml;stet
      und betrieben wird, dass ihr Umwelteinfluss &uuml;ber den gesamten
      Lebenszyklus minimiert wird:{" "}
      <strong>Wasser, Energie, Chemie, Materialien und Langlebigkeit</strong>.
      Wahre Nachhaltigkeit entspricht dem, was der europ&auml;ische Rahmen{" "}
      <strong>DIN EN 17645:2023</strong> als <em>gesamten Umwelteinfluss</em>{" "}
      eines privaten Freibad-Pools definiert.
    </p>

    <h2>Die 6 S&auml;ulen der Pool-Nachhaltigkeit</h2>
    <ul>
      <li><strong>Effiziente Filtration (VSD)</strong> &mdash; drehzahlgeregelte Pumpe senkt Verbrauch um <strong>60-80 %</strong>.</li>
      <li><strong>Motorisierte Thermoabdeckung</strong> &mdash; reduziert Verdunstung 60-70 % und n&auml;chtliche Verluste 40-60 %.</li>
      <li><strong>Heizung COP &ge; 6</strong> &mdash; W&auml;rmepumpe COP 6 verbraucht die H&auml;lfte einer COP-3-Einheit.</li>
      <li><strong>LED-Beleuchtung</strong> &mdash; 15-30 W statt 100-300 W Halogen: bis zu <strong>85 % weniger</strong>.</li>
      <li><strong>Optimierte Wasseraufbereitung</strong> &mdash; Salzelektrolyse, UV oder Hydrolyse reduzieren Chloreinsatz.</li>
      <li><strong>Gut ged&auml;mmte Struktur</strong> &mdash; perimeterged&auml;mmtes Becken reduziert Leitungsverluste zum Erdreich.</li>
    </ul>

    <h2>Wie Nachhaltigkeit gemessen wird: DIN EN 17645:2023</h2>
    <p>
      <strong>DIN EN 17645:2023</strong>, ver&ouml;ffentlicht von CEN und in
      Deutschland durch DIN &uuml;bernommen, ist der erste harmonisierte
      europ&auml;ische Rahmen zur Bewertung der Umweltauswirkung eines
      privaten Freibad-Pools. Er bewertet bis zu <strong>8 Komponenten</strong>{" "}
      und vergibt eine <strong>Klasse von A bis F</strong>. Klasse A ist die
      effizienteste, F die geringste.
    </p>

    <h2>5 Ma&szlig;nahmen, die Ihre Klasse in 6 Monaten verbessern</h2>
    <ul>
      <li><strong>Einstufige Pumpe durch VSD ersetzen</strong> &mdash; Sprung 1-2 Klassen (E&rarr;C).</li>
      <li><strong>Motorisierte Thermoabdeckung installieren</strong> &mdash; bis zu 1 zus&auml;tzliche Klasse.</li>
      <li><strong>Halogen durch LED ersetzen</strong> &mdash; partiell aber rentabel (Payback &lt; 12 Monate).</li>
      <li><strong>Filtration und W&auml;rmepumpe in Schwachlaststunden programmieren</strong> &mdash; 25-40 % Einsparung.</li>
      <li><strong>Auf Salzelektrolyse oder Hydrolyse umstellen</strong> &mdash; reduziert Chlor.</li>
    </ul>

    <h2>Zertifizieren Sie Ihren Pool als nachhaltig mit PoolLex.io</h2>
    <p>
      PoolLex.io stellt in 2 Minuten das <strong>kostenlose Datenblatt</strong>{" "}
      mit Ihrer gesch&auml;tzten DIN EN 17645 Klasse aus, und das{" "}
      <strong>Siegel eines Ingenieurs</strong> f&uuml;r
      Immobiliengesch&auml;fte oder kommunale Genehmigungen. Mehr auf{" "}
      <Link to="/initiative">der PoolLex Initiative</Link>, Diagnose auf{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> und zertifiziertes Siegel
      auf <Link to="/solicitar-sello">/solicitar-sello</Link>.
    </p>
  </>
);

const Article15BodyIT = () => (
  <>
    <h2>Cosa &egrave; davvero una piscina sostenibile</h2>
    <p>
      Una piscina sostenibile non &egrave; semplicemente una piscina a basso
      consumo elettrico. &Egrave; un impianto progettato, equipaggiato e
      gestito per minimizzare il proprio impatto ambientale su tutto il ciclo
      di vita: <strong>acqua, energia, prodotti chimici, materiali e
      durabilit&agrave;</strong>. La vera sostenibilit&agrave; &egrave; quella
      che il quadro europeo <strong>UNI EN 17645:2023</strong> definisce come{" "}
      <em>impatto ambientale complessivo</em> della piscina residenziale
      esterna interrata.
    </p>

    <h2>I 6 pilastri della sostenibilit&agrave; di una piscina</h2>
    <ul>
      <li><strong>Filtrazione efficiente (VSD)</strong> &mdash; pompa a velocit&agrave; variabile riduce consumo del <strong>60-80 %</strong>.</li>
      <li><strong>Copertura termica motorizzata</strong> &mdash; riduce evaporazione 60-70 % e perdite notturne 40-60 %.</li>
      <li><strong>Climatizzazione COP &ge; 6</strong> &mdash; pompa COP 6 consuma la met&agrave; di una COP 3.</li>
      <li><strong>Illuminazione LED</strong> &mdash; 15-30 W vs alogeni 100-300 W: fino all'<strong>85 % in meno</strong>.</li>
      <li><strong>Trattamento chimico ottimizzato</strong> &mdash; clorazione salina, UV o idrolisi riducono cloro.</li>
      <li><strong>Struttura ben isolata</strong> &mdash; vaso con isolamento perimetrale riduce perdite al terreno.</li>
    </ul>

    <h2>Come si misura la sostenibilit&agrave;: la UNI EN 17645:2023</h2>
    <p>
      La <strong>UNI EN 17645:2023</strong>, pubblicata dal CEN e adottata in
      Italia da UNI, &egrave; il primo quadro europeo armonizzato per valutare
      l'impatto ambientale di una piscina residenziale esterna interrata.
      Valuta fino a <strong>8 componenti</strong> e assegna una{" "}
      <strong>classe dalla A alla F</strong>. La classe A rappresenta la
      massima efficienza, la F la minima.
    </p>

    <h2>5 migliorie che moltiplicano la tua classe in 6 mesi</h2>
    <ul>
      <li><strong>Sostituire la pompa monovelocit&agrave; con una VSD</strong> &mdash; salto 1-2 classi (E&rarr;C).</li>
      <li><strong>Installare copertura motorizzata</strong> &mdash; fino a 1 classe aggiuntiva.</li>
      <li><strong>Sostituire alogeni con LED</strong> &mdash; guadagno parziale ma redditizio (payback &lt; 12 mesi).</li>
      <li><strong>Programmare filtrazione e pompa di calore in fasce basse</strong> &mdash; risparmio 25-40 %.</li>
      <li><strong>Passare a clorazione salina o idrolisi</strong> &mdash; riduce cloro chimico.</li>
    </ul>

    <h2>Certifica la tua piscina come sostenibile con PoolLex.io</h2>
    <p>
      PoolLex.io emette la <strong>Scheda Tecnica gratuita</strong> in 2
      minuti con la classe UNI EN 17645 stimata, e il{" "}
      <strong>Sigillo firmato da ingegnere iscritto all'Albo</strong> per
      transazioni immobiliari o licenze comunali. Vedi{" "}
      <Link to="/initiative">l'iniziativa PoolLex</Link>, diagnosi su{" "}
      <Link to="/ficha-tecnica">/ficha-tecnica</Link> e sigillo certificato su{" "}
      <Link to="/solicitar-sello">/solicitar-sello</Link>.
    </p>
  </>
);

const Article15BodyNL = () => (
  <>
    <h2>Wat een duurzaam zwembad echt is</h2>
    <p>
      Een duurzaam zwembad is niet gewoon een zwembad met laag
      elektriciteitsverbruik. Het is een installatie die zo ontworpen,
      uitgerust en beheerd wordt dat haar milieu-impact over de volledige
      levenscyclus wordt geminimaliseerd:{" "}
      <strong>water, energie, chemicali&euml;n, materialen en
      duurzaamheid</strong>. Echte duurzaamheid is wat het Europese kader{" "}
      <strong>NEN-EN 17645:2023</strong> de <em>globale milieu-impact</em>{" "}
      noemt van een residentieel buitenzwembad in de grond.
    </p>

    <h2>De 6 pijlers van zwembadduurzaamheid</h2>
    <ul>
      <li><strong>Effici&euml;nte filtratie (VSD)</strong> &mdash; pomp met variabele snelheid verlaagt verbruik met <strong>60-80 %</strong>.</li>
      <li><strong>Gemotoriseerde thermische afdekking</strong> &mdash; vermindert verdamping 60-70 % en nachtelijk warmteverlies 40-60 %.</li>
      <li><strong>Verwarming COP &ge; 6</strong> &mdash; warmtepomp COP 6 verbruikt de helft van een COP 3.</li>
      <li><strong>LED-verlichting</strong> &mdash; 15-30 W vs halogeen 100-300 W: tot <strong>85 % minder</strong>.</li>
      <li><strong>Geoptimaliseerde waterbehandeling</strong> &mdash; zoutelektrolyse, UV of hydrolyse verminderen chloor.</li>
      <li><strong>Goed ge&iuml;soleerde structuur</strong> &mdash; kuip met perimeterisolatie vermindert geleidingsverliezen.</li>
    </ul>

    <h2>Hoe duurzaamheid gemeten wordt: NEN-EN 17645:2023</h2>
    <p>
      <strong>NEN-EN 17645:2023</strong>, gepubliceerd door CEN en in
      Nederland overgenomen door NEN, is het eerste geharmoniseerde Europese
      kader om de milieu-impact van een residentieel buitenzwembad in de
      grond te beoordelen. Het scoort tot <strong>8 componenten</strong> en
      kent een <strong>klasse van A tot F</strong> toe. Klasse A is het meest
      effici&euml;nt, F het minst.
    </p>

    <h2>5 verbeteringen die uw klasse in 6 maanden verhogen</h2>
    <ul>
      <li><strong>De &eacute;&eacute;nsnelheidspomp vervangen door een VSD</strong> &mdash; sprong 1-2 klassen (E&rarr;C).</li>
      <li><strong>Gemotoriseerde thermische afdekking plaatsen</strong> &mdash; tot 1 extra klasse.</li>
      <li><strong>Halogeen vervangen door LED</strong> &mdash; gedeeltelijke maar rendabele winst (payback &lt; 12 maanden).</li>
      <li><strong>Filtratie en warmtepomp in daluren plannen</strong> &mdash; besparing 25-40 %.</li>
      <li><strong>Overstappen op zoutelektrolyse of hydrolyse</strong> &mdash; vermindert chemisch chloor.</li>
    </ul>

    <h2>Certificeer uw zwembad als duurzaam met PoolLex.io</h2>
    <p>
      PoolLex.io geeft in 2 minuten de <strong>gratis Technische Fiche</strong>{" "}
      uit met uw geschatte NEN-EN 17645 klasse, en het{" "}
      <strong>Zegel ondertekend door een gediplomeerd ingenieur
      (ir.)</strong> voor vastgoedtransacties of gemeentelijke
      vergunningen. Lees op <Link to="/initiative">het PoolLex-initiatief</Link>,
      diagnose op <Link to="/ficha-tecnica">/ficha-tecnica</Link> en
      gecertificeerd zegel op{" "}
      <Link to="/solicitar-sello">/solicitar-sello</Link>.
    </p>
  </>
);

// ============================================================================
// ARTICLES EXPORT
// ============================================================================

// ============================================================================
// ARTICLE 16 — Consumo eléctrico del clorador salino (Sprint 128, newsjacking)
// Angle: el 90 % de piscinas nuevas en España son salinas (Innowater, vía
// Xataka 25-26/08/2026) pero nadie mide lo que consumen. Rellena el hueco de
// contenido que la noticia deja abierto y conecta con el vector energético
// de la EN 17645.
// ============================================================================

const Article16BodyES = () => (
  <>
    <h2>Nueve de cada diez piscinas nuevas ya son salinas</h2>
    <p>
      Seg&uacute;n datos de <strong>Innowater</strong> recogidos por{" "}
      <em>Xataka</em> en agosto de 2026, <strong>nueve de cada diez piscinas
      nuevas en Espa&ntilde;a instalan un clorador salino</strong>. Sobre un
      parque de 1,3 millones de piscinas &mdash; m&aacute;s del 98 % al aire
      libre &mdash; la electr&oacute;lisis salina ha dejado de ser una
      alternativa para convertirse en el est&aacute;ndar de facto.
    </p>
    <p>
      Las ventajas est&aacute;n bien documentadas: menos olor a cloro, menos
      cloraminas irritantes, dosificaci&oacute;n autom&aacute;tica y m&aacute;s
      estable. Lo que casi nadie cuantifica es la otra cara:{" "}
      <strong>un clorador salino es un equipo el&eacute;ctrico que funciona
      varias horas cada d&iacute;a durante toda la temporada</strong>.
    </p>

    <h2>Qu&eacute; consume realmente un clorador salino</h2>
    <p>
      La celda de electr&oacute;lisis trabaja con corriente continua de baja
      tensi&oacute;n. Un equipo dom&eacute;stico t&iacute;pico para una piscina
      de 50-80 m&sup3; consume entre <strong>100 y 250 W</strong> seg&uacute;n
      modelo y nivel de producci&oacute;n (habitualmente 10-33 g de cloro por
      hora). El consumo anual depende de tres variables:
    </p>
    <ul>
      <li><strong>Potencia del equipo (W)</strong> &mdash; indicada en la placa de caracter&iacute;sticas.</li>
      <li><strong>Horas diarias de funcionamiento</strong> &mdash; el clorador solo produce cuando la bomba filtra, por lo que suele coincidir con las 8-12 h de filtraci&oacute;n en verano.</li>
      <li><strong>Meses de temporada</strong> &mdash; entre 4 y 8 seg&uacute;n clima y uso.</li>
    </ul>
    <p>
      Con valores medios &mdash; 150 W, 10 h/d&iacute;a y 6 meses de temporada
      &mdash; el c&aacute;lculo es directo:{" "}
      <strong>0,15 kW &times; 10 h &times; 180 d&iacute;as = 270 kWh</strong>{" "}
      por temporada. A una tarifa de 0,25 &euro;/kWh son unos{" "}
      <strong>65-70 &euro; al a&ntilde;o</strong> solo en cloraci&oacute;n. No
      es la partida m&aacute;s grande de la piscina &mdash; la bomba de
      filtraci&oacute;n y la climatizaci&oacute;n pesan mucho m&aacute;s &mdash;
      pero es una partida que casi ning&uacute;n propietario tiene contabilizada.
    </p>

    <h2>El electrodo de titanio: el coste que aparece a los 3-5 a&ntilde;os</h2>
    <p>
      La celda incorpora placas de titanio con recubrimiento de metales nobles
      que se degrada con el uso. La vida &uacute;til habitual est&aacute; entre{" "}
      <strong>5.000 y 10.000 horas</strong> de funcionamiento, lo que equivale a{" "}
      <strong>3-5 temporadas</strong> a 10 h/d&iacute;a. La sustituci&oacute;n
      cuesta entre 200 y 500 &euro; seg&uacute;n modelo.
    </p>
    <p>
      Para la norma europea esto no es solo un gasto: es un{" "}
      <strong>vector de ciclo de vida y generaci&oacute;n de residuos</strong>.
      Un equipo que exige recambio cada tres a&ntilde;os no tiene el mismo
      impacto ambiental que uno que dura ocho, aunque su consumo instant&aacute;neo
      sea id&eacute;ntico.
    </p>

    <h2>C&oacute;mo entra la cloraci&oacute;n salina en la EN 17645</h2>
    <p>
      La norma <strong>UNE-EN 17645:2023</strong> eval&uacute;a el impacto
      ambiental de la piscina residencial exterior enterrada puntuando hasta
      ocho componentes y asignando una <strong>clase de la A a la F</strong>.
      El sistema de tratamiento es uno de esos componentes, y su consumo
      el&eacute;ctrico suma al vector energ&eacute;tico global de la
      instalaci&oacute;n.
    </p>
    <p>
      Esto tiene una consecuencia pr&aacute;ctica interesante: migrar a salino
      mejora el vector qu&iacute;mico (menos cloro envasado, menos transporte,
      menos residuo de envase) pero a&ntilde;ade consumo el&eacute;ctrico.{" "}
      <strong>El balance suele ser favorable</strong>, sobre todo si la
      instalaci&oacute;n ya tiene bomba de velocidad variable y cubierta, pero
      no es autom&aacute;tico: depende de la potencia del equipo, de las horas
      de funcionamiento y de la durabilidad de la celda.
    </p>

    <h2>Qu&eacute; puedes hacer con estos n&uacute;meros</h2>
    <ul>
      <li><strong>Ajusta la producci&oacute;n al 50-70 %</strong> si tu agua mantiene el cloro libre en rango. Muchos equipos salen de f&aacute;brica al 80-100 % y sobreproducen.</li>
      <li><strong>Sincroniza clorador y filtraci&oacute;n</strong> &mdash; si el clorador funciona con la bomba parada, no est&aacute; clorando: est&aacute; gastando.</li>
      <li><strong>Mant&eacute;n la salinidad en el rango del fabricante</strong> &mdash; por debajo, la celda fuerza y se degrada antes; por encima, aumenta la corrosi&oacute;n galv&aacute;nica.</li>
      <li><strong>Programa en horas valle</strong> &mdash; el mismo consumo puede costar un 30-40 % menos.</li>
    </ul>

    <h2>Calcula tu caso concreto</h2>
    <p>
      Puedes empezar por la{" "}
      <Link to="/calculadora-sal-piscina">calculadora de sal</Link> para saber
      cu&aacute;nta necesitas y en qu&eacute; rango debes mantenerla, y seguir
      con la{" "}
      <Link to="/tools/pool-energy-calculator">calculadora de consumo
      el&eacute;ctrico</Link> para ver el peso real de cada equipo. Si quieres
      la clase ambiental estimada de tu piscina seg&uacute;n la EN 17645,
      la <Link to="/ficha-tecnica">Ficha T&eacute;cnica</Link> es gratuita y
      tarda dos minutos.
    </p>
  </>
);

const Article16BodyEN = () => (
  <>
    <h2>Nine out of ten new pools are already saltwater</h2>
    <p>
      According to <strong>Innowater</strong> data reported by <em>Xataka</em>{" "}
      in August 2026, <strong>nine out of ten new pools in Spain install a salt
      chlorinator</strong>. Across a stock of 1.3 million pools &mdash; over
      98 % of them outdoor &mdash; salt electrolysis has stopped being an
      alternative and become the de facto standard.
    </p>
    <p>
      The advantages are well documented: less chlorine smell, fewer irritating
      chloramines, automatic and more stable dosing. What almost nobody
      quantifies is the other side:{" "}
      <strong>a salt chlorinator is an electrical device that runs for several
      hours every day throughout the season</strong>.
    </p>

    <h2>What a salt chlorinator actually consumes</h2>
    <p>
      The electrolysis cell works on low-voltage direct current. A typical
      residential unit for a 50-80 m&sup3; pool draws between{" "}
      <strong>100 and 250 W</strong> depending on model and output level
      (usually 10-33 g of chlorine per hour). Annual consumption depends on
      three variables:
    </p>
    <ul>
      <li><strong>Unit power (W)</strong> &mdash; shown on the rating plate.</li>
      <li><strong>Daily running hours</strong> &mdash; the chlorinator only produces while the pump filters, so it normally matches the 8-12 h of summer filtration.</li>
      <li><strong>Season length</strong> &mdash; between 4 and 8 months depending on climate and use.</li>
    </ul>
    <p>
      With average values &mdash; 150 W, 10 h/day and a 6-month season &mdash;
      the maths is straightforward:{" "}
      <strong>0.15 kW &times; 10 h &times; 180 days = 270 kWh</strong> per
      season. At a tariff of &euro;0.25/kWh that is roughly{" "}
      <strong>&euro;65-70 a year</strong> on chlorination alone. It is not the
      biggest line item &mdash; the filtration pump and heating weigh far more
      &mdash; but it is one that almost no owner has on the books.
    </p>

    <h2>The titanium electrode: the cost that shows up after 3-5 years</h2>
    <p>
      The cell carries titanium plates with a noble-metal coating that degrades
      with use. Typical service life is{" "}
      <strong>5,000 to 10,000 operating hours</strong>, which works out at{" "}
      <strong>3-5 seasons</strong> at 10 h/day. Replacement costs between
      &euro;200 and &euro;500 depending on the model.
    </p>
    <p>
      For the European standard this is not merely an expense: it is a{" "}
      <strong>life-cycle and waste-generation vector</strong>. A unit needing
      replacement every three years does not carry the same environmental
      impact as one lasting eight, even if instantaneous consumption is
      identical.
    </p>

    <h2>How salt chlorination fits into EN 17645</h2>
    <p>
      Standard <strong>EN 17645:2023</strong> assesses the environmental impact
      of outdoor in-ground residential pools by scoring up to eight components
      and assigning a <strong>class from A to F</strong>. The treatment system
      is one of those components, and its electricity draw adds to the overall
      energy vector of the installation.
    </p>
    <p>
      This has an interesting practical consequence: switching to salt improves
      the chemical vector (less packaged chlorine, less transport, less
      packaging waste) but adds electrical consumption.{" "}
      <strong>The balance is usually favourable</strong>, especially where the
      installation already has a variable-speed pump and a cover, but it is not
      automatic: it depends on unit power, running hours and cell durability.
    </p>

    <h2>What to do with these numbers</h2>
    <ul>
      <li><strong>Set output to 50-70 %</strong> if your water holds free chlorine in range. Many units ship at 80-100 % and overproduce.</li>
      <li><strong>Sync chlorinator and filtration</strong> &mdash; if the chlorinator runs with the pump stopped, it is not chlorinating: it is just drawing power.</li>
      <li><strong>Keep salinity within the manufacturer range</strong> &mdash; below it the cell strains and degrades sooner; above it galvanic corrosion increases.</li>
      <li><strong>Schedule during off-peak hours</strong> &mdash; the same consumption can cost 30-40 % less.</li>
    </ul>

    <h2>Work out your own case</h2>
    <p>
      Start with the{" "}
      <Link to="/calculadora-sal-piscina">salt calculator</Link> to find how
      much you need and the range to hold, then move to the{" "}
      <Link to="/tools/pool-energy-calculator">energy consumption
      calculator</Link> to see the real weight of each device. If you want your
      pool's estimated EN 17645 environmental class, the{" "}
      <Link to="/ficha-tecnica">Technical Sheet</Link> is free and takes two
      minutes.
    </p>
  </>
);

const Article16BodyFR = () => (
  <>
    <h2>Neuf piscines neuves sur dix sont d&eacute;j&agrave; au sel</h2>
    <p>
      Selon les donn&eacute;es d&rsquo;<strong>Innowater</strong> relay&eacute;es
      par <em>Xataka</em> en ao&ucirc;t 2026,{" "}
      <strong>neuf piscines neuves sur dix en Espagne installent un
      &eacute;lectrolyseur au sel</strong>. Sur un parc de 1,3 million de
      piscines &mdash; plus de 98 % en ext&eacute;rieur &mdash;
      l&rsquo;&eacute;lectrolyse au sel n&rsquo;est plus une alternative mais le
      standard de fait.
    </p>
    <p>
      Les avantages sont bien document&eacute;s : moins d&rsquo;odeur de chlore,
      moins de chloramines irritantes, dosage automatique et plus stable. Ce que
      presque personne ne quantifie, c&rsquo;est l&rsquo;autre face :{" "}
      <strong>un &eacute;lectrolyseur est un appareil &eacute;lectrique qui
      fonctionne plusieurs heures par jour pendant toute la saison</strong>.
    </p>

    <h2>Ce que consomme r&eacute;ellement un &eacute;lectrolyseur au sel</h2>
    <p>
      La cellule fonctionne en courant continu basse tension. Un appareil
      domestique type pour une piscine de 50-80 m&sup3; consomme entre{" "}
      <strong>100 et 250 W</strong> selon le mod&egrave;le et le niveau de
      production (g&eacute;n&eacute;ralement 10-33 g de chlore par heure). La
      consommation annuelle d&eacute;pend de trois variables :
    </p>
    <ul>
      <li><strong>Puissance de l&rsquo;appareil (W)</strong> &mdash; indiqu&eacute;e sur la plaque signal&eacute;tique.</li>
      <li><strong>Heures de fonctionnement quotidiennes</strong> &mdash; l&rsquo;&eacute;lectrolyseur ne produit que lorsque la pompe filtre, ce qui co&iuml;ncide avec les 8-12 h de filtration estivale.</li>
      <li><strong>Dur&eacute;e de la saison</strong> &mdash; entre 4 et 8 mois selon le climat et l&rsquo;usage.</li>
    </ul>
    <p>
      Avec des valeurs moyennes &mdash; 150 W, 10 h/jour et 6 mois de saison
      &mdash; le calcul est direct :{" "}
      <strong>0,15 kW &times; 10 h &times; 180 jours = 270 kWh</strong> par
      saison. &Agrave; 0,25 &euro;/kWh, cela repr&eacute;sente environ{" "}
      <strong>65-70 &euro; par an</strong> pour la seule chloration. Ce
      n&rsquo;est pas le poste le plus lourd &mdash; la pompe de filtration et
      le chauffage p&egrave;sent bien davantage &mdash; mais c&rsquo;est un
      poste que quasiment aucun propri&eacute;taire ne comptabilise.
    </p>

    <h2>L&rsquo;&eacute;lectrode de titane : le co&ucirc;t qui arrive au bout de 3-5 ans</h2>
    <p>
      La cellule comporte des plaques de titane avec un rev&ecirc;tement de
      m&eacute;taux nobles qui se d&eacute;grade &agrave;
      l&rsquo;usage. La dur&eacute;e de vie habituelle se situe entre{" "}
      <strong>5 000 et 10 000 heures</strong> de fonctionnement, soit{" "}
      <strong>3 &agrave; 5 saisons</strong> &agrave; 10 h/jour. Le remplacement
      co&ucirc;te entre 200 et 500 &euro; selon le mod&egrave;le.
    </p>
    <p>
      Pour la norme europ&eacute;enne il ne s&rsquo;agit pas seulement
      d&rsquo;une d&eacute;pense : c&rsquo;est un{" "}
      <strong>vecteur de cycle de vie et de production de
      d&eacute;chets</strong>. Un appareil exigeant un remplacement tous les
      trois ans n&rsquo;a pas le m&ecirc;me impact environnemental qu&rsquo;un
      appareil qui dure huit ans, m&ecirc;me &agrave; consommation instantan&eacute;e
      identique.
    </p>

    <h2>Comment l&rsquo;&eacute;lectrolyse au sel entre dans la NF EN 17645</h2>
    <p>
      La norme <strong>NF EN 17645:2023</strong> &eacute;value l&rsquo;impact
      environnemental de la piscine r&eacute;sidentielle ext&eacute;rieure
      enterr&eacute;e en notant jusqu&rsquo;&agrave; huit composants et en
      attribuant une <strong>classe de A &agrave; F</strong>. Le
      syst&egrave;me de traitement est l&rsquo;un de ces composants, et sa
      consommation &eacute;lectrique s&rsquo;ajoute au vecteur
      &eacute;nerg&eacute;tique global de l&rsquo;installation.
    </p>
    <p>
      Cons&eacute;quence pratique int&eacute;ressante : passer au sel
      am&eacute;liore le vecteur chimique (moins de chlore conditionn&eacute;,
      moins de transport, moins de d&eacute;chets d&rsquo;emballage) mais
      ajoute de la consommation &eacute;lectrique.{" "}
      <strong>Le bilan est g&eacute;n&eacute;ralement favorable</strong>,
      surtout si l&rsquo;installation dispose d&eacute;j&agrave; d&rsquo;une
      pompe &agrave; vitesse variable et d&rsquo;une couverture, mais il
      n&rsquo;est pas automatique : tout d&eacute;pend de la puissance, des
      heures de fonctionnement et de la durabilit&eacute; de la cellule.
    </p>

    <h2>Que faire de ces chiffres</h2>
    <ul>
      <li><strong>R&eacute;glez la production &agrave; 50-70 %</strong> si votre eau maintient le chlore libre dans la plage. Beaucoup d&rsquo;appareils sortent d&rsquo;usine &agrave; 80-100 % et surproduisent.</li>
      <li><strong>Synchronisez &eacute;lectrolyseur et filtration</strong> &mdash; si l&rsquo;&eacute;lectrolyseur tourne pompe arr&ecirc;t&eacute;e, il ne chlore pas : il consomme.</li>
      <li><strong>Maintenez la salinit&eacute; dans la plage du fabricant</strong> &mdash; en dessous la cellule force et se d&eacute;grade plus vite ; au-dessus la corrosion galvanique augmente.</li>
      <li><strong>Programmez en heures creuses</strong> &mdash; la m&ecirc;me consommation peut co&ucirc;ter 30-40 % de moins.</li>
    </ul>

    <h2>Calculez votre cas pr&eacute;cis</h2>
    <p>
      Commencez par le{" "}
      <Link to="/calculadora-sal-piscina">calculateur de sel</Link> pour savoir
      quelle quantit&eacute; il vous faut et dans quelle plage la maintenir,
      puis passez au{" "}
      <Link to="/tools/pool-energy-calculator">calculateur de consommation
      &eacute;lectrique</Link> pour mesurer le poids r&eacute;el de chaque
      &eacute;quipement. Pour conna&icirc;tre la classe environnementale
      estim&eacute;e de votre piscine selon la NF EN 17645, la{" "}
      <Link to="/ficha-tecnica">Fiche Technique</Link> est gratuite et prend
      deux minutes.
    </p>
  </>
);

const Article16BodyDE = () => (
  <>
    <h2>Neun von zehn neuen Pools sind bereits Salzwasserpools</h2>
    <p>
      Nach Daten von <strong>Innowater</strong>, berichtet von <em>Xataka</em>{" "}
      im August 2026, <strong>installieren neun von zehn neuen Pools in Spanien
      eine Salzelektrolyse</strong>. Bei einem Bestand von 1,3 Millionen Pools
      &mdash; &uuml;ber 98 % davon im Freien &mdash; ist die Salzelektrolyse
      keine Alternative mehr, sondern der De-facto-Standard.
    </p>
    <p>
      Die Vorteile sind gut dokumentiert: weniger Chlorgeruch, weniger reizende
      Chloramine, automatische und stabilere Dosierung. Was fast niemand
      quantifiziert, ist die andere Seite:{" "}
      <strong>eine Salzelektrolyse ist ein elektrisches Ger&auml;t, das
      w&auml;hrend der gesamten Saison t&auml;glich mehrere Stunden
      l&auml;uft</strong>.
    </p>

    <h2>Was eine Salzelektrolyse tats&auml;chlich verbraucht</h2>
    <p>
      Die Zelle arbeitet mit Niederspannungs-Gleichstrom. Ein typisches
      Hausger&auml;t f&uuml;r einen Pool von 50-80 m&sup3; nimmt zwischen{" "}
      <strong>100 und 250 W</strong> auf, je nach Modell und Produktionsstufe
      (meist 10-33 g Chlor pro Stunde). Der Jahresverbrauch h&auml;ngt von drei
      Gr&ouml;&szlig;en ab:
    </p>
    <ul>
      <li><strong>Ger&auml;teleistung (W)</strong> &mdash; auf dem Typenschild angegeben.</li>
      <li><strong>T&auml;gliche Betriebsstunden</strong> &mdash; die Anlage produziert nur, wenn die Pumpe filtert, also meist parallel zu den 8-12 h Sommerfiltration.</li>
      <li><strong>Saisonl&auml;nge</strong> &mdash; zwischen 4 und 8 Monaten je nach Klima und Nutzung.</li>
    </ul>
    <p>
      Mit Mittelwerten &mdash; 150 W, 10 h/Tag und 6 Monate Saison &mdash; ist
      die Rechnung einfach:{" "}
      <strong>0,15 kW &times; 10 h &times; 180 Tage = 270 kWh</strong> pro
      Saison. Bei einem Tarif von 0,25 &euro;/kWh sind das rund{" "}
      <strong>65-70 &euro; im Jahr</strong> allein f&uuml;r die Chlorung. Es ist
      nicht der gr&ouml;&szlig;te Posten &mdash; Filterpumpe und Heizung wiegen
      deutlich schwerer &mdash; aber es ist einer, den fast kein Besitzer
      erfasst hat.
    </p>

    <h2>Die Titanelektrode: die Kosten nach 3-5 Jahren</h2>
    <p>
      Die Zelle enth&auml;lt Titanplatten mit einer Edelmetallbeschichtung, die
      sich im Betrieb abbaut. Die &uuml;bliche Lebensdauer liegt bei{" "}
      <strong>5.000 bis 10.000 Betriebsstunden</strong>, was{" "}
      <strong>3-5 Saisons</strong> bei 10 h/Tag entspricht. Der Austausch kostet
      je nach Modell 200 bis 500 &euro;.
    </p>
    <p>
      F&uuml;r die europ&auml;ische Norm ist das nicht nur eine Ausgabe,
      sondern ein <strong>Lebenszyklus- und Abfallvektor</strong>. Ein
      Ger&auml;t, das alle drei Jahre Ersatz braucht, hat nicht dieselbe
      Umweltwirkung wie eines, das acht Jahre h&auml;lt &mdash; auch bei
      identischem Momentanverbrauch.
    </p>

    <h2>Wie die Salzelektrolyse in die DIN EN 17645 eingeht</h2>
    <p>
      Die Norm <strong>DIN EN 17645:2023</strong> bewertet die Umweltwirkung
      privater Freibad-Pools, indem sie bis zu acht Komponenten bepunktet und
      eine <strong>Klasse von A bis F</strong> vergibt. Das
      Aufbereitungssystem ist eine dieser Komponenten, und sein Stromverbrauch
      z&auml;hlt zum Gesamtenergievektor der Anlage.
    </p>
    <p>
      Daraus folgt praktisch Interessantes: der Wechsel zu Salz verbessert den
      Chemievektor (weniger abgepacktes Chlor, weniger Transport, weniger
      Verpackungsabfall), f&uuml;gt aber Stromverbrauch hinzu.{" "}
      <strong>Die Bilanz f&auml;llt meist positiv aus</strong>, besonders wenn
      die Anlage bereits eine drehzahlgeregelte Pumpe und eine Abdeckung hat,
      aber automatisch ist das nicht: es h&auml;ngt von Leistung, Laufzeit und
      Zellhaltbarkeit ab.
    </p>

    <h2>Was Sie mit diesen Zahlen tun k&ouml;nnen</h2>
    <ul>
      <li><strong>Produktion auf 50-70 % stellen</strong>, wenn Ihr Wasser das freie Chlor im Bereich h&auml;lt. Viele Ger&auml;te kommen ab Werk mit 80-100 % und produzieren zu viel.</li>
      <li><strong>Elektrolyse und Filtration synchronisieren</strong> &mdash; l&auml;uft die Anlage bei stehender Pumpe, chlort sie nicht: sie verbraucht nur.</li>
      <li><strong>Salzgehalt im Herstellerbereich halten</strong> &mdash; darunter arbeitet die Zelle unter Last und altert schneller, dar&uuml;ber steigt die galvanische Korrosion.</li>
      <li><strong>In Schwachlastzeiten programmieren</strong> &mdash; derselbe Verbrauch kann 30-40 % weniger kosten.</li>
    </ul>

    <h2>Rechnen Sie Ihren Fall durch</h2>
    <p>
      Beginnen Sie mit dem{" "}
      <Link to="/calculadora-sal-piscina">Salzrechner</Link>, um Menge und
      Zielbereich zu bestimmen, und gehen Sie dann zum{" "}
      <Link to="/tools/pool-energy-calculator">Stromverbrauchsrechner</Link>,
      um das reale Gewicht jedes Ger&auml;ts zu sehen. F&uuml;r die
      gesch&auml;tzte Umweltklasse Ihres Pools nach DIN EN 17645 ist das{" "}
      <Link to="/ficha-tecnica">Datenblatt</Link> kostenlos und in zwei Minuten
      fertig.
    </p>
  </>
);

const Article16BodyIT = () => (
  <>
    <h2>Nove piscine nuove su dieci sono gi&agrave; al sale</h2>
    <p>
      Secondo i dati di <strong>Innowater</strong> riportati da <em>Xataka</em>{" "}
      nell&rsquo;agosto 2026, <strong>nove piscine nuove su dieci in Spagna
      installano un elettrolizzatore al sale</strong>. Su un parco di 1,3
      milioni di piscine &mdash; oltre il 98 % all&rsquo;aperto &mdash;
      l&rsquo;elettrolisi salina non &egrave; pi&ugrave; un&rsquo;alternativa ma
      lo standard di fatto.
    </p>
    <p>
      I vantaggi sono ben documentati: meno odore di cloro, meno cloramine
      irritanti, dosaggio automatico e pi&ugrave; stabile. Quello che quasi
      nessuno quantifica &egrave; l&rsquo;altra faccia:{" "}
      <strong>un elettrolizzatore &egrave; un apparecchio elettrico che
      funziona diverse ore al giorno per tutta la stagione</strong>.
    </p>

    <h2>Quanto consuma davvero un elettrolizzatore al sale</h2>
    <p>
      La cella lavora in corrente continua a bassa tensione. Un apparecchio
      domestico tipico per una piscina da 50-80 m&sup3; assorbe tra{" "}
      <strong>100 e 250 W</strong> secondo modello e livello di produzione
      (di norma 10-33 g di cloro all&rsquo;ora). Il consumo annuo dipende da tre
      variabili:
    </p>
    <ul>
      <li><strong>Potenza dell&rsquo;apparecchio (W)</strong> &mdash; indicata sulla targhetta.</li>
      <li><strong>Ore di funzionamento giornaliere</strong> &mdash; l&rsquo;elettrolizzatore produce solo quando la pompa filtra, quindi coincide con le 8-12 h di filtrazione estiva.</li>
      <li><strong>Durata della stagione</strong> &mdash; tra 4 e 8 mesi secondo clima e utilizzo.</li>
    </ul>
    <p>
      Con valori medi &mdash; 150 W, 10 h/giorno e 6 mesi di stagione &mdash; il
      calcolo &egrave; diretto:{" "}
      <strong>0,15 kW &times; 10 h &times; 180 giorni = 270 kWh</strong> a
      stagione. A una tariffa di 0,25 &euro;/kWh sono circa{" "}
      <strong>65-70 &euro; all&rsquo;anno</strong> di sola clorazione. Non
      &egrave; la voce pi&ugrave; pesante &mdash; pompa di filtrazione e
      riscaldamento pesano molto di pi&ugrave; &mdash; ma &egrave; una voce che
      quasi nessun proprietario ha contabilizzato.
    </p>

    <h2>L&rsquo;elettrodo di titanio: il costo che arriva dopo 3-5 anni</h2>
    <p>
      La cella monta piastre di titanio con rivestimento di metalli nobili che
      si degrada con l&rsquo;uso. La vita utile abituale &egrave; tra{" "}
      <strong>5.000 e 10.000 ore</strong> di funzionamento, cio&egrave;{" "}
      <strong>3-5 stagioni</strong> a 10 h/giorno. La sostituzione costa tra 200
      e 500 &euro; secondo il modello.
    </p>
    <p>
      Per la norma europea non &egrave; solo una spesa: &egrave; un{" "}
      <strong>vettore di ciclo di vita e generazione di rifiuti</strong>. Un
      apparecchio che richiede ricambio ogni tre anni non ha lo stesso impatto
      ambientale di uno che dura otto, anche a consumo istantaneo identico.
    </p>

    <h2>Come l&rsquo;elettrolisi salina entra nella UNI EN 17645</h2>
    <p>
      La norma <strong>UNI EN 17645:2023</strong> valuta l&rsquo;impatto
      ambientale della piscina residenziale esterna interrata assegnando un
      punteggio a un massimo di otto componenti e una{" "}
      <strong>classe dalla A alla F</strong>. Il sistema di trattamento &egrave;
      uno di questi componenti, e il suo consumo elettrico si somma al vettore
      energetico complessivo dell&rsquo;impianto.
    </p>
    <p>
      Ne deriva una conseguenza pratica interessante: passare al sale migliora
      il vettore chimico (meno cloro confezionato, meno trasporto, meno rifiuti
      di imballaggio) ma aggiunge consumo elettrico.{" "}
      <strong>Il bilancio &egrave; di solito favorevole</strong>, soprattutto se
      l&rsquo;impianto ha gi&agrave; pompa a velocit&agrave; variabile e
      copertura, ma non &egrave; automatico: dipende da potenza, ore di
      funzionamento e durabilit&agrave; della cella.
    </p>

    <h2>Cosa fare con questi numeri</h2>
    <ul>
      <li><strong>Imposta la produzione al 50-70 %</strong> se la tua acqua mantiene il cloro libero in range. Molti apparecchi escono di fabbrica all&rsquo;80-100 % e sovraproducono.</li>
      <li><strong>Sincronizza elettrolizzatore e filtrazione</strong> &mdash; se l&rsquo;elettrolizzatore gira a pompa ferma, non sta clorando: sta consumando.</li>
      <li><strong>Mantieni la salinit&agrave; nel range del costruttore</strong> &mdash; sotto, la cella forza e si degrada prima; sopra, aumenta la corrosione galvanica.</li>
      <li><strong>Programma nelle ore di minor costo</strong> &mdash; lo stesso consumo pu&ograve; costare il 30-40 % in meno.</li>
    </ul>

    <h2>Calcola il tuo caso specifico</h2>
    <p>
      Parti dal{" "}
      <Link to="/calculadora-sal-piscina">calcolatore del sale</Link> per sapere
      quanto te ne serve e in che range mantenerlo, poi passa al{" "}
      <Link to="/tools/pool-energy-calculator">calcolatore di consumo
      elettrico</Link> per vedere il peso reale di ogni apparecchio. Se vuoi la
      classe ambientale stimata della tua piscina secondo la UNI EN 17645, la{" "}
      <Link to="/ficha-tecnica">Scheda Tecnica</Link> &egrave; gratuita e
      richiede due minuti.
    </p>
  </>
);

const Article16BodyNL = () => (
  <>
    <h2>Negen van de tien nieuwe zwembaden zijn al zoutbaden</h2>
    <p>
      Volgens gegevens van <strong>Innowater</strong>, gemeld door{" "}
      <em>Xataka</em> in augustus 2026,{" "}
      <strong>installeren negen van de tien nieuwe zwembaden in Spanje een
      zoutelektrolyse</strong>. Op een bestand van 1,3 miljoen zwembaden &mdash;
      ruim 98 % daarvan buiten &mdash; is zoutelektrolyse geen alternatief meer
      maar de feitelijke standaard.
    </p>
    <p>
      De voordelen zijn goed gedocumenteerd: minder chloorgeur, minder
      irriterende chlooraminen, automatische en stabielere dosering. Wat bijna
      niemand kwantificeert is de andere kant:{" "}
      <strong>een zoutelektrolyse is een elektrisch apparaat dat het hele
      seizoen elke dag meerdere uren draait</strong>.
    </p>

    <h2>Wat een zoutelektrolyse werkelijk verbruikt</h2>
    <p>
      De cel werkt op laagspanningsgelijkstroom. Een typisch huishoudelijk
      apparaat voor een zwembad van 50-80 m&sup3; neemt tussen{" "}
      <strong>100 en 250 W</strong> op, afhankelijk van model en
      productieniveau (meestal 10-33 g chloor per uur). Het jaarverbruik hangt
      af van drie variabelen:
    </p>
    <ul>
      <li><strong>Vermogen van het apparaat (W)</strong> &mdash; vermeld op het typeplaatje.</li>
      <li><strong>Dagelijkse draaiuren</strong> &mdash; de elektrolyse produceert alleen terwijl de pomp filtert, dus meestal gelijk aan de 8-12 h zomerfiltratie.</li>
      <li><strong>Seizoenslengte</strong> &mdash; tussen 4 en 8 maanden afhankelijk van klimaat en gebruik.</li>
    </ul>
    <p>
      Met gemiddelde waarden &mdash; 150 W, 10 h/dag en een seizoen van 6
      maanden &mdash; is de rekensom eenvoudig:{" "}
      <strong>0,15 kW &times; 10 h &times; 180 dagen = 270 kWh</strong> per
      seizoen. Bij een tarief van 0,25 &euro;/kWh is dat ongeveer{" "}
      <strong>65-70 &euro; per jaar</strong> alleen voor chlorering. Het is niet
      de grootste post &mdash; filterpomp en verwarming wegen veel zwaarder
      &mdash; maar het is er wel een die bijna geen enkele eigenaar in beeld
      heeft.
    </p>

    <h2>De titaanelektrode: de kosten die na 3-5 jaar opduiken</h2>
    <p>
      De cel bevat titaanplaten met een edelmetaalcoating die door gebruik
      degradeert. De gebruikelijke levensduur ligt tussen{" "}
      <strong>5.000 en 10.000 bedrijfsuren</strong>, oftewel{" "}
      <strong>3-5 seizoenen</strong> bij 10 h/dag. Vervanging kost 200 tot 500
      &euro; afhankelijk van het model.
    </p>
    <p>
      Voor de Europese norm is dat niet alleen een uitgave, maar een{" "}
      <strong>levenscyclus- en afvalvector</strong>. Een apparaat dat elke drie
      jaar vervanging vraagt heeft niet dezelfde milieu-impact als een dat acht
      jaar meegaat, ook al is het momentane verbruik identiek.
    </p>

    <h2>Hoe zoutelektrolyse meetelt in de NEN-EN 17645</h2>
    <p>
      De norm <strong>NEN-EN 17645:2023</strong> beoordeelt de milieu-impact van
      particuliere buitenzwembaden door maximaal acht componenten te scoren en
      een <strong>klasse van A tot F</strong> toe te kennen. Het
      behandelsysteem is een van die componenten, en het stroomverbruik ervan
      telt mee in de totale energievector van de installatie.
    </p>
    <p>
      Dat heeft een interessant praktisch gevolg: overstappen op zout verbetert
      de chemievector (minder verpakt chloor, minder transport, minder
      verpakkingsafval) maar voegt stroomverbruik toe.{" "}
      <strong>De balans valt meestal gunstig uit</strong>, zeker als de
      installatie al een toerengeregelde pomp en een afdekking heeft, maar
      automatisch is het niet: het hangt af van vermogen, draaiuren en
      levensduur van de cel.
    </p>

    <h2>Wat je met deze cijfers kunt doen</h2>
    <ul>
      <li><strong>Zet de productie op 50-70 %</strong> als je water het vrije chloor binnen bereik houdt. Veel apparaten staan af fabriek op 80-100 % en produceren te veel.</li>
      <li><strong>Synchroniseer elektrolyse en filtratie</strong> &mdash; draait de elektrolyse terwijl de pomp stilstaat, dan chloreert hij niet: hij verbruikt alleen.</li>
      <li><strong>Houd het zoutgehalte binnen het bereik van de fabrikant</strong> &mdash; eronder werkt de cel onder belasting en veroudert sneller, erboven neemt galvanische corrosie toe.</li>
      <li><strong>Programmeer in daluren</strong> &mdash; hetzelfde verbruik kan 30-40 % minder kosten.</li>
    </ul>

    <h2>Reken je eigen situatie door</h2>
    <p>
      Begin met de{" "}
      <Link to="/calculadora-sal-piscina">zoutcalculator</Link> om te bepalen
      hoeveel je nodig hebt en welk bereik je moet aanhouden, en ga daarna naar
      de{" "}
      <Link to="/tools/pool-energy-calculator">stroomverbruikcalculator</Link>{" "}
      om het werkelijke gewicht van elk apparaat te zien. Wil je de geschatte
      milieuklasse van je zwembad volgens NEN-EN 17645, dan is de{" "}
      <Link to="/ficha-tecnica">Technische Fiche</Link> gratis en klaar in twee
      minuten.
    </p>
  </>
);

// ============================================================
// ARTICLE 17 — Legalizar piscina en España (Sprint 146)
// SERP dominada per proveidors de servei; legalizatupiscina.com
// hi es al 3 amb la home. Aquest article NO competeix pel mateix
// angle: declara la relacio entre els dos dominis i es queda amb
// la capa de documentacio, que es el que PoolLex fa.
// ============================================================
const Article17BodyES = () => (
  <>
    <h2>Qu&eacute; significa legalizar una piscina</h2>
    <p>
      Legalizar una piscina es <strong>regularizar una obra que ya existe</strong>{" "}
      y que se ejecut&oacute; sin licencia, o con desviaciones respecto a la que
      se concedi&oacute;. No es lo mismo que pedir permiso antes de construir, ni
      que cerrar una obra que s&iacute; ten&iacute;a licencia: eso &uacute;ltimo
      es un{" "}
      <Link to="/blog/certificado-finalizacion-obra-piscina">
        certificado de finalizaci&oacute;n de obra
      </Link>
      , un tr&aacute;mite ordinario.
    </p>
    <p>
      La distinci&oacute;n importa porque el punto de partida cambia todo lo
      dem&aacute;s. Conviene situarse antes de mover un papel:
    </p>
    <table>
      <thead>
        <tr>
          <th>Tu situaci&oacute;n</th>
          <th>Qu&eacute; procede</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Tuve licencia y termin&eacute; la obra</td>
          <td>Certificado final de obra. Tr&aacute;mite ordinario.</td>
        </tr>
        <tr>
          <td>Tuve licencia pero constru&iacute; algo distinto</td>
          <td>
            Legalizaci&oacute;n de la modificaci&oacute;n. Depende de si lo
            construido encaja en el planeamiento.
          </td>
        </tr>
        <tr>
          <td>No tuve licencia y la piscina es reciente</td>
          <td>
            Legalizaci&oacute;n con expediente de disciplina urban&iacute;stica
            abierto en paralelo.
          </td>
        </tr>
        <tr>
          <td>No tuve licencia y la piscina es antigua</td>
          <td>
            Puede haber prescrito la acci&oacute;n de restauraci&oacute;n. El
            plazo var&iacute;a por comunidad aut&oacute;noma.
          </td>
        </tr>
      </tbody>
    </table>

    <h2>La pregunta que decide el expediente</h2>
    <p>
      Antes de reunir un solo documento hay que responder a una cosa:{" "}
      <strong>
        &iquest;la piscina que ya est&aacute; construida ser&iacute;a legalizable
        hoy?
      </strong>
    </p>
    <p>
      Una piscina se legaliza si <em>hoy</em> cumple el planeamiento vigente:
      retranqueos a linde, ocupaci&oacute;n m&aacute;xima de parcela,
      edificabilidad, y las afecciones sectoriales que le apliquen. Si no
      cumple, no hay expediente que la salve; hay que modificarla o demolerla.
      Un t&eacute;cnico que empieza por juntar papeles sin comprobar esto
      primero te est&aacute; cobrando por un expediente que puede acabar en un
      requerimiento de demolici&oacute;n.
    </p>
    <p>
      Las afecciones que con m&aacute;s frecuencia bloquean un expediente no son
      urban&iacute;sticas sino sectoriales, y casi nadie las mira hasta que el
      ayuntamiento las saca:
    </p>
    <ul>
      <li>
        <strong>Dominio p&uacute;blico hidr&aacute;ulico y zonas
        inundables.</strong> El Reglamento del DPH (RD 849/1986, en su
        redacci&oacute;n dada por el RD 638/2016) restringe severamente la obra
        nueva en zona de flujo preferente. Si la parcela est&aacute; en zona
        inundable, hace falta autorizaci&oacute;n del organismo de cuenca
        adem&aacute;s de la licencia municipal.
      </li>
      <li>
        <strong>Servidumbres de costas, carreteras, v&iacute;as pecuarias o
        l&iacute;neas el&eacute;ctricas.</strong> Cada una tiene su
        &oacute;rgano y su plazo, y ninguna se resuelve en el ayuntamiento.
      </li>
      <li>
        <strong>Protecci&oacute;n del patrimonio o suelo r&uacute;stico
        protegido.</strong> Cambia qui&eacute;n autoriza y, a veces, si se
        autoriza.
      </li>
    </ul>
    <p>
      Estas comprobaciones se hacen sobre visores oficiales, no a ojo. Un
      sombreado en un mapa no es un dictamen: hay que consultar el servicio de
      la administraci&oacute;n competente y quedarse con la respuesta.
    </p>

    <h2>Qu&eacute; normativa se aplica de verdad</h2>
    <p>
      Hay una confusi&oacute;n muy extendida que conviene despejar pronto,
      porque condiciona el alcance del proyecto.
    </p>
    <p>
      El <strong>RD 742/2013</strong>, que establece los criterios
      t&eacute;cnico-sanitarios de las piscinas,{" "}
      <strong>
        excluye expresamente de su &aacute;mbito las piscinas unifamiliares de
        uso particular
      </strong>
      . Si tu piscina es la de tu casa, ese real decreto no te obliga: no
      necesitas libro de registro de par&aacute;metros ni socorrista.
    </p>
    <p>
      Lo que s&iacute; puede exigirte es la <strong>ordenanza municipal</strong>
      , y eso es otra cosa: condiciones de vallado y seguridad, o
      c&oacute;mo verter el agua de vaciado. Var&iacute;a de un municipio a
      otro y no tiene nada que ver con el r&eacute;gimen
      t&eacute;cnico-sanitario de las piscinas de uso colectivo.
    </p>
    <p>Lo que s&iacute; aplica en cualquier caso, sea cual sea la comunidad:</p>
    <ul>
      <li>
        <strong>RD 842/2002 (REBT), ITC-BT-31</strong> &mdash; instalaciones
        el&eacute;ctricas en piscinas y fuentes. Define los volumenes de
        protecci&oacute;n alrededor del vaso y qu&eacute; se puede instalar en
        cada uno. Es la parte que m&aacute;s incumplimientos reales genera.
      </li>
      <li>
        <strong>RD 470/2021 (C&oacute;digo Estructural)</strong> &mdash; para el
        vaso, si es de obra.
      </li>
      <li>
        <strong>RD 1027/2007 (RITE)</strong> &mdash; si hay climatizaci&oacute;n
        del agua.
      </li>
      <li>
        <strong>RD 105/2008</strong> &mdash; producci&oacute;n y gesti&oacute;n
        de residuos de construcci&oacute;n y demolici&oacute;n. Es el que obliga
        a la fianza de residuos, que muchos expedientes olvidan hasta que el
        ayuntamiento la reclama.
      </li>
      <li>
        <strong>Ley 39/2015 (LPAC)</strong> &mdash; procedimiento. Sus
        art&iacute;culos 30 y 32 rigen c&oacute;mo se cuentan los plazos y
        c&oacute;mo se piden ampliaciones. Sabersela ahorra requerimientos.
      </li>
    </ul>
    <p>
      Por encima de esto, cada comunidad aut&oacute;noma tiene su ley de
      urbanismo y cada ayuntamiento su plan general y sus ordenanzas.{" "}
      <strong>
        Ah&iacute; es donde est&aacute; el 80 % de la variabilidad
      </strong>
      : lo que en un municipio es obra menor con declaraci&oacute;n
      responsable, en el de al lado es obra mayor con proyecto visado.
    </p>

    <h2>Los tr&aacute;mites, y por qu&eacute; el orden importa</h2>
    <p>
      Un expediente t&iacute;pico de legalizaci&oacute;n de piscina enterrada
      pasa por estas fases. No son intercambiables: varias son requisito de la
      siguiente, y presentarlas desordenadas es la causa m&aacute;s
      com&uacute;n de que un expediente se pare meses.
    </p>
    <ol>
      <li>
        <strong>Comprobaci&oacute;n previa de afecciones.</strong> Antes de
        nada, y ya explicado arriba.
      </li>
      <li>
        <strong>Proyecto t&eacute;cnico</strong> redactado por t&eacute;cnico
        competente, con el estado real de lo construido &mdash; no el que se
        pretend&iacute;a construir.
      </li>
      <li>
        <strong>Visado colegial</strong>, si tu comunidad o el ayuntamiento lo
        exigen para este tipo de obra.
      </li>
      <li>
        <strong>Tasa municipal de servicios urban&iacute;sticos.</strong>{" "}
        Habitualmente un porcentaje del presupuesto de ejecuci&oacute;n
        material. Sin ella, muchos ayuntamientos ni abren el expediente.
      </li>
      <li>
        <strong>Fianza de residuos</strong> y contrato con gestor autorizado. Se
        deposita a nombre del promotor, no del t&eacute;cnico.
      </li>
      <li>
        <strong>Autorizaciones sectoriales</strong> que hayan salido del paso 1.
        Estas van por su cuenta y con sus plazos, en paralelo.
      </li>
      <li>
        <strong>Certificados t&eacute;cnicos</strong> de seguridad, funcionalidad
        y habitabilidad sobre la instalaci&oacute;n ya ejecutada.
      </li>
      <li>
        <strong>Presentaci&oacute;n conjunta</strong> en sede electr&oacute;nica.
      </li>
      <li>
        <strong>Declaraci&oacute;n catastral</strong> de la alteraci&oacute;n. Una
        piscina modifica el valor catastral del inmueble, y no declararla deja
        una discrepancia que aparece en la siguiente compraventa.
      </li>
    </ol>
    <p>
      Los pasos 4, 5 y 6 dependen de &oacute;rganos distintos &mdash;
      ayuntamiento, consejo insular o diputaci&oacute;n, organismo de cuenca,
      colegio profesional&mdash; y cada uno tiene su cola. Lanzarlos en paralelo
      en cuanto se sabe cu&aacute;les aplican es lo que separa un expediente de
      tres meses de uno de un a&ntilde;o.
    </p>

    <h2>El error que m&aacute;s caro sale</h2>
    <p>
      No es olvidar un documento: es <strong>describir mal lo que hay</strong>.
    </p>
    <p>
      Un expediente de legalizaci&oacute;n documenta una obra terminada, y el
      ayuntamiento puede ir a verla. Si el proyecto dice 8 &times; 4 y la
      piscina mide 8,40 &times; 4,20, o dice profundidad constante y el fondo
      tiene pendiente, la discrepancia aparece en la inspecci&oacute;n. Eso
      reabre el expediente con el agravante de haber declarado algo inexacto.
    </p>
    <p>
      Medir bien no es un detalle administrativo. El volumen real condiciona la
      dosificaci&oacute;n qu&iacute;mica, el caudal de la bomba y la
      clasificaci&oacute;n ambiental. Puedes calcularlo con nuestra{" "}
      <Link to="/tools/pool-volume-calculator">calculadora de volumen</Link>, que
      permite describir el fondo por tramos en lugar de asumir una profundidad
      media que ninguna piscina real tiene.
    </p>

    <h2>Cu&aacute;nto cuesta y cu&aacute;nto tarda</h2>
    <p>
      Con honestidad: depende tanto del municipio que dar una cifra cerrada
      ser&iacute;a enga&ntilde;arte. Lo que s&iacute; se puede acotar son los{" "}
      <strong>conceptos</strong> que vas a pagar, para que ninguno te
      sorprenda a mitad del expediente:
    </p>
    <ul>
      <li>Honorarios del t&eacute;cnico redactor y de la direcci&oacute;n.</li>
      <li>Visado colegial, si aplica.</li>
      <li>
        Tasa municipal de servicios urban&iacute;sticos, normalmente un
        porcentaje del presupuesto de ejecuci&oacute;n material.
      </li>
      <li>
        Impuesto sobre construcciones, instalaciones y obras (ICIO), que fija
        cada ayuntamiento dentro del m&aacute;ximo legal.
      </li>
      <li>Fianza de residuos, reintegrable al acreditar la gesti&oacute;n.</li>
      <li>Tasas de las autorizaciones sectoriales que apliquen.</li>
      <li>
        Posible sanci&oacute;n del expediente de disciplina, si la
        infracci&oacute;n no ha prescrito. Es independiente de la
        legalizaci&oacute;n: legalizar no borra la multa.
      </li>
    </ul>
    <p>
      En plazos, la horquilla habitual va de tres meses a m&aacute;s de un
      a&ntilde;o. Lo que m&aacute;s la alarga no es el ayuntamiento: son los
      requerimientos de subsanaci&oacute;n por documentaci&oacute;n incompleta y
      las autorizaciones sectoriales que se piden tarde.
    </p>

    <h2>D&oacute;nde encaja PoolLex.io</h2>
    <p>
      PoolLex.io es la plataforma creada por{" "}
      <strong>
        <a
          href="https://legalizatupiscina.com/"
          rel="noopener"
          target="_blank"
        >
          LegalizaTuPiscina.com
        </a>
      </strong>
      , el despacho que lleva estos expedientes. Nacieron de la misma
      constataci&oacute;n: en la mayor&iacute;a de legalizaciones, la primera
      semana se va en reconstruir datos que deber&iacute;an estar en alguna
      parte y no lo est&aacute;n. Cu&aacute;nto mide, qu&eacute; bomba tiene,
      de qu&eacute; a&ntilde;o es el vaso, qui&eacute;n la construy&oacute;.
    </p>
    <p>
      La divisi&oacute;n entre las dos casas es sencilla:
    </p>
    <ul>
      <li>
        <strong>LegalizaTuPiscina.com</strong> tramita. Redacta el proyecto,
        firma los certificados y lleva el expediente ante la
        administraci&oacute;n.
      </li>
      <li>
        <strong>PoolLex.io</strong> documenta. Genera el{" "}
        <Link to="/blog/poollex-id-identificador-piscina-europeo">PoolLex ID</Link>, un
        identificador &uacute;nico de la piscina, y una{" "}
        <Link to="/ficha-tecnica">Ficha T&eacute;cnica</Link> con sus
        caracter&iacute;sticas, su volumen real y su estimaci&oacute;n de clase
        ambiental seg&uacute;n la{" "}
        <Link to="/blog/une-en-17645-norma-piscinas-explicada">UNE-EN 17645:2023</Link>.
      </li>
    </ul>
    <p>
      La Ficha no sustituye a un proyecto ni es una certificaci&oacute;n
      oficial: no legaliza nada por s&iacute; misma. Lo que hace es que cuando
      llegues al t&eacute;cnico &mdash;el nuestro o cualquier otro&mdash; la
      piscina ya est&eacute; descrita. Es gratuita y se genera en un par de
      minutos.
    </p>
    <p>
      Si lo que necesitas es que alguien se ocupe del expediente,{" "}
      <a href="https://legalizatupiscina.com/" rel="noopener" target="_blank">
        habla con LegalizaTuPiscina.com
      </a>
      . Si lo que quieres es empezar por tener tu piscina documentada, la{" "}
      <Link to="/ficha-tecnica">Ficha T&eacute;cnica</Link> es el punto de partida.
    </p>

    <h2>Antes de dar el primer paso</h2>
    <p>
      Tres cosas que ahorran meses, y ninguna cuesta dinero:
    </p>
    <ol>
      <li>
        <strong>Comprueba las afecciones antes que nada.</strong> Si la parcela
        est&aacute; en zona inundable o en dominio p&uacute;blico, el orden de
        los tr&aacute;mites cambia por completo.
      </li>
      <li>
        <strong>Mide la piscina de verdad.</strong> Con cinta, en el interior
        del vaso y hasta la l&aacute;mina de agua. No desde el coronamiento ni
        desde el plano del constructor.
      </li>
      <li>
        <strong>Pregunta en tu ayuntamiento qu&eacute; figura exige</strong>{" "}
        para este caso concreto: licencia, declaraci&oacute;n responsable o
        comunicaci&oacute;n previa. Cambia entre municipios vecinos y determina
        el resto del expediente. Para una primera orientaci&oacute;n antes de
        ir, el <Link to="/pool-check">Pool Check</Link> revisa en dos minutos
        la situaci&oacute;n urban&iacute;stica, catastral y sanitaria a partir
        de tu municipio.
      </li>
    </ol>
  </>
);

export const ARTICLES = [
  {
    id: "gasto-mensual-piscina-2026",
    slugs: {
      es: "cuanto-gasta-piscina-mes-2026",
      en: "how-much-does-pool-cost-month-2026",
      fr: "combien-coute-piscine-par-mois-2026",
      de: "wie-viel-kostet-pool-monat-2026",
      it: "quanto-costa-piscina-al-mese-2026",
      nl: "hoeveel-kost-zwembad-per-maand-2026",
    },
    publishDate: "2026-07-31",
    updatedDate: "2026-07-31",
    readingTime: 9,
    category: "gastos-mensuales",
    coverTheme: "dark",
    // Portada propia per idioma (1200x630). La franja 25-80 sobre l'eix de cost mensual.
    // L'alt descriu el que es VEU, no de que va l'article.
    cover: {
      es: "/img/blog/pool-monthly-cost-range-es.jpg",
      en: "/img/blog/pool-monthly-cost-range-en.jpg",
      fr: "/img/blog/pool-monthly-cost-range-fr.jpg",
      de: "/img/blog/pool-monthly-cost-range-de.jpg",
      it: "/img/blog/pool-monthly-cost-range-it.jpg",
    },
    coverTitle: {
      es: "Cuánto cuesta una piscina al mes",
      en: "What a pool costs to run each month",
      fr: "Ce que coûte une piscine par mois",
      de: "Was ein Pool im Monat kostet",
      it: "Quanto costa una piscina al mese",
    },
    imageAlt: {
      es: "Escala de coste mensual de 0 a 100 €/mes con la franja de 25 a 80 € marcada en verde, y encima las dos variables que deciden dónde cae cada piscina: el tamaño y el clima",
      en: "Monthly cost scale from 0 to 100 €/month with the 25 to 80 € band marked in green, and above it the two variables that decide where each pool falls: size and climate",
      fr: "Échelle de coût mensuel de 0 à 100 €/mois avec la plage de 25 à 80 € marquée en vert, et au-dessus les deux variables qui décident où se situe chaque piscine : la taille et le climat",
      de: "Skala der monatlichen Kosten von 0 bis 100 €/Monat mit dem grün markierten Bereich von 25 bis 80 €, darüber die zwei Variablen, die den Wert bestimmen: Größe und Klima",
      it: "Scala del costo mensile da 0 a 100 €/mese con la fascia da 25 a 80 € evidenziata in verde, e sopra le due variabili che decidono dove si colloca ogni piscina: dimensioni e clima",
    },
    snippetTitle: {
      nl: "Kosten zwembad 2026: 25-80 €/maand",
    },
    snippetDescription: {
      es: "¿Cuánto cuesta mantener una piscina en 2026? Entre 25 y 80 €/mes según tamaño y clima. Consumos de luz, agua y químicos, y 10 acciones de ahorro.",
      en: "What does a pool cost to run in 2026? Between €25 and €80/month depending on size and climate. Electricity, water, chemicals and 10 ways to save.",
      fr: "Combien coûte l'entretien d'une piscine en 2026 ? Entre 25 et 80 €/mois selon la taille et le climat. Électricité, eau, produits et 10 économies.",
      de: "Was kostet ein Pool 2026? Zwischen 25 und 80 €/Monat je nach Größe und Klima. Strom, Wasser und Chemie, dazu 10 Sparmaßnahmen.",
      it: "Quanto costa mantenere una piscina nel 2026? Tra 25 e 80 €/mese secondo dimensioni e clima. Luce, acqua, chimici e 10 azioni di risparmio.",
      nl: "Wat kost een zwembad in 2026? Tussen 25 en 80 €/maand naargelang omvang en klimaat. Elektriciteit, water, chemie en 10 besparingsacties.",
    },
    translations: {
      es: {
        title: "Coste Piscina 2026: 25-80€/mes · Guía Real + 15 Ahorros",
        h1: "Coste Piscina 2026: 25-80€/mes · Guía Real + 15 Ahorros",
        description: "¿Cuánto cuesta mantener una piscina en 2026? Entre 25 y 80 €/mes según tamaño y clima. Guía completa con consumos eléctricos, agua, químicos y 10 acciones de ahorro.",
        keywords: [
          "cuanto gasta una piscina al mes",
          "consumo energético piscina",
          "coste mantener piscina",
          "ahorro piscina",
          "gasto piscina residencial",
        ],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article1BodyES,
        faq: [
          { question: "¿Cuánto cuesta mantener una piscina residencial al mes en España en 2026?", answer: "Entre 25 y 80 € al mes de media anual para una piscina residencial exterior enterrada de 30 a 60 m³, según clima, uso y equipamiento. La franja baja corresponde a piscinas pequeñas sin climatizar en zonas templadas; la alta, a piscinas de 60-80 m³ climatizadas con bomba de calor en zonas frías o de temporada larga." },
          { question: "¿Qué gasta más en una piscina: la electricidad o el agua?", answer: "En términos económicos, la electricidad suele suponer el 55-70 % del coste total anual (filtración, iluminación y climatización si existe), mientras que el agua de reposición representa el 10-20 % y los productos químicos otro 10-20 %. El desequilibrio se acentúa en piscinas climatizadas." },
          { question: "¿Cuánta agua evapora una piscina al día en verano?", answer: "Entre 5 y 8 mm/día en zonas cálidas del sur y levante peninsular durante los meses de julio y agosto. Para una piscina de 32 m² de superficie, equivale a 160-256 litros diarios de evaporación pura, sin contar salpicaduras ni contralavados. Una cubierta térmica reduce esa pérdida en torno al 60-70 %." },
          { question: "¿Cómo puedo saber exactamente cuánto gasta mi piscina?", answer: "Necesitas tres datos: volumen de la piscina (m³), horas diarias de filtración y potencia real de la bomba (kW). Con ellos y la tarifa eléctrica actual puedes calcular el consumo anual. En /calculadoras tienes herramientas gratuitas para estimarlo en segundos, y en /ficha-tecnica puedes obtener la clasificación EN 17645 estimada de tu piscina." },
          { question: "¿La cubierta térmica compensa realmente la inversión?", answer: "Sí. Una cubierta térmica reduce entre un 60 y un 70 % la evaporación y entre un 40 y un 60 % las pérdidas de calor nocturnas. En climatizadas el retorno se sitúa entre 1 y 3 temporadas; en no climatizadas, entre 2 y 4 años, principalmente por el ahorro de agua, productos químicos y horas de filtración." },
        ],
      },
      en: {
        title: "Pool Cost 2026: €25-80/month · Real Data + 15 Savings",
        h1: "Pool Cost 2026: €25-80/month · Real Data + 15 Savings",
        description: "How much does it cost to run a pool in 2026? Between €25 and €80/month depending on size and climate. Complete guide with electricity, water, chemicals and 10 savings actions.",
        keywords: [
          "how much does a pool cost per month",
          "pool running costs",
          "pool electricity bill",
          "swimming pool operating cost",
          "outdoor pool cost",
        ],
        author: "Víctor Saiz Vela, Chartered Industrial Engineer",
        Body: Article1BodyEN,
        faq: [
          { question: "How much does it cost to run a residential pool per month in 2026?", answer: "Between €25 and €80 per month on an annualised basis for an outdoor in-ground pool of 30 to 60 m³, depending on climate, usage and equipment. The low end applies to small unheated pools in mild climates; the top end to 60-80 m³ heated pools with a heat pump in cold zones or long seasons." },
          { question: "What costs more in a pool: electricity or water?", answer: "In monetary terms electricity typically accounts for 55-70 % of the annual cost (filtration, lighting and heating if present), while top-up water represents 10-20 % and chemicals another 10-20 %. The imbalance grows for heated pools." },
          { question: "How much water does a pool evaporate per day in summer?", answer: "Between 5 and 8 mm/day in warm zones during July and August. For a 32 m² pool that is 160-256 litres per day of pure evaporation, before splashing or backwash. A thermal cover cuts that loss by around 60-70 %." },
          { question: "How can I know exactly how much my pool costs?", answer: "You need three data points: pool volume (m³), daily filtration hours and real pump power (kW). With those plus your current tariff you can compute annual use. /calculadoras has free tools that estimate it in seconds, and /ficha-tecnica delivers an estimated EN 17645 classification for your pool." },
          { question: "Does a thermal cover really pay back?", answer: "Yes. A thermal cover reduces evaporation by 60-70 % and night-time heat loss by 40-60 %. Payback is 1-3 seasons on heated pools and 2-4 years on unheated ones, mostly through savings on water, chemicals and filtration hours." },
        ],
      },
      fr: {
        title: "Coût Piscine 2026 : 25-80€/mois · Guide + 15 Économies",
        h1: "Coût Piscine 2026 : 25-80€/mois · Guide + 15 Économies",
        description: "Combien coûte entretenir une piscine en 2026 ? Entre 25 et 80 €/mois selon la taille et le climat. Guide complet avec consommations électrique, eau, produits chimiques et 10 actions d'économie.",
        keywords: [
          "combien coûte une piscine par mois",
          "coût entretien piscine",
          "consommation électrique piscine",
          "prix piscine mensuel",
          "coût piscine extérieure",
        ],
        author: "Víctor Saiz Vela, Ingénieur diplômé",
        Body: Article1BodyFR,
        faq: [
          { question: "Combien coûte l'entretien mensuel d'une piscine résidentielle en 2026 ?", answer: "Entre 25 et 80 € par mois en moyenne annualisée pour une piscine extérieure enterrée de 30 à 60 m³, selon climat, usage et équipement. La fourchette basse correspond aux petites piscines non chauffées en climat tempéré ; la haute aux piscines de 60-80 m³ chauffées par pompe à chaleur en climat froid ou en longue saison." },
          { question: "Qu'est-ce qui coûte le plus : l'électricité ou l'eau ?", answer: "En termes financiers, l'électricité représente généralement 55-70 % du coût annuel total (filtration, éclairage et chauffage éventuel), tandis que l'eau d'appoint pèse 10-20 % et les produits chimiques 10-20 %. Le déséquilibre s'accentue sur les piscines chauffées." },
          { question: "Combien d'eau une piscine évapore-t-elle par jour en été ?", answer: "Entre 5 et 8 mm/jour dans les zones chaudes du sud pendant juillet et août. Pour une piscine de 32 m² cela représente 160-256 litres/jour d'évaporation pure, hors éclaboussures et contre-lavages. Une couverture thermique réduit cette perte d'environ 60-70 %." },
          { question: "Comment connaître exactement le coût de ma piscine ?", answer: "Il vous faut trois données : volume (m³), heures de filtration quotidiennes et puissance réelle de la pompe (kW). Avec votre tarif électrique actuel vous calculez la consommation annuelle. /calculadoras propose des outils gratuits d'estimation, et /ficha-tecnica délivre une classification NF EN 17645 estimée pour votre piscine." },
          { question: "La couverture thermique est-elle vraiment rentable ?", answer: "Oui. Une couverture thermique réduit l'évaporation de 60-70 % et les pertes de chaleur nocturnes de 40-60 %. Le retour sur investissement est de 1-3 saisons pour les piscines chauffées et de 2-4 ans pour les non chauffées, principalement par les économies d'eau, de produits et d'heures de filtration." },
        ],
      },
      de: {
        title: "Poolkosten 2026: 25-80€/Monat · Real + 15 Sparmaßnahmen",
        h1: "Poolkosten 2026: 25-80€/Monat · Real + 15 Sparmaßnahmen",
        description: "Was kostet ein Pool 2026? Zwischen 25 und 80 €/Monat je nach Größe und Klima. Kompletter Leitfaden mit Strom-, Wasser- und Chemieverbrauch sowie 10 Sparmaßnahmen.",
        keywords: [
          "was kostet ein Pool im Monat",
          "Pool Betriebskosten",
          "Poolpumpe Stromverbrauch",
          "Pool Wasserkosten",
          "Schwimmbad Kosten",
        ],
        author: "Víctor Saiz Vela, Ingenieur",
        Body: Article1BodyDE,
        faq: [
          { question: "Was kostet der Betrieb eines privaten Pools pro Monat 2026?", answer: "Zwischen 25 und 80 € pro Monat im Jahresdurchschnitt für einen im Freien eingebauten Pool von 30 bis 60 m³, abhängig von Klima, Nutzung und Ausstattung. Der untere Bereich betrifft kleine unbeheizte Pools in milden Klimazonen; der obere 60-80 m³ Pools mit Wärmepumpe in kühleren Regionen oder langer Saison." },
          { question: "Was kostet mehr: Strom oder Wasser?", answer: "In Euro betrachtet macht Strom typischerweise 55-70 % der jährlichen Gesamtkosten aus (Filtration, Beleuchtung und Heizung falls vorhanden), Nachfüllwasser 10-20 %, Chemie weitere 10-20 %. Bei beheizten Pools verschiebt sich das Verhältnis noch stärker zum Strom." },
          { question: "Wie viel Wasser verdunstet ein Pool im Sommer?", answer: "5 bis 8 mm/Tag in warmen Regionen im Juli und August. Bei 32 m² Wasserfläche entspricht das 160-256 Litern reiner Verdunstung pro Tag, ohne Spritzverluste oder Rückspülungen. Eine Thermoabdeckung senkt diesen Verlust um rund 60-70 %." },
          { question: "Wie erfahre ich die genauen Kosten meines Pools?", answer: "Drei Werte genügen: Poolvolumen (m³), tägliche Filterlaufzeit und reale Pumpenleistung (kW). Mit dem aktuellen Stromtarif lässt sich der Jahresverbrauch berechnen. Unter /calculadoras stehen kostenlose Rechner bereit, und /ficha-tecnica liefert eine geschätzte DIN EN 17645 Klassifizierung." },
          { question: "Rentiert sich eine Thermoabdeckung wirklich?", answer: "Ja. Sie reduziert die Verdunstung um 60-70 % und die nächtlichen Wärmeverluste um 40-60 %. Amortisation: 1-3 Saisons bei beheizten, 2-4 Jahre bei unbeheizten Pools — hauptsächlich durch Ersparnisse bei Wasser, Chemie und Filterlaufzeit." },
        ],
      },
      it: {
        title: "Costo Piscina 2026: 25-80€/mese · Reale + 15 Risparmi",
        h1: "Costo Piscina 2026: 25-80€/mese · Reale + 15 Risparmi",
        description: "Quanto costa mantenere una piscina nel 2026? Tra 25 e 80 €/mese in base a dimensioni e clima. Guida completa con consumi elettrici, acqua, chimici e 10 azioni di risparmio.",
        keywords: [
          "quanto costa una piscina al mese",
          "costo gestione piscina",
          "consumo pompa piscina",
          "bolletta piscina",
          "spese piscina esterna",
        ],
        author: "Víctor Saiz Vela, Ingegnere iscritto all'Albo",
        Body: Article1BodyIT,
        faq: [
          { question: "Quanto costa mantenere una piscina residenziale al mese nel 2026?", answer: "Tra 25 e 80 € al mese in media annualizzata per una piscina esterna interrata da 30 a 60 m³, in funzione di clima, uso ed equipaggiamento. La fascia bassa è tipica di piscine piccole non riscaldate in climi miti; la alta di piscine 60-80 m³ riscaldate con pompa di calore in zone fredde o stagioni lunghe." },
          { question: "Cosa incide di più: l'elettricità o l'acqua?", answer: "In termini economici l'elettricità rappresenta di solito il 55-70 % del costo annuale (filtrazione, illuminazione e riscaldamento se presente), l'acqua di reintegro il 10-20 % e i prodotti chimici un altro 10-20 %. Lo sbilanciamento cresce nelle piscine riscaldate." },
          { question: "Quanta acqua evapora una piscina al giorno d'estate?", answer: "Tra 5 e 8 mm/giorno in zone calde durante luglio e agosto. Per una piscina di 32 m² sono 160-256 litri al giorno di pura evaporazione, senza contare spruzzi e controlavaggi. Una copertura termica riduce questa perdita del 60-70 % circa." },
          { question: "Come posso sapere quanto costa esattamente la mia piscina?", answer: "Servono tre dati: volume (m³), ore quotidiane di filtrazione e potenza reale della pompa (kW). Con la tariffa elettrica attuale si ottiene il consumo annuale. In /calculadoras ci sono strumenti gratuiti che lo stimano in pochi secondi, e in /ficha-tecnica si ottiene la classificazione UNI EN 17645 stimata della propria piscina." },
          { question: "La copertura termica ripaga davvero l'investimento?", answer: "Sì. Riduce l'evaporazione del 60-70 % e le perdite di calore notturne del 40-60 %. Il ritorno è di 1-3 stagioni su piscine riscaldate e 2-4 anni su quelle non riscaldate, soprattutto grazie al risparmio di acqua, chimici e ore di filtrazione." },
        ],
      },
        nl: {
          title: "Kosten Zwembad 2026: 25-80€/maand · Echte Gids + 15 Besparingen",
          h1: "Kosten Zwembad 2026: 25-80€/maand · Echte Gids + 15 Besparingen",
          description: "Wat kost een zwembad onderhouden in 2026? Tussen 25 en 80 €/maand naargelang omvang en klimaat. Volledige gids met elektriciteits-, water- en chemieverbruik en 10 besparingsacties.",
          keywords: [
            "hoeveel kost een zwembad per maand",
            "zwembad onderhoudskosten",
            "zwembadpomp stroomverbruik",
            "waterverbruik zwembad",
            "zwembad kosten",
          ],
          author: "Víctor Saiz Vela, ir.",
          Body: Article1BodyNL,
          faq: [
            { question: "Hoeveel kost het onderhoud van een residentieel zwembad per maand in 2026?", answer: "Tussen 25 en 80 € per maand op jaarbasis voor een residentieel buitenzwembad in de grond van 30 tot 60 m³, afhankelijk van klimaat, gebruik en uitrusting. De onderkant komt overeen met kleine onverwarmde zwembaden in gematigde zones; de bovenkant met zwembaden van 60-80 m³ verwarmd met warmtepomp in koudere zones of lange seizoenen." },
            { question: "Wat kost meer in een zwembad: elektriciteit of water?", answer: "In monetaire termen vertegenwoordigt elektriciteit doorgaans 55-70 % van de jaarlijkse totale kosten (filtratie, verlichting en verwarming indien aanwezig), terwijl aanvullingswater 10-20 % vertegenwoordigt en chemicaliën nog eens 10-20 %. Het onevenwicht wordt groter bij verwarmde zwembaden." },
            { question: "Hoeveel water verdampt een zwembad per dag in de zomer?", answer: "Tussen 5 en 8 mm/dag in warme zones tijdens juli en augustus. Voor een zwembad van 32 m² komt dat neer op 160-256 liter per dag aan pure verdamping, zonder spatverlies of terugspoelen. Een thermische afdekking vermindert dit verlies met ongeveer 60-70 %." },
            { question: "Hoe kan ik precies weten hoeveel mijn zwembad kost?", answer: "U heeft drie gegevens nodig: zwembadvolume (m³), dagelijkse filtratie-uren en reëel pompvermogen (kW). Met die gegevens plus uw huidig tarief kunt u het jaarverbruik berekenen. Op /calculadoras staan gratis tools die het in seconden schatten, en op /ficha-tecnica ontvangt u een geschatte NEN-EN 17645 klassering voor uw zwembad." },
            { question: "Loont een thermische afdekking de investering echt?", answer: "Ja. Een thermische afdekking vermindert de verdamping met 60-70 % en de nachtelijke warmteverliezen met 40-60 %. De terugverdientijd bedraagt 1-3 seizoenen bij verwarmde zwembaden en 2-4 jaar bij onverwarmde, hoofdzakelijk door besparingen op water, chemicaliën en filtratie-uren." },
          ],
        },
    },
  },
  {
    id: "certificado-en-17645",
    slugs: {
      es: "certificado-ambiental-piscina-en-17645",
      en: "pool-environmental-certificate-en-17645",
      fr: "certificat-environnemental-piscine-nf-en-17645",
      de: "pool-umweltzertifikat-din-en-17645",
      it: "certificato-ambientale-piscina-uni-en-17645",
      nl: "zwembad-milieucertificaat-en-17645",
    },
    publishDate: "2026-07-31",
    updatedDate: "2026-07-31",
    readingTime: 10,
    category: "certificacion",
    coverTheme: "light",
    // Portada propia per idioma (1200x630). Els tres passos: dades, vuit components, classe.
    // L'alt descriu el que es VEU, no de que va l'article.
    cover: {
      es: "/img/blog/en17645-certificate-steps-es.jpg",
      en: "/img/blog/en17645-certificate-steps-en.jpg",
      fr: "/img/blog/en17645-certificate-steps-fr.jpg",
      de: "/img/blog/en17645-certificate-steps-de.jpg",
      it: "/img/blog/en17645-certificate-steps-it.jpg",
    },
    coverTitle: {
      es: "Cómo se certifica una piscina",
      en: "How a pool gets certified",
      fr: "Comment une piscine est certifiée",
      de: "Wie ein Pool zertifiziert wird",
      it: "Come si certifica una piscina",
    },
    imageAlt: {
      es: "Los tres pasos de la certificación en fila: el formulario con los datos de la piscina marcado con 2 minutos, los ocho componentes que se evalúan y la escala de clases de la A a la F",
      en: "The three certification steps in a row: the pool data form marked 2 minutes, the eight components assessed, and the class scale from A to F",
      fr: "Les trois étapes de la certification en ligne : le formulaire de données de la piscine marqué 2 minutes, les huit composants évalués et l'échelle de classes de A à F",
      de: "Die drei Schritte der Zertifizierung in einer Reihe: das Formular mit den Pooldaten mit 2 Minuten markiert, die acht bewerteten Komponenten und die Klassenskala von A bis F",
      it: "I tre passi della certificazione in fila: il modulo con i dati della piscina segnato con 2 minuti, gli otto componenti valutati e la scala delle classi dalla A alla F",
    },
    snippetDescription: {
      es: "Guía práctica del certificado ambiental de tu piscina según UNE-EN 17645:2023: clases A-F y los 8 componentes que se evalúan.",
      en: "Practical guide to your pool's environmental certificate under EN 17645:2023: the A-F classes and the 8 components that get assessed.",
      fr: "Guide pratique du certificat environnemental de votre piscine selon la NF EN 17645:2023 : classes A-F et 8 composants évalués.",
      de: "Praktischer Leitfaden zum Umweltzertifikat Ihres Pools nach DIN EN 17645:2023: Klassen A-F und die 8 bewerteten Komponenten.",
      it: "Guida pratica al certificato ambientale della tua piscina secondo UNI EN 17645:2023: classi A-F e gli 8 componenti valutati.",
      nl: "Praktische gids voor het milieucertificaat van uw zwembad volgens NEN-EN 17645:2023: klassen A-F en de 8 geëvalueerde componenten.",
    },
    translations: {
      es: {
        title: "Certificado Ambiental Piscina UNE-EN 17645: Guía 2026",
        h1: "Certificado Ambiental Piscina UNE-EN 17645: Guía 2026",
        description: "Guía práctica para obtener el certificado ambiental de tu piscina según UNE-EN 17645:2023. Clases A-F, 8 componentes evaluados y cómo conseguir tu Ficha Técnica gratis en 2 minutos.",
        keywords: [
          "certificado sostenibilidad piscina",
          "certificado ambiental piscina",
          "UNE-EN 17645",
          "clasificación energética piscina",
          "certificado piscina sostenible",
        ],
        author: "Equipo técnico PoolLex.io",
        Body: Article2BodyES,
        faq: [
          { question: "¿Es obligatorio el certificado ambiental de piscina según UNE-EN 17645?", answer: "No. UNE-EN 17645:2023 es una norma técnica voluntaria armonizada a nivel europeo. Ningún reglamento estatal ni autonómico exige actualmente el certificado, pero algunas ordenanzas municipales lo están empezando a valorar para licencias, y su uso crece en operaciones inmobiliarias, subvenciones y programas de eficiencia." },
          { question: "¿Cuál es la diferencia entre la Ficha Técnica gratuita y el Sello firmado?", answer: "La Ficha Técnica gratuita ofrece una estimación de clase A-F basada en los datos que declaras. Es orientativa y sirve para tener un diagnóstico rápido. El Sello firmado es un documento técnico revisado y firmado por un ingeniero industrial colegiado, con validez para presentar ante administraciones, notarios, aseguradoras o compradores." },
          { question: "¿Qué piscinas se pueden certificar según UNE-EN 17645?", answer: "La norma UNE-EN 17645:2023 se aplica exclusivamente a piscinas residenciales exteriores enterradas. Quedan fuera de su alcance las piscinas públicas, las de uso colectivo, las cubiertas o interiores, las elevadas o desmontables, y las piscinas con fines terapéuticos o deportivos. Estas otras categorías tienen normativa específica." },
          { question: "¿Cuántas piscinas obtienen clase A y cuántas clase F?", answer: "Los datos preliminares del sector, aún limitados por la juventud de la norma, sugieren que en el parque residencial español predominan las clases D y E (parque antiguo con equipos monovelocidad, sin cubierta motorizada y sin climatización eficiente). Menos del 5-10 % del parque alcanzaría hoy la clase A sin intervenciones. Este dato mejora rápidamente con reformas parciales." },
          { question: "¿Cuánto cuesta obtener el sello certificado por ingeniero?", answer: "El coste depende del alcance del informe y de si se aporta documentación técnica previa. En PoolLex.io el sello firmado se ofrece bajo consulta con estimaciones basadas en volumen y equipamiento de la piscina. Contacta a través de /pool-check para presupuesto personalizado." },
        ],
      },
      en: {
        title: "Pool Environmental Certificate EN 17645: 2026 Guide",
        h1: "Pool Environmental Certificate EN 17645: 2026 Guide",
        description: "Practical guide to obtain your pool environmental certificate under EN 17645:2023. A-F classes, the 8 evaluated components, and how to get your Technical Sheet free in 2 minutes.",
        keywords: [
          "pool sustainability certificate",
          "pool environmental certificate",
          "EN 17645",
          "pool energy rating",
          "sustainable pool certificate",
        ],
        author: "PoolLex.io Technical Team",
        Body: Article2BodyEN,
        faq: [
          { question: "Is the pool environmental certificate under EN 17645 mandatory?", answer: "No. EN 17645:2023 is a voluntary European technical standard. No national or regional regulation currently mandates certification, but several municipal ordinances are starting to weight it in permits, and it is gaining ground in real-estate deals, subsidies and efficiency programmes." },
          { question: "What is the difference between the free Technical Sheet and the signed Seal?", answer: "The free Technical Sheet is an estimated A-F class based on the data you declare. It is indicative and works as a fast diagnostic. The signed Seal is a technical document reviewed and signed by a chartered industrial engineer, valid to submit to authorities, notaries, insurers or buyers." },
          { question: "Which pools can be certified under EN 17645?", answer: "EN 17645:2023 applies only to residential outdoor in-ground pools. Public pools, communal pools, indoor or covered pools, above-ground or demountable pools and therapy or sport pools fall outside its scope. Other categories have specific standards." },
          { question: "How many pools reach class A and how many class F?", answer: "Preliminary sector data, still limited given the standard's youth, suggest the Spanish residential stock is concentrated in classes D and E (older pools with single-speed pumps, no motorised cover and no efficient heating). Fewer than 5-10 % would reach class A without upgrades today. That improves quickly with partial retrofits." },
          { question: "How much does the engineer-signed seal cost?", answer: "It depends on the scope of the report and whether prior technical documentation is provided. At PoolLex.io the signed seal is quoted on request based on pool volume and equipment. Reach out via /pool-check for a tailored estimate." },
        ],
      },
      fr: {
        title: "Certificat Environnemental Piscine NF EN 17645 · 2026",
        h1: "Certificat Environnemental Piscine NF EN 17645 · 2026",
        description: "Guide pratique pour obtenir le certificat environnemental de votre piscine selon la NF EN 17645:2023. Classes A-F, 8 composants évalués et comment obtenir votre Fiche Technique gratuite en 2 minutes.",
        keywords: [
          "certificat durabilité piscine",
          "certificat environnemental piscine",
          "NF EN 17645",
          "classe énergétique piscine",
          "certificat piscine durable",
        ],
        author: "Équipe technique PoolLex.io",
        Body: Article2BodyFR,
        faq: [
          { question: "Le certificat environnemental piscine NF EN 17645 est-il obligatoire ?", answer: "Non. La NF EN 17645:2023 est une norme technique européenne volontaire. Aucune réglementation nationale ne l'impose aujourd'hui, mais certaines ordonnances municipales commencent à la valoriser pour les autorisations, et son usage croît dans les opérations immobilières, subventions et programmes d'efficacité." },
          { question: "Quelle est la différence entre la Fiche Technique gratuite et le Sceau signé ?", answer: "La Fiche Technique gratuite offre une estimation de classe A-F basée sur les données déclarées. Elle est indicative et sert au diagnostic rapide. Le Sceau signé est un document technique révisé et signé par un ingénieur, valide pour être présenté aux administrations, notaires, assureurs ou acheteurs." },
          { question: "Quelles piscines peuvent être certifiées selon NF EN 17645 ?", answer: "La NF EN 17645:2023 s'applique exclusivement aux piscines résidentielles extérieures enterrées. Sont exclues les piscines publiques, collectives, couvertes ou intérieures, hors-sol ou démontables et à finalité thérapeutique ou sportive. Ces catégories ont des normes spécifiques." },
          { question: "Combien de piscines atteignent la classe A et combien la classe F ?", answer: "Les données préliminaires du secteur, encore limitées par la jeunesse de la norme, suggèrent que le parc résidentiel se concentre en classes D et E (parc ancien avec pompe monovitesse, sans couverture motorisée ni chauffage efficace). Moins de 5-10 % du parc atteindrait la classe A sans intervention aujourd'hui. Ce chiffre progresse vite avec des réformes partielles." },
          { question: "Combien coûte l'obtention du sceau signé par un ingénieur ?", answer: "Le coût dépend du périmètre du rapport et de la documentation technique préalable. Sur PoolLex.io le sceau signé est sur devis, en fonction du volume et de l'équipement de la piscine. Contactez /pool-check pour un devis personnalisé." },
        ],
      },
      de: {
        title: "Pool Umweltzertifikat DIN EN 17645: Leitfaden 2026",
        h1: "Pool Umweltzertifikat DIN EN 17645: Leitfaden 2026",
        description: "Praktischer Leitfaden zum Umweltzertifikat Ihres Pools nach DIN EN 17645:2023. Klassen A-F, 8 bewertete Komponenten und wie Sie Ihr Datenblatt in 2 Minuten kostenlos erhalten.",
        keywords: [
          "Pool Nachhaltigkeitszertifikat",
          "Pool Umweltzertifikat",
          "DIN EN 17645",
          "Pool Energieausweis",
          "nachhaltiges Pool Zertifikat",
        ],
        author: "PoolLex.io Fachteam",
        Body: Article2BodyDE,
        faq: [
          { question: "Ist das Pool-Umweltzertifikat nach DIN EN 17645 verpflichtend?", answer: "Nein. DIN EN 17645:2023 ist eine freiwillige europäische Norm. Keine staatliche oder länderspezifische Vorschrift verlangt sie derzeit, doch einige kommunale Ordnungen bewerten sie zunehmend für Genehmigungen. Sie gewinnt an Bedeutung bei Immobiliengeschäften, Förderungen und Effizienzprogrammen." },
          { question: "Was ist der Unterschied zwischen dem kostenlosen Datenblatt und dem signierten Siegel?", answer: "Das kostenlose Datenblatt ist eine geschätzte A-F Klasse basierend auf Ihren Angaben. Es ist indikativ und dient der schnellen Diagnose. Das signierte Siegel ist ein von einem Ingenieur geprüftes und unterzeichnetes Dokument, gültig zur Vorlage bei Behörden, Notaren, Versicherern oder Käufern." },
          { question: "Welche Pools können nach DIN EN 17645 zertifiziert werden?", answer: "DIN EN 17645:2023 gilt ausschließlich für private Freibad-Pools mit Einbaukörper. Öffentliche Pools, Gemeinschaftsbecken, Hallenbäder, Aufstell- oder Zerlegbare Pools sowie Therapie- oder Sportbecken fallen nicht unter die Norm. Für diese Kategorien gelten separate Normen." },
          { question: "Wie viele Pools erreichen Klasse A und wie viele Klasse F?", answer: "Vorläufige Sektordaten, noch begrenzt durch das junge Alter der Norm, deuten darauf hin, dass der private Poolbestand sich auf Klassen D und E konzentriert (ältere Anlagen mit einstufiger Pumpe, ohne motorisierte Abdeckung und ohne effiziente Heizung). Weniger als 5-10 % des Bestands erreichten heute die Klasse A ohne Umbauten. Teilsanierungen verbessern das Bild schnell." },
          { question: "Was kostet das vom Ingenieur signierte Siegel?", answer: "Der Preis hängt vom Umfang des Berichts und von der vorhandenen technischen Dokumentation ab. Bei PoolLex.io wird das signierte Siegel auf Anfrage kalkuliert, basierend auf Volumen und Ausstattung. Kontaktieren Sie /pool-check für ein individuelles Angebot." },
        ],
      },
      it: {
        title: "Certificato Ambientale Piscina UNI EN 17645 · 2026",
        h1: "Certificato Ambientale Piscina UNI EN 17645 · 2026",
        description: "Guida pratica per ottenere il certificato ambientale della tua piscina secondo UNI EN 17645:2023. Classi A-F, 8 componenti valutati e come ricevere la Scheda Tecnica gratis in 2 minuti.",
        keywords: [
          "certificato sostenibilità piscina",
          "certificato ambientale piscina",
          "UNI EN 17645",
          "classe energetica piscina",
          "certificato piscina sostenibile",
        ],
        author: "Team tecnico PoolLex.io",
        Body: Article2BodyIT,
        faq: [
          { question: "Il certificato ambientale piscina UNI EN 17645 è obbligatorio?", answer: "No. La UNI EN 17645:2023 è una norma europea volontaria. Nessun regolamento statale la impone oggi, ma alcuni regolamenti comunali cominciano a valorizzarla per le licenze e il suo uso cresce nelle operazioni immobiliari, negli incentivi e nei programmi di efficienza." },
          { question: "Che differenza c'è tra la Scheda Tecnica gratuita e il Sigillo firmato?", answer: "La Scheda Tecnica gratuita fornisce una stima di classe A-F basata sui dati dichiarati. È indicativa e serve come diagnosi rapida. Il Sigillo firmato è un documento tecnico rivisto e firmato da un ingegnere, valido per la presentazione ad amministrazioni, notai, assicurazioni o acquirenti." },
          { question: "Quali piscine possono essere certificate secondo UNI EN 17645?", answer: "La UNI EN 17645:2023 si applica esclusivamente alle piscine residenziali esterne interrate. Sono escluse le piscine pubbliche, collettive, coperte o interne, fuori terra o smontabili, e quelle a scopo terapeutico o sportivo. Queste categorie hanno normative specifiche." },
          { question: "Quante piscine ottengono classe A e quante classe F?", answer: "I dati preliminari del settore, ancora limitati per la giovinezza della norma, suggeriscono che il parco residenziale si concentra sulle classi D ed E (parco vecchio con pompe monovelocità, senza copertura motorizzata e senza riscaldamento efficiente). Meno del 5-10 % raggiungerebbe oggi la classe A senza interventi. Le riqualificazioni parziali migliorano rapidamente il quadro." },
          { question: "Quanto costa ottenere il sigillo firmato dall'ingegnere?", answer: "Il costo dipende dallo scope del rapporto e dalla documentazione tecnica già disponibile. Su PoolLex.io il sigillo firmato è su preventivo, in base a volume ed equipaggiamento. Contatta /pool-check per un preventivo personalizzato." },
        ],
      },
        nl: {
          title: "Milieucertificaat Zwembad NEN-EN 17645: Gids 2026",
          h1: "Milieucertificaat Zwembad NEN-EN 17645: Gids 2026",
          description: "Praktische gids om het milieucertificaat van uw zwembad te bekomen volgens NEN-EN 17645:2023. Klassen A-F, 8 geëvalueerde componenten en hoe u uw Technische Fiche gratis krijgt in 2 minuten.",
          keywords: [
            "zwembad duurzaamheidscertificaat",
            "milieucertificaat zwembad",
            "NEN-EN 17645",
            "energielabel zwembad",
            "duurzaam zwembad certificaat",
          ],
          author: "PoolLex.io technisch team",
          Body: Article2BodyNL,
          faq: [
            { question: "Is het milieucertificaat voor zwembaden volgens NEN-EN 17645 verplicht?", answer: "Nee. NEN-EN 17645:2023 is een vrijwillige Europees geharmoniseerde technische norm. Geen enkele nationale regelgeving vereist momenteel het certificaat, maar sommige gemeentelijke verordeningen beginnen het te waarderen voor vergunningen, en het gebruik ervan groeit in vastgoedtransacties, subsidies en efficiëntieprogramma's." },
            { question: "Wat is het verschil tussen de gratis Technische Fiche en het ondertekend Zegel?", answer: "De gratis Technische Fiche biedt een geschatte A-F klasse gebaseerd op de gegevens die u opgeeft. Ze is indicatief en dient voor een snelle diagnose. Het ondertekend Zegel is een technisch document nagekeken en ondertekend door een gediplomeerd ingenieur (ir.), geldig om voor te leggen aan overheden, notarissen, verzekeraars of kopers." },
            { question: "Welke zwembaden kunnen gecertificeerd worden volgens NEN-EN 17645?", answer: "De norm NEN-EN 17645:2023 is uitsluitend van toepassing op residentiële buitenzwembaden in de grond. Publieke zwembaden, collectieve zwembaden, overdekte of binnenzwembaden, bovengrondse of demonteerbare zwembaden en therapeutische of sportzwembaden vallen buiten het toepassingsgebied. Deze categorieën hebben specifieke normen." },
            { question: "Hoeveel zwembaden halen klasse A en hoeveel klasse F?", answer: "Voorlopige sectorgegevens, nog beperkt door de jeugd van de norm, suggereren dat het residentiële zwembadpark zich concentreert in klassen D en E (ouder park met éénsnelheidspompen, zonder gemotoriseerde afdekking en zonder efficiënte verwarming). Minder dan 5-10 % van het park zou vandaag klasse A bereiken zonder interventies. Dit cijfer verbetert snel met gedeeltelijke renovaties." },
            { question: "Hoeveel kost het door een ingenieur ondertekend zegel?", answer: "De kost hangt af van de omvang van het rapport en of er voorafgaande technische documentatie beschikbaar is. Bij PoolLex.io wordt het ondertekend zegel op offerte aangeboden, gebaseerd op volume en uitrusting van het zwembad. Neem contact op via /pool-check voor een gepersonaliseerde offerte." },
          ],
        },
    },
  },
  {
    id: "ahorro-energetico-15-acciones",
    slugs: {
      es: "ahorro-energetico-piscina-guia-completa",
      en: "pool-energy-savings-complete-guide",
      fr: "economies-energie-piscine-guide-complet",
      de: "pool-energie-sparen-komplette-anleitung",
      it: "risparmio-energetico-piscina-guida-completa",
      nl: "zwembad-energie-besparen-complete-gids",
    },
    publishDate: "2026-07-31",
    updatedDate: "2026-07-31",
    readingTime: 12,
    category: "ahorro-energetico",
    coverTheme: "light",
    // Portada propia per idioma (1200x630). L'escala que baixa del 100 % al 30 % en quinze graons.
    // L'alt descriu el que es VEU, no de que va l'article.
    cover: {
      es: "/img/blog/pool-savings-15-actions-es.jpg",
      en: "/img/blog/pool-savings-15-actions-en.jpg",
      fr: "/img/blog/pool-savings-15-actions-fr.jpg",
      de: "/img/blog/pool-savings-15-actions-de.jpg",
      it: "/img/blog/pool-savings-15-actions-it.jpg",
    },
    coverTitle: {
      es: "15 acciones para gastar menos en la piscina",
      en: "15 actions to spend less on the pool",
      fr: "15 actions pour dépenser moins sur la piscine",
      de: "15 Maßnahmen für weniger Poolverbrauch",
      it: "15 azioni per spendere meno sulla piscina",
    },
    imageAlt: {
      es: "Una escalera descendente de quince peldaños que baja el consumo del 100 % al 30 %, con las tres columnas que describen cada acción: inversión, ahorro anual y ROI",
      en: "A descending staircase of fifteen steps taking consumption from 100 % down to 30 %, with the three columns that describe each action: investment, annual saving and ROI",
      fr: "Un escalier descendant de quinze marches qui fait passer la consommation de 100 % à 30 %, avec les trois colonnes qui décrivent chaque action : investissement, économie annuelle et ROI",
      de: "Eine absteigende Treppe mit fünfzehn Stufen, die den Verbrauch von 100 % auf 30 % senkt, mit den drei Spalten zu jeder Maßnahme: Investition, jährliche Ersparnis und ROI",
      it: "Una scala discendente di quindici gradini che porta il consumo dal 100 % al 30 %, con le tre colonne che descrivono ogni azione: investimento, risparmio annuo e ROI",
    },
    snippetDescription: {
      es: "15 acciones para reducir el consumo de tu piscina hasta un 70 %. Inversión, ahorro anual y ROI de cada medida, una por una.",
      fr: "15 actions pour réduire la consommation de votre piscine jusqu'à 70 %. Investissement, économies annuelles et ROI de chacune.",
      de: "15 Maßnahmen, die den Poolverbrauch um bis zu 70 % senken. Investition, jährliche Ersparnis und ROI jeder einzelnen Maßnahme.",
      nl: "15 acties die het verbruik van uw zwembad tot 70 % verlagen. Investering, jaarlijkse besparing en ROI van elke maatregel.",
    },
    translations: {
      es: {
        title: "Ahorra hasta 70% en Piscina: 15 Acciones con ROI · 2026",
        h1: "Ahorra hasta 70% en Piscina: 15 Acciones Concretas con ROI",
        description: "Guía práctica con 15 acciones para reducir el consumo de tu piscina hasta un 70%. Inversión, ahorro anual y ROI de cada medida basados en la norma UNE-EN 17645.",
        keywords: [
          "ahorro energético piscina",
          "reducir consumo piscina",
          "piscina eficiente",
          "eficiencia energética piscina",
          "ROI piscina sostenible",
        ],
        author: "Equipo técnico PoolLex.io",
        Body: Article3BodyES,
        faq: [
          { question: "¿Cuánto consume una piscina respecto al total de la vivienda?", answer: "Una piscina residencial exterior representa entre el 15 y el 25 % del consumo eléctrico anual de una vivienda unifamiliar con piscina, y puede superar el 35 % si está climatizada con bomba de calor de mayo a octubre. En consumo de agua, la piscina puede suponer entre el 8 y el 15 % del total anual del hogar, principalmente por evaporación." },
          { question: "¿Qué medida de ahorro tiene mejor ROI en una piscina existente?", answer: "La cubierta térmica motorizada es la medida de mayor retorno sobre inversión: reduce evaporación 60-70 %, pérdidas de calor 40-60 %, horas de filtración necesarias y consumo de químicos. En piscinas climatizadas su ROI se sitúa entre 1 y 3 temporadas." },
          { question: "¿Merece la pena instalar placas solares fotovoltaicas para la piscina?", answer: "Depende del consumo anual. En piscinas con climatización o con bomba VSD y uso prolongado (consumo superior a 3.000 kWh/año) la solar fotovoltaica dedicada tiene sentido económico, con ROI típico entre 6 y 10 años según radiación y precio de la electricidad. Para piscinas de temporada corta sin climatización, el ROI se alarga y compensa menos." },
          { question: "¿Cuánto ahorra una bomba de filtración de velocidad variable (VSD)?", answer: "Una bomba VSD bien programada ahorra entre el 60 y el 80 % del consumo eléctrico de filtración respecto a una bomba monovelocidad equivalente. La clave es aprovechar que a menor caudal el consumo cae de forma no lineal (aproximadamente al cubo de la velocidad, según las leyes de afinidad hidráulica), y que la piscina se filtra igual moviendo más volumen a menor velocidad durante más horas." },
          { question: "¿Cuánto tiempo debe filtrar mi piscina cada día?", answer: "Regla orientativa: horas diarias de filtración ≈ temperatura del agua en °C / 2. Para agua a 26 °C, aproximadamente 13 horas. Para agua a 20 °C, 10 horas. Esta regla debe ajustarse al caudal real de la bomba, al volumen de la piscina y al número de bañistas. Puedes calcular el óptimo para tu instalación en /calculadoras." },
        ],
      },
      en: {
        title: "Cut Pool Costs up to 70%: 15 Actions with ROI · 2026",
        h1: "Cut Pool Costs up to 70%: 15 Concrete Actions with ROI",
        description: "Practical guide with 15 actions to cut pool consumption by up to 70%. Investment, annual savings and ROI for each measure based on the EN 17645 standard.",
        keywords: [
          "pool energy savings",
          "reduce pool consumption",
          "efficient pool",
          "pool energy efficiency",
          "sustainable pool ROI",
        ],
        author: "PoolLex.io Technical Team",
        Body: Article3BodyEN,
        faq: [
          { question: "How much does a pool consume relative to the whole home?", answer: "An outdoor residential pool accounts for 15 to 25 % of the annual electricity use of a single-family home with a pool, and can exceed 35 % if heated with a heat pump from May to October. In water terms the pool typically represents 8 to 15 % of the annual household total, mainly through evaporation." },
          { question: "Which saving measure has the best ROI on an existing pool?", answer: "The motorised thermal cover is the highest-ROI action: it cuts evaporation 60-70 %, heat loss 40-60 %, filtration hours and chemicals. On heated pools payback lands between 1 and 3 seasons." },
          { question: "Is installing solar PV panels for the pool worth it?", answer: "It depends on annual consumption. On heated pools or those with a VSD pump and long use (over 3,000 kWh/year) dedicated solar PV makes economic sense, with typical ROI between 6 and 10 years depending on solar radiation and power price. On short-season unheated pools the payback lengthens and it pays back less." },
          { question: "How much does a variable-speed (VSD) filtration pump save?", answer: "A well-programmed VSD pump saves 60-80 % of the filtration electricity compared with an equivalent single-speed pump. The key is that at lower flow the consumption drops non-linearly (approximately with the cube of the speed, per hydraulic affinity laws), and the pool filters the same by moving more volume at lower speed for longer hours." },
          { question: "How long should my pool filter each day?", answer: "Rule of thumb: daily filtration hours ≈ water temperature in °C / 2. At 26 °C water, roughly 13 hours. At 20 °C, 10 hours. Adjust to real pump flow, pool volume and bather count. You can compute the optimum for your setup at /calculadoras." },
        ],
      },
      fr: {
        title: "Économisez 70% en Piscine : 15 Actions ROI · Guide 2026",
        h1: "Économisez 70% en Piscine : 15 Actions Concrètes avec ROI",
        description: "Guide pratique avec 15 actions pour réduire la consommation de votre piscine jusqu'à 70 %. Investissement, économies annuelles et ROI de chaque mesure selon la NF EN 17645.",
        keywords: [
          "économies énergie piscine",
          "réduire consommation piscine",
          "piscine efficiente",
          "efficacité énergétique piscine",
          "ROI piscine durable",
        ],
        author: "Équipe technique PoolLex.io",
        Body: Article3BodyFR,
        faq: [
          { question: "Combien consomme une piscine par rapport au foyer ?", answer: "Une piscine extérieure résidentielle représente 15 à 25 % de la consommation électrique annuelle d'une maison unifamiliale, et peut dépasser 35 % si chauffée par pompe à chaleur de mai à octobre. En eau, elle pèse 8 à 15 % du total annuel domestique, principalement par évaporation." },
          { question: "Quelle mesure a le meilleur ROI sur une piscine existante ?", answer: "La couverture thermique motorisée est la mesure au meilleur ROI : elle réduit l'évaporation 60-70 %, les pertes de chaleur 40-60 %, les heures de filtration et les produits chimiques. Sur piscines chauffées, l'amortissement est de 1-3 saisons." },
          { question: "Faut-il installer des panneaux solaires photovoltaïques pour la piscine ?", answer: "Cela dépend de la consommation annuelle. Sur piscines chauffées ou avec pompe VSD et usage prolongé (plus de 3 000 kWh/an) le solaire photovoltaïque dédié a du sens, avec un ROI typique entre 6 et 10 ans selon rayonnement et prix de l'électricité. Sur piscines de courte saison non chauffées, le ROI s'allonge." },
          { question: "Combien fait économiser une pompe VSD (vitesse variable) ?", answer: "Une pompe VSD bien programmée économise 60 à 80 % de la consommation électrique de filtration par rapport à une pompe monovitesse équivalente. La clé : à faible débit la consommation chute de façon non linéaire (environ au cube de la vitesse, selon les lois d'affinité hydraulique), et la piscine se filtre autant en déplaçant plus de volume à vitesse plus basse pendant plus d'heures." },
          { question: "Combien de temps faut-il filtrer ma piscine par jour ?", answer: "Règle indicative : heures de filtration quotidiennes ≈ température de l'eau en °C / 2. À 26 °C, environ 13 heures. À 20 °C, 10 heures. Ajustez au débit réel de la pompe, au volume et au nombre de baigneurs. Calculez l'optimum sur /calculadoras." },
        ],
      },
      de: {
        title: "Pool: 70% Sparen · 15 Maßnahmen mit ROI · Anleitung 2026",
        h1: "Pool 70% Sparen: 15 Konkrete Maßnahmen mit ROI",
        description: "Praktische Anleitung mit 15 Maßnahmen zur Reduzierung des Poolverbrauchs um bis zu 70 %. Investition, jährliche Ersparnis und ROI jeder Maßnahme nach DIN EN 17645.",
        keywords: [
          "Pool Energie sparen",
          "Pool Verbrauch reduzieren",
          "effizienter Pool",
          "Pool Energieeffizienz",
          "Nachhaltigkeit Pool ROI",
        ],
        author: "PoolLex.io Fachteam",
        Body: Article3BodyDE,
        faq: [
          { question: "Wie viel verbraucht ein Pool im Verhältnis zum gesamten Haushalt?", answer: "Ein privater Freibad-Pool macht 15 bis 25 % des Jahresstromverbrauchs eines Einfamilienhauses mit Pool aus und kann 35 % übersteigen, wenn er von Mai bis Oktober per Wärmepumpe beheizt wird. Beim Wasser entfallen 8 bis 15 % des Jahresverbrauchs des Haushalts auf den Pool, hauptsächlich durch Verdunstung." },
          { question: "Welche Sparmaßnahme hat den besten ROI bei einem bestehenden Pool?", answer: "Die motorisierte Thermoabdeckung liefert den besten ROI: sie senkt Verdunstung um 60-70 %, Wärmeverluste um 40-60 %, Filterlaufzeit und Chemieverbrauch. Bei beheizten Pools liegt die Amortisation bei 1-3 Saisons." },
          { question: "Lohnt sich Photovoltaik für den Pool?", answer: "Es kommt auf den Jahresverbrauch an. Bei beheizten Pools oder solchen mit VSD-Pumpe und langem Einsatz (über 3.000 kWh/Jahr) ist dedizierte Photovoltaik wirtschaftlich sinnvoll, mit typischer Amortisation von 6-10 Jahren je nach Strahlung und Strompreis. Bei kurzen Saisons ohne Heizung dauert der ROI länger." },
          { question: "Wie viel spart eine drehzahlgeregelte (VSD) Filterpumpe?", answer: "Eine gut programmierte VSD-Pumpe spart 60 bis 80 % des Filterstroms gegenüber einer vergleichbaren einstufigen Pumpe. Der Schlüssel: bei geringerem Durchfluss sinkt der Verbrauch nicht linear (etwa mit der dritten Potenz der Drehzahl, nach den hydraulischen Ähnlichkeitsgesetzen), und der Pool wird gleich gut gefiltert, wenn länger und langsamer mehr Volumen bewegt wird." },
          { question: "Wie lange sollte mein Pool täglich filtern?", answer: "Faustregel: tägliche Filterstunden ≈ Wassertemperatur in °C / 2. Bei 26 °C etwa 13 Stunden. Bei 20 °C 10 Stunden. Passen Sie an den realen Pumpenfluss, das Volumen und die Nutzerzahl an. Das Optimum berechnen Sie unter /calculadoras." },
        ],
      },
      it: {
        title: "Piscina: Risparmia 70% · 15 Azioni con ROI · Guida 2026",
        h1: "Piscina: Risparmia 70% · 15 Azioni Concrete con ROI",
        description: "Guida pratica con 15 azioni per ridurre i consumi della piscina fino al 70 %. Investimento, risparmio annuo e ROI di ogni misura secondo la UNI EN 17645.",
        keywords: [
          "risparmio energetico piscina",
          "ridurre consumo piscina",
          "piscina efficiente",
          "efficienza energetica piscina",
          "ROI piscina sostenibile",
        ],
        author: "Team tecnico PoolLex.io",
        Body: Article3BodyIT,
        faq: [
          { question: "Quanto consuma una piscina rispetto al totale dell'abitazione?", answer: "Una piscina residenziale esterna rappresenta il 15-25 % del consumo elettrico annuo di una casa unifamiliare con piscina, e può superare il 35 % se riscaldata con pompa di calore da maggio a ottobre. In termini d'acqua pesa l'8-15 % del totale annuo domestico, soprattutto per evaporazione." },
          { question: "Quale misura ha il miglior ROI su una piscina esistente?", answer: "La copertura termica motorizzata è la misura con ROI migliore: riduce l'evaporazione 60-70 %, le perdite di calore 40-60 %, le ore di filtrazione e i prodotti chimici. Su piscine riscaldate l'ammortamento è di 1-3 stagioni." },
          { question: "Vale la pena installare pannelli solari fotovoltaici per la piscina?", answer: "Dipende dal consumo annuo. Su piscine riscaldate o con pompa VSD e uso prolungato (oltre 3.000 kWh/anno) il fotovoltaico dedicato ha senso economico, con ROI tipico di 6-10 anni in base a irraggiamento e prezzo dell'elettricità. Su piscine a stagione breve non riscaldate il ROI si allunga." },
          { question: "Quanto risparmia una pompa di filtrazione a velocità variabile (VSD)?", answer: "Una pompa VSD ben programmata risparmia il 60-80 % del consumo elettrico di filtrazione rispetto a una monovelocità equivalente. La chiave: a portata inferiore il consumo cala in modo non lineare (circa al cubo della velocità, secondo le leggi di affinità idraulica), e la piscina si filtra ugualmente spostando più volume a velocità minore per più ore." },
          { question: "Quanto tempo deve filtrare la mia piscina ogni giorno?", answer: "Regola indicativa: ore di filtrazione giornaliera ≈ temperatura dell'acqua in °C / 2. A 26 °C circa 13 ore. A 20 °C circa 10 ore. Va calibrata sulla portata reale della pompa, sul volume e sul numero di bagnanti. Puoi calcolare l'ottimo per la tua installazione su /calculadoras." },
        ],
      },
        nl: {
          title: "Zwembad: Bespaar tot 70% · 15 Acties met ROI · Gids 2026",
          h1: "Zwembad: Bespaar tot 70% · 15 Concrete Acties met ROI",
          description: "Praktische gids met 15 acties om het verbruik van uw zwembad tot 70 % te verminderen. Investering, jaarlijkse besparing en ROI van elke maatregel gebaseerd op de norm NEN-EN 17645.",
          keywords: [
            "zwembad energie besparen",
            "zwembad verbruik verminderen",
            "efficient zwembad",
            "energie-efficiëntie zwembad",
            "ROI duurzaam zwembad",
          ],
          author: "PoolLex.io technisch team",
          Body: Article3BodyNL,
          faq: [
            { question: "Hoeveel verbruikt een zwembad in verhouding tot de hele woning?", answer: "Een residentieel buitenzwembad vertegenwoordigt 15 tot 25 % van het jaarlijkse elektriciteitsverbruik van een eengezinswoning met zwembad, en kan meer dan 35 % overschrijden indien verwarmd met een warmtepomp van mei tot oktober. Qua waterverbruik vertegenwoordigt het zwembad 8 tot 15 % van het jaarlijkse huishoudelijk totaal, hoofdzakelijk door verdamping." },
            { question: "Welke besparingsmaatregel heeft de beste ROI op een bestaand zwembad?", answer: "De gemotoriseerde thermische afdekking is de maatregel met de hoogste ROI: ze vermindert verdamping 60-70 %, warmteverlies 40-60 %, filtratie-uren en chemicaliën. Op verwarmde zwembaden bedraagt de terugverdientijd 1-3 seizoenen." },
            { question: "Loont het om fotovoltaïsche zonnepanelen voor het zwembad te installeren?", answer: "Het hangt af van het jaarverbruik. Op verwarmde zwembaden of zwembaden met VSD-pomp en langdurig gebruik (meer dan 3.000 kWh/jaar) heeft toegewijde fotovoltaïsche zonne-energie economische zin, met een typische ROI tussen 6 en 10 jaar afhankelijk van straling en elektriciteitsprijs. Voor onverwarmde zwembaden met een kort seizoen verlengt de ROI zich en loont het minder." },
            { question: "Hoeveel bespaart een filtratiepomp met variabele snelheid (VSD)?", answer: "Een goed geprogrammeerde VSD-pomp bespaart 60 tot 80 % van het elektriciteitsverbruik voor filtratie vergeleken met een gelijkwaardige éénsnelheidspomp. De sleutel: bij lager debiet daalt het verbruik op niet-lineaire wijze (ongeveer met de derde macht van de snelheid, volgens de hydraulische affiniteitswetten), en het zwembad wordt evengoed gefilterd door meer volume aan lagere snelheid en langere uren te verplaatsen." },
            { question: "Hoe lang moet mijn zwembad elke dag filteren?", answer: "Vuistregel: dagelijkse filtratie-uren ≈ watertemperatuur in °C / 2. Voor water aan 26 °C ongeveer 13 uur. Voor water aan 20 °C 10 uur. Deze regel moet worden aangepast aan het reële debiet van de pomp, het volume van het zwembad en het aantal zwemmers. U kunt het optimum voor uw installatie berekenen op /calculadoras." },
          ],
        },
    },
  },
  // ==========================================================================
  // Sprint 119.100 (2026-08-12) — 5 nous articles single-language des de GSC
  // queries reals. Cadascun captura una query amb tràfic real; sense
  // traduccions fins que es validi rendiment.
  // ==========================================================================
  {
    id: "certificado-finalizacion-obra-piscina",
    slugs: {
      es: "certificado-finalizacion-obra-piscina",
    },
    publishDate: "2026-08-12",
    updatedDate: "2026-08-12",
    readingTime: 9,
    category: "normativa",
    coverTheme: "dark",
    // Sprint 149 — Portada propia (1200x630), mateix sistema visual que la
    // resta de la serie. Dibuixa el paquet de documentacio: el CFO no es un
    // paper, son SIS documents, i el destacat es el CERTIFICADO FINAL VISADO,
    // que es el que dona nom al trami. L'escala inferior es el termini de
    // resolucio, 1-4 mesos.
    //
    // Dos intents descartats per Victor, que es qui firma aquests documents:
    //   · partir la piscina entre obra civil i instal-lacions amb el tecnic
    //     que signa cadascuna;
    //   · destacar la prova d'estanqueitat amb el seu criteri (<1 mm/24 h).
    //     Diu que aquell document se'l va inventar, aixi que s'ha retirat de
    //     la portada I dels tres llocs on sortia al cos. Per aixo son SIS
    //     documents i no set.
    //
    // NO hi van els honoraris (150-900 EUR): a l'article tenen context, pero
    // una xifra en gran a una portada de PoolLex es llegeix com el nostre preu.
    cover: {
      es: "/img/blog/cfo-piscina-documentos-es.jpg",
    },
    coverTitle: {
      es: "Certificado final de obra de piscina",
    },
    // El alt descriu el que es VEU. El primer descrivia el document signat.
    imageAlt: {
      es: "Lista de los seis documentos del certificado final de obra de una piscina, con el certificado final visado destacado, y una escala de 1 a 4 meses de resolución municipal",
    },
    translations: {
      es: {
        title: "Certificado Finalización Obra Piscina: Guía Completa 2026",
        h1: "Certificado de Finalización de Obra para Piscinas Residenciales",
        description: "Guía práctica del certificado final de obra (CFO) para piscinas: cuándo es obligatorio, quién puede firmarlo, documentación, coste (150-900€) y procedimiento en el ayuntamiento.",
        keywords: [
          "certificado final de obra piscina",
          "certificado finalización obra piscina",
          "CFO piscina",
          "legalización piscina",
          "documentación final obra piscina",
        ],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article6BodyES,
        faq: [
          { question: "¿Es obligatorio el certificado final de obra para todas las piscinas?", answer: "No. Depende del tipo de licencia inicial. Piscinas enterradas con obra mayor: CFO obligatorio. Piscinas prefabricadas con obra menor: certificado de instalación (equiparable a CFO simplificado en algunos municipios). Piscinas desmontables sin obra: normalmente no se exige CFO, aunque puede requerirse declaración responsable o comunicación previa según ordenanza." },
          { question: "¿Quién puede firmar el certificado final de obra de una piscina?", answer: "Técnicos competentes según la LOE: arquitecto o arquitecto técnico para la obra civil (vaso, coronación, playa) e ingeniero industrial o técnico industrial para las instalaciones (sala de máquinas, hidráulica, electricidad, climatización). Ambos deben estar colegiados y con visado si aplica en tu comunidad autónoma." },
          { question: "¿Qué diferencia hay entre el CFO y la legalización urbanística?", answer: "Son procedimientos distintos. El CFO es el cierre normal de una obra que tuvo licencia previa; es un trámite ordinario. La legalización urbanística regulariza una piscina construida sin licencia o con desviaciones sustanciales del proyecto aprobado; puede acabar con multa o incluso demolición. Si no tenías licencia, primero legalizas y después obtienes CFO retrospectivo." },
          { question: "¿Cuánto cuesta el certificado final de obra de una piscina?", answer: "Honorarios profesionales orientativos en 2026: 150-300 € para piscinas prefabricadas pequeñas, 300-500 € para piscinas enterradas medias con instalaciones estándar y 500-900 € para piscinas grandes con climatización o dotación compleja. A esto se suma la tasa municipal de finalización de obra, habitualmente 60-200 € o 0,5-1,5 % del PEM." },
          { question: "¿Cuánto tarda el ayuntamiento en resolver el certificado final de obra?", answer: "Entre 1 y 4 meses desde la presentación con la documentación completa. El tiempo depende de la cola de expedientes del ayuntamiento y de si hay requerimientos de subsanación. Presentar la documentación revisada previamente con un chequeo en /pool-check reduce significativamente los tiempos de tramitación." },
        ],
      },
    },
  },
  {
    id: "costes-operativos-piscina-2026",
    slugs: {
      es: "costes-operativos-piscina-2026",
    },
    publishDate: "2026-08-12",
    updatedDate: "2026-08-12",
    readingTime: 8,
    category: "costes-mensuales",
    coverTheme: "light",
    // Portada propia per idioma (1200x630). Els dos casos comparats i les cinc partides d'OPEX.
    // L'alt descriu el que es VEU, no de que va l'article.
    cover: {
      es: "/img/blog/pool-opex-30-50-m3-es.jpg",
    },
    coverTitle: {
      es: "Costes operativos de una piscina residencial",
    },
    imageAlt: {
      es: "Las plantas de las dos piscinas comparadas en el artículo, de 30 m³ y 50 m³, y debajo las cinco partidas que componen el OPEX: electricidad, agua, químicos, mano de obra y amortización",
    },
    snippetTitle: {
      es: "Costes operativos piscina 2026: 600-1.800 €/año",
    },
    snippetDescription: {
      es: "Desglose del OPEX real de una piscina en 2026: electricidad, agua, químicos, mano de obra y amortización. Casos de 30 y 50 m³.",
    },
    translations: {
      es: {
        title: "Costes Operativos Piscina 2026: 600-1800€/año · Desglose Real",
        h1: "Costes Operativos de una Piscina Residencial en 2026",
        description: "Desglose completo del OPEX real de una piscina residencial en 2026: electricidad, agua, químicos, mano de obra y amortización. Casos reales de 30 m³ y 50 m³.",
        keywords: [
          "costes operativos piscina",
          "coste operativo piscina anual",
          "OPEX piscina",
          "presupuesto mantenimiento piscina",
          "coste real piscina 2026",
        ],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article4BodyES,
        faq: [
          { question: "¿Qué diferencia hay entre coste operativo y coste de instalación de una piscina?", answer: "El coste de instalación (CAPEX) es la inversión inicial única: obra civil, vaso, sala de máquinas y primera puesta en marcha. El coste operativo (OPEX) es todo lo que la piscina consume año tras año para funcionar: electricidad, agua, productos químicos, mano de obra y reparaciones. Este artículo se centra exclusivamente en el OPEX anual." },
          { question: "¿Cuál es el coste operativo anual medio de una piscina en 2026?", answer: "Entre 600 y 1.800 €/año para una piscina residencial exterior enterrada, según tamaño, equipamiento y clima. Una piscina pequeña no climatizada gestionada por el propietario está en torno a 600 €/año; una piscina media climatizada con servicio externo de mantenimiento puede superar los 1.600 €/año." },
          { question: "¿Qué partida pesa más en el OPEX de una piscina?", answer: "La electricidad, con un 40-65 % del total anual. Le siguen la mano de obra y mantenimiento programado (10-25 %), productos químicos (10-18 %), reparaciones y amortización (5-12 %) y agua de reposición (8-15 %). En piscinas climatizadas la electricidad puede superar el 70 %." },
          { question: "¿Cuánto se ahorra con una bomba de calor frente a resistencia eléctrica?", answer: "Una bomba de calor con COP 5 aporta 5 kWh térmicos por cada kWh eléctrico consumido, frente al 1:1 de una resistencia. El ahorro anual típico es de 600-1.200 €/año para el mismo salto térmico, con amortización de la inversión adicional en 3-5 temporadas." },
          { question: "¿Merece la pena una cubierta térmica motorizada en términos de OPEX?", answer: "Sí. Reduce la evaporación 60-70 %, las pérdidas de calor nocturnas 40-60 %, las horas necesarias de filtración y el consumo de químicos. En piscinas climatizadas ahorra 250-450 €/año en energía y 40-80 €/año en agua y químicos. El retorno económico es de 6-10 años, pero además es el componente que más puntos otorga en la clasificación UNE-EN 17645." },
        ],
      },
    },
  },
  {
    id: "calculadora-m3-piscina-redonda",
    slugs: {
      es: "calculadora-m3-piscina-redonda-formula",
    },
    publishDate: "2026-08-12",
    updatedDate: "2026-08-12",
    readingTime: 7,
    category: "calculadoras",
    coverTheme: "light",
    // Portada propia per idioma (1200x630). Planta i seccio acotades amb l'exemple resolt de les FAQ.
    // L'alt descriu el que es VEU, no de que va l'article.
    cover: {
      es: "/img/blog/round-pool-volume-formula-es.jpg",
    },
    coverTitle: {
      es: "Cómo calcular los m³ de una piscina redonda",
    },
    imageAlt: {
      es: "Planta y sección acotadas de una piscina redonda de 4,57 m de diámetro con 1,07 m de altura de agua, y el resultado de aplicar la fórmula: 17.550 litros",
    },
    snippetDescription: {
      es: "Fórmula V = π × r² × h paso a paso, tabla para diámetros de 3 a 6 m y ejemplos resueltos para piscinas Intex y Bestway.",
    },
    translations: {
      es: {
        title: "Calculadora M3 Piscina Redonda: Fórmula + Tabla + Ejemplos",
        h1: "Cómo Calcular los M3 de una Piscina Redonda: Fórmula y Tabla",
        description: "Fórmula V = π × r² × h explicada paso a paso, tabla de referencia para diámetros de 3 a 6 m, ejemplos resueltos para piscinas Intex y Bestway. Calculadora online gratis.",
        keywords: [
          "calcular m3 piscina redonda",
          "calculadora piscina redonda",
          "volumen piscina redonda",
          "fórmula volumen piscina circular",
          "m3 piscina intex",
        ],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article5BodyES,
        faq: [
          { question: "¿Cuál es la fórmula para calcular los m3 de una piscina redonda?", answer: "V (m³) = π × r² × h, donde π ≈ 3,1416, r es el radio en metros (la mitad del diámetro) y h es la altura del agua en metros (no la altura de la pared). Equivalente con diámetro directo: V ≈ 0,7854 × D² × h." },
          { question: "¿Cuántos litros tiene una piscina redonda de 4,57 m de diámetro?", answer: "Aproximadamente 17.550 litros (17,55 m³) con altura de agua de 1,07 m, típica en piscinas desmontables Intex Prism Frame 4,57 × 1,22 m llenadas al 90 % de la pared. Si tu piscina tiene una altura de agua distinta, aplica la fórmula V = 3,1416 × 2,285² × h_agua." },
          { question: "¿Por qué el volumen que pone la caja de mi Intex no coincide con mi cálculo?", answer: "Los fabricantes declaran el volumen a llenado del 80-90 % de la altura de la pared, no del 100 %, por seguridad ante desbordamientos por peso de los bañistas. Tu cálculo teórico con altura de pared da un número mayor; el real depende de dónde marques el nivel de agua. Para dosificar químicos usa el volumen real, no el declarado." },
          { question: "¿Cómo convierto los m3 a litros?", answer: "1 m³ equivale exactamente a 1.000 litros. Por tanto, una piscina de 17,55 m³ contiene 17.550 litros. Para galones USA multiplica los m³ por 264,17; para galones UK, por 219,97." },
          { question: "¿Por qué es tan importante conocer el volumen exacto de mi piscina?", answer: "Cinco decisiones dependen directamente: dosificación de cloro (5-10 g/m³), carga de sal para clorador salino (4-5 kg/m³), horas de filtración (1 volumen cada 4-6 h), dimensionamiento de bomba de calor y dosis de antialgas/floculante. Un error del 20 % en el volumen te lleva a subdosificar o sobredosificar con consecuencias reales." },
        ],
      },
    },
  },
  {
    id: "pool-volume-in-liters-guide",
    slugs: {
      en: "pool-volume-in-liters-guide",
      es: "volumen-piscina-litros-guia",
      fr: "volume-piscine-litres-guide",
      it: "volume-piscina-litri-guida",
      nl: "zwembad-volume-liters-gids",
      de: "poolrechner-volumen-berechnen",
    },
    publishDate: "2026-08-12",
    updatedDate: "2026-08-12",
    readingTime: 7,
    category: "calculators",
    coverTheme: "dark",
    // Portada propia per idioma (1200x630). Les tres formes amb la seva formula i l'escala de litres.
    // L'alt descriu el que es VEU, no de que va l'article.
    cover: {
      es: "/img/blog/pool-volume-formulas-es.jpg",
      en: "/img/blog/pool-volume-formulas-en.jpg",
      fr: "/img/blog/pool-volume-formulas-fr.jpg",
      de: "/img/blog/pool-volume-formulas-de.jpg",
      it: "/img/blog/pool-volume-formulas-it.jpg",
      nl: "/img/blog/pool-volume-formulas-nl.jpg",
    },
    coverTitle: {
      es: "Volumen de piscina en litros",
      en: "Pool volume in litres",
      fr: "Volume de piscine en litres",
      de: "Poolvolumen in Litern",
      it: "Volume della piscina in litri",
      nl: "Zwembadvolume in liters",
    },
    imageAlt: {
      es: "Tres esquemas de piscina —rectangular, redonda y ovalada— cada uno con su fórmula de volumen, sobre una escala de 9.000 a 75.000 litros",
      en: "Three pool diagrams —rectangular, round and oval— each with its volume formula, over a scale from 9,000 to 75,000 litres",
      fr: "Trois schémas de piscine —rectangulaire, ronde et ovale— chacun avec sa formule de volume, sur une échelle de 9 000 à 75 000 litres",
      de: "Drei Poolskizzen —rechteckig, rund und oval— jeweils mit ihrer Volumenformel, über einer Skala von 9.000 bis 75.000 Litern",
      it: "Tre schemi di piscina —rettangolare, rotonda e ovale— ciascuno con la sua formula di volume, su una scala da 9.000 a 75.000 litri",
      nl: "Drie zwembadschema's —rechthoekig, rond en ovaal— elk met zijn volumeformule, op een schaal van 9.000 tot 75.000 liter",
    },
    // Sprint 158 — Bloc A (experiment de CTR, es mesura el 05/10/2026).
    // Títol i descripció NOMÉS del snippet: ≤ 50 i ≤ 155 caràcters, sense
    // el sufix « | PoolLex.io Blog» (que el faria passar del límit). No
    // toquen `title`/`description` de translations, que són les de les
    // targetes del blog: així el resultat s'atribueix només al snippet.
    // «Calculator» NO: la pàgina és una guia; la calculadora és a /tools/.
    snippetTitle: {
      en: "How to Calculate Pool Volume: Litres, Gallons, m³",
      de: "Poolrechner: Volumen in Litern und m³ berechnen",
    },
    snippetDescription: {
      en: "Calculate your pool's volume in litres, gallons or m³ for any shape, including the sloped-floor formula that avoids a 20-30 % overestimate.",
      de: "Poolvolumen für jede Form berechnen – mit Formel für geneigte Böden, die 20-30 % Überschätzung vermeidet. Sofort in Liter und m³.",
      es: "Calcula el volumen de tu piscina en litros: fórmulas para rectangular, redonda y ovalada, y tabla de 9.000 a 75.000 L.",
      fr: "Calculez le volume de votre piscine en litres : formules rectangulaire, ronde et ovale, et tableau de 9 000 à 75 000 L.",
      it: "Calcola il volume della tua piscina in litri: formule per vasche rettangolari, rotonde e ovali, e tabella da 9.000 a 75.000 L.",
      nl: "Bereken het volume van je zwembad in liters: formules voor rechthoekige, ronde en ovale baden, en tabel van 9.000 tot 75.000 L.",
    },
    translations: {
      en: {
        title: "Pool Volume in Litres: Complete Guide + Calculator 2026",
        h1: "How to Calculate Pool Volume in Litres, Gallons and m³",
        description: "Calculate your pool volume in litres. Formulas for rectangular, round and oval pools, quick reference table (9,000 to 75,000 L), chemical dosing and pump sizing applications.",
        keywords: [
          "pool volume calculator litres",
          "swimming pool volume calculator litres",
          "calculate pool volume in litres",
          "how many liters in a swimming pool",
          "pool volume in liters",
        ],
        author: "Víctor Saiz Vela, Chartered Industrial Engineer",
        Body: Article7BodyEN,
        faq: [
          { question: "How do I calculate pool volume in litres?", answer: "Compute volume in cubic metres first, then multiply by 1,000 to get litres. For a rectangular pool: V = length × width × depth. For a round pool: V = π × r² × h. For an oval pool: V = length × width × depth × 0.893. 1 m³ = 1,000 L always." },
          { question: "How many litres does a swimming pool hold?", answer: "It depends on shape and dimensions. Typical residential pools range from 9,000 L (3 × 2 × 1.5 m) to 75,000 L (10 × 5 × 1.5 m). Above-ground round pools hold 4,000 to 30,000 L depending on diameter (3 to 5.5 m). See the quick-reference table in this article for common sizes." },
          { question: "Do I use pool depth or wall height in the formula?", answer: "Always the water depth, not the wall height. Pools are typically filled to 80-90 % of wall height for safety. If your pool wall is 1.32 m tall but water sits at 1.20 m, use 1.20 m in the formula. Measuring wall height instead of water depth overstates volume by 10-15 %." },
          { question: "How do I calculate volume of a pool with a sloped bottom?", answer: "Use the average depth = (shallow depth + deep depth) / 2. For an 8 × 4 m pool with 1.2 m shallow and 1.8 m deep, average depth is 1.5 m and volume is 8 × 4 × 1.5 = 48 m³ = 48,000 L. This works for linear slopes; for hopper bottoms split into rectangles and add." },
          { question: "Why does my Intex pool box show a different volume than my calculation?", answer: "Manufacturers state volume at 80-90 % fill of wall height (a safety recommendation to prevent overflow when bathers enter). Your theoretical calculation using wall height gives a higher number; the real usable water depends on where you mark the water line. For chemistry dosing, use actual filled volume, not the box number." },
        ],
      },
      es: {
        title: "Volumen Piscina en Litros: Guía Completa + Fórmulas 2026",
        h1: "Volumen de Piscina en Litros: Guía Completa y Fórmulas",
        description: "Calcula el volumen de tu piscina en litros. Fórmulas para piscinas rectangulares, redondas y ovaladas, tabla de referencia (9.000 a 75.000 L), dosificación química y dimensionado de bomba.",
        keywords: [
          "volumen piscina litros",
          "calcular litros piscina",
          "cuantos litros tiene una piscina",
          "formula volumen piscina",
          "metros cubicos piscina",
        ],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article7BodyES,
        faq: [
          { question: "¿Cuántos litros tiene una piscina de 8 × 4 × 1,5 m?", answer: "48.000 litros (48 m³). La fórmula es largo × ancho × profundidad media: 8 × 4 × 1,5 = 48 m³, y como 1 m³ equivale a 1.000 litros, el resultado es 48.000 L. Es una de las medidas residenciales más habituales en España." },
          { question: "¿Cómo calculo el volumen si mi piscina tiene fondo inclinado?", answer: "Usa la profundidad media: suma la profundidad mínima y la máxima y divide entre 2. Por ejemplo, una piscina que va de 1,2 m a 1,8 m tiene una profundidad media de 1,5 m. Aplicar solo la profundidad máxima sobreestima el volumen entre un 20 y un 30 %." },
          { question: "¿Cuál es la fórmula para una piscina redonda?", answer: "V = π × r² × h, donde r es el radio (la mitad del diámetro) y h la altura del agua. Si prefieres trabajar con el diámetro directamente: V ≈ 0,7854 × D² × h. El error más frecuente es usar el diámetro como si fuera el radio, lo que devuelve cuatro veces el volumen real." },
          { question: "¿Por qué mi piscina desmontable tiene menos litros de los que indica la caja?", answer: "Porque los fabricantes indican el volumen al 80 % o 90 % de llenado, que es el nivel de trabajo recomendado para evitar desbordes. El agua real puede diferir entre un 10 y un 15 % respecto al dato nominal del embalaje. Mide siempre la lámina de agua real." },
          { question: "¿Qué precisión necesito en el volumen para dosificar productos químicos?", answer: "Un margen de error del 5 % es perfectamente asumible para uso doméstico. A partir del 20 % los problemas son reales: en una piscina de 40.000 L supone dosificar 40 g de cloro de más o de menos, suficiente para dejar el agua turbia o provocar irritación cutánea." },
        ],
      },
      fr: {
        title: "Volume Piscine en Litres : Guide Complet + Formules 2026",
        h1: "Volume de Piscine en Litres : Guide Complet et Formules",
        description: "Calculez le volume de votre piscine en litres. Formules pour bassins rectangulaires, ronds et ovales, tableau de référence (9 000 à 75 000 L), dosage chimique et dimensionnement de pompe.",
        keywords: [
          "volume piscine litres",
          "calculer litres piscine",
          "combien de litres piscine",
          "formule volume piscine",
          "metres cubes piscine",
        ],
        author: "Víctor Saiz Vela, Ingénieur diplômé",
        Body: Article7BodyFR,
        faq: [
          { question: "Combien de litres fait une piscine de 8 × 4 × 1,5 m ?", answer: "48 000 litres (48 m³). La formule est longueur × largeur × profondeur moyenne : 8 × 4 × 1,5 = 48 m³, et comme 1 m³ vaut 1 000 litres, cela donne 48 000 L. C'est l'une des dimensions résidentielles les plus courantes." },
          { question: "Comment calculer le volume si mon bassin a un fond incliné ?", answer: "Utilisez la profondeur moyenne : additionnez la profondeur minimale et la maximale, puis divisez par 2. Par exemple, un bassin allant de 1,2 m à 1,8 m a une profondeur moyenne de 1,5 m. Appliquer uniquement la profondeur maximale surestime le volume de 20 à 30 %." },
          { question: "Quelle est la formule pour une piscine ronde ?", answer: "V = π × r² × h, où r est le rayon (la moitié du diamètre) et h la hauteur d'eau. Si vous préférez travailler directement avec le diamètre : V ≈ 0,7854 × D² × h. L'erreur la plus fréquente consiste à utiliser le diamètre comme rayon, ce qui renvoie quatre fois le volume réel." },
          { question: "Pourquoi ma piscine hors-sol contient-elle moins de litres que ce qu'indique l'emballage ?", answer: "Parce que les fabricants indiquent le volume à 80 % ou 90 % de remplissage, le niveau de travail recommandé pour éviter les débordements. L'eau réelle peut différer de 10 à 15 % par rapport à la donnée nominale. Mesurez toujours la hauteur d'eau réelle." },
          { question: "Quelle précision me faut-il sur le volume pour doser les produits ?", answer: "Une marge d'erreur de 5 % est parfaitement acceptable pour un usage domestique. Au-delà de 20 %, les problèmes deviennent réels : sur un bassin de 40 000 L, cela revient à doser 40 g de chlore en trop ou en moins, de quoi laisser l'eau trouble ou provoquer des irritations cutanées." },
        ],
      },
      it: {
        title: "Volume Piscina in Litri: Guida Completa + Formule 2026",
        h1: "Volume della Piscina in Litri: Guida Completa e Formule",
        description: "Calcola il volume della tua piscina in litri. Formule per vasche rettangolari, rotonde e ovali, tabella di riferimento (9.000 a 75.000 L), dosaggio chimico e dimensionamento della pompa.",
        keywords: [
          "volume piscina litri",
          "calcolare litri piscina",
          "quanti litri ha una piscina",
          "formula volume piscina",
          "metri cubi piscina",
        ],
        author: "Víctor Saiz Vela, Ingegnere iscritto all'Albo",
        Body: Article7BodyIT,
        faq: [
          { question: "Quanti litri ha una piscina di 8 × 4 × 1,5 m?", answer: "48.000 litri (48 m³). La formula è lunghezza × larghezza × profondità media: 8 × 4 × 1,5 = 48 m³, e poiché 1 m³ equivale a 1.000 litri, il risultato è 48.000 L. È una delle misure residenziali più comuni." },
          { question: "Come calcolo il volume se la vasca ha il fondo inclinato?", answer: "Usa la profondità media: somma la profondità minima e quella massima e dividi per 2. Ad esempio, una vasca che va da 1,2 m a 1,8 m ha una profondità media di 1,5 m. Applicare solo la profondità massima sovrastima il volume del 20-30 %." },
          { question: "Qual è la formula per una piscina rotonda?", answer: "V = π × r² × h, dove r è il raggio (metà del diametro) e h l'altezza dell'acqua. Se preferisci lavorare direttamente con il diametro: V ≈ 0,7854 × D² × h. L'errore più frequente è usare il diametro come raggio, che restituisce quattro volte il volume reale." },
          { question: "Perché la mia piscina fuori terra contiene meno litri di quanto indica la confezione?", answer: "Perché i produttori indicano il volume all'80 % o 90 % di riempimento, il livello di lavoro consigliato per evitare tracimazioni. L'acqua reale può differire del 10-15 % rispetto al dato nominale. Misura sempre l'altezza d'acqua effettiva." },
          { question: "Che precisione mi serve sul volume per dosare i prodotti chimici?", answer: "Un margine di errore del 5 % è del tutto accettabile per uso domestico. Oltre il 20 % i problemi diventano reali: su una vasca da 40.000 L significa dosare 40 g di cloro in più o in meno, abbastanza per lasciare l'acqua torbida o provocare irritazioni cutanee." },
        ],
      },
      nl: {
        title: "Zwembadvolume in Liters: Complete Gids + Formules 2026",
        h1: "Zwembadvolume in Liters: Complete Gids en Formules",
        description: "Bereken het volume van je zwembad in liters. Formules voor rechthoekige, ronde en ovale baden, naslagtabel (9.000 tot 75.000 L), chemische dosering en pompdimensionering.",
        keywords: [
          "zwembad volume liters",
          "hoeveel liter zwembad",
          "zwembadinhoud berekenen",
          "formule zwembadvolume",
          "kubieke meter zwembad",
        ],
        author: "Víctor Saiz Vela, ir.",
        Body: Article7BodyNL,
        faq: [
          { question: "Hoeveel liter heeft een zwembad van 8 × 4 × 1,5 m?", answer: "48.000 liter (48 m³). De formule is lengte × breedte × gemiddelde diepte: 8 × 4 × 1,5 = 48 m³, en omdat 1 m³ gelijk is aan 1.000 liter, kom je uit op 48.000 L. Dit is een van de meest voorkomende particuliere afmetingen." },
          { question: "Hoe bereken ik het volume bij een hellende bodem?", answer: "Gebruik de gemiddelde diepte: tel de minimale en maximale diepte op en deel door 2. Een bad dat van 1,2 m naar 1,8 m loopt heeft dus een gemiddelde diepte van 1,5 m. Alleen de maximale diepte toepassen overschat het volume met 20 tot 30 %." },
          { question: "Wat is de formule voor een rond zwembad?", answer: "V = π × r² × h, waarbij r de straal is (de helft van de diameter) en h de waterhoogte. Werk je liever direct met de diameter: V ≈ 0,7854 × D² × h. De meest gemaakte fout is de diameter als straal gebruiken, wat vier keer het echte volume oplevert." },
          { question: "Waarom bevat mijn opzetzwembad minder liters dan de verpakking aangeeft?", answer: "Omdat fabrikanten het volume vermelden bij 80 % of 90 % vulling, het aanbevolen werkniveau om overlopen te voorkomen. Het werkelijke water kan 10 tot 15 % afwijken van de nominale opgave. Meet altijd de echte waterhoogte." },
          { question: "Hoe nauwkeurig moet het volume zijn om chemicaliën te doseren?", answer: "Een foutmarge van 5 % is voor huishoudelijk gebruik prima. Vanaf 20 % worden de problemen reëel: bij een bad van 40.000 L betekent dat 40 g chloor te veel of te weinig doseren, genoeg om het water troebel te laten of huidirritatie te veroorzaken." },
        ],
      },
      de: {
        title: "Poolrechner: Poolvolumen richtig berechnen · Formeln 2026",
        h1: "Poolrechner: Poolvolumen in m³ und Liter richtig berechnen",
        description: "Poolvolumen berechnen — Formeln für rechteckige, runde und ovale Pools, Referenztabelle von 9.000 bis 75.000 Litern, Chemiedosierung und Wärmepumpen-Auslegung. Kostenloser Poolrechner online.",
        keywords: [
          "poolrechner",
          "pool rechner",
          "pool kubikmeter rechner",
          "volumenrechner pool",
          "wassermenge pool berechnen",
          "pool volumen berechnen",
          "liter berechnen pool",
        ],
        author: "Víctor Saiz Vela, Ingenieur",
        Body: Article8BodyDE,
        faq: [
          { question: "Wie berechne ich das Poolvolumen richtig?", answer: "Zuerst das Volumen in Kubikmetern berechnen, dann mit 1.000 multiplizieren für Liter. Rechteckiger Pool: V = Länge × Breite × Tiefe. Runder Pool: V = π × r² × h. Ovaler Pool: V = Länge × Breite × Tiefe × 0,893. 1 m³ entspricht immer 1.000 Liter." },
          { question: "Wie viele Liter fasst ein typischer Gartenpool?", answer: "Je nach Form und Maßen. Typische Wohnpools reichen von 9.000 L (3 × 2 × 1,5 m) bis 75.000 L (10 × 5 × 1,5 m). Runde Aufstellpools fassen 4.000 bis 30.000 L je nach Durchmesser (3 bis 5,5 m). Die Referenztabelle in diesem Artikel deckt die häufigsten Größen ab." },
          { question: "Verwende ich die Wassertiefe oder die Wandhöhe im Poolrechner?", answer: "Immer die Wassertiefe, niemals die Wandhöhe. Pools werden aus Sicherheitsgründen zu 80-90 % der Wandhöhe gefüllt. Wenn die Wand 1,32 m hoch ist, das Wasser aber bei 1,20 m steht, verwenden Sie 1,20 m. Die Wandhöhe verwenden führt zu 10-15 % Volumenüberschätzung." },
          { question: "Wie berechne ich das Volumen eines Pools mit geneigtem Boden?", answer: "Mit der mittleren Wassertiefe = (flache Tiefe + tiefe Tiefe) / 2. Bei einem 8 × 4 m Pool mit 1,2 m flach und 1,8 m tief ergibt sich mittlere Tiefe von 1,5 m und Volumen 8 × 4 × 1,5 = 48 m³ = 48.000 L. Bei Trichterböden Pool in Rechtecke unterteilen und addieren." },
          { question: "Warum stimmt das Volumen auf meinem Intex-Karton nicht mit meiner Berechnung überein?", answer: "Hersteller geben das Volumen bei 80-90 % Füllstand der Wandhöhe an (Sicherheitsempfehlung gegen Überlaufen bei Badenden). Ihre theoretische Rechnung mit voller Wandhöhe ergibt einen höheren Wert. Für die Chemiedosierung immer das reale gefüllte Volumen verwenden, nicht die Kartonangabe." },
        ],
      },
    },
  },
  {
    id: "que-es-poollex",
    slugs: {
      es: "que-es-poollex",
      en: "what-is-poollex",
      fr: "qu-est-ce-que-poollex",
      de: "was-ist-poollex",
      it: "cos-e-poollex",
      nl: "wat-is-poollex",
    },
    publishDate: "2026-08-17",
    updatedDate: "2026-08-17",
    readingTime: 5,
    category: "iniciativa",
    coverTheme: "light",
    // Portada propia per idioma (1200x630). Els tres nivells, amb els noms que fa servir la propia web.
    // L'alt descriu el que es VEU, no de que va l'article.
    cover: {
      es: "/img/blog/poollex-three-levels-es.jpg",
      en: "/img/blog/poollex-three-levels-en.jpg",
      fr: "/img/blog/poollex-three-levels-fr.jpg",
      de: "/img/blog/poollex-three-levels-de.jpg",
      it: "/img/blog/poollex-three-levels-it.jpg",
      nl: "/img/blog/poollex-three-levels-nl.jpg",
    },
    coverTitle: {
      es: "Los tres niveles de PoolLex.io",
      en: "The three levels of PoolLex.io",
      fr: "Les trois niveaux de PoolLex.io",
      de: "Die drei Stufen von PoolLex.io",
      it: "I tre livelli di PoolLex.io",
      nl: "De drie niveaus van PoolLex.io",
    },
    imageAlt: {
      es: "Los tres niveles de PoolLex.io apilados: la Ficha Técnica, el sello firmado y la herramienta para instaladores",
      en: "The three levels of PoolLex.io stacked: the Technical Sheet, the signed seal and the tool for installers",
      fr: "Les trois niveaux de PoolLex.io empilés : la Fiche Technique, le sceau signé et l'outil pour les installateurs",
      de: "Die drei Stufen von PoolLex.io übereinander: das Technische Datenblatt, das unterschriebene Siegel und das Werkzeug für Installateure",
      it: "I tre livelli di PoolLex.io impilati: la Scheda Tecnica, il sigillo firmato e lo strumento per installatori",
      nl: "De drie niveaus van PoolLex.io boven elkaar: de Technische Fiche, het ondertekende zegel en het hulpmiddel voor installateurs",
    },
    snippetTitle: {
      es: "Qué es PoolLex.io · Certificación de sostenibilidad",
      en: "What is PoolLex.io · Pool sustainability certified",
      fr: "Qu'est-ce que PoolLex.io · Durabilité des piscines",
      de: "Was ist PoolLex.io · Pool-Nachhaltigkeit zertifiziert",
      it: "Cos'è PoolLex.io · Certificazione di sostenibilità",
      nl: "Wat is PoolLex.io · Duurzaamheid gecertificeerd",
    },
    snippetDescription: {
      es: "PoolLex.io es la iniciativa europea de certificación digital de sostenibilidad de piscinas privadas, según UNE-EN 17645:2023.",
      en: "PoolLex.io is the European initiative for digital certification of private pool sustainability, based on EN 17645:2023.",
      fr: "PoolLex.io est l'initiative européenne de certification numérique de durabilité des piscines privées, selon NF EN 17645:2023.",
      de: "PoolLex.io ist die europäische Initiative zur digitalen Zertifizierung der Nachhaltigkeit privater Pools nach DIN EN 17645:2023.",
      it: "PoolLex.io è l'iniziativa europea di certificazione digitale della sostenibilità delle piscine private, secondo UNI EN 17645:2023.",
      nl: "PoolLex.io is het Europese initiatief voor digitale certificering van de duurzaamheid van privézwembaden, volgens NEN-EN 17645:2023.",
    },
    translations: {
      es: {
        title: "Qué es PoolLex.io · Certificación digital sostenibilidad piscinas",
        h1: "Qué es PoolLex.io — Certificación digital de sostenibilidad para piscinas",
        description: "PoolLex.io es la iniciativa europea de certificación digital de sostenibilidad ambiental de piscinas privadas, basada en UNE-EN 17645:2023. Guía completa: qué hacemos, cómo funciona el PoolLex ID único europeo, a quién servimos y qué diferencia una piscina certificada.",
        keywords: ["poollex", "poollex.io", "que es poollex", "poollex id", "certificacion piscina EN 17645", "sostenibilidad piscina"],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article9BodyES,
        faq: [
          { question: "¿Qué es PoolLex.io?", answer: "PoolLex.io es la iniciativa europea de certificación digital de sostenibilidad ambiental para piscinas privadas exteriores enterradas, basada en la norma UNE-EN 17645:2023. Emite el PoolLex ID (identificador único europeo con formato PLX-XXXXXXX) y genera fichas técnicas oficiales gratuitas." },
          { question: "¿Qué diferencia hay entre una piscina con PoolLex ID y una sin certificar?", answer: "Una piscina con PoolLex ID tiene su información técnica documentada oficialmente (dimensiones, volumen, equipamiento, zona climática aplicada) y una clasificación ambiental A-F según UNE-EN 17645:2023. Sirve como referencia técnica estable para trámites urbanísticos, transmisiones, contratos de mantenimiento o solicitud de ayudas de eficiencia. Una piscina sin certificar carece de este registro estandarizado." },
          { question: "¿Cuánto cuesta usar PoolLex.io?", answer: "La ficha técnica con PoolLex ID es 100% gratuita, sin registro. El servicio Pool Check (comprobación normativa básica) también es gratuito. Solo el Sello EN 17645 firmado por ingeniero industrial colegiado es un servicio de pago voluntario que se solicita cuando se necesita el sello oficial firmado." },
          { question: "¿Quién está detrás de PoolLex.io?", answer: "Víctor Saiz Vela, Ingeniero Industrial colegiado con experiencia en aplicación de normativa técnica. PoolLex.io actúa como aplicador de la norma UNE-EN 17645:2023 (no como autor de la norma), traduciendo sus criterios a documentación digital accesible para propietarios, instaladores y administraciones." },
        ],
      },
      en: {
        title: "What is PoolLex.io · Pool Sustainability Digital Certification",
        h1: "What is PoolLex.io — Digital Pool Sustainability Certification",
        description: "PoolLex.io is the European initiative for digital certification of pool environmental sustainability, based on EN 17645:2023. Complete guide: what we do, how the unique European PoolLex ID works, who we serve and what a certified pool means.",
        keywords: ["poollex", "poollex.io", "what is poollex", "poollex id", "pool certification EN 17645", "pool sustainability"],
        author: "Víctor Saiz Vela, Chartered Industrial Engineer",
        Body: Article9BodyEN,
        faq: [
          { question: "What is PoolLex.io?", answer: "PoolLex.io is the European initiative for digital certification of pool environmental sustainability, focused on private outdoor in-ground pools, based on the EN 17645:2023 standard. It issues the PoolLex ID (unique European identifier with format PLX-XXXXXXX) and generates free official technical sheets." },
          { question: "What's the difference between a pool with PoolLex ID and one without certification?", answer: "A pool with PoolLex ID has its technical information officially documented (dimensions, volume, equipment, applied climate zone) and an A-F environmental classification per EN 17645:2023. It serves as a stable technical reference for urban procedures, transfers, maintenance contracts or energy efficiency grant applications. A pool without certification lacks this standardized record." },
          { question: "How much does PoolLex.io cost to use?", answer: "The technical sheet with PoolLex ID is 100% free, no registration required. The Pool Check service (basic regulatory verification) is also free. Only the EN 17645 Seal signed by a chartered industrial engineer is a paid voluntary service requested when the official signed seal is needed." },
          { question: "Who is behind PoolLex.io?", answer: "Víctor Saiz Vela, Chartered Industrial Engineer with experience in technical regulation application. PoolLex.io acts as an applier of the EN 17645:2023 standard (not as author of the standard), translating its criteria into accessible digital documentation for owners, installers and administrations." },
        ],
      },
      fr: {
        title: "Qu'est-ce que PoolLex.io · Certification numérique durabilité piscines",
        h1: "Qu'est-ce que PoolLex.io — Certification numérique de durabilité de piscines",
        description: "PoolLex.io est l'initiative européenne de certification numérique de durabilité environnementale de piscines privées, basée sur NF EN 17645:2023. Guide complet : ce que nous faisons, comment fonctionne le PoolLex ID unique européen, à qui nous servons.",
        keywords: ["poollex", "poollex.io", "qu est ce que poollex", "poollex id", "certification piscine NF EN 17645", "durabilité piscine"],
        author: "Víctor Saiz Vela, Ingénieur diplômé",
        Body: Article9BodyFR,
        faq: [
          { question: "Qu'est-ce que PoolLex.io ?", answer: "PoolLex.io est l'initiative européenne de certification numérique de durabilité environnementale pour piscines privées extérieures enterrées, basée sur la norme NF EN 17645:2023. Il émet le PoolLex ID (identifiant unique européen au format PLX-XXXXXXX) et génère des fiches techniques officielles gratuites." },
          { question: "Quelle est la différence entre une piscine avec PoolLex ID et une piscine sans certification ?", answer: "Une piscine avec PoolLex ID a ses informations techniques officiellement documentées (dimensions, volume, équipement, zone climatique appliquée) et une classification environnementale A-F selon NF EN 17645:2023. Cela sert de référence technique stable pour démarches d'urbanisme, transmissions, contrats de maintenance ou demandes d'aides à l'efficacité énergétique. Une piscine sans certification n'a pas ce registre standardisé." },
          { question: "Combien coûte l'utilisation de PoolLex.io ?", answer: "La fiche technique avec PoolLex ID est 100% gratuite, sans inscription. Le service Pool Check (vérification réglementaire de base) est également gratuit. Seul le Sceau NF EN 17645 signé par un ingénieur diplômé est un service payant volontaire demandé lorsque le sceau officiel signé est nécessaire." },
          { question: "Qui se cache derrière PoolLex.io ?", answer: "Víctor Saiz Vela, ingénieur diplômé avec expérience en application de réglementation technique. PoolLex.io agit comme applicateur de la norme NF EN 17645:2023 (et non comme auteur de la norme), traduisant ses critères en documentation numérique accessible pour propriétaires, installateurs et administrations." },
        ],
      },
      de: {
        title: "Was ist PoolLex.io · Digitale Zertifizierung Pool-Nachhaltigkeit",
        h1: "Was ist PoolLex.io — Digitale Zertifizierung von Pool-Nachhaltigkeit",
        description: "PoolLex.io ist die europäische Initiative zur digitalen Zertifizierung der Umweltnachhaltigkeit privater Pools, basierend auf DIN EN 17645:2023. Vollständiger Leitfaden: was wir tun, wie die eindeutige europäische PoolLex ID funktioniert, wem wir dienen.",
        keywords: ["poollex", "poollex.io", "was ist poollex", "poollex id", "pool zertifizierung DIN EN 17645", "pool nachhaltigkeit"],
        author: "Víctor Saiz Vela, Ingenieur",
        Body: Article9BodyDE,
        faq: [
          { question: "Was ist PoolLex.io?", answer: "PoolLex.io ist die europäische Initiative zur digitalen Zertifizierung der Umweltnachhaltigkeit für private Freibad-Pools mit Erdeinbau, basierend auf der Norm DIN EN 17645:2023. Es gibt die PoolLex ID (eindeutige europäische Kennung im Format PLX-XXXXXXX) und generiert kostenlose offizielle technische Datenblätter." },
          { question: "Was ist der Unterschied zwischen einem Pool mit PoolLex ID und einem ohne Zertifizierung?", answer: "Ein Pool mit PoolLex ID hat seine technischen Informationen offiziell dokumentiert (Abmessungen, Volumen, Ausstattung, angewandte Klimazone) und eine A-F Umweltklassifizierung gemäß DIN EN 17645:2023. Er dient als stabile technische Referenz für städtebauliche Verfahren, Übertragungen, Wartungsverträge oder Anträge auf Energieeffizienzförderung. Ein Pool ohne Zertifizierung hat diesen standardisierten Nachweis nicht." },
          { question: "Was kostet die Nutzung von PoolLex.io?", answer: "Das technische Datenblatt mit PoolLex ID ist 100% kostenlos, ohne Registrierung. Der Pool Check Service (grundlegende regulatorische Überprüfung) ist ebenfalls kostenlos. Nur das DIN EN 17645-Siegel unterzeichnet von einem eingetragenen Ingenieur ist ein kostenpflichtiger freiwilliger Service, der angefordert wird, wenn das offizielle unterzeichnete Siegel benötigt wird." },
          { question: "Wer steht hinter PoolLex.io?", answer: "Víctor Saiz Vela, Ingenieur mit Erfahrung in der Anwendung technischer Vorschriften. PoolLex.io fungiert als Anwender der Norm DIN EN 17645:2023 (nicht als Autor der Norm), indem sie ihre Kriterien in zugängliche digitale Dokumentation für Eigentümer, Installateure und Verwaltungen übersetzt." },
        ],
      },
      it: {
        title: "Cos'è PoolLex.io · Certificazione digitale sostenibilità piscine",
        h1: "Cos'è PoolLex.io — Certificazione digitale di sostenibilità per piscine",
        description: "PoolLex.io è l'iniziativa europea di certificazione digitale della sostenibilità ambientale di piscine private, basata su UNI EN 17645:2023. Guida completa: cosa facciamo, come funziona il PoolLex ID unico europeo, a chi serviamo.",
        keywords: ["poollex", "poollex.io", "cos e poollex", "poollex id", "certificazione piscina UNI EN 17645", "sostenibilità piscina"],
        author: "Víctor Saiz Vela, Ingegnere iscritto all'Albo",
        Body: Article9BodyIT,
        faq: [
          { question: "Cos'è PoolLex.io?", answer: "PoolLex.io è l'iniziativa europea di certificazione digitale della sostenibilità ambientale per piscine private esterne interrate, basata sulla norma UNI EN 17645:2023. Emette il PoolLex ID (identificatore unico europeo con formato PLX-XXXXXXX) e genera schede tecniche ufficiali gratuite." },
          { question: "Qual è la differenza tra una piscina con PoolLex ID e una senza certificazione?", answer: "Una piscina con PoolLex ID ha le sue informazioni tecniche documentate ufficialmente (dimensioni, volume, attrezzatura, zona climatica applicata) e una classificazione ambientale A-F secondo UNI EN 17645:2023. Serve come riferimento tecnico stabile per pratiche urbanistiche, trasferimenti, contratti di manutenzione o richieste di aiuti per l'efficienza energetica. Una piscina senza certificazione manca di questo registro standardizzato." },
          { question: "Quanto costa usare PoolLex.io?", answer: "La scheda tecnica con PoolLex ID è 100% gratuita, senza registrazione. Il servizio Pool Check (verifica normativa di base) è anch'esso gratuito. Solo il Sigillo UNI EN 17645 firmato da un ingegnere iscritto all'Albo è un servizio a pagamento volontario richiesto quando serve il sigillo ufficiale firmato." },
          { question: "Chi c'è dietro PoolLex.io?", answer: "Víctor Saiz Vela, ingegnere iscritto all'Albo con esperienza nell'applicazione di normativa tecnica. PoolLex.io agisce come applicatore della norma UNI EN 17645:2023 (non come autore della norma), traducendo i suoi criteri in documentazione digitale accessibile per proprietari, installatori e amministrazioni." },
        ],
      },
      nl: {
        title: "Wat is PoolLex.io · Digitale certificering zwembad-duurzaamheid",
        h1: "Wat is PoolLex.io — Digitale certificering van zwembad-duurzaamheid",
        description: "PoolLex.io is het Europese initiatief voor digitale certificering van milieuduurzaamheid van privézwembaden, gebaseerd op NEN-EN 17645:2023. Complete gids: wat we doen, hoe de unieke Europese PoolLex ID werkt, wie we bedienen.",
        keywords: ["poollex", "poollex.io", "wat is poollex", "poollex id", "zwembadcertificering NEN-EN 17645", "zwembad duurzaamheid"],
        author: "Víctor Saiz Vela, ir.",
        Body: Article9BodyNL,
        faq: [
          { question: "Wat is PoolLex.io?", answer: "PoolLex.io is het Europese initiatief voor digitale certificering van milieuduurzaamheid voor privé buitenzwembaden ingegraven, gebaseerd op de norm NEN-EN 17645:2023. Het geeft de PoolLex ID uit (unieke Europese identificatie met formaat PLX-XXXXXXX) en genereert gratis officiële technische fiches." },
          { question: "Wat is het verschil tussen een zwembad met PoolLex ID en een zonder certificering?", answer: "Een zwembad met PoolLex ID heeft zijn technische informatie officieel gedocumenteerd (afmetingen, volume, uitrusting, toegepaste klimaatzone) en een A-F milieuclassificatie volgens NEN-EN 17645:2023. Het dient als stabiele technische referentie voor stedelijke procedures, overdrachten, onderhoudscontracten of aanvragen voor energie-efficiëntiesubsidies. Een zwembad zonder certificering heeft dit gestandaardiseerde register niet." },
          { question: "Hoeveel kost het gebruik van PoolLex.io?", answer: "De technische fiche met PoolLex ID is 100% gratis, zonder registratie. De Pool Check-service (basisverificatie regelgeving) is ook gratis. Alleen het NEN-EN 17645-zegel ondertekend door een gediplomeerd ingenieur (ir.) is een betaalde vrijwillige service aangevraagd wanneer het officiële ondertekende zegel nodig is." },
          { question: "Wie zit er achter PoolLex.io?", answer: "Víctor Saiz Vela, gediplomeerd ingenieur (ir.) met ervaring in het toepassen van technische regelgeving. PoolLex.io fungeert als toepasser van de norm NEN-EN 17645:2023 (niet als auteur van de norm), waarbij de criteria vertaald worden in toegankelijke digitale documentatie voor eigenaars, installateurs en administraties." },
        ],
      },
    },
  },
  // ============================================================
  // ARTICLE 10 — UNE-EN 17645 explicada (Sprint 119.111c LONG_TAIL)
  // ============================================================
  {
    id: "sal-piscina-calculo-dosificacion",
    slugs: {
      es: "como-calcular-sal-piscina-2026",
      en: "how-to-calculate-pool-salt-2026",
      fr: "comment-calculer-sel-piscine-2026",
      de: "wie-viel-salz-pool-berechnen-2026",
      it: "come-calcolare-sale-piscina-2026",
      nl: "hoeveel-zout-zwembad-berekenen-2026",
    },
    publishDate: "2026-08-17",
    updatedDate: "2026-08-17",
    readingTime: 6,
    category: "guias",
    coverTheme: "dark",
    // Sprint 142 — Portada propia per idioma (1200x630).
    // Les sis versions compartien la imatge generica del lloc, que a mes
    // esta en castella: qui comparteix l'article en neerlandes veia una
    // targeta en espanyol.
    cover: {
      es: "/img/blog/salt-calculation-pool-diagram-es.jpg",
      en: "/img/blog/salt-calculation-pool-diagram-en.jpg",
      fr: "/img/blog/salt-calculation-pool-diagram-fr.jpg",
      de: "/img/blog/salt-calculation-pool-diagram-de.jpg",
      it: "/img/blog/salt-calculation-pool-diagram-it.jpg",
      nl: "/img/blog/salt-calculation-pool-diagram-nl.jpg",
    },
    coverTitle: {
      es: "Cálculo de sal para piscina",
      en: "Pool salt calculation",
      fr: "Calcul du sel pour piscine",
      de: "Salzberechnung für den Pool",
      it: "Calcolo del sale per piscina",
      nl: "Zoutberekening voor het zwembad",
    },
    // El alt descriu el que es VEU a la imatge, no el tema de l'article.
    // El text anterior descrivia l'article ("formula, concentracions i
    // manteniment"), cosa que un lector de pantalla no pot comprovar
    // mirant el diagrama.
    imageAlt: {
      es: "Diagrama técnico del cálculo de sal en una piscina con clorador salino y escala de concentración en ppm",
      en: "Technical diagram of pool salt calculation with salt chlorinator and ppm concentration scale",
      fr: "Schéma technique du calcul du sel dans une piscine avec électrolyseur et échelle de concentration en ppm",
      de: "Technisches Diagramm der Salzberechnung im Pool mit Salzelektrolyse und ppm-Konzentrationsskala",
      it: "Schema tecnico del calcolo del sale in piscina con elettrolizzatore e scala di concentrazione in ppm",
      nl: "Technisch schema van de zoutberekening in een zwembad met zoutelektrolyse en ppm-concentratieschaal",
    },
    snippetDescription: {
      es: "Fórmula de cálculo de sal para clorador salino, cuándo añadirla, errores comunes y efecto sobre TDS y pH. Guía técnica 2026.",
      en: "Salt dosing formula for a salt chlorinator, when to add it, common mistakes and the effect on TDS and pH. Technical guide 2026.",
      fr: "Formule de calcul du sel pour électrolyseur, quand en ajouter, erreurs fréquentes et effet sur TDS et pH. Guide technique 2026.",
      de: "Salzberechnung für die Salzelektrolyse, wann nachfüllen, häufige Fehler und Wirkung auf TDS und pH. Technischer Leitfaden 2026.",
      it: "Formula di calcolo del sale per elettrolisi, quando aggiungerlo, errori frequenti ed effetto su TDS e pH. Guida tecnica 2026.",
      nl: "Zoutberekening voor zoutelektrolyse, wanneer zout toevoegen, veelgemaakte fouten en effect op TDS en pH. Technische gids 2026.",
    },
    translations: {
      es: {
        title: "Sal para piscina · Cálculo y dosificación correcta 2026",
        h1: "Sal para piscina — cálculo y dosificación correcta",
        description: "Fórmula de cálculo de sal para piscina con clorador salino, cuándo añadir sal, errores comunes, efecto sobre TDS y pH, y mantenimiento mensual. Guía técnica 2026.",
        keywords: ["calculadora sal piscina", "cuanta sal echar piscina", "clorador salino dosificacion", "sal para clorador", "concentracion sal piscina"],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article12BodyES,
        faq: [
          { question: "¿Cuánta sal necesito para mi piscina?", answer: "La fórmula es: Sal (kg) = Volumen (m³) × Concentración objetivo (g/L) / 1000. Con objetivo típico de 4 g/L, una piscina de 50 m³ necesita 200 kg. Consulta la placa del clorador salino para la concentración exacta recomendada por el fabricante." },
          { question: "¿Puedo usar sal de mesa en la piscina?", answer: "No. La sal para clorador debe ser NaCl puro >99,5 %, sin antiaglomerantes, yodo ni conservantes. La sal de mesa contiene aditivos que dañan la célula de electrólisis y provocan turbidez y depósitos. Usa siempre sal específica para piscinas o sal industrial certificada." },
          { question: "¿Cada cuánto tiempo hay que añadir sal?", answer: "La sal no se consume por electrólisis — se recicla continuamente. Solo hay que reponerla por pérdidas físicas: salpicaduras, contralavados y evacuaciones parciales. En una piscina bien cuidada basta con revisar mensualmente y añadir un par de sacos al año." },
          { question: "¿La sal daña la piscina?", answer: "A concentraciones normales (3-5 g/L) no. Por encima de 6 g/L (TDS >6.000 ppm) empieza a haber riesgo de corrosión en elementos metálicos: escaleras, focos, tornillos. Vigila el TDS con tiras reactivas y evita sobredosificar cuando repongas." },
        ],
      },
      en: {
        title: "Pool Salt · Calculation and Correct Dosing 2026",
        h1: "Pool salt — calculation and correct dosing",
        description: "Salt calculation formula for pools with salt chlorinator, when to add salt, common mistakes, effect on TDS and pH, and monthly maintenance. Technical guide 2026.",
        keywords: ["pool salt calculator", "how much salt for pool", "salt chlorinator dosing", "pool salt concentration", "salt water pool"],
        author: "Víctor Saiz Vela, Chartered Industrial Engineer",
        Body: Article12BodyEN,
        faq: [
          { question: "How much salt does my pool need?", answer: "Formula: Salt (kg) = Volume (m³) × Target concentration (g/L) / 1000. With a typical 4 g/L target, a 50 m³ pool needs 200 kg. Check the chlorinator's plate for the exact concentration recommended by the manufacturer." },
          { question: "Can I use table salt in the pool?", answer: "No. Salt for a chlorinator must be pure NaCl >99.5 %, without anti-caking agents, iodine or preservatives. Table salt contains additives that damage the electrolysis cell and cause cloudiness and deposits. Always use pool-specific salt or certified industrial salt." },
          { question: "How often should I add salt?", answer: "Salt is not consumed by electrolysis — it cycles continuously. It only needs replenishing due to physical losses: splashing, backwash and partial drain-downs. In a well-maintained pool, checking monthly and adding a couple of sacks a year is enough." },
          { question: "Does salt damage the pool?", answer: "At normal concentrations (3-5 g/L) no. Above 6 g/L (TDS >6,000 ppm) corrosion risk starts on metal parts: ladders, lights, screws. Monitor TDS with test strips and avoid overdosing when topping up." },
        ],
      },
      fr: {
        title: "Sel pour piscine · Calcul et dosage correct 2026",
        h1: "Sel pour piscine — calcul et dosage correct",
        description: "Formule de calcul du sel pour piscine avec électrolyseur, quand ajouter du sel, erreurs fréquentes, effet sur TDS et pH, entretien mensuel. Guide technique 2026.",
        keywords: ["calculateur sel piscine", "quantite sel piscine", "dosage electrolyseur", "concentration sel piscine", "piscine au sel"],
        author: "Víctor Saiz Vela, Ingénieur diplômé",
        Body: Article12BodyFR,
        faq: [
          { question: "Combien de sel pour ma piscine ?", answer: "Formule : Sel (kg) = Volume (m³) × Concentration cible (g/L) / 1000. Avec un objectif typique de 4 g/L, une piscine de 50 m³ demande 200 kg. Consulte la plaque de l'électrolyseur pour la concentration exacte recommandée par le fabricant." },
          { question: "Puis-je utiliser du sel de table dans la piscine ?", answer: "Non. Le sel pour électrolyseur doit être du NaCl pur >99,5 %, sans anti-agglomérant, iode ni conservateur. Le sel de table contient des additifs qui endommagent la cellule d'électrolyse et provoquent turbidité et dépôts. Utilise toujours du sel spécifique pour piscines ou du sel industriel certifié." },
          { question: "À quelle fréquence ajouter du sel ?", answer: "Le sel n'est pas consommé par l'électrolyse — il se recycle en continu. Il ne faut le compléter que pour pertes physiques : éclaboussures, contre-lavages et vidanges partielles. Sur une piscine bien entretenue, un contrôle mensuel et l'ajout de deux sacs par an suffisent." },
          { question: "Le sel endommage-t-il la piscine ?", answer: "À concentrations normales (3-5 g/L) non. Au-dessus de 6 g/L (TDS >6 000 ppm) apparaît un risque de corrosion des éléments métalliques : échelles, projecteurs, vis. Surveille le TDS avec des bandelettes et évite de surdoser lors des compléments." },
        ],
      },
      de: {
        title: "Poolsalz · Berechnung und korrekte Dosierung 2026",
        h1: "Poolsalz — Berechnung und korrekte Dosierung",
        description: "Salzberechnungsformel für Pools mit Salzelektrolyse, wann Salz nachfüllen, häufige Fehler, Wirkung auf TDS und pH, monatliche Wartung. Technischer Leitfaden 2026.",
        keywords: ["poolsalz rechner", "wie viel salz pool", "salzelektrolyse dosierung", "salzkonzentration pool", "salzwasser pool"],
        author: "Víctor Saiz Vela, Ingenieur",
        Body: Article12BodyDE,
        faq: [
          { question: "Wie viel Salz brauche ich für meinen Pool?", answer: "Formel: Salz (kg) = Volumen (m³) × Zielkonzentration (g/L) / 1000. Bei typischem Ziel 4 g/L braucht ein 50 m³ Pool 200 kg. Prüfen Sie das Typenschild der Elektrolyse für die vom Hersteller empfohlene Konzentration." },
          { question: "Kann ich Speisesalz für den Pool verwenden?", answer: "Nein. Salz für die Elektrolyse muss reines NaCl >99,5 % sein, ohne Rieselhilfen, Jod oder Konservierungsstoffe. Speisesalz enthält Zusätze, die die Elektrolysezelle beschädigen und Trübungen sowie Ablagerungen verursachen. Verwenden Sie immer poolspezifisches oder zertifiziertes Industriesalz." },
          { question: "Wie oft muss Salz nachgefüllt werden?", answer: "Salz wird durch die Elektrolyse nicht verbraucht — es zirkuliert kontinuierlich. Nachfüllen nur bei physischen Verlusten: Spritzwasser, Rückspülungen und Teilentleerungen. In einem gut gewarteten Pool genügt monatliche Kontrolle und Nachfüllung von zwei Säcken pro Jahr." },
          { question: "Schädigt Salz den Pool?", answer: "Bei Normalkonzentration (3-5 g/L) nein. Über 6 g/L (TDS >6.000 ppm) besteht Korrosionsrisiko für Metallteile: Leitern, Scheinwerfer, Schrauben. Überwachen Sie den TDS mit Teststreifen und vermeiden Sie Überdosierung beim Nachfüllen." },
        ],
      },
      it: {
        title: "Sale per piscina · Calcolo e dosaggio corretto 2026",
        h1: "Sale per piscina — calcolo e dosaggio corretto",
        description: "Formula di calcolo del sale per piscina con elettrolisi, quando aggiungere sale, errori frequenti, effetto su TDS e pH, manutenzione mensile. Guida tecnica 2026.",
        keywords: ["calcolatore sale piscina", "quanto sale piscina", "dosaggio elettrolisi", "concentrazione sale piscina", "piscina a sale"],
        author: "Víctor Saiz Vela, Ingegnere iscritto all'Albo",
        Body: Article12BodyIT,
        faq: [
          { question: "Quanto sale serve alla mia piscina?", answer: "Formula: Sale (kg) = Volume (m³) × Concentrazione obiettivo (g/L) / 1000. Con obiettivo tipico 4 g/L una piscina di 50 m³ richiede 200 kg. Consulta la targa dell'elettrolizzatore per la concentrazione esatta raccomandata dal produttore." },
          { question: "Posso usare sale da tavola in piscina?", answer: "No. Il sale per elettrolisi deve essere NaCl puro >99,5 %, senza antiagglomeranti, iodio o conservanti. Il sale da tavola contiene additivi che danneggiano la cellula elettrolitica e causano torbidità e depositi. Usa sempre sale specifico per piscine o sale industriale certificato." },
          { question: "Ogni quanto va aggiunto sale?", answer: "Il sale non viene consumato dall'elettrolisi — si ricicla in continuo. Va reintegrato solo per perdite fisiche: schizzi, controlavaggi e svuotamenti parziali. In una piscina ben mantenuta è sufficiente un controllo mensile e l'aggiunta di un paio di sacchi all'anno." },
          { question: "Il sale danneggia la piscina?", answer: "A concentrazioni normali (3-5 g/L) no. Sopra 6 g/L (TDS >6.000 ppm) c'è rischio di corrosione degli elementi metallici: scale, proiettori, viti. Controlla il TDS con strisce reattive ed evita di sovradosare nei reintegri." },
        ],
      },
      nl: {
        title: "Zwembadzout · Berekening en correcte dosering 2026",
        h1: "Zwembadzout — berekening en correcte dosering",
        description: "Zoutberekeningsformule voor zwembad met zoutelektrolyse, wanneer zout toevoegen, veelgemaakte fouten, effect op TDS en pH, maandelijks onderhoud. Technische gids 2026.",
        keywords: ["zoutcalculator zwembad", "hoeveel zout zwembad", "zoutelektrolyse dosering", "zoutconcentratie zwembad", "zoutwater zwembad"],
        author: "Víctor Saiz Vela, ir.",
        Body: Article12BodyNL,
        faq: [
          { question: "Hoeveel zout heeft mijn zwembad nodig?", answer: "Formule: Zout (kg) = Volume (m³) × Doelconcentratie (g/L) / 1000. Met typisch doel 4 g/L heeft een zwembad van 50 m³ 200 kg nodig. Raadpleeg het typeplaatje van de elektrolyse voor de exacte concentratie aanbevolen door de fabrikant." },
          { question: "Kan ik tafelzout gebruiken in het zwembad?", answer: "Nee. Zout voor zoutelektrolyse moet zuiver NaCl >99,5 % zijn, zonder antiklontermiddelen, jodium of conserveermiddelen. Tafelzout bevat additieven die de elektrolysecel beschadigen en troebelheid en afzettingen veroorzaken. Gebruik altijd zwembad-specifiek zout of gecertificeerd industrieel zout." },
          { question: "Hoe vaak moet zout worden toegevoegd?", answer: "Zout wordt niet verbruikt door elektrolyse — het circuleert continu. Enkel aanvullen bij fysieke verliezen: spatten, terugspoelen en gedeeltelijke afvoer. In een goed onderhouden zwembad volstaat een maandelijkse controle en het toevoegen van twee zakken per jaar." },
          { question: "Beschadigt zout het zwembad?", answer: "Bij normale concentraties (3-5 g/L) niet. Boven 6 g/L (TDS >6.000 ppm) ontstaat corrosierisico voor metalen onderdelen: ladders, spots, schroeven. Monitor de TDS met teststrips en vermijd overdosering bij aanvullingen." },
        ],
      },
    },
  },
  // ============================================================
  // ARTICLE 13 — Consumo eléctrico desglose (Sprint 119.111c LONG_TAIL)
  // ============================================================
  {
    id: "une-en-17645-explicada",
    slugs: {
      es: "une-en-17645-norma-piscinas-explicada",
      en: "en-17645-standard-pools-explained",
      fr: "nf-en-17645-norme-piscines-expliquee",
      de: "din-en-17645-norm-pools-erklaert",
      it: "uni-en-17645-norma-piscine-spiegata",
      nl: "nen-en-17645-norm-zwembaden-uitgelegd",
    },
    publishDate: "2026-08-17",
    updatedDate: "2026-08-17",
    readingTime: 6,
    category: "normativa",
    coverTheme: "dark",
    // Portada propia per idioma (1200x630). Els cinc noms nacionals del mateix text i les sis classes.
    // L'alt descriu el que es VEU, no de que va l'article.
    cover: {
      es: "/img/blog/en17645-national-variants-es.jpg",
      en: "/img/blog/en17645-national-variants-en.jpg",
      fr: "/img/blog/en17645-national-variants-fr.jpg",
      de: "/img/blog/en17645-national-variants-de.jpg",
      it: "/img/blog/en17645-national-variants-it.jpg",
      nl: "/img/blog/en17645-national-variants-nl.jpg",
    },
    coverTitle: {
      es: "La norma europea de sostenibilidad de piscinas",
      en: "The European pool sustainability standard",
      fr: "La norme européenne de durabilité des piscines",
      de: "Die europäische Nachhaltigkeitsnorm für Pools",
      it: "La norma europea di sostenibilità delle piscine",
      nl: "De Europese duurzaamheidsnorm voor zwembaden",
    },
    imageAlt: {
      es: "Las cinco designaciones nacionales de la misma norma —UNE-EN, NF EN, DIN EN, UNI EN y NEN-EN 17645:2023— con la española destacada, y debajo las seis clases de la A a la F",
      en: "The five national designations of the same standard —UNE-EN, NF EN, DIN EN, UNI EN and NEN-EN 17645:2023— and below them the six classes from A to F",
      fr: "Les cinq désignations nationales de la même norme —UNE-EN, NF EN, DIN EN, UNI EN et NEN-EN 17645:2023— avec la française mise en évidence, et dessous les six classes de A à F",
      de: "Die fünf nationalen Bezeichnungen derselben Norm —UNE-EN, NF EN, DIN EN, UNI EN und NEN-EN 17645:2023— mit der deutschen hervorgehoben, darunter die sechs Klassen von A bis F",
      it: "Le cinque designazioni nazionali della stessa norma —UNE-EN, NF EN, DIN EN, UNI EN e NEN-EN 17645:2023— con quella italiana evidenziata, e sotto le sei classi dalla A alla F",
      nl: "De vijf nationale aanduidingen van dezelfde norm —UNE-EN, NF EN, DIN EN, UNI EN en NEN-EN 17645:2023— met de Nederlandse uitgelicht, en daaronder de zes klassen van A tot F",
    },
    snippetTitle: {
      es: "UNE-EN 17645 explicada · Norma europea de piscinas",
    },
    snippetDescription: {
      es: "Guía de la norma UNE-EN 17645:2023: qué es, quién la publica, los 9 grupos evaluados, las clases A-F y quién debe usarla.",
      en: "Guide to EN 17645:2023: what it is, who publishes it, the 9 evaluated groups, the A-F classes and who should be using it.",
      fr: "Guide de la NF EN 17645:2023 : ce qu'elle est, qui la publie, les 9 groupes évalués, les classes A-F et qui doit l'utiliser.",
      de: "Leitfaden zur DIN EN 17645:2023: was sie ist, wer sie herausgibt, die 9 bewerteten Gruppen, die Klassen A-F und für wen sie gilt.",
      it: "Guida alla UNI EN 17645:2023: cos'è, chi la pubblica, i 9 gruppi valutati, le classi A-F e chi dovrebbe usarla.",
      nl: "Gids over NEN-EN 17645:2023: wat het is, wie het publiceert, de 9 geëvalueerde groepen, de klassen A-F en voor wie het geldt.",
    },
    translations: {
      es: {
        title: "UNE-EN 17645 explicada · Norma europea sostenibilidad piscinas",
        h1: "UNE-EN 17645 — la norma europea de sostenibilidad de piscinas",
        description: "Guía completa a la norma UNE-EN 17645:2023: qué es, quién la publica, 9 grupos evaluados, clases A-F, quién debe usarla y sus variantes nacionales (NF/DIN/UNI/NEN).",
        keywords: ["UNE-EN 17645", "norma piscina sostenibilidad", "EN 17645 explicada", "clasificacion A-F piscina", "certificacion ambiental piscina"],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article10BodyES,
        faq: [
          { question: "¿Es obligatoria UNE-EN 17645?", answer: "No. Es una norma técnica europea voluntaria publicada por CEN y adoptada por AENOR en España como UNE-EN 17645:2023. Ninguna ley estatal o autonómica la impone hoy, aunque algunas ordenanzas municipales la están valorando para licencias de instalación." },
          { question: "¿A qué piscinas se aplica?", answer: "Solo a piscinas residenciales privadas exteriores enterradas. Quedan fuera piscinas públicas, elevadas, desmontables, cubiertas colectivas, terapéuticas o deportivas — cada categoría tiene su propia normativa específica." },
          { question: "¿Qué evalúa exactamente?", answer: "Nueve grupos técnicos: geometría del vaso, filtración, calentamiento, iluminación, cubierta, tratamiento químico, zona climática, uso previsto y sistemas de recuperación de agua. El resultado es una clase ambiental de A (máxima eficiencia) a F (mínima)." },
          { question: "¿UNE-EN 17645 es lo mismo que NF EN 17645 o DIN EN 17645?", answer: "Sí. Es la misma norma europea publicada bajo códigos nacionales distintos: UNE-EN en España, NF EN en Francia, DIN EN en Alemania, UNI EN en Italia, NEN-EN en Países Bajos y Bélgica. El contenido técnico es idéntico." },
        ],
      },
      en: {
        title: "EN 17645 Explained · European Pool Sustainability Standard",
        h1: "EN 17645 — the European pool sustainability standard",
        description: "Full guide to EN 17645:2023: what it is, who publishes it, the 9 evaluated groups, A-F classes, who should use it and its national variants (UNE/NF/DIN/UNI/NEN).",
        keywords: ["EN 17645", "pool sustainability standard", "EN 17645 explained", "A-F pool classification", "pool environmental certification"],
        author: "Víctor Saiz Vela, Chartered Industrial Engineer",
        Body: Article10BodyEN,
        faq: [
          { question: "Is EN 17645 mandatory?", answer: "No. It is a voluntary European technical standard published by CEN and adopted by each national standards body. No European law currently mandates it, though some municipal ordinances are starting to weight it for installation permits." },
          { question: "Which pools does it cover?", answer: "Only private outdoor in-ground residential pools. Public, above-ground, demountable, collective indoor, therapeutic and sport pools fall outside its scope — each category has its own specific standard." },
          { question: "What does it actually assess?", answer: "Nine technical groups: basin geometry, filtration, heating, lighting, cover, chemical treatment, climate zone, intended use and water recovery systems. The result is an environmental class from A (maximum efficiency) to F (minimum)." },
          { question: "Is EN 17645 the same as UNE-EN 17645 or DIN EN 17645?", answer: "Yes. It is the same European standard published under different national codes: UNE-EN in Spain, NF EN in France, DIN EN in Germany, UNI EN in Italy, NEN-EN in Netherlands and Belgium. Technical content is identical." },
        ],
      },
      fr: {
        title: "NF EN 17645 expliquée · Norme européenne durabilité piscines",
        h1: "NF EN 17645 — la norme européenne de durabilité des piscines",
        description: "Guide complet de la NF EN 17645:2023 : qu'est-ce que c'est, qui la publie, les 9 groupes évalués, classes A-F, qui doit l'utiliser et ses variantes nationales (UNE/DIN/UNI/NEN).",
        keywords: ["NF EN 17645", "norme piscine durabilité", "EN 17645 expliquée", "classification A-F piscine", "certification environnementale piscine"],
        author: "Víctor Saiz Vela, Ingénieur diplômé",
        Body: Article10BodyFR,
        faq: [
          { question: "La NF EN 17645 est-elle obligatoire ?", answer: "Non. C'est une norme technique européenne volontaire publiée par le CEN et adoptée par l'AFNOR en France comme NF EN 17645:2023. Aucune loi ne l'impose aujourd'hui, mais certaines collectivités locales commencent à la valoriser pour les autorisations d'installation." },
          { question: "Quelles piscines sont concernées ?", answer: "Uniquement les piscines résidentielles privées extérieures enterrées. Sont exclues les piscines publiques, hors-sol, démontables, couvertes collectives, thérapeutiques ou sportives — chaque catégorie a sa propre norme spécifique." },
          { question: "Qu'évalue-t-elle exactement ?", answer: "Neuf groupes techniques : géométrie du bassin, filtration, chauffage, éclairage, couverture, traitement chimique, zone climatique, usage prévu et systèmes de récupération d'eau. Le résultat est une classe environnementale de A (efficacité maximale) à F (minimale)." },
          { question: "NF EN 17645 est-elle identique à UNE-EN 17645 ou DIN EN 17645 ?", answer: "Oui. C'est la même norme européenne publiée sous différents codes nationaux : UNE-EN en Espagne, NF EN en France, DIN EN en Allemagne, UNI EN en Italie, NEN-EN aux Pays-Bas et en Belgique. Le contenu technique est identique." },
        ],
      },
      de: {
        title: "DIN EN 17645 erklärt · Europäische Pool-Nachhaltigkeitsnorm",
        h1: "DIN EN 17645 — die europäische Norm für Pool-Nachhaltigkeit",
        description: "Vollständiger Leitfaden zur DIN EN 17645:2023: was sie ist, wer sie herausgibt, die 9 bewerteten Gruppen, A-F Klassen, wer sie nutzen sollte und ihre nationalen Varianten (UNE/NF/UNI/NEN).",
        keywords: ["DIN EN 17645", "Pool Nachhaltigkeitsnorm", "EN 17645 erklärt", "A-F Pool Klassifizierung", "Pool Umweltzertifizierung"],
        author: "Víctor Saiz Vela, Ingenieur",
        Body: Article10BodyDE,
        faq: [
          { question: "Ist die DIN EN 17645 verpflichtend?", answer: "Nein. Es ist eine freiwillige europäische Norm, herausgegeben vom CEN und in Deutschland vom DIN als DIN EN 17645:2023 übernommen. Kein Gesetz verlangt sie derzeit, doch einige kommunale Ordnungen bewerten sie zunehmend für Installationsgenehmigungen." },
          { question: "Für welche Pools gilt sie?", answer: "Nur für private Freibad-Pools mit Erdeinbau. Öffentliche, Aufstell-, Zerlegbare, kollektive Hallenbäder, Therapie- und Sportbecken fallen nicht darunter — jede Kategorie hat ihre eigene spezifische Norm." },
          { question: "Was wird konkret bewertet?", answer: "Neun technische Gruppen: Beckengeometrie, Filtration, Heizung, Beleuchtung, Abdeckung, chemische Aufbereitung, Klimazone, geplante Nutzung und Wasserrückgewinnungssysteme. Ergebnis: eine Umweltklasse von A (maximale Effizienz) bis F (Minimum)." },
          { question: "Ist DIN EN 17645 dasselbe wie UNE-EN 17645 oder NF EN 17645?", answer: "Ja. Es ist dieselbe europäische Norm, veröffentlicht unter verschiedenen nationalen Codes: UNE-EN in Spanien, NF EN in Frankreich, DIN EN in Deutschland, UNI EN in Italien, NEN-EN in den Niederlanden und Belgien. Der technische Inhalt ist identisch." },
        ],
      },
      it: {
        title: "UNI EN 17645 spiegata · Norma europea sostenibilità piscine",
        h1: "UNI EN 17645 — la norma europea di sostenibilità delle piscine",
        description: "Guida completa alla UNI EN 17645:2023: cos'è, chi la pubblica, i 9 gruppi valutati, classi A-F, chi dovrebbe usarla e le sue varianti nazionali (UNE/NF/DIN/NEN).",
        keywords: ["UNI EN 17645", "norma piscina sostenibilità", "EN 17645 spiegata", "classificazione A-F piscina", "certificazione ambientale piscina"],
        author: "Víctor Saiz Vela, Ingegnere iscritto all'Albo",
        Body: Article10BodyIT,
        faq: [
          { question: "La UNI EN 17645 è obbligatoria?", answer: "No. È una norma tecnica europea volontaria pubblicata dal CEN e adottata in Italia dall'UNI come UNI EN 17645:2023. Nessuna legge la impone oggi, ma alcuni regolamenti comunali cominciano a valutarla per le licenze di installazione." },
          { question: "A quali piscine si applica?", answer: "Solo alle piscine residenziali private esterne interrate. Sono escluse piscine pubbliche, fuori terra, smontabili, coperte collettive, terapeutiche o sportive — ogni categoria ha la propria norma specifica." },
          { question: "Cosa valuta esattamente?", answer: "Nove gruppi tecnici: geometria della vasca, filtrazione, riscaldamento, illuminazione, copertura, trattamento chimico, zona climatica, uso previsto e sistemi di recupero acqua. Il risultato è una classe ambientale da A (massima efficienza) a F (minima)." },
          { question: "UNI EN 17645 è la stessa di UNE-EN 17645 o DIN EN 17645?", answer: "Sì. È la stessa norma europea pubblicata sotto codici nazionali diversi: UNE-EN in Spagna, NF EN in Francia, DIN EN in Germania, UNI EN in Italia, NEN-EN in Paesi Bassi e Belgio. Il contenuto tecnico è identico." },
        ],
      },
      nl: {
        title: "NEN-EN 17645 uitgelegd · Europese zwembad duurzaamheidsnorm",
        h1: "NEN-EN 17645 — de Europese norm voor zwembad-duurzaamheid",
        description: "Volledige gids over NEN-EN 17645:2023: wat het is, wie het publiceert, de 9 geëvalueerde groepen, A-F klassen, wie het zou moeten gebruiken en de nationale varianten (UNE/NF/DIN/UNI).",
        keywords: ["NEN-EN 17645", "norm zwembad duurzaamheid", "EN 17645 uitgelegd", "A-F zwembad classificatie", "zwembad milieucertificering"],
        author: "Víctor Saiz Vela, ir.",
        Body: Article10BodyNL,
        faq: [
          { question: "Is NEN-EN 17645 verplicht?", answer: "Nee. Het is een vrijwillige Europese technische norm gepubliceerd door de CEN en in Nederland en België overgenomen als NEN-EN 17645:2023 (in België ook NBN EN 17645). Geen wet vereist ze vandaag, hoewel sommige gemeentelijke verordeningen ze beginnen te waarderen voor installatievergunningen." },
          { question: "Op welke zwembaden is het van toepassing?", answer: "Alleen op privé residentiële buitenzwembaden in de grond. Publieke, bovengrondse, demonteerbare, collectieve overdekte, therapeutische en sportzwembaden vallen buiten het toepassingsgebied — elke categorie heeft haar eigen specifieke norm." },
          { question: "Wat beoordeelt het precies?", answer: "Negen technische groepen: bassingeometrie, filtratie, verwarming, verlichting, afdekking, chemische behandeling, klimaatzone, bedoeld gebruik en waterrecuperatiesystemen. Resultaat: een milieuklasse van A (maximale efficiëntie) tot F (minimaal)." },
          { question: "Is NEN-EN 17645 hetzelfde als UNE-EN 17645 of DIN EN 17645?", answer: "Ja. Het is dezelfde Europese norm gepubliceerd onder verschillende nationale codes: UNE-EN in Spanje, NF EN in Frankrijk, DIN EN in Duitsland, UNI EN in Italië, NEN-EN in Nederland en België. De technische inhoud is identiek." },
        ],
      },
    },
  },
  // ============================================================
  // ARTICLE 11 — PoolLex ID explicado (Sprint 119.111c LONG_TAIL)
  // ============================================================
  {
    id: "poollex-id-identificador-europeo",
    slugs: {
      es: "poollex-id-identificador-piscina-europeo",
      en: "poollex-id-unique-european-pool-identifier",
      fr: "poollex-id-identifiant-piscine-europeen",
      de: "poollex-id-europaeische-pool-kennung",
      it: "poollex-id-identificatore-piscina-europeo",
      nl: "poollex-id-europese-zwembad-identificatie",
    },
    publishDate: "2026-08-17",
    updatedDate: "2026-08-17",
    readingTime: 5,
    category: "iniciativa",
    coverTheme: "light",
    // Portada propia per idioma (1200x630). El codi desmuntat en prefix i set digits, amb les equivalencies.
    // L'alt descriu el que es VEU, no de que va l'article.
    cover: {
      es: "/img/blog/poollex-id-format-es.jpg",
      en: "/img/blog/poollex-id-format-en.jpg",
      fr: "/img/blog/poollex-id-format-fr.jpg",
      de: "/img/blog/poollex-id-format-de.jpg",
      it: "/img/blog/poollex-id-format-it.jpg",
      nl: "/img/blog/poollex-id-format-nl.jpg",
    },
    coverTitle: {
      es: "El identificador único europeo de piscina",
      en: "The unique European pool identifier",
      fr: "L'identifiant européen unique de piscine",
      de: "Die eindeutige europäische Pool-Kennung",
      it: "L'identificatore unico europeo della piscina",
      nl: "De unieke Europese zwembad-identificatie",
    },
    imageAlt: {
      es: "El código PLX-0000001 separado en sus dos partes —el prefijo y los siete dígitos— junto a dos identificadores equivalentes: el VIN del coche y la referencia catastral",
      en: "The code PLX-0000001 split into its two parts —the prefix and the seven digits— beside two equivalent identifiers: a car VIN and a land registry reference",
      fr: "Le code PLX-0000001 séparé en ses deux parties —le préfixe et les sept chiffres— à côté de deux identifiants équivalents : le VIN de la voiture et la référence cadastrale",
      de: "Der Code PLX-0000001 in seine zwei Teile zerlegt —das Präfix und die sieben Ziffern— neben zwei gleichwertigen Kennungen: der FIN des Autos und dem Katasterzeichen",
      it: "Il codice PLX-0000001 diviso nelle sue due parti —il prefisso e le sette cifre— accanto a due identificatori equivalenti: il telaio dell'auto e il riferimento catastale",
      nl: "De code PLX-0000001 opgesplitst in zijn twee delen —het prefix en de zeven cijfers— naast twee gelijkwaardige identificaties: het VIN van de auto en het kadasternummer",
    },
    snippetDescription: {
      es: "Qué es el PoolLex ID, formato PLX-XXXXXXX, cómo obtenerlo gratis en 30 segundos y para qué sirve en una compraventa.",
      en: "What the PoolLex ID is, the PLX-XXXXXXX format, how to get it free in 30 seconds and what it is for in a property sale.",
      fr: "Ce qu'est le PoolLex ID, le format PLX-XXXXXXX, comment l'obtenir gratuitement en 30 secondes et à quoi il sert.",
      de: "Was die PoolLex ID ist, das Format PLX-XXXXXXX, wie Sie sie in 30 Sekunden kostenlos erhalten und wozu sie dient.",
      it: "Cos'è il PoolLex ID, il formato PLX-XXXXXXX, come ottenerlo gratis in 30 secondi e a cosa serve in una compravendita.",
      nl: "Wat de PoolLex ID is, het formaat PLX-XXXXXXX, hoe u het gratis krijgt in 30 seconden en waar het voor dient.",
    },
    translations: {
      es: {
        title: "PoolLex ID · El identificador único europeo de piscinas",
        h1: "PoolLex ID — el identificador único europeo de piscinas",
        description: "Qué es PoolLex ID, formato PLX-XXXXXXX, cómo obtenerlo gratis en 30 segundos, usos prácticos (compraventa, ayudas, contratos) y analogía con VIN de coche y referencia catastral.",
        keywords: ["poollex id", "identificador piscina", "PLX-XXXXXXX", "codigo unico piscina europa", "ficha tecnica piscina PoolLex"],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article11BodyES,
        faq: [
          { question: "¿Qué es el PoolLex ID?", answer: "Es el identificador único europeo con formato PLX-XXXXXXX que PoolLex.io asigna a cada piscina residencial al generar su ficha técnica. Sirve como referencia estable durante todo el ciclo de vida de la piscina." },
          { question: "¿Es de pago o gratis?", answer: "El PoolLex ID es 100% gratis, sin registro obligatorio y descargable inmediatamente. Se emite al generar la ficha técnica gratuita en /ficha-tecnica en menos de un minuto." },
          { question: "¿Para qué sirve el PoolLex ID?", answer: "Como referencia técnica común entre propietario, instalador, mantenedor, notario, administración municipal, asegurador y comprador. Útil en compraventa inmobiliaria, solicitud de ayudas, contratos de mantenimiento y trámites urbanísticos." },
          { question: "¿A qué se parece el PoolLex ID?", answer: "Al VIN (Vehicle Identification Number) de un coche o a la referencia catastral de un inmueble: un código único e inmutable que identifica un activo concreto. La diferencia: PoolLex.io lo emite como tercera parte neutral, no vinculada a ningún fabricante." },
        ],
      },
      en: {
        title: "PoolLex ID · The Unique European Pool Identifier",
        h1: "PoolLex ID — the unique European pool identifier",
        description: "What the PoolLex ID is, PLX-XXXXXXX format, how to get it free in 30 seconds, practical uses (property transactions, grants, contracts) and analogy with car VIN and cadastral reference.",
        keywords: ["poollex id", "pool identifier", "PLX-XXXXXXX", "european unique pool code", "PoolLex technical sheet"],
        author: "Víctor Saiz Vela, Chartered Industrial Engineer",
        Body: Article11BodyEN,
        faq: [
          { question: "What is the PoolLex ID?", answer: "It is the unique European identifier with PLX-XXXXXXX format that PoolLex.io assigns to every residential pool when its technical sheet is generated. It acts as a stable reference throughout the pool's life cycle." },
          { question: "Is it paid or free?", answer: "The PoolLex ID is 100% free, without mandatory registration and immediately downloadable. It is issued when generating the free technical sheet at /ficha-tecnica in under a minute." },
          { question: "What is the PoolLex ID for?", answer: "As a shared technical reference between owner, installer, service provider, notary, municipal authority, insurer and buyer. Useful in property transactions, grant applications, maintenance contracts and urban permits." },
          { question: "What does the PoolLex ID resemble?", answer: "The VIN (Vehicle Identification Number) of a car or the cadastral reference of a property: a unique, immutable code identifying a specific asset. The difference: PoolLex.io issues it as a neutral third party, not tied to any manufacturer." },
        ],
      },
      fr: {
        title: "PoolLex ID · L'identifiant unique européen de piscines",
        h1: "PoolLex ID — l'identifiant unique européen de piscines",
        description: "Ce qu'est le PoolLex ID, format PLX-XXXXXXX, comment l'obtenir gratuitement en 30 secondes, usages pratiques (transactions, aides, contrats) et analogie avec le VIN automobile et la référence cadastrale.",
        keywords: ["poollex id", "identifiant piscine", "PLX-XXXXXXX", "code unique piscine europe", "fiche technique piscine PoolLex"],
        author: "Víctor Saiz Vela, Ingénieur diplômé",
        Body: Article11BodyFR,
        faq: [
          { question: "Qu'est-ce que le PoolLex ID ?", answer: "C'est l'identifiant unique européen au format PLX-XXXXXXX que PoolLex.io attribue à chaque piscine résidentielle lors de la génération de sa fiche technique. Il sert de référence stable pendant tout le cycle de vie de la piscine." },
          { question: "Est-il payant ou gratuit ?", answer: "Le PoolLex ID est 100% gratuit, sans inscription obligatoire et téléchargeable immédiatement. Il est émis lors de la génération de la fiche technique gratuite sur /ficha-tecnica en moins d'une minute." },
          { question: "À quoi sert le PoolLex ID ?", answer: "Comme référence technique commune entre propriétaire, installateur, mainteneur, notaire, administration municipale, assureur et acheteur. Utile pour transactions immobilières, demandes d'aides, contrats de maintenance et démarches d'urbanisme." },
          { question: "À quoi ressemble le PoolLex ID ?", answer: "Au VIN (Vehicle Identification Number) d'une voiture ou à la référence cadastrale d'un bien : un code unique et immuable identifiant un actif concret. La différence : PoolLex.io l'émet en tant que tierce partie neutre, non liée à un fabricant." },
        ],
      },
      de: {
        title: "PoolLex ID · Die eindeutige europäische Pool-Kennung",
        h1: "PoolLex ID — die eindeutige europäische Pool-Kennung",
        description: "Was die PoolLex ID ist, Format PLX-XXXXXXX, wie man sie kostenlos in 30 Sekunden erhält, praktische Anwendungen (Transaktionen, Förderungen, Verträge) und Analogie zu Fahrzeug-VIN und Grundbuch.",
        keywords: ["poollex id", "pool kennung", "PLX-XXXXXXX", "europäischer pool code", "poollex technisches datenblatt"],
        author: "Víctor Saiz Vela, Ingenieur",
        Body: Article11BodyDE,
        faq: [
          { question: "Was ist die PoolLex ID?", answer: "Es ist die eindeutige europäische Kennung im Format PLX-XXXXXXX, die PoolLex.io jedem Privatpool bei Erstellung seines technischen Datenblatts zuweist. Sie dient als stabile Referenz während des gesamten Lebenszyklus des Pools." },
          { question: "Ist sie kostenpflichtig oder kostenlos?", answer: "Die PoolLex ID ist 100% kostenlos, ohne verpflichtende Registrierung und sofort herunterladbar. Sie wird bei Erstellung des kostenlosen Datenblatts auf /ficha-tecnica in unter einer Minute ausgegeben." },
          { question: "Wozu dient die PoolLex ID?", answer: "Als gemeinsame technische Referenz zwischen Eigentümer, Installateur, Wartungsfirma, Notar, Kommune, Versicherer und Käufer. Nützlich bei Immobilientransaktionen, Förderungen, Wartungsverträgen und Baubehördlichen Verfahren." },
          { question: "Womit ist die PoolLex ID vergleichbar?", answer: "Mit der VIN (Vehicle Identification Number) eines Autos oder der Grundbuch-Referenz einer Immobilie: ein eindeutiger, unveränderlicher Code, der ein konkretes Objekt identifiziert. Unterschied: PoolLex.io erteilt sie als neutrale Drittpartei, unabhängig von Herstellern." },
        ],
      },
      it: {
        title: "PoolLex ID · L'identificatore unico europeo di piscine",
        h1: "PoolLex ID — l'identificatore unico europeo di piscine",
        description: "Cos'è il PoolLex ID, formato PLX-XXXXXXX, come ottenerlo gratis in 30 secondi, usi pratici (compravendita, incentivi, contratti) e analogia con VIN dell'auto e riferimento catastale.",
        keywords: ["poollex id", "identificatore piscina", "PLX-XXXXXXX", "codice unico piscina europa", "scheda tecnica piscina poollex"],
        author: "Víctor Saiz Vela, Ingegnere iscritto all'Albo",
        Body: Article11BodyIT,
        faq: [
          { question: "Cos'è il PoolLex ID?", answer: "È l'identificatore unico europeo con formato PLX-XXXXXXX che PoolLex.io assegna a ogni piscina residenziale al momento della generazione della scheda tecnica. Funge da riferimento stabile durante tutto il ciclo di vita della piscina." },
          { question: "È a pagamento o gratis?", answer: "Il PoolLex ID è 100% gratis, senza registrazione obbligatoria e scaricabile immediatamente. Viene emesso alla generazione della scheda tecnica gratuita su /ficha-tecnica in meno di un minuto." },
          { question: "A cosa serve il PoolLex ID?", answer: "Come riferimento tecnico condiviso tra proprietario, installatore, manutentore, notaio, amministrazione comunale, assicuratore e acquirente. Utile per transazioni immobiliari, richieste di incentivi, contratti di manutenzione e pratiche urbanistiche." },
          { question: "A cosa somiglia il PoolLex ID?", answer: "Al VIN (Vehicle Identification Number) di un'auto o al riferimento catastale di un immobile: un codice unico e immutabile che identifica un bene concreto. Differenza: PoolLex.io lo emette come terza parte neutrale, non legata ad alcun produttore." },
        ],
      },
      nl: {
        title: "PoolLex ID · De unieke Europese zwembad-identificatie",
        h1: "PoolLex ID — de unieke Europese zwembad-identificatie",
        description: "Wat de PoolLex ID is, PLX-XXXXXXX formaat, hoe u het gratis krijgt in 30 seconden, praktische toepassingen (transacties, subsidies, contracten) en analogie met auto-VIN en kadastrale referentie.",
        keywords: ["poollex id", "zwembad identificatie", "PLX-XXXXXXX", "europese unieke zwembadcode", "poollex technische fiche"],
        author: "Víctor Saiz Vela, ir.",
        Body: Article11BodyNL,
        faq: [
          { question: "Wat is de PoolLex ID?", answer: "Het is de unieke Europese identificatie met formaat PLX-XXXXXXX die PoolLex.io toekent aan elk residentieel zwembad bij het genereren van de technische fiche. Het dient als stabiele referentie tijdens de hele levenscyclus van het zwembad." },
          { question: "Is het betaald of gratis?", answer: "De PoolLex ID is 100% gratis, zonder verplichte registratie en meteen te downloaden. Ze wordt uitgegeven bij het genereren van de gratis technische fiche op /ficha-tecnica in minder dan een minuut." },
          { question: "Waarvoor dient de PoolLex ID?", answer: "Als gedeelde technische referentie tussen eigenaar, installateur, onderhoudsfirma, notaris, gemeente, verzekeraar en koper. Nuttig bij vastgoedtransacties, subsidieaanvragen, onderhoudscontracten en stedenbouwkundige procedures." },
          { question: "Waarop lijkt de PoolLex ID?", answer: "Op de VIN (Vehicle Identification Number) van een auto of de kadastrale referentie van een pand: een unieke, onveranderlijke code die een concreet goed identificeert. Verschil: PoolLex.io geeft ze uit als neutrale derde partij, niet gebonden aan een fabrikant." },
        ],
      },
    },
  },
  // ============================================================
  // ARTICLE 12 — Sal piscina (Sprint 119.111c LONG_TAIL)
  // ============================================================
  {
    id: "consumo-electrico-piscina-desglose",
    slugs: {
      es: "consumo-electrico-piscina-desglose-2026",
      en: "pool-electricity-consumption-breakdown-2026",
      fr: "consommation-electrique-piscine-detaillee-2026",
      de: "pool-stromverbrauch-aufschluesselung-2026",
      it: "consumo-elettrico-piscina-dettagliato-2026",
      nl: "zwembad-elektriciteitsverbruik-verdeling-2026",
    },
    publishDate: "2026-08-17",
    updatedDate: "2026-08-17",
    readingTime: 6,
    category: "costes",
    coverTheme: "light",
    // Portada propia per idioma (1200x630). Barres de rang en kWh/any, ordenades de mes a menys.
    // L'alt descriu el que es VEU, no de que va l'article.
    cover: {
      es: "/img/blog/pool-electricity-by-component-es.jpg",
      en: "/img/blog/pool-electricity-by-component-en.jpg",
      fr: "/img/blog/pool-electricity-by-component-fr.jpg",
      de: "/img/blog/pool-electricity-by-component-de.jpg",
      it: "/img/blog/pool-electricity-by-component-it.jpg",
      nl: "/img/blog/pool-electricity-by-component-nl.jpg",
    },
    coverTitle: {
      es: "Qué consume cada equipo de la piscina",
      en: "What each piece of pool kit consumes",
      fr: "Ce que consomme chaque équipement de piscine",
      de: "Was jede Pool-Komponente verbraucht",
      it: "Cosa consuma ogni componente della piscina",
      nl: "Wat elk onderdeel van het zwembad verbruikt",
    },
    imageAlt: {
      es: "Cuatro barras de consumo anual en una misma escala de kWh: bomba de calor 2.500-5.000, bomba de filtración 1.500-3.000, clorador salino 200-500 e iluminación LED 30-100",
      en: "Four annual consumption bars on a single kWh scale: heat pump 2,500-5,000, filtration pump 1,500-3,000, salt chlorinator 200-500 and LED lighting 30-100",
      fr: "Quatre barres de consommation annuelle sur une même échelle en kWh : pompe à chaleur 2 500-5 000, pompe de filtration 1 500-3 000, électrolyseur au sel 200-500 et éclairage LED 30-100",
      de: "Vier Balken des Jahresverbrauchs auf einer gemeinsamen kWh-Skala: Wärmepumpe 2.500-5.000, Filterpumpe 1.500-3.000, Salzelektrolyse 200-500 und LED-Beleuchtung 30-100",
      it: "Quattro barre di consumo annuo sulla stessa scala in kWh: pompa di calore 2.500-5.000, pompa di filtrazione 1.500-3.000, elettrolisi al sale 200-500 e illuminazione LED 30-100",
      nl: "Vier balken van het jaarverbruik op één kWh-schaal: warmtepomp 2.500-5.000, filtratiepomp 1.500-3.000, zoutelektrolyse 200-500 en LED-verlichting 30-100",
    },
    snippetTitle: {
      fr: "Consommation électrique piscine par composant",
      it: "Consumo elettrico piscina per componente",
      nl: "Elektriciteitsverbruik zwembad per component",
    },
    snippetDescription: {
      es: "Cuánto consume cada equipo: bomba de filtración, bomba de calor, LED y clorador salino. La tarifa valle baja la factura un 25-40 %.",
      en: "What each pool component really draws: filtration pump, heat pump, LED and salt chlorinator. Off-peak scheduling cuts the bill 25-40 %.",
      fr: "Ce que consomme chaque équipement : pompe de filtration, pompe à chaleur, LED et électrolyseur. Les heures creuses réduisent la facture de 25-40 %.",
      de: "Was jede Poolkomponente wirklich zieht: Filterpumpe, Wärmepumpe, LED und Salzelektrolyse. Schwachlast senkt die Rechnung um 25-40 %.",
      it: "Cosa consuma ogni componente: pompa di filtrazione, pompa di calore, LED ed elettrolisi. Le fasce basse riducono la bolletta del 25-40 %.",
      nl: "Wat elke component echt verbruikt: filtratiepomp, warmtepomp, LED en zoutelektrolyse. Dalurenplanning verlaagt de rekening 25-40 %.",
    },
    translations: {
      es: {
        title: "Consumo eléctrico piscina · Desglose real por componente",
        h1: "Consumo eléctrico piscina — desglose real por componente",
        description: "Cuánto consume realmente cada equipo de tu piscina: bomba de filtración, bomba de calor, iluminación LED, clorador salino, y cómo la tarifa vall/punta puede reducir la factura un 25-40 %.",
        keywords: ["calculadora consumo energetico piscina", "consumo kwh piscina", "consumo bomba filtracion", "bomba calor piscina consumo", "tarifa piscina"],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article13BodyES,
        faq: [
          { question: "¿Qué equipo de la piscina consume más electricidad?", answer: "Depende de si la piscina está climatizada. Sin bomba de calor, la bomba de filtración es el mayor consumidor (1.500-3.000 kWh/año). Con bomba de calor, esta puede duplicar el consumo total. En piscinas climatizadas la bomba de calor es siempre el mayor consumidor." },
          { question: "¿Vale la pena cambiar a bomba de velocidad variable?", answer: "Sí. Una VSD bien programada reduce el consumo de filtración un 60-80 %. El retorno de inversión típico es de 2-4 años según horas de uso y tarifa eléctrica. Es la actuación con mayor retorno económico en la mayoría de reformas de piscina existente." },
          { question: "¿Cuánto se ahorra programando en horas valle?", answer: "Entre un 25 y un 40 % sobre la factura eléctrica de la piscina, sin cambiar ningún equipo. La diferencia entre kWh valle (~0,08 €/kWh) y punta (~0,25 €/kWh) es de 3× — programar filtración y bomba de calor mayoritariamente en 00:00-08:00 y fines de semana es determinante." },
          { question: "¿La iluminación LED aporta mucho ahorro?", answer: "Rentable pero de bajo impacto absoluto. El cambio de halógenos a LED reduce el consumo por foco de 100-300 W a 15-30 W, pero como las horas de uso son pocas, el ahorro anual está en decenas de euros, no cientos. La renta es principalmente por menor calor emitido al agua." },
        ],
      },
      en: {
        title: "Pool Electricity Consumption · Real Breakdown by Component",
        h1: "Pool electricity consumption — real breakdown by component",
        description: "How much each pool component really consumes: filtration pump, heat pump, LED lighting, salt chlorinator, and how off-peak/peak scheduling can cut the bill by 25-40 %.",
        keywords: ["pool energy calculator", "pool kwh consumption", "filtration pump consumption", "heat pump pool energy", "pool electricity tariff"],
        author: "Víctor Saiz Vela, Chartered Industrial Engineer",
        Body: Article13BodyEN,
        faq: [
          { question: "Which pool component uses the most electricity?", answer: "It depends on whether the pool is heated. Without a heat pump, the filtration pump is the largest consumer (1,500-3,000 kWh/year). With a heat pump, the heat pump can double the total. In heated pools the heat pump is always the top consumer." },
          { question: "Is it worth switching to a variable-speed pump?", answer: "Yes. A well-programmed VSD cuts filtration consumption by 60-80 %. Typical payback is 2-4 years depending on runtime and electricity tariff. It is the highest-return refurbishment in most existing pools." },
          { question: "How much can off-peak scheduling save?", answer: "Between 25 and 40 % on the pool's electricity bill, without changing any equipment. The gap between off-peak kWh (~€0.08/kWh) and peak (~€0.25/kWh) is 3× — scheduling filtration and the heat pump mostly to 00:00-08:00 and weekends is decisive." },
          { question: "Does LED lighting deliver much saving?", answer: "Profitable but low absolute impact. Switching from halogens to LEDs cuts per-fixture consumption from 100-300 W to 15-30 W, but as runtime hours are few, annual savings are tens of euros, not hundreds. The main benefit is less heat emitted into the water." },
        ],
      },
      fr: {
        title: "Consommation électrique piscine · Décomposition réelle par composant",
        h1: "Consommation électrique piscine — décomposition réelle par composant",
        description: "Ce que consomme vraiment chaque équipement de piscine : pompe filtration, pompe à chaleur, éclairage LED, électrolyseur au sel, et comment la programmation heures creuses/pleines peut réduire la facture de 25-40 %.",
        keywords: ["calculateur consommation piscine", "consommation kwh piscine", "consommation pompe filtration", "pompe a chaleur piscine consommation", "tarif piscine"],
        author: "Víctor Saiz Vela, Ingénieur diplômé",
        Body: Article13BodyFR,
        faq: [
          { question: "Quel équipement consomme le plus d'électricité ?", answer: "Cela dépend si la piscine est chauffée. Sans pompe à chaleur, la pompe de filtration est le plus gros consommateur (1 500-3 000 kWh/an). Avec pompe à chaleur, celle-ci peut doubler le total. Sur piscine chauffée, la pompe à chaleur est toujours le principal poste." },
          { question: "Le passage à une pompe à vitesse variable est-il rentable ?", answer: "Oui. Une VSD bien programmée réduit la consommation de filtration de 60-80 %. Le retour sur investissement typique est de 2-4 ans selon les heures d'usage et le tarif. C'est l'action au meilleur retour économique dans la plupart des rénovations." },
          { question: "Combien économise-t-on en programmant en heures creuses ?", answer: "Entre 25 et 40 % sur la facture électrique de la piscine, sans changer d'équipement. L'écart entre kWh heures creuses (~0,08 €/kWh) et pointe (~0,25 €/kWh) est de 3× — programmer filtration et pompe à chaleur essentiellement en 00h00-08h00 et week-ends est décisif." },
          { question: "L'éclairage LED apporte-t-il beaucoup d'économies ?", answer: "Rentable mais d'impact absolu faible. Passer des halogènes aux LED réduit la consommation par projecteur de 100-300 W à 15-30 W, mais les heures d'usage étant limitées, l'économie annuelle est de quelques dizaines d'euros, pas des centaines. Le principal bénéfice est moins de chaleur émise dans l'eau." },
        ],
      },
      de: {
        title: "Poolstromverbrauch · Reale Aufschlüsselung nach Komponenten",
        h1: "Poolstromverbrauch — reale Aufschlüsselung nach Komponenten",
        description: "Was jede Poolkomponente wirklich verbraucht: Filterpumpe, Wärmepumpe, LED-Beleuchtung, Salzelektrolyse, und wie Schwachlast-Programmierung die Rechnung um 25-40 % senkt.",
        keywords: ["poolenergie rechner", "pool kwh verbrauch", "filterpumpe stromverbrauch", "poolwärmepumpe verbrauch", "pool stromtarif"],
        author: "Víctor Saiz Vela, Ingenieur",
        Body: Article13BodyDE,
        faq: [
          { question: "Welche Poolkomponente verbraucht am meisten Strom?", answer: "Es hängt davon ab, ob der Pool beheizt ist. Ohne Wärmepumpe ist die Filterpumpe der größte Verbraucher (1.500-3.000 kWh/Jahr). Mit Wärmepumpe kann diese den Gesamtverbrauch verdoppeln. In beheizten Pools ist die Wärmepumpe stets Spitzenreiter." },
          { question: "Lohnt sich der Wechsel zu drehzahlgeregelten Pumpen?", answer: "Ja. Eine gut programmierte VSD senkt den Filtrationsverbrauch um 60-80 %. Typische Amortisation 2-4 Jahre je nach Laufzeit und Stromtarif. Es ist die renditestärkste Sanierungsmaßnahme in den meisten Bestandspools." },
          { question: "Wie viel spart die Programmierung in Schwachlast?", answer: "Zwischen 25 und 40 % auf der Poolstromrechnung, ohne Gerätewechsel. Der Unterschied zwischen Schwachlast-kWh (~0,08 €/kWh) und Spitzenlast (~0,25 €/kWh) beträgt das 3-Fache — Filtration und Wärmepumpe überwiegend in 00:00-08:00 und Wochenenden zu betreiben ist entscheidend." },
          { question: "Bringt LED-Beleuchtung große Einsparungen?", answer: "Rentabel, aber geringer absoluter Effekt. Der Wechsel von Halogen zu LED senkt den Verbrauch pro Leuchtpunkt von 100-300 W auf 15-30 W, aber wegen der wenigen Betriebsstunden liegen Jahresersparnisse bei zehn Euro, nicht Hunderten. Hauptvorteil: weniger Wärmeeintrag ins Wasser." },
        ],
      },
      it: {
        title: "Consumo elettrico piscina · Ripartizione reale per componente",
        h1: "Consumo elettrico piscina — ripartizione reale per componente",
        description: "Cosa consuma davvero ogni componente della piscina: pompa filtrazione, pompa di calore, illuminazione LED, elettrolisi al sale, e come le fasce basse possono ridurre la bolletta del 25-40 %.",
        keywords: ["calcolatore energia piscina", "consumo kwh piscina", "consumo pompa filtrazione", "pompa calore piscina consumo", "tariffa piscina"],
        author: "Víctor Saiz Vela, Ingegnere iscritto all'Albo",
        Body: Article13BodyIT,
        faq: [
          { question: "Quale componente della piscina consuma più elettricità?", answer: "Dipende se la piscina è riscaldata. Senza pompa di calore, la pompa di filtrazione è il maggior consumatore (1.500-3.000 kWh/anno). Con pompa di calore, questa può raddoppiare il totale. Su piscina riscaldata la pompa di calore è sempre in cima." },
          { question: "Conviene passare a pompa a velocità variabile?", answer: "Sì. Una VSD ben programmata riduce il consumo di filtrazione del 60-80 %. Ritorno tipico 2-4 anni in base a ore di uso e tariffa elettrica. È l'intervento con il maggior ritorno economico in quasi tutte le ristrutturazioni." },
          { question: "Quanto si risparmia programmando in fasce basse?", answer: "Tra il 25 e il 40 % sulla bolletta elettrica della piscina, senza cambiare apparecchiature. La differenza tra kWh in fascia bassa (~0,08 €/kWh) e picco (~0,25 €/kWh) è di 3× — programmare filtrazione e pompa di calore prevalentemente in 00:00-08:00 e weekend è determinante." },
          { question: "L'illuminazione LED porta molti risparmi?", answer: "Conveniente ma di basso impatto assoluto. Il passaggio da alogeni a LED riduce il consumo per proiettore da 100-300 W a 15-30 W, ma le ore d'uso sono poche, quindi il risparmio annuo è di poche decine di euro, non centinaia. Il beneficio principale è meno calore emesso nell'acqua." },
        ],
      },
      nl: {
        title: "Zwembad elektriciteitsverbruik · Reële verdeling per component",
        h1: "Zwembad elektriciteitsverbruik — reële verdeling per component",
        description: "Wat elke zwembadcomponent echt verbruikt: filtratiepomp, warmtepomp, LED-verlichting, zoutelektrolyse, en hoe dal/piek-planning de rekening met 25-40 % kan verlagen.",
        keywords: ["energiecalculator zwembad", "zwembad kwh verbruik", "filtratiepomp verbruik", "warmtepomp zwembad verbruik", "zwembad elektriciteitstarief"],
        author: "Víctor Saiz Vela, ir.",
        Body: Article13BodyNL,
        faq: [
          { question: "Welke zwembadcomponent verbruikt de meeste elektriciteit?", answer: "Het hangt af of het zwembad verwarmd is. Zonder warmtepomp is de filtratiepomp de grootste verbruiker (1.500-3.000 kWh/jaar). Met warmtepomp kan die het totaal verdubbelen. In verwarmde zwembaden staat de warmtepomp altijd bovenaan." },
          { question: "Is overschakelen op een pomp met variabele snelheid rendabel?", answer: "Ja. Een goed geprogrammeerde VSD verlaagt het filtratieverbruik met 60-80 %. Typische terugverdientijd 2-4 jaar afhankelijk van bedrijfsuren en tarief. Het is de renovatie met de hoogste rendabiliteit in de meeste bestaande zwembaden." },
          { question: "Hoeveel bespaart daluren-planning?", answer: "Tussen 25 en 40 % op de elektriciteitsrekening van het zwembad, zonder apparatuur te vervangen. Het verschil tussen daluren-kWh (~€0,08/kWh) en piekuren (~€0,25/kWh) is 3× — filtratie en warmtepomp voornamelijk in 00:00-08:00 en weekends plannen is bepalend." },
          { question: "Levert LED-verlichting veel besparing op?", answer: "Rendabel maar met lage absolute impact. Van halogenen naar LED verlaagt het verbruik per spot van 100-300 W naar 15-30 W, maar omdat de bedrijfsuren beperkt zijn, ligt de jaarlijkse besparing bij tientallen euro's, niet honderden. Grootste voordeel: minder warmte-inbreng in het water." },
        ],
      },
    },
  },
  // ============================================================
  // ARTICLE 14 — Coste mensual caso real 40m2 (Sprint 119.111c LONG_TAIL)
  // ============================================================
  {
    id: "coste-mensual-piscina-40m2",
    slugs: {
      es: "coste-mensual-piscina-caso-real-40m2",
      en: "monthly-pool-cost-real-case-40m2",
      fr: "cout-mensuel-piscine-cas-reel-40m2",
      de: "monatliche-poolkosten-fallbeispiel-40qm",
      it: "costo-mensile-piscina-caso-reale-40m2",
      nl: "maandelijkse-zwembadkosten-praktijkgeval-40m2",
    },
    publishDate: "2026-08-17",
    updatedDate: "2026-08-17",
    readingTime: 7,
    category: "costes",
    coverTheme: "dark",
    // Portada propia per idioma (1200x630). Les quatre partides sumades fins al total mensual.
    // L'alt descriu el que es VEU, no de que va l'article.
    cover: {
      es: "/img/blog/pool-cost-40m2-breakdown-es.jpg",
      en: "/img/blog/pool-cost-40m2-breakdown-en.jpg",
      fr: "/img/blog/pool-cost-40m2-breakdown-fr.jpg",
      de: "/img/blog/pool-cost-40m2-breakdown-de.jpg",
      it: "/img/blog/pool-cost-40m2-breakdown-it.jpg",
      nl: "/img/blog/pool-cost-40m2-breakdown-nl.jpg",
    },
    coverTitle: {
      es: "Coste mensual de una piscina de 40 m²",
      en: "Monthly cost of a 40 m² pool",
      fr: "Coût mensuel d'une piscine de 40 m²",
      de: "Monatliche Kosten eines 40-m²-Pools",
      it: "Costo mensile di una piscina da 40 m²",
      nl: "Maandelijkse kosten van een zwembad van 40 m²",
    },
    imageAlt: {
      es: "Las cuatro partidas de gasto de una piscina de 40 m² —electricidad, agua, químicos y mantenimiento— sumadas hasta un total de 80 a 110 €/mes",
      en: "The four cost lines of a 40 m² pool —electricity, water, chemicals and maintenance— added up to a total of 80 to 110 €/month",
      fr: "Les quatre postes de dépense d'une piscine de 40 m² —électricité, eau, produits chimiques et entretien— additionnés jusqu'à un total de 80 à 110 €/mois",
      de: "Die vier Kostenpositionen eines 40-m²-Pools —Strom, Wasser, Chemie und Wartung— aufsummiert zu insgesamt 80 bis 110 €/Monat",
      it: "Le quattro voci di spesa di una piscina da 40 m² —elettricità, acqua, chimici e manutenzione— sommate fino a un totale di 80-110 €/mese",
      nl: "De vier kostenposten van een zwembad van 40 m² —elektriciteit, water, chemie en onderhoud— opgeteld tot een totaal van 80 tot 110 €/maand",
    },
    snippetTitle: {
      nl: "Maandelijkse kosten zwembad van 40 m²",
    },
    snippetDescription: {
      es: "Coste mensual real de una piscina de 40 m² en el sur de Europa: electricidad, agua, químicos y mantenimiento. Unos 80-110 €/mes.",
      en: "The real monthly cost of a 40 m² pool in southern Europe: electricity, water, chemicals and maintenance. Around €80-110/month.",
      fr: "Coût mensuel réel d'une piscine de 40 m² au sud de l'Europe : électricité, eau, produits et entretien. Environ 80-110 €/mois.",
      de: "Reale Monatskosten eines 40 m² Pools in Südeuropa: Strom, Wasser, Chemie und Wartung. Etwa 80-110 € pro Monat.",
      it: "Costo mensile reale di una piscina da 40 m² nel sud Europa: elettricità, acqua, chimici e manutenzione. Circa 80-110 €/mese.",
      nl: "Reële maandkost van een zwembad van 40 m² in Zuid-Europa: elektriciteit, water, chemie en onderhoud. Ongeveer 80-110 €/maand.",
    },
    translations: {
      es: {
        title: "Coste mensual piscina · Caso real detallado 40 m²",
        h1: "Coste mensual piscina — caso real detallado 40 m²",
        description: "Análisis técnico del coste mensual real de una piscina de 40 m² en zona templada del sur de Europa: desglose por electricidad, agua, químicos y mantenimiento. Resultado ~80-110 €/mes.",
        keywords: ["coste mensual piscina", "gasto piscina 40m2", "coste real mantener piscina", "presupuesto piscina anual", "cuanto cuesta piscina al mes"],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article14BodyES,
        faq: [
          { question: "¿Cuánto cuesta al mes una piscina de 40 m²?", answer: "Entre 80 y 110 €/mes de promedio anual para una piscina residencial estándar en zona templada del sur de Europa, con bomba de calor de temporada extendida. Sin amortización de bomba de calor: 80 €/mes. Con amortización incluida: 110 €/mes. La cifra baja a 40-60 €/mes en piscinas sin climatizar." },
          { question: "¿Qué componente pesa más en la factura?", answer: "La electricidad, con un 48 % del gasto operativo. Dentro de ella, la bomba de calor concentra la mayor parte si la piscina está climatizada (1.333 kWh/año). En piscinas sin climatizar, la bomba de filtración pasa a ser el mayor consumidor." },
          { question: "¿Cómo puedo reducir el coste mensual?", answer: "Tres actuaciones con máximo retorno: (1) instalar bomba de velocidad variable (VSD) que reduce filtración un 60-80 %; (2) programar bomba de calor y filtración en horas valle, ahorro 25-40 % en factura eléctrica; (3) cubierta térmica motorizada que reduce evaporación y pérdidas nocturnas en un 60-70 %." },
          { question: "¿Se puede mantener una piscina climatizada por menos de 100 €/mes?", answer: "Sí, si se combinan las tres actuaciones anteriores. Un caso similar con bomba VSD, cubierta motorizada y programación en horas valle puede bajar a 60-80 €/mes de coste operativo. La amortización de equipos añade 20-30 €/mes adicionales." },
        ],
      },
      en: {
        title: "Monthly Pool Cost · Real Detailed Case 40 m²",
        h1: "Monthly pool cost — real detailed case 40 m²",
        description: "Technical analysis of the real monthly cost of a 40 m² pool in temperate southern Europe: breakdown by electricity, water, chemicals and maintenance. Result: ~€80-110/month.",
        keywords: ["monthly pool cost", "40m2 pool cost", "real pool running cost", "annual pool budget", "how much pool per month"],
        author: "Víctor Saiz Vela, Chartered Industrial Engineer",
        Body: Article14BodyEN,
        faq: [
          { question: "How much does a 40 m² pool cost per month?", answer: "Between €80 and €110/month annual average for a standard residential pool in temperate southern Europe with a heat pump and extended season. Without heat pump depreciation: €80/month. With depreciation: €110/month. Unheated pools drop to €40-60/month." },
          { question: "Which component weighs most on the bill?", answer: "Electricity, at 48 % of operating cost. Within it, the heat pump concentrates the largest share when the pool is heated (1,333 kWh/year). In unheated pools the filtration pump becomes the top consumer." },
          { question: "How can I lower the monthly cost?", answer: "Three high-return actions: (1) install a variable-speed pump (VSD) that cuts filtration by 60-80 %; (2) schedule heat pump and filtration to off-peak hours, saving 25-40 % on the electricity bill; (3) a motorised thermal cover that cuts evaporation and night losses by 60-70 %." },
          { question: "Can a heated pool be run for under €100/month?", answer: "Yes, if the three actions above are combined. A similar case with VSD pump, motorised cover and off-peak scheduling can drop to €60-80/month in operating cost. Equipment depreciation adds another €20-30/month." },
        ],
      },
      fr: {
        title: "Coût mensuel piscine · Cas réel détaillé 40 m²",
        h1: "Coût mensuel piscine — cas réel détaillé 40 m²",
        description: "Analyse technique du coût mensuel réel d'une piscine de 40 m² en zone tempérée du sud de l'Europe : décomposition par électricité, eau, produits chimiques et entretien. Résultat ~80-110 €/mois.",
        keywords: ["cout mensuel piscine", "budget piscine 40m2", "cout reel entretien piscine", "budget piscine annuel", "combien piscine par mois"],
        author: "Víctor Saiz Vela, Ingénieur diplômé",
        Body: Article14BodyFR,
        faq: [
          { question: "Combien coûte par mois une piscine de 40 m² ?", answer: "Entre 80 et 110 €/mois en moyenne annuelle pour une piscine résidentielle standard en zone tempérée du sud de l'Europe, avec pompe à chaleur et saison étendue. Sans amortissement pompe à chaleur : 80 €/mois. Avec amortissement inclus : 110 €/mois. Piscines non chauffées : 40-60 €/mois." },
          { question: "Quel composant pèse le plus sur la facture ?", answer: "L'électricité, 48 % du coût opérationnel. Dans celle-ci, la pompe à chaleur concentre l'essentiel si la piscine est chauffée (1 333 kWh/an). Sur piscine non chauffée, la pompe de filtration devient le principal poste." },
          { question: "Comment réduire le coût mensuel ?", answer: "Trois actions au meilleur retour : (1) installer une pompe à vitesse variable (VSD) réduisant la filtration de 60-80 % ; (2) programmer pompe à chaleur et filtration en heures creuses, économie 25-40 % sur la facture électrique ; (3) couverture thermique motorisée réduisant évaporation et pertes nocturnes de 60-70 %." },
          { question: "Peut-on entretenir une piscine chauffée pour moins de 100 €/mois ?", answer: "Oui, en combinant les trois actions ci-dessus. Un cas similaire avec pompe VSD, couverture motorisée et programmation en heures creuses peut descendre à 60-80 €/mois de coût opérationnel. L'amortissement des équipements ajoute 20-30 €/mois." },
        ],
      },
      de: {
        title: "Monatliche Poolkosten · Detailliertes Fallbeispiel 40 m²",
        h1: "Monatliche Poolkosten — detailliertes Fallbeispiel 40 m²",
        description: "Technische Analyse der realen monatlichen Kosten eines 40 m² Pools in gemäßigtem Südeuropa: Aufschlüsselung nach Strom, Wasser, Chemie und Wartung. Ergebnis: ~80-110 €/Monat.",
        keywords: ["monatliche poolkosten", "poolkosten 40qm", "reale poolbetriebskosten", "poolbudget jährlich", "was kostet pool pro monat"],
        author: "Víctor Saiz Vela, Ingenieur",
        Body: Article14BodyDE,
        faq: [
          { question: "Was kostet ein 40 m² Pool pro Monat?", answer: "Zwischen 80 und 110 €/Monat Jahresdurchschnitt für einen Standard-Privatpool in gemäßigtem Südeuropa mit Wärmepumpe und verlängerter Saison. Ohne Wärmepumpen-Abschreibung: 80 €/Monat. Mit Abschreibung: 110 €/Monat. Unbeheizte Pools: 40-60 €/Monat." },
          { question: "Welche Komponente wiegt am schwersten in der Rechnung?", answer: "Strom, mit 48 % der Betriebskosten. Darin konzentriert die Wärmepumpe den größten Anteil bei beheizten Pools (1.333 kWh/Jahr). In unbeheizten Pools wird die Filterpumpe zum Spitzenreiter." },
          { question: "Wie kann ich die monatlichen Kosten senken?", answer: "Drei renditestarke Maßnahmen: (1) drehzahlgeregelte Pumpe (VSD) installieren, senkt Filtration um 60-80 %; (2) Wärmepumpe und Filtration in Schwachlaststunden betreiben, spart 25-40 % der Stromrechnung; (3) motorisierte Thermoabdeckung, reduziert Verdunstung und nächtliche Verluste um 60-70 %." },
          { question: "Kann ein beheizter Pool unter 100 €/Monat betrieben werden?", answer: "Ja, wenn die drei genannten Maßnahmen kombiniert werden. Ein vergleichbares Fallbeispiel mit VSD-Pumpe, motorisierter Abdeckung und Schwachlast-Programmierung kann auf 60-80 €/Monat Betriebskosten sinken. Geräte-Abschreibung addiert weitere 20-30 €/Monat." },
        ],
      },
      it: {
        title: "Costo mensile piscina · Caso reale dettagliato 40 m²",
        h1: "Costo mensile piscina — caso reale dettagliato 40 m²",
        description: "Analisi tecnica del costo mensile reale di una piscina da 40 m² in zona temperata del sud Europa: ripartizione per elettricità, acqua, chimici e manutenzione. Risultato ~80-110 €/mese.",
        keywords: ["costo mensile piscina", "spesa piscina 40m2", "costo reale piscina", "budget piscina annuale", "quanto costa piscina al mese"],
        author: "Víctor Saiz Vela, Ingegnere iscritto all'Albo",
        Body: Article14BodyIT,
        faq: [
          { question: "Quanto costa al mese una piscina da 40 m²?", answer: "Tra 80 e 110 €/mese di media annua per una piscina residenziale standard in zona temperata del sud Europa, con pompa di calore e stagione estesa. Senza ammortamento pompa di calore: 80 €/mese. Con ammortamento incluso: 110 €/mese. Piscine non riscaldate: 40-60 €/mese." },
          { question: "Quale componente pesa di più sulla bolletta?", answer: "L'elettricità, con il 48 % del costo operativo. Al suo interno, la pompa di calore concentra la maggior parte se la piscina è riscaldata (1.333 kWh/anno). Su piscine non riscaldate la pompa di filtrazione diventa il maggior consumatore." },
          { question: "Come posso ridurre il costo mensile?", answer: "Tre interventi ad alto ritorno: (1) installare pompa a velocità variabile (VSD) che riduce la filtrazione del 60-80 %; (2) programmare pompa di calore e filtrazione in fasce basse, risparmio 25-40 % sulla bolletta elettrica; (3) copertura termica motorizzata che riduce evaporazione e perdite notturne del 60-70 %." },
          { question: "Si può gestire una piscina riscaldata sotto i 100 €/mese?", answer: "Sì, combinando i tre interventi sopra. Un caso simile con pompa VSD, copertura motorizzata e programmazione in fasce basse può scendere a 60-80 €/mese di costo operativo. L'ammortamento delle apparecchiature aggiunge altri 20-30 €/mese." },
        ],
      },
      nl: {
        title: "Maandelijkse zwembadkosten · Gedetailleerd praktijkgeval 40 m²",
        h1: "Maandelijkse zwembadkosten — gedetailleerd praktijkgeval 40 m²",
        description: "Technische analyse van de reële maandelijkse kost van een zwembad van 40 m² in gematigd Zuid-Europa: verdeling naar elektriciteit, water, chemie en onderhoud. Resultaat ~80-110 €/maand.",
        keywords: ["maandelijkse zwembadkosten", "zwembadkosten 40m2", "reële zwembadkosten", "zwembadbudget jaarlijks", "hoeveel kost zwembad per maand"],
        author: "Víctor Saiz Vela, ir.",
        Body: Article14BodyNL,
        faq: [
          { question: "Hoeveel kost een zwembad van 40 m² per maand?", answer: "Tussen €80 en €110/maand jaargemiddelde voor een standaard residentieel zwembad in gematigd Zuid-Europa, met warmtepomp en verlengd seizoen. Zonder afschrijving warmtepomp: €80/maand. Met afschrijving inbegrepen: €110/maand. Onverwarmde zwembaden: €40-60/maand." },
          { question: "Welke component weegt het zwaarst op de rekening?", answer: "Elektriciteit, met 48 % van de operationele kost. Daarin concentreert de warmtepomp het grootste aandeel bij verwarmde zwembaden (1.333 kWh/jaar). In onverwarmde zwembaden wordt de filtratiepomp de grootste verbruiker." },
          { question: "Hoe kan ik de maandelijkse kost verlagen?", answer: "Drie acties met hoog rendement: (1) een pomp met variabele snelheid (VSD) installeren die filtratie met 60-80 % verlaagt; (2) warmtepomp en filtratie in daluren plannen, besparing 25-40 % op de elektriciteitsrekening; (3) gemotoriseerde thermische afdekking die verdamping en nachtverliezen met 60-70 % verlaagt." },
          { question: "Kan een verwarmd zwembad onder €100/maand?", answer: "Ja, door bovenstaande drie acties te combineren. Een vergelijkbaar geval met VSD-pomp, gemotoriseerde afdekking en daluren-planning kan zakken naar €60-80/maand aan operationele kost. Afschrijving van apparatuur voegt nog €20-30/maand toe." },
        ],
      },
    },
  },
  // Sprint 119.123 — Article 15: Piscina sostenible (keyword estratègic)
  {
    id: "piscina-sostenible-guia-2026",
    slugs: {
      es: "piscina-sostenible-guia-completa-2026",
      en: "sustainable-pool-complete-guide-2026",
      fr: "piscine-durable-guide-complet-2026",
      de: "nachhaltiger-pool-kompletter-leitfaden-2026",
      it: "piscina-sostenibile-guida-completa-2026",
      nl: "duurzaam-zwembad-complete-gids-2026",
    },
    publishDate: "2026-08-19",
    updatedDate: "2026-08-19",
    readingTime: 6,
    category: "sostenibilidad",
    coverTheme: "light",
    // Sprint 150 — Portada propia per idioma (1200x630), primera de TEMA CLAR.
    // Victor vol anar intercalant fons fosc i clar cada dos articles al
    // llistat del blog: una paret de portades fosques identiques cansa.
    // Mateix sistema (reixa, filet cian+verd, columna esquerra, dibuix amb
    // escala a peu); nomes canvia la paleta. En clar el logotip va tal qual.
    //
    // Dibuixa els SIS PILARS que la propia description de l'article llista,
    // amb la RODA OFICIAL de classificacio A-F de la norma al costat: la
    // mateixa que ja fan servir la Ficha i el segell. Abans eren lletres
    // dibuixades per mi, que ni tenien autoritat ni es llegien.
    cover: {
      es: "/img/blog/sustainable-pool-en17645-scale-es.jpg",
      en: "/img/blog/sustainable-pool-en17645-scale-en.jpg",
      fr: "/img/blog/sustainable-pool-en17645-scale-fr.jpg",
      de: "/img/blog/sustainable-pool-en17645-scale-de.jpg",
      it: "/img/blog/sustainable-pool-en17645-scale-it.jpg",
      nl: "/img/blog/sustainable-pool-en17645-scale-nl.jpg",
    },
    coverTitle: {
      es: "Qué hace sostenible una piscina",
      en: "What makes a pool sustainable",
      fr: "Ce qui rend durable une piscine",
      de: "Was einen Pool nachhaltig macht",
      it: "Cosa rende sostenibile una piscina",
      nl: "Wat een zwembad duurzaam maakt",
    },
    // El alt descriu el que es VEU. L'anterior descrivia una fotografia
    // d'una piscina; la imatge es un esquema de sis fitxes.
    imageAlt: {
      es: "Los seis pilares de una piscina sostenible en fichas —filtración VSD, cubierta, bomba de calor COP≥6, iluminación LED, tratamiento y aislamiento— junto a la rueda oficial de clasificación de la A a la F de la norma",
      en: "The six pillars of a sustainable pool as tiles —VSD filtration, cover, COP≥6 heat pump, LED lighting, treatment and insulation— beside the standard's official A-to-F classification wheel",
      fr: "Les six piliers d'une piscine durable en fiches —filtration VSD, couverture, PAC COP≥6, éclairage LED, traitement et isolation— à côté de la roue officielle de classification de A à F de la norme",
      de: "Die sechs Säulen eines nachhaltigen Pools als Kacheln —VSD-Filtration, Abdeckung, Wärmepumpe COP≥6, LED-Licht, Behandlung und Dämmung— neben dem offiziellen Klassifizierungsrad der Norm von A bis F",
      it: "I sei pilastri di una piscina sostenibile in schede —filtrazione VSD, copertura, pompa di calore COP≥6, illuminazione LED, trattamento e isolamento— accanto alla ruota ufficiale di classificazione dalla A alla F della norma",
      nl: "De zes pijlers van een duurzaam zwembad als tegels —VSD-filtratie, afdekking, warmtepomp COP≥6, LED-verlichting, behandeling en isolatie— naast het officiële classificatiewiel van de norm van A tot F",
    },
    snippetTitle: {
      es: "Piscina sostenible 2026: 6 pilares + EN 17645",
      fr: "Piscine durable 2026 : 6 piliers + NF EN 17645",
      de: "Nachhaltiger Pool 2026: 6 Säulen + DIN EN 17645",
      it: "Piscina sostenibile 2026: 6 pilastri + UNI EN 17645",
      nl: "Duurzaam zwembad 2026: 6 pijlers + NEN-EN 17645",
    },
    snippetDescription: {
      es: "Los 6 pilares de una piscina sostenible en 2026: filtración VSD, cubierta, COP≥6, LED, tratamiento y aislamiento, con UNE-EN 17645.",
      en: "The 6 pillars of a sustainable pool in 2026: VSD filtration, cover, COP≥6, LED, treatment and insulation, under EN 17645:2023.",
    },
    translations: {
      es: {
        title: "Piscina sostenible: guía completa 2026 · 6 pilares + EN 17645",
        h1: "Piscina sostenible — guía completa 2026",
        description: "Qué es una piscina sostenible en 2026: 6 pilares (filtración VSD, cubierta, COP≥6, LED, tratamiento, aislamiento), la norma UNE-EN 17645:2023 y 5 mejoras que multiplican tu clase A-F en 6 meses.",
        keywords: ["piscina sostenible", "piscina ecológica", "sostenibilidad piscina", "certificado piscina sostenible", "UNE-EN 17645", "piscina eficiente 2026"],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article15BodyES,
        faq: [
          { question: "¿Qué es una piscina sostenible?", answer: "Una instalación diseñada, equipada y operada para minimizar su impacto ambiental global sobre agua, energía, productos químicos, materiales y durabilidad. Se apoya en 6 pilares técnicos y se mide de forma armonizada con la norma UNE-EN 17645:2023 mediante una clasificación A-F." },
          { question: "¿Cuánto cuesta hacer sostenible una piscina existente?", answer: "Depende del alcance. Mejoras aisladas (LED, horas valle) tienen coste bajo (300-800 €) y retorno inferior a 12 meses. Una reforma media (bomba VSD + cubierta motorizada + clorador salino) se sitúa en 3.500-6.500 € con retorno 2-4 temporadas. Una reforma integral puede superar los 12.000 € con salto de 2-3 clases." },
          { question: "¿Cuál es la diferencia entre piscina eficiente y piscina sostenible?", answer: "Una piscina eficiente optimiza el consumo energético. Una piscina sostenible amplía el foco al agua, los productos químicos, los materiales y la durabilidad. La eficiencia energética es parte necesaria pero no suficiente de la sostenibilidad según UNE-EN 17645:2023." },
          { question: "¿Cómo obtengo un certificado de piscina sostenible?", answer: "En 2 minutos gratis via Ficha Técnica PoolLex.io con clase EN 17645 estimada. Para operaciones inmobiliarias o licencias necesitas el Sello firmado por ingeniero industrial colegiado, que se solicita en /solicitar-sello." },
        ],
      },
      en: {
        title: "Sustainable Pool: Complete 2026 Guide · 6 Pillars + EN 17645",
        h1: "Sustainable pool — complete 2026 guide",
        description: "What a sustainable pool is in 2026: 6 pillars (VSD filtration, cover, COP≥6, LED, treatment, insulation), the EN 17645:2023 standard and 5 upgrades that boost your A-F class in 6 months.",
        keywords: ["sustainable pool", "eco friendly pool", "pool sustainability", "sustainable pool certificate", "EN 17645", "efficient pool 2026"],
        author: "Víctor Saiz Vela, Chartered Industrial Engineer",
        Body: Article15BodyEN,
        faq: [
          { question: "What is a sustainable pool?", answer: "A facility designed, equipped and operated to minimise its overall environmental impact on water, energy, chemicals, materials and durability. It rests on 6 technical pillars and is measured in a harmonised way by EN 17645:2023 through an A-F class." },
          { question: "How much does it cost to make an existing pool sustainable?", answer: "Depends on scope. Isolated upgrades (LED, off-peak) are cheap (€300-800) with payback under 12 months. Mid-range retrofit (VSD + motorised cover + salt) lands at €3,500-6,500 with 2-4-season payback. Full retrofit exceeds €12,000 with 2-3 class jump." },
          { question: "What is the difference between an efficient pool and a sustainable pool?", answer: "An efficient pool optimises energy consumption. A sustainable pool broadens focus to water, chemicals, shell materials and overall durability. Energy efficiency is a necessary but not sufficient part of sustainability per EN 17645:2023." },
          { question: "How do I get a sustainable pool certificate?", answer: "In 2 minutes for free via the PoolLex.io Technical Sheet with estimated EN 17645 class. For property transactions or permits you need the Seal signed by a chartered industrial engineer, requested at /solicitar-sello." },
        ],
      },
      fr: {
        title: "Piscine durable : guide complet 2026 · 6 piliers + NF EN 17645",
        h1: "Piscine durable — guide complet 2026",
        description: "Qu'est-ce qu'une piscine durable en 2026 : 6 piliers, la norme NF EN 17645:2023 et 5 améliorations qui multiplient votre classe A-F en 6 mois.",
        keywords: ["piscine durable", "piscine écologique", "durabilité piscine", "certificat piscine durable", "NF EN 17645", "piscine efficiente 2026"],
        author: "Víctor Saiz Vela, Ingénieur diplômé",
        Body: Article15BodyFR,
        faq: [
          { question: "Qu'est-ce qu'une piscine durable ?", answer: "Une installation conçue, équipée et exploitée pour minimiser son impact environnemental global sur l'eau, l'énergie, les produits chimiques, les matériaux et la durabilité. Mesurée par la NF EN 17645:2023 via une classification A-F." },
          { question: "Combien coûte rendre durable une piscine existante ?", answer: "Améliorations isolées (LED, heures creuses) : 300-800 €. Rénovation moyenne (VSD + couverture motorisée + électrolyse) : 3 500-6 500 €. Rénovation intégrale au-delà de 12 000 € avec saut de 2-3 classes." },
          { question: "Quelle différence entre piscine efficiente et piscine durable ?", answer: "L'efficience énergétique est une composante nécessaire mais non suffisante de la durabilité définie par la NF EN 17645:2023, qui englobe eau, chimie, matériaux et durabilité." },
          { question: "Comment obtenir un certificat de piscine durable ?", answer: "Fiche Technique gratuite PoolLex.io en 2 min avec classe NF EN 17645 estimée. Pour transactions immobilières ou permis, Sceau signé par ingénieur diplômé demandé sur /solicitar-sello." },
        ],
      },
      de: {
        title: "Nachhaltiger Pool: kompletter Leitfaden 2026 · 6 Säulen + DIN EN 17645",
        h1: "Nachhaltiger Pool — kompletter Leitfaden 2026",
        description: "Was ein nachhaltiger Pool 2026 ist: 6 Säulen, DIN EN 17645:2023 und 5 Maßnahmen, die Ihre A-F-Klasse in 6 Monaten verbessern.",
        keywords: ["nachhaltiger Pool", "ökologischer Pool", "Pool Nachhaltigkeit", "nachhaltiges Pool Zertifikat", "DIN EN 17645", "effizienter Pool 2026"],
        author: "Víctor Saiz Vela, Ingenieur",
        Body: Article15BodyDE,
        faq: [
          { question: "Was ist ein nachhaltiger Pool?", answer: "Eine Anlage, die so betrieben wird, dass ihr gesamter Umwelteinfluss auf Wasser, Energie, Chemie, Materialien und Langlebigkeit minimiert wird. Gemessen durch DIN EN 17645:2023 mittels A-F-Klasse." },
          { question: "Was kostet es, einen bestehenden Pool nachhaltig zu machen?", answer: "Einzelmaßnahmen (LED, Schwachlastprogrammierung): 300-800 €. Mittlere Sanierung (VSD-Pumpe + motorisierte Abdeckung + Salzelektrolyse): 3.500-6.500 €. Umfassende Sanierung: über 12.000 € mit Sprung von 2-3 Klassen." },
          { question: "Wo liegt der Unterschied zwischen effizientem und nachhaltigem Pool?", answer: "Energieeffizienz ist notwendiger aber nicht ausreichender Bestandteil der Nachhaltigkeit nach DIN EN 17645:2023, die Wasser, Chemikalien, Materialien und Gesamtlanglebigkeit umfasst." },
          { question: "Wie erhalte ich ein nachhaltiges Pool-Zertifikat?", answer: "Kostenloses Datenblatt PoolLex.io in 2 Minuten mit geschätzter DIN EN 17645 Klasse. Für Immobiliengeschäfte oder Genehmigungen: Siegel eines Ingenieurs auf /solicitar-sello anfordern." },
        ],
      },
      it: {
        title: "Piscina sostenibile: guida completa 2026 · 6 pilastri + UNI EN 17645",
        h1: "Piscina sostenibile — guida completa 2026",
        description: "Cos'è una piscina sostenibile nel 2026: 6 pilastri, la norma UNI EN 17645:2023 e 5 migliorie che moltiplicano la classe A-F in 6 mesi.",
        keywords: ["piscina sostenibile", "piscina ecologica", "sostenibilità piscina", "certificato piscina sostenibile", "UNI EN 17645", "piscina efficiente 2026"],
        author: "Víctor Saiz Vela, Ingegnere iscritto all'Albo",
        Body: Article15BodyIT,
        faq: [
          { question: "Cos'è una piscina sostenibile?", answer: "Un impianto progettato, equipaggiato e gestito per minimizzare il proprio impatto ambientale complessivo su acqua, energia, prodotti chimici, materiali e durabilità. Misurata dalla UNI EN 17645:2023 tramite classe A-F." },
          { question: "Quanto costa rendere sostenibile una piscina esistente?", answer: "Migliorie isolate (LED, fasce basse): 300-800 €. Ristrutturazione media (VSD + copertura motorizzata + salina): 3.500-6.500 €. Ristrutturazione integrale oltre 12.000 € con salto di 2-3 classi." },
          { question: "Qual è la differenza tra piscina efficiente e sostenibile?", answer: "L'efficienza energetica è componente necessaria ma non sufficiente della sostenibilità definita dalla UNI EN 17645:2023, che include acqua, chimica, materiali e durabilità." },
          { question: "Come ottengo un certificato di piscina sostenibile?", answer: "Scheda Tecnica gratuita PoolLex.io in 2 minuti con classe UNI EN 17645 stimata. Per transazioni immobiliari o licenze, Sigillo firmato da ingegnere iscritto all'Albo su /solicitar-sello." },
        ],
      },
      nl: {
        title: "Duurzaam zwembad: complete gids 2026 · 6 pijlers + NEN-EN 17645",
        h1: "Duurzaam zwembad — complete gids 2026",
        description: "Wat een duurzaam zwembad is in 2026: 6 pijlers, de NEN-EN 17645:2023 norm en 5 verbeteringen die uw A-F klasse in 6 maanden verhogen.",
        keywords: ["duurzaam zwembad", "ecologisch zwembad", "zwembad duurzaamheid", "duurzaam zwembad certificaat", "NEN-EN 17645", "efficient zwembad 2026"],
        author: "Víctor Saiz Vela, ir.",
        Body: Article15BodyNL,
        faq: [
          { question: "Wat is een duurzaam zwembad?", answer: "Een installatie die zo beheerd wordt dat haar globale milieu-impact op water, energie, chemicaliën, materialen en duurzaamheid geminimaliseerd wordt. Gemeten door NEN-EN 17645:2023 via A-F klasse." },
          { question: "Hoeveel kost het om een bestaand zwembad duurzaam te maken?", answer: "Geïsoleerde verbeteringen (LED, daluren): 300-800 €. Middelgrote renovatie (VSD + gemotoriseerde afdekking + zoutelektrolyse): 3.500-6.500 €. Integrale renovatie boven 12.000 € met sprong van 2-3 klassen." },
          { question: "Wat is het verschil tussen een efficient en een duurzaam zwembad?", answer: "Energie-efficiëntie is een noodzakelijk maar onvoldoende onderdeel van duurzaamheid volgens NEN-EN 17645:2023, dat water, chemicaliën, materialen en globale duurzaamheid omvat." },
          { question: "Hoe krijg ik een certificaat voor duurzaam zwembad?", answer: "Gratis Technische Fiche PoolLex.io in 2 minuten met geschatte NEN-EN 17645 klasse. Voor vastgoedtransacties of vergunningen: Zegel ondertekend door gediplomeerd ingenieur (ir.) op /solicitar-sello." },
        ],
      },
    },
  },
  {
    id: "consumo-clorador-salino-en17645",
    slugs: {
      es: "consumo-electrico-clorador-salino",
      en: "salt-chlorinator-power-consumption",
      fr: "consommation-electrique-electrolyseur-sel",
      de: "salzelektrolyse-stromverbrauch",
      it: "consumo-elettrico-elettrolisi-sale",
      nl: "zoutelektrolyse-stroomverbruik",
    },
    publishDate: "2026-08-26",
    updatedDate: "2026-08-26",
    readingTime: 7,
    category: "consumo-energetico",
    coverTheme: "dark",
    // Sprint 146 — Portada propia per idioma (1200x630), mateix sistema
    // visual que la de l'article de sal. La caixa mono porta el calcul
    // literal de l'article (0,15 kW x 10 h x 180 d = 270 kWh) i l'escala
    // inferior la vida de l'electrode de titani, 5.000-10.000 h.
    cover: {
      es: "/img/blog/salt-chlorinator-consumption-es.jpg",
      en: "/img/blog/salt-chlorinator-consumption-en.jpg",
      fr: "/img/blog/salt-chlorinator-consumption-fr.jpg",
      de: "/img/blog/salt-chlorinator-consumption-de.jpg",
      it: "/img/blog/salt-chlorinator-consumption-it.jpg",
      nl: "/img/blog/salt-chlorinator-consumption-nl.jpg",
    },
    coverTitle: {
      es: "Consumo eléctrico del clorador salino",
      en: "Salt chlorinator power consumption",
      fr: "Consommation de l'électrolyseur au sel",
      de: "Stromverbrauch der Salzelektrolyse",
      it: "Consumo elettrico dell'elettrolisi al sale",
      nl: "Stroomverbruik van zoutelektrolyse",
    },
    // El alt descriu el que es VEU. L'anterior descrivia una fotografia
    // d'un clorador amb quadre de control, i la imatge es un diagrama.
    imageAlt: {
      es: "Diagrama del circuito hidráulico con bomba, filtro y celda de electrólisis de 150 W, y escala de vida útil del electrodo de titanio",
      en: "Diagram of the hydraulic circuit with pump, filter and 150 W electrolysis cell, and a scale of the titanium electrode service life",
      fr: "Schéma du circuit hydraulique avec pompe, filtre et cellule d'électrolyse de 150 W, et échelle de durée de vie de l'électrode en titane",
      de: "Schema des Hydraulikkreislaufs mit Pumpe, Filter und 150-W-Elektrolysezelle sowie Skala der Lebensdauer der Titanelektrode",
      it: "Schema del circuito idraulico con pompa, filtro e cella di elettrolisi da 150 W, e scala della durata dell'elettrodo in titanio",
      nl: "Schema van het hydraulische circuit met pomp, filter en elektrolysecel van 150 W, en een schaal voor de levensduur van de titanium elektrode",
    },
    // Sprint 158 — Bloc A (experiment de CTR, es mesura el 05/10/2026)
    snippetTitle: {
      en: "Salt Chlorinator Power Consumption: kWh/Year",
      fr: "Électrolyseur au sel : 270 kWh/an de consommation",
      it: "Elettrolisi al sale: 270 kWh/anno di consumo",
    },
    snippetDescription: {
      en: "What a salt chlorinator really costs to run: 100-250 W, annual kWh and electrode replacement. The pump, not the cell, drives your bill.",
      es: "Nueve de cada diez piscinas nuevas en España llevan clorador salino. Su consumo real es de 100-250 W; aquí está el cálculo.",
      fr: "Neuf piscines neuves sur dix en Espagne ont un électrolyseur au sel. Sa consommation réelle est de 100-250 W : voici le calcul.",
      de: "Neun von zehn neuen Pools in Spanien haben eine Salzelektrolyse. Der reale Verbrauch liegt bei 100-250 W: hier die Rechnung.",
      it: "Nove piscine nuove su dieci in Spagna hanno l'elettrolisi al sale. Il consumo reale è di 100-250 W: ecco il calcolo.",
      nl: "Negen van de tien nieuwe zwembaden in Spanje hebben zoutelektrolyse. Het werkelijke verbruik is 100-250 W: hier de berekening.",
    },
    translations: {
      es: {
        title: "Consumo Eléctrico Clorador Salino: 270 kWh/año · Datos 2026",
        h1: "El 90 % de las piscinas nuevas son salinas. Nadie mide lo que consumen",
        description: "Nueve de cada diez piscinas nuevas en España instalan clorador salino. Calculamos su consumo real (100-250 W), el coste del electrodo de titanio y cómo afecta a la clase ambiental EN 17645.",
        keywords: [
          "consumo electrico clorador salino",
          "cuanto consume un clorador salino",
          "clorador salino kwh",
          "electrodo titanio clorador salino",
          "electrolisis salina consumo",
        ],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article16BodyES,
        faq: [
          { question: "¿Cuánto consume un clorador salino de piscina?", answer: "Un equipo doméstico para una piscina de 50-80 m³ consume entre 100 y 250 W según modelo y nivel de producción. Con valores medios (150 W, 10 h/día, 6 meses de temporada) el consumo es de unos 270 kWh por temporada, equivalentes a 65-70 € anuales a 0,25 €/kWh." },
          { question: "¿El clorador salino consume más que el cloro tradicional?", answer: "En electricidad sí: el cloro envasado no consume nada en la instalación. Pero el balance ambiental global suele ser favorable al salino porque reduce el cloro fabricado, transportado y envasado. La norma EN 17645 valora ambos vectores, no solo el eléctrico." },
          { question: "¿Cada cuánto hay que cambiar el electrodo de titanio?", answer: "La vida útil habitual está entre 5.000 y 10.000 horas de funcionamiento, lo que equivale a 3-5 temporadas a 10 h/día. El recambio cuesta entre 200 y 500 € según el modelo. Mantener la salinidad en el rango del fabricante alarga notablemente su duración." },
          { question: "¿Cómo puedo reducir el consumo de mi clorador salino?", answer: "Cuatro acciones: ajustar la producción al 50-70 % si el cloro libre se mantiene en rango, sincronizar el clorador con las horas de filtración, mantener la salinidad dentro del rango del fabricante y programar el funcionamiento en horas valle, que puede abaratar el mismo consumo un 30-40 %." },
          { question: "¿Afecta el clorador salino a la clasificación EN 17645 de mi piscina?", answer: "Sí. La UNE-EN 17645:2023 puntúa hasta ocho componentes de la piscina y el sistema de tratamiento es uno de ellos. Su consumo eléctrico suma al vector energético global, mientras que la reducción de químicos envasados mejora el vector de materiales. El resultado neto depende de la potencia del equipo, sus horas de uso y la durabilidad de la celda." },
        ],
      },
      en: {
        title: "Salt Chlorinator Power Consumption: 270 kWh/year · 2026 Data",
        h1: "Salt Chlorinator Power Consumption: kWh per Year",
        description: "Nine out of ten new pools in Spain install a salt chlorinator. We calculate real consumption (100-250 W), titanium electrode cost and how it affects your EN 17645 environmental class.",
        keywords: [
          "salt chlorinator power consumption",
          "how much electricity does a salt chlorinator use",
          "salt cell running cost",
          "titanium electrode replacement",
          "saltwater pool electricity",
        ],
        author: "Víctor Saiz Vela, Chartered Industrial Engineer",
        Body: Article16BodyEN,
        faq: [
          { question: "How much electricity does a pool salt chlorinator use?", answer: "A residential unit for a 50-80 m³ pool draws between 100 and 250 W depending on model and output level. With average values (150 W, 10 h/day, a 6-month season) consumption is around 270 kWh per season, roughly €65-70 a year at €0.25/kWh." },
          { question: "Does a salt chlorinator use more than traditional chlorine?", answer: "In electricity, yes: packaged chlorine draws nothing on site. But the overall environmental balance usually favours salt because it cuts manufactured, transported and packaged chlorine. EN 17645 weighs both vectors, not just the electrical one." },
          { question: "How often does the titanium electrode need replacing?", answer: "Typical service life is 5,000 to 10,000 operating hours, which is 3-5 seasons at 10 h/day. Replacement costs €200-500 depending on model. Keeping salinity within the manufacturer's range extends cell life considerably." },
          { question: "How can I reduce my salt chlorinator's consumption?", answer: "Four actions: set output to 50-70 % if free chlorine holds in range, sync the chlorinator with filtration hours, keep salinity within the manufacturer's range, and schedule operation during off-peak hours, which can cut the cost of the same consumption by 30-40 %." },
          { question: "Does a salt chlorinator affect my pool's EN 17645 class?", answer: "Yes. EN 17645:2023 scores up to eight pool components and the treatment system is one of them. Its electricity draw adds to the overall energy vector, while cutting packaged chemicals improves the materials vector. The net result depends on unit power, running hours and cell durability." },
        ],
      },
      fr: {
        title: "Consommation Électrique Électrolyseur au Sel : 270 kWh/an · 2026",
        h1: "90 % des piscines neuves sont au sel. Personne ne mesure ce qu'elles consomment",
        description: "Neuf piscines neuves sur dix en Espagne installent un électrolyseur au sel. Nous calculons la consommation réelle (100-250 W), le coût de l'électrode titane et l'impact sur la classe environnementale NF EN 17645.",
        keywords: [
          "consommation electrique electrolyseur sel",
          "combien consomme un electrolyseur piscine",
          "cout electrolyse sel piscine",
          "remplacement electrode titane",
          "piscine au sel consommation",
        ],
        author: "Víctor Saiz Vela, Ingénieur diplômé",
        Body: Article16BodyFR,
        faq: [
          { question: "Combien consomme un électrolyseur au sel de piscine ?", answer: "Un appareil domestique pour une piscine de 50-80 m³ consomme entre 100 et 250 W selon le modèle et le niveau de production. Avec des valeurs moyennes (150 W, 10 h/jour, saison de 6 mois), la consommation est d'environ 270 kWh par saison, soit 65-70 € par an à 0,25 €/kWh." },
          { question: "L'électrolyseur consomme-t-il plus que le chlore traditionnel ?", answer: "En électricité oui : le chlore conditionné ne consomme rien sur place. Mais le bilan environnemental global est généralement favorable au sel car il réduit le chlore fabriqué, transporté et emballé. La NF EN 17645 évalue les deux vecteurs, pas seulement l'électrique." },
          { question: "À quelle fréquence faut-il changer l'électrode de titane ?", answer: "La durée de vie habituelle est de 5 000 à 10 000 heures de fonctionnement, soit 3 à 5 saisons à 10 h/jour. Le remplacement coûte 200 à 500 € selon le modèle. Maintenir la salinité dans la plage du fabricant allonge sensiblement la durée de vie de la cellule." },
          { question: "Comment réduire la consommation de mon électrolyseur ?", answer: "Quatre actions : régler la production à 50-70 % si le chlore libre reste dans la plage, synchroniser l'électrolyseur avec les heures de filtration, maintenir la salinité dans la plage du fabricant et programmer le fonctionnement en heures creuses, ce qui peut réduire le coût de 30-40 % à consommation égale." },
          { question: "L'électrolyse au sel influence-t-elle la classe NF EN 17645 de ma piscine ?", answer: "Oui. La NF EN 17645:2023 note jusqu'à huit composants de la piscine et le système de traitement en fait partie. Sa consommation électrique s'ajoute au vecteur énergétique global, tandis que la réduction des produits chimiques conditionnés améliore le vecteur matériaux. Le résultat net dépend de la puissance, des heures d'utilisation et de la durabilité de la cellule." },
        ],
      },
      de: {
        title: "Salzelektrolyse Stromverbrauch: 270 kWh/Jahr · Daten 2026",
        h1: "90 % der neuen Pools sind Salzwasserpools. Niemand misst ihren Verbrauch",
        description: "Neun von zehn neuen Pools in Spanien haben eine Salzelektrolyse. Wir berechnen den realen Verbrauch (100-250 W), die Kosten der Titanelektrode und die Auswirkung auf die DIN EN 17645 Umweltklasse.",
        keywords: [
          "salzelektrolyse stromverbrauch",
          "wie viel verbraucht eine salzanlage pool",
          "salzelektrolyse kosten",
          "titanelektrode wechseln",
          "salzwasserpool stromkosten",
        ],
        author: "Víctor Saiz Vela, Ingenieur",
        Body: Article16BodyDE,
        faq: [
          { question: "Wie viel Strom verbraucht eine Salzelektrolyse für den Pool?", answer: "Ein Hausgerät für einen Pool von 50-80 m³ nimmt je nach Modell und Produktionsstufe 100 bis 250 W auf. Mit Mittelwerten (150 W, 10 h/Tag, 6 Monate Saison) sind das rund 270 kWh pro Saison, also etwa 65-70 € im Jahr bei 0,25 €/kWh." },
          { question: "Verbraucht die Salzelektrolyse mehr als klassisches Chlor?", answer: "Beim Strom ja: abgepacktes Chlor verbraucht vor Ort nichts. Die Gesamtumweltbilanz fällt aber meist zugunsten der Salzelektrolyse aus, weil hergestelltes, transportiertes und verpacktes Chlor entfällt. Die DIN EN 17645 bewertet beide Vektoren, nicht nur den elektrischen." },
          { question: "Wie oft muss die Titanelektrode gewechselt werden?", answer: "Die übliche Lebensdauer liegt bei 5.000 bis 10.000 Betriebsstunden, also 3-5 Saisons bei 10 h/Tag. Der Austausch kostet je nach Modell 200 bis 500 €. Ein Salzgehalt im Herstellerbereich verlängert die Zelllebensdauer deutlich." },
          { question: "Wie kann ich den Verbrauch meiner Salzanlage senken?", answer: "Vier Maßnahmen: Produktion auf 50-70 % stellen, wenn das freie Chlor im Bereich bleibt, Elektrolyse mit den Filterzeiten synchronisieren, Salzgehalt im Herstellerbereich halten und den Betrieb in Schwachlastzeiten programmieren, was denselben Verbrauch um 30-40 % verbilligen kann." },
          { question: "Beeinflusst die Salzelektrolyse die DIN EN 17645 Klasse meines Pools?", answer: "Ja. Die DIN EN 17645:2023 bepunktet bis zu acht Poolkomponenten, und das Aufbereitungssystem ist eine davon. Sein Stromverbrauch zählt zum Gesamtenergievektor, während weniger abgepackte Chemie den Materialvektor verbessert. Das Nettoergebnis hängt von Leistung, Laufzeit und Zellhaltbarkeit ab." },
        ],
      },
      it: {
        title: "Consumo Elettrico Elettrolisi al Sale: 270 kWh/anno · Dati 2026",
        h1: "Il 90 % delle piscine nuove è al sale. Nessuno misura quanto consumano",
        description: "Nove piscine nuove su dieci in Spagna installano un elettrolizzatore al sale. Calcoliamo il consumo reale (100-250 W), il costo dell'elettrodo di titanio e l'effetto sulla classe ambientale UNI EN 17645.",
        keywords: [
          "consumo elettrico elettrolisi sale",
          "quanto consuma un elettrolizzatore piscina",
          "costo elettrolisi salina",
          "sostituzione elettrodo titanio",
          "piscina al sale consumi",
        ],
        author: "Víctor Saiz Vela, Ingegnere iscritto all'Albo",
        Body: Article16BodyIT,
        faq: [
          { question: "Quanto consuma un elettrolizzatore al sale per piscina?", answer: "Un apparecchio domestico per una piscina da 50-80 m³ assorbe tra 100 e 250 W secondo modello e livello di produzione. Con valori medi (150 W, 10 h/giorno, stagione di 6 mesi) il consumo è di circa 270 kWh a stagione, pari a 65-70 € all'anno a 0,25 €/kWh." },
          { question: "L'elettrolizzatore consuma più del cloro tradizionale?", answer: "In elettricità sì: il cloro confezionato non consuma nulla in loco. Ma il bilancio ambientale complessivo è di norma favorevole al sale perché riduce cloro prodotto, trasportato e imballato. La UNI EN 17645 valuta entrambi i vettori, non solo quello elettrico." },
          { question: "Ogni quanto va sostituito l'elettrodo di titanio?", answer: "La vita utile abituale è di 5.000-10.000 ore di funzionamento, cioè 3-5 stagioni a 10 h/giorno. La sostituzione costa 200-500 € secondo il modello. Mantenere la salinità nel range del costruttore allunga sensibilmente la durata della cella." },
          { question: "Come posso ridurre il consumo del mio elettrolizzatore?", answer: "Quattro azioni: impostare la produzione al 50-70 % se il cloro libero resta in range, sincronizzare l'elettrolizzatore con le ore di filtrazione, mantenere la salinità nel range del costruttore e programmare il funzionamento nelle ore di minor costo, che può ridurre la spesa del 30-40 % a parità di consumo." },
          { question: "L'elettrolisi al sale influisce sulla classe UNI EN 17645 della mia piscina?", answer: "Sì. La UNI EN 17645:2023 assegna un punteggio a un massimo di otto componenti e il sistema di trattamento è uno di questi. Il suo consumo elettrico si somma al vettore energetico complessivo, mentre la riduzione di prodotti chimici confezionati migliora il vettore materiali. Il risultato netto dipende da potenza, ore di utilizzo e durabilità della cella." },
        ],
      },
      nl: {
        title: "Zoutelektrolyse Stroomverbruik: 270 kWh/jaar · Cijfers 2026",
        h1: "90 % van de nieuwe zwembaden is zout. Niemand meet wat ze verbruiken",
        description: "Negen van de tien nieuwe zwembaden in Spanje krijgen een zoutelektrolyse. We berekenen het werkelijke verbruik (100-250 W), de kosten van de titaanelektrode en het effect op de NEN-EN 17645 milieuklasse.",
        keywords: [
          "zoutelektrolyse stroomverbruik",
          "hoeveel verbruikt een zoutelektrolyse",
          "kosten zoutelektrolyse zwembad",
          "titaancel vervangen",
          "zoutwaterzwembad stroomkosten",
        ],
        author: "Víctor Saiz Vela, ir.",
        Body: Article16BodyNL,
        faq: [
          { question: "Hoeveel stroom verbruikt een zoutelektrolyse voor het zwembad?", answer: "Een huishoudelijk apparaat voor een zwembad van 50-80 m³ neemt 100 tot 250 W op, afhankelijk van model en productieniveau. Met gemiddelde waarden (150 W, 10 h/dag, seizoen van 6 maanden) is dat ongeveer 270 kWh per seizoen, oftewel 65-70 € per jaar bij 0,25 €/kWh." },
          { question: "Verbruikt zoutelektrolyse meer dan traditioneel chloor?", answer: "Qua stroom wel: verpakt chloor verbruikt ter plaatse niets. Maar de totale milieubalans valt meestal gunstig uit voor zout, omdat geproduceerd, getransporteerd en verpakt chloor wegvalt. De NEN-EN 17645 weegt beide vectoren, niet alleen de elektrische." },
          { question: "Hoe vaak moet de titaanelektrode vervangen worden?", answer: "De gebruikelijke levensduur is 5.000 tot 10.000 bedrijfsuren, oftewel 3-5 seizoenen bij 10 h/dag. Vervanging kost 200 tot 500 € afhankelijk van het model. Het zoutgehalte binnen het bereik van de fabrikant houden verlengt de levensduur van de cel aanzienlijk." },
          { question: "Hoe kan ik het verbruik van mijn zoutelektrolyse verlagen?", answer: "Vier acties: zet de productie op 50-70 % als het vrije chloor binnen bereik blijft, synchroniseer de elektrolyse met de filtratie-uren, houd het zoutgehalte binnen het bereik van de fabrikant en programmeer het bedrijf in daluren, wat hetzelfde verbruik 30-40 % goedkoper kan maken." },
          { question: "Beïnvloedt zoutelektrolyse de NEN-EN 17645 klasse van mijn zwembad?", answer: "Ja. De NEN-EN 17645:2023 scoort maximaal acht zwembadcomponenten en het behandelsysteem is er daar een van. Het stroomverbruik telt mee in de totale energievector, terwijl minder verpakte chemie de materialenvector verbetert. Het nettoresultaat hangt af van vermogen, draaiuren en levensduur van de cel." },
        ],
      },
    },
  },
  {
    id: "legalizar-piscina-espana",
    slugs: {
      es: "legalizar-piscina-espana",
    },
    publishDate: "2026-09-04",
    updatedDate: "2026-09-04",
    readingTime: 8,
    category: "normativa",
    coverTheme: "dark",
    // Sprint 145 — Portada propia (1200x630), mateix sistema visual que la
    // de l'article de sal. Dibuixa els dos parametres que la FAQ 1 dona per
    // determinants i que es poden representar: retranqueo i ocupacio.
    // Sense xifres: els limits els fixa el planejament de cada municipi.
    cover: {
      es: "/img/blog/legalizar-piscina-parcela-retranqueo-es.jpg",
    },
    coverTitle: {
      es: "Legalizar una piscina en España",
    },
    // El alt descriu el que es VEU, no el tema de l'article.
    imageAlt: {
      es: "Planta de una parcela con la edificación, la piscina y la cota de retranqueo al linde, junto a una escala de ocupación permitida",
    },
    snippetDescription: {
      es: "Cómo legalizar una piscina construida sin licencia: la comprobación previa que decide el expediente, los trámites y los costes.",
    },
    translations: {
      es: {
        // 12/10/2026 — abans: "Legalizar una Piscina en España: Guía Completa
        // 2026". No era fals, era GENÈRIC: no deia l'única cosa que defineix
        // aquesta pàgina i que la gent escriu de debò. A GSC, la consulta que
        // hi porta més gent és «como legalizar una piscina ya construida»
        // (p8-9), i darrere hi van «multa por construir piscina sin licencia»
        // i «licencia piscina». La promesa de la piscina JA FETA i sense
        // papers només sortia a la description.
        //
        // La pàgina va arribar a p7,5 amb 178 impressions i 2 clics (1,1 %,
        // quan a aquella posició el normal és 3-5 %) i des d'allà ha caigut a
        // p17 en quatre setmanes. Quan una pàgina ben posicionada no es
        // clica, la palanca és el que es llegeix a la SERP.
        //
        // «España» s'hi queda a posta: el 28 % de les impressions del lloc
        // són de LatAm i això és dret espanyol. Dir-ho al títol no treu
        // impressions, però evita el clic que se'n va de seguida.
        //
        // 45 caràcters. El sufix de marca (18) no hi cabria sense passar de
        // 60, i server.js ja no l'hi posa quan no hi cap — serveix el títol
        // pelat, que és el que volem.
        title: "Legalizar una piscina ya construida en España",
        h1: "Legalizar una piscina en España",
        description: "Cómo legalizar una piscina construida sin licencia: la comprobación previa que decide el expediente, qué normativa aplica de verdad, los trámites por orden y los costes reales.",
        keywords: [
          "legalizar piscina España",
          "legalización piscina sin licencia",
          "regularizar piscina",
          "trámites legalizar piscina",
          "piscina sin licencia qué hacer",
        ],
        author: "Víctor Saiz Vela, Ingeniero Industrial colegiado",
        Body: Article17BodyES,
        faq: [
          { question: "¿Se puede legalizar cualquier piscina construida sin licencia?", answer: "No. Solo se legaliza lo que cumpliría el planeamiento vigente hoy: retranqueos, ocupación de parcela, edificabilidad y afecciones sectoriales. Si la piscina invade un retranqueo o está en zona de flujo preferente, no hay expediente que la regularice; habría que modificarla o demolerla. Por eso la comprobación previa es el primer paso y no la recogida de documentación." },
          { question: "¿Legalizar la piscina me libra de la multa?", answer: "No. Son dos procedimientos independientes. La legalización regulariza la obra ante el planeamiento; el expediente de disciplina urbanística sanciona la infracción de haber construido sin licencia. Pueden tramitarse en paralelo, y legalizar no extingue la sanción, aunque en muchos casos la reduce por regularización voluntaria." },
          { question: "¿El RD 742/2013 se aplica a la piscina de mi casa?", answer: "No. El RD 742/2013, que fija los criterios técnico-sanitarios de las piscinas, excluye expresamente de su ámbito las piscinas unifamiliares de uso particular. No necesitas libro de registro ni socorrista. Lo que sí puede exigirte es la ordenanza municipal: vallado, condiciones de seguridad o cómo verter el agua de vaciado. Eso varía de un municipio a otro y no tiene nada que ver con el régimen técnico-sanitario de las piscinas de uso colectivo." },
          { question: "¿Cuánto tarda legalizar una piscina?", answer: "La horquilla habitual va de tres meses a más de un año. Lo que más alarga el plazo no es el ayuntamiento, sino los requerimientos de subsanación por documentación incompleta y las autorizaciones sectoriales que se solicitan tarde. Lanzar en paralelo las que apliquen, en cuanto se sabe cuáles son, es lo que marca la diferencia." },
          { question: "¿Qué pasa si la piscina es muy antigua?", answer: "Puede haber prescrito la acción de restauración de la legalidad urbanística, lo que impide al ayuntamiento ordenar la demolición, aunque la obra siga sin estar legalizada. El plazo varía según la comunidad autónoma y no corre en suelos especialmente protegidos. Es una comprobación que debe hacer un técnico sobre la normativa de tu comunidad, no una regla general." },
          { question: "¿Necesito declarar la piscina en el Catastro?", answer: "Sí. Una piscina es una construcción que altera las características del inmueble y debe declararse. No hacerlo deja una discrepancia entre la realidad física y la descripción catastral que suele aflorar en la siguiente compraventa, y afecta al valor de referencia del inmueble." },
        ],
      },
    },
  },
];

export default ARTICLES;
